import { jsxs, Fragment, jsx } from 'react/jsx-runtime';
import styled, { css, keyframes, ThemeProvider as ThemeProvider$1 } from 'styled-components';
import React, { forwardRef, Component, useMemo, useCallback, useRef, useState, useEffect, useImperativeHandle, createContext, useContext, Fragment as Fragment$1, createElement } from 'react';
import { findDOMNode, createPortal } from 'react-dom';
import { useForm, Controller } from 'react-hook-form';

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

var __assign$1 = function() {
    __assign$1 = Object.assign || function __assign(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
        }
        return t;
    };
    return __assign$1.apply(this, arguments);
};

function __rest$1(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}

function __spreadArray$1(to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
}

function __makeTemplateObject(cooked, raw) {
    if (Object.defineProperty) { Object.defineProperty(cooked, "raw", { value: raw }); } else { cooked.raw = raw; }
    return cooked;
}

var BREAKPOINTS_NAMES = ['xs', 'sm', 'md', 'lg', 'xl'];
var BREAKPOINT_VALUES = {
    xs: 0,
    sm: 600,
    md: 905,
    lg: 1240,
    xl: 1440,
};
var customMediaQuery = function (minWidth) {
    return "@media (min-width: ".concat(minWidth, "px)");
};
var breakpoints = {
    xs: customMediaQuery(BREAKPOINT_VALUES.xs),
    sm: customMediaQuery(BREAKPOINT_VALUES.sm),
    md: customMediaQuery(BREAKPOINT_VALUES.md),
    lg: customMediaQuery(BREAKPOINT_VALUES.lg),
    xl: customMediaQuery(BREAKPOINT_VALUES.xl),
};

// naming should match "colorPalete" field in sanity.json
var crediteaColors = {
    primary: {
        brandBlue: {
            100: '#D5EDF7',
            200: '#B2DBED',
            300: '#7FBAD4',
            400: '#3288AD',
            500: '#0E5F80',
            600: '#10465B',
            700: '#0D394A',
            800: '#0B2D3B',
        },
        neutral: {
            50: '#FFFFFF',
            100: '#F7F7F7',
            200: '#EBEBEB',
            300: '#D2D4D6',
            400: '#9EA9B0',
            500: '#667885',
            600: '#47535C',
            700: '#313940',
            800: '#16181A',
        },
    },
    secondary: {
        lime: {
            100: '#E9F5CE',
            200: '#DAEBB5',
            300: '#C9E18D',
            400: '#B8D661',
            500: '#A7CB19',
            600: '#667D0B',
            700: '#4D5E08',
            800: '#394704',
        },
        lightBlue: {
            100: '#DFF5F7',
            200: '#CBEEF2',
            300: '#B7E6EB',
            400: '#9DDAE0',
            500: '#79C9D6',
            600: '#3396A6',
            700: '#20616B',
            800: '#17454D',
        },
    },
    accent: {
        purple: {
            100: '#EED8F2',
            200: '#E5BEED',
            300: '#D39BDE',
            400: '#BF7ACC',
            500: '#9E5CAB',
            600: '#7C4187',
            700: '#622E6B',
        },
        red: {
            100: '#FCDEE1',
            200: '#FAC3C5',
            300: '#F59FA3',
            400: '#F18087',
            500: '#E85D6A',
            600: '#C23644',
            700: '#9E2935',
        },
        tangerine: {
            100: '#FFE0CC',
            200: '#FBCBAB',
            300: '#FBAB76',
            400: '#F5964E',
            500: '#F48122',
            600: '#D6711E',
            700: '#874813',
        },
        yellow: {
            100: '#FCF2D7',
            200: '#FAE9B9',
            300: '#FFE499',
            400: '#FFD769',
            500: '#FFCB00',
            600: '#DBAB18',
            700: '#8C680E',
        },
        flamingo: {
            100: '#FFDCE4',
            200: '#FFB8CC',
            300: '#F291B2',
            400: '#E86898',
            500: '#E4407D',
            600: '#C6366C',
            700: '#91244D',
        },
    },
};

var buttonColors = {
    primary: {
        dark: {
            backgroundColor: crediteaColors.primary.brandBlue[500],
            color: crediteaColors.primary.neutral[50],
            hover: {
                backgroundColor: crediteaColors.primary.brandBlue[600],
                color: crediteaColors.primary.neutral[50],
            },
            disabled: {
                backgroundColor: crediteaColors.primary.neutral[200],
                color: crediteaColors.primary.neutral[400],
            },
        },
        light: {
            backgroundColor: crediteaColors.secondary.lightBlue[400],
            color: crediteaColors.primary.brandBlue[700],
            hover: {
                backgroundColor: crediteaColors.secondary.lightBlue[600],
                color: crediteaColors.primary.brandBlue[700],
            },
            disabled: {
                backgroundColor: crediteaColors.primary.brandBlue[800],
                color: crediteaColors.primary.neutral[500],
            },
        },
        lime: {
            backgroundColor: crediteaColors.secondary.lime[500],
            color: crediteaColors.primary.brandBlue[600],
            hover: {
                backgroundColor: crediteaColors.secondary.lime[600],
                color: crediteaColors.primary.brandBlue[800],
            },
            disabled: {
                backgroundColor: crediteaColors.primary.neutral[200],
                color: crediteaColors.primary.neutral[400],
            },
        },
    },
    secondary: {
        dark: {
            borderColor: crediteaColors.primary.brandBlue[500],
            color: crediteaColors.primary.brandBlue[500],
            hover: {
                backgroundColor: crediteaColors.primary.brandBlue[200],
                color: crediteaColors.primary.brandBlue[500],
            },
            disabled: {
                borderColor: crediteaColors.primary.neutral[400],
                color: crediteaColors.primary.neutral[400],
            },
        },
        light: {
            borderColor: crediteaColors.primary.neutral[50],
            color: crediteaColors.primary.neutral[50],
            hover: {
                backgroundColor: crediteaColors.primary.brandBlue[400],
                color: crediteaColors.primary.neutral[50],
            },
            disabled: {
                borderColor: crediteaColors.primary.neutral[300],
                color: crediteaColors.primary.neutral[400],
            },
        },
    },
    text: {
        dark: {
            color: crediteaColors.primary.brandBlue[500],
            hover: {
                color: crediteaColors.primary.brandBlue[600],
            },
            disabled: {
                color: crediteaColors.primary.neutral[400],
            },
        },
        light: {
            color: crediteaColors.secondary.lightBlue[500],
            hover: {
                color: crediteaColors.secondary.lightBlue[400],
            },
            disabled: {
                color: crediteaColors.primary.neutral[500],
            },
        },
    },
};

var cardColors = {
    subDescription: crediteaColors.primary.neutral[800],
    backgroundColor: crediteaColors.primary.neutral[100],
};

var checkboxColors = {
    light: {
        checked: {
            rect: crediteaColors.primary.brandBlue[500],
            check: crediteaColors.primary.neutral[50],
        },
        unchecked: crediteaColors.primary.neutral[500],
    },
    dark: {
        checked: {
            rect: crediteaColors.secondary.lightBlue[500],
            check: crediteaColors.primary.brandBlue[600],
        },
        unchecked: crediteaColors.primary.neutral[100],
    },
};

var datePickerColors = {
    wrapper: {
        backgroundColor: '',
        border: '',
        color: '',
    },
    callendar: {
        navigation: {
            backgroundColor: '',
        },
        button: {
            color: '',
        },
        monthViewWeeks: {
            color: '',
        },
        monthViewDays: {
            button: {
                color: '',
                active: {
                    backgroundColor: '',
                    color: '',
                },
            },
        },
    },
};

var descriptiveListItemColors = {
    dark: {
        icon: {
            // check correstponds with lowercased icon name
            check: {
                circle: crediteaColors.secondary.lime[500],
                icon: crediteaColors.primary.neutral[800],
            },
        },
        title: crediteaColors.primary.neutral[800],
        subtitle: crediteaColors.primary.brandBlue[500],
        additionalInfo: crediteaColors.primary.neutral[500],
    },
};

var dividerColors = {
    backgroundColor: crediteaColors.primary.neutral[300],
};

var errorMessageColors = {
    dark: {
        color: crediteaColors.accent.red[200],
    },
    light: {
        color: crediteaColors.accent.red[600],
    },
};

var fullWidthLinkColors = {
    hover: {
        color: crediteaColors.primary.brandBlue[500],
    },
};

var inlineNotificationColors = {
    success: '',
    error: '',
    info: '',
};

var inputColors = {
    backgroundColor: crediteaColors.primary.neutral[50],
    borderColor: crediteaColors.primary.neutral[300],
    placeholder: crediteaColors.primary.neutral[500],
    closeButton: crediteaColors.primary.neutral[500],
    searchIconOn: crediteaColors.primary.brandBlue[500],
    searchIconOff: crediteaColors.primary.neutral[300],
    focus: {
        outlineColor: crediteaColors.primary.brandBlue[500],
    },
};

var linkWithArrowColors = {
    dark: {
        textColor: crediteaColors.primary.brandBlue[500],
        fillColor: crediteaColors.primary.brandBlue[500],
    },
    light: {
        textColor: crediteaColors.secondary.lightBlue[500],
        fillColor: crediteaColors.secondary.lightBlue[500],
    },
};

var loaderColors = {
    stroke: crediteaColors.primary.brandBlue[500],
    strokeAnimated: crediteaColors.primary.brandBlue[100],
};

var menuDropdownColors = {
    backgroundColor: crediteaColors.primary.neutral[100],
};

var phoneButtonColors = {
    dark: {
        color: crediteaColors.primary.neutral[800],
    },
    light: {
        color: crediteaColors.primary.neutral[50],
    },
};

var promoBarColors = {
    dark: {
        backgroundColor: crediteaColors.primary.brandBlue[800],
        color: crediteaColors.primary.neutral[50],
    },
    light: {
        backgroundColor: crediteaColors.primary.brandBlue[100],
        color: crediteaColors.primary.neutral[800],
    },
};

var rangeInputColors = {
    rangeTrack0: {
        backgroundColor: crediteaColors.secondary.lightBlue[600],
    },
    rangeTrack1: {
        backgroundColor: crediteaColors.primary.neutral[300],
    },
    rangeThumbPrimary: {
        backgroundColor: crediteaColors.secondary.lightBlue[600],
        border: crediteaColors.secondary.lightBlue[700],
    },
    rangeThumbSecondary: {
        backgroundColor: crediteaColors.secondary.lightBlue[600],
        color: crediteaColors.primary.neutral[50],
        border: crediteaColors.secondary.lightBlue[700],
    },
    body1: {
        color: crediteaColors.primary.neutral[800],
    },
    inputTrack: {
        backgroundColor: crediteaColors.secondary.lime[300],
    },
};

var selectButtonColors = {
    textVariant: {
        default: {
            color: crediteaColors.primary.neutral[800],
            backgroundColor: crediteaColors.primary.neutral[100],
        },
        selected: {
            color: crediteaColors.primary.neutral[50],
            backgroundColor: crediteaColors.primary.brandBlue[500],
        },
    },
    // there are no designs for ther variants
    otherVariants: {
        backgroundColor: crediteaColors.primary.neutral[50],
        color: crediteaColors.primary.brandBlue[500],
        borderColor: {
            default: crediteaColors.primary.neutral[50],
            selected: crediteaColors.primary.brandBlue[500], // replacement for blue
        },
    },
};

var tooltipColors = {
    backgroundColor: crediteaColors.primary.neutral[700],
    color: crediteaColors.primary.neutral[50],
    iconHover: crediteaColors.primary.neutral[800],
};

var accordionColors = {
    backgroundColor: crediteaColors.primary.neutral[100],
    color: crediteaColors.primary.neutral[800],
};

var blogCardColors = {
    category: crediteaColors.primary.brandBlue[500],
    title: crediteaColors.primary.neutral[800],
    description: crediteaColors.primary.neutral[700],
    date: crediteaColors.primary.neutral[600],
};

var carouselCardColors = {
    backgroundColor: crediteaColors.primary.neutral[100],
    title: crediteaColors.primary.neutral[800],
    text: crediteaColors.primary.neutral[700],
};

var carouselPromotionCardColors = {
    defaultColor: crediteaColors.primary.neutral[50],
};

var circleDiagramColors = {
    backgroundColor: '',
    stroke: '',
    foregroundStroke: '',
    figcaption: '',
};

var contentListColors = {
    backgroundColor: crediteaColors.accent.purple[100],
    linkColor: crediteaColors.primary.brandBlue[500],
};

var featureCardColors = {
    dark: {
        title: crediteaColors.primary.neutral[800],
        description: crediteaColors.primary.neutral[800],
        infoLabel: {
            backgroundColor: crediteaColors.secondary.lime[500],
            color: crediteaColors.primary.neutral[800],
        },
    },
    light: {
        title: crediteaColors.primary.neutral[50],
        description: crediteaColors.primary.neutral[50],
        infoLabel: {
            backgroundColor: crediteaColors.secondary.lime[500],
            color: crediteaColors.primary.neutral[50],
        },
    },
};

var flippingCardColors = {
    defaultBackground: crediteaColors.primary.brandBlue[600],
    iconColor: crediteaColors.secondary.lime[400],
    titleColor: crediteaColors.primary.neutral[50],
    border: crediteaColors.secondary.lime[400],
    defaultTextColor: crediteaColors.primary.neutral[700],
    backgroundReversedCard: crediteaColors.primary.neutral[50],
};

var checkboxItemColors = {
    light: {
        label: crediteaColors.primary.neutral[700],
    },
    dark: {
        label: crediteaColors.primary.neutral[300],
    },
};

var selectItemColors = {
    input: {
        backgroundColor: crediteaColors.primary.neutral[50],
        border: crediteaColors.primary.neutral[300],
        outlineColor: crediteaColors.primary.brandBlue[500],
        textInput: crediteaColors.primary.neutral[800],
        icon: crediteaColors.primary.neutral[800],
    },
    list: {
        backgroundColor: crediteaColors.primary.neutral[50],
        border: crediteaColors.primary.neutral[300],
    },
    option: {
        backgroundColor: crediteaColors.primary.neutral[100],
        selected: {
            backgroundColor: crediteaColors.primary.brandBlue[100],
            hover: {
                backgroundColor: crediteaColors.primary.brandBlue[200],
            },
        },
        hover: {
            backgroundColor: crediteaColors.primary.neutral[200],
        },
    },
    disabled: {
        textInput: crediteaColors.primary.neutral[500],
        icon: crediteaColors.primary.neutral[500],
    },
};

var textAreaItemColors = {
    border: crediteaColors.primary.neutral[300],
    focus: {
        border: crediteaColors.primary.brandBlue[500],
        outline: crediteaColors.primary.brandBlue[500],
    },
};

var languagePickerColors = {
    languageDropdownColor: crediteaColors.primary.neutral[100],
    hover: {
        backgroundColor: crediteaColors.primary.brandBlue[100],
    },
};

var linkBodyColors = {
    color: crediteaColors.primary.brandBlue[500],
    hover: {
        color: crediteaColors.primary.brandBlue[500],
    },
};

var linksWithTitleColors = {
    description: crediteaColors.primary.neutral[700],
    borderTop: crediteaColors.primary.neutral[300],
};

var menuItemBodyColors = {
    backgroundColor: '',
    color: '',
    active: {
        color: '',
    },
};

var mobileDrawerColors = {
    backgroundColor: crediteaColors.primary.neutral[50],
};

var modalColors = {
    overlay: {
        backgroundColor: crediteaColors.primary.neutral[800],
    },
    container: {
        backgroundColor: crediteaColors.primary.neutral[50],
    },
};

var paginationColors = {
    wrapper: {
        borderTop: crediteaColors.primary.neutral[300],
    },
    pageControl: {
        color: crediteaColors.primary.brandBlue[500],
        active: {
            backgroundColor: crediteaColors.primary.brandBlue[500],
            color: crediteaColors.primary.neutral[50],
        },
    },
};

var providentTextLogoColors = {
    dark: {
        color: crediteaColors.primary.neutral[800],
    },
    light: {
        color: crediteaColors.primary.neutral[50],
    },
};

var shopCardColors = {
    white: crediteaColors.primary.neutral[50],
};

var sliderColors = {
    title: crediteaColors.primary.brandBlue[500],
    sliderButton: {
        color: crediteaColors.primary.brandBlue[500],
        border: crediteaColors.primary.brandBlue[500],
    },
    infoPill: {
        backgroundColor: crediteaColors.secondary.lime[300],
        color: crediteaColors.primary.neutral[800],
    },
};

var sliderArrowsColors = {
    disabledIconColor: crediteaColors.primary.neutral[500],
    activeIconColor: crediteaColors.primary.brandBlue[500],
};

var snackbarColors = {
    success: crediteaColors.secondary.lime[300],
    warning: crediteaColors.accent.red[400],
};

var socialsColors = {
    circle: crediteaColors.primary.brandBlue[600],
};

var stepperColors = {
    step: crediteaColors.primary.neutral[300],
    activeStep: crediteaColors.primary.brandBlue[500],
};

var styledListColors = {
    oddChild: {
        backgroundColor: crediteaColors.primary.neutral[100],
    },
};

var tableColors = {
    border: crediteaColors.primary.neutral[300],
    header: {
        backgroundColor: crediteaColors.primary.neutral[100],
    },
    loanAppTable: {
        oddRows: crediteaColors.primary.neutral[100],
    },
};

var tabsColors = {
    backgroundColor: crediteaColors.primary.neutral[100],
    color: crediteaColors.primary.neutral[800],
    active: {
        backgroundColor: crediteaColors.primary.brandBlue[500],
    },
    contentContainer: {
        backgroundColor: crediteaColors.primary.neutral[100],
    },
};

var alternativeFeaturesColors = {
    defaultColor: crediteaColors.primary.neutral[800],
    defaultBackground: crediteaColors.primary.neutral[100],
};

var blogHeroColors = {
    category: crediteaColors.primary.brandBlue[500],
    date: crediteaColors.primary.neutral[600],
};

var ctaBannerColors = {
    defaultBackground: crediteaColors.secondary.lime[500],
    defaultColor: crediteaColors.primary.neutral[800],
};

var ctaTextSectionColors = {
    defaultBackground: crediteaColors.primary.neutral[100],
    defaultColor: crediteaColors.primary.neutral[800],
};

var cardsSectionColors = {
    defaultColor: crediteaColors.primary.neutral[800],
    defaultBackground: crediteaColors.primary.neutral[100],
};

var carouselColors = {
    dots: {
        color: crediteaColors.primary.neutral[300],
        active: {
            color: crediteaColors.primary.brandBlue[500],
        },
    },
};

var featureColors = {
    defaultBackground: crediteaColors.primary.neutral[50],
    defaultColor: crediteaColors.primary.neutral[800],
};

var featureListColors = {
    defaultBackground: crediteaColors.primary.brandBlue[100],
    defaultColor: crediteaColors.primary.neutral[800],
};

var flippingCardsColors = {
    defaultBackground: crediteaColors.primary.brandBlue[600],
    textColor: crediteaColors.primary.neutral[50],
};

var footerColors = {
    defaultBackground: crediteaColors.primary.neutral[100],
};

var headerColors = {
    defaultStickyBackground: crediteaColors.primary.neutral[50],
};

var latestArticlesColors = {
    backgroundColor: crediteaColors.primary.neutral[100],
};

var loanFormColors = {
    backgroundColor: crediteaColors.primary.neutral[50],
    total: crediteaColors.primary.brandBlue[600],
    totalRefundsValueColor: crediteaColors.primary.brandBlue[600],
    termsDescription: crediteaColors.primary.neutral[500],
    description: crediteaColors.primary.neutral[700],
    summaryWrapper: {
        backgroundColor: crediteaColors.primary.brandBlue[100],
    },
};

var newsletterColors = {
    defaultBackground: crediteaColors.primary.neutral[100],
    termsColor: crediteaColors.primary.neutral[500],
};

var priceInfoColors = {
    backgroundColor: crediteaColors.primary.neutral[100],
    priceTitle: crediteaColors.primary.brandBlue[500],
    pricePeriod: crediteaColors.primary.brandBlue[500],
    listCategoryTitle: {
        color: crediteaColors.primary.neutral[600],
        borderBottom: crediteaColors.primary.neutral[300],
    },
};

var promoBannerColors = {
    backgroundColor: crediteaColors.accent.purple[100],
    defaultColor: crediteaColors.primary.neutral[800],
};

var relatedArticlesColors = {
    backgroundColorVariant: {
        gray: crediteaColors.primary.neutral[100],
        lime: crediteaColors.secondary.lime[200],
    },
};

var roundedFeatureColors = {
    defaultBackground: crediteaColors.primary.neutral[100],
    defaultColor: crediteaColors.primary.neutral[800],
};

var stepsSectionColors = {
    defaultBackgroundTemporary: crediteaColors.primary.neutral[100],
    defaultColor: crediteaColors.primary.neutral[800],
};

var colors = {
    // atoms
    button: __assign$1({}, buttonColors),
    card: __assign$1({}, cardColors),
    checkbox: __assign$1({}, checkboxColors),
    datePicker: __assign$1({}, datePickerColors),
    descriptiveListItem: __assign$1({}, descriptiveListItemColors),
    divider: __assign$1({}, dividerColors),
    errorMessage: __assign$1({}, errorMessageColors),
    fullWidthLink: __assign$1({}, fullWidthLinkColors),
    inlineNotification: __assign$1({}, inlineNotificationColors),
    input: __assign$1({}, inputColors),
    linkWithArrow: __assign$1({}, linkWithArrowColors),
    loader: __assign$1({}, loaderColors),
    menuDropdown: __assign$1({}, menuDropdownColors),
    phoneButton: __assign$1({}, phoneButtonColors),
    promoBar: __assign$1({}, promoBarColors),
    rangeInput: __assign$1({}, rangeInputColors),
    selectButton: __assign$1({}, selectButtonColors),
    tooltip: __assign$1({}, tooltipColors),
    // !atoms
    // molecules
    accordion: __assign$1({}, accordionColors),
    blogCard: __assign$1({}, blogCardColors),
    carouselCard: __assign$1({}, carouselCardColors),
    carouselPromotionCard: __assign$1({}, carouselPromotionCardColors),
    circleDiagram: __assign$1({}, circleDiagramColors),
    contentList: __assign$1({}, contentListColors),
    featureCard: __assign$1({}, featureCardColors),
    flippingCard: __assign$1({}, flippingCardColors),
    // formItems
    checkboxItem: __assign$1({}, checkboxItemColors),
    selectItem: __assign$1({}, selectItemColors),
    textAreaItem: __assign$1({}, textAreaItemColors),
    // !formItems
    languagePicker: __assign$1({}, languagePickerColors),
    linkBody: __assign$1({}, linkBodyColors),
    linksWidthTitle: __assign$1({}, linksWithTitleColors),
    menuItemBody: __assign$1({}, menuItemBodyColors),
    mobileDrawer: __assign$1({}, mobileDrawerColors),
    modal: __assign$1({}, modalColors),
    pagination: __assign$1({}, paginationColors),
    providentTextLogo: __assign$1({}, providentTextLogoColors),
    slider: __assign$1({}, sliderColors),
    sliderArrows: __assign$1({}, sliderArrowsColors),
    snackbar: __assign$1({}, snackbarColors),
    socials: __assign$1({}, socialsColors),
    stepper: __assign$1({}, stepperColors),
    styledList: __assign$1({}, styledListColors),
    table: __assign$1({}, tableColors),
    tabs: __assign$1({}, tabsColors),
    shopCard: __assign$1({}, shopCardColors),
    // !molecules
    // organisms
    alternativeFeatures: __assign$1({}, alternativeFeaturesColors),
    blogHero: __assign$1({}, blogHeroColors),
    ctaBanner: __assign$1({}, ctaBannerColors),
    cardsSection: __assign$1({}, cardsSectionColors),
    carousel: __assign$1({}, carouselColors),
    ctaTextSection: __assign$1({}, ctaTextSectionColors),
    feature: __assign$1({}, featureColors),
    featureList: __assign$1({}, featureListColors),
    flippingCards: __assign$1({}, flippingCardsColors),
    footer: __assign$1({}, footerColors),
    header: __assign$1({}, headerColors),
    latestArticles: __assign$1({}, latestArticlesColors),
    loanForm: __assign$1({}, loanFormColors),
    newsletter: __assign$1({}, newsletterColors),
    priceInfo: __assign$1({}, priceInfoColors),
    promoBanner: __assign$1({}, promoBannerColors),
    relatedArticles: __assign$1({}, relatedArticlesColors),
    roundedFeature: __assign$1({}, roundedFeatureColors),
    stepsSection: __assign$1({}, stepsSectionColors),
};

var fonts = {
    fontFamily: {
        primary: 'Rubik, sans-serif',
    },
    size: {
        mobile: {
            xxxxs: '1rem',
            xxxs: '1.2rem',
            xxs: '1.4rem',
            xs: '1.4rem',
            s: '1.6rem',
            m: '1.8rem',
            l: '2rem',
            xl: '2.4rem',
            xxl: '2.6rem',
            xxxl: '3.0rem',
            xxxxl: '3.6rem',
        },
        tablet: {
            xxxxs: '1rem',
            xxxs: '1.2rem',
            xxs: '1.4rem',
            xs: '1.6rem',
            s: '1.8rem',
            m: '2rem',
            l: '2.4rem',
            xl: '2.6rem',
            xxl: '3.2rem',
            xxxl: '4.0rem',
            xxxxl: '4.8rem',
        },
        desktop: {
            xxxxs: '1rem',
            xxxs: '1.2rem',
            xxs: '1.4rem',
            xs: '1.6rem',
            s: '1.8rem',
            m: '2rem',
            l: '2.4rem',
            xl: '2.6rem',
            xxl: '3.2rem',
            xxxl: '4.2rem',
            xxxxl: '5.6rem',
        },
    },
    lineHeight: {
        mobile: {
            xxxs: '2rem',
            xxs: '2.4rem',
            xs: '2.6rem',
            s: '3rem',
            m: '3.2rem',
            l: '3.4rem',
            xl: '3.6rem',
            xxl: '4.4rem',
        },
        tablet: {
            xxxs: '2rem',
            xxs: '2.4rem',
            xs: '2.6rem',
            s: '3rem',
            m: '3.4rem',
            l: '3.6rem',
            xl: '3.8rem',
            xxl: '5.0rem',
            xxxl: '5.8rem',
        },
        desktop: {
            xxxs: '2rem',
            xxs: '2.4rem',
            xs: '2.6rem',
            s: '3rem',
            m: '3.4rem',
            l: '3.6rem',
            xl: '3.8rem',
            xxl: '5.0rem',
            xxxl: '6.7rem',
        },
    },
    weight: {
        regular400: 400,
        medium500: 500,
        bold700: 700,
    },
};

var AppStore = {
    GooglePlayDark: function () { return ({
        height: 40,
        width: 135,
        viewBox: '0 0 135 40',
        icon: (jsxs(Fragment, { children: [jsx("path", { fill: "#16181A", d: "M130 40H5a5 5 0 0 1-5-5V5a5 5 0 0 1 5-5h125a5 5 0 0 1 5 5v30a5 5 0 0 1-5 5Z" }), jsx("path", { fill: "#03d2fe", d: "M10.5 32.4a2 2 0 0 1-.5-1.5v.2V8.9v.2c0-.7.2-1.2.5-1.5L23 20 10.5 32.4ZM10 8.9Zm0-.1Zm0 0Z" }), jsx("path", { fill: "#ffd001", d: "M27 24.3h.1l5-2.9-5 2.8Zm0-.2L23 20l4.1-4.1 5 2.8c.6.4 1 .8 1 1.3s-.4 1-1 1.3l-5 2.8Z" }), jsx("path", { fill: "#f93646", d: "M11.4 32.9c-.4 0-.7-.2-.9-.4a1.3 1.3 0 0 0 .9.4Zm0-.2c-.4 0-.7-.1-.9-.3L23 20l4.1 4.1-14.5 8.3-1.1.3Zm-.9-.2Z" }), jsx("path", { fill: "#00f075", d: "M22.9 20 10.5 7.6c.2-.2.5-.3.9-.3.3 0 .7 0 1.1.3L27 16 23 20Zm4.2-4.2L12.5 7.5c-.4-.3-.8-.4-1.1-.4.3 0 .7.1 1.1.4l14.6 8.3Z" }), jsx("path", { fill: "#fff", d: "M47.4 10.2c0 .9-.2 1.5-.7 2a3 3 0 0 1-2.2 1 3 3 0 0 1-2.2-1 3 3 0 0 1-1-2.2 3 3 0 0 1 1-2.2 3 3 0 0 1 2.2-1l1.2.3 1 .7-.6.5a2 2 0 0 0-1.6-.7c-.7 0-1.2.2-1.7.7-.4.4-.7 1-.7 1.7s.3 1.3.7 1.7c.5.5 1 .7 1.7.7.6 0 1.2-.2 1.6-.7.3-.3.5-.7.5-1.2h-2.1v-.7h2.9v.4Z" }), jsx("path", { fill: "#fff", d: "M47.4 10.2c0 .9-.3 1.5-.8 2s-1.2.8-2.1.8a3 3 0 0 1-2.2-.8 3 3 0 0 1-.8-2.2c0-.9.2-1.6.8-2.2a3 3 0 0 1 2.2-.8c.4 0 .8 0 1.2.2l.9.7v-.1l-.6.4.1.1c-.3-.5-1-.8-1.6-.8-.7 0-1.3.2-1.7.7-.5.5-.8 1-.8 1.8 0 .7.3 1.3.8 1.8.4.5 1 .7 1.7.7s1.3-.2 1.7-.7c.3-.3.5-.7.5-1.3h-2.1v-.6h2.8v-.1h-.1v.4h.2v-.5h-3.1v.9h2.2v-.1l-.5 1.2c-.5.4-1 .6-1.6.6-.6 0-1.2-.2-1.6-.6-.4-.5-.7-1-.7-1.7s.3-1.2.7-1.7c.4-.4 1-.6 1.6-.6a2 2 0 0 1 1.5.7h.1l.6-.5v-.1c-.2-.4-.6-.6-1-.8l-1.2-.2c-1 0-1.7.3-2.3.9-.6.6-1 1.4-1 2.3 0 1 .4 1.7 1 2.3.6.6 1.4 1 2.3 1a3 3 0 0 0 2.2-1c.6-.5.8-1.2.8-2ZM52 7.7h-2.7v2h2.5v.7h-2.5v1.9H52v.7h-3.5V7H52v.7Z" }), jsx("path", { fill: "#fff", d: "M52 7.7h-2.8v2h2.5v.6h-2.5v2h2.7v.6h-3.3V7.1H52v.6h.2V7h-3.7v6.2h3.7v-.9h-2.7v-1.7h2.5v-.9h-2.5V7.7h2.7ZM55.3 13h-.8V7.7h-1.7V7H57v.7h-1.7V13Z" }), jsx("path", { fill: "#fff", d: "M55.3 13v-.1h-.7V7.6H53v-.5h4v.5h-1.7V13v-.1.1h.2V7.8H57V7h-4.4v.9h1.7v5.3h.9V13ZM60 13V7h.7v6H60Z" }), jsx("path", { fill: "#fff", d: "M60 13V7.1h.6v5.8H60v.1h.1v.1H60.7V6.9H60v6.2-.1ZM64.1 13h-.7V7.7h-1.7V7h4.1v.7h-1.7V13Z" }), jsx("path", { fill: "#fff", d: "M64.1 13v-.1h-.6V7.6h-1.7v-.5h3.9v.5H64V13h.1v-.1.1h.1V7.8H66V7h-4.3v.9h1.7v5.3h.9V13ZM69.8 11.7c.4.5 1 .7 1.6.7.7 0 1.2-.2 1.6-.7.5-.4.7-1 .7-1.7s-.2-1.3-.7-1.7c-.4-.5-1-.7-1.6-.7-.6 0-1.2.2-1.6.7-.5.4-.7 1-.7 1.7s.2 1.3.7 1.7Zm3.8.5a3 3 0 0 1-2.2 1 3 3 0 0 1-3-3.1 3 3 0 0 1 3-3.2 3 3 0 0 1 3 3.1 3 3 0 0 1-.8 2.2Z" }), jsx("path", { fill: "#fff", d: "M69.8 11.7c.4.6 1 .8 1.6.8.7 0 1.3-.2 1.7-.7.5-.5.7-1 .7-1.8 0-.7-.2-1.3-.7-1.8-.4-.5-1-.7-1.7-.7s-1.2.2-1.7.7c-.5.5-.7 1-.7 1.8 0 .7.2 1.3.7 1.8l.2-.1c-.5-.5-.7-1-.7-1.7s.2-1.2.7-1.7c.4-.4.9-.6 1.5-.6s1.1.2 1.6.6c.4.5.6 1 .6 1.7s-.2 1.2-.6 1.7c-.5.4-1 .6-1.6.6-.6 0-1.1-.2-1.5-.6h-.1Zm3.8.5c-.6.5-1.3.8-2.2.8-.8 0-1.5-.3-2.1-.8a3 3 0 0 1-.9-2.2 3 3 0 0 1 3-3c.9 0 1.6.3 2.1.8.6.6.9 1.4.9 2.2a3 3 0 0 1-.9 2.2h.1c.7-.5 1-1.3 1-2.2a3.2 3.2 0 0 0-3.2-3.2 3.2 3.2 0 0 0-3.2 3.2c0 .9.3 1.7 1 2.3a3 3 0 0 0 2.2 1 3 3 0 0 0 2.3-1ZM75.6 13V7h1l2.8 4.7V7h.8v6h-.8l-3-4.9V13h-.8Z" }), jsx("path", { fill: "#fff", d: "M75.6 13V7.1h.9l2.9 4.7h.2V7h.5v5.8h-.6L76.4 8h-.2V13h-.6v.2H76.4v-5 .1h-.1l3 4.9h1V6.9h-.9v4.8-.1h.1l-3-4.7h-1v6.2-.1ZM107 30h1.8V17.5h-1.9V30Zm16.7-8-2.1 5.4-2.3-5.4h-2l3.3 7.6-1.9 4.2h2l5.1-11.8h-2Zm-10.5 6.6c-.7 0-1.5-.3-1.5-1 0-1 1-1.4 2-1.4.8 0 1.2.2 1.7.4-.2 1.2-1.2 2-2.2 2Zm.2-6.9c-1.4 0-2.8.6-3.3 2l1.6.6c.4-.7 1-.9 1.7-.9 1 0 2 .6 2 1.6v.2c-.4-.2-1-.5-2-.5-1.8 0-3.6 1-3.6 2.8 0 1.7 1.5 2.7 3.1 2.7 1.3 0 2-.5 2.4-1.2v1h1.9v-4.8c0-2.2-1.7-3.5-3.8-3.5Zm-11.5 1.8h-2.7v-4.3h2.7c1.3 0 2.1 1.2 2.1 2.2 0 1-.8 2.1-2.1 2.1Zm0-6h-4.6V30h1.9v-4.7h2.6c2 0 4.1-1.5 4.1-4 0-2.3-2-3.8-4-3.8Zm-24.5 11c-1.3 0-2.3-1-2.3-2.5s1-2.6 2.3-2.6c1.3 0 2.3 1.1 2.3 2.6s-1 2.6-2.3 2.6Zm2.2-5.8h-.1a3 3 0 0 0-2.2-1c-2.2 0-4.1 2-4.1 4.3 0 2.4 2 4.3 4 4.3 1 0 1.9-.5 2.3-1v.6c0 1.6-.8 2.5-2.2 2.5-1.1 0-1.9-.8-2.1-1.5l-1.7.7a4 4 0 0 0 3.8 2.5c2.2 0 4-1.3 4-4.5V22h-1.7v.7Zm3 7.3h1.9V17.5h-1.9V30Zm4.7-4.1c-.1-1.7 1.2-2.5 2.2-2.5.7 0 1.3.4 1.6.9l-3.8 1.6Zm5.8-1.4c-.4-1-1.5-2.7-3.7-2.7s-4 1.7-4 4.2a4.2 4.2 0 0 0 7.8 2.4l-1.5-1c-.5.7-1.1 1.2-2 1.2-1 0-1.7-.5-2.1-1.3l5.6-2.4-.1-.4ZM47.7 23v1.8h4.4c-.2 1-.5 1.7-1 2.2-.6.7-1.6 1.4-3.4 1.4a4.7 4.7 0 0 1-4.7-4.8 4.7 4.7 0 0 1 8-3.6l1.3-1.2c-1.1-1-2.5-1.9-4.6-1.9-3.6 0-6.7 3-6.7 6.7 0 3.6 3.1 6.6 6.7 6.6a6 6 0 0 0 4.7-1.9 6 6 0 0 0 1.5-4.2V23h-6.2Zm11.1 5.5c-1.3 0-2.4-1-2.4-2.6 0-1.5 1.1-2.6 2.4-2.6 1.3 0 2.4 1 2.4 2.6 0 1.5-1 2.6-2.4 2.6Zm0-6.8a4.2 4.2 0 0 0-4.2 4.2c0 2.5 1.9 4.3 4.2 4.3 2.4 0 4.3-1.8 4.3-4.3s-2-4.2-4.3-4.2Zm9.3 6.8c-1.3 0-2.4-1-2.4-2.6 0-1.5 1.1-2.6 2.4-2.6 1.3 0 2.4 1 2.4 2.6 0 1.5-1 2.6-2.4 2.6Zm0-6.8A4.2 4.2 0 0 0 64 26c0 2.5 1.9 4.3 4.2 4.3 2.4 0 4.3-1.8 4.3-4.3s-2-4.2-4.3-4.2Z" })] })),
    }); },
    GooglePlayLight: function () { return ({
        height: 40,
        width: 135,
        viewBox: '0 0 135 40',
        icon: (jsxs(Fragment, { children: [jsx("rect", { width: "135", height: "40", fill: "#fff", rx: "6" }), jsx("path", { fill: "#00D7FE", fillRule: "evenodd", d: "M12.4 7.5A2 2 0 0 0 12 9v22c0 .7.1 1.1.4 1.5h.1L25 20.1V20L12.5 7.5Z", clipRule: "evenodd" }), jsx("path", { fill: "#ffd001", fillRule: "evenodd", d: "M29 24.3 25 20V20l4.1-4.2 5 2.9c1.4.8 1.4 2 0 2.8l-4.9 2.8Z", clipRule: "evenodd" }), jsx("path", { fill: "#f93646", fillRule: "evenodd", d: "M29.1 24.2 25 20 12.4 32.5c.5.4 1.3.5 2.1 0l14.6-8.3Z", clipRule: "evenodd" }), jsx("path", { fill: "#00f075", fillRule: "evenodd", d: "M29.1 15.8 14.5 7.5c-.8-.5-1.6-.4-2 0L24.8 20l4.2-4.2Z", clipRule: "evenodd" }), jsx("path", { fillRule: "evenodd", d: "M106.9 31h1.9V18.5h-2V31Zm16.8-8-2.1 5.4h-.1l-2.2-5.4h-2l3.3 7.5-1.9 4.2h2l5-11.7h-2Zm-10.6 6.5c-.6 0-1.4-.3-1.4-1 0-1 1-1.4 2-1.4.8 0 1.1.2 1.6.5-.1 1.1-1.1 2-2.2 2Zm.2-6.8c-1.3 0-2.7.6-3.3 1.9l1.7.7c.3-.7 1-1 1.7-1 1 0 2 .6 2 1.7v.1c-.4-.2-1.1-.5-2-.5-1.8 0-3.6 1-3.6 2.8 0 1.7 1.5 2.8 3 2.8 1.3 0 2-.6 2.5-1.2v1h1.8V26c0-2.2-1.6-3.4-3.8-3.4Zm-11.5 1.8h-2.6v-4.3h2.6c1.4 0 2.2 1.1 2.2 2.1s-.8 2.2-2.2 2.2Zm0-6h-4.5V31h1.9v-4.8h2.6c2 0 4-1.5 4-3.9s-2-3.8-4-3.8Zm-24.4 11c-1.3 0-2.4-1-2.4-2.5s1-2.6 2.4-2.6c1.3 0 2.3 1 2.3 2.6 0 1.5-1 2.5-2.3 2.5Zm2.1-5.8a3 3 0 0 0-2.3-1c-2.1 0-4 1.9-4 4.3s1.9 4.2 4 4.2c1 0 1.8-.4 2.3-1v.7c0 1.6-.8 2.5-2.2 2.5-1.2 0-1.9-.9-2.2-1.6l-1.6.7a4 4 0 0 0 3.8 2.5c2.1 0 4-1.3 4-4.4V23h-1.8v.7Zm3 7.3h2V18.5h-2V31Zm4.7-4.2c0-1.6 1.3-2.5 2.2-2.5.8 0 1.4.4 1.6 1l-3.8 1.5Zm5.8-1.4c-.3-1-1.4-2.7-3.6-2.7s-4 1.7-4 4.3c0 2.3 1.8 4.2 4.2 4.2 2 0 3-1.2 3.5-1.9l-1.4-1c-.5.8-1.2 1.2-2.1 1.2-1 0-1.6-.4-2-1.3l5.6-2.3-.2-.5ZM47.7 24v1.8H52c-.1 1-.4 1.8-1 2.3-.6.6-1.6 1.3-3.3 1.3a4.7 4.7 0 0 1-4.7-4.8 4.7 4.7 0 0 1 8-3.5l1.2-1.3c-1-1-2.5-1.8-4.5-1.8-3.6 0-6.7 3-6.7 6.6 0 3.6 3 6.6 6.7 6.6a6 6 0 0 0 4.6-1.8A6 6 0 0 0 54 25l-.1-1h-6.1Zm11 5.5c-1.2 0-2.3-1-2.3-2.5 0-1.6 1-2.6 2.4-2.6 1.3 0 2.4 1 2.4 2.6 0 1.5-1.1 2.5-2.4 2.5Zm0-6.8a4.2 4.2 0 0 0-4.2 4.3c0 2.4 2 4.2 4.3 4.2S63 29.4 63 27c0-2.5-2-4.3-4.3-4.3Zm9.4 6.8c-1.3 0-2.4-1-2.4-2.5 0-1.6 1.1-2.6 2.4-2.6 1.3 0 2.4 1 2.4 2.6 0 1.5-1.1 2.5-2.4 2.5Zm0-6.8a4.2 4.2 0 0 0-4.3 4.3c0 2.4 2 4.2 4.3 4.2s4.3-1.8 4.3-4.2c0-2.5-2-4.3-4.3-4.3Z", clipRule: "evenodd" }), jsx("path", { fill: "#666", d: "M46.5 10.8v2.4a2 2 0 0 1-1.1.7 3.6 3.6 0 0 1-2.3 0l-.8-.6c-.3-.3-.4-.6-.6-1a4 4 0 0 1-.2-1.3v-.4c0-.5 0-1 .2-1.3l.5-1 .8-.6 1.1-.2c.5 0 1 .1 1.2.3.4.1.6.4.8.7l.4 1h-.9c0-.3 0-.5-.2-.7l-.5-.4-.8-.2c-.3 0-.5 0-.8.2-.2 0-.4.2-.5.4l-.3.8-.1 1v.4l.1 1 .4.8a1.6 1.6 0 0 0 1.3.6h.7l.5-.3.2-.2v-1.4h-1.5v-.7h2.4Zm5.2 2.5v.7h-3.4v-.7h3.4Zm-3.2-5.7V14h-.9V7.6h.9Zm2.7 2.8v.6h-3v-.6h3Zm.4-2.8v.7h-3.3v-.7h3.3Zm3.3 0V14h-.8V7.6h.8Zm2 0v.7H52v-.7h5Zm3.4 0V14h-.8V7.6h.8Zm3.5 0V14H63V7.6h.8Zm2.1 0v.7h-5v-.7h5Zm7.4 3v.4c0 .5 0 1-.2 1.3l-.5 1-.8.6-1 .2c-.5 0-.8 0-1.1-.2l-.8-.6c-.3-.3-.4-.6-.6-1l-.2-1.3v-.4l.2-1.3c.2-.4.3-.7.6-1l.8-.6 1-.2c.4 0 .8 0 1 .2l.9.6.5 1 .2 1.3Zm-.8.4v-.4c0-.4 0-.7-.2-1 0-.3-.2-.6-.3-.8l-.6-.4-.7-.2c-.2 0-.5 0-.7.2-.2 0-.4.2-.5.4l-.4.8-.1 1v.4l.1 1c0 .3.2.6.4.8a1.6 1.6 0 0 0 1.2.6c.3 0 .5 0 .8-.2.2 0 .4-.2.5-.4.1-.2.3-.5.3-.8l.2-1Zm6.7-3.4V14h-.8L75 9v5h-.8V7.6h.8l3.3 5v-5h.8Z" })] })),
    }); },
    AppStoreDark: function () { return ({
        height: 40,
        width: 124,
        viewBox: '0 0 124 40',
        icon: (jsxs(Fragment, { children: [jsx("path", { d: "M8 40a42.3 42.3 0 0 1-3-.2 6.2 6.2 0 0 1-3.2-1.6A5.6 5.6 0 0 1 .2 35a13 13 0 0 1-.2-3V8a107.2 107.2 0 0 1 .2-3 6 6 0 0 1 1.6-3.2A5.9 5.9 0 0 1 5 .2L7 0h110c.6 0 1.3 0 2 .2.6 0 1.2.3 1.7.6a5.9 5.9 0 0 1 3.1 4.2l.2 2v26c0 .7 0 1.3-.2 2a6 6 0 0 1-1.6 3.2 5.7 5.7 0 0 1-3.3 1.6 13.3 13.3 0 0 1-2.9.2H8Z" }), jsx("path", { fill: "#fff", d: "M25.1 20.3a5.2 5.2 0 0 1 2.5-4.3 5.3 5.3 0 0 0-4.2-2.3c-1.8-.2-3.5 1-4.4 1-.9 0-2.3-1-3.8-1a5.6 5.6 0 0 0-4.7 3C8.5 20 10 25.2 12 28c1 1.4 2 3 3.6 2.9 1.4 0 2-1 3.7-1 1.8 0 2.3 1 3.8 1 1.6 0 2.6-1.5 3.5-2.9.7-1 1.2-2 1.6-3.2a5 5 0 0 1-3-4.6ZM22.3 11.9a5 5 0 0 0 1.1-3.7c-1.3.1-2.5.8-3.3 1.7a4.8 4.8 0 0 0-1.2 3.6 4.3 4.3 0 0 0 3.4-1.6ZM43.6 27.5h-5L37.4 31h-2.1L40 18h2.2l4.7 13h-2.1l-1.2-3.5ZM39 25.8h4l-2-5.7-2 5.7ZM57 26.2c0 3-1.5 4.9-3.9 4.9a3.2 3.2 0 0 1-3-1.7v4.7h-2V21.5h2v1.6a3.4 3.4 0 0 1 3-1.7c2.4 0 4 2 4 4.8Zm-2 0c0-1.9-1-3.1-2.4-3.1-1.5 0-2.5 1.3-2.5 3.1 0 2 1 3.2 2.5 3.2s2.5-1.2 2.5-3.2ZM67.6 26.2c0 3-1.6 4.9-4 4.9a3.2 3.2 0 0 1-3-1.7v4.7h-2V21.5h1.9v1.6a3.4 3.4 0 0 1 3-1.7c2.5 0 4 2 4 4.8Zm-2 0c0-1.9-1-3.1-2.6-3.1-1.5 0-2.5 1.3-2.5 3.1 0 2 1 3.2 2.5 3.2 1.6 0 2.6-1.2 2.6-3.2ZM74.5 27.4c.1 1.2 1.4 2 3.1 2 1.7 0 2.8-.8 2.8-2 0-1-.7-1.5-2.4-2l-1.7-.3c-2.4-.6-3.5-1.7-3.5-3.5 0-2.3 2-3.8 4.8-3.8 2.7 0 4.6 1.5 4.7 3.8h-2c0-1.3-1.2-2.1-2.7-2.1-1.6 0-2.7.8-2.7 2 0 .8.7 1.4 2.4 1.8l1.4.3c2.7.7 3.8 1.7 3.8 3.6 0 2.5-2 4-5 4s-4.9-1.5-5-3.8h2ZM86.7 19.3v2.2h1.8V23h-1.8v5.3c0 .8.4 1.2 1.2 1.2h.6v1.4l-1 .1c-2 0-2.7-.7-2.7-2.5V23h-1.4v-1.5h1.4v-2.2h2ZM89.6 26.2c0-3 1.8-4.8 4.5-4.8 2.8 0 4.5 1.9 4.5 4.8 0 3-1.7 4.9-4.5 4.9s-4.5-1.9-4.5-4.9Zm7 0c0-2-1-3.2-2.5-3.2-1.6 0-2.5 1.2-2.5 3.2s1 3.3 2.5 3.3c1.6 0 2.5-1.2 2.5-3.3ZM100.2 21.5h1.9v1.6a2.3 2.3 0 0 1 2.3-1.7h.7v1.9l-.9-.1a2 2 0 0 0-2 1.3V31h-2v-9.5ZM114.1 28.2c-.3 1.7-2 2.9-4 2.9-2.9 0-4.6-1.9-4.6-4.8 0-3 1.7-5 4.4-5s4.3 1.9 4.3 4.8v.6h-6.7v.1a2.4 2.4 0 0 0 .7 2 2.5 2.5 0 0 0 1.9.7 2.2 2.2 0 0 0 2.2-1.3h1.8Zm-6.6-2.8h4.8a2.3 2.3 0 0 0-2.4-2.4 2.4 2.4 0 0 0-2.2 1.4l-.2 1ZM38.9 8.2a2.8 2.8 0 0 1 2.8 2c.1.3.2.7.1 1.1 0 2-1 3.2-3 3.2h-2.2V8.2h2.3Zm-1.3 5.4h1.2a2 2 0 0 0 2-1.4v-.9a2 2 0 0 0-1.2-2 2 2 0 0 0-.8-.2h-1.2v4.5ZM43 12.1a2.2 2.2 0 0 1 1.2-2.3 2.3 2.3 0 0 1 3 1.4c.2.3.2.6.2.9a2.2 2.2 0 0 1-1.3 2.3 2.3 2.3 0 0 1-3-1.4c-.2-.3-.2-.6-.2-.9Zm3.4 0c0-1-.4-1.6-1.3-1.6-.8 0-1.2.6-1.2 1.6s.4 1.6 1.2 1.6c.9 0 1.3-.6 1.3-1.6ZM53.3 14.5h-1l-1-3.5-1 3.5h-1l-1.2-4.8h.9l.8 3.6h.1l1-3.6h.9l1 3.6.9-3.6h1l-1.4 4.8ZM55.7 9.7h1v.8a1.4 1.4 0 0 1 1.4-.8 1.5 1.5 0 0 1 1.6 1v3.7h-.9v-2.8c0-.7-.3-1-1-1a1 1 0 0 0-1.1.6v3.3h-1V9.7ZM61.2 8h1v6.5h-1V7.9ZM63.5 12.1a2.2 2.2 0 0 1 1.3-2.3 2.3 2.3 0 0 1 3 1.4c.1.3.2.6.1.9a2.2 2.2 0 0 1-2.2 2.4 2.3 2.3 0 0 1-2.1-1.5c-.1-.3-.2-.6-.1-.9Zm3.5 0c0-1-.5-1.6-1.3-1.6s-1.3.6-1.3 1.6.5 1.6 1.3 1.6 1.3-.6 1.3-1.6ZM69 13.1c0-.8.5-1.3 1.7-1.4H72v-.5c0-.5-.4-.7-1-.7-.5 0-.9.1-1 .5h-1c.2-.8 1-1.3 2-1.3 1.2 0 1.9.5 1.9 1.5v3.3h-1v-.7a1.6 1.6 0 0 1-1.4.7 1.4 1.4 0 0 1-1.6-1.4Zm3-.4v-.4l-1.2.1c-.7 0-1 .3-1 .7 0 .4.4.6 1 .6a1.1 1.1 0 0 0 1.2-1ZM74.1 12.1c0-1.5.8-2.4 2-2.4a1.6 1.6 0 0 1 1.4.8h.1V7.9h1v6.6h-1v-.8a1.6 1.6 0 0 1-1.5.8c-1.2 0-2-1-2-2.4Zm1 0c0 1 .4 1.6 1.2 1.6.8 0 1.3-.6 1.3-1.6s-.5-1.6-1.3-1.6c-.7 0-1.2.6-1.2 1.6ZM82.4 12.1a2.2 2.2 0 0 1 1.3-2.3 2.3 2.3 0 0 1 3 1.4c.2.3.2.6.2.9a2.2 2.2 0 0 1-1.4 2.3 2.3 2.3 0 0 1-3-1.4l-.1-.9Zm3.5 0c0-1-.5-1.6-1.3-1.6s-1.2.6-1.2 1.6.4 1.6 1.2 1.6c.8 0 1.3-.6 1.3-1.6ZM88.1 9.7h1v.8a1.4 1.4 0 0 1 1.4-.8 1.5 1.5 0 0 1 1.6 1v3.7h-.9v-2.8c0-.7-.3-1-1-1a1 1 0 0 0-1.1.6v3.3h-1V9.7ZM97.4 8.6v1.2h1v.7h-1V13c0 .5.2.7.7.7h.3v.7a3 3 0 0 1-.5 0c-1 0-1.4-.3-1.4-1.2v-2.7h-.8v-.7h.8V8.6h1ZM99.7 8h1v2.5a1.5 1.5 0 0 1 1.5-.8 1.6 1.6 0 0 1 1.5 1l.1.7v3h-1v-2.8c0-.7-.3-1-1-1a1.1 1.1 0 0 0-1 .6c-.1.2-.2.3-.1.5v2.8h-1V7.9ZM109.2 13.2a2 2 0 0 1-2 1.3A2.2 2.2 0 0 1 105 13v-.9a2.2 2.2 0 0 1 2-2.4c1.4 0 2.2.8 2.2 2.3v.4H106a1.3 1.3 0 0 0 .3 1 1.3 1.3 0 0 0 1 .4 1.1 1.1 0 0 0 1-.6h1Zm-3.2-1.5h2.4a1.1 1.1 0 0 0-1.2-1.3 1.2 1.2 0 0 0-1.2 1.3Z" })] })),
    }); },
    AppStoreLight: function () { return ({
        height: 40,
        width: 124,
        viewBox: '0 0 124 40',
        icon: (jsxs(Fragment, { children: [jsx("rect", { width: "124", height: "40", fill: "#fff", rx: "6" }), jsx("path", { d: "M22.8 20.6a5 5 0 0 1 2.3-4.2 5 5 0 0 0-4-2.1c-1.6-.2-3.3 1-4.1 1-.9 0-2.2-1-3.6-1A5.3 5.3 0 0 0 8.9 17c-2 3.4-.5 8.3 1.4 11 .9 1.3 2 2.8 3.4 2.8s1.9-1 3.6-1c1.6 0 2.1 1 3.5 1 1.5 0 2.5-1.4 3.4-2.7.6-1 1.1-2 1.5-3.1-1.8-.8-3-2.5-3-4.4ZM20 12.5c.8-1 1.2-2.2 1.2-3.5a5 5 0 0 0-3.3 1.7c-.8.9-1.2 2-1.1 3.3 1.3 0 2.4-.5 3.2-1.5Z" }), jsx("path", { fill: "#666", d: "M38.6 13.8v-5H40c.4 0 .7 0 1 .2.3 0 .5.2.7.5.1.2.3.4.3.7a4 4 0 0 1 0 2c0 .3-.2.6-.3.8l-.4.4a2.2 2.2 0 0 1-1 .4h-1.7ZM37.7 8v6.4h2.2c.5 0 1 0 1.4-.2a2.4 2.4 0 0 0 1.5-1.7L43 11c0-1-.3-1.8-.8-2.3-.5-.5-1.3-.7-2.3-.7h-2.2Zm7 4v-.7l.4-.5a1.3 1.3 0 0 1 1-.5 1.3 1.3 0 0 1 1 .5c.2.1.3.3.3.5l.1.8v.7l-.4.6a1.4 1.4 0 0 1-1 .4h-.6l-.4-.4c-.2-.2-.3-.3-.3-.6l-.1-.7Zm-.9 0c0 .4 0 .7.2 1 0 .3.2.6.4.8l.7.5 1 .2c.4 0 .7 0 1-.2a2 2 0 0 0 1.1-1.3l.2-1c0-.3 0-.6-.2-.9a2.1 2.1 0 0 0-1.1-1.3l-1-.2-1 .2a2.1 2.1 0 0 0-1.1 1.3 3 3 0 0 0-.2 1ZM54 14.6l1.5-4.7h-.8l-1 3.9-1-3.9H52l-1 3.9-1-3.9H49l1.5 4.7h.8l1-3.7 1 3.7h.7Zm2.4-4.7v4.7h.8v-3.2l.3-.5.5-.3h.6c.3 0 .5 0 .7.2l.2.7v3.1h.8v-3.7l-.3-.6c-.2-.1-.3-.3-.5-.3a2 2 0 0 0-.8-.2c-.7 0-1.2.3-1.5.9v-.8h-.8Zm5.3-1.7v6.4h.7V8.1h-.7Zm2.7 4v-.7l.4-.5a1.3 1.3 0 0 1 1-.5 1.3 1.3 0 0 1 1 .5c.2.1.3.3.3.5l.2.8c0 .3 0 .5-.2.7 0 .3-.1.4-.3.6a1.4 1.4 0 0 1-1 .4h-.6l-.4-.4c-.2-.2-.3-.3-.3-.6l-.1-.7Zm-.9 0c0 .4 0 .7.2 1 0 .3.2.6.4.8l.7.5 1 .2c.4 0 .7 0 1-.2a2 2 0 0 0 1.1-1.3l.2-1c0-.3 0-.6-.2-.9a2.1 2.1 0 0 0-1.1-1.3l-1-.2-1 .2a2.1 2.1 0 0 0-1.1 1.3 3 3 0 0 0-.2 1Zm9.8 2.4a1 1 0 0 1-.6.1c-.2 0-.3 0-.5-.2l-.1-.5-.8.5a2.4 2.4 0 0 1-1.5.1l-.5-.2-.3-.4-.1-.6.1-.6.4-.4c.1-.2.3-.2.5-.3a6.4 6.4 0 0 1 1.2-.2l.5-.1.3-.2.1-.3v-.4l-.3-.3a1 1 0 0 0-.3 0H71l-.8.1c-.2.1-.3.4-.4.7h-.7c0-.3 0-.5.2-.7 0-.2.2-.4.4-.5l.6-.3h1.4c.2 0 .4 0 .5.2.2 0 .3.2.4.4l.2.6v2.8l.2.1h.3v.6ZM72 12a1 1 0 0 1-.4.2 9.5 9.5 0 0 1-1 .1l-.4.1-.4.3-.1.4v.4l.3.2.3.1h1l.4-.3.2-.4.1-.3V12Zm2.7.1v-.7l.3-.5a1.2 1.2 0 0 1 1.1-.6c.3 0 .5 0 .7.2.2 0 .3.2.4.4.2.1.2.3.3.5a2.6 2.6 0 0 1 0 1.3l-.2.6-.5.4-.6.1-.7-.1-.4-.4-.3-.5v-.7Zm3.6 2.3V8.1h-.7v2.4l-.4-.4-.4-.2-.4-.1a2.3 2.3 0 0 0-1.3.1l-.7.6-.4.7a3.4 3.4 0 0 0 0 1.9l.4.8a2 2 0 0 0 1.6.7c.4 0 .7 0 1-.2.2 0 .4-.3.5-.5v.6h.8Zm4.6-2.3.1-.8.3-.5a1.3 1.3 0 0 1 1-.5 1.3 1.3 0 0 1 1 .5l.4.5.1.8-.1.7c0 .3-.2.4-.3.6a1.4 1.4 0 0 1-1 .4h-.6l-.5-.4-.3-.6v-.7Zm-.8 0c0 .3 0 .6.2 1l.4.7.7.5 1 .2c.3 0 .7 0 1-.2a2 2 0 0 0 1-1.3c.2-.3.2-.6.2-1l-.1-.9a2.1 2.1 0 0 0-1.2-1.3l-1-.2-1 .2a2.1 2.1 0 0 0-1.1 1.3 3 3 0 0 0 0 1Zm5.6-2.4v4.7h.8v-3.2l.3-.5.4-.3h.6c.3 0 .6 0 .7.2.2.1.3.4.3.7v3.1h.8v-3l-.1-.7c0-.3-.2-.4-.3-.6-.1-.1-.3-.3-.5-.3a2 2 0 0 0-.7-.2c-.7 0-1.2.3-1.6.9v-.8h-.7Zm9 0V8.5h-.8v1.3H95v.7h.8V14l.2.3.4.2h1.1v-.7h-.4a4 4 0 0 1-.3 0h-.2v-3.3h.9v-.7h-1Zm1.9-1.7v6.4h.8v-3.2l.3-.5.4-.3h.6c.3 0 .6 0 .7.2.2.1.3.4.3.7v3.1h.7v-3.7c0-.3-.2-.4-.3-.6-.1-.1-.3-.3-.5-.3a2.2 2.2 0 0 0-1.6 0l-.4.3a1 1 0 0 0-.2.3V8.1h-.8Zm8.4 3.6h-2.7a1.5 1.5 0 0 1 .4-.9l.4-.3h1.1l.4.3.3.4.1.5Zm.7 1.3h-.7c0 .3-.2.6-.4.7-.2.2-.5.2-.8.2h-.7l-.4-.4-.3-.5v-.6h3.4v-.9l-.4-.9a1.9 1.9 0 0 0-1.7-1l-.9.3a2.1 2.1 0 0 0-1.1 1.3l-.2 1c0 .3 0 .6.2 1l.4.7.7.5 1 .2c.5 0 1-.1 1.3-.4.3-.3.5-.7.6-1.2Z" }), jsx("path", { d: "M54 22c2.2 0 3.7 1.8 3.7 4.6s-1.5 4.6-3.7 4.6a3 3 0 0 1-2.9-1.6v4.5h-1.9v-12H51v1.5c.6-1 1.7-1.7 3-1.6Zm9.9 0c2.3 0 3.8 1.8 3.8 4.6s-1.5 4.6-3.8 4.6a3 3 0 0 1-2.8-1.6v4.5h-2v-12H61v1.5c.6-1 1.7-1.7 2.9-1.6Zm13.3-3.5c2.7 0 4.4 1.5 4.5 3.6h-1.9c0-1.2-1.1-2-2.6-2s-2.5.8-2.5 1.9c0 .9.6 1.4 2.2 1.8l1.4.3c2.5.6 3.6 1.6 3.6 3.5 0 2.3-1.8 3.7-4.8 3.7-2.7 0-4.6-1.4-4.7-3.6h1.9c.1 1.2 1.3 2 3 2 1.5 0 2.6-.8 2.6-2 0-.9-.6-1.5-2.2-1.9l-1.7-.3c-2.2-.6-3.3-1.7-3.3-3.4 0-2.1 1.9-3.6 4.5-3.6ZM93 22c2.7 0 4.3 1.8 4.3 4.6 0 2.9-1.6 4.6-4.3 4.6-2.6 0-4.3-1.7-4.3-4.6 0-2.8 1.7-4.6 4.3-4.6Zm15 0c2.6 0 4.2 1.7 4.2 4.4v.7h-6.4a2.4 2.4 0 0 0 2.4 2.6 2 2 0 0 0 2-1.2h1.9c-.3 1.6-1.9 2.7-4 2.7-2.6 0-4.2-1.7-4.2-4.6 0-2.8 1.6-4.6 4.2-4.6Zm-22-2v2h1.7v1.5H86v5c0 .8.4 1.2 1.1 1.2h.6V31h-1c-1.8 0-2.5-.6-2.5-2.4v-5.2h-1.4v-1.4h1.4v-2.2h1.8Zm-42.3-1.3L48 31.1h-2L45 27.8H40L39 31h-2l4.5-12.4h2Zm59.2 3.3h.6v1.8l-.8-.1a1.9 1.9 0 0 0-2 2v5.4h-1.8v-9h1.7v1.5c.3-1 1.2-1.7 2.3-1.6Zm-9.9 1.5c-1.5 0-2.4 1.2-2.4 3.1 0 2 1 3.1 2.4 3.1 1.5 0 2.4-1.1 2.4-3.1s-.9-3.1-2.4-3.1Zm-39.5 0c-1.4 0-2.3 1.3-2.3 3.1s1 3 2.3 3c1.5 0 2.4-1.2 2.4-3s-1-3-2.4-3Zm10 0c-1.4 0-2.4 1.3-2.4 3.1s1 3 2.4 3 2.4-1.2 2.4-3-1-3-2.4-3Zm-20.9-2.7-1.9 5.4h3.8l-1.9-5.4Zm65.5 2.7a2.3 2.3 0 0 0-2.3 2.3h4.5a2.2 2.2 0 0 0-2.2-2.3Z" })] })),
    }); },
    AppStoreBlack: function () { return ({
        height: 30,
        width: 105,
        viewBox: '0 0 105 30',
        icon: (jsxs(Fragment, { children: [jsx("path", { d: "M21.2139 24.7721C20.138 26.357 19.025 27.9323 17.2678 27.9653C15.5384 27.9983 14.984 26.9334 13.0106 26.9334C11.0351 26.9334 10.4171 27.9323 8.78431 27.9983C7.08789 28.0623 5.79795 26.2865 4.71314 24.7086C2.49689 21.4825 0.805367 15.5867 3.08029 11.6079C4.20683 9.63199 6.22545 8.38178 8.41293 8.34878C10.0789 8.31789 11.6527 9.48021 12.6703 9.48021C13.6894 9.48021 15.5996 8.08471 17.6078 8.2894C18.4484 8.32438 20.8077 8.63061 22.3229 10.864C22.2031 10.9433 19.5068 12.5235 19.5397 15.8157C19.5727 19.7483 22.9606 21.0555 23 21.0733C22.9693 21.1657 22.4584 22.9394 21.2139 24.7721ZM13.8359 3.96933C14.766 2.87146 16.3375 2.05487 17.6344 2C17.8001 3.52499 17.191 5.05209 16.2915 6.1525C15.3902 7.25092 13.9148 8.10658 12.4668 7.99233C12.27 6.50034 12.9998 4.94432 13.8359 3.96933Z", fill: "#16181A" }), jsx("path", { d: "M33.8179 21.2414L34.8477 18.2529H39.1389L40.1687 21.2414H42.0178L37.953 10.1839H36.0649L32 21.2414H33.8179ZM38.6942 16.8736H35.2925L36.9699 12.023H37.0167L38.6942 16.8736ZM44.7798 24V19.8774H44.811C45.3181 20.7969 46.2778 21.3257 47.4949 21.3257C49.6248 21.3257 51.037 19.6552 51.037 17.0805C51.037 14.4981 49.617 12.8352 47.4559 12.8352C46.2778 12.8352 45.2323 13.4023 44.7564 14.2989H44.7173V12.9195H43.0867V24H44.7798ZM47.0268 19.9157C45.677 19.9157 44.772 18.7816 44.7642 17.0805C44.772 15.387 45.6848 14.2452 47.0268 14.2452C48.3999 14.2452 49.3049 15.364 49.3049 17.0805C49.3049 18.8046 48.3999 19.9157 47.0268 19.9157ZM54.0798 24V19.8774H54.111C54.6181 20.7969 55.5778 21.3257 56.7949 21.3257C58.9249 21.3257 60.337 19.6552 60.337 17.0805C60.337 14.4981 58.9171 12.8352 56.7559 12.8352C55.5778 12.8352 54.5323 13.4023 54.0564 14.2989H54.0174V12.9195H52.3868V24H54.0798ZM56.3268 19.9157C54.977 19.9157 54.072 18.7816 54.0642 17.0805C54.072 15.387 54.9848 14.2452 56.3268 14.2452C57.7 14.2452 58.605 15.364 58.605 17.0805C58.605 18.8046 57.7 19.9157 56.3268 19.9157ZM69.3328 21.4253C72.0011 21.4253 73.6786 20.1303 73.6786 18.0613C73.6786 16.4444 72.7189 15.5326 70.4095 14.9962L69.169 14.6973C67.7178 14.3448 67.1248 13.8851 67.1248 13.1034C67.1248 12.1226 68.0533 11.4483 69.4108 11.4483C70.7684 11.4483 71.6968 12.1149 71.7983 13.2184H73.4991C73.4445 11.3103 71.8139 10 69.4342 10C67.0312 10 65.3382 11.3103 65.3382 13.2184C65.3382 14.7586 66.2978 15.7088 68.3654 16.1992L69.8243 16.5441C71.2833 16.8966 71.8997 17.41 71.8997 18.2682C71.8997 19.2567 70.8776 19.977 69.4576 19.977C67.9753 19.977 66.8908 19.2567 66.7659 18.1609H65.0417C65.1509 20.1609 66.8362 21.4253 69.3328 21.4253ZM77.9307 21.295C78.2974 21.295 78.6953 21.2567 78.8825 21.2107V19.9004C78.7733 19.9157 78.4846 19.9387 78.3052 19.9387C77.5952 19.9387 77.2675 19.6015 77.2675 18.8812V14.2299H78.8903V12.9195H77.2675V11.0498H75.5823V12.9195H74.4119V14.2299H75.5823V19.0498C75.5823 20.659 76.2376 21.295 77.9307 21.295ZM83.9461 21.341C86.3959 21.341 87.9485 19.7011 87.9485 17.0805C87.9485 14.4598 86.3881 12.8123 83.9461 12.8123C81.504 12.8123 79.9358 14.4674 79.9358 17.0805C79.9358 19.7011 81.4962 21.341 83.9461 21.341ZM83.9461 19.9617C82.5105 19.9617 81.6523 18.8966 81.6523 17.0805C81.6523 15.272 82.5105 14.1916 83.9461 14.1916C85.3816 14.1916 86.2321 15.2644 86.2321 17.0805C86.2321 18.8966 85.3816 19.9617 83.9461 19.9617ZM90.9991 21.2414V16.2759C90.9991 15.1034 91.7013 14.3448 92.817 14.3448C93.1525 14.3448 93.449 14.3908 93.5894 14.4444V12.8889C93.4568 12.8582 93.2461 12.8352 92.9964 12.8352C91.9978 12.8352 91.2176 13.3946 90.9523 14.3218H90.9211V12.9195H89.3061V21.2414H90.9991ZM98.2784 21.341C100.182 21.341 101.672 20.2989 101.914 18.8046H100.299C100.065 19.5326 99.2927 19.9923 98.3096 19.9923C96.9209 19.9923 96.0002 19.0421 96.0002 17.6015V17.5019H102V16.9195C102 14.3985 100.533 12.8123 98.2082 12.8123C95.8364 12.8123 94.2916 14.5057 94.2916 17.1111C94.2916 19.7165 95.8208 21.341 98.2784 21.341ZM100.307 16.3218H96.008C96.1017 15.0421 96.9833 14.1686 98.2004 14.1686C99.4331 14.1686 100.268 15.0268 100.307 16.3218Z", fill: "#16181A" })] })),
    }); },
    GooglePlayBlack: function () { return ({
        height: 30,
        width: 105,
        viewBox: '0 0 105 30',
        icon: (jsx("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M1 25.3582V4.64418C1 3.92135 1.40742 3.29653 1.99867 3L13.6479 14.9977L1.99398 27C1.40527 26.7026 1 26.0792 1 25.3582ZM17.3437 18.8038L4.60597 26.3778L14.6519 16.0315L17.3437 18.8038ZM22 15.0012C22 14.4093 21.7267 13.883 21.303 13.549L18.6146 11.9504L15.6558 14.9977L18.6166 18.0469L21.3222 16.438C21.735 16.1034 22 15.5842 22 15.0012ZM4.61343 3.62514L17.3417 11.1935L14.6519 13.9637L4.61343 3.62514ZM32.8793 13.962V15.5324L36.6647 15.5307C36.5485 16.3969 36.254 17.0311 35.8063 17.475C35.2542 18.0224 34.3924 18.6249 32.8793 18.6249C30.5464 18.6249 28.7219 16.7607 28.7219 14.4476C28.7219 12.1345 30.5464 10.2704 32.8793 10.2704C34.1383 10.2704 35.0573 10.761 35.7356 11.3919L36.8532 10.2837C35.9073 9.38753 34.6483 8.69995 32.8809 8.69995C29.6863 8.69995 27 11.2801 27 14.4476C27 17.6152 29.6863 20.1953 32.8809 20.1953C34.6045 20.1953 35.9056 19.6345 36.9239 18.5831C37.9708 17.5451 38.2957 16.0881 38.2957 14.9099C38.2957 14.5594 38.2687 14.2357 38.2166 13.962H32.8793ZM64.8109 19.97V9.12219H63.2119V19.97H64.8109ZM42.6772 20.1969C44.7407 20.1969 46.4222 18.6265 46.4222 16.4953C46.4222 14.3491 44.7407 12.7937 42.6772 12.7937C40.6136 12.7937 38.9321 14.3491 38.9321 16.4953C38.9321 18.6265 40.6136 20.1969 42.6772 20.1969ZM42.6772 14.2523C43.8082 14.2523 44.7828 15.1635 44.7828 16.4953C44.7828 17.8121 43.8082 18.7383 42.6772 18.7383C41.5461 18.7383 40.5715 17.8137 40.5715 16.4953C40.5715 15.1635 41.5461 14.2523 42.6772 14.2523ZM50.7558 20.1969C52.8193 20.1969 54.5008 18.6265 54.5008 16.4953C54.5008 14.3491 52.8193 12.7937 50.7558 12.7937C48.6922 12.7937 47.0107 14.3508 47.0107 16.4953C47.0107 18.6265 48.6922 20.1969 50.7558 20.1969ZM50.7558 14.2523C51.8868 14.2523 52.8614 15.1635 52.8614 16.4953C52.8614 17.8121 51.8868 18.7383 50.7558 18.7383C49.6247 18.7383 48.6501 17.8137 48.6501 16.4953C48.6501 15.1635 49.6247 14.2523 50.7558 14.2523ZM62.2437 13.019V19.6629C62.2437 22.3965 60.6178 23.513 58.6956 23.513C56.8862 23.513 55.7972 22.3131 55.3865 21.3318L56.8138 20.7426C57.068 21.3451 57.6908 22.056 58.6939 22.056C59.9243 22.056 60.6868 21.3034 60.6868 19.8865V19.3541H60.6295C60.2626 19.803 59.5557 20.1952 58.6636 20.1952C56.797 20.1952 55.0869 18.5831 55.0869 16.5087C55.0869 14.4192 56.797 12.7937 58.6636 12.7937C59.554 12.7937 60.2609 13.1859 60.6295 13.6215H60.6868V13.019H62.2437ZM60.8029 16.5087C60.8029 15.2052 59.926 14.2523 58.8101 14.2523C57.679 14.2523 56.7314 15.2052 56.7314 16.5087C56.7314 17.7987 57.679 18.7383 58.8101 18.7383C59.926 18.7383 60.8029 17.7987 60.8029 16.5087ZM71.0417 17.7136L72.3141 18.5547C71.9034 19.1571 70.9137 20.1952 69.2037 20.1952C67.0829 20.1952 65.499 18.5697 65.499 16.4936C65.499 14.2923 67.0963 12.792 69.0202 12.792C70.9575 12.792 71.9051 14.3207 72.2148 15.1468L72.3848 15.5674L67.3943 17.6168C67.7763 18.3594 68.3705 18.7383 69.2037 18.7383C70.0385 18.7383 70.6175 18.331 71.0417 17.7136ZM67.125 16.3818L70.461 15.0083C70.2775 14.546 69.7254 14.2239 69.0757 14.2239C68.2426 14.2239 67.0829 14.9532 67.125 16.3818ZM85.5594 8.80359V19H84.7694V8.80359H85.5594ZM78.1776 12.4812C78.0791 12.5889 77.9881 12.7052 77.9045 12.83L77.8656 11.8174H77.1221V21.7616H77.9187V18.0966C77.9905 18.1923 78.0679 18.2833 78.151 18.3694C78.3944 18.6128 78.6843 18.8009 79.0206 18.9337C79.357 19.0664 79.7398 19.1328 80.1691 19.1328C80.6028 19.1328 80.99 19.0443 81.3308 18.8673C81.6715 18.6903 81.957 18.4402 82.1871 18.1171C82.4217 17.7897 82.6009 17.4024 82.7248 16.9554C82.8487 16.5085 82.9107 16.0172 82.9107 15.4817V15.3423C82.9107 14.7847 82.8487 14.2824 82.7248 13.8354C82.6053 13.384 82.4261 12.999 82.1871 12.6804C81.9525 12.3573 81.6627 12.1117 81.3175 11.9435C80.9767 11.7709 80.5873 11.6846 80.1491 11.6846C79.7287 11.6846 79.3548 11.7532 79.0273 11.8904C78.6998 12.0276 78.4165 12.2245 78.1776 12.4812ZM78.0979 17.5131C78.0317 17.4106 77.972 17.3051 77.9187 17.1964V13.7425C78.0205 13.4991 78.1599 13.2756 78.3369 13.072C78.5139 12.864 78.7352 12.6981 79.0007 12.5742C79.2707 12.4458 79.5959 12.3816 79.9765 12.3816C80.3439 12.3816 80.6603 12.4569 80.9258 12.6073C81.1958 12.7578 81.4171 12.968 81.5897 13.238C81.7667 13.5035 81.8972 13.8155 81.9813 14.174C82.0698 14.5325 82.1141 14.9219 82.1141 15.3423V15.4817C82.1141 15.9022 82.0698 16.2916 81.9813 16.6501C81.8972 17.0085 81.7667 17.3228 81.5897 17.5927C81.4171 17.8627 81.198 18.0729 80.9325 18.2234C80.6669 18.3738 80.3527 18.4491 79.9898 18.4491C79.5384 18.4491 79.1556 18.365 78.8414 18.1968C78.5316 18.0242 78.2838 17.7963 78.0979 17.5131ZM92.1054 17.9972C91.9786 18.1568 91.8279 18.3097 91.6533 18.4557C91.4099 18.6548 91.1223 18.8186 90.7904 18.9469C90.4629 19.0708 90.0911 19.1328 89.6751 19.1328C89.206 19.1328 88.7966 19.0443 88.447 18.8673C88.1018 18.6903 87.8319 18.4491 87.6372 18.1437C87.4469 17.8339 87.3517 17.4843 87.3517 17.0948C87.3517 16.7408 87.4269 16.4244 87.5774 16.1456C87.7279 15.8623 87.9447 15.6234 88.228 15.4286C88.5156 15.2295 88.8608 15.079 89.2635 14.9772C89.6707 14.871 90.1265 14.8179 90.631 14.8179H92.0981V14.008C92.0981 13.6673 92.0273 13.373 91.8857 13.1251C91.7441 12.8773 91.5361 12.687 91.2617 12.5542C90.9873 12.4215 90.6487 12.3551 90.246 12.3551C89.8743 12.3551 89.5424 12.4215 89.2503 12.5542C88.9626 12.6826 88.7347 12.8574 88.5665 13.0787C88.4028 13.2955 88.3209 13.5367 88.3209 13.8022L87.5243 13.7956C87.5243 13.5256 87.5907 13.2645 87.7235 13.0123C87.8562 12.76 88.0443 12.5343 88.2877 12.3352C88.5311 12.136 88.821 11.9789 89.1573 11.8639C89.4981 11.7444 89.8721 11.6846 90.2792 11.6846C90.7926 11.6846 91.244 11.7709 91.6334 11.9435C92.0273 12.1161 92.3349 12.375 92.5562 12.7202C92.7774 13.0654 92.8881 13.4991 92.8881 14.0213V17.4931C92.8881 17.741 92.9058 17.9977 92.9412 18.2632C92.981 18.5287 93.0363 18.7478 93.1071 18.9204V19H92.2641C92.211 18.8407 92.1689 18.6416 92.1379 18.4026C92.1227 18.2631 92.1119 18.1279 92.1054 17.9972ZM92.0981 17.0157C92.0173 17.1973 91.9088 17.3698 91.7728 17.533C91.5516 17.7985 91.2705 18.0131 90.9298 18.1769C90.5934 18.3406 90.204 18.4225 89.7614 18.4225C89.4295 18.4225 89.1418 18.3605 88.8984 18.2366C88.6595 18.1083 88.4736 17.9379 88.3408 17.7255C88.2125 17.5131 88.1483 17.2785 88.1483 17.0218C88.1483 16.774 88.2058 16.5527 88.3209 16.358C88.4404 16.1633 88.6108 15.9973 88.8321 15.8601C89.0533 15.7185 89.3233 15.6123 89.6419 15.5415C89.965 15.4662 90.3301 15.4286 90.7373 15.4286H92.0981V17.0157ZM94.0896 11.8174L96.8019 18.9404L96.4528 19.8896C96.3511 20.1772 96.2338 20.4162 96.101 20.6065C95.9682 20.8012 95.8023 20.9473 95.6031 21.0446C95.4084 21.142 95.1672 21.1907 94.8796 21.1907C94.8442 21.1907 94.78 21.1863 94.687 21.1774C94.5941 21.1686 94.5233 21.1575 94.4746 21.1442L94.4813 21.8147C94.5388 21.8324 94.6317 21.8501 94.7601 21.8678C94.8928 21.8899 95.0013 21.901 95.0854 21.901C95.4128 21.901 95.6894 21.8412 95.9151 21.7217C96.1453 21.6067 96.3356 21.4562 96.486 21.2703C96.6409 21.0889 96.7648 20.8986 96.8578 20.6994C96.9551 20.5003 97.0348 20.3189 97.0968 20.1551L100.137 11.8174H99.2808L97.1932 17.8508L94.9526 11.8174H94.0896Z", fill: "#16181A" })),
    }); },
};

var Arrows = {
    ArrowDown: function () { return ({
        height: 24,
        width: 24,
        viewBox: '0 0 24 24',
        icon: (jsx("g", __assign$1({ id: "ic/arrow/black/down", stroke: "none", strokeWidth: "1", fill: "none", fillRule: "evenodd" }, { children: jsx("path", { d: "M12.3492086,9.2407434 C12.6924909,8.94650147 13.182438,8.92198131 13.5536644,9.16718292 L13.6507914,9.2407434 L20.6507914,15.2407434 C21.0701172,15.6001655 21.1186788,16.2314655 20.7592566,16.6507914 C20.4274823,17.0378614 19.8640488,17.1090139 19.4495188,16.8349748 L19.3492086,16.7592566 L13,11.317 L6.65079137,16.7592566 C6.26372136,17.0910309 5.69603434,17.0751741 5.32829297,16.7409603 L5.2407434,16.6507914 C4.9089691,16.2637214 4.92482588,15.6960343 5.25903968,15.328293 L5.34920863,15.2407434 L12.3492086,9.2407434 Z", id: "Path-3", fill: "#16181A", transform: "translate(13.000000, 13.000009) rotate(-180.000000) translate(-13.000000, -13.000009) " }) }))),
    }); },
    ArrowUp: function () { return ({
        height: 24,
        width: 24,
        viewBox: '0 0 24 24',
        icon: (jsx("g", __assign$1({ id: "ic/arrow/black/up", stroke: "none", strokeWidth: "1", fill: "none", fillRule: "evenodd" }, { children: jsx("path", { d: "M12.3492086,9.2407434 C12.6924909,8.94650147 13.182438,8.92198131 13.5536644,9.16718292 L13.6507914,9.2407434 L20.6507914,15.2407434 C21.0701172,15.6001655 21.1186788,16.2314655 20.7592566,16.6507914 C20.4274823,17.0378614 19.8640488,17.1090139 19.4495188,16.8349748 L19.3492086,16.7592566 L13,11.317 L6.65079137,16.7592566 C6.26372136,17.0910309 5.69603434,17.0751741 5.32829297,16.7409603 L5.2407434,16.6507914 C4.9089691,16.2637214 4.92482588,15.6960343 5.25903968,15.328293 L5.34920863,15.2407434 L12.3492086,9.2407434 Z", id: "Path-3", fill: "#16181A" }) }))),
    }); },
    ArrowRight: function () { return ({
        height: 24,
        width: 24,
        viewBox: '0 0 24 24',
        icon: (jsx("g", __assign$1({ id: "ic/arrow/black/right", stroke: "none", strokeWidth: "1", fill: "none", fillRule: "evenodd" }, { children: jsx("path", { d: "M12.3492086,9.2407434 C12.6924909,8.94650147 13.182438,8.92198131 13.5536644,9.16718292 L13.6507914,9.2407434 L20.6507914,15.2407434 C21.0701172,15.6001655 21.1186788,16.2314655 20.7592566,16.6507914 C20.4274823,17.0378614 19.8640488,17.1090139 19.4495188,16.8349748 L19.3492086,16.7592566 L13,11.317 L6.65079137,16.7592566 C6.26372136,17.0910309 5.69603434,17.0751741 5.32829297,16.7409603 L5.2407434,16.6507914 C4.9089691,16.2637214 4.92482588,15.6960343 5.25903968,15.328293 L5.34920863,15.2407434 L12.3492086,9.2407434 Z", id: "Path-3", fill: "#16181A", transform: "translate(13.000000, 13.000009) rotate(-270.000000) translate(-13.000000, -13.000009) " }) }))),
    }); },
    ArrowLeftOn: function () { return ({
        height: 26,
        width: 26,
        viewBox: '0 0 26 26',
        icon: (jsx("g", __assign$1({ id: "Icons", stroke: "none", strokeWidth: "1", fill: "none", fillRule: "evenodd" }, { children: jsx("g", __assign$1({ id: "ic/arrow/blue/left/on", fill: "#16181A" }, { children: jsx("path", { d: "M15.6128994,4.20970461 L15.7071068,4.29289322 L23.7071068,12.2928932 C24.0675907,12.6533772 24.0953203,13.2206082 23.7902924,13.6128994 L23.7071068,13.7071068 L15.7071068,21.7071068 C15.3165825,22.0976311 14.6834175,22.0976311 14.2928932,21.7071068 C13.9324093,21.3466228 13.9046797,20.7793918 14.2097046,20.3871006 L14.2928932,20.2928932 L20.585,14 L3,14 C2.44771525,14 2,13.5522847 2,13 C2,12.4477153 2.44771525,12 3,12 L20.585,12 L14.2928932,5.70710678 C13.9324093,5.34662282 13.9046797,4.77939176 14.2097046,4.38710056 L14.2928932,4.29289322 C14.6533772,3.93240926 15.2206082,3.90467972 15.6128994,4.20970461 Z", id: "Combined-Shape", transform: "translate(13.000000, 13.000000) rotate(-180.000000) translate(-13.000000, -13.000000) " }) })) }))),
    }); },
    ArrowRightOn: function () { return ({
        height: 26,
        width: 26,
        viewBox: '0 0 26 26',
        icon: (jsx("g", __assign$1({ id: "Icons", stroke: "none", strokeWidth: "1", fill: "none", fillRule: "evenodd" }, { children: jsx("g", __assign$1({ id: "ic/arrow/blue/right/on", fill: "#16181A" }, { children: jsx("path", { d: "M15.6128994,4.20970461 L15.7071068,4.29289322 L23.7071068,12.2928932 C24.0675907,12.6533772 24.0953203,13.2206082 23.7902924,13.6128994 L23.7071068,13.7071068 L15.7071068,21.7071068 C15.3165825,22.0976311 14.6834175,22.0976311 14.2928932,21.7071068 C13.9324093,21.3466228 13.9046797,20.7793918 14.2097046,20.3871006 L14.2928932,20.2928932 L20.585,14 L3,14 C2.44771525,14 2,13.5522847 2,13 C2,12.4477153 2.44771525,12 3,12 L20.585,12 L14.2928932,5.70710678 C13.9324093,5.34662282 13.9046797,4.77939176 14.2097046,4.38710056 L14.2928932,4.29289322 C14.6533772,3.93240926 15.2206082,3.90467972 15.6128994,4.20970461 Z", id: "Combined-Shape" }) })) }))),
    }); },
    TriangleLeft: function () { return ({
        height: 26,
        width: 26,
        viewBox: '0 0 4 8',
        icon: (jsx("g", __assign$1({ id: "Icons", stroke: "none", strokeWidth: "1", fill: "none", fillRule: "evenodd" }, { children: jsx("g", __assign$1({ id: "ic/triangle/left", fill: "#001487", transform: "rotate(-90 2 4)" }, { children: jsx("path", { d: "m2 2 4 4h-8z" }) })) }))),
    }); },
    TriangleRight: function () { return ({
        height: 26,
        width: 26,
        viewBox: '0 0 4 8',
        icon: (jsx("g", __assign$1({ id: "Icons", stroke: "none", strokeWidth: "1", fill: "none", fillRule: "evenodd" }, { children: jsx("g", __assign$1({ id: "ic/triangle/left", fill: "#001487", transform: "rotate(90 2 4)" }, { children: jsx("path", { d: "m2 2 4 4h-8z" }) })) }))),
    }); },
};

var Buttons = {
    ButtonClose: function () { return ({
        height: 26,
        width: 26,
        viewBox: '0 0 26 26',
        icon: (jsx("g", __assign$1({ id: "ic/close/small", stroke: "none", strokeWidth: "1", fill: "none", fillRule: "evenodd" }, { children: jsx("path", { d: "M8.11289944,6.70970461 L8.20710678,6.79289322 L13,11.585 L17.7928932,6.79289322 L17.8871006,6.70970461 C18.2793918,6.40467972 18.8466228,6.43240926 19.2071068,6.79289322 C19.5976311,7.18341751 19.5976311,7.81658249 19.2071068,8.20710678 L19.2071068,8.20710678 L14.415,13 L19.2071068,17.7928932 C19.5976311,18.1834175 19.5976311,18.8165825 19.2071068,19.2071068 C18.8466228,19.5675907 18.2793918,19.5953203 17.8871006,19.2902954 L17.7928932,19.2071068 L13,14.415 L8.20710678,19.2071068 L8.11289944,19.2902954 C7.72060824,19.5953203 7.15337718,19.5675907 6.79289322,19.2071068 C6.40236893,18.8165825 6.40236893,18.1834175 6.79289322,17.7928932 L6.79289322,17.7928932 L11.585,13 L6.79289322,8.20710678 C6.40236893,7.81658249 6.40236893,7.18341751 6.79289322,6.79289322 C7.15337718,6.43240926 7.72060824,6.40467972 8.11289944,6.70970461 Z", id: "Combined-Shape", fill: "#16181A" }) }))),
    }); },
    ButtonCloseBig: function () { return ({
        height: 26,
        width: 26,
        viewBox: '0 0 26 26',
        icon: (jsx("g", { children: jsx("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M21.2929 3.29289C21.6834 2.90237 22.3166 2.90237 22.7071 3.29289C23.0976 3.68342 23.0976 4.31658 22.7071 4.70711L14.4142 13L22.7071 21.2929C23.0976 21.6834 23.0976 22.3166 22.7071 22.7071C22.3166 23.0976 21.6834 23.0976 21.2929 22.7071L13 14.4142L4.70711 22.7071C4.31658 23.0976 3.68342 23.0976 3.29289 22.7071C2.90237 22.3166 2.90237 21.6834 3.29289 21.2929L11.5858 13L3.29289 4.70711C2.90237 4.31658 2.90237 3.68342 3.29289 3.29289C3.68342 2.90237 4.31658 2.90237 4.70711 3.29289L13 11.5858L21.2929 3.29289Z", fill: "#16181A" }) })),
    }); },
    ButtonModalClose: function () { return ({
        height: 26,
        width: 26,
        viewBox: '0 0 26 26',
        icon: (jsx("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M21.2929 3.29289C21.6834 2.90237 22.3166 2.90237 22.7071 3.29289C23.0976 3.68342 23.0976 4.31658 22.7071 4.70711L14.4142 13L22.7071 21.2929C23.0976 21.6834 23.0976 22.3166 22.7071 22.7071C22.3166 23.0976 21.6834 23.0976 21.2929 22.7071L13 14.4142L4.70711 22.7071C4.31658 23.0976 3.68342 23.0976 3.29289 22.7071C2.90237 22.3166 2.90237 21.6834 3.29289 21.2929L11.5858 13L3.29289 4.70711C2.90237 4.31658 2.90237 3.68342 3.29289 3.29289C3.68342 2.90237 4.31658 2.90237 4.70711 3.29289L13 11.5858L21.2929 3.29289Z", fill: "#16181A" })),
    }); },
    ButtonHelp: function () { return ({
        height: 26,
        width: 26,
        viewBox: '0 0 26 26',
        icon: (jsx("g", __assign$1({ id: "ic/help/black", stroke: "none", strokeWidth: "1", fill: "none", fillRule: "evenodd" }, { children: jsx("path", { d: "M13,1 C19.627417,1 25,6.372583 25,13 C25,19.627417 19.627417,25 13,25 C11.0153127,25 9.1431589,24.5181868 7.4941254,23.6651471 C7.10975545,23.4663138 6.47994181,23.6594641 5.758727,23.9516383 L4.73306543,24.3839231 L4.31051817,24.5542313 C3.2534781,24.9627333 2.2087589,25.2087054 1.56696429,24.5492932 C0.905113588,23.8692743 1.11350676,22.6759603 1.46285915,21.4746566 L1.60838925,20.9949068 L1.90306822,20.0615968 L2.03147298,19.6224097 C2.20683373,18.982753 2.29291715,18.4372388 2.13219775,18.095013 C1.40590265,16.5484858 1,14.8216447 1,13 C1,6.372583 6.372583,1 13,1 Z M13,3 C7.4771525,3 3,7.4771525 3,13 C3,14.4895022 3.32457074,15.9290533 3.94250313,17.2448398 C4.26387353,17.9291458 4.28240305,18.6041936 4.13465052,19.4030879 L4.04675048,19.8200412 L3.96542458,20.1423897 L3.80532003,20.679355 L3.47940524,21.713409 L3.39814927,21.9823931 C3.31327991,22.270732 3.24980473,22.5170077 3.20644272,22.7259612 L3.185,22.832 L3.19897718,22.8288793 C3.29148698,22.7986901 3.39279625,22.7629988 3.50309478,22.7215923 L3.67530824,22.6551695 L4.21901841,22.4301811 L5.21666615,22.0126811 L5.4668417,21.9179065 L5.69385746,21.8416474 L5.86080411,21.7901619 C6.79042806,21.5092875 7.59036336,21.4631794 8.41304884,21.8887519 C9.81813814,22.6156001 11.379302,23 13,23 C18.5228475,23 23,18.5228475 23,13 C23,7.4771525 18.5228475,3 13,3 Z M13,17.933456 C13.5522847,17.933456 14,18.3811713 14,18.933456 C14,19.4857408 13.5522847,19.933456 13,19.933456 C12.4477153,19.933456 12,19.4857408 12,18.933456 C12,18.3811713 12.4477153,17.933456 13,17.933456 Z M16.7510129,9.88025663 C16.7510129,11.5621487 15.8017793,12.9396383 14.0326822,13.9881948 L13.8616706,14.085456 L13.861876,16.0080468 C13.861876,16.5208826 13.4758358,16.943554 12.9784971,17.0013191 L12.861876,17.0080468 C12.3490402,17.0080468 11.9263688,16.6220066 11.8686037,16.1246679 L11.861876,16.0080468 L11.861876,13.4821912 C11.861876,13.0993107 12.080496,12.7500417 12.4248837,12.5827259 C14.0379309,11.79905 14.7510129,10.9100389 14.7510129,9.88025663 C14.7510129,8.80032819 13.3638403,8.22817108 12.1618466,8.63030329 C11.4591066,8.865408 11.0937812,9.36141202 10.9944372,10.2923569 C10.9358339,10.8415236 10.4431389,11.2392038 9.89397219,11.1806005 C9.34480546,11.1219972 8.94712522,10.6293023 9.00572852,10.0801356 C9.1848438,8.40166092 10.0491811,7.22814608 11.5273072,6.73363259 C13.8533317,5.955451 16.7510129,7.15063676 16.7510129,9.88025663 Z", id: "Combined-Shape", fill: "#16181A" }) }))),
    }); },
    ButtonMenu: function () { return ({
        height: 26,
        width: 26,
        viewBox: '0 0 26 26',
        icon: (jsx("g", __assign$1({ id: "ic/menu/black", stroke: "none", strokeWidth: "1", fill: "none", fillRule: "evenodd" }, { children: jsx("path", { d: "M24,20 C24.5522847,20 25,20.4477153 25,21 C25,21.5522847 24.5522847,22 24,22 L2,22 C1.44771525,22 1,21.5522847 1,21 C1,20.4477153 1.44771525,20 2,20 L24,20 Z M24,12 C24.5522847,12 25,12.4477153 25,13 C25,13.5522847 24.5522847,14 24,14 L2,14 C1.44771525,14 1,13.5522847 1,13 C1,12.4477153 1.44771525,12 2,12 L24,12 Z M24,4 C24.5522847,4 25,4.44771525 25,5 C25,5.55228475 24.5522847,6 24,6 L2,6 C1.44771525,6 1,5.55228475 1,5 C1,4.44771525 1.44771525,4 2,4 L24,4 Z", id: "Combined-Shape", fill: "#16181A" }) }))),
    }); },
    ButtonSearch: function () { return ({
        height: 26,
        width: 26,
        viewBox: '0 0 26 26',
        icon: (jsx("g", __assign$1({ id: "ic/search/gray", stroke: "none", strokeWidth: "1", fill: "none", fillRule: "evenodd" }, { children: jsx("path", { d: "M10.82,2.32 C15.5144204,2.32 19.32,6.12557963 19.32,10.82 C19.32,12.8069834 18.6382183,14.6347282 17.495829,16.0820601 L23.3910678,21.9768542 C23.7815921,22.3673785 23.7815921,23.0005435 23.3910678,23.3910678 C23.0005435,23.7815921 22.3673785,23.7815921 21.9768542,23.3910678 L16.0820601,17.495829 C14.6347282,18.6382183 12.8069834,19.32 10.82,19.32 C6.12557963,19.32 2.32,15.5144204 2.32,10.82 C2.32,6.12557963 6.12557963,2.32 10.82,2.32 Z M10.82,4.32 C7.23014913,4.32 4.32,7.23014913 4.32,10.82 C4.32,14.4098509 7.23014913,17.32 10.82,17.32 C14.4098509,17.32 17.32,14.4098509 17.32,10.82 C17.32,7.23014913 14.4098509,4.32 10.82,4.32 Z", id: "Combined-Shape", fill: "#16181A" }) }))),
    }); },
    ButtonSearchOff: function () { return ({
        height: 26,
        width: 26,
        viewBox: '0 0 26 26',
        icon: (jsxs("g", __assign$1({ id: "ic/search/gray", stroke: "none", strokeWidth: "1", fill: "none", fillRule: "evenodd" }, { children: [jsx("circle", { cx: "13", cy: "13", r: "12", fill: "#D2D4D6" }), jsx("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M16 12C16 14.2091 14.2091 16 12 16C9.79086 16 8 14.2091 8 12C8 9.79086 9.79086 8 12 8C14.2091 8 16 9.79086 16 12ZM15.4765 16.8907C14.4957 17.5892 13.2958 18 12 18C8.68629 18 6 15.3137 6 12C6 8.68629 8.68629 6 12 6C15.3137 6 18 8.68629 18 12C18 13.2958 17.5892 14.4957 16.8907 15.4765L19.7071 18.2929C20.0976 18.6834 20.0976 19.3166 19.7071 19.7071C19.3166 20.0976 18.6834 20.0976 18.2929 19.7071L15.4765 16.8907Z", fill: "white" })] }))),
    }); },
    ButtonTooltip: function () { return ({
        height: 20,
        width: 20,
        viewBox: '0 0 20 20',
        icon: (jsxs("g", __assign$1({ id: "ic/tooltip/blue", stroke: "none", strokeWidth: "1", fill: "none", fillRule: "evenodd" }, { children: [jsx("path", { d: "M10 0C4.514 0 0 4.514 0 10s4.514 10 10 10 10-4.514 10-10S15.486 0 10 0Zm0 2c4.389 0 8 3.611 8 8 0 4.389-3.611 8-8 8-4.389 0-8-3.611-8-8 0-4.389 3.611-8 8-8Z", fill: "#001487" }), jsx("path", { d: "M9.249 11.631h1.144c.147 0 .252-.097.315-.291.007-.07.027-.202.058-.397.047-.218.138-.422.275-.612.136-.191.328-.423.577-.695.35-.373.613-.702.788-.986.175-.284.262-.624.262-1.021 0-.342-.095-.673-.286-.992-.19-.319-.494-.581-.91-.787-.416-.207-.947-.31-1.593-.31-.614 0-1.151.115-1.61.344-.459.23-.809.531-1.05.905-.241.373-.37.774-.385 1.202 0 .086.023.153.07.204a.242.242 0 0 0 .186.076h.993c.17 0 .283-.089.338-.269.155-.754.614-1.131 1.377-1.131.342 0 .634.095.875.286.241.19.346.445.315.764-.029.2-.11.389-.234.548a5.457 5.457 0 0 1-.583.642 7.585 7.585 0 0 0-.811.951 2.036 2.036 0 0 0-.344.963v.373c0 .062.023.117.07.163.047.047.101.07.163.07Zm-.035 2.194h1.132a.284.284 0 0 0 .21-.081.274.274 0 0 0 .082-.199V12.53a.285.285 0 0 0-.082-.21.285.285 0 0 0-.21-.082H9.214a.258.258 0 0 0-.198.088.287.287 0 0 0-.082.204v1.015a.27.27 0 0 0 .082.199.273.273 0 0 0 .198.081Z", fill: "#001487" })] }))),
    }); },
    ButtonInfoBlueOn: function () { return ({
        height: 26,
        width: 26,
        viewBox: '0 0 26 26',
        icon: (jsxs("g", __assign$1({ id: "ic/Info/BlueOn", stroke: "none", strokeWidth: "1", fill: "none", fillRule: "evenodd" }, { children: [jsx("circle", { id: "Oval", cx: "20", cy: "20", r: "19" }), jsx("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M23 13C23 18.5228 18.5228 23 13 23C7.47715 23 3 18.5228 3 13C3 7.47715 7.47715 3 13 3C18.5228 3 23 7.47715 23 13ZM14 9C14 9.55228 13.5523 10 13 10C12.4477 10 12 9.55228 12 9C12 8.44772 12.4477 8 13 8C13.5523 8 14 8.44772 14 9ZM12 12C12 11.4477 12.4477 11 13 11C13.5523 11 14 11.4477 14 12V17C14 17.5523 13.5523 18 13 18C12.4477 18 12 17.5523 12 17V12Z", fill: "#10465B" })] }))),
    }); },
    ButtonInfoBlueOff: function () { return ({
        height: 26,
        width: 26,
        viewBox: '0 0 26 26',
        icon: (jsxs("g", __assign$1({ id: "ic/Info/BlueOff", stroke: "none", strokeWidth: "1", fill: "none", fillRule: "evenodd" }, { children: [jsx("circle", { id: "Oval", cx: "20", cy: "20", r: "19" }), jsx("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M23 13C23 18.5228 18.5228 23 13 23C7.47715 23 3 18.5228 3 13C3 7.47715 7.47715 3 13 3C18.5228 3 23 7.47715 23 13ZM14 9C14 9.55228 13.5523 10 13 10C12.4477 10 12 9.55228 12 9C12 8.44772 12.4477 8 13 8C13.5523 8 14 8.44772 14 9ZM12 12C12 11.4477 12.4477 11 13 11C13.5523 11 14 11.4477 14 12V17C14 17.5523 13.5523 18 13 18C12.4477 18 12 17.5523 12 17V12Z", fill: "#0E5F80" })] }))),
    }); },
    ButtonPlus: function () { return ({
        height: 26,
        width: 26,
        viewBox: '0 0 26 26',
        icon: (jsxs("g", __assign$1({ id: "ic/Info/BlueOff", stroke: "none", strokeWidth: "1", fill: "none", fillRule: "evenodd" }, { children: [jsx("circle", { cx: "13", cy: "13", r: "12", fill: "#A7CB19" }), jsx("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M14 6C14 5.44772 13.5523 5 13 5C12.4477 5 12 5.44772 12 6V12H6C5.44771 12 5 12.4477 5 13C5 13.5523 5.44771 14 6 14H12V20C12 20.5523 12.4477 21 13 21C13.5523 21 14 20.5523 14 20V14H20C20.5523 14 21 13.5523 21 13C21 12.4477 20.5523 12 20 12H14V6Z", fill: "#10465B" })] }))),
    }); },
};

var Check = {
    Check: function () { return ({
        height: 26,
        width: 26,
        viewBox: '0 0 26 26',
        icon: (jsxs("g", __assign$1({ id: "ic/check", stroke: "none", strokeWidth: "1", fill: "none", fillRule: "evenodd" }, { children: [jsx("circle", { id: "Oval", fill: "#A7CB19", cx: "13", cy: "13", r: "12" }), jsx("path", { d: "M18.2525907,7.83564687 C18.6195087,7.4228641 19.2515811,7.38568338 19.6643638,7.75260139 C20.0453941,8.09129495 20.1063847,8.65591884 19.8249223,9.06544478 L19.7474093,9.16437455 L11.7474093,18.1643746 C11.3924567,18.5636962 10.7933569,18.6090795 10.3838354,18.2877788 L10.2928932,18.2071175 L6.29289322,14.2071175 C5.90236893,13.8165932 5.90236893,13.1834282 6.29289322,12.7929039 C6.65337718,12.43242 7.22060824,12.4046904 7.61289944,12.7097153 L7.70710678,12.7929039 L10.957,16.0420107 L18.2525907,7.83564687 Z", id: "Path-18", fill: "#000000" })] }))),
    }); },
    CheckBlue: function () { return ({
        height: 26,
        width: 26,
        viewBox: '0 0 26 26',
        icon: (jsxs("g", __assign$1({ id: "ic/check_blue", stroke: "none", strokeWidth: "1", fill: "none", fillRule: "evenodd" }, { children: [jsx("circle", { cx: "13", cy: "13", r: "12", fill: "#0E5F80" }), jsx("path", { d: "M7 13L11 17L19 9", stroke: "white", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round" })] }))),
    }); },
    CheckField: function () { return ({
        height: 26,
        width: 26,
        viewBox: '0 0 26 26',
        icon: (jsxs("g", __assign$1({ id: "ic/success", stroke: "none", strokeWidth: "1", fill: "none", fillRule: "evenodd" }, { children: [jsx("path", { d: "M13,1 C19.627417,1 25,6.372583 25,13 C25,19.627417 19.627417,25 13,25 C6.372583,25 1,19.627417 1,13 C1,6.372583 6.372583,1 13,1 Z M13,3 C7.4771525,3 3,7.4771525 3,13 C3,18.5228475 7.4771525,23 13,23 C18.5228475,23 23,18.5228475 23,13 C23,7.4771525 18.5228475,3 13,3 Z", id: "Combined-Shape", fill: "#16181A" }), jsx("path", { d: "M16.7407434,8.84922676 C17.1001655,8.42990091 17.7314655,8.38133938 18.1507914,8.74076153 C18.5378614,9.07253582 18.6090139,9.63596933 18.3349748,10.0504994 L18.2592566,10.1508095 L12.2592566,17.1508095 C11.9072421,17.5614931 11.2997604,17.6131922 10.8849856,17.2886948 L10.7928932,17.2071249 L7.79289322,14.2071249 C7.40236893,13.8166006 7.40236893,13.1834356 7.79289322,12.7929113 C8.15337718,12.4324274 8.72060824,12.4046979 9.11289944,12.7097227 L9.20710678,12.7929113 L11.443,15.0280181 L16.7407434,8.84922676 Z", id: "Line", fill: "#16181A" })] }))),
    }); },
};

var Checkbox$3 = {
    CheckboxOnDark: function () { return ({
        height: 26,
        width: 26,
        viewBox: '0 0 26 26',
        icon: (jsxs("g", __assign$1({ id: "ic/checkbox/on", stroke: "none", strokeWidth: "1", fill: "none", fillRule: "evenodd" }, { children: [jsx("rect", { id: "Rectangle", fill: "#0E5F80", x: "1", y: "1", width: "24", height: "24", rx: "6" }), jsx("path", { d: "M16.7407434,8.84922676 C17.1001655,8.42990091 17.7314655,8.38133938 18.1507914,8.74076153 C18.5378614,9.07253582 18.6090139,9.63596933 18.3349748,10.0504994 L18.2592566,10.1508095 L12.2592566,17.1508095 C11.9072421,17.5614931 11.2997604,17.6131922 10.8849856,17.2886948 L10.7928932,17.2071249 L7.79289322,14.2071249 C7.40236893,13.8166006 7.40236893,13.1834356 7.79289322,12.7929113 C8.15337718,12.4324274 8.72060824,12.4046979 9.11289944,12.7097227 L9.20710678,12.7929113 L11.443,15.0280181 L16.7407434,8.84922676 Z", id: "Line", fill: "#FFFFFF" })] }))),
    }); },
    CheckboxOffDark: function () { return ({
        height: 26,
        width: 26,
        viewBox: '0 0 26 26',
        icon: (jsx("g", __assign$1({ id: "ic/checkbox/off", stroke: "none", strokeWidth: "1", fill: "none", fillRule: "evenodd" }, { children: jsx("path", { d: "M19,1 C22.3137085,1 25,3.6862915 25,7 L25,19 C25,22.3137085 22.3137085,25 19,25 L7,25 C3.6862915,25 1,22.3137085 1,19 L1,7 C1,3.6862915 3.6862915,1 7,1 L19,1 Z M19,3 L7,3 C4.85780461,3 3.10892112,4.68396847 3.00489531,6.80035966 L3,7 L3,19 C3,21.1421954 4.68396847,22.8910789 6.80035966,22.9951047 L7,23 L19,23 C21.1421954,23 22.8910789,21.3160315 22.9951047,19.1996403 L23,19 L23,7 C23,4.85780461 21.3160315,3.10892112 19.1996403,3.00489531 L19,3 Z", id: "Rectangle", fill: "#667885" }) }))),
    }); },
    CheckboxOnLight: function () { return ({
        height: 26,
        width: 26,
        viewBox: '0 0 26 26',
        icon: (jsxs("g", __assign$1({ id: "ic/checkbox/onLight", stroke: "none", strokeWidth: "1", fill: "none", fillRule: "evenodd" }, { children: [jsx("rect", { id: "Rectangle", fill: "#79C9D6", x: "1", y: "1", width: "24", height: "24", rx: "6" }), jsx("path", { d: "M16.7407434,8.84922676 C17.1001655,8.42990091 17.7314655,8.38133938 18.1507914,8.74076153 C18.5378614,9.07253582 18.6090139,9.63596933 18.3349748,10.0504994 L18.2592566,10.1508095 L12.2592566,17.1508095 C11.9072421,17.5614931 11.2997604,17.6131922 10.8849856,17.2886948 L10.7928932,17.2071249 L7.79289322,14.2071249 C7.40236893,13.8166006 7.40236893,13.1834356 7.79289322,12.7929113 C8.15337718,12.4324274 8.72060824,12.4046979 9.11289944,12.7097227 L9.20710678,12.7929113 L11.443,15.0280181 L16.7407434,8.84922676 Z", id: "Line", fill: "#10465B" })] }))),
    }); },
    CheckboxOffLight: function () { return ({
        height: 26,
        width: 26,
        viewBox: '0 0 26 26',
        icon: (jsx("g", __assign$1({ id: "ic/checkbox/offLight", stroke: "none", strokeWidth: "1", fill: "none", fillRule: "evenodd" }, { children: jsx("path", { d: "M19,1 C22.3137085,1 25,3.6862915 25,7 L25,19 C25,22.3137085 22.3137085,25 19,25 L7,25 C3.6862915,25 1,22.3137085 1,19 L1,7 C1,3.6862915 3.6862915,1 7,1 L19,1 Z M19,3 L7,3 C4.85780461,3 3.10892112,4.68396847 3.00489531,6.80035966 L3,7 L3,19 C3,21.1421954 4.68396847,22.8910789 6.80035966,22.9951047 L7,23 L19,23 C21.1421954,23 22.8910789,21.3160315 22.9951047,19.1996403 L23,19 L23,7 C23,4.85780461 21.3160315,3.10892112 19.1996403,3.00489531 L19,3 Z", id: "Rectangle", fill: "#F7F7F7" }) }))),
    }); },
};

var Contacts = {
    Email: function () { return ({
        height: 26,
        width: 26,
        viewBox: '0 0 26 26',
        icon: (jsx("g", __assign$1({ id: "ic/40/Contacts/Email", stroke: "none", strokeWidth: "1", fill: "none", fillRule: "evenodd" }, { children: jsx("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M21.5859 6H4.41427L12.293 13.8787C12.6835 14.2692 13.3167 14.2692 13.7072 13.8787L21.5859 6ZM8.58586 13L3 7.41415L3 18.5859L8.58586 13ZM21.5858 20H4.4143L10.0001 14.4142L10.8788 15.2929C12.0503 16.4645 13.9498 16.4645 15.1214 15.2929L16.0001 14.4142L21.5858 20ZM23 18.5857V7.4143L17.4143 13L23 18.5857ZM1 6C1 4.89543 1.89543 4 3 4H23C24.1046 4 25 4.89543 25 6V20C25 21.1046 24.1046 22 23 22H3C1.89543 22 1 21.1046 1 20V6Z", fill: "#16181A" }) }))),
    }); },
    EmailCircle: function () { return ({
        height: 26,
        width: 26,
        viewBox: '0 0 26 26',
        icon: (jsx("g", __assign$1({ id: "ic/40/Contacts/Email", stroke: "none", strokeWidth: "1", fill: "none", fillRule: "evenodd" }, { children: jsx("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M13 25C6.37258 25 1 19.6274 1 13C1 6.37258 6.37258 1 13 1C19.6274 1 25 6.37258 25 13C25 19.6274 19.6274 25 13 25ZM13.1672 12.3265C14.1498 12.3265 18.9272 9.64 18.9272 9.64L18.9349 9.16C18.9349 8.63008 18.5048 8.2 17.9734 8.2H8.36096C7.83008 8.2 7.4 8.63008 7.4 9.16V9.58768C7.4 9.58768 12.2298 12.3265 13.1672 12.3265ZM13.1672 13.6465C12.2298 13.6465 7.4072 11.08 7.40768 11.08L7.4 16.84C7.4 17.3699 7.83056 17.8 8.36096 17.8H17.9734C18.5048 17.8 18.9349 17.3699 18.9349 16.84L18.9272 11.08C18.9272 11.08 14.1949 13.6465 13.1672 13.6465Z", fill: "#0E5F80" }) }))),
    }); },
    Phone: function () { return ({
        height: 26,
        width: 26,
        viewBox: '0 0 26 26',
        icon: (jsx("g", __assign$1({ id: "ic/40/Contacts/Email", stroke: "none", strokeWidth: "1", fill: "none", fillRule: "evenodd" }, { children: jsx("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M21.4708 4.12151C20.2676 2.91833 18.705 2.1495 17.4988 1.68557C16.8867 1.45017 16.3456 1.28544 15.9556 1.17906C15.7601 1.12576 15.6016 1.08683 15.4897 1.06078C15.4338 1.04775 15.3894 1.03793 15.3579 1.03113L15.3204 1.02318L15.3091 1.02085L15.3054 1.0201L15.303 1.01961C14.7614 0.9113 14.2346 1.26252 14.1263 1.80408C14.018 2.3455 14.3694 2.87228 14.9107 2.98077L14.9137 2.9814L14.9362 2.98617C14.9576 2.99077 14.9912 2.99821 15.0361 3.00866C15.1259 3.02957 15.2601 3.06245 15.4293 3.10859C15.7685 3.20108 16.2438 3.34571 16.7808 3.55226C17.8727 3.97221 19.1385 4.61759 20.0566 5.53573C20.9747 6.45386 21.6201 7.71964 22.0401 8.81152C22.2466 9.34853 22.3913 9.82388 22.4837 10.163C22.5299 10.3322 22.5628 10.4664 22.5837 10.5562C22.5941 10.6011 22.6016 10.6348 22.6062 10.6561L22.6109 10.6786L22.6116 10.6816L22.6118 10.6827C22.7206 11.2236 23.2471 11.5743 23.7883 11.4661C24.3298 11.3577 24.681 10.8309 24.5727 10.2894L23.5921 10.4855C24.5727 10.2894 24.5725 10.2883 24.5725 10.2883L24.5722 10.2869L24.5715 10.2832L24.5692 10.2719L24.5612 10.2344C24.5544 10.2029 24.5446 10.1586 24.5316 10.1026C24.5055 9.99076 24.4666 9.8322 24.4133 9.63678C24.3069 9.24671 24.1422 8.7056 23.9068 8.09356C23.4428 6.88734 22.674 5.3247 21.4708 4.12151ZM19.3494 6.24276C18.2091 5.10239 17.2213 4.49526 16.473 4.17456C16.0997 4.01459 15.7903 3.92777 15.557 3.88111C15.4406 3.85782 15.3437 3.84466 15.268 3.83734C15.2302 3.83368 15.1977 3.83148 15.1708 3.83019C15.1573 3.82955 15.1452 3.82914 15.1345 3.82889L15.1196 3.82862L15.1129 3.82856L15.1098 3.82855L15.1083 3.82855C15.1075 3.82855 15.1068 3.82855 15.1068 4.82854V3.82855C14.5545 3.82854 14.1068 4.27626 14.1068 4.82854C14.1068 5.37043 14.5378 5.81165 15.0757 5.82807C15.086 5.82908 15.1161 5.83253 15.1648 5.84227C15.2629 5.8619 15.4397 5.90766 15.6851 6.01286C16.1743 6.22249 16.9543 6.67601 17.9352 7.65697C18.9162 8.63793 19.3697 9.41789 19.5793 9.90705C19.6845 10.1525 19.7303 10.3293 19.7499 10.4274C19.7597 10.4761 19.7631 10.5062 19.7641 10.5165C19.7805 11.0544 20.2218 11.4854 20.7636 11.4854C21.3159 11.4854 21.7636 11.0377 21.7636 10.4854H20.7636C21.7636 10.4854 21.7636 10.4847 21.7636 10.4839L21.7636 10.4824L21.7636 10.4793L21.7636 10.4726L21.7633 10.4577C21.7631 10.4501 21.7629 10.4419 21.7625 10.433C21.7624 10.4292 21.7622 10.4254 21.762 10.4214C21.7607 10.3945 21.7585 10.362 21.7548 10.3242C21.7475 10.2485 21.7344 10.1516 21.7111 10.0352C21.6644 9.80185 21.5776 9.49247 21.4176 9.11921C21.0969 8.37093 20.4898 7.38312 19.3494 6.24276ZM15.5482 7.09216C16.0105 7.349 16.6198 7.7558 17.2281 8.36406C17.8364 8.97232 18.2432 9.58161 18.5 10.0439C18.6287 10.2756 18.7208 10.4723 18.7825 10.6163C18.8134 10.6883 18.8368 10.7475 18.8534 10.7914C18.8617 10.8134 18.8683 10.8317 18.8734 10.8459L18.8798 10.8642L18.8821 10.871L18.8831 10.8738L18.8835 10.8751C18.8836 10.8754 18.8839 10.8763 17.9352 11.1925L18.8839 10.8763C19.0585 11.4002 18.7754 11.9665 18.2514 12.1412C17.7295 12.3152 17.1655 12.0348 16.9885 11.5147L16.9825 11.4982C16.9756 11.4801 16.9631 11.448 16.9442 11.4041C16.9065 11.3161 16.8439 11.1813 16.7517 11.0152C16.5666 10.682 16.2663 10.2307 15.8139 9.77827C15.3615 9.32588 14.9101 9.02557 14.5769 8.84047C14.4109 8.74821 14.2761 8.68564 14.188 8.64791C14.1441 8.62908 14.1121 8.61655 14.0939 8.60969L14.0775 8.60363C13.5573 8.42669 13.277 7.86268 13.451 7.34073C13.6256 6.81678 14.1919 6.53362 14.7159 6.70827L14.3997 7.65695C14.7159 6.70827 14.7156 6.70817 14.7159 6.70827L14.7183 6.70908L14.7211 6.71003L14.728 6.71236L14.7462 6.71876C14.7605 6.7238 14.7787 6.73043 14.8007 6.73875C14.8447 6.75536 14.9038 6.77874 14.9759 6.80962C15.1199 6.87133 15.3165 6.96344 15.5482 7.09216ZM2.92237 4.30529C1.62864 5.59902 1.10976 7.5426 1.81924 9.31777C2.80526 11.7848 4.71806 15.8437 7.59693 18.7226C10.4758 21.6015 14.5347 23.5143 17.0018 24.5003C18.7769 25.2098 20.7205 24.6909 22.0143 23.3972L23.8604 21.551C24.6414 20.77 24.6414 19.5037 23.8604 18.7226L20.3249 15.1871C19.5438 14.406 18.2775 14.406 17.4964 15.1871L15.4494 17.2341C15.1973 17.1206 14.8516 16.9336 14.4191 16.6448C13.6822 16.1526 12.7896 15.43 11.8396 14.48C10.8895 13.5299 10.1669 12.6373 9.67479 11.9005C9.38594 11.468 9.1989 11.1223 9.08549 10.8701L11.1325 8.82312C11.9135 8.04207 11.9135 6.77574 11.1325 5.99469L7.59693 2.45916C6.81588 1.67811 5.54955 1.67811 4.76851 2.45916L2.92237 4.30529ZM3.67641 8.57551C3.29364 7.6178 3.54927 6.50682 4.33659 5.7195L6.18272 3.87337L9.71825 7.4089L7.24338 9.88378C7.02081 10.1063 6.91602 10.4207 6.96054 10.7323C7.05297 11.3794 7.46358 12.1907 8.01163 13.0113C8.58606 13.8713 9.39549 14.8643 10.4254 15.8942C11.4552 16.924 12.4482 17.7335 13.3083 18.3079C14.1288 18.856 14.9402 19.2666 15.5872 19.359C15.8988 19.4035 16.2132 19.2987 16.4358 19.0762L18.9106 16.6013L22.4462 20.1368L20.6 21.983C19.8127 22.7703 18.7017 23.0259 17.744 22.6431C15.3421 21.6831 11.5945 19.8918 9.01115 17.3084C6.42775 14.725 4.63641 10.9775 3.67641 8.57551Z", fill: "#16181A" }) }))),
    }); },
};

var Dots$1 = {
    BulletPoint: function () { return ({
        height: 26,
        width: 26,
        viewBox: '0 0 26 26',
        icon: (jsx("g", __assign$1({ id: "ic/bulletpoint", stroke: "none", strokeWidth: "1", fill: "none", fillRule: "evenodd" }, { children: jsx("circle", { id: "Oval", fill: "currentColor", cx: "13", cy: "13", r: "3" }) }))),
    }); },
    Elliipsis: function () { return ({
        height: 26,
        width: 26,
        viewBox: '0 0 26 26',
        icon: (jsx("g", __assign$1({ id: "ic/other", stroke: "none", strokeWidth: "1", fill: "none", fillRule: "evenodd" }, { children: jsx("path", { d: "M5,10 C6.65685425,10 8,11.3431458 8,13 C8,14.6568542 6.65685425,16 5,16 C3.34314575,16 2,14.6568542 2,13 C2,11.3431458 3.34314575,10 5,10 Z M21,10 C22.6568542,10 24,11.3431458 24,13 C24,14.6568542 22.6568542,16 21,16 C19.3431458,16 18,14.6568542 18,13 C18,11.3431458 19.3431458,10 21,10 Z M13,10 C14.6568542,10 16,11.3431458 16,13 C16,14.6568542 14.6568542,16 13,16 C11.3431458,16 10,14.6568542 10,13 C10,11.3431458 11.3431458,10 13,10 Z M5,12 C4.44771525,12 4,12.4477153 4,13 C4,13.5522847 4.44771525,14 5,14 C5.55228475,14 6,13.5522847 6,13 C6,12.4477153 5.55228475,12 5,12 Z M21,12 C20.4477153,12 20,12.4477153 20,13 C20,13.5522847 20.4477153,14 21,14 C21.5522847,14 22,13.5522847 22,13 C22,12.4477153 21.5522847,12 21,12 Z M13,12 C12.4477153,12 12,12.4477153 12,13 C12,13.5522847 12.4477153,14 13,14 C13.5522847,14 14,13.5522847 14,13 C14,12.4477153 13.5522847,12 13,12 Z", id: "Combined-Shape", fill: "#16181A" }) }))),
    }); },
};

var Errors = {
    Error: function () { return ({
        height: 26,
        width: 26,
        viewBox: '0 0 26 26',
        icon: (jsx("g", __assign$1({ id: "Icons", stroke: "none", strokeWidth: "1", fill: "none", fillRule: "evenodd" }, { children: jsx("g", __assign$1({ id: "ic/error/red", fill: "#C23644" }, { children: jsx("path", { d: "M13,3 C18.5228475,3 23,7.4771525 23,13 C23,18.5228475 18.5228475,23 13,23 C7.4771525,23 3,18.5228475 3,13 C3,7.4771525 7.4771525,3 13,3 Z M13,5 C8.581722,5 5,8.581722 5,13 C5,17.418278 8.581722,21 13,21 C17.418278,21 21,17.418278 21,13 C21,8.581722 17.418278,5 13,5 Z M13,11 C13.5522847,11 14,11.4477153 14,12 L14,17 C14,17.5522847 13.5522847,18 13,18 C12.4477153,18 12,17.5522847 12,17 L12,12 C12,11.4477153 12.4477153,11 13,11 Z M13,8 C13.5522847,8 14,8.44771525 14,9 C14,9.55228475 13.5522847,10 13,10 C12.4477153,10 12,9.55228475 12,9 C12,8.44771525 12.4477153,8 13,8 Z", id: "Combined-Shape" }) })) }))),
    }); },
    ErrorCross: function () { return ({
        height: 26,
        width: 26,
        viewBox: '0 0 26 26',
        icon: (jsxs("g", __assign$1({ id: "Icons", stroke: "none", strokeWidth: "1", fill: "none", fillRule: "evenodd" }, { children: [jsx("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M13 23C18.5228 23 23 18.5228 23 13C23 7.47715 18.5228 3 13 3C7.47715 3 3 7.47715 3 13C3 18.5228 7.47715 23 13 23ZM13 25C19.6274 25 25 19.6274 25 13C25 6.37258 19.6274 1 13 1C6.37258 1 1 6.37258 1 13C1 19.6274 6.37258 25 13 25Z", fill: "#16181A" }), jsx("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M16.2929 8.29289C16.6834 7.90237 17.3166 7.90237 17.7071 8.29289C18.0976 8.68342 18.0976 9.31658 17.7071 9.70711L14.4142 13L17.7071 16.2929C18.0976 16.6834 18.0976 17.3166 17.7071 17.7071C17.3166 18.0976 16.6834 18.0976 16.2929 17.7071L13 14.4142L9.70709 17.7071C9.31657 18.0976 8.6834 18.0976 8.29288 17.7071C7.90235 17.3166 7.90235 16.6834 8.29288 16.2929L11.5858 13L8.29288 9.70711C7.90236 9.31658 7.90236 8.68342 8.29288 8.29289C8.68341 7.90237 9.31657 7.90237 9.7071 8.29289L13 11.5858L16.2929 8.29289Z", fill: "#16181A" })] }))),
    }); },
    ErrorForm: function () { return ({
        height: 94,
        width: 94,
        viewBox: '0 0 94 94',
        icon: (jsx("g", __assign$1({ id: "Desktop", stroke: "none", strokeWidth: "1", fill: "none", fillRule: "evenodd" }, { children: jsx("g", __assign$1({ id: "01.2_Desktop_Home_Sent_Error", transform: "translate(-674.000000, -307.000000)" }, { children: jsx("g", __assign$1({ id: "Group-19", transform: "translate(476.000000, 257.000000)" }, { children: jsxs("g", __assign$1({ id: "Group", transform: "translate(194.000000, 48.000000)" }, { children: [jsx("circle", { cx: "76.5", cy: "22.5", r: "20.5", fill: "#FCDEE1" }), jsx("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M60.0018 18.0075C60.7466 17.5789 61.7 17.8818 62.0595 18.6613L64.6784 24.3426C64.9561 24.9446 65.6054 25.2842 66.2601 25.1691L72.4508 24.0804C73.2989 23.9312 74.0915 24.542 74.1612 25.398L74.6663 31.607C74.7199 32.269 75.2158 32.811 75.8715 32.9251L82.0666 34.0031C82.9171 34.1511 83.454 34.9982 83.2235 35.828L81.5619 41.8128C81.3836 42.4551 81.6647 43.1362 82.2449 43.467L87.6917 46.5746C88.4433 47.0034 88.6581 47.9862 88.1544 48.6884L84.533 53.7328C84.1428 54.2763 84.1735 55.0149 84.607 55.5245L88.6528 60.2788C89.214 60.9385 89.079 61.9378 88.3625 62.4257L83.2172 65.9299C82.6628 66.3075 82.4378 67.0124 82.6711 67.6402L84.836 73.4672C85.1376 74.2786 84.6679 75.1717 83.8276 75.3859L77.77 76.93C77.1211 77.0957 76.6675 77.6807 76.6705 78.3493L76.6992 84.5528C76.7031 85.4167 75.9578 86.094 75.096 86.0104L68.8503 85.4031C68.1853 85.3385 67.5613 85.7316 67.3343 86.3583L65.2186 92.199C64.9254 93.0089 63.9959 93.3903 63.2165 93.0201L57.5377 90.3255C56.9365 90.0403 56.2178 90.1967 55.79 90.7054L51.7772 95.4791C51.224 96.1369 50.223 96.1778 49.6178 95.567L45.1992 91.1072C44.7322 90.6359 44.0052 90.5383 43.4296 90.8691L37.9984 93.9928C37.2532 94.4213 36.2999 94.1182 35.9406 93.3388L33.3216 87.6575C33.0441 87.0556 32.3943 86.7159 31.74 86.8311L25.5492 87.9199C24.7012 88.0691 23.9084 87.4583 23.8388 86.6022L23.3338 80.3933C23.28 79.7311 22.7843 79.1893 22.1284 79.0751L15.9334 77.997C15.0829 77.8488 14.5458 77.0019 14.7762 76.1721L16.4379 70.1871C16.6162 69.5448 16.3351 68.8641 15.7551 68.5332L10.3083 65.4256C9.55669 64.9966 9.34159 64.0139 9.84564 63.3117L13.4668 58.2673C13.8569 57.7238 13.8264 56.9853 13.3928 56.4756L9.34728 51.7212C8.78596 51.0616 8.92101 50.0623 9.63742 49.5744L14.7828 46.0701C15.3369 45.6927 15.5622 44.9874 15.3291 44.3599L13.1641 38.5329C12.8626 37.7216 13.332 36.8286 14.1725 36.6142L20.2298 35.0699C20.8791 34.9043 21.3324 34.3195 21.3293 33.651L21.3008 27.447C21.2969 26.5835 22.0424 25.9059 22.9041 25.9897L29.1495 26.5971C29.8145 26.6618 30.4389 26.2684 30.6659 25.6416L32.7812 19.8009C33.0745 18.9911 34.004 18.61 34.7836 18.9799L40.4622 21.6745C41.0635 21.9598 41.7822 21.8036 42.21 21.2947L46.2227 16.5211C46.776 15.863 47.7769 15.8222 48.3822 16.4331L52.8008 20.8928C53.2678 21.3641 53.9948 21.462 54.5703 21.1309L60.0018 18.0075Z", fill: "#E85D6A" }), jsx("path", { d: "M12.0491 26.3573L5.87235 26.1869", stroke: "#F48122", strokeWidth: "4", strokeLinecap: "round" }), jsx("path", { d: "M22.4729 9.96477L23.5601 16.3399", stroke: "#F48122", strokeWidth: "4", strokeLinecap: "round" }), jsx("path", { d: "M10.615 11.4916L18.2024 22.2102", stroke: "#F48122", strokeWidth: "4", strokeLinecap: "round" }), jsx("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M67.1542 36.7606C66.0645 35.7102 64.34 35.7531 63.3025 36.8562L50 51.0003L36.6975 36.8562C35.66 35.7531 33.9355 35.7102 32.8458 36.7606C31.798 37.7705 31.7186 39.4234 32.6364 40.5298L32.7513 40.6598L46.2382 55L32.7513 69.3402L32.6364 69.4702C31.7186 70.5766 31.798 72.2295 32.8458 73.2394C33.9355 74.2898 35.66 74.2469 36.6975 73.1438L50 58.9997L63.3025 73.1438C64.34 74.2469 66.0645 74.2898 67.1542 73.2394C68.202 72.2295 68.2814 70.5766 67.3636 69.4702L67.2487 69.3402L53.7618 55L67.2487 40.6598L67.3636 40.5298C68.2814 39.4234 68.202 37.7705 67.1542 36.7606Z", fill: "white" })] })) })) })) }))),
    }); },
    Warning: function () { return ({
        height: 94,
        width: 94,
        viewBox: '0 0 85 85',
        icon: (jsx("g", __assign$1({ "data-name": "Layer 2" }, { children: jsxs("g", __assign$1({ "data-name": "Layer 1" }, { children: [jsx("circle", { cx: "42.52", cy: "42.52", r: "42.52", fill: "none" }), jsx("path", { d: "M38.121 25.715 20.92 55.509a5.079 5.079 0 0 0 4.398 7.618H59.72a5.079 5.079 0 0 0 4.399-7.618L46.918 25.715a5.079 5.079 0 0 0-8.797 0z", fill: "none", stroke: "#E64117", strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: 4 }), jsx("path", { fill: "#E64117", d: "M43.913 50.928a.556.556 0 0 1 .58.58v2.066a.602.602 0 0 1-.163.43.544.544 0 0 1-.417.173H41.15a.594.594 0 0 1-.605-.603v-2.066a.547.547 0 0 1 .174-.418.61.61 0 0 1 .43-.162zm-.023-13.813a.556.556 0 0 1 .58.58v10.911a.558.558 0 0 1-.58.581h-2.74a.574.574 0 0 1-.405-.163.54.54 0 0 1-.174-.418v-10.91a.54.54 0 0 1 .174-.418.574.574 0 0 1 .406-.163z" }), jsxs("g", { children: [jsx("path", { d: "M68.779 31.807a31.096 31.096 0 0 0-9.894-9.894M26.155 21.912a31.095 31.095 0 0 0-9.894 9.895", fill: "none", stroke: "#E64117", strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: 4 }), jsx("g", { children: jsx("path", { d: "M63.58 35.046a24.94 24.94 0 0 0-7.935-7.935M29.394 27.11a24.94 24.94 0 0 0-7.935 7.936", fill: "none", stroke: "#E64117", strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: 4 }) })] })] })) }))),
    }); },
    WarningTriangle: function () { return ({
        height: 26,
        width: 26,
        viewBox: '0 0 26 26',
        icon: (jsx("svg", __assign$1({ width: "26", height: "26", viewBox: "0 0 26 26", fill: "none", xmlns: "http://www.w3.org/2000/svg" }, { children: jsx("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M15.59 4.506c-1.149-2.008-4.031-2.008-5.18 0L2.402 18.502C1.257 20.503 2.694 23 4.992 23h16.016c2.298 0 3.735-2.497 2.59-4.498L15.59 4.506zm-3.453.997a.993.993 0 0 1 1.726 0L21.872 19.5a1 1 0 0 1-.864 1.5H4.992a1 1 0 0 1-.864-1.5l8.009-13.996zM12 10a1 1 0 1 1 2 0v5a1 1 0 1 1-2 0v-5zm2 8a1 1 0 1 1-2 0 1 1 0 0 1 2 0z", fill: "#16181A" }) }))),
    }); },
};

var Flags = {
    FlagsChange: function () { return ({
        height: 26,
        width: 26,
        viewBox: '0 0 26 26',
        icon: (jsx("g", __assign$1({ id: "ic/flags/language/black", stroke: "none", strokeWidth: "1", fill: "none", fillRule: "evenodd" }, { children: jsx("path", { d: "M18,11.998 L19,12 C19.5522847,12 20,12.4477153 20,13 C20,13.5522847 19.5522847,14 19,14 C18.9999294,14.1038015 18.9837243,14.2088487 18.949706,14.312321 C18.6293273,15.2868064 18.2416378,16.1350613 17.7880129,16.8514689 C18.4243433,17.4038967 19.1598461,17.6671267 20,17.6671267 C20.5522847,17.6671267 21,18.114842 21,18.6671267 C21,19.2194115 20.5522847,19.6671267 20,19.6671267 C18.6429319,19.6671267 17.4727401,19.2288934 16.4965549,18.3815419 C15.5209493,19.2291803 14.3510796,19.6671267 12.9944559,19.6671267 C12.4421712,19.6671267 11.9944559,19.2194115 11.9944559,18.6671267 C11.9944559,18.114842 12.4421712,17.6671267 12.9944559,17.6671267 C13.8346098,17.6671267 14.5701126,17.4038967 15.2071073,16.8523521 C14.7528181,16.1350613 14.3651286,15.2868064 14.0447499,14.312321 C14.0107317,14.2088487 13.9945265,14.1038015 13.9944561,14.0005019 L13.977,13.999 L16,14 L16.0884932,13.9980773 C16.059155,13.9993547 16.0296529,14 16,14 L10,14 L5.5547002,16.9635332 C5.09517151,17.2698857 4.47430216,17.1457118 4.16794971,16.6861831 C4.05843803,16.5219156 4,16.3289079 4,16.1314829 L4,14 L3,14 C1.8954305,14 1,13.1045695 1,12 L1,3 C1,1.8954305 1.8954305,1 3,1 L16,1 C17.1045695,1 18,1.8954305 18,3 L18,8.999 L23,9 C24.0543618,9 24.9181651,9.81587779 24.9945143,10.8507377 L25,11 L25,20 C25,21.0543618 24.1841222,21.9181651 23.1492623,21.9945143 L23,22 L22,22 L22,24.1314829 C22,24.3289079 21.941562,24.5219156 21.8320503,24.6861831 C21.5475802,25.1128883 20.9919194,25.2504355 20.5460663,25.0226252 L20.4452998,24.9635332 L16,22 L10,22 C8.9456382,22 8.08183488,21.1841222 8.00548574,20.1492623 L8,20 L8,15.333 L10,14 L10,20 L16,20 L16.1685387,20.0071127 C16.4368814,20.0298048 16.6978023,20.1064631 16.9356169,20.2323387 L17.1094004,20.3358994 L20,22.262 L20,22 L20.0054857,21.8507377 C20.0780174,20.8676208 20.8612265,20.0821352 21.8433495,20.0060445 L22,20 L23,20 L23,11 L18,10.999 L18,11.998 Z M16.9424362,14.0012939 L16.0520197,14.0012939 C16.1907365,14.3907199 16.3390021,14.7473331 16.4970225,15.0719745 C16.6554538,14.7473331 16.8037194,14.3907199 16.9424362,14.0012939 Z M16,3 L3,3 L3,12 L4,12 C5.0543618,12 5.91816512,12.8158778 5.99451426,13.8507377 L6,14 L6,14.262 L8.89059961,12.3358994 C9.17220107,12.1481651 9.49603299,12.0354778 9.8314613,12.0071127 L10,12 L16,12 L16,3 Z M10.3254526,4.43473122 L10.3898178,4.54368399 L12.9539158,9.54368399 C13.2059322,10.0351168 13.0118469,10.6378014 12.5204141,10.8898178 C12.0640836,11.123833 11.5118268,10.9731993 11.2334828,10.5570176 L11.1742803,10.456316 L10.9399375,9.99999973 L8.11193755,9.99999973 L7.88709294,10.4481923 C7.65724389,10.9066353 7.12271019,11.1114567 6.65223077,10.9401917 L6.54496349,10.8939372 C6.08652046,10.6640881 5.88169907,10.1295544 6.05296405,9.65907502 L6.09921856,9.55180774 L8.60606281,4.55180774 C8.95374919,3.85833351 9.90823639,3.81757119 10.3254526,4.43473122 Z M9.51,7.21 L9.11393755,7.99999973 L9.91493755,7.99999973 L9.51,7.21 Z", id: "Combined-Shape", fill: "#16181A" }) }))),
    }); },
    FlagEstonia: function () { return ({
        height: 26,
        width: 26,
        viewBox: '0 0 26 26',
        icon: (jsxs(Fragment, { children: [jsx("path", { fill: "#5DA8F1", d: "M23.6 10H2.4a11 11 0 0 1 21.2 0Z" }), jsx("path", { fill: "#fff", d: "M23.6 16H2.4a11 11 0 0 0 21.2 0Z" }), jsx("path", { fill: "#262626", fillRule: "evenodd", d: "M23.6 16a11 11 0 0 0 0-6H2.4a11 11 0 0 0 0 6h21.2Z", clipRule: "evenodd" })] })),
    }); },
    FlagLithuania: function () { return ({
        height: 26,
        width: 26,
        viewBox: '0 0 26 26',
        icon: (jsxs(Fragment, { children: [jsx("path", { fill: "#006B43", fillRule: "evenodd", d: "M23.3 17a11 11 0 0 0 0-8H2.7a11 11 0 0 0 0 8h20.6Z", clipRule: "evenodd" }), jsx("path", { fill: "#C32427", fillRule: "evenodd", d: "M23.3 17a11 11 0 0 1-20.6 0h20.6Z", clipRule: "evenodd" }), jsx("path", { fill: "#FFBA00", fillRule: "evenodd", d: "M23.3 9H2.7a11 11 0 0 1 20.6 0Z", clipRule: "evenodd" })] })),
    }); },
    FlagLatvia: function () { return ({
        height: 26,
        width: 26,
        viewBox: '0 0 26 26',
        icon: (jsxs(Fragment, { children: [jsx("path", { fill: "#B9414B", fillRule: "evenodd", d: "M23.6 16H2.4a11 11 0 0 0 21.2 0Zm0-6H2.4a11 11 0 0 1 21.2 0Z", clipRule: "evenodd" }), jsx("path", { fill: "#fff", fillRule: "evenodd", d: "M23.6 16a11 11 0 0 0 0-6H2.4a11 11 0 0 0 0 6h21.2Z", clipRule: "evenodd" })] })),
    }); },
    FlagGlobal: function () { return ({
        height: 26,
        width: 26,
        viewBox: '0 0 26 26',
        icon: (jsx("g", __assign$1({ id: "ic/flags/global", stroke: "none", strokeWidth: "1", fill: "none", fillRule: "evenodd" }, { children: jsx("path", { d: "M13,1 C19.627417,1 25,6.372583 25,13 C25,19.627417 19.627417,25 13,25 C6.372583,25 1,19.627417 1,13 C1,6.372583 6.372583,1 13,1 Z M22.9505349,14.0009045 L19.9507245,14.0006245 C19.6889773,16.9080274 18.2865799,19.7783069 15.7751238,22.6095192 C19.6387126,21.4962527 22.5416373,18.1154947 22.9505349,14.0009045 Z M6.04500012,14.0006245 L3.04947156,14.0009696 C3.45821085,18.1136948 6.35871811,21.493242 10.2198435,22.6084614 C7.70848338,19.7769532 6.3066856,16.9073418 6.04500012,14.0006245 Z M11.999,14 L8.05528001,14.0004088 C8.33037789,16.4929641 9.63369552,19.0235834 11.9979784,21.5942815 L11.999,14 Z M17.9404446,14.0004088 L14.001,14 L14.0023351,21.5892907 C16.3637156,19.0203081 17.6655248,16.491351 17.9404446,14.0004088 Z M10.2189366,3.39235718 L10.1300997,3.41791853 C6.31352548,4.55936401 3.45457154,7.91877903 3.04937235,12.0000291 L6.0449115,12.0003603 C6.30637288,9.09326302 7.70824165,6.22328098 10.2189366,3.39235718 Z M8.05516976,12.0005906 L11.999,12 L11.9979784,4.40571847 C9.63337951,6.97676019 8.33002951,9.50771243 8.05516976,12.0005906 Z M14.0023351,4.41070928 L14.001,12 L17.9405549,12.0005906 C17.665873,9.50932547 16.3640314,6.98003553 14.0023351,4.41070928 Z M15.7749861,3.39004672 L15.990147,3.63611377 C18.3669844,6.38799284 19.6968532,9.17666852 19.9508131,12.0003603 L22.9506341,12.0000941 C22.542107,7.88505226 19.6390252,4.50383735 15.7749861,3.39004672 Z", id: "Combined-Shape", fill: "#16181A" }) }))),
    }); },
    FlagMexico: function () { return ({
        height: 26,
        width: 26,
        viewBox: '0 0 26 26',
        icon: (jsxs(Fragment, { children: [jsx("mask", __assign$1({ id: "a", width: "22", height: "22", x: "2", y: "2", maskUnits: "userSpaceOnUse" }, { children: jsx("circle", { cx: "13", cy: "13", r: "11", fill: "#D9D9D9" }) })), jsxs("g", __assign$1({ mask: "url(#a)" }, { children: [jsx("path", { fill: "#fff", d: "M9 2h8v22H9z" }), jsx("path", { fill: "#FCCA3E", d: "M14.3 15v.2h.1v-.1Z" }), jsx("path", { fill: "#AA8C30", d: "M14.4 15v.2-.1Zm0 0v.2-.1Z" }), jsx("path", { fill: "#FCCA3E", d: "M14.3 15Z" }), jsx("path", { fill: "#AA8C30", d: "M14.3 15Zm0 0Z" }), jsx("path", { fill: "#FCCA3E", d: "M14.5 15.3h-.1.1Z" }), jsx("path", { fill: "#AA8C30", d: "m14.4 15.2.1.1h-.1Zm0 0 .1.2v-.2h-.1Z" }), jsx("path", { fill: "#FCCA3E", d: "M14.5 15.4h.1Z" }), jsx("path", { fill: "#AA8C30", d: "M14.5 15.3v.1h.1Zm.1 0v.1Z" }), jsx("path", { fill: "#FCCA3E", d: "m15.5 13.6.1.1v-.1h-.1Z" }), jsx("path", { fill: "#AA8C30", d: "M15.5 13.6h.1v.1l-.1-.1Zm0 0h.1-.1Z" }), jsx("path", { fill: "#FCCA3E", d: "M15.5 13.6v-.1.1Z" }), jsx("path", { fill: "#AA8C30", d: "M15.5 13.5Zm0 0Z" }), jsx("path", { fill: "#FCCA3E", d: "M15.8 13.7h-.2.2Z" }), jsx("path", { fill: "#AA8C30", d: "m15.8 13.6-.2.1h.2Zm-.1 0v.1h.1-.1Z" }), jsx("path", { fill: "#FCCA3E", d: "M15.9 13.7v-.1Z" }), jsx("path", { fill: "#AA8C30", d: "M15.9 13.6Zm-.1 0h.1-.1Z" }), jsx("path", { fill: "#FCCA3E", d: "m15.2 14.6-.1-.2.1.2Z" }), jsx("path", { fill: "#AA8C30", d: "m15 14.4.2.2-.2-.2Zm0 0 .2.2-.1-.2Z" }), jsx("path", { fill: "#FCCA3E", d: "M15 14.4Z" }), jsx("path", { fill: "#AA8C30", d: "M15 14.3v.1Zm0 0h.1-.1Z" }), jsx("path", { fill: "#FCCA3E", d: "M15.2 14.6h.1-.1Z" }), jsx("path", { fill: "#AA8C30", d: "M15.2 14.6h.1-.1Zm0 0h.1Z" }), jsx("path", { fill: "#FCCA3E", d: "M15.4 14.7v-.1Z" }), jsx("path", { fill: "#AA8C30", d: "M15.4 14.6Zm0 0Z" }), jsx("path", { fill: "#FCCA3E", d: "M11.7 15h-.1Z" }), jsx("path", { fill: "#AA8C30", d: "M11.6 15Zm0 0Z" }), jsx("path", { fill: "#FCCA3E", d: "M11.7 15.1v.2-.2Z" }), jsx("path", { fill: "#AA8C30", d: "M11.7 15.1v.2-.2Zm0 0v.2-.2Z" }), jsx("path", { fill: "#FCCA3E", d: "M11.7 15v.1h-.1.1Z" }), jsx("path", { fill: "#AA8C30", d: "M11.7 15h-.1v.1h.1Zm0 0h-.1v.1h.1Z" }), jsx("path", { fill: "#FCCA3E", d: "M11.4 15.4v-.1Z" }), jsx("path", { fill: "#AA8C30", d: "M11.3 15.3h.1Zm0 0h.1Z" }), jsx("path", { fill: "#FCCA3E", d: "M11.5 15.3h.2-.2Z" }), jsx("path", { fill: "#AA8C30", d: "M11.6 15.3h-.1.1Zm-.1 0h.2-.2Z" }), jsx("path", { fill: "#FCCA3E", d: "M11.5 15.3v.1c0-.1-.1-.1 0-.2v.1Z" }), jsx("path", { fill: "#AA8C30", d: "M11.4 15.2v.1c0 .1 0 .1 0 0h.1Zm.1.1h-.1v.1h.1v-.1Z" }), jsx("path", { fill: "#FCCA3E", d: "M11 14.4v-.1h-.1Z" }), jsx("path", { fill: "#AA8C30", d: "M10.9 14.3Zm0 0Z" }), jsx("path", { fill: "#FCCA3E", d: "M11 14.4v.2h-.1v-.2Z" }), jsx("path", { fill: "#AA8C30", d: "M10.9 14.4v.2-.2Zm0 0v.2-.2Z" }), jsx("path", { fill: "#FCCA3E", d: "M11 14.4h-.1Z" }), jsx("path", { fill: "#AA8C30", d: "M10.8 14.4a.1.1 0 0 0 .2 0h-.1Zm0 0h.1Z" }), jsx("path", { fill: "#FCCA3E", d: "M10.6 14.6Z" }), jsx("path", { fill: "#AA8C30", d: "M10.6 14.6Zm0 0Z" }), jsx("path", { fill: "#FCCA3E", d: "M10.7 14.6h.2-.2Z" }), jsx("path", { fill: "#AA8C30", d: "M10.9 14.6h-.2.2Zm-.2 0h.2-.2Z" }), jsx("path", { fill: "#FCCA3E", d: "M10.7 14.5v.1Zm-.1-1h-.1v-.1Z" }), jsx("path", { fill: "#AA8C30", d: "M10.5 13.4Zm0 0Zm.1 1.1v.1h.1Zm0 0h.1v.1Z" }), jsx("path", { fill: "#FCCA3E", d: "M10.4 13.7v-.2.2Z" }), jsx("path", { fill: "#AA8C30", d: "m10.5 13.5-.1.2v-.2Zm-.1.2v-.2.2Z" }), jsx("path", { fill: "#FCCA3E", d: "M10.5 13.5Zm-.4 0c0 .1 0 .1 0 0Z" }), jsx("path", { fill: "#AA8C30", d: "M10.4 13.5h.1Zm0 0h.1Zm-.3 0Zm0 0v.1Z" }), jsx("path", { fill: "#FCCA3E", d: "M10.2 13.6h.2v.2l-.2-.2Z" }), jsx("path", { fill: "#AA8C30", d: "M10.2 13.6h.2v.2l-.2-.2Zm0 0 .2.1h-.2Z" }), jsx("path", { fill: "#FCCA3E", d: "M10.2 13.6c0-.1 0 0 0 0Z" }), jsx("path", { fill: "#AA8C30", d: "M10.2 13.5v.1Zm0 .1ZM14.4 15v.1Zm0 0h-.1Zm.1.3h-.1.1Zm.1 0v.1Zm1-1.7h-.1Zm0 0h-.1Zm.3 0h-.1.1ZM15.8 13.6Zm-.7.9Zm0-.1c-.1 0-.1 0 0 0H15s.1 0 0 0Zm.2.2ZM15.4 14.6Zm-3.7.4v.1h-.1.1ZM11.7 15.1Zm-.2.2Zm0 0v.1Zm-.2 0h.1Zm-.3-1v.1h-.2.1Zm0 .2h-.1Zm-.3 0v.1h.2-.2Zm0 0v.1Zm0 .1h-.2.1Zm-.1-1h-.1v-.1ZM10.4 13.6Zm-.2 0 .1.1v-.1Zm0 0Zm0 0H10Z" }), jsx("path", { fill: "#9CA168", d: "M16 13.2c0 .2-.2.2-.2.3l-.1.1v.4h.1l-.3.4h-.2v.1l-.1.1v.1l-.1.1v.3h-.6v.1h-.1v.1l-.2.1.2.1h-.2l-.4.2-.3-.2H13s0-.1.2 0H13.6l.2-.2.2-.1h.1v.1h.2l.1-.1h.1V15l.2-.2.1-.1v-.1.1H15.2v-.1l.1-.1V14l.1-.2.1-.1h.2V13h-.1l.2.2v-.4l.1.1v.2l.1-.1.1.2Zm-2.5 2.5-.2-.2h-.2l.2.1h.1Z" }), jsx("path", { fill: "#717732", d: "m14.3 15.3.2-.1h.1l.3-.2.2-.2v.1l-.3.2h-.2l-.2.2Zm.5-.5-.2.3.2-.2.2-.1v-.1h.1l-.3.1Zm-.3.4V15l.3-.3c.1 0 0-.2 0-.2v.1l-.2.3v.1l-.1.2Zm-.1.3h-.2l-.5.1-.2-.1H13h.3l.3.2.5-.2h.2Zm-1 0 .3-.1.3-.2h.2-.4l-.2.2h-.1Zm0 .1H13h.2l.1.1Zm.1 0h.2c.2 0 .3-.2.4-.3h.2-.1l-.5.2h-.2Zm2.1-1.6-.2.4h-.1l-.1.2v-.1l.3-.2.2-.3h.1s0-.1-.2 0Zm-.3.4.1-.2.1-.3.1-.1c-.2.1-.2.2-.3.4v.2Zm0 .1v-.4l.2-.3h-.2l-.1.3v.4Zm.3-.7v-.2l.2-.2.1-.1V13v.3l-.2.2v-.7V13.7Zm-.1-.9v.4l.1.2v-.4l-.1-.2Z" }), jsx("path", { fill: "#9CA168", d: "M12.5 15.6h.1l.3-.2v.1h-.2v.1h-.2Z" }), jsx("path", { fill: "#9CA168", d: "M12.8 15.4h-.4l-.2-.1v.1l-.2-.1v.1h-.4v.1h.2c.1 0 0 0 0 .1h.1s.1-.1.2 0H12l.1.1.2-.1s-.1 0 0 .1l.2-.1v-.1H12.9v-.1Zm-1 0-.1-.2h-.2V15h-.1v-.2h-.2v-.2l-.1.1-.1-.2v.1c-.1 0-.2 0-.2-.2v-.2h-.1V14h-.1v-.1h-.1l-.1-.2V13.4s.1-.1 0-.2l.1-.2h-.1V13c0-.1 0 0 0 0h-.1v.1H10V13.3l.1.1.1.1v.4c-.1.1 0 .1 0 .2h.2-.2a.1.1 0 0 0 0 .2h.3-.2.1l.1.1.2.2h.2l-.1.1.1.1H11v.2h.3-.2l.1.2s.1-.1.2 0H11.6l.1.2Z" }), jsx("path", { fill: "#717732", d: "M12.6 15.6H13h-.4Zm.3-.1H12.5h.2Zm-2-.8-.2-.2h.1l.2.2Zm1.4 1v-.1l.2-.1v-.1h-.1s0-.2-.2-.2H12s.1.2 0 .2l-.1-.2h-.1v.2l-.1-.1-.2.1h.3-.1.1v.2-.1h.1v-.2l.1.2v.1l.1-.1v-.2.1h.1v.2Zm-.8-.5V14.9v.1l-.1-.2-.1-.1v.1c-.1 0 0-.1 0-.2H11v.1l.1.1v-.2V15H11h.2v.1l.1-.1v-.1.2H11h.2l.1.1h.1Zm-1.1-1.4v-.1l-.1-.2h-.1v-.1l-.1-.1h.2l-.1-.1V13h.2-.1v-.2h.1s0-.1 0 0h-.1v.1H10v.2c-.1 0 0 .2 0 .2l.2.1V13.8Zm.4.7V14s0 .1 0 0V14l-.1-.1-.1.1V14h-.2v.1l.1-.1v.1h-.2.2v.2l.1-.1V14v.2h-.2.2v.1h.1v-.1.1h-.2.2v.1Z" }), jsx("path", { fill: "#fff", d: "M12.8 15.9h-.1v-.2l.2-.2v-.2h.2v.3l.2.2H13v-.1l-.1.1h-.1Z" }), jsx("path", { fill: "#016848", d: "M13 15.3v.3l-.2.1-.1.1h-.1l.1-.1.2-.1v-.2Zm.2.6v-.1H13h.2v.1Z" }), jsx("path", { fill: "#CD202A", d: "M13 15.3h.1v.3l-.2.2v.1h-.1l.1-.2.2-.2v-.2Z" }), jsx("path", { fill: "#CD202A", d: "M13.5 15.8H13v-.2l.3.1v.1Z" }), jsx("path", { fill: "#30C2DC", d: "m14 15-.2-.1.3-.2H12.3l-.2-.3H12v.2l-.5-.3v.1l.2.2v.1s.4 0 .4.2h-.1v.1h.4l.1.2h.8l.3-.1h.2V15Z" }), jsx("path", { fill: "#0872A7", d: "m11.6 14.3.5.2a.5.5 0 0 0 .1 0l.2.1a65.5 65.5 0 0 1 1.6 0 .5.5 0 0 1 .1 0v.1a2.7 2.7 0 0 0-.3.2.5.5 0 0 0 .1 0v.1h-.2l-.2.2h-.1a1.7 1.7 0 0 1-.4 0 3 3 0 0 1-.4 0l-.1-.1h-.1a1.2 1.2 0 0 0-.2 0L12 15l.1-.1-.2-.1a1.6 1.6 0 0 0-.2 0l-.1-.1h.1v-.1h-.1l-.1-.1v-.2Zm0 .1a2 2 0 0 1 .2.2v.1h.2l.1.1.1.1v.1a2.4 2.4 0 0 1 .2 0 .4.4 0 0 1 .2.2h.1a3 3 0 0 0 .7 0h.1l.2-.1a1.5 1.5 0 0 1 .2-.1v-.1h-.1a1.1 1.1 0 0 1 .3-.2H12.3l-.2-.2H12c-.2 0-.4 0-.5-.2v.1Z" }), jsx("path", { fill: "#fff", d: "M11.7 14.8v-.1.1Z" }), jsx("path", { fill: "#0872A7", d: "M11.7 14.7v.1-.1Zm-.1 0a.1.1 0 1 1 .2 0 .1.1 0 0 1-.2 0Z" }), jsx("path", { fill: "#fff", d: "M12.1 14.5v-.2.2Z" }), jsx("path", { fill: "#0872A7", d: "M12.1 14.3v.1Zm0 0a.1.1 0 1 1 .1 0 .1.1 0 0 1-.2 0Z" }), jsx("path", { fill: "#fff", d: "M14 15V15v.1Z" }), jsx("path", { fill: "#0872A7", d: "M14 15Zm-.1 0a.1.1 0 1 1 .2 0 .1.1 0 0 1-.2 0Z" }), jsx("path", { fill: "#F8C83C", d: "m11.3 14.3.2.1c0-.2-.2-.2-.2-.1Z" }), jsx("path", { fill: "#fff", d: "M11.5 14.4v-.1h-.1v.1h.1Z" }), jsx("path", { fill: "#F8C83C", d: "m12.2 15-.1.1h-.2l.1-.1h.2Z" }), jsx("path", { fill: "#fff", d: "M12 15h.1-.2v.1h.2Z" }), jsx("path", { fill: "#F8C83C", d: "M14.1 14.8h.2v-.2h-.2v.2Z" }), jsx("path", { fill: "#fff", d: "M14.1 14.7h.1Z" }), jsx("path", { fill: "#F9AA51", d: "M13 14.6h-.2l.1.3.2.3h.2v-.4l-.2-.2Z" }), jsx("path", { fill: "#953220", d: "m13 14.6.2.1v.4H13l-.2-.2-.1-.3h.1Zm0 0h-.2l.1.3.2.2h.2v-.3l-.2-.2Z" }), jsx("path", { fill: "#F9AA51", d: "M12.6 14.6h.2l.1.1.1.3.1.2H13l-.3-.4v-.2Z" }), jsx("path", { fill: "#953220", d: "M12.8 14.6h-.1v.2l.2.3h.2a3.4 3.4 0 0 1 0-.1l-.1-.1v-.2a.8.8 0 0 0-.2-.1Zm0 0h-.1v.2l.2.4h.2a3.3 3.3 0 0 1 0-.2l-.1-.1v-.2a.8.8 0 0 0-.2-.1Z" }), jsx("path", { fill: "#F9AA51", d: "M12.6 14.6c.1 0 .2 0 .2.2v.2l.2.2h-.4V15h-.1v-.5Zm.8 0H13l.2.4v.2H13.6v-.5Z" }), jsx("path", { fill: "#953220", d: "M13 14.6h.5v.5a1.1 1.1 0 0 1-.2 0h-.1V15l-.2-.3v-.1Zm.4.5h.1v-.4l-.2-.1H13a1.1 1.1 0 0 0 .2.5h.2Zm-.8-.5c.1 0 .2 0 .2.2v.2a.9.9 0 0 0 .2.2m-.4-.6h-.1v.5h.2a.9.9 0 0 0 .3 0m-.4-.5h-.1v.4a.4.4 0 0 1 .1.1h.1a.8.8 0 0 0 .3 0 .9.9 0 0 1-.1-.1v-.1l-.1-.1-.1-.2h-.1Z" }), jsx("path", { fill: "#F9AA51", d: "M12.5 14.6h.1-.2v.4l.1.2.1-.1m.9-.4h-.1v-.1h.2v.4l-.1.2-.1-.1h.1" }), jsx("path", { fill: "#953220", d: "M12.4 14.6h.2-.1V15a.2.2 0 0 1 0-.2v-.1Zm0 .4.1.2.1-.1h-.2V15m0 0h.1m1-.4v.1h-.1v-.1h.2a.2.2 0 0 1 0 .2.2.2 0 0 1 0 .2v.2h-.1l-.1-.1h.1-.1.1a.1.1 0 0 0 0-.1m0 0a.2.2 0 0 0 0-.1v-.1a.2.2 0 0 0 0-.1v-.1m0 .4" }), jsx("path", { fill: "#953220", d: "M12.6 15v.1H13.5l-.1.1h-.6V15Zm.8-.4v.2H13l.3-.2Z" }), jsx("path", { fill: "#231F20", d: "M14 14.6v-.1Zm.4-.1ZM14.5 14.5h-.1.1Zm.2-.2h-.1.1Zm-.3-.4v.1Zm-.2.1v.1Z" }), jsx("path", { fill: "#8CBEBF", d: "m13.9 14.3-.2.1.5.1.4-.2v-.2l-.2-.1-.2.1-.2.2h-.1Z" }), jsx("path", { fill: "#04534E", d: "M14.6 14v.1a.6.6 0 0 1 0 .2.8.8 0 0 1-.4.2h-.3a1.4 1.4 0 0 1-.1 0v-.2h.2a.4.4 0 0 0 .1-.1 1 1 0 0 1 .2-.2H14.7Zm-.7.3h-.1a1 1 0 0 0 .1.2h.6a.7.7 0 0 0 0-.1h.1v-.1a.5.5 0 0 0 0-.2.7.7 0 0 0-.2 0h-.2a.4.4 0 0 0 0 .1H14l-.1.1h-.1Z" }), jsx("path", { fill: "#0C8489", d: "M13.8 14.3h.4l.2-.2h.1l.1.2v.1h-.2l-.2.1H14l-.1-.1Z" }), jsx("path", { fill: "#04534E", d: "M14.2 14.2h.1Zm.1 0h.1Zm0 .2Zm-.1 0Zm-.3 0Z" }), jsx("path", { fill: "#8CBEBF", d: "M14.3 14.2h.1Zm0 .2Zm-.1 0Zm0-.2Zm-.3.2h.2-.1Z" }), jsx("path", { fill: "#231F20", d: "M14 14.3c0 .1 0 0 0 0Zm.2 0ZM14.2 14.3Zm0-.2Zm.2.1Zm0 .2Z" }), jsx("path", { fill: "#04534E", d: "M14.6 14.2v.2l-.5.1-.3-.1h.1l.2.1h.4l.1-.1v-.1Z" }), jsx("path", { fill: "#231F20", d: "M11.3 13.3v.1-.1Zm.3.3Zm-.6 0Zm0 .2h.1Zm.3.2Z" }), jsx("path", { fill: "#8CBEBF", d: "M11.6 13.9s0-.3-.2-.4c-.1-.2-.3-.2-.3 0v.3c.3.2.5.1.5.1Z" }), jsx("path", { fill: "#04534E", d: "M11.2 13.3s.2 0 .3.2a.5.5 0 0 1 .1.4H11c-.2-.2-.1-.4 0-.5h.1Zm.4.6v-.2l-.2-.2-.2-.1h-.1s-.1.3 0 .4a.5.5 0 0 0 .5.1Z" }), jsx("path", { fill: "#0C8489", d: "M11 13.6v-.1c.1 0 .3-.1.4 0l.1.4h-.2l-.2-.1v-.2Z" }), jsx("path", { fill: "#04534E", d: "M11.6 13.9h-.1s-.4 0-.5-.3v.1l.1.1h.1l.1.1h.2ZM11.4 13.8Zm0-.1h.1Zm-.1.1Zm0-.1Zm-.2 0h.1Zm0-.2h.1Zm.2 0Z" }), jsx("path", { fill: "#8CBEBF", d: "M11.2 13.5Zm0 .2Zm.1-.2Zm0 .2Zm.1 0h.1Zm-.1.1Zm.1 0Z" }), jsx("path", { fill: "#231F20", d: "M11.5 13.6Zm-.2-.2v.1Zm-.1 0v.1ZM11.1 13.5s.1 0 0 0Zm.1.1h.1Zm0 0Zm.2.1v.1ZM11.4 13.8Zm-.1 0Z" }), jsx("path", { fill: "#04534E", d: "M11.2 13.4h-.1v.2-.2h.1Zm.4.4v-.1l-.1-.2v.1l.1.2Z" }), jsx("path", { fill: "#231F20", d: "M11.7 13.7Zm-.2.5.1-.1Zm.3 0h.1Zm0 0Z" }), jsx("path", { fill: "#8CBEBF", d: "M12.3 14s-.2-.3-.5-.3a.3.3 0 0 0-.2.2c0 .2 0 .3.3.3l.4-.1Z" }), jsx("path", { fill: "#04534E", d: "m11.8 13.7.4.2a.8.8 0 0 1 .1.1v.2h-.4c-.3 0-.4-.1-.4-.2a.3.3 0 0 1 .3-.3Zm.5.3-.1-.1-.4-.2a.3.3 0 0 0-.3.3s.1.2.4.2h.3a.2.2 0 0 0 0-.1h.1Z" }), jsx("path", { fill: "#0C8489", d: "m12 13.8.2.1v.1l-.3-.2-.3.1v.2l.4.1h.2V14l-.1-.2Z" }), jsx("path", { fill: "#04534E", d: "M11.8 13.9Zm-.1 0Zm.2.1Z" }), jsx("path", { fill: "#8CBEBF", d: "M11.7 14v.1Z" }), jsx("path", { fill: "#04534E", d: "M12 14.1Zm0 0h.1Z" }), jsx("path", { fill: "#8CBEBF", d: "M11.8 13.9Zm0 0h-.1Zm0 .2Zm.1 0V14Zm.2 0Zm0 0H12Z" }), jsx("path", { fill: "#231F20", d: "M12 14h.1Zm0 .1Zm-.2-.1h.1ZM11.8 14h.1Zm-.1 0ZM11.7 14Zm0-.1Zm0-.1h.1Z" }), jsx("path", { fill: "#04534E", d: "M12.2 14.1H12l-.5-.1v.1h.2l.2.1H12.2Zm0 0v-.2l-.2-.1h.1l.1.1v.1Z" }), jsx("path", { fill: "#231F20", d: "M12.3 13.8v.1Zm.1 0h.1ZM12.5 13.7v.1Zm.2.1Zm.1 0v.1Zm-.4.6Zm.2 0s.1 0 0 0Zm.2 0ZM12.9 14.5v-.1Z" }), jsx("path", { fill: "#8CBEBF", d: "m12.2 14.1.3-.3.3.1h.2c0 .1.1 0 .1 0h.2l-.1.3H13l-.2.2h-.4l-.3-.3Z" }), jsx("path", { fill: "#04534E", d: "M12.3 14V14.3h.4c0 .1.2 0 .2 0a.4.4 0 0 0 .2 0v-.1l.2-.2H13a.3.3 0 0 1-.1 0 1.4 1.4 0 0 0-.4-.2l-.2.1v.1Zm.8 0h.2l-.1.2H13l-.2.2h-.4l-.2-.1v-.4h.6a.3.3 0 0 0 .2 0Z" }), jsx("path", { fill: "#04534E", d: "m12.2 14.1.2-.2h.5v.1h.4l-.2.2-.2.2h-.4l-.2-.1a.2.2 0 0 1 0-.2v.2h.2l.2.1h.2c.1 0 .2 0 .2-.2l.2-.2H13l-.2-.1a1 1 0 0 0-.3 0h-.2v.2Z" }), jsx("path", { fill: "#0C8489", d: "M13.2 14.2H13l-.6-.3c-.1 0-.2 0-.2.2v.2H13.1v-.1Z" }), jsx("path", { fill: "#04534E", d: "M12.5 14v.1Zm0 0h.1Zm.3 0Zm0 .2h.1Zm0 0v.1Zm-.3 0Z" }), jsx("path", { fill: "#8CBEBF", d: "M12.5 14Zm.1 0Zm.3 0h-.1Z" }), jsx("path", { fill: "#04534E", d: "M12.7 14.1Z" }), jsx("path", { fill: "#8CBEBF", d: "M12.6 14.2h-.1Zm.1 0V14Zm.2 0Zm-.1 0v.1Z" }), jsx("path", { fill: "#231F20", d: "M12.8 14.2Zm0-.2Zm-.2 0v.1ZM12.6 14v.1Zm0 0Zm-.2 0v.1ZM12.4 14v.1Zm.3.3Zm-.2 0Z" }), jsx("path", { fill: "#04534E", d: "m13.1 14.2-.2.1h-.2a.6.6 0 0 1-.4-.1v.1H12.6v.1h.4v-.1h.1ZM13 14h-.1l-.4-.1h-.2.6Zm.2 0h-.1.1Z" }), jsx("path", { fill: "#0C8489", d: "M13 14.7H13.4v-.1l.2-.1h.1l.2-.1.1-.1v-.1l-.1-.1H13.2l-.1.1v.5Z" }), jsx("path", { fill: "#04534E", d: "M13.6 14h.2v.2h.1v.1l-.1.1H13.5l-.1.1v.2H13a3 3 0 0 1 0-.2v-.2a.3.3 0 0 1 .1-.2h.2l.2-.1Zm-.4.1-.1.1v.2a2.4 2.4 0 0 0 0 .3h.2v-.1l.2-.1h.1l.2-.1V14H13.2Z" }), jsx("path", { fill: "#8CBEBF", d: "m13 14.7.1-.2.1-.1h.1v-.1l.2-.1h.1c.1 0 0 0 0 0h.2V14h-.4l-.2.1s0 .1 0 0l-.1.2v.2ZM13.2 14.5Zm.3-.2s0 .1 0 0Zm.1 0v.1Zm-.5.1Z" }), jsx("path", { fill: "#04534E", d: "M13.3 14.6h-.1Zm.5-.4h-.1Zm-.5.1Zm.1 0v.1h.1Zm.2-.1Z" }), jsx("path", { fill: "#04534E", d: "M13 14.8h.3v-.3h.4l.2-.2-.1-.2H13.5l-.2.1c0 .1 0 0 0 0V14l.3-.1h.2l.1.1v.2l-.2.1-.3.1v.3H13Z" }), jsx("path", { fill: "#231F20", d: "M13.2 14.4v-.1Zm0 0v-.1h.1Zm0 .1ZM13.3 14.5Zm.2-.2ZM13.5 14.3Zm0-.1V14Zm.3 0s-.1 0 0 0Zm-.1.1s0 .1 0 0Z" }), jsx("path", { fill: "url(#b)", d: "m12 14 .1-.2v-.2c-.1 0-.2 0-.2.2v.1Z" }), jsx("path", { fill: "#FCCA3E", d: "M12 13.5v.1-.1Z" }), jsx("path", { fill: "#AA8C30", d: "M12 13.5h.1v.1H12v-.1Zm0 0v.1-.1Z" }), jsx("path", { fill: "#FCCA3E", d: "M12.1 13.4v.1Z" }), jsx("path", { fill: "#AA8C30", d: "M12 13.4h.1v.1Zm0 0 .1.1Z" }), jsx("path", { fill: "#FCCA3E", d: "m14.9 13.8-.2.2.2-.2Z" }), jsx("path", { fill: "#AA8C30", d: "m14.9 13.8-.1.2h-.1l.2-.2Zm-.2.2.2-.2-.2.2Z" }), jsx("path", { fill: "#FCCA3E", d: "M15 13.7h-.1v.1Z" }), jsx("path", { fill: "#AA8C30", d: "M14.9 13.7v.1Zm0 0v.1Z" }), jsx("path", { fill: "#FCCA3E", d: "M10.9 13v.1-.2Z" }), jsx("path", { fill: "#AA8C30", d: "M10.9 13v.2-.2Zm0 0v.1-.1Z" }), jsx("path", { fill: "#FCCA3E", d: "M10.8 12.9c.1 0 .1 0 0 0Z" }), jsx("path", { fill: "#AA8C30", d: "M10.8 12.9h.1-.1Zm0 0h.1ZM12.1 13.4v.1ZM12 13.5Zm3 .3h-.1ZM14.9 13.9h-.1Zm-4-1h-.1ZM10.9 13Z" }), jsx("path", { fill: "url(#c)", d: "M11 13.4h.1v-.2H11s-.1 0 0 .2Z" }), jsx("path", { fill: "#E92736", d: "M10.8 13.3h.1v-.1h-.1ZM10.9 13.1h.1-.1ZM11 13.1c.1 0 .1 0 0 0Zm1 .5ZM12 13.6h.1ZM12.1 13.7h.1v-.1H12Zm2.5.2h.1ZM14.7 14h.1v-.1ZM14.7 14h.1Z" }), jsx("path", { fill: "#F7E204", d: "M11 13.2h-.1Zm0 0V13Zm0-.1Zm1 .5Zm0 0h.1Zm.1 0Zm2.6.3Zm0 0Zm0 .1Z" }), jsx("path", { fill: "url(#d)", d: "M14.5 14.2h.2V14h-.1l-.1.2Z" }), jsx("path", { fill: "#A8AC71", d: "M11.7 11.7h-.2l-.1-.3v-.1l.2-.2h.1v.1l-.1.1h.2-.1l-.1.1v.1h.2v.2Zm-.5.6v.4c0 .2 0 .2.2.3v.1s0 .3.3.3h.2l-.1-.2h-.1l-.1-.3-.3-.2v-.3l-.1-.2H11Z" }), jsx("path", { fill: "#F1A720", d: "M11.7 11.3V11v.2Z" }), jsx("path", { fill: "#A8AC71", d: "M11.8 12.9s-.2-.1-.1-.4c0-.2.4-.4.4-.5 0-.1.1-.1-.2-.4V12l-.2.2h-.1v.2l-.1.3s0 .2.3.3Z" }), jsx("path", { fill: "#78732E", d: "M11.6 13v.2h-.1l.1-.2Zm-.2 0Zm.1 0Zm0 .4v-.1Zm.2-.2h-.1Zm-.2.2h.1Zm.2 0v-.1Zm-.1 0h.2-.2Zm0-.7h-.1v-.1Zm0 .1Z" }), jsx("path", { fill: "#C6C7A6", d: "M11.4 11.2v.1h.2V11l-.2.1Z" }), jsx("path", { fill: "#1C242F", d: "M11.6 11.1Z" }), jsx("path", { fill: "#A8AC71", d: "M11.5 11.1h.1-.1Z" }), jsx("path", { fill: "#78732E", d: "M11.7 12.6h-.1Zm-.2 0V12.2v.2h.1-.1Zm.2 0v-.1m-.2-.2.1-.1v.1Zm.3.3h.1-.1Zm0-.2ZM11.8 12.3Zm0-.2Zm.1 0v-.2.1Zm0 0h.1Zm-.3-.5h.1Zm.3.6Zm.2-.2v-.2.2ZM12 11.9h.1-.1Zm0 0v-.2.1Z" }), jsx("path", { fill: "#fff", d: "m11.2 12.5.1.1h-.1Zm0-.2h.1Zm.2.2h-.2.1Zm-.2-.2v-.1.1Zm0 0v-.1Zm.3-.8v.1h.2-.1c-.1 0 0-.1 0-.1h-.2Zm0 0h.1v.2-.1Zm-.3.9h.1Zm.8-.4v-.1h.1-.1v-.2h-.1.1V12h-.1v.1h-.2v.1h-.1v.1h-.1V12.6h.2-.1v-.1l.1-.1v-.2H12V12h.1Zm-.4.5v.1-.1Zm0 0v-.2.1Zm.2-.2h-.2v-.1h.1v.1Zm0 0V12v.1Zm.1-.2V12v.1Zm0 0v-.2h.1v.1Zm-.2.6h-.1v.1h.1Zm-.5 0h.1-.1Zm.5.7v-.2h-.1V13v.2h-.2.1v.2H11.8Zm-.2-.2h.1-.1Zm0 .1h.2-.2Zm0-.3v-.1h-.2v.1h.1Zm-.2-.3h-.1v.1h.1Zm.2.1h-.2v.2-.1h.2Zm-.2 0v.1Zm.2-1.7h.1v.1l-.1.2v-.2l.1-.1h-.1Z" }), jsx("path", { fill: "#78732E", d: "M11.7 11.5h-.1Zm-.1 0h-.1Zm0 .2-.1-.1Zm.2 1.7H11.5V13h-.2a.3.3 0 0 1-.1-.3v-.4H11h.1v.4a.3.3 0 0 0 0 .2l.2.1V13.3l.1.1h.4Z" }), jsx("path", { fill: "#78732E", d: "M11.8 13.2h-.1V13l-.1-.1-.2-.2v-.4l-.1-.2h-.1l.2.2v.4l.2.2v.2h.1Z" }), jsx("path", { fill: "#A8AC71", d: "M12.1 12.9v.2-.2Z" }), jsx("path", { fill: "#F8C83C", d: "m11 12.3-.1-.1V12h.1v.2Z" }), jsx("path", { fill: "#977C2E", d: "M11 12.1h-.1v.1l.1-.1Zm0 0v.2h-.1V12h.1Zm0 0Z" }), jsx("path", { fill: "#F8C83C", d: "M11 12.3V12v.2Z" }), jsx("path", { fill: "#977C2E", d: "M11 12.1v.1-.1Zm0 0h.1-.1v.2l.1-.2Z" }), jsx("path", { fill: "#F8C83C", d: "M11.2 12.3H11V12v.1Z" }), jsx("path", { fill: "#977C2E", d: "M11.1 12.2v.1-.1Zm0 0v.1-.2Z" }), jsx("path", { fill: "#AF7029", d: "M12 12.9v-.1l-.2-.1v-.1l-.1-.1v-.1.3l.1.2h.2Z" }), jsx("path", { fill: "#816C2A", d: "M12 11.8c0 .2-.2.2-.3.3l-.2.1v.3l.1.3.2.1H12.1v.1-.1h-.5l-.1-.4v-.3l.3-.1.2-.3Z" }), jsx("path", { fill: "#fff", d: "M12.2 13H12Z" }), jsx("path", { fill: "#FCCA3D", d: "M12.3 13h-.1l-.1-.1H12l-.2-.2h.4l.1.1v.1Z" }), jsx("path", { fill: "#AF7029", d: "M12.3 13h-.1v.2h.1V13Z" }), jsx("path", { fill: "#FCCA3D", d: "M12.3 13Zm0-1.2h-.1v-.1l-.1-.1h-.2l.2.2h.1Z" }), jsx("path", { fill: "#AF7029", d: "M12.1 11.9v.1-.1Z" }), jsx("path", { fill: "#816C2A", d: "M12.3 13.1h-.1Z" }), jsx("path", { fill: "#6F5B24", d: "M12.3 13v.1h-.1l.1.1V13Z" }), jsx("path", { fill: "#404118", d: "M12.3 13.1Z" }), jsx("path", { fill: "#78732E", d: "M11.6 11a.8.8 0 0 0-.2.2v.2a1.2 1.2 0 0 0 0 .1v.1a1 1 0 0 0 .3 0v-.1h-.1v-.1a.2.2 0 0 0 .2-.1h-.1a.3.3 0 0 1-.1 0V11a.2.2 0 0 0 .1 0Zm0 .4v.1a.5.5 0 0 0 .2 0v.2h-.1a1.6 1.6 0 0 1-.2 0l-.1-.2v-.2a.9.9 0 0 1 .1-.2.2.2 0 0 1 .1 0h.1v.1h-.1v.1h.1v.1h-.1Z" }), jsx("path", { fill: "#AB6D29", d: "M13 10.6s-.3 0-.3.2l.3.2.1.2v.4l.1.2.2-.1.1-.3-.1-.6-.4-.2Z" }), jsx("path", { fill: "#4D2A15", d: "m13 10.6.4.2.1.6v.3l-.3.1v-.2l-.1-.3a.2.2 0 0 0 0-.1V11h-.3v-.1h-.1v-.2h.1a.3.3 0 0 1 .1 0Zm.1.7v.3l.1.2.2-.1.1-.3-.1-.6-.5-.2h-.2v.2l.3.2.1.1a.2.2 0 0 1 0 .2Z" }), jsx("path", { fill: "#4D2A15", d: "m13.2 11.7.1-.1v-.1s.1 0 0 0V11.1h-.1.1V11h-.1l-.1-.1H13v-.1H12.6h.1v-.1h.2l.3.2h.1l.1.3v.4l-.2.2h-.1Z" }), jsx("path", { fill: "#D2A567", d: "M13 10.7h-.2c-.1 0 0 .1 0 .1h.1-.1v-.1h.1Z" }), jsx("path", { fill: "#8F4620", d: "M13.3 11.8h-.1v.3l.3.4h.1v-.1h.1l.1.1h.1l.2.3V12.4h.1v.2l.1.2v-.3l.1.1v.2h.1v-.4V12.9l.1.1v.2l.1.1v-.7.5l.2.2v-.7.2l.1.4v.1l.1-1h.1l-.2-.3v-.2l-.2-.2-.2-.3-.2-.2v-.1h-.2l-.2-.2h-.2l-.3-.2H13v.2l.3.2v.1l.1.5v.2h-.1Z" }), jsx("path", { fill: "#AB6D29", d: "M13.5 12.4h.1v-.2s.1.1 0 .2v.1-.1Z" }), jsx("path", { fill: "#4D2A15", d: "M15 13.4V12.5h-.1v-.2l-.1-.3v.1H15.2v.2l.1.3v.7H15Z" }), jsx("path", { fill: "#8F4620", d: "m15 12.9.1-.1v-.5l-.2-.1v.1h.1V13Z" }), jsx("path", { fill: "#4D2A15", d: "M14.9 13.1v-.5l-.2-.2v-.2l-.1-.1h-.1V12l.2.1.1.3v.2l.1.4v.3-.2Zm0-.9h.1v.1ZM14.6 13v-.4l-.1-.2-.1-.2V12l.1.1v.3l.1.1v.2l.1.3v.2-.1Zm-.1 0v-.3l-.1-.1-.2-.2-.1-.2V12h.2V12.5l.1.1v.2l.1.2Zm-.1 0v-.1l-.1-.2v-.1a4.6 4.6 0 0 0-.3-.3h-.1V12v.1h.1l.1.2.2.2V13Zm-.2-.1-.1-.1v-.1l-.2-.2v-.1l.1.1v.1l.1.1v.2Zm.6-.9s0 .2.1 0V12l-.2-.3-.2-.3h-.1l.2.4c.2.2.1.3.1.3Zm-.7-.8.4.4.1.3h-.1l-.2-.4-.2-.1-.1-.2h.1Zm-.1.4a4.1 4.1 0 0 0 .3.4h.1l-.4-.5v.1Zm0 0v.2l.2.1v.1s-.1-.1-.1 0L14 12l-.1-.1v-.2ZM13.4 12.1h.1v-.2s.2.2.2 0v-.1h.1v-.3.1h.1v-.2l-.1-.2s0-.1 0 0h.2V11h-.4V11.3l.1.2v.2l-.1.2-.2.1v.1Z" }), jsx("path", { fill: "#AB6D29", d: "M14 12.5v-.1h-.1l.1.1Zm.3 0-.1-.1-.1-.2v.2l.2.1Zm.2 0-.1-.2-.1-.1v.2h.1v.1Zm.1 0V12h-.1V12.4h.1Zm.1-.4h.1l.1.4-.2-.2V12Z" }), jsx("path", { fill: "#D2A567", d: "M13.8 12.3V12v.1l.1.2c0 .1-.1 0-.1 0Zm.1-.1h.2L14 12v-.2.1l.1.2H14Zm.5-.2v-.1.1Z" }), jsx("path", { fill: "#D2A567", d: "m14.1 12 .2.1-.2-.2h.1l.1.2v-.2.1h.2v.1h-.4Zm.4 0h.2v-.3s.1.3 0 .4h-.1v-.2Zm.5.4Zm-.2-.6h.1Zm0 0v-.1Zm.2.5Zm-.4-.9c.1 0 0 0 0 0Zm-.2-.2.1.1h-.1v-.1Zm-.1 0V11a.1.1 0 0 0 0-.1h-.1v.1ZM14 11l.2-.1h.1l-.2.2Zm0-.2c-.1 0 0 0 0 .1v.1-.1Zm-.2 0-.2-.2.2.2v.1-.1Zm-.2-.1-.4-.1H13l.2.1h.2l.1.1-.2-.1H13.6v.2-.2Zm-.4 1s.2 0 .2-.2V11c-.1 0 0 0 0 0v.7h-.2Zm.2.5.2-.1v-.2.1h.1v-.2h.2l-.1-.2h.2v-.1.1h-.1v.2h-.1v.2h-.1v.1h-.1l-.1.1h-.1Z" }), jsx("path", { fill: "#D2A567", d: "M13.9 11.4h.1l-.1-.2V11h.2V11.4h-.2Zm.1 1.1-.1-.1v.1h.1Zm.2 0v-.1l-.1-.1v.1l.1.1Zm.2 0v-.1l-.1-.2v.2l.1.1Zm.2 0v-.3h-.1v.2Zm.3 0v-.2l-.2-.1.1.2Zm.2-.3L15 12h-.2H15.2Z" }), jsx("path", { fill: "#202020", d: "m14 12.2.1.2.2.1v.3l.1.2v-.1l-.1-.3-.3-.3Z" }), jsx("path", { fill: "#202020", d: "M14.3 12.8v-.1l-.2-.2H14l-.1-.2.1.2.1.3v.1h.1v-.1l-.2-.3.2.2.1.1v.2-.2Zm-.2-.7h.1l.1.3.2.1v.6-.4l-.2-.1.1.1v.2h.1V12.6l-.2-.2v-.2h-.1L14 12Z" }), jsx("path", { fill: "#202020", d: "M14.3 12h.1l.1.1v.3l.2.1v1-.3l-.1-.3v-.3h-.1l.1.3.1.3v-.7l-.2-.1V12h-.2Z" }), jsx("path", { fill: "#202020", d: "M14.5 12h.2v.4l.2.1v1.1-.4l-.1-.1v-.5h-.1l.1.5v.1V12.3l-.1-.2h-.1v-.2Z" }), jsx("path", { fill: "#202020", d: "m14.8 12 .1.1h.2l.1.5v.3l-.1.6v.2H15v-1.1l-.1-.1v.1l.1.6v-.9 1l.1-.4.1-.4v-.3h-.3l-.1-.2Zm-1.4.2h.2V12h.2V11.7h.1l.1-.1v-.4l.1-.1.2-.1v.1h.1l.2.3.1.2c.1 0 0 0 0 0l.2.3-.3-.4-.2-.2.2.3.1.2-.3-.5-.3-.2v.2l.3.3.3.4-.3-.4-.3-.2H14l.2.2h.1-.1l.1.2h.1v.2s0-.2-.2-.3l-.2-.1h-.1l.2.3.1.2-.1-.2-.2-.1.2.2-.2-.2h-.1l.3.4-.3-.2.1.2-.1-.2h-.1l.1.3-.2-.2v.2-.1.2l-.1-.1Z" }), jsx("path", { fill: "#202020", d: "M15.1 12.2h.1V12l-.4-.7-.5-.4s.3.1.5.4l.3.7h.1Zm-1.8-.1.1-.1v-.1h.2-.1v-.2.1l.2-.1h-.1v-.1s.1-.2 0-.2v-.2l-.1-.1h.1V11h-.1v-.2l-.2-.1.3.2v-.2.2h.2v-.1.1l.1.1h.1v-.2.2h-.3.2v.2l-.2-.2v.1l.2.2h-.2l.1.2v.1h-.1v.2h-.1v.2h-.2s.1 0 0 0v.1Z" }), jsx("path", { fill: "#202020", d: "M14.3 11v-.1l-.2-.1s-.4-.3-.9-.3h-.3.3s.6 0 .9.3h.2v.1Zm-1 0-.3-.2.3.1v.1Z" }), jsx("path", { fill: "#D2A567", d: "M13.4 11.4h-.2.1Zm0-.2h-.2a.1.1 0 0 1 .1 0Zm0-.1h-.2V11h.1Zm-.2-.1v-.1H13h.2Zm-.2-.2H12.7h.3Z" }), jsx("path", { fill: "#F8C83C", d: "M12.4 13.2h.1l.1.1h-.2v-.1Zm0-.1V13c-.1 0 0 0 0 0v.1Z" }), jsx("path", { fill: "#977C2E", d: "M12.3 13a.3.3 0 0 1 .1 0v.1-.1Zm0 0a.2.2 0 0 1 0 .1.5.5 0 0 0 .1 0 .3.3 0 0 0 0-.1Zm0 0Zm0 .3h.3l-.2-.1v.1Zm.1-.1v.2h.2v-.1l-.2-.1Z" }), jsx("path", { fill: "#F8C83C", d: "M12.4 13.3V13v.1Z" }), jsx("path", { fill: "#977C2E", d: "M12.4 13v.1a.5.5 0 0 1 0 .1V13Zm0 .2V13a.3.3 0 0 0 0 .1Zm0 0h.1a.1.1 0 0 1-.1 0Z" }), jsx("path", { fill: "#F8C83C", d: "M12.3 13.3h.1c0-.1-.1 0-.1 0Z" }), jsx("path", { fill: "#977C2E", d: "M12.4 13.2v.1h-.1Zm-.1 0v.1a.1.1 0 0 0 .1 0h-.1Z" }), jsx("path", { fill: "#F8C83C", d: "M12.4 13.2h-.1Z" }), jsx("path", { fill: "#977C2E", d: "M12.3 13.2Zm0 0Z" }), jsx("path", { fill: "#F8C83C", d: "M12.3 13.3h-.1v-.1h.1Z" }), jsx("path", { fill: "#977C2E", d: "M12.2 13.2h.1a.9.9 0 0 1 0 .2h-.1v-.2Zm0 0a1 1 0 0 0 0 .1h.1l-.1-.1Z" }), jsx("path", { fill: "#F8C83C", d: "M12.1 13.2h.2-.1Z" }), jsx("path", { fill: "#977C2E", d: "M12.1 13.2Zm.2 0h-.1Zm-.1 0Z" }), jsx("path", { fill: "#202220", d: "m11.8 13.3.1.1h.1l-.1-.1Z" }), jsx("path", { fill: "#4B4139", d: "m11.8 13.3.1.1h.1l-.1-.1h.1v.1h-.1Z" }), jsx("path", { fill: "#202220", d: "M11.9 13.3h.2-.2Z" }), jsx("path", { fill: "#4B4139", d: "M11.9 13.3h.2-.2.2-.2ZM11.9 13.3h.1-.1Z" }), jsx("path", { fill: "#202220", d: "M12 13.3h.1v-.1H12Z" }), jsx("path", { fill: "#4B4139", d: "M12 13.3h.1v-.1H12l.1.1H12ZM12 13.3h.1-.1Zm-.1 0v.1Z" }), jsx("path", { fill: "#F8C83C", d: "M12.1 13.2h.1v.2l-.1-.1v-.1Zm-.3 0Z" }), jsx("path", { fill: "#977C2E", d: "M11.8 13.2Zm0 0Zm.3 0v.1h.1a.2.2 0 0 0 0-.1Zm0 0v.1h.1v-.1Z" }), jsx("path", { fill: "#F8C83C", d: "M11.8 13.2v.1Z" }), jsx("path", { fill: "#977C2E", d: "M11.8 13.2v.1-.1Zm0 0v.1Z" }), jsx("path", { fill: "#F8C83C", d: "M11.9 13.3h-.1Zm0-.2h-.1v.1h.1V13Z" }), jsx("path", { fill: "#977C2E", d: "M11.9 13v.2a.2.2 0 0 1-.1 0V13Zm0 .2V13h-.1a.2.2 0 0 0 0 .1Zm0 0a.4.4 0 0 1 0 .1h-.1Zm0 0a.3.3 0 0 0-.1 0v.1Z" }), jsx("path", { fill: "#F8C83C", d: "M11.9 13.1v.1c-.1 0 0 0 0 0Z" }), jsx("path", { fill: "#977C2E", d: "M11.9 13.1v.1h-.1.1Zm0 0v.1Z" }), jsx("path", { fill: "#F8C83C", d: "M12 13.3h-.1v-.1Zm0-.3h-.1v.2l.1-.1V13Z" }), jsx("path", { fill: "#977C2E", d: "M12 13v.1a.7.7 0 0 1-.1 0V13a.1.1 0 0 1 .1 0Zm0 0h-.1a.2.2 0 0 0 0 .1 1 1 0 0 0 .1 0Zm0 .2h-.1v.1Zm0 0a.3.3 0 0 1-.1 0v.1Zm0 0Z" }), jsx("path", { fill: "#F8C83C", d: "M12 13.2h-.1V13h.1v.1Z" }), jsx("path", { fill: "#977C2E", d: "M11.9 13.1v.1a.3.3 0 0 0 .1 0 .2.2 0 0 0 0-.1h-.1Zm.1 0v.1h-.1a.2.2 0 0 1 0-.1h.1Zm0 0Z" }), jsx("path", { fill: "#F8C83C", d: "M12 13.2c0 .1 0 0 0 0-.1 0 0 0 0 0Z" }), jsx("path", { fill: "#977C2E", d: "M12 13.2s0 .1 0 0Zm0 0Z" }), jsx("path", { fill: "#F8C83C", d: "M12 13.1v.1h.1V13H12Z" }), jsx("path", { fill: "#977C2E", d: "M12.1 13v.2H12V13h.1Zm-.1.1v.1h.1V13H12Z" }), jsx("path", { fill: "#202220", d: "M12.1 13H12v.1s.1 0 0 0l.1-.1Z" }), jsx("path", { fill: "#4B4139", d: "M12.1 13H12h.1Z" }), jsx("path", { fill: "#F8C83C", d: "m13.3 13.9.2-.1v.1h-.1Z" }), jsx("path", { fill: "#977C2E", d: "M13.5 13.8v.1h-.1v-.1Zm-.2 0v.1h.1v-.1Z" }), jsx("path", { fill: "#F8C83C", d: "M13.2 13.9h.1Zm.5 0v-.1h-.1v.1h.1Z" }), jsx("path", { fill: "#977C2E", d: "M13.6 13.7a.2.2 0 0 1 .1.1v.1h-.1v-.2Zm0 .1v.1h.1Zm-.3 0v.1Zm0 .1Z" }), jsx("path", { fill: "#F8C83C", d: "M13.2 13.9v.1l.3-.1h-.2Z" }), jsx("path", { fill: "#977C2E", d: "M13.2 13.9h.2a3 3 0 0 1-.2.1v-.1Zm.2 0h-.2a.3.3 0 0 0 0 .1 3.6 3.6 0 0 0 .2 0Zm-.2 0Z" }), jsx("path", { fill: "#F8C83C", d: "M13.4 14h-.1.1Zm.4-.1h-.1Z" }), jsx("path", { fill: "#977C2E", d: "M13.7 13.8v.1Zm0 0v.1Zm-.3.1v.1h-.1Zm0 0h-.1v.1h.1Z" }), jsx("path", { fill: "#F8C83C", d: "M13.5 13.9h-.1.2-.1Zm.3 0v.1Z" }), jsx("path", { fill: "#977C2E", d: "M13.5 13.8v.1h-.1Zm0 0-.1.1h.1Zm.3 0v.2a.5.5 0 0 1 0-.1Zm0 .1v.1-.1Z" }), jsx("path", { fill: "#F8C83C", d: "m13.7 14 .1.1h.1V14h-.2v-.1h-.2l-.1.1h-.2v.1h.1c.1 0 0 0 0 0h.1l.1-.1" }), jsx("path", { fill: "#977C2E", d: "M13.6 13.8v.1h-.1v.1H13.1v.1h.1-.1V14m0 0h.2m.1-.1Zm0 .2h-.1m0 0 .1-.1h-.1m.3 0a.2.2 0 0 1 0-.1m0 0v.1h.2v.1a.2.2 0 0 1-.1 0m0 0-.1-.1m0 0v.1h.2V14a.2.2 0 0 1-.1 0h-.1v-.1" }), jsx("path", { fill: "#F8C83C", d: "M13.6 13.6V14h-.1V13.6Z" }), jsx("path", { fill: "#977C2E", d: "M13.5 13.8v.1h.1V13.5l-.1.2Zm0 0a.1.1 0 0 0 0 .1.3.3 0 0 0 0-.1v-.2.2Z" }), jsx("path", { fill: "#F8C83C", d: "M13.5 14h.1" }), jsx("path", { fill: "#977C2E", d: "M13.7 14h-.2m.2 0Z" }), jsx("path", { fill: "#202220", d: "M13 14h.1v-.1H13v.2Zm.2.3V14a.1.1 0 0 0 0 .2Zm.7 0 .2-.2h-.2v.1Z" }), jsx("path", { fill: "#4B4139", d: "M13.2 14.3V14v.2ZM13 14V14l.1-.1H13v.2Zm1 .1V14v.2Z" }), jsx("path", { fill: "#904720", d: "M12.3 11.4H12.6l-.1.1h-.2Z" }), jsx("path", { fill: "#202020", d: "M13.6 13.4v-.2l-.5.2.2.2.3-.2Z" }), jsx("path", { fill: "#D2A567", d: "M12.6 13h-.1l.1.1Zm0 .2h.1v.1h-.2v-.1Z" }), jsx("path", { fill: "#D2A567", d: "M12.7 13.3v-.5l-.1.1V13.2l.1.1Z" }), jsx("path", { fill: "#AB6D29", d: "M12.6 13v.3-.4c0-.2 0 0 0 0Zm.1 0v.2-.2Zm0 .4h-.1l-.2-.1h.4Zm-.2 0h-.1.1Z" }), jsx("path", { fill: "#D2A567", d: "M12.4 13.3h.2-.1Z" }), jsx("path", { fill: "#AB6D29", d: "M12.8 13.2V13v.1Z" }), jsx("path", { fill: "#4D2A15", d: "m12.6 13-.1-.1.1.2Z" }), jsx("path", { fill: "#D2A567", d: "M12.3 13.4h.2-.2Z" }), jsx("path", { fill: "#8F4620", d: "M12.8 13V13.3h-.1V13Z" }), jsx("path", { fill: "#AB6D29", d: "M12.4 13.3h.2v.1h-.1Z" }), jsx("path", { fill: "#8F4620", d: "m12.6 13-.1-.1v-.2.1l.1.3Z" }), jsx("path", { fill: "#AB6D29", d: "m12.5 13.5.3-.2v.1l-.2.1Z" }), jsx("path", { fill: "#8F4620", d: "M12.6 13.4h-.2.1Zm.3 0h-.2l-.1.1h.2Z" }), jsx("path", { fill: "#AB6D29", d: "m12.9 13.5-.1-.2V13l.1.1v.3Z" }), jsx("path", { fill: "#8F4620", d: "M12.9 13.2V13.5 13.2Z" }), jsx("path", { fill: "#202020", d: "M13 13.3v.3l-.1-.1v-.2Zm-.1.2h-.4H12.8Z" }), jsx("path", { fill: "#AB6D29", d: "m12.6 13.2.1.1-.1-.2Z" }), jsx("path", { fill: "#8F4620", d: "m12.6 13.5-.2-.1h-.1.2Z" }), jsx("path", { fill: "#202020", d: "M12.6 13.5h-.2l-.1-.1H12.6Z" }), jsx("path", { fill: "#AB6D29", d: "M12.6 13.2h-.1.1Z" }), jsx("path", { fill: "#8F4620", d: "M12.4 13h.1l.1.1h-.1V13ZM12.6 13h-.1Z" }), jsx("path", { fill: "#202020", d: "m12.9 12.9.6-.3.3.4-.6.1h-.4v-.2Z" }), jsx("path", { fill: "#4D2A15", d: "m13.7 13 .8.7v.1h-.3l-.4-.4-.1-.2v-.1ZM14.7 13.6l-.4-.3-.6-.5v.2l.7.6.2.1h.1Z" }), jsx("path", { fill: "#803F1D", d: "m13.7 13.1.4.4.4.3-.8-.7Z" }), jsx("path", { fill: "#4D2A15", d: "M14.3 13.8H14l-.5-.5h.1l.6.5Z" }), jsx("path", { fill: "#202020", d: "M14.7 13.6s0 .2-.2.1l-.8-.7.9.8.2-.1Z" }), jsx("path", { fill: "#803F1D", d: "m13.6 13.2.4.4.2.2c.1 0 0 0 0 0l-.3-.4-.2-.2Z" }), jsx("path", { fill: "#202020", d: "m13.7 13.2.6.6h.2-.2l-.6-.6Z" }), jsx("path", { fill: "#4D2A15", d: "m13.9 13.8-.2-.2v-.2l.4.4h-.2Z" }), jsx("path", { fill: "#202020", d: "M14.3 13.8H14l-.4-.4.5.4ZM14 13.8l-.3-.3.3.3Z" }), jsx("path", { fill: "#B07229", d: "m13.8 13 .3.2.1.2h.2-.1l.1.1h.2-.1l.1.2-.2-.1-.2-.2v.1l-.1-.2-.3-.3Z" }), jsx("path", { fill: "#4D2A15", d: "M13.4 13.7c0 .1-.2 0-.2 0v-.2h.1v.2Zm.3 0V13.3h-.1v.2h.1ZM13.6 13.8V13.5l-.1-.1V13.7s.1.1.1 0Z" }), jsx("path", { fill: "#202020", d: "M13.6 13.4V13.7h.1V13.3v.1-.5l-.2.3v.1Z" }), jsx("path", { fill: "#202020", d: "M13.6 13.4v.4-.3h-.1v-.1Z" }), jsx("path", { fill: "#4D2A15", d: "M13.5 13.9v-.4h-.2v-.1.1l.1.2.1.2Z" }), jsx("path", { fill: "#202020", d: "M13.2 13.4V13.7l.1.1v-.3h-.1Zm.2.2v-.1l-.1-.1h.1v.2Z" }), jsx("path", { fill: "#5C3A1D", d: "M13.3 13.5v.1l.1.1-.1-.2Z" }), jsx("path", { fill: "#202020", d: "M13.5 13.5v-.1Zm0 0V13.8v-.3Z" }), jsx("path", { fill: "#4D2A15", d: "M13.5 13.4V12.8h-.2v.2l.2.4Z" }), jsx("path", { fill: "#8B441F", d: "M13.1 13.5c.1 0 .1 0 0 0L13 13l.2.4Z" }), jsx("path", { fill: "#8B441F", d: "M13.3 13.4v.1l-.4-.4-.1-.1h.1l.4.4Z" }), jsx("path", { fill: "#5C3A1D", d: "M12.8 13a.9.9 0 0 0 .1.1v.1a6.6 6.6 0 0 0 .4.3.2.2 0 0 0 0-.1l-.4-.4a1.5 1.5 0 0 0-.1 0Zm.1 0 .4.4v.1a1.8 1.8 0 0 1-.3-.3 1.5 1.5 0 0 1-.2-.2h.1Z" }), jsx("path", { fill: "#202020", d: "M13.1 13.5v-.1l-.2-.3h-.1l.2.2.1.2Z" }), jsx("path", { fill: "#4D2A15", d: "M13.4 13.5v-.2l-.1-.3H13v-.1.1l.1.3.2.2Zm.2-.2V12.6h-.1v.5h.1Z" }), jsx("path", { fill: "#202020", d: "m13.3 13.4-.1-.2-.1-.1h-.2l.2.1.2.2Z" }), jsx("path", { fill: "#8B441F", d: "M13.5 13.4v-.6.5-.2.3l-.2-.4s.1.5.2.4Z" }), jsx("path", { fill: "#4D2A15", d: "m13.6 12.5.1.2V13.3l-.1-.4v-.3Z" }), jsx("path", { fill: "#8B441F", d: "M13.4 13.5v-.2h-.1l-.2-.2.3.4Z" }), jsx("path", { fill: "#202020", d: "M13.3 13.3V13H13l.2.3Zm.2 0-.1-.3-.1-.2v.2l.2.2Zm.1-.3v-.4.4Zm.1 0v-.5.5Zm-.2 0V12.8v.4Z" }), jsx("path", { fill: "#904720", d: "M13.6 13.3V13a7.7 7.7 0 0 0 0 .1s0-.1 0 0v.2ZM13.7 13.2V13l-.1-.3v.3l.1.4v-.1Z" }), jsx("path", { fill: "#312317", d: "M13.3 13.5h-.1l.1-.1Z" }), jsx("path", { fill: "#D2A567", d: "M13.5 12.4h.1v-.1h.1l-.1.2Z" }), jsx("path", { fill: "#8F4620", d: "M13.5 12.3v.2-.2Z" }), jsx("path", { fill: "#1E2121", d: "m12.4 12.4.4.6.5-.6-.3-.3-.6.3Z" }), jsx("path", { fill: "#4D2A15", d: "m13 12-.1.1v.2h.1l.1.1v-.1h.1l.1-.2v-.2H13v.1h-.2Z" }), jsx("path", { fill: "#5C3818", d: "M13.1 12.4V12v.4ZM13.1 12.2h.1V12v.4-.1Z" }), jsx("path", { fill: "#8F4620", d: "M13 12.3v.1h.1-.1v-.1Z" }), jsx("path", { fill: "#D2A567", d: "M13 13c0 .1 0 0 0 0l-.3-.2v-.1h.2v-.1l.2-.1h.3v-.2l.1.2v.2c-.1 0 0 .2 0 .2l-.2-.1v.2l-.2-.1v.2Z" }), jsx("path", { fill: "#8F4620", d: "M13 12.8v.2l-.1-.2h.1Zm.4-.5s.1.3 0 .4v-.2l-.2-.2h.2Z" }), jsx("path", { fill: "#8F4620", d: "m13 13-.1-.1-.2-.1v-.2l.2.2v.1Zm.2 0-.2-.2-.1-.2v-.1l.3.4Zm0-.2v-.2.2Zm.1 0-.1-.2v-.1H13v-.1l.2.3Z" }), jsx("path", { fill: "#AB6D29", d: "M13 13h-.1a.8.8 0 0 1-.4-.2v-.2l.3.3.1.1Z" }), jsx("path", { fill: "#8F4620", d: "M13 13h-.1l-.3-.2-.2-.2v-.1l.3.4.2.1Z" }), jsx("path", { fill: "#4D2A15", d: "m13.2 12.9-.1-.1-.2-.3h.2v.1l.1.2-.1-.1v.2Zm-.2 0v-.1h-.2l-.1-.2v-.1l.1.1.2.2Zm-.5-.3.2.3.2.1-.3-.3v-.1Zm.8.1v-.1l-.2-.2v-.1l.2.1v.2s0-.1 0 0v.1Zm.1-.1v-.2l-.1-.1V12l.1.1v.4Zm-.8.3-.2-.2v-.1l.2.2.2.2v.1l-.2-.2Z" }), jsx("path", { fill: "#202020", d: "M12.8 13s-.2 0-.3-.2l-.1-.2.4.5Zm.1 0-.2-.2-.1-.2.1.2.2.2Zm0-.2-.1-.2v-.1c0 .1 0 0 0 0v.2h.1Zm0-.4v.1l.2.2-.1-.1v-.1l.2.1-.1-.1-.2-.1ZM13.3 12.6l-.1-.2v-.2.1l-.1.1c-.1 0 0 0 0 0h.1v.2Z" }), jsx("path", { fill: "#202020", d: "M13.1 12.2s.1.1.2 0V12l.2.2v.2-.2l-.2-.2V12.4l.1.1-.1-.1v-.1h-.1Z" }), jsx("path", { fill: "#171717", d: "m12.9 12.6.1.2-.1-.2Z" }), jsx("path", { fill: "#8F4620", d: "M13.4 12.6v.1c0-.1 0 0 0 0Z" }), jsx("path", { fill: "#D2A567", d: "M12.5 12v.1l-.1.1V12.5l.2.2v-.2h.2s0 .1 0 0h.1l-.1-.4h-.3Z" }), jsx("path", { fill: "#AB6D29", d: "M12.8 12.4v.1h.1V12v.4Zm-.3-.1v.2s.1.1.1 0V12v.2Z" }), jsx("path", { fill: "#45392D", d: "M12.3 12V12.6 12Z" }), jsx("path", { fill: "#AB6D29", d: "M12.3 12V12.6h.1V12Z" }), jsx("path", { fill: "#DBAD6C", d: "M12.9 12.3h-.1v-.1h.1Z" }), jsx("path", { fill: "#D2A567", d: "m12.4 12-.1.3v.3-.4l.1-.1Z" }), jsx("path", { fill: "#8F4620", d: "M12.5 12v.5-.1l.1-.1V12h.1V12h-.2Z" }), jsx("path", { fill: "#DBAD6C", d: "M12.9 12.4Z" }), jsx("path", { fill: "#8F4620", d: "M12.4 12.4v.1V12.1h-.1V12h.1V12.4Zm.4-.3V12.5v-.1c0-.1 0 0 0 0h.1V12h-.1Z" }), jsx("path", { fill: "#1E2121", d: "M13.2 12v.2V12l-.1.1v.2-.2.2-.1V12.5v-.1L13 12v-.2l.1.1Zm-.3 0-.2.1.1-.2Zm-.5 0v.1h.2V12h-.1Zm0 0Zm.8-.2v.1l.1.1h-.1v-.1Z" }), jsx("path", { fill: "#DBAD6C", d: "M12.3 12H12.7c0 .1 0 .1.1 0h.1l.1-.1v-.2h-.4l-.1.1v.2Z" }), jsx("path", { fill: "#8F4620", d: "M12.8 11.8v.1l-.1.2.1-.2v-.2Zm0-.1v.2l.1.1h.1v-.2l.1.1h.1v-.1l-.1-.2h-.2Z" }), jsx("path", { fill: "#AB6D29", d: "m13 11.5.1.2v.1-.2Z" }), jsx("path", { fill: "#4D2A15", d: "M13.2 11.8v.1-.1l-.1-.2.1.2Zm-.3.2s.2 0 .2-.2v-.3l-.1.3v.1Zm0-.3-.2.1v-.2h.1Z" }), jsx("path", { fill: "#1E2121", d: "M13.2 11.5v.3-.2H13v-.1Zm-.2.3V11.5l-.1.2v-.1.2Z" }), jsx("path", { fill: "#8F4620", d: "M12.3 12v-.2h.1a.1.1 0 0 0 .2-.1v-.1V12c0-.1 0 0 0 0v-.2h-.1v.2-.1h-.1v.2h-.1Z" }), jsx("path", { fill: "#874F20", d: "M13.2 11.4H13h.1Z" }), jsx("path", { fill: "#B27129", d: "M12.5 11.5v.1l-.1.1v-.1Z" }), jsx("path", { fill: "#DBAD6C", d: "m13 11.4-.1.3-.1-.1-.1.1-.1-.1c-.1.2-.2.1-.2.1v-.2H12.8v-.1l.1-.1h.1v.1Z" }), jsx("path", { fill: "#B27129", d: "M12.7 11.2v.2h.1v.1-.1.2l.1-.1c0-.1 0 0 0 0h.1c.1 0 0 0 0 0v-.2l-.2-.2v.1h-.1Z" }), jsx("path", { fill: "#8F4620", d: "M13 11.2v.3-.2l-.1-.1V11h.1Z" }), jsx("path", { fill: "#4D2A15", d: "M13 11.1v.2l.2.1h-.1v-.1l-.2-.2Z" }), jsx("path", { fill: "#1E2121", d: "M13 11v.2l.1.1.1.1-.2-.2v.1l-.1-.2V11.4 11Z" }), jsx("path", { fill: "#8F4620", d: "M12.7 11.2h.1v.2-.1Z" }), jsx("path", { fill: "#B27129", d: "M12.7 11.4V11.7 11.4Z" }), jsx("path", { fill: "#8F4620", d: "M12.6 11.4v.1l-.1.1.1-.1h.1v-.1Z" }), jsx("path", { fill: "#DBAD6C", d: "m13 11-.1.1H12.6V11H13Z" }), jsx("path", { fill: "#AB6D29", d: "M13 11h-.4H12.9Z" }), jsx("path", { fill: "#4D2A15", d: "M12.5 11h.1Z" }), jsx("path", { fill: "#DBAD6C", d: "M12.8 11.1v.1h-.2V11h.2Z" }), jsx("path", { fill: "#DBAD6C", d: "M12.7 11.2s.1.1 0 .2l-.1-.2-.1.1h-.2l.2-.2h.2v.1Z" }), jsx("path", { fill: "#AB6D29", d: "m12.7 11 .2.1h-.2Z" }), jsx("path", { fill: "#4D2A15", d: "M12.5 11h.1Z" }), jsx("path", { fill: "#AB6D29", d: "M12.8 11.2H12.4V11H12.8v.1Zm-.2 0v.2h-.1v-.1h.1Z" }), jsx("path", { fill: "#4D2A15", d: "m12.5 11 .1.1h.2v.1H12.5V11Z" }), jsx("path", { fill: "#AB6D29", d: "M12.8 11.3H12.5h.2Z" }), jsx("path", { fill: "#4D2A15", d: "M12.8 11.3h-.2v-.1h-.2.1v.1H12.9Z" }), jsx("path", { fill: "#DBAD6C", d: "M12.6 11.2v.2-.1Z" }), jsx("path", { fill: "#6C3F18", d: "m12.1 11-.1.2-.1.1.2-.2V11Z" }), jsx("path", { fill: "#DBAD6C", d: "M12.5 10.8v.2l-.2.2-.2.2H12v-.2l.1-.2v.1h.2V10.8v.2h.1v-.2Z" }), jsx("path", { fill: "#904720", d: "M12.5 11.4v.2-.1h-.1v-.1h.1Z" }), jsx("path", { fill: "#AB6D29", d: "M12.5 11.4h-.1Z" }), jsx("path", { fill: "#904720", d: "M12.1 11.3h.2v-.1l.2-.2v-.2.3l-.1.1c.1 0 0 .1 0 .1h.2c0 .1-.1 0-.2 0v.1H12v-.1h.1Z" }), jsx("path", { fill: "#AB6D29", d: "M12.4 11v-.2.2Z" }), jsx("path", { fill: "#904720", d: "M12.4 10.8v.3l-.3.1.2-.1v-.2Z" }), jsx("path", { fill: "#1E2121", d: "m12.2 11.3.2-.1h-.2Zm.1-.2V10.8 11Zm0 0h.2v-.2.1h-.1Zm0 .3h.1Zm0 0v-.1h.2-.2.1-.2Z" }), jsx("path", { fill: "#fff", d: "M12.1 11.2v.1a.1.1 0 0 1-.1 0" }), jsx("path", { fill: "#F16E16", d: "M12.1 11.2v.1H12" }), jsx("path", { d: "M12 11.2h.1v.1" }), jsx("path", { fill: "#D5D3CA", d: "M12 11.3Z" }), jsx("path", { fill: "#AB6D29", d: "m12.1 11.1.1-.1v-.1.2l.1-.3V11c-.2 0-.2 0-.2.2H12l.1-.1Z" }), jsx("path", { fill: "#904720", d: "M12.1 11.2V11v.2Z" }), jsx("path", { fill: "#4D2A15", d: "M12.2 11v-.1.2Z" }), jsx("path", { fill: "#AB6D29", d: "M12 11.4v-.2l.1-.2v.1l-.2.2Z" }), jsx("path", { fill: "#6C4119", d: "M12.5 11.6v-.1h-.1ZM12.5 11.6v-.1Z" }), jsx("path", { fill: "#BF802D", d: "m12 11.5.1.1h.2v-.1H12Z" }), jsx("path", { fill: "#F9C83A", d: "M12.4 11.5H12v-.1h-.1l-.2.2.2.2v-.2h.1l.1-.1h.2l-.2.1.3-.1Z" }), jsx("path", { fill: "#8F4620", d: "M12 11.4a.5.5 0 0 0-.1 0h-.1l-.1.2v.1l.2.1v-.2h.2c0-.1.1-.1.2 0H12a2 2 0 0 0 .3 0v-.1a.4.4 0 0 0-.2 0l-.2-.1Zm.2 0H12a.5.5 0 0 0-.1 0h-.1v.4h.1v-.2h.1l.1-.1h.2a.9.9 0 0 1-.2.1 1.6 1.6 0 0 0 .3 0v-.1a.4.4 0 0 0-.2 0Z" }), jsx("path", { fill: "#FCF3D8", d: "m11.9 11.7-.2-.1.2-.1v-.1h.1l-.2.2v.1Z" }), jsx("path", { fill: "#FDEAAF", d: "M12.4 11.5H12l-.2-.1h.4Z" }), jsx("path", { fill: "#513625", d: "M12 11.6h.2-.1Z" }), jsx("path", { fill: "#F9C83A", d: "M11.9 11.8h-.1v-.2s-.1 0 0-.1v.2l.1.1Z" }), jsx("path", { fill: "#8B5122", d: "M11.9 11.4Z" }), jsx("path", { fill: "#F9C83A", d: "M12 11.5H12.4l-.2.1h.2v-.1H12Z" }), jsx("path", { fill: "#8F4620", d: "M11.9 11.7s0 .1 0 0h-.2l.1.1h.1v-.1Z" }), jsx("path", { fill: "#FCCA3D", d: "M12.3 13Zm-.1-1.4H12Z" }), jsx("path", { fill: "#816C2A", d: "M12.3 13h-.1.1Zm0 0h-.1.1Zm0-.2-.1.1h.1Zm0 0-.2.1h.1v-.1Zm-.1-.1v.1H12v.1-.2Zm-.1 0v.1-.1Zm0 0-.1.2v-.2Zm-.1 0v.2-.2Zm0 0h-.1v.2-.2Zm-.1 0-.1.2v-.2Zm0 0h-.1v.1h-.1l.1-.1Zm-.1 0h-.1Zm0 0h-.1Zm0-.1h-.1Zm0 0h-.1Zm-.1 0Zm0-.2v.1Zm.4-.4Zm0 0v-.1h.1-.1Zm0-.1h.1v-.1H12Zm0 0v-.1h.1-.1Zm0-.1v-.1Zm-.1-.1h.1v-.1H12Zm0 0v-.1Z" }), jsx("path", { fill: "#78732E", d: "m12 11.5.1.1.1.2v.2c-.2 0-.3.2-.3.2l-.2.2a.2.2 0 0 0 0 .2.2.2 0 0 0 .2.1h.4v.3a1 1 0 0 1 0 .2v-.5a.2.2 0 0 0-.2 0h-.2a.2.2 0 0 1-.2 0v-.4l.3-.2.2-.3v-.2H12Z" }), jsx("path", { fill: "#A8AC71", d: "M12 11.7Zm0 0Z" }), jsx("path", { fill: "#78732E", d: "M12 11.8Zm0 0Zm.1 0Zm0 .1Zm0 0Zm-.5.6Z" }), jsx("path", { fill: "#fff", d: "M11.6 12.5Z" }), jsx("path", { fill: "#78732E", d: "M11.6 12.6Z" }), jsx("path", { fill: "#fff", d: "M11.6 12.7Zm0 0Zm0 0Z" }), jsx("path", { fill: "#A8AC71", d: "M11.7 12.8Zm0 0Zm.4.1Z" }), jsx("path", { fill: "#fff", d: "M12.2 13Z" }), jsx("path", { fill: "#A8AC71", d: "M12.2 13Zm0 0Z" }), jsx("path", { fill: "#fff", d: "M12.2 13Zm-.6-1.9Z" }), jsx("path", { fill: "#F9C83A", d: "M12 11.5Z" }), jsx("path", { fill: "#8F4620", d: "M12 11.5Z" }), jsx("path", { fill: "#977C2E", d: "M12.3 13.2Z" }), jsx("path", { fill: "#006847", d: "M2 2h7v22H2z" }), jsx("path", { fill: "#CE1126", d: "M17 2h7v22h-7z" })] })), jsxs("defs", { children: [jsxs("radialGradient", __assign$1({ id: "b", cx: "0", cy: "0", r: "1", gradientTransform: "matrix(.1 0 0 .1 12 13.8)", gradientUnits: "userSpaceOnUse" }, { children: [jsx("stop", {}), jsx("stop", { offset: "1" })] })), jsxs("radialGradient", __assign$1({ id: "c", cx: "0", cy: "0", r: "1", gradientTransform: "matrix(0 -.1 .1 0 11 13.3)", gradientUnits: "userSpaceOnUse" }, { children: [jsx("stop", {}), jsx("stop", { offset: "1" })] })), jsxs("radialGradient", __assign$1({ id: "d", cx: "0", cy: "0", r: "1", gradientTransform: "matrix(0 0 -.1 0 14.6 14)", gradientUnits: "userSpaceOnUse" }, { children: [jsx("stop", {}), jsx("stop", { offset: "1" })] }))] })] })),
    }); },
    FlagAustralia: function () { return ({
        height: 26,
        width: 26,
        viewBox: '0 0 26 26',
        icon: (jsxs(Fragment, { children: [jsx("mask", __assign$1({ id: "mask0_1445_5632", maskUnits: "userSpaceOnUse", x: "2", y: "2", width: "22", height: "22" }, { children: jsx("circle", { cx: "13", cy: "13", r: "11", fill: "#D9D9D9" }) })), jsxs("g", __assign$1({ mask: "url(#mask0_1445_5632)" }, { children: [jsx("path", { d: "M-2 2H27.3333V24H-2V2Z", fill: "#00008B" }), jsx("path", { d: "M1.11874 2L6.71041 6.14792L12.2792 2H14.0667V3.42083L8.56666 7.52292L14.0667 11.6021V13H12.2333L6.73333 8.89792L1.25624 13H-0.600006V11.625L4.87708 7.54583L-0.600006 3.46667V2H1.11874Z", fill: "white" }), jsx("path", { d: "M9.11666 8.43958L14.0667 12.0833V13L7.85624 8.43958H9.11666ZM4.89999 8.89792L5.03749 9.7L0.637494 13H-0.600006L4.89999 8.89792ZM14.0667 2V2.06875L8.36041 6.37708L8.40624 5.36875L12.9208 2H14.0667ZM-0.600006 2L4.87708 6.03333H3.50208L-0.600006 2.9625V2Z", fill: "#FF0000" }), jsx("path", { d: "M4.92291 2V13H8.58958V2H4.92291ZM-0.600006 5.66667V9.33333H14.0667V5.66667H-0.600006Z", fill: "white" }), jsx("path", { d: "M-0.600006 6.42292V8.62292H14.0667V6.42292H-0.600006ZM5.65624 2V13H7.85624V2H5.65624Z", fill: "#FF0000" }), jsx("path", { d: "M20.2815 19.0493L19.5942 19.1364L19.668 19.8237L19.1718 19.341L18.679 19.8271L18.746 19.1398L18.0588 19.0593L18.6388 18.6839L18.2733 18.0971L18.9304 18.3151L19.1618 17.6613L19.3998 18.3117L20.0535 18.0871L19.6948 18.6771L20.2782 19.0493H20.2815ZM20.1575 15.1201L20.248 14.6842L19.9194 14.3825L20.362 14.3322L20.5464 13.9265L20.7308 14.3322L21.1733 14.3825L20.8448 14.6842L20.9353 15.1201L20.5464 14.8988L20.1575 15.1201ZM16.6674 13.1085L15.9869 13.1823L16.0472 13.8628L15.5644 13.3767L15.0683 13.8494L15.1487 13.1689L14.4715 13.0783L15.0515 12.7163L14.6995 12.1296L15.3465 12.3575L15.5879 11.7172L15.8125 12.3642L16.4629 12.153L16.0975 12.733L16.6708 13.1085H16.6674ZM23.5 12.0089L22.7993 12.0994L22.8764 12.8001L22.3702 12.3072L21.8673 12.8034L21.9377 12.0994L21.237 12.0189L21.8304 11.6334L21.4583 11.0333L22.1288 11.2579L22.3635 10.5941L22.6049 11.2579L23.272 11.0266L22.9032 11.63L23.5 12.0089ZM20.2782 9.20946L19.5842 9.28657L19.6479 9.98391L19.1551 9.48773L18.6488 9.9705L18.7293 9.27651L18.0353 9.18264L18.6287 8.81386L18.27 8.21375L18.9304 8.44507L19.1752 7.79132L19.4031 8.45178L20.067 8.23386L19.6948 8.82392L20.2782 9.20946ZM10.4585 18.6805L8.92298 18.8615L9.0772 20.4004L7.97756 19.3141L6.87121 20.3937L7.03548 18.8548L5.5 18.6604L6.80415 17.8289L5.99953 16.508L7.46126 17.0109L7.99097 15.5559L8.51062 17.0143L9.97569 16.5214L9.16102 17.8356L10.4618 18.6771L10.4585 18.6805Z", fill: "white" })] }))] })),
    }); },
    FlagCzechRepublic: function () { return ({
        height: 26,
        width: 26,
        viewBox: '0 0 26 26',
        icon: (jsxs(Fragment, { children: [jsx("mask", __assign$1({ id: "mask0_2598_6001", mask: "mask-type:alpha", maskUnits: "userSpaceOnUse", x: "2", y: "2", width: "22", height: "22" }, { children: jsx("circle", { cx: "13", cy: "13", r: "11", fill: "#FFFFFF" }) })), jsxs("g", __assign$1({ mask: "url(#mask0_2598_6001)" }, { children: [jsx("path", { d: "M-2 2H27.5002V13.0626H-2V2Z", fill: "white" }), jsx("path", { d: "M-2 13.0625H27.5002V24.1251H-2V13.0625Z", fill: "#D7141A" }), jsx("path", { d: "M14.5938 13.0626L-2 2V24.1251L14.5938 13.0626Z", fill: "#11457E" })] }))] })),
    }); },
};

var GetTheApp = {
    GetTheAppStep1: function () { return ({
        height: 56,
        width: 56,
        viewBox: '0 0 56 56',
        icon: (jsxs("g", __assign$1({ fill: "none", fillRule: "evenodd" }, { children: [jsxs("g", __assign$1({ clipPath: "url(#a)" }, { children: [jsx("circle", { cx: "43", cy: "43", r: "12", fill: "#0E5F80" }), jsx("path", { fill: "#79C9D6", d: "M.7 26.8a1 1 0 0 1 .2-1.3L4 23.3c.4-.3.5-.7.4-1L3 18.4a1 1 0 0 1 .6-1.2l3.7-1c.4 0 .7-.4.7-.8v-4c0-.6.5-1 1-1l3.7.5c.4 0 .8-.2 1-.5L15 6.7c.1-.5.7-.7 1.2-.4L19.7 8c.3.2.8.1 1-.2l2.5-3c.3-.4 1-.4 1.3 0L27 7.7c.3.3.7.3 1 .1L31.5 6c.5-.2 1 0 1.3.5l1.5 3.6c.2.4.6.6 1 .5l3.7-.6c.5 0 1 .3 1 .9l.2 4c0 .4.4.7.8.8l3.7.8c.5 0 .8.6.6 1.1l-1 3.8c-.1.4 0 .9.4 1l3.2 2.1c.5.3.6 1 .3 1.4L45.9 29a1 1 0 0 0 0 1.1l2.4 3c.3.5.2 1.1-.2 1.4L45 36.7a1 1 0 0 0-.4 1l1.3 3.8c.2.6-.1 1.1-.6 1.2l-3.7 1a1 1 0 0 0-.7.8v4c0 .6-.5 1-1 1l-3.7-.5c-.4 0-.8.2-1 .5L34 53.3c-.1.5-.7.7-1.2.4L29.3 52a.9.9 0 0 0-1 .2l-2.5 3c-.3.4-1 .4-1.3 0L22 52.3a.9.9 0 0 0-1-.1L17.5 54c-.5.2-1 0-1.3-.5L14.8 50a.9.9 0 0 0-1-.5l-3.7.6c-.5 0-1-.3-1-.9l-.2-4a1 1 0 0 0-.8-.8l-3.7-.8a1 1 0 0 1-.6-1.1l1-3.8a1 1 0 0 0-.4-1l-3.2-2.1a1 1 0 0 1-.3-1.4L3.1 31a1 1 0 0 0 0-1.1l-2.4-3Z", clipRule: "evenodd" }), jsx("path", { fill: "#10465B", d: "M22.8 17.7a2 2 0 0 0-3.7 1.5l5.8 16.3-6.7-3.3H18a2 2 0 0 0-1.6 3.6l11.3 5.5h.1c1 .4 2 0 2.5-1L35.8 29v-.2a2 2 0 0 0-3.6-1.6l-3.4 7.3-6-16.5v-.2Z", clipRule: "evenodd" }), jsx("path", { stroke: "#F48122", strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: "4", d: "m52.3 4-7.1 7.2M38.7 2.6v4.1M53.2 16.5l-4 1.3" })] })), jsx("defs", { children: jsx("clipPath", __assign$1({ id: "a" }, { children: jsx("path", { fill: "#fff", d: "M0 0h56v56H0z" }) })) })] }))),
    }); },
    GetTheAppStep2: function () { return ({
        height: 56,
        width: 56,
        viewBox: '0 0 56 56',
        icon: (jsxs("g", __assign$1({ fill: "none", fillRule: "evenodd" }, { children: [jsxs("g", __assign$1({ clipPath: "url(#a)" }, { children: [jsx("path", { fill: "#A7CB19", d: "M24 42a1 1 0 1 1 0 2 1 1 0 0 1 0-2Zm4 0a1 1 0 1 1 0 2 1 1 0 0 1 0-2Zm4 0a1 1 0 1 1 0 2 1 1 0 0 1 0-2Zm4 0a1 1 0 1 1 0 2 1 1 0 0 1 0-2Zm4 0a1 1 0 1 1 0 2 1 1 0 0 1 0-2Zm4 0a1 1 0 1 1 0 2 1 1 0 0 1 0-2Zm-20-4a1 1 0 1 1 0 2 1 1 0 0 1 0-2Zm4 0a1 1 0 1 1 0 2 1 1 0 0 1 0-2Zm4 0a1 1 0 1 1 0 2 1 1 0 0 1 0-2Zm4 0a1 1 0 1 1 0 2 1 1 0 0 1 0-2Zm4 0a1 1 0 1 1 0 2 1 1 0 0 1 0-2Zm4 0a1 1 0 1 1 0 2 1 1 0 0 1 0-2Zm-20-4a1 1 0 1 1 0 2 1 1 0 0 1 0-2Zm4 0a1 1 0 1 1 0 2 1 1 0 0 1 0-2Zm4 0a1 1 0 1 1 0 2 1 1 0 0 1 0-2Zm4 0a1 1 0 1 1 0 2 1 1 0 0 1 0-2Zm4 0a1 1 0 1 1 0 2 1 1 0 0 1 0-2Zm4 0a1 1 0 1 1 0 2 1 1 0 0 1 0-2Zm-20-4a1 1 0 1 1 0 2 1 1 0 0 1 0-2Zm4 0a1 1 0 1 1 0 2 1 1 0 0 1 0-2Zm4 0a1 1 0 1 1 0 2 1 1 0 0 1 0-2Zm4 0a1 1 0 1 1 0 2 1 1 0 0 1 0-2Zm4 0a1 1 0 1 1 0 2 1 1 0 0 1 0-2Zm4 0a1 1 0 1 1 0 2 1 1 0 0 1 0-2Zm-20-4a1 1 0 1 1 0 2 1 1 0 0 1 0-2Zm4 0a1 1 0 1 1 0 2 1 1 0 0 1 0-2Zm4 0a1 1 0 1 1 0 2 1 1 0 0 1 0-2Zm4 0a1 1 0 1 1 0 2 1 1 0 0 1 0-2Zm4 0a1 1 0 1 1 0 2 1 1 0 0 1 0-2Zm4 0a1 1 0 1 1 0 2 1 1 0 0 1 0-2Zm-20-4a1 1 0 1 1 0 2 1 1 0 0 1 0-2Zm4 0a1 1 0 1 1 0 2 1 1 0 0 1 0-2Zm4 0a1 1 0 1 1 0 2 1 1 0 0 1 0-2Zm4 0a1 1 0 1 1 0 2 1 1 0 0 1 0-2Zm4 0a1 1 0 1 1 0 2 1 1 0 0 1 0-2Zm4 0a1 1 0 1 1 0 2 1 1 0 0 1 0-2Zm4 20a1 1 0 1 1 0 2 1 1 0 0 1 0-2Zm0-4a1 1 0 1 1 0 2 1 1 0 0 1 0-2Zm0-4a1 1 0 1 1 0 2 1 1 0 0 1 0-2Zm0-4a1 1 0 1 1 0 2 1 1 0 0 1 0-2Zm0-4a1 1 0 1 1 0 2 1 1 0 0 1 0-2Zm0-4a1 1 0 1 1 0 2 1 1 0 0 1 0-2Zm4 20a1 1 0 1 1 0 2 1 1 0 0 1 0-2Zm0-4a1 1 0 1 1 0 2 1 1 0 0 1 0-2Zm0-4a1 1 0 1 1 0 2 1 1 0 0 1 0-2Zm0-4a1 1 0 1 1 0 2 1 1 0 0 1 0-2Zm0-4a1 1 0 1 1 0 2 1 1 0 0 1 0-2Zm0-4a1 1 0 1 1 0 2 1 1 0 0 1 0-2Z", clipRule: "evenodd" }), jsx("circle", { cx: "19", cy: "37", r: "18", fill: "#EED8F2" }), jsx("path", { fill: "#10465B", d: "M52.1 13.8a2 2 0 0 0-1.3-2.5l-3-.8-25-7.2-2.8-.8h-.2a2 2 0 0 0-1 3.9l1.8.4c-3 5.9-2.7 12.2 1 19a18 18 0 0 0-10.9 15.7L9 41h-.2a2 2 0 0 0-1 3.8l3 .8 25 7.2 2.8.8h.2a2 2 0 0 0 1-3.8l-1.8-.5a18 18 0 0 0-1-19c6.8-3.8 10.5-9 11-15.6l1.7.5h.1c1 .2 2-.4 2.3-1.4ZM24.4 8.2l.1-.2L44 13.6v.2c-.2 5.5-3 9.7-9 13a4 4 0 0 0-1.4 5.6l.2.3a14 14 0 0 1 .3 15.4l-19.4-5.5v-.4c.3-5.9 3.1-10 8.7-12.8a4 4 0 0 0 1.7-5.5l-.2-.3c-3-5.8-3.2-10.7-.5-15.4Zm3.8 23.7c4.9 1.4 6 8 5.7 12 0 1.2-1.2 1.8-2.2 1.5L18 41.5c-1-.3-1.6-1.4-1.1-2.3 2-3.7 6.4-8.7 11.2-7.3Zm-1 3.9c-2-.6-5 2.7-5 2.7l7.6 2.2s-.8-4.3-2.5-4.9h-.2Z", clipRule: "evenodd" }), jsx("path", { stroke: "#A7CB19", strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: "4", d: "M44.7 2.7c2 .3 5.6 1.6 7.6 2l-7.6-2Z", clipRule: "evenodd" }), jsx("path", { stroke: "#A7CB19", strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: "4", d: "M52.3 4.6c-2 2-3.7 4.4-5.6 6.5M47 11.2l4 2.6M51 13.9c-3 2.7-6.8 6.4-9.9 9" }), jsx("path", { stroke: "#A7CB19", strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: "4", d: "M43.5 16c-1 1.9-1.3 5.1-2.4 7M44 16c-1.2-1.1-3-2.3-4.3-3.3M44.3 2.9c-2.5 5.7-1 2.4-4.7 9.7" })] })), jsx("defs", { children: jsx("clipPath", __assign$1({ id: "a" }, { children: jsx("path", { fill: "#fff", d: "M0 0h56v56H0z" }) })) })] }))),
    }); },
    GetTheAppStep3: function () { return ({
        height: 56,
        width: 56,
        viewBox: '0 0 56 56',
        icon: (jsxs("g", __assign$1({ fill: "none", fillRule: "evenodd" }, { children: [jsx("path", { fill: "#A7CB19", d: "M3 52a1 1 0 1 1 0 2 1 1 0 0 1 0-2Zm4 0a1 1 0 1 1 0 2 1 1 0 0 1 0-2Zm4 0a1 1 0 1 1 0 2 1 1 0 0 1 0-2Zm4 0a1 1 0 1 1 0 2 1 1 0 0 1 0-2Zm4 0a1 1 0 1 1 0 2 1 1 0 0 1 0-2Zm4 0a1 1 0 1 1 0 2 1 1 0 0 1 0-2ZM3 48a1 1 0 1 1 0 2 1 1 0 0 1 0-2Zm4 0a1 1 0 1 1 0 2 1 1 0 0 1 0-2Zm4 0a1 1 0 1 1 0 2 1 1 0 0 1 0-2Zm4 0a1 1 0 1 1 0 2 1 1 0 0 1 0-2Zm4 0a1 1 0 1 1 0 2 1 1 0 0 1 0-2Zm4 0a1 1 0 1 1 0 2 1 1 0 0 1 0-2ZM3 44a1 1 0 1 1 0 2 1 1 0 0 1 0-2Zm4 0a1 1 0 1 1 0 2 1 1 0 0 1 0-2Zm4 0a1 1 0 1 1 0 2 1 1 0 0 1 0-2Zm4 0a1 1 0 1 1 0 2 1 1 0 0 1 0-2Zm4 0a1 1 0 1 1 0 2 1 1 0 0 1 0-2Zm4 0a1 1 0 1 1 0 2 1 1 0 0 1 0-2ZM3 40a1 1 0 1 1 0 2 1 1 0 0 1 0-2Zm4 0a1 1 0 1 1 0 2 1 1 0 0 1 0-2Zm4 0a1 1 0 1 1 0 2 1 1 0 0 1 0-2Zm4 0a1 1 0 1 1 0 2 1 1 0 0 1 0-2Zm4 0a1 1 0 1 1 0 2 1 1 0 0 1 0-2Zm4 0a1 1 0 1 1 0 2 1 1 0 0 1 0-2ZM3 36a1 1 0 1 1 0 2 1 1 0 0 1 0-2Zm4 0a1 1 0 1 1 0 2 1 1 0 0 1 0-2Zm4 0a1 1 0 1 1 0 2 1 1 0 0 1 0-2Zm4 0a1 1 0 1 1 0 2 1 1 0 0 1 0-2Zm4 0a1 1 0 1 1 0 2 1 1 0 0 1 0-2Zm4 0a1 1 0 1 1 0 2 1 1 0 0 1 0-2ZM3 32a1 1 0 1 1 0 2 1 1 0 0 1 0-2Zm4 0a1 1 0 1 1 0 2 1 1 0 0 1 0-2Zm4 0a1 1 0 1 1 0 2 1 1 0 0 1 0-2Zm4 0a1 1 0 1 1 0 2 1 1 0 0 1 0-2Zm4 0a1 1 0 1 1 0 2 1 1 0 0 1 0-2Zm4 0a1 1 0 1 1 0 2 1 1 0 0 1 0-2Zm4 20a1 1 0 1 1 0 2 1 1 0 0 1 0-2Zm0-4a1 1 0 1 1 0 2 1 1 0 0 1 0-2Zm0-4a1 1 0 1 1 0 2 1 1 0 0 1 0-2Zm0-4a1 1 0 1 1 0 2 1 1 0 0 1 0-2Zm0-4a1 1 0 1 1 0 2 1 1 0 0 1 0-2Zm0-4a1 1 0 1 1 0 2 1 1 0 0 1 0-2Zm4 20a1 1 0 1 1 0 2 1 1 0 0 1 0-2Zm0-4a1 1 0 1 1 0 2 1 1 0 0 1 0-2Zm0-4a1 1 0 1 1 0 2 1 1 0 0 1 0-2Zm0-4a1 1 0 1 1 0 2 1 1 0 0 1 0-2Zm0-4a1 1 0 1 1 0 2 1 1 0 0 1 0-2Zm0-4a1 1 0 1 1 0 2 1 1 0 0 1 0-2Z", clipRule: "evenodd" }), jsx("circle", { cx: "36.5", cy: "19.5", r: "17.5", fill: "#79C9D6" }), jsx("path", { fill: "#10465B", d: "M22.6 28.4c0-1-.7-1.7-1.6-1.7h-5a1.7 1.7 0 1 0 0 3.3h5c.9 0 1.6-.7 1.6-1.6ZM43 20.8c-.2-.9-.9-1.5-1.8-1.6l-3.5-.2c-1.2 0-2.2 1-2 2.1.1.9.9 1.5 1.7 1.6l3.6.2c1.2 0 2.2-1 2-2.1ZM44 35c0-1-.7-1.7-1.6-1.6l-26.7.5a1.8 1.8 0 1 0 0 3.5l26.8-.9c.8 0 1.5-.7 1.5-1.6Z" }), jsx("path", { stroke: "#10465B", strokeWidth: "4", d: "M46 18.5a2.7 2.7 0 0 0-2.6-2.4l-32.5-2C9.2 13.8 8 15.2 8 17l2 21.6A2.7 2.7 0 0 0 13 41l32.4-1c1.6 0 2.9-1.5 2.7-3l-2-18.5Z", clipRule: "evenodd" })] }))),
    }); },
    GetTheAppStep4: function () { return ({
        height: 56,
        width: 56,
        viewBox: '0 0 56 56',
        icon: (jsxs("g", __assign$1({ fill: "none", fillRule: "evenodd" }, { children: [jsxs("g", __assign$1({ clipPath: "url(#a)" }, { children: [jsx("circle", { cx: "44", cy: "35", r: "11", fill: "#AD47AB" }), jsx("path", { fill: "#A7CB19", d: "M4 46.5A2 2 0 0 0 2.6 49l1.1 4a3 3 0 0 0 3.5 2.1l14.3-3a2 2 0 0 0-.8-3.9L7.3 51l-.9-3A2 2 0 0 0 4 46.4Z", clipRule: "evenodd" }), jsx("circle", { cx: "18.5", cy: "18.5", r: "17.5", fill: "#D5EDF7" }), jsx("path", { stroke: "#10465B", strokeLinecap: "round", strokeWidth: "4", d: "M9 45.7 4.6 30A2 2 0 0 1 6 27.5l32.1-8a2 2 0 0 1 2.4 1.3l4.9 16.6a2 2 0 0 1-1.5 2.6L11.3 47A2 2 0 0 1 9 45.7Z" }), jsx("path", { stroke: "#10465B", strokeLinecap: "round", strokeWidth: "4", d: "m45 35.5 5.5-2.2a2 2 0 0 0 1-2.7l-8.2-17.3a2 2 0 0 0-2.6-1L8 27M40 12.5l-4.7-5.1a2 2 0 0 0-2.8-.2L10 26" }), jsx("path", { stroke: "#10465B", strokeWidth: "4", d: "M28.7 31.3c-.3-1-1.3-2.3-2.4-2.7-1-.3-1.7 0-2.5.5a7 7 0 0 0-2.2 1.2c-1 .8-1.4 1.8-1.6 3-.1 1.2.1 2.5 1 3.3 1.3 1 2.5 1 4 .8 1.4 0 2.4-.7 3.3-1.9.9-1.2.8-2.8.4-4.2Z", clipRule: "evenodd" }), jsx("path", { stroke: "#A7CB19", strokeLinejoin: "round", strokeWidth: "4", d: "M32 18c1-2 1.2-4.4.5-7.2 1.6 1.4 2.3 4 6.8 3.9a8.2 8.2 0 0 0-1.3 6.8 8.8 8.8 0 0 0-6-3.6ZM43 10.7c2.3-1.5 3.9-4 4.7-7.5 1 2.6.2 5.9 5.3 8.3a10.2 10.2 0 0 0-5.5 6.7 11 11 0 0 0-4.6-7.5Z", clipRule: "evenodd" })] })), jsx("defs", { children: jsx("clipPath", __assign$1({ id: "a" }, { children: jsx("path", { fill: "#fff", d: "M0 0h56v56H0z" }) })) })] }))),
    }); },
    GetTheAppQuote: function () { return ({
        height: 56,
        width: 56,
        viewBox: '0 0 56 56',
        icon: (jsx("path", { fill: "#A7CB19", fillRule: "evenodd", d: "M2.9 42c3 5.6 9.4 9 15.5 7.1a12 12 0 0 0 8.1-8c1.3-4.2 0-9-3.4-11.9l-.3-.2c-1.5-1.2-3.1-1.8-4.8-2.1h-.3l.6-1 7-11.6-12-7.3-6.9 11.4-.7 1.2-.5.8-.2.4-.4.7-.4.7-.3.6-.3.6c-.4.9-.8 1.7-1 2.5l-.3.6-.2.6-.2.7A19 19 0 0 0 3 42Zm28 0c3 5.6 9.4 9 15.5 7.1a12 12 0 0 0 8.1-8c1.3-4.2 0-9-3.4-11.9l-.3-.2c-1.5-1.2-3.1-1.8-4.8-2.1h-.3l.6-1 7-11.6-12-7.3-6.9 11.4-.7 1.2-.5.8-.2.4-.4.7-.4.7-.3.6-.3.6a27 27 0 0 0-1 2.5l-.3.6-.2.6-.2.7A19 19 0 0 0 31 42Z", clipRule: "evenodd" })),
    }); },
    GetTheAppID: function () { return ({
        height: 56,
        width: 56,
        viewBox: '0 0 56 56',
        icon: (jsxs("g", __assign$1({ fill: "none", fillRule: "evenodd" }, { children: [jsx("circle", { cx: "37.5", cy: "37.5", r: "17.5", fill: "#D5EDF7" }), jsx("path", { fill: "#10465B", d: "M6 8c0-1.1.9-2 2-2h4a2 2 0 1 0 0-4H8a6 6 0 0 0-6 6v4a2 2 0 1 0 4 0V8Zm44 0a2 2 0 0 0-2-2h-4a2 2 0 1 1 0-4h4a6 6 0 0 1 6 6v4a2 2 0 1 1-4 0V8ZM8 50a2 2 0 0 1-2-2v-4a2 2 0 1 0-4 0v4a6 6 0 0 0 6 6h4a2 2 0 1 0 0-4H8Zm42-2a2 2 0 0 1-2 2h-4a2 2 0 1 0 0 4h4a6 6 0 0 0 6-6v-4a2 2 0 1 0-4 0v4ZM43 19H13v18h30V19Zm-30-4a4 4 0 0 0-4 4v18a4 4 0 0 0 4 4h30a4 4 0 0 0 4-4V19a4 4 0 0 0-4-4H13Zm8.5 11a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3Zm3.2 1.7a4.5 4.5 0 0 0-3.2-7.7 4.5 4.5 0 0 0-3.2 7.7 7.5 7.5 0 0 0-4.2 6c-.1.8.4 1.4 1.2 1.5.9.2 1.7-.6 1.9-1.5V33a4.5 4.5 0 0 1 8.7.8c.2.5.5.9 1 1.1 1 .5 2-.2 2-1.3V33a7.5 7.5 0 0 0-4.2-5.3ZM30 25c0-1.1.9-2 2-2h8a2 2 0 1 1 0 4h-8a2 2 0 0 1-2-2Zm2 4a2 2 0 1 0 0 4h8a2 2 0 1 0 0-4h-8Z", clipRule: "evenodd" }), jsx("path", { stroke: "#A7CB19", strokeLinecap: "round", strokeWidth: "4", d: "M50 18.2c.6 3.5 1.4 12.1 0 19.4M14.2 44.2c3.8.8 16 1.8 26.3 0" }), jsx("path", { stroke: "#79C9D6", strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: "4", d: "m4.5 9.9 7.2 7.2M3 23.5h4.1M16.9 9l1.3 4" })] }))),
    }); },
    GetTheAppPhone: function () { return ({
        height: 56,
        width: 56,
        viewBox: '0 0 56 56',
        icon: (jsxs("g", __assign$1({ fill: "none", fillRule: "evenodd" }, { children: [jsxs("g", __assign$1({ clipPath: "url(#a)" }, { children: [jsx("path", { fill: "#FFCB00", d: "M24 42a1 1 0 1 1 0 2 1 1 0 0 1 0-2Zm4 0a1 1 0 1 1 0 2 1 1 0 0 1 0-2Zm4 0a1 1 0 1 1 0 2 1 1 0 0 1 0-2Zm4 0a1 1 0 1 1 0 2 1 1 0 0 1 0-2Zm4 0a1 1 0 1 1 0 2 1 1 0 0 1 0-2Zm4 0a1 1 0 1 1 0 2 1 1 0 0 1 0-2Zm-20-4a1 1 0 1 1 0 2 1 1 0 0 1 0-2Zm4 0a1 1 0 1 1 0 2 1 1 0 0 1 0-2Zm4 0a1 1 0 1 1 0 2 1 1 0 0 1 0-2Zm4 0a1 1 0 1 1 0 2 1 1 0 0 1 0-2Zm4 0a1 1 0 1 1 0 2 1 1 0 0 1 0-2Zm4 0a1 1 0 1 1 0 2 1 1 0 0 1 0-2Zm-20-4a1 1 0 1 1 0 2 1 1 0 0 1 0-2Zm4 0a1 1 0 1 1 0 2 1 1 0 0 1 0-2Zm4 0a1 1 0 1 1 0 2 1 1 0 0 1 0-2Zm4 0a1 1 0 1 1 0 2 1 1 0 0 1 0-2Zm4 0a1 1 0 1 1 0 2 1 1 0 0 1 0-2Zm4 0a1 1 0 1 1 0 2 1 1 0 0 1 0-2Zm-20-4a1 1 0 1 1 0 2 1 1 0 0 1 0-2Zm4 0a1 1 0 1 1 0 2 1 1 0 0 1 0-2Zm4 0a1 1 0 1 1 0 2 1 1 0 0 1 0-2Zm4 0a1 1 0 1 1 0 2 1 1 0 0 1 0-2Zm4 0a1 1 0 1 1 0 2 1 1 0 0 1 0-2Zm4 0a1 1 0 1 1 0 2 1 1 0 0 1 0-2Zm-20-4a1 1 0 1 1 0 2 1 1 0 0 1 0-2Zm4 0a1 1 0 1 1 0 2 1 1 0 0 1 0-2Zm4 0a1 1 0 1 1 0 2 1 1 0 0 1 0-2Zm4 0a1 1 0 1 1 0 2 1 1 0 0 1 0-2Zm4 0a1 1 0 1 1 0 2 1 1 0 0 1 0-2Zm4 0a1 1 0 1 1 0 2 1 1 0 0 1 0-2Zm-20-4a1 1 0 1 1 0 2 1 1 0 0 1 0-2Zm4 0a1 1 0 1 1 0 2 1 1 0 0 1 0-2Zm4 0a1 1 0 1 1 0 2 1 1 0 0 1 0-2Zm4 0a1 1 0 1 1 0 2 1 1 0 0 1 0-2Zm4 0a1 1 0 1 1 0 2 1 1 0 0 1 0-2Zm4 0a1 1 0 1 1 0 2 1 1 0 0 1 0-2Zm4 20a1 1 0 1 1 0 2 1 1 0 0 1 0-2Zm0-4a1 1 0 1 1 0 2 1 1 0 0 1 0-2Zm0-4a1 1 0 1 1 0 2 1 1 0 0 1 0-2Zm0-4a1 1 0 1 1 0 2 1 1 0 0 1 0-2Zm0-4a1 1 0 1 1 0 2 1 1 0 0 1 0-2Zm0-4a1 1 0 1 1 0 2 1 1 0 0 1 0-2Zm4 20a1 1 0 1 1 0 2 1 1 0 0 1 0-2Zm0-4a1 1 0 1 1 0 2 1 1 0 0 1 0-2Zm0-4a1 1 0 1 1 0 2 1 1 0 0 1 0-2Zm0-4a1 1 0 1 1 0 2 1 1 0 0 1 0-2Zm0-4a1 1 0 1 1 0 2 1 1 0 0 1 0-2Zm0-4a1 1 0 1 1 0 2 1 1 0 0 1 0-2Z", clipRule: "evenodd" }), jsx("circle", { cx: "19", cy: "37", r: "18", fill: "#D5EDF7" }), jsx("path", { stroke: "#10465B", strokeLinecap: "round", strokeWidth: "4", d: "M17 39.7A42.9 42.9 0 0 1 5.4 20.2c-.7-2.6.4-5.3 2.3-7.3l2.6-2.6a4 4 0 0 1 5.7 0l4.2 4.3a4 4 0 0 1 0 5.6L18.6 22c-.8.7-1.2 1.8-1 2.8.6 1.7 1.9 4.7 5.5 8.2 3.5 3.6 6.5 5 8.2 5.4 1 .3 2-.1 2.8-.9l1-1a4 4 0 0 1 5.6 0l5 5a4 4 0 0 1 0 5.7l-2 1.9c-1.9 2-4.6 3-7.2 2.3-4.5-1.1-11.9-4-19.5-11.6Z" }), jsx("path", { stroke: "#A7CB19", strokeLinecap: "round", strokeWidth: "4", d: "M30.8 13.9s3.6.7 7.5 4.6c3.8 3.8 4.5 7.4 4.5 7.4M31.5 4.7s6.8 1 12.8 7c6 6 7 12.8 7 12.8" })] })), jsx("defs", { children: jsx("clipPath", __assign$1({ id: "a" }, { children: jsx("path", { fill: "#fff", d: "M0 0h56v56H0z" }) })) })] }))),
    }); },
    GetTheAppComputer: function () { return ({
        height: 56,
        width: 56,
        viewBox: '0 0 56 56',
        icon: (jsxs("g", __assign$1({ fill: "none", fillRule: "evenodd" }, { children: [jsxs("g", __assign$1({ clipPath: "url(#a)" }, { children: [jsx("circle", { cx: "43", cy: "43", r: "12", fill: "#A7CB19" }), jsx("path", { fill: "#79C9D6", d: "M1.2 20.1a.9.9 0 0 1 .1-1.2l2.8-2c.3-.2.4-.6.3-1l-1.1-3.3c-.2-.5 0-1 .5-1.1l3.2-.8c.4-.1.6-.4.6-.8V6.3c0-.5.5-.9 1-.8l3.2.4c.3 0 .7-.2.8-.5L13.7 2c.2-.4.7-.6 1.1-.4l3 1.6c.3.2.7 0 .9-.2L20.8.3c.3-.4.9-.4 1.2 0l2.3 2.6c.2.2.6.3.9.1l2.9-1.7c.4-.3.9 0 1 .4L30.6 5c.1.3.5.5.8.5l3.3-.6c.4 0 .8.3.9.8l.2 3.5c0 .4.3.7.6.8l3.2.7c.5 0 .8.6.7 1l-1 3.5c0 .3 0 .7.4 1l2.8 1.8c.4.2.5.8.3 1.2l-2 2.9c-.2.3-.2.7 0 1l2.1 2.8c.3.4.2 1-.1 1.2l-2.8 2c-.3.2-.4.6-.3 1l1.1 3.3c.2.5 0 1-.5 1.1l-3.2.8c-.4.1-.6.4-.6.8v3.6c0 .5-.5.9-1 .8l-3.2-.4c-.3 0-.7.2-.8.5L30.3 44c-.2.4-.7.6-1.1.4l-3-1.6a.7.7 0 0 0-.9.2l-2.1 2.7c-.3.4-.9.4-1.2 0l-2.3-2.6a.7.7 0 0 0-.9-.1l-2.9 1.7c-.4.3-.9 0-1-.4L13.4 41a.8.8 0 0 0-.8-.5l-3.3.6c-.4 0-.8-.3-.9-.8l-.2-3.5c0-.4-.3-.7-.6-.8l-3.2-.7c-.5 0-.8-.6-.7-1l1-3.5c0-.3 0-.7-.4-1L1.6 28a.9.9 0 0 1-.3-1.2l2-2.9c.2-.3.2-.7 0-1l-2.1-2.8Z", clipRule: "evenodd" }), jsx("path", { fill: "#10465B", d: "M44.5 19.2 9 13.2 6.2 30l35.5 6 2.8-16.8ZM5.2 35.9l.3-2L41 40l-.3 2-13.8-2.3-7.9-1.3-13.8-2.4ZM40 46l-11.1-2 .8 6.3 3 .5a2 2 0 1 1-.7 4l-4.4-.8h-.1l-13.7-2.3h-.1l-5-.9a2 2 0 0 1 .7-3.9l3.5.6 2.8-5.6-11.2-2a4 4 0 0 1-3.3-4.5L5 12.6a4 4 0 0 1 4.6-3.3l35.5 6a4 4 0 0 1 3.3 4.5l-3.8 22.7a4 4 0 0 1-4.6 3.3ZM17 48l8.5 1.4-.8-6.2-5-.9-2.7 5.7Z", clipRule: "evenodd" }), jsx("path", { fill: "#10465B", stroke: "#10465B", strokeWidth: "2", d: "M30.4 22a.9.9 0 0 1 1.1 1.3L24 29.7c-.4.3-1 .2-1.3-.2l-2.8-4a.9.9 0 0 1 1.4-1l2.3 3.2 6.8-5.7Z", clipRule: "evenodd" })] })), jsx("defs", { children: jsx("clipPath", __assign$1({ id: "a" }, { children: jsx("path", { fill: "#fff", d: "M0 0h56v56H0z" }) })) })] }))),
    }); },
    GetTheAppCheck: function () { return ({
        height: 56,
        width: 56,
        viewBox: '0 0 56 56',
        icon: (jsxs("g", __assign$1({ fill: "none", fillRule: "evenodd" }, { children: [jsx("path", { fill: "#79C9D6", d: "M31.6 4.2c.4-.3 1-.3 1.3.2l2 3c.2.3.6.5 1 .3l3.5-1.4c.5-.2 1 .1 1.1.6l.8 3.6c0 .4.4.7.8.7L46 11c.5 0 .9.4.8 1l-.6 3.6c0 .4.2.8.6 1l3.4 1.1c.5.2.7.8.5 1.2l-1.8 3.4c-.2.4-.1.8.2 1.1l2.7 2.3c.4.4.4 1 0 1.3L49 29.8a1 1 0 0 0-.2 1.1l1.8 3.2c.2.4 0 1-.5 1.2L46.5 37a1 1 0 0 0-.5 1l.5 3.6c0 .5-.3 1-.8 1l-3.8.4a1 1 0 0 0-.8.8l-.8 3.7c-.1.5-.6.8-1.1.7l-3.6-.9a1 1 0 0 0-1 .4l-2 3.3c-.3.5-.9.6-1.3.3l-3-2a.9.9 0 0 0-1 0l-3 2.5c-.3.3-1 .3-1.2-.2l-2-3a.8.8 0 0 0-1-.3l-3.5 1.4c-.5.2-1-.1-1.1-.6l-.8-3.6c0-.4-.4-.7-.8-.7L10 45a.8.8 0 0 1-.8-1l.6-3.6c0-.4-.2-.8-.6-1L6 38.3c-.5-.2-.7-.8-.5-1.2l1.8-3.4c.2-.4.1-.8-.2-1.1l-2.7-2.3c-.4-.4-.4-1 0-1.3L7 26.2c.3-.3.3-.8.2-1.1l-1.8-3.2c-.2-.4 0-1 .5-1.2L9.5 19c.3-.1.5-.5.5-1l-.5-3.6c0-.5.3-1 .8-1l3.8-.4c.4 0 .7-.4.8-.8l.8-3.7c.1-.5.6-.8 1.1-.7l3.6.9c.4.1.8 0 1-.4l2-3.3c.3-.5.9-.6 1.3-.3l3 2c.2.3.7.3 1 0l3-2.5Z", clipRule: "evenodd" }), jsx("path", { fill: "#10465B", d: "M41.5 18.5c.7.7.7 1.9 0 2.6L25.3 37.5c-.7.7-1.9.7-2.6 0l-7.2-7.3c-.7-.7-.7-1.9 0-2.6.7-.7 1.9-.7 2.6 0l5.9 6 15-15c.6-.8 1.8-.8 2.5 0Z", clipRule: "evenodd" }), jsx("path", { stroke: "#AD47AB", strokeLinejoin: "round", strokeWidth: "4", d: "M30.5 45c1-2 1.2-4.4.5-7.2 1.7 1.4 2.4 4 6.9 3.9a8.2 8.2 0 0 0-1.4 6.8 8.8 8.8 0 0 0-6-3.6ZM42.9 43.5c2.7-.6 5.1-2.4 7.2-5.4 0 2.8-2 5.6 1.9 9.7-2.8.1-5.6 1.6-7.5 4.3a11 11 0 0 0-1.6-8.6Z", clipRule: "evenodd" }), jsx("path", { stroke: "#F5940D", strokeLinecap: "round", strokeWidth: "4", d: "m14.5 13-6-10M8 15.5l-5-3M20 9.5 22 4" })] }))),
    }); },
    GetTheAppFast: function () { return ({
        height: 56,
        width: 56,
        viewBox: '0 0 56 56',
        icon: (jsxs("g", __assign$1({ fill: "none", fillRule: "evenodd" }, { children: [jsx("circle", { cx: "19", cy: "37", r: "18", fill: "#D5EDF7" }), jsx("path", { stroke: "#10465B", strokeLinecap: "round", strokeWidth: "4", d: "M9 45.7 4.6 30A2 2 0 0 1 6 27.5l32.1-8a2 2 0 0 1 2.4 1.3l4.9 16.6a2 2 0 0 1-1.5 2.6L11.3 47A2 2 0 0 1 9 45.7Z" }), jsx("path", { stroke: "#10465B", strokeLinecap: "round", strokeWidth: "4", d: "m45 35.5 5.5-2.2a2 2 0 0 0 1-2.7l-8.2-17.3a2 2 0 0 0-2.6-1L8 27M40 12.5l-4.7-5.1a2 2 0 0 0-2.8-.2L10 26" }), jsx("path", { stroke: "#10465B", strokeWidth: "4", d: "M28.7 31.3c-.3-1-1.3-2.3-2.4-2.7-1-.3-1.7 0-2.5.5a7 7 0 0 0-2.2 1.2c-1 .8-1.4 1.8-1.6 3-.1 1.2.1 2.5 1 3.3 1.3 1 2.5 1 4 .8 1.4 0 2.4-.7 3.3-1.9.9-1.2.8-2.8.4-4.2Z", clipRule: "evenodd" }), jsx("path", { stroke: "#A7CB19", strokeLinecap: "round", strokeWidth: "4", d: "M6 20.2h4M3 13l13 .2M12 6.3h11" }), jsx("path", { stroke: "#AD47AB", strokeLinejoin: "round", strokeWidth: "4", d: "M41.6 40.6c2.5-1.2 4.5-3.5 5.8-6.8.6 2.6-.6 5.8 4 8.9-2.6.8-5 2.8-6.3 6a11 11 0 0 0-3.5-8ZM31.5 49c1-2 1.2-4.4.5-7.2 1.7 1.4 2.4 4 6.9 3.9a8.2 8.2 0 0 0-1.4 6.8 8.8 8.8 0 0 0-6-3.6Z", clipRule: "evenodd" })] }))),
    }); },
    GetTheAppUpwards: function () { return ({
        height: 56,
        width: 56,
        viewBox: '0 0 56 56',
        icon: (jsxs("g", __assign$1({ fill: "none", fillRule: "evenodd" }, { children: [jsxs("g", __assign$1({ clipPath: "url(#a)" }, { children: [jsx("path", { fill: "#79C9D6", d: "M29.3 4.2c.4-.3 1-.3 1.2.1l1.8 2.8c.2.3.6.4 1 .3l3.2-1.3c.4-.1 1 .1 1 .6l.7 3.3c0 .3.4.6.7.6l3.5-.1c.4 0 .8.4.7.8l-.5 3.5c0 .3.2.7.5.8l3.2 1c.4.2.6.7.4 1.2L45 20.9c-.2.3-.1.7.1 1l2.5 2.1c.4.3.4.9 0 1.2l-2.5 2.5c-.3.2-.4.6-.2 1l1.6 2.9c.2.4 0 1-.4 1.1L43 34.2c-.3.2-.5.6-.5 1l.4 3.3c.1.4-.2.9-.7 1l-3.4.3c-.4 0-.7.3-.8.7l-.7 3.4c-.1.4-.6.7-1 .6l-3.3-.8c-.4-.1-.7 0-1 .4l-1.8 3c-.2.4-.8.6-1.1.3l-2.7-2a.8.8 0 0 0-1 .1l-2.7 2.3c-.4.3-1 .3-1.2-.1l-1.8-2.8a.7.7 0 0 0-1-.3l-3.2 1.3c-.4.1-.9-.1-1-.6l-.7-3.3c0-.3-.4-.6-.7-.6l-3.5.1a.7.7 0 0 1-.7-.8l.5-3.5c0-.3-.2-.7-.5-.8l-3.2-1c-.4-.2-.6-.7-.4-1.2L7 31.1c.2-.3.1-.7-.1-1L4.3 28c-.4-.3-.4-.9 0-1.2l2.5-2.5c.3-.2.4-.6.2-1l-1.6-2.9c-.2-.4 0-1 .4-1.1L9 17.8c.3-.2.5-.6.5-1l-.4-3.3c-.1-.4.2-.9.7-1l3.4-.3c.4 0 .7-.3.8-.7l.7-3.4c.1-.4.6-.7 1-.6l3.3.8c.4.1.7 0 1-.4l1.8-3c.2-.4.8-.6 1.1-.3l2.7 2c.3.2.7.1 1-.1l2.7-2.3Z", clipRule: "evenodd" }), jsx("path", { stroke: "#10465B", strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: "4", d: "M17.9 16.2 31 33.8M18 16.2l-1.7 12m1.7-12L29.8 18" }), jsx("path", { stroke: "#F5940D", strokeLinecap: "round", strokeWidth: "4", d: "M12.7 11.5 4 3.8M9.8 16.6 4 16.5M18 8.9 17.8 3" }), jsx("circle", { cx: "43", cy: "43", r: "12", fill: "#AD47AB" }), jsx("path", { stroke: "#fff", strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: "4", d: "m37 43 4 4 8-8" })] })), jsx("defs", { children: jsx("clipPath", __assign$1({ id: "a" }, { children: jsx("path", { fill: "#fff", d: "M0 0h56v56H0z" }) })) })] }))),
    }); },
};

var Gift = {
    Gift: function () { return ({
        height: 100,
        width: 100,
        viewBox: '0 0 100 100',
        icon: (jsx("g", __assign$1({ id: "Desktop", stroke: "none", strokeWidth: "1", fill: "none", fillRule: "evenodd" }, { children: jsx("g", __assign$1({ id: "01.1_Desktop_Home_Sent", transform: "translate(-671.000000, -333.000000)" }, { children: jsx("g", __assign$1({ id: "Group-19", transform: "translate(476.000000, 284.000000)" }, { children: jsxs("g", __assign$1({ id: "Group", transform: "translate(194.000000, 48.000000)" }, { children: [jsx("circle", { cx: "81.5", cy: "19.5", r: "15.5", fill: "#A7CB19" }), jsx("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M55.0741 6.36119C55.7997 5.75688 56.8796 5.9304 57.4052 6.73576L61.2356 12.6055C61.6414 13.2275 62.4012 13.4939 63.0849 13.2538L69.5537 10.9822C70.4398 10.671 71.4018 11.2144 71.6292 12.1545L73.2783 18.9734C73.4541 19.7003 74.0841 20.2182 74.8104 20.2328L81.6696 20.3702C82.6114 20.389 83.3402 21.2391 83.24 22.2015L82.5174 29.1423C82.4397 29.8871 82.8639 30.5964 83.5469 30.8651L89.9621 33.3879C90.8471 33.736 91.2543 34.7922 90.8362 35.6595L87.8377 41.8901C87.5144 42.5614 87.6792 43.3775 88.2363 43.8701L93.4369 48.4645C94.1588 49.102 94.1914 50.2365 93.5069 50.9019L88.5937 55.6808C88.0646 56.1954 87.948 57.0184 88.3101 57.6764L91.6777 63.7859C92.1471 64.6366 91.8005 65.7103 90.9341 66.0926L84.6907 68.8481C84.0209 69.1435 83.6378 69.8715 83.7598 70.6145L84.8951 77.509C85.0536 78.469 84.3713 79.3507 83.4296 79.4049L76.5998 79.7993C75.8725 79.8411 75.2706 80.3858 75.138 81.1219L73.9015 87.9803C73.7301 88.931 72.7977 89.5144 71.8927 89.2362L65.3015 87.2125C64.6035 86.9979 63.8579 87.2954 63.4881 87.9346L60.0196 93.9307C59.5414 94.7574 58.4715 94.9743 57.7113 94.3986L52.1616 90.1962C51.5751 89.7515 50.7753 89.7677 50.2148 90.2342L44.9258 94.6388C44.2002 95.2432 43.1203 95.0696 42.5948 94.2638L38.7644 88.3948C38.3585 87.7726 37.5987 87.5061 36.915 87.7463L30.4463 90.018C29.5601 90.3287 28.5982 89.7856 28.3708 88.8457L26.7216 82.0268C26.5458 81.2993 25.9158 80.7817 25.1895 80.767L18.3304 80.6299C17.3888 80.6105 16.6598 79.7606 16.76 78.7983L17.4826 71.8576C17.5601 71.1128 17.1362 70.4035 16.4531 70.1349L10.0377 67.612C9.15243 67.2639 8.74589 66.2077 9.16331 65.3404L12.1622 59.1099C12.4853 58.4385 12.3209 57.6223 11.7634 57.1299L6.56289 52.5354C5.8413 51.898 5.80868 50.7634 6.4928 50.098L11.4063 45.3191C11.9355 44.8044 12.0523 43.9815 11.6896 43.3235L8.32194 37.214C7.85301 36.3633 8.19917 35.2896 9.06548 34.9073L15.3094 32.1518C15.9789 31.8565 16.3624 31.1283 16.2401 30.3855L15.1046 23.4905C14.9466 22.5308 15.6282 21.6495 16.5706 21.595L23.4002 21.2005C24.1274 21.1584 24.7293 20.614 24.8619 19.878L26.0984 13.0198C26.2698 12.0689 27.2022 11.4857 28.1072 11.7635L34.6985 13.7874C35.3964 14.0017 36.142 13.7049 36.5118 13.0656L39.9803 7.06902C40.4586 6.24233 41.5285 6.02542 42.2887 6.60111L47.8383 10.8039C48.4248 11.2481 49.2247 11.2324 49.7851 10.7656L55.0741 6.36119Z", fill: "#D5EDF7" }), jsx("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M77.0046 33.6181C73.7761 33.3875 64.2114 33.8121 53.7825 34.4101C43.2674 35.0131 31.6507 35.8076 24.1619 36.3556C23.5043 36.4037 22.9126 36.7729 22.5803 37.3425C21.8306 38.6278 20.9028 40.8067 20.3895 43.3784C19.8753 45.954 19.7493 49.0646 20.7682 52.1212C21.0518 52.9721 21.8672 53.5301 22.763 53.4864C23.7376 53.4389 24.6979 53.3924 25.6447 53.3467C25.756 56.9914 25.9608 61.7703 26.2679 66.1978C26.4493 68.813 26.6678 71.3264 26.9266 73.4198C27.1766 75.4417 27.4865 77.2801 27.9096 78.4084C28.2084 79.2052 28.8055 79.7022 29.2892 79.9995C29.7866 80.3052 30.3564 80.519 30.9149 80.6788C32.0359 80.9995 33.4639 81.1992 35.047 81.3236C38.2371 81.5742 42.4477 81.5463 46.9853 81.3605C56.0815 80.9882 66.7557 79.9697 73.8097 79.1452C74.7451 79.0359 75.4775 78.2888 75.5682 77.3514C76.0961 71.8957 76.7386 59.832 75.2267 49.7489C75.4977 49.7124 75.7689 49.6754 76.0405 49.638C76.5572 49.5667 77.0257 49.2963 77.3459 48.8846C78.9835 46.7792 81.2087 41.3428 78.7288 34.8951C78.4503 34.1711 77.7783 33.6734 77.0046 33.6181ZM52.5745 51.9292C55.8717 51.7119 59.0523 51.4723 62.1797 51.1942C65.2854 51.0303 68.3429 50.8843 71.3324 50.7612C72.5664 59.3895 72.1905 69.5993 71.7285 75.3574C66.5909 75.9338 59.9586 76.5701 53.4957 76.9995C52.0644 68.3371 52.248 62.8182 52.4437 56.9351V56.9351C52.4975 55.3182 52.5521 53.6739 52.5745 51.9292ZM35.6273 52.8657C40.2178 52.6412 44.4955 52.4218 48.5696 52.1796C48.5456 53.6788 48.4969 55.1342 48.4483 56.5904C48.2516 62.4754 48.0544 68.3741 49.4821 77.2392C48.5832 77.2863 47.6948 77.3281 46.8217 77.3639C42.3311 77.5477 38.3095 77.5676 35.3603 77.3358C33.8737 77.219 32.7553 77.0449 32.0151 76.8331C31.8047 76.7729 31.6527 76.7173 31.5476 76.6726C31.3407 75.9496 31.1134 74.6842 30.8964 72.929C30.65 70.9361 30.4374 68.502 30.2583 65.9211C29.9573 61.5809 29.7553 56.8842 29.6448 53.2879C31.6334 53.1463 33.6291 53.0052 35.6273 52.8657ZM61.9044 47.2032C66.1853 46.8215 70.3588 46.3672 74.5995 45.7973C75.4338 44.3106 76.4215 41.2734 75.3999 37.5586C71.5678 37.4995 63.309 37.8704 54.0115 38.4035C44.0315 38.9758 33.0497 39.7216 25.5691 40.2637C25.1191 41.2368 24.6239 42.5992 24.3121 44.1614C23.9784 45.8331 23.8716 47.647 24.1942 49.4121C25.8974 49.3296 27.5554 49.25 29.1725 49.1724C31.3179 49.0695 33.3912 48.97 35.4018 48.8717C44.3111 48.2497 53.2868 47.6584 61.9044 47.2032ZM31.3648 76.5791C31.365 76.5791 31.3681 76.5811 31.3735 76.5852C31.3674 76.5813 31.3647 76.5792 31.3648 76.5791Z", fill: "#10465B" }), jsx("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M55.2002 33.5205C55.3243 33.455 55.449 33.3885 55.5741 33.3209C56.7299 32.5696 57.9315 31.8454 59.1391 31.1935C61.6888 29.5335 64.2147 27.5956 66.2723 25.6621C68.4823 23.5855 69.8869 21.7554 70.3503 20.4851C70.4563 20.1944 70.4913 19.9885 70.4969 19.8574C70.502 19.7365 70.4821 19.6936 70.4812 19.6917L70.4812 19.6917C70.4798 19.6885 70.4767 19.682 70.4673 19.6701C70.4574 19.6576 70.435 19.6324 70.3905 19.5975C70.2985 19.5253 70.1129 19.4127 69.7727 19.3019C66.4578 18.2226 63.1525 20.261 60.0076 24.5837C58.0275 27.3053 56.3899 30.5859 55.2002 33.5205ZM45.4525 34.023C45.8827 34.1192 46.3113 34.1974 46.7359 34.2565C45.2846 30.9553 43.3143 27.2026 41.1793 24.4248C39.7728 22.5949 38.4599 21.4089 37.3597 20.9173C36.8445 20.687 36.445 20.6399 36.1247 20.6844C35.814 20.7275 35.4188 20.8784 34.937 21.3143C33.6198 22.5059 33.3537 23.7067 33.6079 24.9202C33.894 26.2857 34.9012 27.8792 36.5951 29.4301C38.1171 30.8236 40.0216 32.0289 42.0398 32.9021C43.1922 33.237 44.3405 33.6182 45.4525 34.023ZM40.6989 36.6783C39.375 36.3013 38.0645 35.9984 36.8312 35.8109C34.6746 35.4829 33.0409 35.5554 32.0169 35.9529C31.544 36.1365 31.2582 36.367 31.0762 36.6156C30.8953 36.8627 30.7218 37.2574 30.6846 37.9272C30.6472 38.5998 30.7786 38.9804 30.9118 39.2027C31.0441 39.4237 31.2733 39.6476 31.7052 39.8454C32.6573 40.2815 34.2676 40.44 36.467 40.2194C39.1938 39.9459 42.3458 39.145 45.2212 38.2091C44.9229 38.0935 44.6213 37.9793 44.3173 37.8671C43.0909 37.5764 41.8747 37.1727 40.6989 36.6783ZM50.8463 33.6899C52.1528 30.1436 54.1705 25.8076 56.773 22.2304C60.0525 17.7228 64.9678 13.5308 71.0111 15.4984C72.3615 15.9381 73.55 16.7414 74.1393 18.0735C74.7126 19.3695 74.5203 20.7256 74.1081 21.8557C73.3257 24.0008 71.4035 26.304 69.1861 28.412C69.2369 28.4196 69.2876 28.4278 69.3381 28.4365C70.4636 28.6305 71.5449 29.1033 72.4315 29.9676C73.3122 30.8261 73.8787 31.9517 74.1826 33.2529C74.487 34.5564 74.4766 35.8151 74.0827 36.978C73.6873 38.1456 72.9551 39.069 72.0564 39.7763C70.3298 41.1352 67.904 41.7676 65.5047 42.0553C62.647 42.398 59.3908 42.2973 56.3738 41.9946C56.8671 42.9807 57.3357 44.0246 57.7659 45.0463C58.6097 47.0502 59.3438 49.0613 59.8651 50.5645C60.1264 51.3179 60.3357 51.9479 60.4801 52.391C60.5524 52.6126 60.6084 52.7877 60.6467 52.9083L60.6907 53.0474L60.7023 53.0845L60.7055 53.0947L60.7064 53.0976L60.7067 53.0985C60.7068 53.0988 60.7068 53.0991 58.8794 53.6662L60.7068 53.0991C61.0342 54.154 60.4444 55.2746 59.3895 55.602C58.3346 55.9294 57.214 55.3396 56.8866 54.2847L56.8861 54.2833L56.8839 54.2762L56.8743 54.2452L56.8344 54.1191C56.7989 54.0074 56.7459 53.842 56.677 53.6305C56.5392 53.2075 56.3379 52.6015 56.086 51.8752C55.5809 50.4188 54.8779 48.4951 54.0794 46.5985C53.2718 44.6806 52.4037 42.8806 51.5805 41.623C51.5013 41.502 51.425 41.3898 51.3515 41.286C51.0872 41.2377 50.8298 41.1886 50.58 41.1393C50.4953 41.1225 50.4125 41.1005 50.332 41.0735C49.9419 41.5435 49.4921 42.1685 48.995 42.9341C47.7702 44.8202 46.4053 47.3287 45.1156 49.8741C43.8316 52.4084 42.646 54.9316 41.7801 56.8247C41.3476 57.7703 40.9959 58.5564 40.7527 59.1052C40.6311 59.3795 40.5367 59.5944 40.473 59.7402L40.4008 59.9057L40.3827 59.9472L40.3784 59.9573L40.3774 59.9596L40.3772 59.9601L38.8603 59.3042L40.3772 59.9601C39.9387 60.9739 38.7615 61.4404 37.7476 61.002C36.7338 60.5636 36.2673 59.3863 36.7057 58.3724L38.3522 59.0844C36.7057 58.3724 36.7058 58.3722 36.7059 58.372L36.7063 58.3711L36.7077 58.3678L36.7129 58.3559L36.7326 58.3106L36.8083 58.1371C36.8744 57.9859 36.9714 57.7651 37.0957 57.4845C37.3444 56.9235 37.7026 56.1228 38.1426 55.1609C39.0216 53.2391 40.2312 50.6642 41.5475 48.0663C42.4905 46.205 43.5008 44.307 44.4931 42.6191C41.9621 43.3556 39.3 43.9553 36.8662 44.1994C34.4716 44.4396 31.9829 44.3723 30.0394 43.482C29.0234 43.0167 28.1055 42.3022 27.4801 41.2579C26.8556 40.215 26.6186 39.0034 26.6907 37.7053C26.763 36.4043 27.1335 35.2296 27.8486 34.2528C28.5626 33.2775 29.5248 32.6296 30.5693 32.2241C31.3661 31.9148 32.2363 31.7362 33.1443 31.6557C31.464 29.9389 30.1489 27.917 29.6929 25.7404C29.1392 23.0974 29.9114 20.4668 32.2535 18.348C33.2354 17.4597 34.3508 16.8923 35.5746 16.7224C36.7889 16.5538 37.9477 16.7988 38.9916 17.2653C41.0097 18.1671 42.8178 19.9927 44.3508 21.9872C46.9796 25.4076 49.2867 29.9792 50.8463 33.6899ZM55.9133 37.9218C56.4704 37.5292 57.0439 37.1388 57.6274 36.7572C58.7999 36.1186 59.9961 35.4037 61.1817 34.6365C62.1374 34.1258 63.0859 33.6721 64.0011 33.303C66.0486 32.4772 67.6289 32.2008 68.6586 32.3783C69.1236 32.4585 69.4237 32.6216 69.6394 32.8318C69.8609 33.0478 70.1175 33.4354 70.2874 34.1627C70.4568 34.8878 70.4041 35.3702 70.2942 35.6948C70.1858 36.0148 69.9733 36.3255 69.5826 36.633C68.7303 37.3038 67.1982 37.8236 65.0286 38.0838C62.2594 38.4158 58.9487 38.2718 55.9133 37.9218Z", fill: "#79C9D6" }), jsx("path", { d: "M11.0097 20.4947L4.83112 20.4162", stroke: "#F48122", strokeWidth: "4", strokeLinecap: "round" }), jsx("path", { d: "M21.1884 3.94878L22.3703 10.307", stroke: "#F48122", strokeWidth: "4", strokeLinecap: "round" }), jsx("path", { d: "M9.35428 5.65198L17.1004 16.2565", stroke: "#F48122", strokeWidth: "4", strokeLinecap: "round" }), jsx("path", { d: "M88.6767 78.48L94.8171 77.7891", stroke: "#F48122", strokeWidth: "4", strokeLinecap: "round" }), jsx("path", { d: "M80.6358 96.1637L78.6719 90.0019", stroke: "#F48122", strokeWidth: "4", strokeLinecap: "round" }), jsx("path", { d: "M92.1661 93.0014L83.1607 83.443", stroke: "#F48122", strokeWidth: "4", strokeLinecap: "round" })] })) })) })) }))),
    }); },
};

var Logos = {
    LogoProvidentLight: function () { return ({
        width: 54,
        height: 32,
        viewBox: '0 0 54 32',
        icon: (jsxs(Fragment, { children: [jsxs("g", __assign$1({ fill: "#fff", fillRule: "evenodd", clipPath: "url(#a)", clipRule: "evenodd" }, { children: [jsx("path", { fillOpacity: ".7", d: "M37.8 16.4v.1l.1.2v-.2c0-.2 0-.4-.2-.5l.1.4ZM11.7 14v2.5h1.7c1 0 1.4-.6 1.4-1.3 0-.6-.5-1.3-1.4-1.2h-1.7Z" }), jsx("path", { d: "m37.8 16.4-.1-.4c-.6-.5-1.9-.3-1.8.5v.6h2V16.5h-.1ZM21.1 19.3c.5 0 1-.3 1-.9v-1.9c0-.5-.5-.7-1-.7s-1 .2-1 .7v2l1 .8ZM35.9 18v.5c0 .9 1.5 1.1 2 .2l.3.2V18h-2.3Z" }), jsx("path", { d: "m25.7 19 .1-1 1.1-3.2h1.3l-2 5.5H25l-2-5.5h1.3l1.1 3.2.2 1Zm3.3-6.3c.4 0 .7.3.7.8 0 .4-.3.7-.7.7a.7.7 0 0 1-.8-.7c0-.5.3-.8.8-.8Zm1 3.9c0-1.7 2-2.3 3.2-1.7v-2.2h1.1V20c-1.6.7-4.1.9-4.3-1.4v-2Zm-1.6 3.7h1.2v-5.6h-1.2v5.6ZM24.7 5.5l-.5.2a53 53 0 0 0-1.1.5l-.5.3c-.2 0-.4.2-.5.3l-.5.2-1 .5-.4.3-.3.2h-.1c-.1.2-.2.2-.4.3l-.3.3-.4.2-.3.3-.3.2-.3.3c-1.7 1.3-2 3.2-.7 5.4l.5-.2h1.2v1c-.3-.1-.8 0-1 .1l1.2 1.5v-.9c0-1.2 1-1.9 2.1-1.9s2.2.7 2.2 2v1.9c0 .8-.5 1.3-1 1.6a39.9 39.9 0 0 0 4.9 3 119 119 0 0 0 7.5 3.1 11.6 11.6 0 0 0 1.7-2c0 .1 0 0 0 0l.3-.2.2-.3.1-.3.6-1.2.1-.3v-.1l.1-.2v-.1a9 9 0 0 0 .3-1V20c-.3.2-.8.4-1.2.4-1.1 0-2.2-.7-2.2-2v-1.9c0-1.2 1.1-1.8 2.2-1.8v-.1l-.2-.4h-.1c0-.2-.2-.3-.3-.5l-.3-.4-.3-.4a20 20 0 0 0-1.4-1.6v-.1l-.4-.3-.4-.4a22 22 0 0 0-.8-.7 22.9 22.9 0 0 0-.8-.7l-1-.6c-.1-.2-.3-.2-.4-.3A23.5 23.5 0 0 0 28.6 7l-1.4-.7-2.5-1Z" }), jsx("path", { d: "M33.2 19.3V16c-.7-.4-2-.3-2 .6v2.1c0 1 1.3 1 2 .7Z" }), jsx("path", { fillOpacity: ".7", d: "M13.4 17.7h-1.7v2.6h-1.2v-7.6h3c1.6 0 2.5 1.3 2.5 2.5 0 1.3-.9 2.5-2.6 2.5Zm14.9 5.9-1-.5h-.1l-1-.5-4-2.5c-.3.2-.7.2-1 .3-1.1 0-2.2-.7-2.2-2v-1c-.5-.5-1-1-1.3-1.5-.3.2-.4.4-.4.7v3.7H16v-3.7c0-.8.4-1.3 1-1.6-1.2-2.2-1-4 .7-5.4l.3-.2.3-.3.3-.2.3-.3h.1l.3-.2.4-.3.4-.3.4-.2 1-.6.4-.2a36.5 36.5 0 0 1 1-.6h.1l.5-.2a49 49 0 0 1 1.1-.5c-2.3-.8-4.7-1-6.8-1C11.2 4.9 3 9.7.5 17.6c-1.7 5.3 2.2 12.8 16 13.8a25 25 0 0 0 18.2-5c-2.1-.8-4.3-1.7-6.4-2.7ZM46.6 20.3c-1.3.2-2.4-.3-2.4-2v-5.6h1.2v2h1V16h-1v2.5c0 .8.6.9 1.2.9v1Zm-2.8 0h-1.2v-3.7c0-.9-1.4-1-2-.6v4.3h-1.1V15c1.5-.8 4.3-.6 4.3 1.5v3.7ZM41.6.7a61 61 0 0 0-16.9 4.8 23.8 23.8 0 0 1 4 1.6l.9.5.9.6.4.2 1 .7.3.3.5.4.4.3.4.4.4.3.3.4h.1l.3.4.4.4.3.4.4.4v.1l.3.3v.1l.3.4.3.4v.1l.3.4c1 0 2.1.7 2.1 2V18h-.8v.8l.7.4c-.2.4-.5.6-.7.8v.4l-.1.2a8.2 8.2 0 0 1-.2.9l-.1.3c0 .2-.1.3-.2.3l-.5 1.3-.2.3-.2.3a10 10 0 0 1-.4.6l-1.6 1.6c8.3 3 15.8 3.6 17.8-3.6C55.7 11 51.1 0 41.5.6Z" })] })), jsx("defs", { children: jsx("clipPath", __assign$1({ id: "a" }, { children: jsx("path", { fill: "#fff", d: "M0 .5h54v31H0z" }) })) })] })),
    }); },
    LogoProvidentDark: function () { return ({
        width: 54,
        height: 32,
        viewBox: '0 0 54 32',
        icon: (jsxs(Fragment, { children: [jsxs("g", __assign$1({ fill: "#0E5F80", fillRule: "evenodd", clipPath: "url(#a)", clipRule: "evenodd" }, { children: [jsx("path", { fillOpacity: ".7", d: "M37.8 16.4v.1l.1.2v-.2c0-.2 0-.4-.2-.5l.1.4ZM11.7 14v2.5h1.7c1 0 1.4-.6 1.4-1.3 0-.6-.5-1.3-1.4-1.2h-1.7Z" }), jsx("path", { d: "m37.8 16.4-.1-.4c-.6-.5-1.9-.3-1.8.5v.6h2V16.5h-.1ZM21.1 19.3c.5 0 1-.3 1-.9v-1.9c0-.5-.5-.7-1-.7s-1 .2-1 .7v2l1 .8ZM35.9 18v.5c0 .9 1.5 1.1 2 .2l.3.2V18h-2.3Z" }), jsx("path", { d: "m25.7 19 .1-1 1.1-3.2h1.3l-2 5.5H25l-2-5.5h1.3l1.1 3.2.2 1Zm3.3-6.3c.4 0 .7.3.7.8 0 .4-.3.7-.7.7a.7.7 0 0 1-.8-.7c0-.5.3-.8.8-.8Zm1 3.9c0-1.7 2-2.3 3.2-1.7v-2.2h1.1V20c-1.6.7-4.1.9-4.3-1.4v-2Zm-1.6 3.7h1.2v-5.6h-1.2v5.6ZM24.7 5.5l-.5.2a53 53 0 0 0-1.1.5l-.5.3c-.2 0-.4.2-.5.3l-.5.2-1 .5-.4.3-.3.2h-.1c-.1.2-.2.2-.4.3l-.3.3-.4.2-.3.3-.3.2-.3.3c-1.7 1.3-2 3.2-.7 5.4l.5-.2h1.2v1c-.3-.1-.8 0-1 .1l1.2 1.5v-.9c0-1.2 1-1.9 2.1-1.9s2.2.7 2.2 2v1.9c0 .8-.5 1.3-1 1.6a39.9 39.9 0 0 0 4.9 3 119 119 0 0 0 7.5 3.1 11.6 11.6 0 0 0 1.7-2c0 .1 0 0 0 0l.3-.2.2-.3.1-.3.6-1.2.1-.3v-.1l.1-.2v-.1a9 9 0 0 0 .3-1V20c-.3.2-.8.4-1.2.4-1.1 0-2.2-.7-2.2-2v-1.9c0-1.2 1.1-1.8 2.2-1.8v-.1l-.2-.4h-.1c0-.2-.2-.3-.3-.5l-.3-.4-.3-.4a20 20 0 0 0-1.4-1.6v-.1l-.4-.3-.4-.4a22 22 0 0 0-.8-.7 22.9 22.9 0 0 0-.8-.7l-1-.6c-.1-.2-.3-.2-.4-.3A23.5 23.5 0 0 0 28.6 7l-1.4-.7-2.5-1Z" }), jsx("path", { d: "M33.2 19.3V16c-.7-.4-2-.3-2 .6v2.1c0 1 1.3 1 2 .7Z" }), jsx("path", { fillOpacity: ".7", d: "M13.4 17.7h-1.7v2.6h-1.2v-7.6h3c1.6 0 2.5 1.3 2.5 2.5 0 1.3-.9 2.5-2.6 2.5Zm14.9 5.9-1-.5h-.1l-1-.5-4-2.5c-.3.2-.7.2-1 .3-1.1 0-2.2-.7-2.2-2v-1c-.5-.5-1-1-1.3-1.5-.3.2-.4.4-.4.7v3.7H16v-3.7c0-.8.4-1.3 1-1.6-1.2-2.2-1-4 .7-5.4l.3-.2.3-.3.3-.2.3-.3h.1l.3-.2.4-.3.4-.3.4-.2 1-.6.4-.2a36.5 36.5 0 0 1 1-.6h.1l.5-.2a49 49 0 0 1 1.1-.5c-2.3-.8-4.7-1-6.8-1C11.2 4.9 3 9.7.5 17.6c-1.7 5.3 2.2 12.8 16 13.8a25 25 0 0 0 18.2-5c-2.1-.8-4.3-1.7-6.4-2.7ZM46.6 20.3c-1.3.2-2.4-.3-2.4-2v-5.6h1.2v2h1V16h-1v2.5c0 .8.6.9 1.2.9v1Zm-2.8 0h-1.2v-3.7c0-.9-1.4-1-2-.6v4.3h-1.1V15c1.5-.8 4.3-.6 4.3 1.5v3.7ZM41.6.7a61 61 0 0 0-16.9 4.8 23.8 23.8 0 0 1 4 1.6l.9.5.9.6.4.2 1 .7.3.3.5.4.4.3.4.4.4.3.3.4h.1l.3.4.4.4.3.4.4.4v.1l.3.3v.1l.3.4.3.4v.1l.3.4c1 0 2.1.7 2.1 2V18h-.8v.8l.7.4c-.2.4-.5.6-.7.8v.4l-.1.2a8.2 8.2 0 0 1-.2.9l-.1.3c0 .2-.1.3-.2.3l-.5 1.3-.2.3-.2.3a10 10 0 0 1-.4.6l-1.6 1.6c8.3 3 15.8 3.6 17.8-3.6C55.7 11 51.1 0 41.5.6Z" })] })), jsx("defs", { children: jsx("clipPath", __assign$1({ id: "a" }, { children: jsx("path", { fill: "#0E5F80", d: "M0 .5h54v31H0z" }) })) })] })),
    }); },
    LogoProvidentColor: function () { return ({
        height: 36,
        width: 54,
        viewBox: '0 0 54 36',
        icon: (jsxs("svg", { children: [jsxs("g", __assign$1({ clipPath: "url(#a)" }, { children: [jsx("path", { fill: "#ADDA43", d: "M52 24.4C55.1 13 50.7 2.5 41.4 3a50.2 50.2 0 0 0-23.2 8.7c-3.5 2.7-.6 7.9 8.1 12.6 11 5.3 23 9.4 25.7 0Z" }), jsx("path", { fill: "#00ACE9", d: "M16.9 32.9c14 1 23.6-6.8 20.7-14.9A21.2 21.2 0 0 0 18.3 6.8c-6.5.3-14.5 5-17 12.6C-.2 24.6 3.6 31.8 17 32.9" }), jsx("path", { fill: "#009941", d: "M37.6 18C35.2 13 30 9.3 24.9 7.7c-3 1.3-5.3 2.7-6.7 4-3.5 2.7-.6 7.9 8.1 12.6 2.8 1.4 5.6 2.6 8.4 3.6 3-2.7 4.2-6.2 2.9-9.9Z" }), jsx("path", { fill: "#fff", d: "M12.3 22.1H11v-7.3h2.8c1.7 0 2.6 1.2 2.6 2.4s-.9 2.4-2.6 2.4h-1.6V22Zm3-4.9c0-.6-.5-1.2-1.4-1.2h-1.6v2.5h1.6c1 0 1.4-.7 1.4-1.3ZM18.6 16.7h.6v1c-.6-.1-1.5.1-1.5.9V22h-1.2v-3.6c0-.7.5-1.3 1-1.6l.5-.1h.1l.5-.1ZM21.5 16.6c1 0 2 .6 2 1.9v1.8c0 1.3-1 1.9-2 1.9-1.1 0-2.2-.6-2.2-1.9v-1.8c0-1.3 1.1-1.9 2.2-1.9Zm-1 1.9v1.8c0 .6.5.8 1 .8.4 0 1-.2 1-.8v-1.8c0-.5-.6-.8-1-.8-.5 0-1 .3-1 .8ZM25.9 21l.1-1.1 1-3.2h1.3l-1.9 5.4h-1.2l-1.9-5.4h1.2l1.1 3.2.2 1ZM29 14.8c.5 0 .8.3.8.7 0 .4-.3.7-.7.7a.7.7 0 0 1-.7-.7c0-.4.3-.7.7-.7Zm-.5 2V22h1.2v-5.4h-1.2ZM34.3 14.8v7c-1.5.7-4 .9-4.2-1.3v-2c0-1.6 1.9-2.2 3-1.6v-2.1h1.2Zm-1.1 3c-.7-.4-1.9-.2-2 .7v2c0 1 1.4 1 2 .7v-3.3ZM38.7 21.1c-.4.8-1.2 1.1-2 1.1-1 0-2-.6-2-1.8v-2c0-1.2 1-1.7 2-1.7 1.1 0 2.2.5 2.2 1.8V20h-3v.4c-.1.9 1.4 1 1.9.2l1 .5Zm-1-2.6c0-1-2-1-1.9 0v.5h2v-.5ZM39.3 17.1c1.5-.8 4.2-.6 4.2 1.5V22h-1.1v-3.5c0-1-1.4-1-2-.7v4.2h-1.1v-5ZM46.3 22.2c-1.3.1-2.4-.4-2.4-2v-5.4h1.2v2h1v1h-1v2.5c0 .8.5.9 1.2.9v1Z" })] })), jsx("defs", { children: jsx("clipPath", __assign$1({ id: "a" }, { children: jsx("path", { fill: "#fff", d: "M1 3h52v29.9H1z" }) })) })] })),
    }); },
    LogoCreditea: function () { return ({
        height: 26,
        width: 154,
        viewBox: '0 0 154 26',
        icon: (jsxs("g", __assign$1({ fill: "none", fillRule: "nonzero" }, { children: [jsx("path", { fill: "#001389", d: "M33.8 8.3c.3.4.3 1 0 1.4l-16 16a1 1 0 0 1-1.5 0l-8-8a1 1 0 0 1 0-1.4l16-16a1 1 0 0 1 1.4 0l8 8Z" }), jsx("path", { fill: "#EF4126", d: "M14.6 5.1c.4.4.4 1 0 1.4l-8 8a1 1 0 0 1-1.4 0l-5-4.8a1 1 0 0 1 0-1.4l8.1-8a1 1 0 0 1 1.4 0L14.6 5Z" }), jsx("path", { fill: "#001389", d: "M46 8.6c1 0 2 .2 2.8.4 1 .3 1.7.7 2.4 1.2a6.2 6.2 0 0 1 2.5 4.7h-4.4C49 13 48 12.1 46 12.1c-.8 0-1.4.1-1.9.5a4 4 0 0 0-1.2 1.2 9.3 9.3 0 0 0-.7 5.4L43 21c.3.5.7 1 1.2 1.2.5.4 1 .5 1.8.5 1 0 1.9-.3 2.5-.9.6-.6 1-1.4 1-2.4h4.4a7.5 7.5 0 0 1-2.5 5 8.3 8.3 0 0 1-5.4 1.7 9 9 0 0 1-3.4-.6 7.5 7.5 0 0 1-4.3-4.4 11 11 0 0 1 0-7 7.5 7.5 0 0 1 4.3-4.7 9 9 0 0 1 3.5-.7Zm28.4 0c1.4 0 2.6.3 3.6.9 1 .5 2 1.2 2.6 2.1a9 9 0 0 1 1.5 3.2c.3 1.1.4 2.4.3 3.6h-12c.1 1.5.5 2.6 1.2 3.2.7.7 1.6 1 3 1a4 4 0 0 0 2.3-.7c.6-.4 1-1 1.2-1.4h4c-.7 2-1.6 3.4-3 4.2-1.3.9-2.9 1.3-4.7 1.3-1.3 0-2.5-.2-3.6-.6a7.3 7.3 0 0 1-4.3-4.5 10 10 0 0 1 0-7 8 8 0 0 1 7.9-5.3ZM100 2.8v22.8h-4.4v-2.1c-.6.9-1.3 1.5-2.1 2a7 7 0 0 1-3 .5 6.5 6.5 0 0 1-5.6-2.7c-.5-.8-1-1.7-1.3-2.8a11.8 11.8 0 0 1 0-6.5c.3-1 .8-2 1.4-2.7.6-.8 1.4-1.5 2.3-2 .9-.4 2-.7 3.2-.7 1 0 1.9.3 2.8.7.8.4 1.5 1 2 1.8h.1V2.8h4.6Zm28.6 5.8c1.4 0 2.6.3 3.7.9 1 .5 1.9 1.2 2.5 2.1a9 9 0 0 1 1.5 3.2c.3 1.1.4 2.4.3 3.6h-12c.2 1.5.5 2.6 1.2 3.2.7.7 1.7 1 3 1a4 4 0 0 0 2.3-.7c.7-.4 1-1 1.2-1.4h4c-.6 2-1.6 3.4-3 4.2-1.2.9-2.8 1.3-4.7 1.3-1.3 0-2.5-.2-3.5-.6a7.3 7.3 0 0 1-4.4-4.5 10 10 0 0 1 0-7 8 8 0 0 1 7.9-5.3Zm17.5 0 2.5.2c.8.1 1.6.4 2.3.7.7.3 1.3.8 1.8 1.4.4.6.6 1.4.6 2.4V22c0 .8 0 1.5.2 2.2 0 .6.2 1.1.4 1.5h-4.6a6.6 6.6 0 0 1-.3-1.6 6 6 0 0 1-2.6 1.6 10.3 10.3 0 0 1-5.2.1 5 5 0 0 1-1.8-.9c-.5-.4-.9-.9-1.2-1.5-.3-.6-.4-1.4-.4-2.2 0-1 .2-1.7.5-2.3.3-.6.7-1.1 1.3-1.5.5-.3 1-.6 1.8-.8a19.4 19.4 0 0 1 4-.7c.7 0 1.2-.1 1.8-.3a3 3 0 0 0 1.2-.5c.3-.3.4-.6.4-1 0-.6 0-1-.3-1.3-.1-.2-.3-.5-.6-.6-.3-.2-.6-.3-1-.3l-1-.1c-1 0-1.7.2-2.2.6-.5.3-.8 1-.9 1.9h-4.5c0-1.1.3-2 .8-2.7.5-.7 1-1.2 1.8-1.7.7-.4 1.5-.7 2.4-.9a14 14 0 0 1 2.8-.3ZM116 4.2V9h3.3v3h-3.3v8.2c0 .8.1 1.3.4 1.5.2.3.7.4 1.5.4a8 8 0 0 0 1.4-.1v3.5l-1.3.1a30.4 30.4 0 0 1-3.4-.1c-.6 0-1.1-.3-1.6-.6A3 3 0 0 1 112 24c-.3-.5-.5-1.2-.5-2V12h-2.7v-3h2.7V4h4.6ZM65 8.6l1 .2V13a8.4 8.4 0 0 0-1.7-.1c-.8 0-1.5.1-2 .4-.7.2-1.1.6-1.5 1.1-.3.5-.6 1-.7 1.7a9 9 0 0 0-.2 2v7.5h-4.6V9h4.3v3A5.6 5.6 0 0 1 62 9.7l1.5-.7c.5-.2 1-.3 1.7-.3Zm42.3.5v16.5h-4.6V9h4.6Zm41.5 8.4a2 2 0 0 1-.7.4l-1 .3-1 .1a18.9 18.9 0 0 0-2 .4 3 3 0 0 0-1 .5 2 2 0 0 0-.5.6c-.2.3-.3.7-.3 1.1 0 .4.1.7.3 1 .1.3.3.5.6.7l.9.3 1 .1a4 4 0 0 0 2-.4 3 3 0 0 0 1.2-1.1c.2-.4.4-.8.4-1.3v-2.7Zm-57.1-5.4c-.8 0-1.4.1-1.9.4-.5.3-1 .7-1.2 1.2-.3.5-.6 1-.7 1.7a9 9 0 0 0 0 3.8l.7 1.7c.4.5.8 1 1.3 1.3.5.3 1 .4 1.8.4.7 0 1.3-.1 1.9-.4.5-.3.8-.7 1.1-1.2.3-.5.6-1 .7-1.7a10 10 0 0 0 0-4c-.1-.6-.4-1.1-.7-1.6-.3-.5-.7-.9-1.2-1.2-.5-.3-1-.4-1.8-.4Zm-17.6 0a4 4 0 0 0-1.8.3 3.4 3.4 0 0 0-1.7 2c-.2.5-.2.9-.2 1.2h7.3c-.2-1.2-.6-2-1.1-2.6-.5-.6-1.4-1-2.5-1Zm54.4 0a4 4 0 0 0-1.8.3 3.4 3.4 0 0 0-1.8 2l-.2 1.2h7.4c-.2-1.2-.6-2-1.2-2.6-.5-.6-1.3-1-2.4-1Zm-21.2-9.3v3.7h-4.6V2.8h4.6Z" })] }))),
    }); },
    LogoCrediteaLightMode: function () { return ({
        height: 105,
        width: 600,
        viewBox: '0 0 600 105',
        icon: (jsx("path", { fill: "#10465B", fillRule: "evenodd", d: "M56.8 26.3a4 4 0 0 0 0-5.5L37.8 1a3.7 3.7 0 0 0-5.4 0L1.1 33.5a4 4 0 0 0 0 5.6l19 19.6c1.5 1.5 4 1.5 5.4 0l31.3-32.4Zm74.7 7.2a4 4 0 0 1 0 5.6L69 103.7a3.7 3.7 0 0 1-5.4 0L32.3 71.3a4 4 0 0 1 0-5.5L94.8 1.2c1.5-1.5 4-1.5 5.4 0l31.3 32.3Zm269.2 69.7V36.7h17.7v66.5h-17.7Zm0-76.8v-15h17.7v15h-17.7ZM369.5 55c1.2 2 2 4.2 2.5 6.7a41 41 0 0 1 0 15.9c-.5 2.6-1.3 4.9-2.5 6.9-1.1 2-2.7 3.6-4.6 4.8-2 1.2-4.4 1.8-7.3 1.8-2.7 0-5.1-.6-7-1.9-2-1.2-3.6-2.8-4.9-4.8-1.3-2-2.2-4.4-2.8-7a35 35 0 0 1 0-15.6c.5-2.5 1.3-4.7 2.6-6.7a14 14 0 0 1 4.7-4.8 14 14 0 0 1 7.4-1.8c2.9 0 5.3.6 7.2 1.8 2 1.2 3.5 2.8 4.7 4.7Zm-4.9 47.4c3.4-1.6 6.1-4.2 8.2-7.8h.2v8.5h16.8V11.3h-17.6v33.5h-.3c-2-3.3-4.7-5.7-8.1-7.4-3.5-1.7-7.1-2.5-11-2.5a25.8 25.8 0 0 0-21.3 10.5 34 34 0 0 0-5.3 11 46.3 46.3 0 0 0 0 26.3c1.2 4.3 3 8.1 5.3 11.4a25.7 25.7 0 0 0 21.7 10.7c4.3 0 8-.8 11.4-2.3ZM172 50.6c1.9-1.3 4.2-2 7-2 7.6 0 12 3.9 13.1 11.5h17.3a24.8 24.8 0 0 0-9.8-19 29 29 0 0 0-9.4-4.7 39 39 0 0 0-25 1.2c-4 1.8-7.5 4.3-10.3 7.5-2.8 3.2-5 7-6.4 11.4a45.6 45.6 0 0 0 .2 28 29.3 29.3 0 0 0 16.9 17.8c4 1.7 8.4 2.5 13.2 2.5 8.5 0 15.6-2.3 21-6.9a30.5 30.5 0 0 0 10-20.2h-17c-.6 4.1-2 7.4-4.4 9.8-2.2 2.5-5.5 3.7-9.7 3.7-2.8 0-5-.6-7-2-1.9-1.2-3.4-2.9-4.5-4.9-1.2-2-2-4.2-2.5-6.7a37.2 37.2 0 0 1 0-14.9c.5-2.5 1.4-4.8 2.6-7 1.2-2 2.7-3.7 4.7-5Zm60.7-1.6V36.7H216v66.5h17.7v-30c0-3 .3-5.8.9-8.4.5-2.5 1.5-4.8 2.9-6.7 1.3-2 3.2-3.5 5.4-4.6 2.2-1 5-1.6 8.2-1.6a30.9 30.9 0 0 1 6.4.6v-17c-1.5-.4-2.7-.6-3.9-.6-2.2 0-4.4.3-6.5 1a23.4 23.4 0 0 0-10.7 7.4A22.6 22.6 0 0 0 233 49h-.3Zm66.2 3.2c-2.2-2.4-5.4-3.6-9.7-3.6a13.5 13.5 0 0 0-13.9 9.8c-.4 1.6-.7 3.1-.8 4.4h28.8c-.9-4.6-2.3-8.2-4.4-10.6Zm-20 35c2.6 2.7 6.4 4 11.4 4 3.6 0 6.7-1 9.2-2.8 2.6-1.8 4.2-3.8 4.8-5.8h15.5c-2.5 8-6.3 13.7-11.4 17.1a33 33 0 0 1-18.7 5.1c-5 0-9.6-.8-13.7-2.5-4-1.6-7.5-4-10.3-7.1-2.9-3-5-6.8-6.6-11a41.7 41.7 0 0 1 .1-28.1 31.6 31.6 0 0 1 30.5-21.2 28.9 28.9 0 0 1 24.3 12c2.7 3.8 4.5 8 5.7 12.7 1.2 4.7 1.6 9.6 1.2 14.8h-46.4c.3 5.9 1.7 10.2 4.4 12.8ZM465.2 49V36.7h-13v-20h-17.6v20h-10.7v12.2h10.7V88c0 3.4.5 6 1.6 8.1 1 2 2.5 3.7 4.4 4.8a18 18 0 0 0 6.5 2.2 49.9 49.9 0 0 0 13.1.5 41 41 0 0 0 5-.5V89a23.7 23.7 0 0 1-5.5.5c-3 0-5-.5-6-1.5s-1.5-3-1.5-6.2v-33h13Zm45.3 3.3c-2-2.4-5.3-3.6-9.6-3.6a13.5 13.5 0 0 0-13.9 9.8c-.5 1.6-.7 3.1-.8 4.4h28.7c-.8-4.6-2.3-8.2-4.4-10.6Zm-20 35c2.7 2.7 6.5 4 11.5 4 3.6 0 6.6-1 9.2-2.8 2.6-1.8 4.2-3.8 4.7-5.8h15.6c-2.5 8-6.3 13.7-11.4 17.1a33 33 0 0 1-18.7 5.1c-5 0-9.6-.8-13.7-2.5-4-1.6-7.5-4-10.4-7.1-2.8-3-5-6.8-6.5-11a41.6 41.6 0 0 1 0-28.1A32.7 32.7 0 0 1 488 37.6c4-1.8 8.5-2.7 13.4-2.7a28.9 28.9 0 0 1 24.3 12c2.6 3.8 4.5 8 5.7 12.7 1.1 4.7 1.6 9.6 1.2 14.8h-46.4c.2 5.9 1.7 10.2 4.3 12.8Zm86.8-15c1.1-.3 2-.9 2.8-1.6v6.8l-.3 4.2c-.2 1.7-.8 3.4-1.7 5-1 1.7-2.3 3.2-4.2 4.4-2 1.2-4.6 1.8-8.1 1.8-1.4 0-2.8-.2-4.1-.4a9.7 9.7 0 0 1-3.5-1.4c-1-.6-1.8-1.5-2.4-2.6a8.8 8.8 0 0 1-.9-4.1c0-1.8.3-3.2 1-4.3.5-1 1.3-2 2.2-2.7 1-.8 2-1.4 3.4-1.8a30.7 30.7 0 0 1 8.1-1.7 52.4 52.4 0 0 0 7.7-1.6Zm-35-25.8a21.7 21.7 0 0 0-3 10.7h17.6c.4-3.6 1.5-6.2 3.5-7.7s4.7-2.3 8.2-2.3c1.6 0 3 .1 4.4.3 1.4.2 2.6.7 3.7 1.3a7 7 0 0 1 2.4 2.7 10 10 0 0 1 1 4.7c0 1.9-.5 3.3-1.6 4.3-1.2 1-2.8 1.7-4.8 2.3-2 .5-4.2.9-6.8 1.1a133.2 133.2 0 0 0-15.8 2.8 22 22 0 0 0-7 3.3c-2 1.4-3.6 3.4-5 5.8a20 20 0 0 0-1.8 9.3c0 3.5.5 6.4 1.6 9 1.2 2.4 2.7 4.5 4.7 6 2 1.7 4.3 3 7 3.7a38.8 38.8 0 0 0 20.3-.6c3.8-1.3 7.1-3.4 10-6.4a28.6 28.6 0 0 0 1.2 6.5H600a16 16 0 0 1-1.7-6.2c-.4-2.7-.5-5.6-.5-8.6V53.8c0-4-1-7.3-2.7-9.7a18 18 0 0 0-6.7-5.8 29.4 29.4 0 0 0-9-2.7 52.5 52.5 0 0 0-20.5.4c-3.6.7-6.7 2-9.6 3.6-2.8 1.7-5.1 4-7 6.8Z", clipRule: "evenodd" })),
    }); },
    LogoCrediteaDarkMode: function () { return ({
        height: 105,
        width: 600,
        viewBox: '0 0 600 105',
        icon: (jsx("path", { fill: "#FFF", fillRule: "evenodd", d: "M56.8 26.3a4 4 0 0 0 0-5.5L37.8 1a3.7 3.7 0 0 0-5.4 0L1.1 33.5a4 4 0 0 0 0 5.6l19 19.6c1.5 1.5 4 1.5 5.4 0l31.3-32.4Zm74.7 7.2a4 4 0 0 1 0 5.6L69 103.7a3.7 3.7 0 0 1-5.4 0L32.3 71.3a4 4 0 0 1 0-5.5L94.8 1.2c1.5-1.5 4-1.5 5.4 0l31.3 32.3Zm269.2 69.7V36.7h17.7v66.5h-17.7Zm0-76.8v-15h17.7v15h-17.7ZM369.5 55c1.2 2 2 4.2 2.5 6.7a41 41 0 0 1 0 15.9c-.5 2.6-1.3 4.9-2.5 6.9-1.1 2-2.7 3.6-4.6 4.8-2 1.2-4.4 1.8-7.3 1.8-2.7 0-5.1-.6-7-1.9-2-1.2-3.6-2.8-4.9-4.8-1.3-2-2.2-4.4-2.8-7a35 35 0 0 1 0-15.6c.5-2.5 1.3-4.7 2.6-6.7a14 14 0 0 1 4.7-4.8 14 14 0 0 1 7.4-1.8c2.9 0 5.3.6 7.2 1.8 2 1.2 3.5 2.8 4.7 4.7Zm-4.9 47.4c3.4-1.6 6.1-4.2 8.2-7.8h.2v8.5h16.8V11.3h-17.6v33.5h-.3c-2-3.3-4.7-5.7-8.1-7.4-3.5-1.7-7.1-2.5-11-2.5a25.8 25.8 0 0 0-21.3 10.5 34 34 0 0 0-5.3 11 46.3 46.3 0 0 0 0 26.3c1.2 4.3 3 8.1 5.3 11.4a25.7 25.7 0 0 0 21.7 10.7c4.3 0 8-.8 11.4-2.3ZM172 50.6c1.9-1.3 4.2-2 7-2 7.6 0 12 3.9 13.1 11.5h17.3a24.8 24.8 0 0 0-9.8-19 29 29 0 0 0-9.4-4.7 39 39 0 0 0-25 1.2c-4 1.8-7.5 4.3-10.3 7.5-2.8 3.2-5 7-6.4 11.4a45.6 45.6 0 0 0 .2 28 29.3 29.3 0 0 0 16.9 17.8c4 1.7 8.4 2.5 13.2 2.5 8.5 0 15.6-2.3 21-6.9a30.5 30.5 0 0 0 10-20.2h-17c-.6 4.1-2 7.4-4.4 9.8-2.2 2.5-5.5 3.7-9.7 3.7-2.8 0-5-.6-7-2-1.9-1.2-3.4-2.9-4.5-4.9-1.2-2-2-4.2-2.5-6.7a37.2 37.2 0 0 1 0-14.9c.5-2.5 1.4-4.8 2.6-7 1.2-2 2.7-3.7 4.7-5Zm60.7-1.6V36.7H216v66.5h17.7v-30c0-3 .3-5.8.9-8.4.5-2.5 1.5-4.8 2.9-6.7 1.3-2 3.2-3.5 5.4-4.6 2.2-1 5-1.6 8.2-1.6a30.9 30.9 0 0 1 6.4.6v-17c-1.5-.4-2.7-.6-3.9-.6-2.2 0-4.4.3-6.5 1a23.4 23.4 0 0 0-10.7 7.4A22.6 22.6 0 0 0 233 49h-.3Zm66.2 3.2c-2.2-2.4-5.4-3.6-9.7-3.6a13.5 13.5 0 0 0-13.9 9.8c-.4 1.6-.7 3.1-.8 4.4h28.8c-.9-4.6-2.3-8.2-4.4-10.6Zm-20 35c2.6 2.7 6.4 4 11.4 4 3.6 0 6.7-1 9.2-2.8 2.6-1.8 4.2-3.8 4.8-5.8h15.5c-2.5 8-6.3 13.7-11.4 17.1a33 33 0 0 1-18.7 5.1c-5 0-9.6-.8-13.7-2.5-4-1.6-7.5-4-10.3-7.1-2.9-3-5-6.8-6.6-11a41.7 41.7 0 0 1 .1-28.1 31.6 31.6 0 0 1 30.5-21.2 28.9 28.9 0 0 1 24.3 12c2.7 3.8 4.5 8 5.7 12.7 1.2 4.7 1.6 9.6 1.2 14.8h-46.4c.3 5.9 1.7 10.2 4.4 12.8ZM465.2 49V36.7h-13v-20h-17.6v20h-10.7v12.2h10.7V88c0 3.4.5 6 1.6 8.1 1 2 2.5 3.7 4.4 4.8a18 18 0 0 0 6.5 2.2 49.9 49.9 0 0 0 13.1.5 41 41 0 0 0 5-.5V89a23.7 23.7 0 0 1-5.5.5c-3 0-5-.5-6-1.5s-1.5-3-1.5-6.2v-33h13Zm45.3 3.3c-2-2.4-5.3-3.6-9.6-3.6a13.5 13.5 0 0 0-13.9 9.8c-.5 1.6-.7 3.1-.8 4.4h28.7c-.8-4.6-2.3-8.2-4.4-10.6Zm-20 35c2.7 2.7 6.5 4 11.5 4 3.6 0 6.6-1 9.2-2.8 2.6-1.8 4.2-3.8 4.7-5.8h15.6c-2.5 8-6.3 13.7-11.4 17.1a33 33 0 0 1-18.7 5.1c-5 0-9.6-.8-13.7-2.5-4-1.6-7.5-4-10.4-7.1-2.8-3-5-6.8-6.5-11a41.6 41.6 0 0 1 0-28.1A32.7 32.7 0 0 1 488 37.6c4-1.8 8.5-2.7 13.4-2.7a28.9 28.9 0 0 1 24.3 12c2.6 3.8 4.5 8 5.7 12.7 1.1 4.7 1.6 9.6 1.2 14.8h-46.4c.2 5.9 1.7 10.2 4.3 12.8Zm86.8-15c1.1-.3 2-.9 2.8-1.6v6.8l-.3 4.2c-.2 1.7-.8 3.4-1.7 5-1 1.7-2.3 3.2-4.2 4.4-2 1.2-4.6 1.8-8.1 1.8-1.4 0-2.8-.2-4.1-.4a9.7 9.7 0 0 1-3.5-1.4c-1-.6-1.8-1.5-2.4-2.6a8.8 8.8 0 0 1-.9-4.1c0-1.8.3-3.2 1-4.3.5-1 1.3-2 2.2-2.7 1-.8 2-1.4 3.4-1.8a30.7 30.7 0 0 1 8.1-1.7 52.4 52.4 0 0 0 7.7-1.6Zm-35-25.8a21.7 21.7 0 0 0-3 10.7h17.6c.4-3.6 1.5-6.2 3.5-7.7s4.7-2.3 8.2-2.3c1.6 0 3 .1 4.4.3 1.4.2 2.6.7 3.7 1.3a7 7 0 0 1 2.4 2.7 10 10 0 0 1 1 4.7c0 1.9-.5 3.3-1.6 4.3-1.2 1-2.8 1.7-4.8 2.3-2 .5-4.2.9-6.8 1.1a133.2 133.2 0 0 0-15.8 2.8 22 22 0 0 0-7 3.3c-2 1.4-3.6 3.4-5 5.8a20 20 0 0 0-1.8 9.3c0 3.5.5 6.4 1.6 9 1.2 2.4 2.7 4.5 4.7 6 2 1.7 4.3 3 7 3.7a38.8 38.8 0 0 0 20.3-.6c3.8-1.3 7.1-3.4 10-6.4a28.6 28.6 0 0 0 1.2 6.5H600a16 16 0 0 1-1.7-6.2c-.4-2.7-.5-5.6-.5-8.6V53.8c0-4-1-7.3-2.7-9.7a18 18 0 0 0-6.7-5.8 29.4 29.4 0 0 0-9-2.7 52.5 52.5 0 0 0-20.5.4c-3.6.7-6.7 2-9.6 3.6-2.8 1.7-5.1 4-7 6.8Z", clipRule: "evenodd" })),
    }); },
    LogoCrediteaWhite: function () { return ({
        height: 26,
        width: 154,
        viewBox: '0 0 154 26',
        icon: (jsxs("g", __assign$1({ fill: "none", fillRule: "evenodd" }, { children: [jsx("path", { fill: "#FFF", d: "M33.8 8.3c.3.4.3 1 0 1.4l-16 16a1 1 0 0 1-1.5 0l-8-8a1 1 0 0 1 0-1.4l16-16a1 1 0 0 1 1.4 0l8 8Z" }), jsx("path", { fill: "#EF4126", fillRule: "nonzero", d: "M14.6 5.1c.4.4.4 1 0 1.4l-8 8a1 1 0 0 1-1.4 0l-5-4.8a1 1 0 0 1 0-1.4l8.1-8a1 1 0 0 1 1.4 0L14.6 5Z" }), jsx("path", { fill: "#FFF", d: "M46 8.6c1 0 2 .2 2.8.4 1 .3 1.7.7 2.4 1.2a6.2 6.2 0 0 1 2.5 4.7h-4.4C49 13 48 12.1 46 12.1c-.8 0-1.4.1-1.9.5a4 4 0 0 0-1.2 1.2 9.3 9.3 0 0 0-.7 5.4L43 21c.3.5.7 1 1.2 1.2.5.4 1 .5 1.8.5 1 0 1.9-.3 2.5-.9.6-.6 1-1.4 1-2.4h4.4a7.5 7.5 0 0 1-2.5 5 8.3 8.3 0 0 1-5.4 1.7 9 9 0 0 1-3.4-.6 7.5 7.5 0 0 1-4.3-4.4 11 11 0 0 1 0-7 7.5 7.5 0 0 1 4.3-4.7 9 9 0 0 1 3.5-.7Zm28.4 0c1.4 0 2.6.3 3.6.9 1 .5 2 1.2 2.6 2.1a9 9 0 0 1 1.5 3.2c.3 1.1.4 2.4.3 3.6h-12c.1 1.5.5 2.6 1.2 3.2.7.7 1.6 1 3 1a4 4 0 0 0 2.3-.7c.6-.4 1-1 1.2-1.4h4c-.7 2-1.6 3.4-3 4.2-1.3.9-2.9 1.3-4.7 1.3-1.3 0-2.5-.2-3.6-.6a7.3 7.3 0 0 1-4.3-4.5 10 10 0 0 1 0-7 8 8 0 0 1 7.9-5.3ZM100 2.8v22.8h-4.4v-2.1c-.6.9-1.3 1.5-2.1 2a7 7 0 0 1-3 .5 6.5 6.5 0 0 1-5.6-2.7c-.5-.8-1-1.7-1.3-2.8a11.8 11.8 0 0 1 0-6.5c.3-1 .8-2 1.4-2.7.6-.8 1.4-1.5 2.3-2 .9-.4 2-.7 3.2-.7 1 0 1.9.3 2.8.7.8.4 1.5 1 2 1.8h.1V2.8h4.6Zm28.6 5.8c1.4 0 2.6.3 3.7.9 1 .5 1.9 1.2 2.5 2.1a9 9 0 0 1 1.5 3.2c.3 1.1.4 2.4.3 3.6h-12c.2 1.5.5 2.6 1.2 3.2.7.7 1.7 1 3 1a4 4 0 0 0 2.3-.7c.7-.4 1-1 1.2-1.4h4c-.6 2-1.6 3.4-3 4.2-1.2.9-2.8 1.3-4.7 1.3-1.3 0-2.5-.2-3.5-.6a7.3 7.3 0 0 1-4.4-4.5 10 10 0 0 1 0-7 8 8 0 0 1 7.9-5.3Zm17.5 0 2.5.2c.8.1 1.6.4 2.3.7.7.3 1.3.8 1.8 1.4.4.6.6 1.4.6 2.4V22c0 .8 0 1.5.2 2.2 0 .6.2 1.1.4 1.5h-4.6a6.6 6.6 0 0 1-.3-1.6 6 6 0 0 1-2.6 1.6 10.3 10.3 0 0 1-5.2.1 5 5 0 0 1-1.8-.9c-.5-.4-.9-.9-1.2-1.5-.3-.6-.4-1.4-.4-2.2 0-1 .2-1.7.5-2.3.3-.6.7-1.1 1.3-1.5.5-.3 1-.6 1.8-.8a19.4 19.4 0 0 1 4-.7c.7 0 1.2-.1 1.8-.3a3 3 0 0 0 1.2-.5c.3-.3.4-.6.4-1 0-.6 0-1-.3-1.3-.1-.2-.3-.5-.6-.6-.3-.2-.6-.3-1-.3l-1-.1c-1 0-1.7.2-2.2.6-.5.3-.8 1-.9 1.9h-4.5c0-1.1.3-2 .8-2.7.5-.7 1-1.2 1.8-1.7.7-.4 1.5-.7 2.4-.9a14 14 0 0 1 2.8-.3ZM116 4.2V9h3.3v3h-3.3v8.2c0 .8.1 1.3.4 1.5.2.3.7.4 1.5.4a8 8 0 0 0 1.4-.1v3.5l-1.3.1a30.4 30.4 0 0 1-3.4-.1c-.6 0-1.1-.3-1.6-.6A3 3 0 0 1 112 24c-.3-.5-.5-1.2-.5-2V12h-2.7v-3h2.7V4h4.6ZM65 8.6l1 .2V13a8.4 8.4 0 0 0-1.7-.1c-.8 0-1.5.1-2 .4-.7.2-1.1.6-1.5 1.1-.3.5-.6 1-.7 1.7a9 9 0 0 0-.2 2v7.5h-4.6V9h4.3v3A5.6 5.6 0 0 1 62 9.7l1.5-.7c.5-.2 1-.3 1.7-.3Zm42.3.5v16.5h-4.6V9h4.6Zm41.5 8.4a2 2 0 0 1-.7.4l-1 .3-1 .1a18.9 18.9 0 0 0-2 .4 3 3 0 0 0-1 .5 2 2 0 0 0-.5.6c-.2.3-.3.7-.3 1.1 0 .4.1.7.3 1 .1.3.3.5.6.7l.9.3 1 .1a4 4 0 0 0 2-.4 3 3 0 0 0 1.2-1.1c.2-.4.4-.8.4-1.3v-2.7Zm-57.1-5.4c-.8 0-1.4.1-1.9.4-.5.3-1 .7-1.2 1.2-.3.5-.6 1-.7 1.7a9 9 0 0 0 0 3.8l.7 1.7c.4.5.8 1 1.3 1.3.5.3 1 .4 1.8.4.7 0 1.3-.1 1.9-.4.5-.3.8-.7 1.1-1.2.3-.5.6-1 .7-1.7a10 10 0 0 0 0-4c-.1-.6-.4-1.1-.7-1.6-.3-.5-.7-.9-1.2-1.2-.5-.3-1-.4-1.8-.4Zm-17.6 0a4 4 0 0 0-1.8.3 3.4 3.4 0 0 0-1.7 2c-.2.5-.2.9-.2 1.2h7.3c-.2-1.2-.6-2-1.1-2.6-.5-.6-1.4-1-2.5-1Zm54.4 0a4 4 0 0 0-1.8.3 3.4 3.4 0 0 0-1.8 2l-.2 1.2h7.4c-.2-1.2-.6-2-1.2-2.6-.5-.6-1.3-1-2.4-1Zm-21.2-9.3v3.7h-4.6V2.8h4.6Z" })] }))),
    }); },
    LogoCrediteaSymbol: function () { return ({
        height: 26,
        width: 26,
        viewBox: '0 0 30 30',
        icon: (jsx("g", __assign$1({ fill: "#16181A", fillRule: "nonzero" }, { children: jsx("path", { d: "M25.8 15.3c.3.2.3.7 0 1l-11.3 11c-.3.3-.7.3-1 0L8 21.8a.7.7 0 0 1 0-1l11.3-11c.2-.4.7-.4 1 0l5.6 5.5ZM12.3 13c.3.3.3.8 0 1l-5.7 5.6c-.3.2-.7.2-1 0l-3.4-3.4a.7.7 0 0 1 0-1l5.7-5.5c.2-.3.7-.3 1 0l3.4 3.4Z" }) }))),
    }); },
    LogoMasterCard: function () { return ({
        height: 56,
        width: 120,
        viewBox: '0 0 120 56',
        icon: (jsxs("g", __assign$1({ fill: "none", fillRule: "evenodd" }, { children: [jsx("path", { fill: "#FD0C15", d: "M112.5 23.5c0-1.3 1-2.4 2.3-2.4 1.2 0 2.2 1 2.2 2.4 0 1.3-1 2.3-2.2 2.3-1.2 0-2.3-1-2.3-2.3Zm2.3 1.8c.9 0 1.6-.8 1.6-1.8s-.7-1.8-1.6-1.8c-1 0-1.7.8-1.7 1.8s.7 1.8 1.7 1.8Zm-.3-.8h-.5v-2h1.3l.3.6c0 .2-.2.4-.4.5l.4.9h-.5l-.3-.8h-.3v.8Zm0-1.1h.5l.1-.3-.1-.2h-.5v.5M80.8 8l-.6 4c-1.4-.8-2.3-1-3.4-1-2.7 0-4.7 2.8-4.7 6.8 0 2.7 1.3 4.4 3.5 4.4.9 0 1.9-.3 3.1-1l-.6 4.3c-1.4.4-2.2.5-3.3.5-4 0-6.5-3-6.5-7.9 0-6.5 3.5-11.1 8.4-11.1.6 0 1.2 0 1.7.2l1.5.4.9.3M68.4 10.7h-.3c-1.3 0-2 .6-3.1 2.4l.3-2.3H62l-2.3 14.9h3.7C64.8 16.6 65.1 15 67 15h.2c.4-1.8.8-3 1.5-4.3h-.2M46.2 25.5c-1 .3-1.8.5-2.7.5-1.8 0-2.9-1.2-2.9-3.3l.1-1.4.3-1.4.1-1.2 1.6-10.1h3.7l-.4 2.2h2l-.6 3.6h-2l-.9 6.2v.6c0 .8.4 1 1.2 1h1l-.5 3.3M31.5 15.5c0 1.9 1 3.2 2.9 4.1 1.4.8 1.7 1 1.7 1.7 0 .9-.7 1.3-2.1 1.3-1.1 0-2.1-.1-3.3-.6l-.5 3.5h.1l.7.1 1 .2h2c4 0 5.7-1.5 5.7-4.9 0-2-.7-3.2-2.6-4.1-1.5-.7-1.7-1-1.7-1.6 0-.8.6-1.2 1.8-1.2.8 0 1.7 0 2.7.2l.5-3.4a23 23 0 0 0-3.2-.3c-4.3 0-5.7 2.2-5.7 5M110.4 25.6h-3.5l.1-1.4c-1 1.1-2 1.6-3.4 1.6-2.7 0-4.5-2.4-4.5-6.1 0-5 2.8-9.2 6-9.2 1.5 0 2.6.7 3.6 2l.8-5.2h3.7l-2.8 18.3Zm-5.5-3.4c1.8 0 3-2.1 3-5 0-2-.7-3-2-3-1.7 0-3 2.1-3 5 0 2 .7 3 2 3M58 25.3c-1.3.4-2.5.6-3.9.6-4.1 0-6.3-2.3-6.3-6.6 0-5.2 2.8-8.9 6.5-8.9 3 0 5 2.1 5 5.4 0 1.1-.1 2.2-.5 3.7h-7.4v.5c0 1.7 1.1 2.6 3.3 2.6 1.3 0 2.5-.3 3.8-1l-.5 3.7Zm-2.1-8.8v-.8c0-1.2-.6-1.9-1.7-1.9-1.2 0-2 1-2.4 2.7h4M16.6 25.6h-3.7l2.1-14-4.8 14H7.7l-.3-13.9-2.2 13.9H1.7L4.6 7.4h5.3l.2 11.3 3.6-11.3h5.8l-2.9 18.2M26.2 19h-.9c-2 0-3.1.7-3.1 2.2 0 1 .5 1.5 1.3 1.5 1.5 0 2.6-1.5 2.7-3.7Zm2.7 6.6h-3.1v-1.5c-.9 1.2-2.1 1.8-3.9 1.8-2 0-3.3-1.7-3.3-4 0-3.7 2.4-5.8 6.5-5.8h1.5l.1-.9c0-1-.6-1.3-2.4-1.3-1 0-2.2.1-3 .4l-.5.2h-.4l.5-3.3c1.9-.5 3.1-.8 4.5-.8 3.2 0 4.9 1.6 4.9 4.4l-.3 3-.8 5.2-.1 1-.1.7v.6l-.1.3M87.2 19h-.9c-2 0-3.1.7-3.1 2.2 0 1 .5 1.5 1.3 1.5 1.5 0 2.6-1.5 2.7-3.7Zm2.7 6.6h-3.1v-1.5c-.9 1.2-2.1 1.8-3.8 1.8-2 0-3.4-1.7-3.4-4 0-3.7 2.4-5.8 6.5-5.8h1.5l.1-.9c0-1-.6-1.3-2.4-1.3-1 0-2.2.1-3 .4l-.6.1-.3.1.5-3.3c1.8-.6 3-.8 4.4-.8 3.2 0 4.9 1.5 4.9 4.3 0 .8 0 1.3-.3 3L90 23v1l-.2.6v1M100.3 10.7h-.4c-1.2 0-2 .6-3 2.4l.3-2.3h-3.4l-2.3 14.9h3.8c1.3-9.1 1.6-10.7 3.4-10.7h.3c.3-1.8.8-3 1.4-4.3h-.1" }), jsx("path", { fill: "#F3AE12", d: "M84.7 40c0 3.4-1 5.4-2.7 5.4-1.2 0-2-1.3-2-3.3 0-2.3 1.1-5 2.8-5 1.4 0 1.9 1.4 1.9 3Zm3.6.1c0-3.7-1.8-6.7-5.3-6.7-4 0-6.6 3.6-6.6 8.8 0 3.7 1.6 6.8 5.3 6.8 3.8 0 6.6-2.7 6.6-8.9" }), jsx("path", { fill: "#F3AE12", d: "m77.6 30.8-.6 4a6 6 0 0 0-3-1c-2.7 0-4.5 2.9-4.5 6.9 0 2.7 1.2 4.4 3.3 4.4.8 0 1.8-.3 3-1l-.7 4.3c-1.3.4-2.1.5-3 .5-3.8 0-6.2-3-6.2-7.9 0-6.5 3.3-11.1 7.9-11.1.6 0 1 0 1.5.2l1.4.4 1 .3M54.6 33.5h-.3c-1.2 0-1.8.6-2.9 2.4l.3-2.2h-3l-2.2 14.8H50c1.2-9 1.5-10.6 3.2-10.6h.3a16 16 0 0 1 1.3-4.3h-.2M100 48.4h-3.3l.1-1.5c-.9 1.2-2 1.7-3.2 1.7-2.5 0-4.2-2.5-4.2-6.2 0-5 2.6-9.1 5.7-9.1 1.3 0 2.3.6 3.3 2l.8-5.2h3.4L100 48.4Zm-5.2-3.5c1.7 0 2.8-2 2.8-5 0-2-.7-3-1.9-3-1.5 0-2.7 2.1-2.7 5 0 2 .6 3 1.8 3M22 48.3c-1 .4-2.2.6-3.5.6-3.8 0-5.9-2.3-5.9-6.6 0-5.1 2.6-8.9 6-8.9 3 0 4.8 2 4.8 5.4 0 1.1-.2 2.2-.5 3.7H16v.5c0 1.7 1 2.6 3 2.6 1.3 0 2.4-.3 3.7-1l-.6 3.7Zm-1.9-8.8v-.8c0-1.2-.5-1.9-1.6-1.9-1 0-1.9 1-2.2 2.7h3.8M63.4 48.3c-1.2.4-2.3.6-3.6.6-3.9 0-5.9-2.3-5.9-6.6 0-5.1 2.6-8.9 6-8.9 3 0 4.8 2 4.8 5.4 0 1.1-.2 2.2-.5 3.7h-6.9v.5c0 1.7 1 2.6 3 2.6 1.3 0 2.4-.3 3.7-1l-.6 3.7Zm-2-8.8v-.8c0-1.2-.5-1.9-1.6-1.9s-1.9 1-2.2 2.7h3.8M111.8 48.3a11 11 0 0 1-3.6.6c-3.8 0-5.9-2.3-5.9-6.6 0-5.1 2.6-8.9 6.1-8.9 2.9 0 4.7 2 4.7 5.4 0 1.1-.2 2.2-.5 3.7h-6.8v.5c0 1.7 1 2.6 3 2.6 1.2 0 2.3-.3 3.6-1l-.6 3.7Zm-2-8.8v-.8c0-1.2-.5-1.9-1.6-1.9-1 0-1.8 1-2.2 2.7h3.9M11.8 34.2a5 5 0 0 0-2.9-.8c-1.4 0-2.5.3-2.5 1.8 0 2.6 5 1.6 5 7.3 0 5.1-3.3 6.5-6.3 6.5-1.4 0-3-.5-4.1-1l.8-3.8c.7.7 2.1 1 3.3 1 1.1 0 2.8-.2 2.8-2.1 0-3-5.1-2-5.1-7.2 0-4.8 3-6.2 5.9-6.2 1.6 0 3.1.2 4 .8l-1 3.7M32.2 48.3c-.7.4-1.6.6-3 .6-3 0-5-3-5-6.7 0-4.9 2.8-8.7 6.8-8.7.8 0 2.2.3 3.2 1l-.7 3.5c-.8-.5-1.6-.8-2.4-.8-1.8 0-3.2 1.7-3.2 4.7 0 1.8 1 3.2 2.5 3.2 1 0 1.6-.2 2.3-.6l-.5 3.8M45.2 44.6l-.4 3.8h-3.3l.2-2.4c-1 1.7-2.1 2.8-4 2.8-2.1 0-3.3-2.3-3.3-5.1 0-1 0-1.6.3-3.1l1-6.9h3.7l-1 7c-.1.7-.3 1.4-.3 2.2 0 .8.4 1.7 1.4 1.7 1.6 0 2.5-1.9 2.7-4l1-6.9H47l-1.7 10.9M115.3 48.3h-.6v-2.5h-.8v-.5h2.2v.5h-.8v2.5M119 48.3h-.5v-2.5l-.5 2.5h-.6l-.5-2.5v2.5h-.5v-3h.8l.5 2.3.5-2.3h.8v3" })] }))),
    }); },
    LogoIPFDigital: function () { return ({
        height: 100,
        width: 315,
        viewBox: '67.5 0 315 100',
        icon: (jsxs(Fragment, { children: [jsx("path", { fill: "none", stroke: "#9B9C9E", strokeMiterlimit: "10", d: "M0 0" }), jsx("path", { fill: "#9B9C9E", d: "M193.7 97.7v-7.2h2.3c1.5 0 2.2 1 2.2 2.2 0 1.2-.7 2.1-2.2 2.1h-1.5v2.9h-.8zm.8-3.5h1.4c.9 0 1.5-.5 1.5-1.5s-.5-1.5-1.6-1.5h-1.3v3zM203 97.7h-.7v-.8c-.3.7-.8.9-1.4.9-1.4 0-2-1.3-2-2.8 0-1.6.6-2.9 2-2.9.6 0 1.1.3 1.4.9v-.8h.6v5.5zm-2-.6c.8 0 1.3-.5 1.3-2.1s-.5-2.2-1.3-2.2c-1 0-1.3.7-1.3 2.2 0 1.3.4 2.1 1.3 2.1M204.2 97.7v-5.5h.6v.7c.3-.6.8-.8 1.5-.8v.8h-.3c-.6 0-1 .5-1 1.2v3.6h-.8zM207.9 97.7h-.7v-4.9h-.7v-.6h.7v-1.6h.7v1.6h.8v.6h-.8zM216.1 95c0 1.5-.6 2.8-2.1 2.8s-2.1-1.3-2.1-2.8c0-1.6.6-2.9 2.1-2.9s2.1 1.3 2.1 2.9m-3.5 0c0 1.3.5 2.1 1.4 2.1 1 0 1.4-.8 1.4-2.1s-.5-2.2-1.4-2.2c-1 0-1.4.9-1.4 2.2M219 91.1h-.6c-.3 0-.4.2-.4.5v.6h.8v.6h-.8v4.9h-.8v-4.9h-.6v-.6h.6v-.5c0-.9.5-1.2 1.2-1.2h.5v.6zM223.1 97.7h-.7v-4.9h-.7v-.6h.7v-1.6h.7v1.6h.9v.6h-.9zM224.5 97.7v-7.2h.7v2.3c.3-.5.8-.7 1.4-.7 1.2 0 1.7.8 1.7 1.7v3.9h-.8V94c0-.8-.3-1.2-1-1.2-.8 0-1.3.6-1.3 1.3v3.6h-.7zM230 95.2c0 1 .4 2 1.3 2 .7 0 1-.8 1-1.4h.8c0 1-.8 2-1.9 2-1.2 0-2-1-2-2.9 0-1.6.7-2.8 2-2.8 1.2 0 1.9 1 1.9 2.7v.4h-3.2zm2.3-.6c0-1.1-.3-1.8-1.1-1.8-.7 0-1.2.7-1.3 1.8h2.4zM236.9 90.5h.8v7.2h-.8zM239 97.7v-5.5h.8v.8c.2-.5.6-.9 1.4-.9 1.2 0 1.7.8 1.7 1.7v3.9h-.8V94c0-.8-.3-1.2-1-1.2-.8 0-1.3.6-1.3 1.3v3.6h-.7zM245 97.7h-.7v-4.9h-.7v-.6h.7v-1.6h.7v1.6h.8v.6h-.8zM246.8 95.2c0 1 .5 2 1.3 2 .7 0 1-.8 1.1-1.4h.7c0 1-.8 2-1.8 2-1.2 0-2-1-2-2.9 0-1.6.7-2.8 2-2.8 1.1 0 1.8 1 1.8 2.7v.4h-3.1zm2.4-.6c0-1.1-.4-1.8-1.2-1.8-.6 0-1.2.7-1.2 1.8h2.4zM250.8 97.7v-5.5h.7v.7c.2-.6.7-.8 1.5-.8v.8h-.3c-.7 0-1.1.5-1.1 1.2v3.6h-.8zM253.7 97.7v-5.5h.7v.8c.2-.5.6-.9 1.4-.9 1.2 0 1.6.8 1.6 1.7v3.9h-.7V94c0-.8-.4-1.2-1-1.2-.8 0-1.3.6-1.3 1.3v3.6h-.7zM262.5 97.7h-.7v-.8c-.3.7-.8.9-1.4.9-1.4 0-2-1.3-2-2.8 0-1.6.6-2.9 2-2.9.6 0 1.1.3 1.4.9v-.8h.7v5.5zm-2-.6c.8 0 1.3-.5 1.3-2.1s-.5-2.2-1.3-2.2c-1 0-1.3.7-1.3 2.2 0 1.3.4 2.1 1.3 2.1M264.6 97.7h-.7v-4.9h-.7v-.6h.7v-1.6h.7v1.6h.8v.6h-.8zM266.1 90.5h.8v1h-.8v-1zm0 1.7h.8v5.5h-.8v-5.5zM272.3 95c0 1.5-.6 2.8-2.2 2.8-1.5 0-2.1-1.3-2.1-2.8 0-1.6.6-2.9 2.1-2.9 1.6 0 2.2 1.3 2.2 2.9m-3.6 0c0 1.3.5 2.1 1.4 2.1 1 0 1.4-.8 1.4-2.1s-.4-2.2-1.4-2.2c-.9 0-1.4.9-1.4 2.2M273.3 97.7v-5.5h.7v.8c.2-.5.6-.9 1.4-.9 1.2 0 1.6.8 1.6 1.7v3.9h-.7V94c0-.8-.3-1.2-1-1.2-.8 0-1.3.6-1.3 1.3v3.6h-.7zM282 97.7h-.6v-.8c-.3.7-.7.9-1.4.9-1.4 0-2-1.3-2-2.8 0-1.6.6-2.9 2-2.9.6 0 1.1.3 1.4.9v-.8h.7v5.5zm-2-.6c1 0 1.4-.5 1.4-2.1s-.5-2.2-1.3-2.2c-1 0-1.3.7-1.3 2.2 0 1.3.4 2.1 1.3 2.1M283.5 90.5h.7v7.2h-.7zM288.5 97.7v-7.2h2.3c1.6 0 2.2 1 2.2 2.2 0 1.2-.6 2.1-2.2 2.1h-1.5v2.9h-.8zm.8-3.5h1.4c.9 0 1.5-.5 1.5-1.5s-.5-1.5-1.5-1.5h-1.4v3zM294.4 95.2c0 1 .5 2 1.3 2 .7 0 1-.8 1.1-1.4h.7c0 1-.7 2-1.8 2-1.2 0-2-1-2-2.9 0-1.6.7-2.8 2-2.8 1.1 0 1.8 1 1.8 2.7v.4h-3zm2.4-.6c0-1.1-.4-1.8-1.2-1.8-.6 0-1.2.7-1.2 1.8h2.4zM298.5 97.7v-5.5h.6v.7c.2-.6.7-.8 1.5-.8v.8h-.3c-.7 0-1.1.5-1.1 1.2v3.6h-.7zM304 93.8c-.1-.6-.5-1-1.1-1-.6 0-1 .3-1 .8 0 1.3 2.9.5 2.9 2.5 0 1-.8 1.7-2 1.7-1 0-1.8-.6-1.8-1.7h.7c0 .7.5 1 1 1 .8 0 1.3-.2 1.3-.9 0-1.4-2.9-.4-2.9-2.6 0-.8.7-1.5 1.7-1.5 1.2 0 1.9.6 1.9 1.7h-.8zM309.8 95c0 1.5-.6 2.8-2.2 2.8-1.5 0-2.1-1.3-2.1-2.8 0-1.6.6-2.9 2.1-2.9 1.6 0 2.2 1.3 2.2 2.9m-3.6 0c0 1.3.5 2.1 1.4 2.1 1 0 1.4-.8 1.4-2.1s-.4-2.2-1.4-2.2c-.9 0-1.4.9-1.4 2.2M310.8 97.7v-5.5h.7v.8c.2-.5.6-.9 1.4-.9 1.2 0 1.6.8 1.6 1.7v3.9h-.7V94c0-.8-.3-1.2-1-1.2-.8 0-1.3.6-1.3 1.3v3.6h-.7zM319.6 97.7h-.7v-.8c-.3.7-.8.9-1.4.9-1.4 0-2-1.3-2-2.8 0-1.6.6-2.9 2-2.9.6 0 1.1.3 1.4.9v-.8h.7v5.5zm-2-.6c.8 0 1.3-.5 1.3-2.1s-.5-2.2-1.3-2.2c-1 0-1.3.7-1.3 2.2 0 1.3.4 2.1 1.3 2.1M321 90.5h.7v7.2h-.7zM326 97.7v-7.2h3.3v.7h-2.5v2.4h2.5v.6h-2.5v3.5zM330.5 90.5h.8v1h-.8v-1zm0 1.7h.8v5.5h-.8v-5.5zM332.7 97.7v-5.5h.7v.8c.2-.5.6-.9 1.4-.9 1.1 0 1.6.8 1.6 1.7v3.9h-.7V94c0-.8-.4-1.2-1-1.2-.9 0-1.3.6-1.3 1.3v3.6h-.7zM341.4 97.7h-.6v-.8c-.3.7-.8.9-1.4.9-1.4 0-2-1.3-2-2.8 0-1.6.6-2.9 2-2.9.5 0 1.1.3 1.4.9v-.8h.6v5.5zm-2-.6c.9 0 1.3-.5 1.3-2.1s-.4-2.2-1.2-2.2c-1 0-1.3.7-1.3 2.2 0 1.3.4 2.1 1.3 2.1M342.7 97.7v-5.5h.7v.8c.2-.5.6-.9 1.4-.9 1.2 0 1.7.8 1.7 1.7v3.9h-.8V94c0-.8-.3-1.2-1-1.2-.8 0-1.3.6-1.3 1.3v3.6h-.7zM350.5 94.2c0-.7-.4-1.4-1-1.4-.9 0-1.3.8-1.3 2.2 0 1.4.5 2.1 1.2 2.1.8 0 1-.6 1.1-1.3h.8c-.1 1.2-.7 2-2 2-1.2 0-1.8-1-1.8-2.8 0-1.8.6-2.9 2-2.9 1.1 0 1.7 1 1.7 2h-.7zM352.6 95.2c0 1 .5 2 1.3 2 .7 0 1-.8 1.1-1.4h.7c0 1-.7 2-1.8 2-1.2 0-2-1-2-2.9 0-1.6.7-2.8 2-2.8 1.1 0 1.8 1 1.8 2.7v.4h-3zm2.4-.6c0-1.1-.4-1.8-1.2-1.8-.6 0-1.2.7-1.2 1.8h2.4zM363.5 92.8c0-1-.6-1.8-1.6-1.8-1.3 0-1.9 1-1.9 3.1 0 2 .6 3 2 3 1 0 1.7-.4 1.7-2.3h-2.4v-.6h3.2v1c0 1.6-1 2.6-2.6 2.6-1.9 0-2.7-1.6-2.7-3.7s.8-3.7 2.7-3.7c1.5 0 2.4 1 2.4 2.4h-.8zM365.6 97.7v-5.5h.6v.7c.3-.6.8-.8 1.5-.8v.8h-.3c-.6 0-1 .5-1 1.2v3.6h-.8zM372.5 95c0 1.5-.6 2.8-2.2 2.8s-2.1-1.3-2.1-2.8c0-1.6.6-2.9 2.1-2.9 1.6 0 2.2 1.3 2.2 2.9m-3.6 0c0 1.3.5 2.1 1.4 2.1s1.4-.8 1.4-2.1-.5-2.2-1.4-2.2-1.4.9-1.4 2.2M377.2 92.2v5.5h-.7v-.8c-.2.6-.6.9-1.4.9-1.2 0-1.6-.8-1.6-1.7v-3.9h.7V96c0 .8.3 1.2 1 1.2.8 0 1.3-.6 1.3-1.3v-3.6h.7zM378.5 92.2h.6v.6c.4-.5.8-.7 1.5-.7 1.3 0 1.9 1.3 1.9 2.9 0 1.5-.6 2.8-2 2.8-.5 0-1-.3-1.3-.7v2.5h-.7v-7.4zm2 .6c-.9 0-1.3.6-1.3 2.2s.4 2.1 1.3 2.1 1.3-.7 1.3-2.1c0-1.4-.5-2.2-1.3-2.2M231 63h-4.4v-6.5h-.2a16 16 0 0 1-14.1 7.3c-5.1 0-9.8-2-14.8-7.3-1.7-1.5-3.9-6.2-3.9-11.3 0-10.5 8.8-18.3 19-18.3 5.3 0 10.8 2.3 13.6 6.9h.1V15.2h4.8V63zm-18.7-31.6a13.9 13.9 0 1 0 .1 27.8 14 14 0 0 0 14-14c0-7.9-6.4-13.8-14.1-13.8M239.7 15.2h4.7v8.2h-4.7v-8.2zm0 12.4h4.7V63h-4.7V27.6zM288.3 57.9c0 5.5-.2 8.8-4.1 13.1-3.4 3.7-9 5.7-14 5.7a18 18 0 0 1-17.2-13h4.6c2.8 5.3 7 8.5 13 8.5 4.6 0 9.3-2.1 11.6-6.2 1.6-2.7 1.6-5 1.6-8.1v-2h-.2c-2.1 5.4-8.2 7.9-13.7 7.9a19 19 0 0 1-12.7-5.2c-3.7-3.5-5.5-8-5.5-13.2 0-5 2-9.5 3.3-11 4.7-5.8 9.6-7.5 14.7-7.5 5.6 0 11.3 2.5 14 7.6v-6.9h4.6V58zm-31.9-13c0 7.8 5.5 14.3 13.6 14.3 3.7 0 7-1.2 9.7-4 2.4-2.5 4-6.4 4-9.9 0-8-6.3-13.9-14.1-13.9-7.4 0-13.2 6.3-13.2 13.5M297 15.2h4.7v8.2h-4.8v-8.2zm0 12.4h4.7V63h-4.8V27.6zM314.3 32H307v-4.4h7.3V15.2h4.8v12.4h8.2V32h-8.2v30.9h-4.8zM368.6 63H364v-6.7h-.2c-2.5 5-8 7.5-13.5 7.5-5.3 0-11-2.4-15.6-7.5a18.8 18.8 0 0 1-3.4-11.2 18.4 18.4 0 0 1 19-18.2c5.4 0 11 2.4 13.5 7.4h.2v-6.7h4.6V63zM336 45.3c0 7.8 6.7 13.8 14.3 13.8A14 14 0 0 0 364 45.5c0-7.8-6-14-13.8-14a14 14 0 0 0-14.2 14M377.1 15.2h4.8v47.7h-4.8z" }), jsx("path", { fill: "#E4312A", d: "M97.2 21.8C88.9 44.2 77.7 94 98.4 97.4c16.8 2.7 55.2-30.6 66.1-42.6 10.2-10.8-7-37.2-29-49.2-21.4-11.7-31-2.7-38.3 16.2" }), jsx("path", { fill: "#005AA0", d: "M92.7 72.1c32.5 3.8 84.3-9.6 81.6-25.4-1-6.2-7.5-17.7-45.3-24.1A56.6 56.6 0 0 0 79 37.3C62.5 53.1 61.2 71.6 92.7 72.1" }), jsx("path", { fill: "#FFF", d: "M103.1 34.8h3.6v24.4h-3.6zM115 59.2h-3.5V34.8h7c5.9 0 8.6 3 8.6 7.4s-2.7 7.4-8.6 7.4H115v9.6zm0-12.9h3.5c4 0 5-2.3 5-4 0-1.9-1-4.2-5-4.2H115v8.2zM130.1 59.2V34.8h12.4v3.3h-8.8v7.3h8.8v3.3h-8.8v10.5z" })] })),
    }); },
};

var MenuIcons = {
    Logout: function () { return ({
        height: 26,
        width: 26,
        viewBox: '0 0 26 26',
        icon: (jsx("g", __assign$1({ id: "ic/logout", stroke: "none", strokeWidth: "1", fill: "none", fillRule: "evenodd" }, { children: jsx("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M5 2C3.34315 2 2 3.34315 2 5V21C2 22.6569 3.34315 24 5 24H16C17.6569 24 19 22.6569 19 21C19 20.4477 18.5523 20 18 20C17.4477 20 17 20.4477 17 21C17 21.5523 16.5523 22 16 22H5C4.44772 22 4 21.5523 4 21V5C4 4.44772 4.44772 4 5 4H16C16.5523 4 17 4.44772 17 5C17 5.55228 17.4477 6 18 6C18.5523 6 19 5.55228 19 5C19 3.34315 17.6569 2 16 2H5ZM10 13C10 12.4477 10.4477 12 11 12H20.5858L18.2929 9.70711C17.9024 9.31658 17.9024 8.68342 18.2929 8.29289C18.6834 7.90237 19.3166 7.90237 19.7071 8.29289L23.7071 12.2929C24.0976 12.6834 24.0976 13.3166 23.7071 13.7071L19.7071 17.7071C19.3166 18.0976 18.6834 18.0976 18.2929 17.7071C17.9024 17.3166 17.9024 16.6834 18.2929 16.2929L20.5858 14H11C10.4477 14 10 13.5523 10 13Z", fill: "#16181A" }) }))),
    }); },
    LogoutBlue: function () { return ({
        height: 26,
        width: 26,
        viewBox: '0 0 26 26',
        icon: (jsx("g", __assign$1({ id: "ic/logoutBlue", stroke: "none", strokeWidth: "1", fill: "none", fillRule: "evenodd" }, { children: jsx("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M5 2C3.34315 2 2 3.34315 2 5V21C2 22.6569 3.34315 24 5 24H16C17.6569 24 19 22.6569 19 21C19 20.4477 18.5523 20 18 20C17.4477 20 17 20.4477 17 21C17 21.5523 16.5523 22 16 22H5C4.44772 22 4 21.5523 4 21V5C4 4.44772 4.44772 4 5 4H16C16.5523 4 17 4.44772 17 5C17 5.55228 17.4477 6 18 6C18.5523 6 19 5.55228 19 5C19 3.34315 17.6569 2 16 2H5ZM10 13C10 12.4477 10.4477 12 11 12H20.5858L18.2929 9.70711C17.9024 9.31658 17.9024 8.68342 18.2929 8.29289C18.6834 7.90237 19.3166 7.90237 19.7071 8.29289L23.7071 12.2929C24.0976 12.6834 24.0976 13.3166 23.7071 13.7071L19.7071 17.7071C19.3166 18.0976 18.6834 18.0976 18.2929 17.7071C17.9024 17.3166 17.9024 16.6834 18.2929 16.2929L20.5858 14H11C10.4477 14 10 13.5523 10 13Z", fill: "#0E5F80" }) }))),
    }); },
    Home: function () { return ({
        height: 26,
        width: 26,
        viewBox: '0 0 26 26',
        icon: (jsx("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M23.1585 14H23L23 23C23 24.1046 22.1046 25 21 25H5C3.89543 25 3 24.1046 3 23L2.99999 14H2.84144C0.992457 14 0.133596 11.7062 1.52788 10.4918L11.6864 1.64406C12.4394 0.988276 13.5606 0.988276 14.3136 1.64406L24.4721 10.4918C25.8664 11.7062 25.0075 14 23.1585 14ZM13 3.15222L23.1585 12H22C22 12 21.7841 12 21.589 12.0881C21.2417 12.2449 21 12.5942 21 13V23L17 23V18C17 15.7909 15.2091 14 13 14C10.7909 14 9 15.7909 9 18V23L5 23V13C5 12.5942 4.75831 12.2449 4.41104 12.0881C4.21584 12 4 12 4 12H2.84144L13 3.15222ZM15 18V23H11V18C11 16.8954 11.8954 16 13 16C14.1046 16 15 16.8954 15 18Z", fill: "#0E5F80" })),
    }); },
    Card: function () { return ({
        height: 26,
        width: 26,
        viewBox: '0 0 26 26',
        icon: (jsx("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M23 6H3L3 8H23V6ZM3 20L3 10H23V20H3ZM3 4C1.89543 4 1 4.89543 1 6V20C1 21.1046 1.89543 22 3 22H23C24.1046 22 25 21.1046 25 20V6C25 4.89543 24.1046 4 23 4H3ZM17 17V16H20V17H17ZM15 15C15 14.4477 15.4477 14 16 14H21C21.5523 14 22 14.4477 22 15V18C22 18.5523 21.5523 19 21 19H16C15.4477 19 15 18.5523 15 18V15Z", fill: "#0E5F80" })),
    }); },
    Transaction: function () { return ({
        height: 26,
        width: 26,
        viewBox: '0 0 26 26',
        icon: (jsxs(Fragment, { children: [jsx("path", { d: "M18 19H2M2 19L7 24M2 19L7 14", stroke: "#0E5F80", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round" }), jsx("path", { d: "M8 7H24M24 7L19 12M24 7L19 2", stroke: "#0E5F80", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round" })] })),
    }); },
    Document: function () { return ({
        height: 26,
        width: 26,
        viewBox: '0 0 26 26',
        icon: (jsx("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M20 4H6V22H14V17C14 15.8954 14.8954 15 16 15H20V4ZM16 17V21.1716L20 17.1716V17H16ZM22 4V16V17.1716C22 17.702 21.7893 18.2107 21.4142 18.5858L16.5858 23.4142C16.2107 23.7893 15.702 24 15.1716 24H15H6C4.89543 24 4 23.1046 4 22V4C4 2.89543 4.89543 2 6 2H20C21.1046 2 22 2.89543 22 4ZM9 6C8.44772 6 8 6.44772 8 7C8 7.55228 8.44772 8 9 8H17C17.5523 8 18 7.55228 18 7C18 6.44772 17.5523 6 17 6H9ZM9 10C8.44772 10 8 10.4477 8 11C8 11.5523 8.44772 12 9 12H17C17.5523 12 18 11.5523 18 11C18 10.4477 17.5523 10 17 10H9ZM8 15C8 14.4477 8.44772 14 9 14H12C12.5523 14 13 14.4477 13 15C13 15.5523 12.5523 16 12 16H9C8.44772 16 8 15.5523 8 15Z", fill: "#0E5F80" })),
    }); },
    Calendar: function () { return ({
        height: 26,
        width: 26,
        viewBox: '0 0 26 26',
        icon: (jsx("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M5 2C5 1.44772 5.44772 1 6 1C6.55228 1 7 1.44772 7 2V3H19V2C19 1.44772 19.4477 1 20 1C20.5523 1 21 1.44772 21 2V3H23C24.1046 3 25 3.89543 25 5V23C25 24.1046 24.1046 25 23 25H3C1.89543 25 1 24.1046 1 23V5C1 3.89543 1.89543 3 3 3H5V2ZM5 5H3L3 9H23V5H21V7C21 7.55228 20.5523 8 20 8C19.4477 8 19 7.55228 19 7V5H7V7C7 7.55228 6.55228 8 6 8C5.44772 8 5 7.55228 5 7V5ZM23 11H3L3 23H23V11ZM11.5 14C12.0523 14 12.5 13.5523 12.5 13C12.5 12.4477 12.0523 12 11.5 12C10.9477 12 10.5 12.4477 10.5 13C10.5 13.5523 10.9477 14 11.5 14ZM11.5 18C12.0523 18 12.5 17.5523 12.5 17C12.5 16.4477 12.0523 16 11.5 16C10.9477 16 10.5 16.4477 10.5 17C10.5 17.5523 10.9477 18 11.5 18ZM12.5 21C12.5 21.5523 12.0523 22 11.5 22C10.9477 22 10.5 21.5523 10.5 21C10.5 20.4477 10.9477 20 11.5 20C12.0523 20 12.5 20.4477 12.5 21ZM8.5 18C9.05228 18 9.5 17.5523 9.5 17C9.5 16.4477 9.05228 16 8.5 16C7.94772 16 7.5 16.4477 7.5 17C7.5 17.5523 7.94772 18 8.5 18ZM9.5 21C9.5 21.5523 9.05228 22 8.5 22C7.94772 22 7.5 21.5523 7.5 21C7.5 20.4477 7.94772 20 8.5 20C9.05228 20 9.5 20.4477 9.5 21ZM5.5 18C6.05228 18 6.5 17.5523 6.5 17C6.5 16.4477 6.05228 16 5.5 16C4.94772 16 4.5 16.4477 4.5 17C4.5 17.5523 4.94772 18 5.5 18ZM6.5 21C6.5 21.5523 6.05228 22 5.5 22C4.94772 22 4.5 21.5523 4.5 21C4.5 20.4477 4.94772 20 5.5 20C6.05228 20 6.5 20.4477 6.5 21ZM14.5 14C15.0523 14 15.5 13.5523 15.5 13C15.5 12.4477 15.0523 12 14.5 12C13.9477 12 13.5 12.4477 13.5 13C13.5 13.5523 13.9477 14 14.5 14ZM15.5 17C15.5 17.5523 15.0523 18 14.5 18C13.9477 18 13.5 17.5523 13.5 17C13.5 16.4477 13.9477 16 14.5 16C15.0523 16 15.5 16.4477 15.5 17ZM14.5 22C15.0523 22 15.5 21.5523 15.5 21C15.5 20.4477 15.0523 20 14.5 20C13.9477 20 13.5 20.4477 13.5 21C13.5 21.5523 13.9477 22 14.5 22ZM18.5 13C18.5 13.5523 18.0523 14 17.5 14C16.9477 14 16.5 13.5523 16.5 13C16.5 12.4477 16.9477 12 17.5 12C18.0523 12 18.5 12.4477 18.5 13ZM17.5 18C18.0523 18 18.5 17.5523 18.5 17C18.5 16.4477 18.0523 16 17.5 16C16.9477 16 16.5 16.4477 16.5 17C16.5 17.5523 16.9477 18 17.5 18ZM21.5 13C21.5 13.5523 21.0523 14 20.5 14C19.9477 14 19.5 13.5523 19.5 13C19.5 12.4477 19.9477 12 20.5 12C21.0523 12 21.5 12.4477 21.5 13ZM20.5 18C21.0523 18 21.5 17.5523 21.5 17C21.5 16.4477 21.0523 16 20.5 16C19.9477 16 19.5 16.4477 19.5 17C19.5 17.5523 19.9477 18 20.5 18Z", fill: "#0E5F80" })),
    }); },
    Attachment: function () { return ({
        height: 26,
        width: 26,
        viewBox: '0 0 26 26',
        icon: (jsx("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M20.0711 3.53553C21.9723 5.43677 22.0223 8.4882 20.2212 10.45L20.0711 10.6066L11.5858 19.0919C10.4142 20.2635 8.51471 20.2635 7.34313 19.0919C6.2134 17.9622 6.17306 16.1556 7.22209 14.9775L7.34313 14.8492L14.9081 7.2843C15.2986 6.89378 15.9318 6.89378 16.3223 7.2843C16.6849 7.64693 16.7108 8.21878 16.4 8.6113L16.3223 8.69852L8.75735 16.2635C8.36682 16.654 8.36682 17.2871 8.75735 17.6777C9.11998 18.0403 9.69182 18.0662 10.0843 17.7554L10.1716 17.6777L18.6568 9.19239C19.8284 8.02082 19.8284 6.12132 18.6568 4.94975C17.5271 3.82002 15.7205 3.77967 14.5424 4.8287L14.4142 4.94975L5.92892 13.435C3.9763 15.3876 3.9763 18.5535 5.92892 20.5061C7.83016 22.4073 10.8816 22.4574 12.8434 20.6562L13 20.5061L22.1924 11.3137C22.5829 10.9232 23.2161 10.9232 23.6066 11.3137C23.9692 11.6763 23.9951 12.2482 23.6843 12.6407L23.6066 12.7279L14.4142 21.9203C11.6805 24.654 7.24838 24.654 4.51471 21.9203C1.83799 19.2436 1.78222 14.9384 4.34741 12.1939L4.51471 12.0208L13 3.53553C14.9526 1.58291 18.1184 1.58291 20.0711 3.53553Z", fill: "#16181A" })),
    }); },
    MenuWhatsApp: function () { return ({
        height: 26,
        width: 26,
        viewBox: '0 0 26 26',
        icon: (jsxs(Fragment, { children: [jsx("path", { d: "M1 13C1 19.6274 6.37258 25 13 25C19.6274 25 25 19.6274 25 13C25 6.37258 19.6274 1 13 1C6.37258 1 1 6.37258 1 13Z", fill: "#25D366" }), jsx("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M18.75 7.15625C17.3438 5.75 15.4688 5 13.5 5C9.375 5 6 8.375 6 12.5C6 13.8125 6.37501 15.125 7.03126 16.25L6 20L9.93751 18.9688C11.0625 19.5312 12.2812 19.9062 13.5 19.9062C17.625 19.9062 21 16.5312 21 12.4062C21 10.4375 20.1563 8.5625 18.75 7.15625ZM13.5 18.6875C12.375 18.6875 11.25 18.4063 10.3125 17.8438L10.125 17.75L7.78124 18.4062L8.43751 16.1562L8.24999 15.875C7.59374 14.8438 7.31249 13.7188 7.31249 12.5938C7.31249 9.21875 10.125 6.40625 13.5 6.40625C15.1875 6.40625 16.6875 7.0625 17.9063 8.1875C19.125 9.40625 19.6875 10.9063 19.6875 12.5938C19.6875 15.875 16.9688 18.6875 13.5 18.6875ZM16.875 14C16.6875 13.9062 15.75 13.4375 15.5625 13.4375C15.375 13.3438 15.2812 13.3438 15.1875 13.5312C15.0937 13.7188 14.7188 14.0938 14.625 14.2812C14.5313 14.375 14.4375 14.375 14.25 14.375C14.0625 14.2812 13.5 14.0938 12.75 13.4375C12.1875 12.9688 11.8125 12.3125 11.7188 12.125C11.625 11.9375 11.7188 11.8438 11.8125 11.75C11.9063 11.6562 12 11.5625 12.0938 11.4687C12.1875 11.375 12.1875 11.2813 12.2813 11.1875C12.375 11.0938 12.2813 11 12.2813 10.9062C12.2813 10.8125 11.9063 9.875 11.7188 9.5C11.625 9.21875 11.4375 9.21875 11.3438 9.21875C11.25 9.21875 11.1562 9.21875 10.9687 9.21875C10.875 9.21875 10.6875 9.21875 10.5 9.40625C10.3125 9.59375 9.84376 10.0625 9.84376 11C9.84376 11.9375 10.5 12.7812 10.5937 12.9688C10.6875 13.0625 11.9062 15.0312 13.7812 15.7812C15.375 16.4375 15.6563 16.25 16.0313 16.25C16.4063 16.25 17.1562 15.7812 17.25 15.4062C17.4375 14.9375 17.4375 14.5625 17.3438 14.5625C17.25 14.0938 17.0625 14.0938 16.875 14Z", fill: "white", stroke: "white", strokeWidth: "0.4" })] })),
    }); },
};

var NumberIcons = {
    NumberOne: function () { return ({
        height: 56,
        width: 56,
        viewBox: '0 0 56 56',
        icon: (jsx("g", __assign$1({ id: "Desktop", stroke: "none", strokeWidth: "1", fill: "none", fillRule: "evenodd" }, { children: jsx("g", __assign$1({ id: "03.1_Desktop_Products_Account", transform: "translate(-246.000000, -1099.000000)" }, { children: jsx("g", __assign$1({ id: "Group-2", transform: "translate(70.000000, 1038.000000)" }, { children: jsxs("g", __assign$1({ id: "Group", transform: "translate(175.000000, 60.000000)" }, { children: [jsx("circle", { id: "Oval", fill: "#F7F7F7", cx: "28", cy: "28", r: "27" }), jsx("g", __assign$1({ id: "Group-4", transform: "translate(17.170594, 10.622359)", fill: "#10465B" }, { children: jsx("path", { d: "M12.2755208,-1.42108547e-14 L15.441,4.114 L16.2377535,4.31142831 C14.2729147,12.2672909 13.3416407,20.4719607 13.4697507,28.6596315 L18.618406,29.2256413 L18.6210785,29.2111794 L20.1867858,29.3990041 L19.5014653,35.3596633 L1.6186295,33.3932149 L2.27451083,27.429171 L7.46150462,27.9996022 C7.41269215,22.0311944 7.89290148,16.0590096 8.89338756,10.1728575 L3.65881197,14.2004236 L-2.66453526e-15,9.44510078 L12.2755208,-1.42108547e-14 Z", id: "Combined-Shape" }) }))] })) })) })) }))),
    }); },
    NumberTwo: function () { return ({
        height: 56,
        width: 56,
        viewBox: '0 0 56 56',
        icon: (jsx("g", __assign$1({ id: "Desktop", stroke: "none", strokeWidth: "1", fill: "none", fillRule: "evenodd" }, { children: jsx("g", __assign$1({ id: "03.1_Desktop_Products_Account", transform: "translate(-692.000000, -1099.000000)" }, { children: jsx("g", __assign$1({ id: "Group-2", transform: "translate(70.000000, 1038.000000)" }, { children: jsx("g", __assign$1({ id: "Step_2", transform: "translate(446.000000, 0.000000)" }, { children: jsxs("g", __assign$1({ id: "Group", transform: "translate(175.000000, 60.000000)" }, { children: [jsx("circle", { id: "Oval", fill: "#F7F7F7", cx: "28", cy: "28", r: "27" }), jsx("g", __assign$1({ transform: "translate(17.000101, 10.997897)", fill: "#10465B", id: "Combined-Shape" }, { children: jsx("path", { d: "M12.7996902,0.21682667 C16.0734262,0.871449192 18.8983619,2.92614479 20.3664507,5.65068727 C21.6966065,8.11937583 21.9003336,10.9663332 21.1419096,13.7740327 C20.3586821,16.6727577 18.6139783,19.3539567 16.1584483,21.4875224 L15.8087024,21.7856411 L15.4603867,22.0702897 L15.1050244,22.3478459 L14.7341386,22.6246872 L14.3392526,22.9071914 L13.6833802,23.3555167 L12.9258254,23.8524576 L12.0379791,24.4195387 L11.0073209,25.0703834 C12.5675426,25.454882 14.1639068,25.9778428 15.9112261,26.6597589 C16.5762444,25.8597984 17.2948468,25.2006668 18.1268018,24.630698 L18.3687365,24.4695197 L21.6310607,29.505117 C20.8923745,29.9836767 20.2385903,30.7441136 19.4925764,32.0459694 L19.2251593,32.5283516 C19.1798554,32.6126363 19.1341602,32.6989001 19.0880378,32.7871959 L18.805887,33.3417757 L18.5117506,33.947657 L18.3596469,34.2706281 L16.813,33.549 L15.9220371,33.1556738 C11.3006763,31.148927 8.23734194,30.3327455 4.44692252,30.2182768 L-2.48689958e-14,30.182663 C0.0265918642,26.9480756 1.99049755,24.190716 5.14980689,21.8035243 L5.76477154,21.353038 L6.22237123,21.0328928 L6.88899514,20.586716 L7.70085499,20.0633616 L9.33447067,19.0281329 L10.0285223,18.5787175 L10.6146328,18.1864992 L10.9571057,17.9482614 L11.2691543,17.7226419 L11.5577985,17.5045195 L11.8300581,17.2887731 L12.092953,17.0702816 C12.1363784,17.0333531 12.1797061,16.9960968 12.2230823,16.9584061 C13.804542,15.5843043 14.8871471,13.9205951 15.3495685,12.2091734 C15.7292538,10.8035714 15.6376549,9.52353429 15.084425,8.49677305 C14.4727944,7.36168263 13.1615585,6.40796517 11.6233395,6.10037995 C10.0549571,5.78683566 8.28835477,6.25859167 7.6734921,6.9854823 C7.49298059,7.19889521 7.52961199,7.47696529 7.84922153,7.69052739 L7.95365958,7.75253162 L5.14795064,13.0561151 C1.37592029,11.0606304 0.398769543,6.29533627 3.09250253,3.11062301 C5.24175398,0.569778014 9.23334409,-0.49614194 12.7996902,0.21682667 Z" }) }))] })) })) })) })) }))),
    }); },
    NumberThree: function () { return ({
        height: 56,
        width: 56,
        viewBox: '0 0 56 56',
        icon: (jsx("g", __assign$1({ id: "Desktop", stroke: "none", strokeWidth: "1", fill: "none", fillRule: "evenodd" }, { children: jsx("g", __assign$1({ id: "03.1_Desktop_Products_Account", transform: "translate(-1139.000000, -1099.000000)" }, { children: jsx("g", __assign$1({ id: "Group-2", transform: "translate(70.000000, 1038.000000)" }, { children: jsx("g", __assign$1({ id: "Step_3", transform: "translate(893.000000, 0.000000)" }, { children: jsxs("g", __assign$1({ id: "Group", transform: "translate(175.000000, 60.000000)" }, { children: [jsx("circle", { id: "Oval", fill: "#F7F7F7", cx: "28", cy: "28", r: "27" }), jsx("g", __assign$1({ transform: "translate(13.985707, 10.179579)", fill: "#10465B", fillRule: "nonzero", id: "Combined-Shape" }, { children: jsx("path", { d: "M22.9919178,8.8817842e-16 L25.0366443,5.64084158 C24.5251648,5.82624576 24.0083452,6.00789479 23.4878647,6.1851712 C22.6870092,7.86720387 21.7892755,9.50376647 20.801161,11.0836987 L20.3852932,11.7364208 L20.4213647,11.751429 C22.6562913,12.7249655 24.5839581,14.4577119 25.7396028,16.6653832 L25.8694938,16.922206 C27.7665113,20.806923 27.2492613,25.6533654 24.7375456,29.3046079 C22.4649522,32.6082419 18.7315902,34.845539 14.5974274,35.5839612 C9.22722209,36.5431704 3.62247064,34.6289139 1.0389345,30.2350781 C-0.0577759658,28.3700615 -0.411671232,26.2518931 0.593393451,24.3240936 C1.28208411,23.003048 2.1428056,22.3394235 3.60888678,21.6420501 L4.15216884,21.3933914 L6.58138676,26.8796379 L6.30587468,27.0040417 L6.14729317,27.0784208 L6.21103278,27.1937956 C7.38170158,29.1847592 10.4444056,30.2308004 13.5424297,29.6774411 C16.1611128,29.2097059 18.4735475,27.8239299 19.7942338,25.9040685 C21.1186805,23.9787407 21.3912213,21.4251329 20.4779331,19.5548991 C19.6350111,17.828553 17.6340882,16.6963975 15.9064672,16.9040093 L15.7157068,16.9326286 C12.9512892,17.4319254 11.0112492,14.2320568 12.7916687,12.0138923 C13.6963283,10.8884255 14.5436333,9.71684937 15.3295701,8.50574859 C13.333912,8.95251461 11.415961,9.2861526 9.67266943,9.46924445 L9.47029317,9.48842079 L9.45891558,10.7363828 L3.45937753,10.6619301 L3.49744431,6.5867435 L3.53544065,3.55132244 L6.57020086,3.6251816 C8.15406616,3.66372928 10.2280349,3.41468551 12.5275298,2.96061179 L13.526064,2.75369661 C16.3899949,2.1332345 19.530731,1.22784867 22.4745592,0.185361444 L22.9919178,8.8817842e-16 Z" }) }))] })) })) })) })) }))),
    }); },
    NumberFour: function () { return ({
        height: 56,
        width: 56,
        viewBox: '0 0 56 56',
        icon: (jsxs(Fragment, { children: [jsx("circle", { cx: "27", cy: "27", r: "27", fill: "#F7F7F7" }), jsx("g", __assign$1({ clipPath: "url(#clip0_74_22253)" }, { children: jsx("path", { d: "M41.4199 30.4901C38.9278 30.2177 36.4004 30.0815 33.9689 30.0361C34.3775 27.9627 34.8416 25.6271 35.4218 22.8222L29.4943 21.5964C28.7931 25.0015 28.2533 27.7155 27.7892 30.1067C26.1598 30.1723 24.6464 30.2631 23.3146 30.3589C24.9037 26.4241 26.5633 20.1284 27.1233 10.4326L21.0798 10.0845C20.1011 26.8226 15.7022 31.8017 15.6568 31.8522L15.7123 31.7967L18.2498 36.9523C18.3153 36.9422 21.7507 36.4781 26.5684 36.2309C25.9731 39.1013 25.398 41.6085 24.5203 44.7665L30.3519 46.3858C31.4062 42.5771 32.0671 39.6108 32.7733 36.0796C35.3713 36.0947 38.1005 36.2107 40.754 36.5033L41.4148 30.4851L41.4199 30.4901Z", fill: "#10465B" }) })), jsx("defs", { children: jsx("clipPath", __assign$1({ id: "clip0_74_22253" }, { children: jsx("rect", { width: "25.7631", height: "36.2963", fill: "white", transform: "translate(15.6568 10.0891)" }) })) })] })),
    }); },
    NumberFive: function () { return ({
        height: 56,
        width: 56,
        viewBox: '0 0 56 56',
        icon: (jsxs(Fragment, { children: [jsx("circle", { cx: "27", cy: "27", r: "27", fill: "#F7F7F7" }), jsx("g", __assign$1({ clipPath: "url(#clip0_406_22791)" }, { children: jsx("path", { d: "M41.9487 29.4448C40.8031 26.3914 37.2349 21.5716 26.1317 21.4908C26.2932 20.6631 26.4345 19.6689 26.5102 18.4778C29.3466 18.5989 31.6833 18.3567 34.4793 18.069C34.5146 18.2709 34.555 18.4778 34.5954 18.6898L40.5356 17.5138C40.2984 16.3177 40.2378 15.8231 40.3135 15.0509L40.6769 11.4121L37.033 11.7402C35.8823 11.8411 34.8427 11.9521 33.8737 12.0531C29.4223 12.5174 26.7373 12.795 21.0898 11.7452L19.9845 17.7006C20.1561 17.7309 20.3176 17.7611 20.4892 17.7914C20.3529 20.4007 19.8634 21.6523 19.5808 22.369C19.3082 23.0604 18.7985 24.3474 19.5404 25.7403C20.2873 27.1484 21.7156 27.4714 22.2506 27.5925C22.5383 27.6582 22.836 27.6783 23.1288 27.6582C30.25 27.1535 35.1707 28.6221 36.281 31.5847C37.0936 33.7498 35.7915 36.7527 33.1822 38.7261C29.7251 41.3353 25.3141 41.4161 21.0898 39.0339C21.3371 38.6655 21.645 38.186 21.9982 37.5148L16.6435 34.6835C16.3558 35.2285 16.1287 35.5616 15.9066 35.8846C15.1344 37 14.6247 37.9034 14.5187 40.1947C14.4733 41.2041 14.9326 42.1681 15.7451 42.7636C19.6817 45.6656 23.5678 46.8011 27.0805 46.8011C30.9313 46.8011 34.3279 45.4385 36.8261 43.5459C41.6005 39.9373 43.7051 34.1384 41.9487 29.4448Z", fill: "#0D465C" }) })), jsx("defs", { children: jsx("clipPath", __assign$1({ id: "clip0_406_22791" }, { children: jsx("rect", { width: "28.0458", height: "35.3941", fill: "white", transform: "translate(14.5186 11.4072)" }) })) })] })),
    }); },
    NumberSix: function () { return ({
        height: 56,
        width: 56,
        viewBox: '0 0 56 56',
        icon: (jsxs(Fragment, { children: [jsx("circle", { cx: "27", cy: "27", r: "27", fill: "#F7F7F7" }), jsx("g", __assign$1({ clipPath: "url(#clip0_406_22795)" }, { children: jsx("path", { d: "M38.6171 29.2352C36.4284 26.7285 33.037 25.1618 29.7701 25.1572H29.7516C26.8164 25.1572 24.1162 26.3875 22.1441 28.6223C21.2455 29.6406 20.8769 30.8755 21.1073 32.0966C21.4944 34.1656 23.379 35.3636 24.7613 36.0824L27.1436 31.5022C27.9039 30.9585 28.7794 30.682 29.7562 30.6866C31.4519 30.6866 33.2905 31.5483 34.447 32.8707C35.1658 33.6909 35.9077 34.9811 35.3594 36.4603C34.7189 38.1928 32.6684 39.506 30.0097 39.8931C28.079 40.1742 25.2498 40.3723 22.8352 38.9255C19.0292 36.0179 17.4809 32.9629 18.0984 29.5992C19.2872 23.1251 28.0099 17.0428 30.5626 16.2963C31.71 15.9599 32.5117 15.8908 32.9771 15.9093L38.0089 17.7017C39.1056 14.6283 37.9858 12.8128 37.2256 12.0018C35.5713 10.2508 32.7329 9.90062 29.0098 10.9881C24.8719 12.2045 14.3061 19.66 12.6657 28.6039C11.9607 32.4376 12.5966 38.1191 19.6144 43.4181C19.6789 43.4688 19.748 43.5149 19.8217 43.561C22.5081 45.2336 25.3788 45.6299 27.5859 45.6299C29.0236 45.6299 30.1755 45.464 30.816 45.3672C35.4792 44.6899 39.2115 42.0127 40.557 38.3817C41.6998 35.2853 40.9994 31.9538 38.6263 29.2352H38.6171Z", fill: "#0D465C" }) })), jsx("defs", { children: jsx("clipPath", __assign$1({ id: "clip0_406_22795" }, { children: jsx("rect", { width: "28.6424", height: "35.2593", fill: "white", transform: "translate(12.4445 10.3706)" }) })) })] })),
    }); },
};

var PaymentAccount = {
    Account: function () { return ({
        height: 26,
        width: 26,
        viewBox: '0 0 26 26',
        icon: (jsx("g", __assign$1({ id: "Icons", stroke: "none", strokeWidth: "1", fill: "none", fillRule: "evenodd" }, { children: jsx("g", __assign$1({ id: "ic/account", fill: "#16181A" }, { children: jsx("path", { d: "M13,1 C19.627417,1 25,6.372583 25,13 C25,15.956498 23.9308227,18.6632763 22.1580291,20.7547739 L21.8916078,21.0583166 L21.8916078,21.0583166 L21.6072039,21.3615051 L21.6072039,21.3615051 L21.2827743,21.6830886 L21.2827743,21.6830886 L21.0250308,21.9219599 L21.0250308,21.9219599 C18.8999194,23.8351081 16.0859972,25 13,25 C9.60835636,25 6.54535097,23.5929324 4.36288031,21.3306936 L4.04820376,20.9916908 L4.04820376,20.9916908 L3.93235945,20.8601684 L3.93235945,20.8601684 L3.77155209,20.6709202 L3.77155209,20.6709202 L3.48589853,20.3141091 C1.927009,18.2893396 1,15.7529271 1,13 C1,6.372583 6.372583,1 13,1 Z M13,17 C10.2249798,17 7.74329787,18.2559304 6.09236067,20.2303847 C7.88722378,21.9461743 10.3205448,23 13,23 C15.6789599,23 18.1118767,21.9465638 19.9071092,20.2313325 C18.2559076,18.2555284 15.774576,17 13,17 Z M13,3 C7.4771525,3 3,7.4771525 3,13 C3,15.1157939 3.65708642,17.078117 4.77832492,18.694035 C6.79268129,16.4271316 9.72967842,15 13,15 C16.2704487,15 19.207547,16.4272425 21.2224159,18.6928484 C22.3429136,17.078117 23,15.1157939 23,13 C23,7.4771525 18.5228475,3 13,3 Z M13,5 C15.4852814,5 17.5,7.01471863 17.5,9.5 C17.5,11.9852814 15.4852814,14 13,14 C10.5147186,14 8.5,11.9852814 8.5,9.5 C8.5,7.01471863 10.5147186,5 13,5 Z M13,7 C11.6192881,7 10.5,8.11928813 10.5,9.5 C10.5,10.8807119 11.6192881,12 13,12 C14.3807119,12 15.5,10.8807119 15.5,9.5 C15.5,8.11928813 14.3807119,7 13,7 Z", id: "Shape" }) })) }))),
    }); },
    CreditLine: function () { return ({
        height: 26,
        width: 26,
        viewBox: '0 0 26 26',
        icon: (jsx("g", __assign$1({ id: "Icons", stroke: "none", strokeWidth: "1", fill: "none", fillRule: "evenodd" }, { children: jsx("g", __assign$1({ id: "ic/credit", transform: "translate(-2.000000, -3.000000)", fill: "#16181A" }, { children: jsx("path", { d: "M19,14 L19,21 C19,22.1045695 18.1045695,23 17,23 L9,23 C7.8954305,23 7,22.1045695 7,21 L7,14 L4,14 C2.8954305,14 2,13.1045695 2,12 L2,5 C2,3.8954305 2.8954305,3 4,3 L22,3 C23.1045695,3 24,3.8954305 24,5 L24,12 C24,13.1045695 23.1045695,14 22,14 L19,14 Z M17,10 L9,10 L9,21 L17,21 L17,10 Z M13,12 C14.6568542,12 16,13.3431458 16,15 C16,16.6568542 14.6568542,18 13,18 C11.3431458,18 10,16.6568542 10,15 C10,13.3431458 11.3431458,12 13,12 Z M13,14 C12.4477153,14 12,14.4477153 12,15 C12,15.5522847 12.4477153,16 13,16 C13.5522847,16 14,15.5522847 14,15 C14,14.4477153 13.5522847,14 13,14 Z M19,12 L22,12 L22,5 L4,5 L4,12 L7,12 L7,10 C6.44771525,10 6,9.55228475 6,9 C6,8.44771525 6.44771525,8 7,8 L19,8 C19.5522847,8 20,8.44771525 20,9 C20,9.55228475 19.5522847,10 19,10 L19,12 Z", id: "Combined-Shape" }) })) }))),
    }); },
    DebitCard: function () { return ({
        height: 26,
        width: 26,
        viewBox: '0 0 26 26',
        icon: (jsx("g", __assign$1({ id: "Icons", stroke: "none", strokeWidth: "1", fill: "none", fillRule: "evenodd" }, { children: jsx("g", __assign$1({ id: "ic/card", fill: "#16181A" }, { children: jsx("path", { d: "M23,4 C24.1045695,4 25,4.8954305 25,6 L25,20 C25,21.1045695 24.1045695,22 23,22 L3,22 C1.8954305,22 1,21.1045695 1,20 L1,6 C1,4.8954305 1.8954305,4 3,4 L23,4 Z M23,10 L3,10 L3,20 L23,20 L23,10 Z M21,14 C21.5522847,14 22,14.4477153 22,15 L22,18 C22,18.5522847 21.5522847,19 21,19 L16,19 C15.4477153,19 15,18.5522847 15,18 L15,15 C15,14.4477153 15.4477153,14 16,14 L21,14 Z M20,16 L17,16 L17,17 L20,17 L20,16 Z M23,6 L3,6 L3,8 L23,8 L23,6 Z", id: "Combined-Shape" }) })) }))),
    }); },
    PaymentAccount: function () { return ({
        height: 26,
        width: 26,
        viewBox: '0 0 26 26',
        icon: (jsx("g", __assign$1({ id: "ic/paymentaccount", stroke: "none", strokeWidth: "1", fill: "none", fillRule: "evenodd" }, { children: jsx("path", { d: "M18.5442286,1.83493649 L22.1061811,8.00360254 L22.005,7.99960254 L22.1492623,8.00548574 C23.1841222,8.08183488 24,8.9456382 24,10 L24.0010775,13.2681881 L23.9788822,13.2555193 C24.5883101,13.598223 25,14.2510338 25,15 L25,18 C25,18.7489662 24.5883101,19.401777 23.9788822,19.7444807 L24.0010775,19.7318119 L24.0010775,19.7318119 L24,23 C24,24.1045695 23.1045695,25 22,25 L3,25 C1.8954305,25 1,24.1045695 1,23 L1,10 C1,8.8954305 1.8954305,8 3,8 L22,8 L19.7931811,7.99960254 L16.8121778,2.83493649 L7.86518111,7.99960254 L4.06418111,7.99962477 C4.2017874,7.84616935 4.36564722,7.71154321 4.55384758,7.60288568 L15.8121778,1.10288568 C16.7687631,0.550600933 17.9919439,0.878351244 18.5442286,1.83493649 Z M19.5,20 C17.5670034,20 16,18.4329966 16,16.5 C16,14.5670034 17.5670034,13 19.5,13 L22,13 L22,10 L3,10 L3,23 L22,23 L22,20 L19.5,20 Z M23,15 L19.5,15 C18.6715729,15 18,15.6715729 18,16.5 C18,17.2796961 18.5948881,17.9204487 19.35554,17.9931334 L19.5,18 L23,18 L23,15 Z", id: "Combined-Shape", fill: "#16181A" }) }))),
    }); },
    PaymentPlus: function () { return ({
        height: 26,
        width: 26,
        viewBox: '0 0 26 26',
        icon: (jsxs("g", __assign$1({ id: "ic/plus", stroke: "none", strokeWidth: "1", fill: "none", fillRule: "evenodd" }, { children: [jsx("circle", { cx: "13", cy: "13", r: "12", fill: "#D5EDF7" }), jsx("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M14 6C14 5.44772 13.5523 5 13 5C12.4477 5 12 5.44772 12 6V12H6C5.44771 12 5 12.4477 5 13C5 13.5523 5.44771 14 6 14H12V20C12 20.5523 12.4477 21 13 21C13.5523 21 14 20.5523 14 20V14H20C20.5523 14 21 13.5523 21 13C21 12.4477 20.5523 12 20 12H14V6Z", fill: "#16181A" })] }))),
    }); },
};

var RoundIcons = {
    Affiliates: function () { return ({
        height: 56,
        width: 56,
        viewBox: '0 0 56 56',
        icon: (jsxs("g", __assign$1({ fill: "none", fillRule: "evenodd" }, { children: [jsx("circle", { cx: "28", cy: "28", r: "27", fill: "#F7F7F7" }), jsx("path", { fill: "#10465B", d: "M41 18.5c0-3-2.5-5.5-5.5-5.5a5.5 5.5 0 0 0-5.4 6.7l-8.5 4.6A5.5 5.5 0 0 0 12 28c0 3 2.5 5.5 5.5 5.5 1.6 0 3-.7 4-1.8l8.6 4.6-.1 1.2c0 3 2.4 5.5 5.5 5.5a5.5 5.5 0 1 0 0-11c-1.6 0-3 .8-4.1 1.9l-8.5-4.6a5.4 5.4 0 0 0 0-2.6l8.5-4.6a5.5 5.5 0 0 0 9.6-3.6Zm-8.3 0c0-1.6 1.3-2.8 2.8-2.8 1.5 0 2.7 1.2 2.7 2.8 0 1.5-1.2 2.7-2.7 2.7a2.7 2.7 0 0 1-2.8-2.7Zm0 19c0-1.5 1.3-2.7 2.8-2.7 1.5 0 2.7 1.2 2.7 2.7 0 1.6-1.2 2.8-2.7 2.8a2.7 2.7 0 0 1-2.8-2.8ZM17.5 25.3a2.7 2.7 0 0 0-2.7 2.7c0 1.5 1.2 2.7 2.7 2.7 1.5 0 2.8-1.2 2.8-2.7 0-1.5-1.3-2.7-2.8-2.7Z", clipRule: "evenodd" })] }))),
    }); },
    Bulb: function () { return ({
        height: 56,
        width: 56,
        viewBox: '0 0 56 56',
        icon: (jsxs("g", __assign$1({ fill: "none", fillRule: "evenodd", transform: "translate(-1 -1)" }, { children: [jsx("circle", { cx: "28", cy: "28", r: "27", fill: "#F7F7F7" }), jsx("path", { fill: "#10465B", d: "M28 13a10 10 0 0 1 7 17.2c-.4 1.2-.6 2.3-.7 3.5v.7c0 .6-.5 1-1 1H33v1.4c0 .9-.5 1.7-1.2 2.1v1.6c0 1.4-1.2 2.5-2.5 2.5h-2.6a2.5 2.5 0 0 1-2.5-2.5v-1.6a2.5 2.5 0 0 1-1.2-2.1v-1.4h-.1c-.6 0-1-.4-1.1-1a21 21 0 0 0-.7-4.1A10 10 0 0 1 28 13Zm1.3 26.3h-2.6v1.2h2.6v-1.3Zm1.2-3.8h-5v1.3h5v-1.3Zm-2.5-20a7.5 7.5 0 0 0-5.2 13c.4.3.6.7.8 1.2l.5 2.6v.6H32v-.2l.4-2.3.2-.8c.1-.4.4-.9.7-1.2a7.5 7.5 0 0 0-5.2-13Zm0 1.5h.2l1 .2c2.8.6 5 3 5 5.9v1.1a1.3 1.3 0 0 1-2.4.1v-.9c0-1.8-1.2-3.3-2.9-3.7h-.2l-1-.2a1.3 1.3 0 0 1 .4-2.5ZM41 20.8l1.2.3c.6.2 1 1 .9 1.6-.2.6-1 1-1.6.9l-1.2-.4c-.7-.1-1-.8-.9-1.5.2-.7.9-1 1.6-.9Zm-25.8 0c.7-.2 1.4.2 1.6 1 .1.6-.2 1.3-1 1.4l-1.1.4c-.7.1-1.4-.3-1.6-1-.1-.6.3-1.3 1-1.5Zm27.7-3.7c.3.6.1 1.3-.5 1.7l-1 .6c-.7.3-1.4.1-1.8-.5-.3-.6-.1-1.3.5-1.7l1-.6c.7-.3 1.4-.1 1.8.5Zm-29.6 0c.4-.6 1.1-.8 1.7-.5l1.1.6c.6.4.8 1.1.5 1.7-.4.6-1.1.8-1.7.5l-1.1-.6c-.6-.4-.8-1.1-.5-1.7Zm25.3-4c.7.1 1 .8.9 1.5l-.3 1.2c-.2.7-.9 1-1.6.9-.6-.2-1-.9-.9-1.5l.4-1.3c.1-.6.8-1 1.5-.8Zm-21 0c.7-.2 1.4.2 1.5.8l.4 1.3c.1.6-.3 1.3-1 1.5-.6.2-1.3-.2-1.5-.9l-.3-1.2c-.2-.7.2-1.4.9-1.5Z" })] }))),
    }); },
    Calculator: function () { return ({
        height: 56,
        width: 56,
        viewBox: '0 0 56 56',
        icon: (jsxs("g", __assign$1({ fill: "none", fillRule: "evenodd" }, { children: [jsx("circle", { cx: "28", cy: "28", r: "27", fill: "#F7F7F7" }), jsx("path", { fill: "#10465B", d: "M13.2 26.3v.1c0-.5.1-1 .4-1.5.4-.6 1-1 1.6-1.2l2.8-.8 15.5-8.5c1.3-.7 2.8-.3 3.5 1l1.8 3.2c.4.3.6.7.8 1.3v.2l3 5.5c.7 1.1.4 2.5-.6 3.3l.6 2.2c.3 1.1-.2 2.3-1 2.9v-2.7 8.1c0 1.4-1.2 2.6-2.6 2.6H15.7a2.6 2.6 0 0 1-2.5-2.6V28.2c-.3-.6-.3-1.3 0-1.9ZM15.7 37v2.5h2.6a3.2 3.2 0 0 0-2.6-2.5Zm18.1-10.5h-13a5.8 5.8 0 0 1-5 5.2v2.6c2.6.3 4.8 2.5 5 5.2h13a5.8 5.8 0 0 1 5.2-5.2v-2.6a5.8 5.8 0 0 1-5.2-5.2ZM39 37a3.2 3.2 0 0 0-2.5 2.5H39v-2.5ZM27.4 29c2 0 3.8 1.8 3.8 4 0 2-1.7 3.8-3.8 3.8a3.9 3.9 0 0 1-4-3.9c0-2.1 1.8-3.9 4-3.9Zm0 2.6c-.8 0-1.3.6-1.3 1.3 0 .7.5 1.3 1.3 1.3.7 0 1.2-.6 1.2-1.3 0-.7-.5-1.3-1.2-1.3Zm-9.1-5.2h-2.6V29a3.2 3.2 0 0 0 2.6-2.5Zm20.7 0h-2.5A3.2 3.2 0 0 0 39 29v-2.5Zm-2-5.8-12.2 3.2H38l-.8-2.7-.3-.5Zm-2.2-4-6.4 3.6 7.2-2-.8-1.6Z" })] }))),
    }); },
    ContactAddress: function () { return ({
        height: 56,
        width: 56,
        viewBox: '0 0 56 56',
        icon: (jsxs("g", __assign$1({ fill: "none", fillRule: "evenodd" }, { children: [jsx("circle", { cx: "28", cy: "28", r: "27", fill: "#F7F7F7" }), jsx("path", { fill: "#10465B", d: "M36.8 40.5a1.3 1.3 0 0 1 0 2.5H19.3a1.3 1.3 0 0 1 0-2.5h17.4ZM28 13a10 10 0 0 1 10 10c0 3.3-2.7 8-8.1 14.2a2.5 2.5 0 0 1-3.6.1l-.2-.1-.4-.5C20.6 30.8 18 26.2 18 23a10 10 0 0 1 10-10Zm0 2.5a7.5 7.5 0 0 0-7.5 7.5c0 2.4 2.2 6.4 6.7 11.6l.4.5.4.4.4-.4c4.6-5.4 7-9.4 7.1-11.9V23c0-4.1-3.4-7.5-7.5-7.5Zm0 2.5a5 5 0 1 1 0 10 5 5 0 0 1 0-10Zm0 2.5a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5Z" })] }))),
    }); },
    ContactMail: function () { return ({
        height: 56,
        width: 56,
        viewBox: '0 0 56 56',
        icon: (jsxs("g", __assign$1({ fill: "none", fillRule: "evenodd" }, { children: [jsx("circle", { cx: "28", cy: "28", r: "27", fill: "#F7F7F7" }), jsx("path", { fill: "#10465B", d: "M39.7 18c1.3 0 2.3 1 2.3 2.3v16.4c0 1.3-1 2.3-2.3 2.3H16.3C15 39 14 38 14 36.7V20.3c0-1.3 1-2.3 2.3-2.3h23.4Zm-15 12.2L18 36.7h19.3l-6.4-6.2-.5.4a3.5 3.5 0 0 1-4.7.2l-.2-.2-.7-.7Zm15-8.4-7.2 7 7.2 7v-14Zm-23.4.1v13.2l6.8-6.5-6.8-6.7Zm21.5-1.6H18l9.2 9c.4.4 1 .4 1.5 0h.1l9-9Z" })] }))),
    }); },
    ContactPhone: function () { return ({
        height: 56,
        width: 56,
        viewBox: '0 0 56 56',
        icon: (jsxs("g", __assign$1({ fill: "none", fillRule: "evenodd" }, { children: [jsx("circle", { cx: "28", cy: "28", r: "27", fill: "#F7F7F7" }), jsx("path", { fill: "#10465B", d: "M19.5 15c.7 0 1.4.4 2 1l1.2 1.1.2.3 2 2c1.3 1.3 1.3 3 0 4.2l-.5.5-1.4 1.4c.3.9.8 1.7 1.6 2.7A21 21 0 0 0 30 33l.4.2.3.1 1.9-1.9a3 3 0 0 1 2-1c.7 0 1.4.3 2 .8l.1.2 3.3 3.4a3 3 0 0 1 1 2.1 3 3 0 0 1-1 2.1l-.3.4-.4.4-.8.9-.3.3a4.5 4.5 0 0 1-3.9 1.5c-2-.1-4-1-5.3-1.6a28.6 28.6 0 0 1-14.2-15.4l-.2-.5c-.3-1-.7-2.5-.6-4 .1-1 .5-2 1.1-2.7l.2-.2 2.1-2.1a3 3 0 0 1 2-1Zm0 2.5-.3.1-2.2 2.2a2 2 0 0 0-.6 1.3c0 1.1.2 2.2.7 3.6.8 2.4 2.2 4.6 4.2 7 2.4 3 5.4 5.4 8.7 7 1.7.8 3 1.3 4.4 1.4h.3c.6 0 1.1-.2 1.6-.6l.1-.1.1-.2.4-.4.7-.7.2-.2.4-.4.1-.1.3-.5s0-.2-.2-.3L35 33l-.3-.2-.3.1-.1.1-2.6 2.6c-.3.4-1 .5-1.4.2l-.8-.4-.7-.4-.3-.1-.2-.2A23.4 23.4 0 0 1 21 27l-.2-.4-.4-.8c-.2-.4 0-1 .3-1.3l.8-.8.3-.3 1.4-1.4c.3-.3.3-.4 0-.7V21l-2-2.1-.3-.2-.4-.4-.3-.4-.4-.3c-.1-.2-.2-.2-.3-.2Zm9 3.5c.2-.6 1-1 1.5-.7 1.4.5 2.5 1.2 3.4 2.1.8 1 1.5 2.1 1.9 3.5.2.7-.2 1.3-.8 1.5-.7.2-1.4-.2-1.5-.8a6 6 0 0 0-1.4-2.5 6 6 0 0 0-2.4-1.5c-.6-.2-1-1-.7-1.6Zm1-3c.2-.7.8-1.1 1.5-1a10.4 10.4 0 0 1 7.7 8.3c.2.7-.2 1.3-.9 1.5-.6.1-1.3-.3-1.4-1a8 8 0 0 0-6-6.5c-.7 0-1-.7-.9-1.4Zm.8-3.6c.2-.6.8-1 1.5-.8 2.6.6 4.9 2 6.8 4 1.9 2 3 4.4 3.5 7 0 .7-.4 1.3-1 1.4-.7 0-1.3-.4-1.4-1-.4-2.2-1.3-4-2.9-5.8a11.1 11.1 0 0 0-5.6-3.3c-.6-.2-1-.8-.9-1.5Z" })] }))),
    }); },
    Credit: function () { return ({
        height: 56,
        width: 56,
        viewBox: '0 0 56 56',
        icon: (jsxs("g", __assign$1({ fill: "none", fillRule: "evenodd" }, { children: [jsx("path", { d: "M-741-3779H699v7912H-741z" }), jsxs("g", __assign$1({ transform: "translate(-1 -1)" }, { children: [jsx("circle", { cx: "28", cy: "28", r: "27", fill: "#F7F7F7" }), jsx("path", { fill: "#10465B", d: "M36.2 28.9v9.4c0 1.5-1.2 2.7-2.7 2.7h-11a2.7 2.7 0 0 1-2.7-2.7v-9.4h-4A2.7 2.7 0 0 1 13 26v-9.4c0-1.5 1.2-2.7 2.7-2.7h24.6c1.5 0 2.7 1.2 2.7 2.7v9.4c0 1.5-1.2 2.8-2.7 2.8h-4.1Zm-2.7-5.4h-11v14.8h11V23.5ZM28 26a4 4 0 0 1 4 4.1 4 4 0 0 1-4 4 4 4 0 0 1-4-4 4 4 0 0 1 4-4Zm0 2.8c-.8 0-1.4.6-1.4 1.3 0 .7.6 1.4 1.4 1.4.8 0 1.4-.7 1.4-1.4s-.6-1.3-1.4-1.3Zm8.2-2.8h4v-9.4H15.8v9.4h4.1v-2.7a1.3 1.3 0 1 1 0-2.6h16.4a1.3 1.3 0 1 1 0 2.6v2.8Z" })] }))] }))),
    }); },
    Darts: function () { return ({
        height: 56,
        width: 56,
        viewBox: '0 0 56 56',
        icon: (jsxs("g", __assign$1({ fill: "none", fillRule: "evenodd", transform: "translate(-1 -1)" }, { children: [jsx("circle", { cx: "28", cy: "28", r: "27", fill: "#F7F7F7" }), jsx("path", { fill: "#10465B", d: "M28 13c2.8 0 5.4.8 7.6 2l1-1a1 1 0 0 1 1.7.5l.6 2.3 2.3.6a1 1 0 0 1 .4 1.7l-4.4 4.4-.2.2a10 10 0 1 1-5-4.8l.2-.4 1.6-1.6-.4-.2a12.5 12.5 0 1 0 5.4 5l1.9-1.7A15 15 0 1 1 28 13Zm0 7.5a7.5 7.5 0 1 0 6.2 3.3h-.4l-.2.4-4.6 4.6a1.3 1.3 0 0 1-1.8-1.8l1.4-1.4h-.2l-.4-.1a2.5 2.5 0 1 0 2.4 1.8l1.9-1.9a5 5 0 1 1-1.8-1.8l1.3-1.2v-.8l-.2-.2c-1-.6-2.3-.9-3.6-.9Zm8.5-2.8-2.1 2.1v1.5h1.5l2.1-2.1-1.2-.3-.3-1.2Z" })] }))),
    }); },
    Digital: function () { return ({
        height: 56,
        width: 56,
        viewBox: '0 0 56 56',
        icon: (jsxs("g", __assign$1({ fill: "none", fillRule: "evenodd" }, { children: [jsx("circle", { cx: "28", cy: "28", r: "27", fill: "#F7F7F7" }), jsx("path", { fill: "#10465B", d: "M22.2 13c-2.6 0-4.7 2-4.7 4.6v20.8c0 2.5 2 4.6 4.7 4.6h11.6c2.6 0 4.7-2 4.7-4.6V17.6c0-2.5-2-4.6-4.7-4.6H22.2Zm2.3 2.3h7v1.3h-7v-1.3Zm-4.7 2.3c0-1.3 1-2.3 2.4-2.3v1.3c0 1.3 1 2.3 2.3 2.3h7c1.3 0 2.3-1 2.3-2.3v-1.3c1.3 0 2.4 1 2.4 2.3v20.8c0 1.3-1 2.3-2.4 2.3H22.2c-1.3 0-2.4-1-2.4-2.3V17.6Zm4.7 18.5a1.2 1.2 0 1 0 0 2.3h7a1.2 1.2 0 1 0 0-2.3h-7Zm8.9-13c-.5-.4-1.3-.4-1.7.1l-5 6.6-2-2-.2-.2a1.2 1.2 0 0 0-1.5 1.8l3 2.9h.1c.5.4 1.2.4 1.6 0l5.8-7.6c.4-.6.3-1.2-.1-1.6Z", clipRule: "evenodd" })] }))),
    }); },
    Feather: function () { return ({
        height: 56,
        width: 56,
        viewBox: '0 0 56 56',
        icon: (jsxs("g", __assign$1({ fill: "none", fillRule: "evenodd" }, { children: [jsx("circle", { cx: "28", cy: "28", r: "27", fill: "#F7F7F7" }), jsx("path", { fill: "#10465B", d: "m43.6 14.4.3 4.2a15 15 0 0 1-3.5 10.6l-3.6 4.2a1 1 0 0 1-.9.3l-4.4-.6 2.1 2.1c.4.4.4 1 0 1.4l-.7.8c-.9.9-2.1 1.4-3.4 1.4H19l-3.6 3.5a1.2 1.2 0 0 1-1.6-1.7l3.5-3.5V26.7a5 5 0 0 1 1.5-3.5l6.3-6.3a1 1 0 0 1 1.5 0l2 2-1.4-2.5a1 1 0 0 1 .3-1.4l3-1.3c2.5-1.2 5.3-1.7 8-1.5l3.3.3c1 0 1.8.9 1.8 1.9Zm-22.3 22L37 20.6a1.1 1.1 0 1 0-1.6-1.7L19.6 34.8V26.5c.1-.6.4-1.2.9-1.7l5.4-5.4 1.2 1.2v.1c2 1.7 4.9-.6 3.6-2.9l-.8-1.4 1.5-.7.4-.1c2-.9 4.3-1.3 6.6-1l3 .1.2 4v.4c.1 3.2-1 6.2-3 8.6l-3 3.7-3.8-.6h-.1c-2.1-.1-3.3 2.4-1.8 4l1.2 1.1H31c-.4.4-1 .6-1.5.6h-8.2Z", clipRule: "evenodd" })] }))),
    }); },
    Frozen: function () { return ({
        height: 56,
        width: 56,
        viewBox: '0 0 56 56',
        icon: (jsxs("g", __assign$1({ fill: "none", fillRule: "evenodd" }, { children: [jsx("circle", { cx: "28", cy: "28", r: "27", fill: "#F7F7F7" }), jsx("path", { fill: "#10465B", d: "M28 13c.8 0 1.5.7 1.5 1.5v1.7l2.2-1.4c.6-.5 1.6-.3 2 .4.4.6.3 1.5-.3 2l-3.9 2.6v5.6l4.8-2.8.3-4.6c0-.8.7-1.4 1.5-1.4s1.4.7 1.4 1.4v.2l-.1 2.6 1.5-.8c.7-.5 1.6-.2 2 .5.4.7.2 1.6-.6 2l-1.4 1 2.3 1c.7.4 1 1.3.6 2-.3.8-1 1-1.8.8H40l-4.1-2L31 28l4.8 2.8 4-2c.8-.4 1.7-.1 2 .6.4.7.1 1.5-.5 2h-.1l-2.4 1.2 1.5.9c.8.4 1 1.3.6 2s-1.3 1-2 .6l-1.5-1 .1 2.7c0 .8-.5 1.5-1.4 1.6-.7 0-1.4-.5-1.5-1.2V38l-.3-4.6-4.8-2.8v5.6l3.8 2.6c.7.4.9 1.4.4 2-.4.7-1.2.9-1.9.5h-.1l-2.2-1.5v1.7c0 .8-.7 1.5-1.5 1.5s-1.5-.7-1.5-1.5v-1.7l-2.1 1.5c-.7.4-1.6.2-2-.5-.5-.6-.4-1.4.2-2h.1l3.8-2.6v-5.6l-4.8 2.8-.3 4.6c0 .8-.7 1.5-1.5 1.4-.8 0-1.4-.7-1.4-1.4v-.2l.1-2.6-1.4.9c-.7.4-1.6.1-2-.6-.5-.7-.2-1.6.5-2l1.5-.9-2.4-1.2c-.7-.3-1-1.2-.6-2 .3-.7 1-1 1.8-.7h.1l4.1 2.1L25 28l-4.8-2.8-4 2c-.8.4-1.7.1-2-.6-.4-.7-.1-1.5.5-2h.1l2.3-1.2-1.4-.8c-.7-.5-1-1.4-.5-2.1.4-.7 1.3-1 2-.5l1.4.8-.1-2.6c0-.8.5-1.6 1.4-1.6.7 0 1.4.5 1.5 1.2v.2l.3 4.6 4.8 2.8v-5.6l-3.8-2.5c-.7-.5-.8-1.4-.4-2 .4-.7 1.3-1 2-.6l2.2 1.5v-1.7c0-.8.7-1.5 1.5-1.5Z", clipRule: "evenodd" })] }))),
    }); },
    Heart: function () { return ({
        height: 56,
        width: 56,
        viewBox: '0 0 56 56',
        icon: (jsxs("g", __assign$1({ fill: "none", fillRule: "evenodd", transform: "translate(-1 -1)" }, { children: [jsx("circle", { cx: "28", cy: "28", r: "27", fill: "#F7F7F7" }), jsx("path", { fill: "#10465B", fillRule: "nonzero", d: "M34.6 15a8 8 0 0 1 6 2.6c1.6 1.7 2.4 4 2.4 6.5 0 2.6-1 5-3 7.4-1.2 1.5-2.7 3-4.6 4.6l-.9.8-1.4 1.3-2 1.6-1 .9-1 .9a1.7 1.7 0 0 1-2.3 0l-1-1-1.5-1.2-2.4-2-.9-1a45 45 0 0 1-5-4.9c-2-2.5-3-4.8-3-7.4 0-2.5.8-4.8 2.4-6.5a8 8 0 0 1 6-2.6c1.7 0 3.3.6 4.7 1.6l1.9 2c.6-.7 1.2-1.4 2-2 1.3-1 2.9-1.6 4.6-1.6Zm0 2.5a5 5 0 0 0-3.2 1.1 7 7 0 0 0-1.1 1.2l-.3.3-2 2.9-2-2.9-1.4-1.5a5 5 0 0 0-3.2-1.1c-1.6 0-3 .6-4.2 1.8a7 7 0 0 0-1.7 4.8 9 9 0 0 0 2.4 5.8l.4.4.3.5.4.4.3.2.4.5.5.5.3.3.4.3 1 1 1 .9 1.2 1 3.7 3.1.2.2 2.4-2 2.1-1.9.8-.7 1-.8.8-.8.6-.5.3-.3.5-.5.4-.4.2-.3.4-.4.2-.3.3-.2v-.2a9 9 0 0 0 2.5-5.8 7 7 0 0 0-1.7-4.8 5.5 5.5 0 0 0-4.2-1.8Z" })] }))),
    }); },
    Marketplace: function () { return ({
        height: 56,
        width: 56,
        viewBox: '0 0 56 56',
        icon: (jsxs("g", __assign$1({ fill: "none", fillRule: "evenodd" }, { children: [jsx("circle", { cx: "28", cy: "28", r: "27", fill: "#F7F7F7" }), jsx("path", { fill: "#10465B", d: "M28 20.8V22a1.2 1.2 0 1 1-2.5 0v-1.3h-1.3v3.8H28c.7 0 1.3.6 1.3 1.3v13.7h11.2V20.7h-1.3V22a1.2 1.2 0 1 1-2.5 0v-1.3H28Zm-6.3-1.3v5h-7.4c-.7 0-1.3.6-1.3 1.3v13.7c0 1.4 1.1 2.5 2.5 2.5h25c1.4 0 2.5-1.1 2.5-2.5v-20c0-.7-.6-1.3-1.3-1.3h-2.5a6.9 6.9 0 0 0-13.7 0H23c-.7 0-1.3.6-1.3 1.3Zm15-1.3c-.3-2-2-3.6-4-3.7h-.3c-2.2 0-4 1.6-4.4 3.8h8.7ZM15.5 27h11.3v12.5H15.4V27Zm5.4 5c-1-.1-1.6-.9-1.6-1.9v-.6a1.2 1.2 0 1 0-2.6 0v.6a4.4 4.4 0 0 0 8.8 0v-.6a1.2 1.2 0 1 0-2.5 0v.8c-.1 1-.9 1.7-1.9 1.7H21Z", clipRule: "evenodd" })] }))),
    }); },
    Message: function () { return ({
        height: 56,
        width: 56,
        viewBox: '0 0 56 56',
        icon: (jsxs("g", __assign$1({ fill: "none", fillRule: "evenodd" }, { children: [jsx("circle", { cx: "28", cy: "28", r: "27", fill: "#F7F7F7" }), jsx("path", { fill: "#10465B", d: "M27.5 14.7a3 3 0 0 0-4.9 1l-7.6 18-1.2 1.2a2 2 0 0 0 0 2.9L18 42c.8.8 2 .8 2.8 0l1.2-1.2 1-.4.1 1a2 2 0 0 0 2.7 1.5l4.7-1.8a2 2 0 0 0 1-2.9l-.7-1.1 9.3-4a3 3 0 0 0 1-4.9L27.4 14.7Zm-2.4 24.8 3.6-1.5.6 1-4 1.6-.2-1.1Zm4.6-4.5 5.8-2.4-12.3-12.3-5.5 12.9.3.3 4.2 4.3.3.2 7.1-3Zm-5-18.4-.6 1.4 13.6 13.6 1.5-.6h.1v-.1c.3-.3.4-.7.2-1h-.1L25.9 16.4l-.2-.1a.7.7 0 0 0-1 .2Zm-9.1 19.7 1-1 3.8 3.9-1 1-3.8-3.9Zm19.6-25.1c-.6-.2-1.2.3-1.4.9l-.7 3.5v.1a1.2 1.2 0 0 0 2.3.4l.7-3.6v-.1c0-.6-.3-1.1-.9-1.2Zm4.6 3.1a1.2 1.2 0 0 1 1.7 1.5v.1l-4.3 4.3a1.1 1.1 0 0 1-1.7-1.5v-.1l4.3-4.3Zm-1 7.4c-.1-.6.3-1.2.9-1.3l3.5-.7h.2a1.2 1.2 0 0 1 .3 2.2l-3.6.7c-.7 0-1.2-.3-1.3-.9Z", clipRule: "evenodd" })] }))),
    }); },
    Mountain: function () { return ({
        height: 56,
        width: 56,
        viewBox: '0 0 56 56',
        icon: (jsxs("g", __assign$1({ fill: "none", fillRule: "evenodd", transform: "translate(-1 -1)" }, { children: [jsx("circle", { cx: "28", cy: "28", r: "27", fill: "#F7F7F7" }), jsx("path", { fill: "#10465B", d: "M32.3 12a1 1 0 0 1 .8 1.5l-1.1 2 1.1 2a1 1 0 0 1-.8 1.5h-6v2.5l.4.6 9.6 17 .3.6H41l-5.1-9-2.2 3.9-1.3-2.4 1.8-3.1a2 2 0 0 1 3.4 0l5.6 10a2 2 0 0 1-1.7 2.9H15.4a2 2 0 0 1-1.7-3l9.6-17 .7-.7V13c0-.6.4-1 1-1h7.3ZM25 29.7l-1.2 1.2c-.4.4-1 .5-1.5.1h-.1l-.8-.9-5.5 9.6h18.2l-5.5-9.6-.8.8c-.4.4-1 .5-1.5.1L25 29.6Zm0-6L22.5 28l.5.5 1.2-1.2c.4-.5 1-.5 1.5-.1h.1l1.2 1.3.5-.5-2.5-4.3Zm5-9.4h-3.7v2.4H30l-.6-1.2.6-1.2Z" })] }))),
    }); },
    PayLater: function () { return ({
        height: 56,
        width: 56,
        viewBox: '0 0 56 56',
        icon: (jsxs("g", __assign$1({ fill: "none", fillRule: "evenodd" }, { children: [jsx("circle", { cx: "28", cy: "28", r: "27", fill: "#F7F7F7" }), jsx("path", { fill: "#10465B", d: "M43 19.6c0-1.5-1.1-2.6-2.5-2.6h-25a2.5 2.5 0 0 0-2.5 2.6V37.4c0 1.5 1.1 2.6 2.5 2.6h25c1.4 0 2.5-1.1 2.5-2.6V19.6ZM40.5 22v-2.5h-25V22h25Zm-25 2.6v12.7h25V24.7h-25ZM39.3 31c0-.8-.6-1.3-1.3-1.3h-6.3c-.6 0-1.2.5-1.2 1.3v3.8c0 .7.6 1.3 1.3 1.3H38c.7 0 1.3-.6 1.3-1.3V31ZM33 32.3h3.8v1.3H33v-1.3Z", clipRule: "evenodd" })] }))),
    }); },
    Puzzle: function () { return ({
        height: 56,
        width: 56,
        viewBox: '0 0 56 56',
        icon: (jsxs("g", __assign$1({ fill: "none", fillRule: "evenodd", transform: "translate(-1 -1)" }, { children: [jsx("circle", { cx: "28", cy: "28", r: "27", fill: "#F7F7F7" }), jsx("path", { fill: "#10465B", d: "m23 17.5 3.6-3.6a2 2 0 0 1 2.8 0l3.4 3.3c.1.1.3.1.4 0l.5-.5a4 4 0 1 1 5.1 6c-.1.2-.2.3 0 .5l3.3 3.4c.8.8.8 2 0 2.8l-3.6 3.7a1 1 0 0 1-1.6-.2l-.4-.7a3 3 0 1 0-4.3 4.3l.6.4a1 1 0 0 1 .3 1.6L29.4 42a2 2 0 0 1-2.8 0l-3.4-3.3a.3.3 0 0 0-.4 0l-.5.5a4 4 0 1 1-5.1-6c.1-.2.2-.3 0-.5L14 29.4a2 2 0 0 1 0-2.8l3.6-3.7a1 1 0 0 1 1.6.2l.4.7a3 3 0 1 0 4.3-4.3l-.6-.4a1 1 0 0 1-.3-1.6Zm2.9.6v.1a5.5 5.5 0 0 1-7.7 7.8v-.1L16 28l3 3 .1.2.2.2c.9 1.2.6 2.8-.5 3.8l-.3.2a1.5 1.5 0 1 0 2 2.1h.2l.2-.3V37l.1-.1c1-1 2.8-1 3.9-.1v.1l3.1 3 2.1-2.1v-.1a5.5 5.5 0 0 1 .2-7.2l.2-.1c2-2 5.1-2.2 7.3-.5v.1L40 28l-3-3-.1-.2-.2-.2c-.9-1.2-.6-2.8.5-3.8l.3-.2a1.5 1.5 0 0 0-2-2.1h-.2l-.2.3-.1.2c-1 1-2.8 1-3.9.1V19L28 16 25.9 18Z" })] }))),
    }); },
    Security: function () { return ({
        height: 56,
        width: 56,
        viewBox: '0 0 56 56',
        icon: (jsxs("g", __assign$1({ fill: "none", fillRule: "evenodd" }, { children: [jsx("path", { d: "M-517-3753H923v8320H-517z" }), jsxs("g", __assign$1({ transform: "translate(-1 -1)" }, { children: [jsx("circle", { cx: "28", cy: "28", r: "27", fill: "#F7F7F7" }), jsx("path", { fill: "#10465B", d: "M27 39.1a1.3 1.3 0 0 1 1.6-.6c.6.3.9 1 .6 1.7a20.9 20.9 0 0 1-1.4 2.3c-.4.5-1.1.7-1.7.3-.5-.4-.7-1.2-.3-1.8a15.1 15.1 0 0 0 1.1-1.9Zm7-7.2h.1a1.2 1.2 0 0 1 1.1 1.4v.3a27.4 27.4 0 0 1-2.8 8.4c-.3.6-1 .8-1.6.5-.6-.4-.8-1.1-.5-1.7l.2-.6a25 25 0 0 0 2.2-7.2c.1-.7.8-1.2 1.4-1.1ZM26.8 29c.7 0 1.2.5 1.2 1.2v.5a20.3 20.3 0 0 1-4.2 10.8c-.4.5-1.1.6-1.7.2-.5-.5-.6-1.3-.1-1.8l.5-.7a17.7 17.7 0 0 0 3-9 1.3 1.3 0 0 1 1.3-1.2ZM21 33.7a1.3 1.3 0 0 1 1.6-.8 1.2 1.2 0 0 1 .7 1.6 16.5 16.5 0 0 1-2.9 5.3c-.4.4-1.2.5-1.7 0s-.5-1.2-.1-1.8l.4-.5a14 14 0 0 0 2-3.8Zm14.5 3.7c.2-.6.9-1 1.5-.8h.1c.6.2 1 .8.8 1.5l-.1.4-.1.4a18.3 18.3 0 0 1-.1.4c-.3.6-1 1-1.6.7-.7-.2-1-1-.8-1.6a12.5 12.5 0 0 0 .3-1Zm-15.2-14c.5-.4 1.2-.4 1.6 0 .5.6.5 1.4 0 1.9l-.5.6c-.6 1-1 2.2-1 3.5a12.6 12.6 0 0 1-2.6 7.6c-.5.5-1.2.6-1.7.1-.5-.4-.6-1.2-.2-1.8l.4-.5a10 10 0 0 0 1.6-5.4V29a8.8 8.8 0 0 1 2.4-5.6Zm6.3 1a5 5 0 0 1 5 5 24 24 0 0 1-1 7c-.3.7-1 1-1.6.8-.6-.2-1-1-.8-1.6a18.3 18.3 0 0 0 1-6.2c0-1.4-1.2-2.6-2.6-2.6a2.5 2.5 0 0 0-2.5 2.6v1.1c-.1.7-.7 1.2-1.4 1-.6 0-1.1-.6-1-1.3v-.8c0-1 .2-1.8.6-2.5a5 5 0 0 1 4.3-2.6Zm6.4-4.5c.4-.5 1.2-.6 1.7-.1l.7.6c2.4 2.3 3.7 5.6 3.7 9a31.9 31.9 0 0 1-.3 5c-.2.6-.8 1-1.5 1-.7-.2-1.1-.8-1-1.5a20.8 20.8 0 0 0 .4-4.5 10.1 10.1 0 0 0-3.5-7.6 1.3 1.3 0 0 1-.2-1.9ZM15 25.2c.2-.6 1-1 1.6-.6.6.2.9 1 .7 1.6l-.2.5c-.2.9-.4 1.8-.4 2.7v.4A8.8 8.8 0 0 1 16 33c-.3.5-1 .7-1.6.4-.6-.4-.8-1.1-.5-1.7a5 5 0 0 0 .4-2.4 12.6 12.6 0 0 1 .8-4.2Zm24.6-3.7c.6-.3 1.4 0 1.7.5l.2.6a16.5 16.5 0 0 1 1.1 10c-.1.6-.8 1-1.4.8-.7-.1-1.1-.8-1-1.5l.1-.5c.2-.7.2-1.3.2-2a14 14 0 0 0-1.4-6.1 1.3 1.3 0 0 1 .5-1.8Zm-12.9-1a8.8 8.8 0 0 1 8.8 9c0 .6-.6 1.1-1.3 1.1-.7 0-1.2-.6-1.2-1.3l-.1-1c-.5-3-3-5.2-6.2-5.2-.3 0-.8 0-1.4.2a1.4 1.4 0 0 1-1.7-1c-.2-.5.2-1.2.8-1.4a8 8 0 0 1 2.3-.3Zm0-3.7a12.4 12.4 0 0 1 4.4.8c.6.3.9 1 .6 1.6-.3.6-1 1-1.6.7a8.1 8.1 0 0 0-3.4-.6A10 10 0 0 0 19 23a1.3 1.3 0 0 1-1.9.2c-.5-.4-.6-1.1-.2-1.7l.6-.7c2.4-2.6 5.7-4.1 9.2-4.1Zm-10.8.4c.5-.4 1.3-.3 1.7.2.4.5.4 1.3-.1 1.8l-.2.1a13.8 13.8 0 0 0-2 2.4 1.3 1.3 0 0 1-1.7.4c-.6-.4-.8-1.1-.4-1.7a12.8 12.8 0 0 1 2.7-3.2ZM26.7 13a16.1 16.1 0 0 1 12.1 5.4c.5.6.4 1.3-.2 1.8-.5.4-1.3.3-1.7-.2l-.4-.4a13.6 13.6 0 0 0-15.6-2.8 1.3 1.3 0 0 1-1.7-.6c-.3-.6 0-1.3.5-1.6l1.4-.6c1.8-.7 3.7-1 5.6-1Z" })] }))] }))),
    }); },
    Secure: function () { return ({
        height: 56,
        width: 56,
        viewBox: '0 0 56 56',
        icon: (jsxs("g", __assign$1({ fill: "none", fillRule: "evenodd" }, { children: [jsx("circle", { cx: "28", cy: "28", r: "27", fill: "#F7F7F7" }), jsx("path", { fill: "#10465B", d: "M34.5 14c.6 0 1.1.4 1.3 1l.3 1.5a4 4 0 0 0 3.2 3l2.7.5c.6.2 1 .7 1 1.3V23a26 26 0 0 1-9.8 18.6l-.6.4A10 10 0 0 1 28 44c-.4 0-.9-.1-1.5-.4l-.4-.1-.2-.1-.4-.2A25.2 25.2 0 0 1 13 22.8V21.6l-.1-.3c0-.6.4-1.1 1-1.3l2.7-.5a4 4 0 0 0 3.2-3l.3-1.6c.2-.5.7-.9 1.3-.9h13Zm-1 2.5h-11l-.2.6a6.6 6.6 0 0 1-4.8 4.8h-.3l-1.7.4v.3a23.8 23.8 0 0 0 10.1 17.8l.5.3a5 5 0 0 0 1.9.8c.4 0 1.4-.5 2.6-1.3l.5-.3a23.7 23.7 0 0 0 9.3-16.6v-1l-1.6-.3a6.6 6.6 0 0 1-5-4.6V17l-.2-.6Zm.4 7.4c-.6-.4-1.4-.4-1.8.1l-5.7 6.3-2.3-2.4a1.3 1.3 0 0 0-1.7 1.8l3.2 3.3.1.1c.5.4 1.3.4 1.7-.2l6.6-7.2v-.2c.4-.5.3-1.2-.1-1.6Z", clipRule: "evenodd" })] }))),
    }); },
    Umbrella: function () { return ({
        height: 56,
        width: 56,
        viewBox: '0 0 56 56',
        icon: (jsxs("g", __assign$1({ fill: "none", fillRule: "evenodd" }, { children: [jsx("path", { d: "M-741-4117H699v7912H-741z" }), jsxs("g", __assign$1({ transform: "translate(-1 -1)" }, { children: [jsx("circle", { cx: "28", cy: "28", r: "27", fill: "#F7F7F7" }), jsx("path", { fill: "#10465B", d: "M28.3 12c.5 0 1 .5 1 1 9.5.7 13.6 8.4 13.7 14.2v.3a1 1 0 0 1-1.5 1 9.2 9.2 0 0 0-8.2-.2l-.2.1a1 1 0 0 1-1 0l-1-.5-2-.4v11.3c0 2.6-1.3 4.2-3.7 4.2-2.4 0-3.7-1.6-3.7-4.3a1.2 1.2 0 1 1 2.3 0c0 1.5.4 2 1.4 2 1 0 1.4-.4 1.5-1.8V27.5a9.1 9.1 0 0 0-2.4.6l-.4.2-.2.1a1 1 0 0 1-1 0l-.4-.2-.3-.1-.3-.1a9.2 9.2 0 0 0-7 .3l-.4.2a1 1 0 0 1-1.5-1l.1-1.6v-.5c1-5.6 5.3-11.9 13.9-12.3 0-.6.5-1.1 1-1.1h.3Zm2.4 3.6v.1c1.8 3 2.7 6.4 2.8 10h-.1a11.5 11.5 0 0 1 7-.2l.2.1v-.2c-.9-4.8-4.5-8.8-10-9.8Zm-2.6.2c-2.3 3-3.5 6.2-3.6 9.9h-.3a11.5 11.5 0 0 1 7-.1c-.1-3.7-1.2-6.9-3.1-9.8Zm-2.7-.2c-5.5 1-9 5-9.9 9.6v.4h.2c.8-.3 1.8-.4 2.7-.5h.4c1 0 2 .2 3 .4l.4.1c0-3.6 1.1-7 3.2-10Z" })] }))] }))),
    }); },
    World: function () { return ({
        height: 56,
        width: 56,
        viewBox: '0 0 56 56',
        icon: (jsxs("g", __assign$1({ fill: "none", fillRule: "evenodd" }, { children: [jsx("path", { d: "M-294-3779h1440v7912H-294z" }), jsxs("g", __assign$1({ transform: "translate(-1 -1)" }, { children: [jsx("circle", { cx: "28", cy: "28", r: "27", fill: "#F7F7F7" }), jsx("path", { fill: "#10465B", d: "M28 13a15 15 0 1 1 0 30 15 15 0 0 1 0-30Zm12.4 16.3h-3.7A19 19 0 0 1 31.5 40c4.8-1.4 8.4-5.6 9-10.7Zm-21 0h-3.8c.5 5 4.1 9.3 9 10.7a19 19 0 0 1-5.3-10.7Zm7.4 0h-5a17 17 0 0 0 5 9.4v-9.5Zm7.4 0h-5v9.4a17 17 0 0 0 5-9.4ZM24.5 16h-.1a12.5 12.5 0 0 0-8.8 10.8h3.7A19 19 0 0 1 24.5 16Zm-2.7 10.8h5v-9.5a17 17 0 0 0-5 9.5Zm7.5-9.5v9.4h4.9a17 17 0 0 0-5-9.4Zm2.2-1.3.2.2c3 3.5 4.7 7 5 10.6h3.7c-.5-5.2-4.1-9.4-9-10.8Z" })] }))] }))),
    }); },
    SimpleGift: function () { return ({
        height: 56,
        width: 56,
        viewBox: '0 0 56 56',
        icon: (jsxs("g", __assign$1({ fill: "none", fillRule: "evenodd" }, { children: [jsx("path", { d: "M-294-3779h1440v7912H-294z" }), jsxs("g", __assign$1({ transform: "translate(-1 -1)" }, { children: [jsx("circle", { cx: "28", cy: "28", r: "27", fill: "#F7F7F7" }), jsx("path", { fill: "#10465B", d: "M23 16h.7c1.5 0 2.8 1.3 2.8 2.8v.2H24a2 2 0 0 1-2-2c0-.6.4-1 1-1Zm-3.6 3a5 5 0 0 1-.4-2 4 4 0 0 1 4-4h.7c1.7 0 3.2.7 4.3 2 1-1.3 2.6-2 4.3-2h.7a4 4 0 0 1 4 4 5 5 0 0 1-.4 2H40a3 3 0 0 1 3 3v3a3 3 0 0 1-3 3v12a3 3 0 0 1-3 3H19a3 3 0 0 1-3-3V28a3 3 0 0 1-3-3v-3a3 3 0 0 1 3-3h3.4ZM32 22H16v3h24v-3h-8Zm0-3a2 2 0 0 0 2-2c0-.6-.4-1-1-1h-.7a2.8 2.8 0 0 0-2.8 2.8v.2H32Zm-5 9h2v12h-2V28Zm5 0v12h5V28h-5Zm-8 0h-5v12h5V28Z" })] }))] }))),
    }); },
    Lightning: function () { return ({
        height: 56,
        width: 56,
        viewBox: '0 0 56 56',
        icon: (jsxs("g", __assign$1({ fill: "none", fillRule: "evenodd" }, { children: [jsx("circle", { cx: "28", cy: "28", r: "27", fill: "#F7F7F7" }), jsx("path", { stroke: "#10465B", strokeLinejoin: "round", strokeWidth: "2.5", d: "M34.7 15H24l-6 14.2h7.1L22.8 41 37 23.3h-7.2l4.8-8.3Z" })] }))),
    }); },
    Repay: function () { return ({
        height: 56,
        width: 56,
        viewBox: '0 0 56 56',
        icon: (jsxs("g", __assign$1({ fill: "none", fillRule: "evenodd" }, { children: [jsx("circle", { cx: "28", cy: "28", r: "27", fill: "#F7F7F7" }), jsx("path", { fill: "#10465B", d: "M43 39.3c0 .8-.7 1.5-1.5 1.5h-5.3c-.8 0-1.5-.7-1.5-1.5V34a1.5 1.5 0 0 1 3 0v1a12 12 0 0 0-13.4-18.4c-.8.2-1.7 0-2-.8-.4-.8 0-1.7.7-2a15 15 0 0 1 16.4 24h2.1c.8 0 1.5.6 1.5 1.5ZM13 16.8c0-.9.7-1.6 1.5-1.6h5.3c.8 0 1.4.7 1.4 1.6V22a1.5 1.5 0 1 1-3 0v-1a12 12 0 0 0 13.5 18.4c.8-.2 1.7 0 2 .8.4.8 0 1.7-.7 2a15 15 0 0 1-16.4-24h-2.1c-.8 0-1.5-.6-1.5-1.4Zm5.3 1.4Z", clipRule: "evenodd" })] }))),
    }); },
    Lower: function () { return ({
        height: 56,
        width: 56,
        viewBox: '0 0 56 56',
        icon: (jsxs("g", __assign$1({ fill: "none", fillRule: "evenodd" }, { children: [jsx("circle", { cx: "28", cy: "28", r: "27", fill: "#F7F7F7" }), jsx("path", { fill: "#10465B", d: "M39.2 18.8H16.8v12.6H21a1.4 1.4 0 1 1 0 2.8h-4.2a2.8 2.8 0 0 1-2.8-2.8V18.8c0-1.5 1.3-2.8 2.8-2.8h22.4c1.5 0 2.8 1.3 2.8 2.8v12.6c0 1.5-1.3 2.8-2.8 2.8H35a1.4 1.4 0 1 1 0-2.8h4.2V18.8ZM28 26.5a1.4 1.4 0 1 0 0-2.8 1.4 1.4 0 0 0 0 2.8Zm0 2.8a4.2 4.2 0 1 0 0-8.4 4.2 4.2 0 0 0 0 8.4Zm0 2.1c.8 0 1.4.6 1.4 1.4v6.4l3.2-3.2a1.4 1.4 0 1 1 2 2L29 43.6c-.6.5-1.4.5-2 0L21.4 38a1.4 1.4 0 1 1 2-2l3.2 3.2v-6.4c0-.8.6-1.4 1.4-1.4Z", clipRule: "evenodd" })] }))),
    }); },
    Repay2: function () { return ({
        height: 56,
        width: 56,
        viewBox: '0 0 56 56',
        icon: (jsxs("g", __assign$1({ fill: "none", fillRule: "evenodd" }, { children: [jsx("circle", { cx: "28", cy: "28", r: "27", fill: "#F7F7F7" }), jsx("path", { fill: "#10465B", d: "M15.3 19h.7a15 15 0 1 0 13.5-6c-.8 0-1.5.6-1.5 1.4 0 .9.7 1.5 1.5 1.6a12 12 0 1 1-11.3 5v1a1.5 1.5 0 0 0 3 0v-4.6c0-.8-.6-1.5-1.4-1.5h-4.6a1.5 1.5 0 0 0 0 3Zm14.2 4.4a1.5 1.5 0 1 0-3 0V28c0 .4.2.8.4 1l3 3a1.5 1.5 0 1 0 2.2-2l-2.6-2.6v-3.9Z", clipRule: "evenodd" })] }))),
    }); },
    Calendar2: function () { return ({
        height: 56,
        width: 56,
        viewBox: '0 0 56 56',
        icon: (jsxs("g", __assign$1({ fill: "none", fillRule: "evenodd" }, { children: [jsx("circle", { cx: "28", cy: "28", r: "27", fill: "#F7F7F7" }), jsx("path", { fill: "#10465B", d: "M18.5 14.4a1.4 1.4 0 0 1 2.7 0V17h13.6v-2.7a1.4 1.4 0 1 1 2.7 0V17h2.8c1.5 0 2.7 1.2 2.7 2.7v20.5c0 1.5-1.2 2.7-2.7 2.7H15.7a2.7 2.7 0 0 1-2.7-2.7V19.8c0-1.5 1.2-2.7 2.7-2.7h2.8v-2.7Zm16.3 5.4v1.4a1.4 1.4 0 1 0 2.7 0v-1.4h2.8V24H15.7v-4h2.8v1.3a1.4 1.4 0 0 0 2.7 0v-1.4h13.6Zm-19 6.8v13.7h24.5V26.6H15.7Zm17.7 6.9v1.3h1.3v-1.3h-1.3ZM32 30.7c-.8 0-1.4.6-1.4 1.4v4c0 .8.6 1.4 1.4 1.4h4c.8 0 1.4-.6 1.4-1.3V32c0-.8-.6-1.4-1.3-1.4H32Z", clipRule: "evenodd" })] }))),
    }); },
    Touch: function () { return ({
        height: 56,
        width: 56,
        viewBox: '0 0 56 56',
        icon: (jsxs("g", __assign$1({ fill: "none", fillRule: "evenodd" }, { children: [jsx("circle", { cx: "28", cy: "28", r: "27", fill: "#F7F7F7" }), jsx("path", { stroke: "#10465B", strokeLinejoin: "round", strokeWidth: "2.5", d: "M22 33.5V28a2 2 0 1 0-4 0v5.4c0 5.3 4.3 9.6 9.6 9.6h2c4.6 0 8.4-3.8 8.4-8.4V30a2 2 0 1 0-4 0v.5V28a2 2 0 1 0-4 0v2.5-4a2 2 0 1 0-4 0v4-11a2 2 0 1 0-4 0v14Z" }), jsx("path", { stroke: "#10465B", strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: "2.5", d: "M30 18.5s-1-5-6-5-6 5-6 5" })] }))),
    }); },
    Computer: function () { return ({
        height: 56,
        width: 56,
        viewBox: '0 0 56 56',
        icon: (jsxs("g", __assign$1({ fill: "none", fillRule: "evenodd" }, { children: [jsx("circle", { cx: "28", cy: "28", r: "27", fill: "#F7F7F7" }), jsx("path", { fill: "#10465B", d: "M40.5 16.5h-25v13.8h25V16.4Zm-25 18.8v-2.5h25v2.5h-25Zm0-21.3a2.5 2.5 0 0 0-2.5 2.5v18.8c0 1.3 1.1 2.5 2.5 2.5h11.3v3.7h-5a1.2 1.2 0 1 0 0 2.5h12.4a1.3 1.3 0 0 0 0-2.5h-5v-3.8h11.3c1.4 0 2.5-1 2.5-2.5V16.6c0-1.4-1.1-2.5-2.5-2.5h-25Zm11.2 3.9c.6.3.9 1 .5 1.7l-1 1.9h3.5l-1-2a1.2 1.2 0 0 1 2.3-1l1.5 3h.5a1.2 1.2 0 1 1 0 2.5h-.3l-1 4c-.1.6-.6 1-1.2 1h-5c-.6 0-1-.4-1.2-1l-1-4H23a1.3 1.3 0 0 1 0-2.5h.5l1.5-3c.3-.7 1-1 1.7-.6Zm-.2 8.6-.6-2.5H30l-.6 2.5h-3Z", clipRule: "evenodd" })] }))),
    }); },
    Bank: function () { return ({
        height: 56,
        width: 56,
        viewBox: '0 0 56 56',
        icon: (jsxs("g", __assign$1({ fill: "none", fillRule: "evenodd" }, { children: [jsx("circle", { cx: "28", cy: "28", r: "27", fill: "#F7F7F7" }), jsx("path", { fill: "#10465B", d: "M40.5 20.6v-.8L28 14.6l-12.5 5.2v.8h25Zm-26-3.1c-.9.4-1.5 1.3-1.5 2.3v.8c0 1.4 1.1 2.5 2.5 2.5h1.3v12.5h-1.3a2.5 2.5 0 0 0-2.5 2.5v2.5c0 .7.6 1.3 1.3 1.3h27.4c.7 0 1.3-.6 1.3-1.3v-2.5c0-1.3-1.1-2.5-2.5-2.5h-1.3V23.1h1.3c1.4 0 2.5-1 2.5-2.5v-.8c0-1-.6-2-1.5-2.3l-13-5.4h-1l-13 5.4Zm22.3 5.6h-1.3v12.5h1.3V23.1Zm-3.8 0v12.5h-3.8V23.1H33Zm-6.3 0H23v12.5h3.8V23.1Zm-6.2 0v12.5h-1.3V23.1h1.3Zm-5 15v1.3h25v-1.3h-25Z", clipRule: "evenodd" })] }))),
    }); },
    Whatsapp: function () { return ({
        height: 56,
        width: 56,
        viewBox: '0 0 56 56',
        icon: (jsxs("g", __assign$1({ fill: "none", fillRule: "evenodd" }, { children: [jsx("circle", { cx: "28", cy: "28", r: "27", fill: "#F7F7F7" }), jsx("path", { fill: "#10465B", d: "M39.5 16.3a15 15 0 0 0-23.4 18.2L14 42l7.9-2c2.2 1 4.7 1.8 7.1 1.8a15 15 0 0 0 15-15c0-4-1.7-7.7-4.5-10.5ZM29 39.3c-2.3 0-4.5-.5-6.4-1.6l-.4-.2-4.6 1.3 1.3-4.5-.4-.5a12 12 0 0 1-1.9-6.6 12.5 12.5 0 0 1 21.2-8.8 12.3 12.3 0 0 1-8.8 21Zm6.8-9.3c-.4-.2-2.3-1.1-2.7-1.1-.4-.2-.5-.2-.7.2-.2.3-1 1-1.1 1.5l-.8.1c-.4-.1-1.5-.5-3-1.8a9.1 9.1 0 0 1-2-2.6c-.3-.4 0-.6.1-.8l.6-.6.4-.5v-.6L25.4 21c-.1-.6-.5-.6-.7-.6h-.8c-.1 0-.5 0-.9.4s-1.3 1.3-1.3 3.2c0 1.9 1.3 3.6 1.5 4 .2.1 2.6 4 6.4 5.6 3.1 1.3 3.7.9 4.5.9.7 0 2.2-1 2.4-1.7.4-1 .4-1.7.2-1.7-.2-1-.6-1-1-1.1Z", clipRule: "evenodd" })] }))),
    }); },
    Question: function () { return ({
        height: 56,
        width: 56,
        viewBox: '0 0 56 56',
        icon: (jsxs("g", __assign$1({ fill: "none", fillRule: "evenodd" }, { children: [jsx("circle", { cx: "28", cy: "28", r: "27", fill: "#F7F7F7" }), jsx("path", { fill: "#10465B", d: "M40.5 27c0 6.9-5.6 12.5-12.5 12.5-1.1 0-3.7-.4-5.8-1.3a5 5 0 0 0-3-.4c-1.3.3-2 .7-2.7 1l-.7.4.3-1.6.5-2a5 5 0 0 0 0-2.2c-.6-2.4-1.1-5.1-1.1-6.4a12.5 12.5 0 1 1 25 0Zm2.5 0a15 15 0 0 1-21.8 13.5c-.5-.2-1-.3-1.5-.2-1 .2-1.5.5-2 .7-.6.4-1.1.7-2.2.8a2 2 0 0 1-2.3-2.3l.5-2.6.4-1.8V34c-.6-2.5-1.1-5.4-1.1-7a15 15 0 1 1 30 0Zm-16.9-5.5c-.4.4-.6 1-.6 1.8a1.2 1.2 0 1 1-2.5 0c0-1.3.4-2.5 1.3-3.5.8-1 2.1-1.6 3.7-1.6s2.9.7 3.7 1.6c.9 1 1.3 2.2 1.3 3.4a5 5 0 0 1-1.3 3.2c-.6.8-1.5 1.4-2.4 1.7v2.6a1.2 1.2 0 1 1-2.6 0v-3c0-1.2.9-1.8 1.7-2 .5-.1 1-.4 1.5-1 .4-.4.6-1 .6-1.4 0-.7-.2-1.4-.6-1.8-.4-.4-1-.8-1.9-.8-1 0-1.5.4-1.9.8Zm3.1 13a1.2 1.2 0 1 1-2.4 0 1.2 1.2 0 0 1 2.4 0Z", clipRule: "evenodd" })] }))),
    }); },
    Discount: function () { return ({
        height: 56,
        width: 56,
        viewBox: '0 0 56 56',
        icon: (jsxs("g", __assign$1({ fill: "none", fillRule: "evenodd" }, { children: [jsx("circle", { cx: "28", cy: "28", r: "27", fill: "#F7F7F7" }), jsx("path", { fill: "#10465B", d: "m31.8 15.5.6.3h.2l2.6-.4c1.4-.2 2.7.6 3 2v.1l.5 2.5c0 .3.2.5.4.6h.1l2.3 1.3c1.3.6 1.8 2 1.3 3.2v.2l-1.2 2.3c-.1.2-.2.4-.1.6v.2l1.2 2.3c.6 1.2.2 2.6-1 3.3l-.2.1-2.3 1.2c-.2.1-.4.3-.4.5v.2l-.5 2.5a2.7 2.7 0 0 1-2.9 2.1h-.2l-2.6-.4a1 1 0 0 0-.7.2h-.1l-2 1.9a2.8 2.8 0 0 1-3.5.1l-.2-.1-1.9-1.8a1 1 0 0 0-.6-.3h-.2l-2.6.4a2.7 2.7 0 0 1-3-2v-.1l-.5-2.5c0-.3-.2-.5-.4-.6h-.1L14.5 34c-1.3-.6-1.8-2-1.3-3.2v-.2l1.2-2.3c.1-.2.2-.4.1-.6v-.2l-1.2-2.3a2.5 2.5 0 0 1 1-3.3l.2-.1 2.3-1.2c.2-.1.4-.3.4-.5V20l.5-2.5a2.7 2.7 0 0 1 2.9-2.1h.2l2.6.4c.3 0 .5 0 .7-.2h.1l2-1.9c1-.9 2.4-1 3.5-.1l.2.1 1.9 1.8Zm1 3.2 2.6-.3.4 2.2c.2 1 .7 1.9 1.5 2.4l.1.1.3.2 2.1 1-1 2c-.4.8-.5 1.8-.2 2.8v.1l.2.4 1 2-2 1c-.8.5-1.5 1.3-1.9 2.2v.1l-.1.4-.4 2.3-2.4-.3a4 4 0 0 0-2.7.6l-.4.3-1.9 1.7-1.8-1.6a4 4 0 0 0-2.5-1h-.5l-2.6.3-.4-2.2c-.2-1-.7-1.9-1.5-2.4l-.1-.1-.3-.2-2.1-1 1-2c.4-.8.5-1.8.2-2.8v-.1l-.2-.4-1-2 2-1c.8-.5 1.5-1.3 1.9-2.2V21l.1-.4.4-2.3 2.4.3a4 4 0 0 0 2.7-.6l.4-.3 1.9-1.7 1.8 1.6a4 4 0 0 0 2.5 1h.5Zm-6.3 5.6a2.3 2.3 0 1 1-4.5 0 2.3 2.3 0 0 1 4.5 0Zm7.5 7.4a2.3 2.3 0 1 1-4.5 0 2.3 2.3 0 0 1 4.5 0Zm-.4-9.3c.5.6.5 1.6 0 2.2l-9 9a1.5 1.5 0 0 1-2.2-2.2l9-9c.6-.5 1.6-.5 2.2 0Z", clipRule: "evenodd" })] }))),
    }); },
    Consumer: function () { return ({
        height: 56,
        width: 56,
        viewBox: '0 0 56 56',
        icon: (jsxs("g", __assign$1({ fill: "none", fillRule: "evenodd" }, { children: [jsx("circle", { cx: "28", cy: "28", r: "27", fill: "#F7F7F7" }), jsx("path", { fill: "#10465B", d: "M28 40.5c3.3 0 6.4-1.3 8.6-3.5a11.3 11.3 0 0 0-17.2 0c2.2 2.2 5.3 3.5 8.6 3.5Zm9.7-6 .6.6a12.5 12.5 0 1 0-20.6 0 13.8 13.8 0 0 1 20-.6ZM43 28a15.1 15.1 0 0 1-5 11.2A15 15 0 1 1 43 28Zm-11.9-4.4a3.1 3.1 0 1 1-6.2 0 3.1 3.1 0 0 1 6.2 0Zm2.5 0a5.6 5.6 0 1 1-11.2 0 5.6 5.6 0 0 1 11.2 0Z", clipRule: "evenodd" })] }))),
    }); },
};

var SelfService = {
    AlertInfo: function () { return ({
        height: 26,
        width: 26,
        viewBox: '0 0 26 26',
        icon: (jsx("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M25 13C25 6.37258 19.6274 1 13 1C6.37258 1 1 6.37258 1 13C1 14.8216 1.4059 16.5485 2.1322 18.095C2.29292 18.4372 2.20683 18.9828 2.03147 19.6224L1.90307 20.0616L1.60839 20.9949L1.46286 21.4747C1.11351 22.676 0.905114 23.8693 1.56696 24.5493C2.20876 25.2087 3.25348 24.9627 4.31052 24.5542L4.73307 24.3839L5.75873 23.9516C6.47994 23.6595 7.10976 23.4663 7.49413 23.6651C9.14316 24.5182 11.0153 25 13 25C19.6274 25 25 19.6274 25 13ZM3 13C3 7.47715 7.47715 3 13 3C18.5228 3 23 7.47715 23 13C23 18.5228 18.5228 23 13 23C11.3793 23 9.81814 22.6156 8.41305 21.8888C7.59036 21.4632 6.79043 21.5093 5.8608 21.7902L5.69386 21.8416L5.46684 21.9179L5.21667 22.0127L4.21902 22.4302L3.67531 22.6552L3.50309 22.7216C3.3928 22.763 3.29149 22.7987 3.19898 22.8289L3.185 22.832L3.20644 22.726C3.2498 22.517 3.31328 22.2707 3.39815 21.9824L3.47941 21.7134L3.80532 20.6794L3.96542 20.1424L4.04675 19.82L4.13465 19.4031C4.2824 18.6042 4.26387 17.9291 3.9425 17.2448C3.32457 15.9291 3 14.4895 3 13ZM14 8C14 8.55228 13.5523 9 13 9C12.4477 9 12 8.55228 12 8C12 7.44772 12.4477 7 13 7C13.5523 7 14 7.44772 14 8ZM12 11C12 10.4477 12.4477 10 13 10C13.5523 10 14 10.4477 14 11V18C14 18.5523 13.5523 19 13 19C12.4477 19 12 18.5523 12 18V11Z", fill: "#C23644" })),
    }); },
    Edit: function () { return ({
        height: 26,
        width: 26,
        viewBox: '0 0 26 26',
        icon: (jsx("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M13.8078 15.0206L20.1856 8.6429L17.3571 5.81448L10.9794 12.1922L10.6258 15.3742L13.8078 15.0206ZM22.4143 6.41421L21.5998 7.2287L18.7714 4.40027L19.5858 3.58579C20.3669 2.80474 21.6332 2.80474 22.4143 3.58579C23.1953 4.36684 23.1953 5.63317 22.4143 6.41421ZM23.8285 2.17157C22.2664 0.609476 19.7337 0.609475 18.1716 2.17157L9.42852 10.9146C9.19583 11.1473 9.04927 11.4522 9.01293 11.7793L8.5 16.3957C8.42923 17.0326 8.9674 17.5707 9.60431 17.5L14.2207 16.987C14.5477 16.9507 14.8527 16.8041 15.0854 16.5715L23.8285 7.82843C25.3906 6.26633 25.3906 3.73367 23.8285 2.17157ZM11 3C11.5523 3 12 3.44772 12 4C12 4.55228 11.5523 5 11 5H3V23H21V15C21 14.4477 21.4477 14 22 14C22.5523 14 23 14.4477 23 15V23C23 24.1046 22.1046 25 21 25H3C1.89543 25 1 24.1046 1 23V5C1 3.89543 1.89543 3 3 3H11Z", fill: "#0E5F80" })),
    }); },
    Holiday: function () { return ({
        height: 40,
        width: 40,
        viewBox: '0 0 40 40',
        icon: (jsx("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M5.24254 2.52985C4.70675 2.3959 4.16381 2.72166 4.02986 3.25746C3.89591 3.79325 4.22167 4.33619 4.75747 4.47013C4.94961 4.51817 5.22135 4.66114 5.56058 4.94798C5.89096 5.22733 6.23232 5.59783 6.56494 6.01941C6.60015 6.06403 6.63508 6.109 6.6697 6.15423C6.35716 6.06079 6.04077 5.97431 5.72724 5.90115C4.4219 5.59657 2.87805 5.44293 1.55279 6.10557C1.05881 6.35256 0.858589 6.95323 1.10558 7.44721C1.35257 7.94119 1.95324 8.14141 2.44722 7.89442C3.08801 7.57402 3.98258 7.56965 5.09423 7.80878L5.03425 7.83491C3.60584 8.46438 2.23607 9.57131 1.13176 11.5039C0.857752 11.9834 1.02435 12.5942 1.50387 12.8682C1.98339 13.1423 2.59424 12.9757 2.86825 12.4961C3.76394 10.9287 4.81084 10.1189 5.84076 9.66508C5.95642 9.61412 6.07254 9.56731 6.18905 9.52424C5.87494 9.90564 5.55478 10.3168 5.24032 10.7506C4.09793 12.3268 2.96675 14.2899 2.52382 16.2831C2.40401 16.8222 2.74394 17.3564 3.28307 17.4762C3.82221 17.596 4.35639 17.2561 4.47619 16.7169C4.83326 15.1101 5.78541 13.4065 6.85969 11.9244C7.52685 11.0039 8.21634 10.2025 8.78758 9.60137C11.252 13.9591 14.9204 22.7579 14.0663 31.8155C10.0446 31.5096 6.76129 31.7055 4.84202 32.0126C4.29667 32.0998 3.92531 32.6126 4.01257 33.158C4.09982 33.7033 4.61265 34.0747 5.158 33.9874C9.17035 33.3455 20.0021 33.2064 30.5939 37.9138C31.0986 38.1381 31.6895 37.9108 31.9138 37.4061C32.1381 36.9014 31.9108 36.3105 31.4061 36.0862C26.8857 34.0771 22.327 32.9145 18.2246 32.2899C19.3084 28.6313 21.5732 25.0805 24.0827 22.0503C26.3699 19.2885 28.8153 17.013 30.6379 15.5445C31.0055 16.3861 31.4028 17.4743 31.705 18.6805C32.1607 20.4993 32.3793 22.5043 32.0194 24.3039C31.9111 24.8454 32.2623 25.3723 32.8039 25.4806C33.3455 25.5889 33.8723 25.2377 33.9806 24.6961C34.4207 22.4957 34.1393 20.1674 33.645 18.1945C33.5236 17.71 33.3882 17.242 33.2448 16.797C34.143 17.7111 34.8468 18.8367 35.0077 20.124C35.0762 20.672 35.576 21.0608 36.124 20.9923C36.6721 20.9238 37.0608 20.424 36.9923 19.876C36.7655 18.0618 35.8128 16.5843 34.7593 15.4858C35.8583 15.7749 36.7722 16.1864 37.2929 16.7071C37.6834 17.0976 38.3166 17.0976 38.7071 16.7071C39.0976 16.3166 39.0976 15.6834 38.7071 15.2929C37.6978 14.2835 36.1278 13.7337 34.7432 13.4241C34.4913 13.3678 34.2393 13.3181 33.9902 13.2745C34.7369 12.9922 35.5429 12.8543 36.3356 12.9864C36.8804 13.0772 37.3956 12.7092 37.4864 12.1644C37.5772 11.6196 37.2092 11.1044 36.6644 11.0136C35.2047 10.7703 33.8302 11.1305 32.7452 11.6287C32.2763 11.8441 31.847 12.0917 31.4706 12.3435C31.2994 11.9307 31.0944 11.4869 30.8576 11.0423C30.3223 10.0376 29.5722 8.92911 28.6 8.19999C28.1582 7.86862 27.5314 7.95816 27.2 8.39999C26.8686 8.84182 26.9582 9.46862 27.4 9.79999C27.9819 10.2364 28.5254 10.9625 28.985 11.7858C27.0851 11.0257 24.5255 10.4751 21.8039 11.0194C21.2623 11.1277 20.9111 11.6545 21.0194 12.1961C21.1277 12.7377 21.6546 13.0889 22.1961 12.9806C23.8426 12.6513 25.4378 12.811 26.8009 13.1673C26.4136 13.2292 26.0228 13.3073 25.6325 13.4049C23.6465 13.9013 21.6148 14.9188 20.1863 16.9188C19.8653 17.3682 19.9694 17.9927 20.4188 18.3137C20.8682 18.6347 21.4927 18.5306 21.8137 18.0812C22.8852 16.5812 24.4369 15.7653 26.1175 15.3451C26.7959 15.1755 27.4854 15.0732 28.1566 15.0166C26.4453 16.5086 24.4407 18.4823 22.5423 20.7747C19.8844 23.9842 17.3777 27.8864 16.2206 32.0218L16.0575 32.0032C16.9068 22.997 13.5761 14.3446 11.0293 9.53324C11.641 9.73785 12.2852 9.99693 12.9069 10.3152C14.3969 11.0779 15.5922 12.0939 16.0637 13.3511C16.2576 13.8682 16.834 14.1302 17.3511 13.9363C17.8682 13.7424 18.1303 13.166 17.9363 12.6489C17.2078 10.706 15.4865 9.3888 13.8182 8.53484C13.2839 8.26137 12.7381 8.02656 12.2043 7.8276C12.5473 7.76455 12.9042 7.72332 13.2685 7.71201C14.7654 7.66557 16.379 8.11909 17.7526 9.66436C18.1195 10.0771 18.7516 10.1143 19.1644 9.7474C19.5772 9.38048 19.6143 8.74841 19.2474 8.33563C17.421 6.28089 15.2013 5.65108 13.2065 5.71298C12.5839 5.7323 11.9824 5.8185 11.4181 5.94841C11.4498 5.90948 11.4818 5.87088 11.514 5.83268C12.2763 4.92998 12.9754 4.49999 13.5 4.49999C14.0523 4.49999 14.5 4.05228 14.5 3.49999C14.5 2.94771 14.0523 2.49999 13.5 2.49999C12.0246 2.49999 10.807 3.57 9.98598 4.54232C9.60624 4.992 9.26398 5.47722 8.96883 5.94899C8.71715 5.56161 8.43629 5.16237 8.13507 4.78059C7.75102 4.29382 7.31739 3.81432 6.85193 3.42076C6.39533 3.03467 5.8504 2.68182 5.24254 2.52985ZM24.8084 29.6421C23.9239 30.2374 23.2709 31.1943 22.8944 31.9472C22.6474 32.4412 22.0468 32.6414 21.5528 32.3944C21.0588 32.1474 20.8586 31.5467 21.1056 31.0528C21.5625 30.139 22.4095 28.8459 23.6916 27.9829C25.0313 27.0812 26.8048 26.6856 28.8939 27.5808C30.7578 28.3796 31.5295 29.7875 31.7393 31.2113C31.9266 32.4818 31.6667 33.7718 31.504 34.5797L31.4806 34.6961C31.3723 35.2377 30.8455 35.5889 30.3039 35.4806C29.7623 35.3723 29.4111 34.8454 29.5194 34.3039L29.5251 34.2755C29.6979 33.4119 29.896 32.4213 29.7607 31.5029C29.6372 30.6647 29.2422 29.906 28.1061 29.4191C26.6951 28.8145 25.6353 29.0855 24.8084 29.6421Z", fill: "#0E5F80" })),
    }); },
    CalendarQuestion: function () { return ({
        height: 40,
        width: 40,
        viewBox: '0 0 40 40',
        icon: (jsxs(Fragment, { children: [jsx("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M8 1C7.44772 1 7 1.44772 7 2V8C7 8.55228 7.44772 9 8 9C8.55228 9 9 8.55228 9 8V2C9 1.44772 8.55228 1 8 1ZM24 1C23.4477 1 23 1.44772 23 2V8C23 8.55228 23.4477 9 24 9C24.5523 9 25 8.55228 25 8V2C25 1.44772 24.5523 1 24 1ZM6 17C6.55228 17 7 16.5523 7 16C7 15.4477 6.55228 15 6 15C5.44772 15 5 15.4477 5 16C5 16.5523 5.44772 17 6 17ZM11 16C11 16.5523 10.5523 17 10 17C9.44772 17 9 16.5523 9 16C9 15.4477 9.44772 15 10 15C10.5523 15 11 15.4477 11 16ZM15 16C15 16.5523 14.5523 17 14 17C13.4477 17 13 16.5523 13 16C13 15.4477 13.4477 15 14 15C14.5523 15 15 15.4477 15 16ZM15 20C15 20.5523 14.5523 21 14 21C13.4477 21 13 20.5523 13 20C13 19.4477 13.4477 19 14 19C14.5523 19 15 19.4477 15 20ZM14 25C14.5523 25 15 24.5523 15 24C15 23.4477 14.5523 23 14 23C13.4477 23 13 23.4477 13 24C13 24.5523 13.4477 25 14 25ZM10 21C10.5523 21 11 20.5523 11 20C11 19.4477 10.5523 19 10 19C9.44772 19 9 19.4477 9 20C9 20.5523 9.44772 21 10 21ZM11 24C11 24.5523 10.5523 25 10 25C9.44772 25 9 24.5523 9 24C9 23.4477 9.44772 23 10 23C10.5523 23 11 23.4477 11 24ZM7 20C7 20.5523 6.55228 21 6 21C5.44772 21 5 20.5523 5 20C5 19.4477 5.44772 19 6 19C6.55228 19 7 19.4477 7 20ZM6 25C6.55228 25 7 24.5523 7 24C7 23.4477 6.55228 23 6 23C5.44772 23 5 23.4477 5 24C5 24.5523 5.44772 25 6 25ZM27 16C27 16.5523 26.5523 17 26 17C25.4477 17 25 16.5523 25 16C25 15.4477 25.4477 15 26 15C26.5523 15 27 15.4477 27 16ZM26 21C26.5523 21 27 20.5523 27 20C27 19.4477 26.5523 19 26 19C25.4477 19 25 19.4477 25 20C25 20.5523 25.4477 21 26 21ZM23 16C23 16.5523 22.5523 17 22 17C21.4477 17 21 16.5523 21 16C21 15.4477 21.4477 15 22 15C22.5523 15 23 15.4477 23 16ZM22 21C22.5523 21 23 20.5523 23 20C23 19.4477 22.5523 19 22 19C21.4477 19 21 19.4477 21 20C21 20.5523 21.4477 21 22 21ZM19 16C19 16.5523 18.5523 17 18 17C17.4477 17 17 16.5523 17 16C17 15.4477 17.4477 15 18 15C18.5523 15 19 15.4477 19 16ZM18 21C18.5523 21 19 20.5523 19 20C19 19.4477 18.5523 19 18 19C17.4477 19 17 19.4477 17 20C17 20.5523 17.4477 21 18 21ZM30.5 37C34.0899 37 37 34.0899 37 30.5C37 26.9101 34.0899 24 30.5 24C26.9101 24 24 26.9101 24 30.5C24 34.0899 26.9101 37 30.5 37ZM30.5 39C35.1944 39 39 35.1944 39 30.5C39 25.8056 35.1944 22 30.5 22C25.8056 22 22 25.8056 22 30.5C22 35.1944 25.8056 39 30.5 39ZM29.7361 32.2265C29.6746 32.2265 29.6207 32.2077 29.5746 32.1701C29.5284 32.1251 29.5054 32.0687 29.5054 32.0011V31.8546V31.6969C29.5438 31.4189 29.6284 31.1711 29.7592 30.9532C29.8976 30.7279 30.0553 30.5213 30.2322 30.3335C30.4168 30.1457 30.5975 29.9729 30.7744 29.8152C30.9513 29.6499 31.0974 29.4847 31.2128 29.3194C31.3359 29.1542 31.4051 28.9889 31.4205 28.8236C31.4435 28.6358 31.4051 28.4743 31.3051 28.3391C31.2128 28.2039 31.0821 28.1025 30.9128 28.0349C30.7513 27.9673 30.5783 27.9335 30.3937 27.9335C30.063 27.9335 29.7938 28.0124 29.5861 28.1701C29.3785 28.3279 29.2362 28.572 29.1593 28.9025C29.1362 28.9851 29.0977 29.0527 29.0439 29.1053C28.9901 29.1504 28.9131 29.1729 28.8132 29.1729H27.6133C27.5441 29.1729 27.4826 29.1466 27.4287 29.0941C27.3749 29.034 27.348 28.9626 27.348 28.88C27.3557 28.5795 27.4287 28.2866 27.5672 28.0011C27.7056 27.7157 27.9094 27.4603 28.1786 27.2349C28.4478 27.002 28.7747 26.818 29.1593 26.6828C29.5438 26.5476 29.9899 26.48 30.4975 26.48C31.0513 26.48 31.5128 26.5476 31.8819 26.6828C32.2511 26.818 32.5434 26.9945 32.7587 27.2124C32.9741 27.4227 33.1279 27.6518 33.2202 27.8997C33.3125 28.1401 33.3548 28.3692 33.3471 28.587C33.3317 28.865 33.2702 29.1166 33.1625 29.342C33.0548 29.5598 32.9202 29.7626 32.7587 29.9504C32.5972 30.1382 32.4319 30.3222 32.2626 30.5025C32.0934 30.6753 31.9396 30.8518 31.8012 31.0321C31.6627 31.2049 31.5666 31.3927 31.5128 31.5955L31.4666 31.7758C31.4589 31.8283 31.4512 31.8847 31.4435 31.9448C31.4051 32.0349 31.3589 32.1063 31.3051 32.1589C31.259 32.2039 31.1936 32.2265 31.109 32.2265H29.7361ZM29.7361 34.48C29.6515 34.48 29.5823 34.4537 29.5284 34.4011C29.4746 34.3485 29.4477 34.2809 29.4477 34.1983V33.0715C29.4477 32.9964 29.4746 32.9326 29.5284 32.88C29.5823 32.8199 29.6515 32.7898 29.7361 32.7898H31.0282C31.1128 32.7898 31.182 32.8199 31.2359 32.88C31.2897 32.9326 31.3166 32.9964 31.3166 33.0715V34.1983C31.3166 34.2809 31.2897 34.3485 31.2359 34.4011C31.182 34.4537 31.1128 34.48 31.0282 34.48H29.7361Z", fill: "#0E5F80" }), jsx("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M29 6H3V11H29V6ZM29 13V22.1319C29.4869 22.0452 29.9882 22 30.5 22C30.6678 22 30.8346 22.0049 31 22.0145V6C31 4.89543 30.1046 4 29 4H3C1.89543 4 1 4.89543 1 6V28C1 29.1046 1.89543 30 3 30H22.0145C22.0546 29.3079 22.1776 28.6379 22.3736 28H3L3 13H29Z", fill: "#0E5F80" })] })),
    }); },
    Notification: function () { return ({
        height: 40,
        width: 40,
        viewBox: '0 0 40 40',
        icon: (jsx("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M10.0093 4.66369C10.1951 5.1838 9.92404 5.75602 9.40393 5.94177C6.97152 6.81049 5.56008 8.72908 4.78422 10.8992C4.00256 13.0855 3.90789 15.4304 4.0621 16.8953C4.11991 17.4446 3.72153 17.9367 3.17228 17.9945C2.62303 18.0524 2.1309 17.654 2.07309 17.1047C1.89396 15.403 1.99929 12.7479 2.90096 10.2259C3.80844 7.68765 5.56366 5.18957 8.73125 4.05829C9.25136 3.87253 9.82358 4.14358 10.0093 4.66369ZM11.9285 8.62864C12.1336 9.14142 11.8842 9.72339 11.3714 9.92851C10.4327 10.304 9.50116 11.2772 8.83444 12.6297C8.17554 13.9663 7.85053 15.5276 7.99451 16.8953C8.05233 17.4446 7.65394 17.9367 7.10469 17.9945C6.55544 18.0523 6.06332 17.654 6.0055 17.1047C5.81614 15.3058 6.24113 13.3671 7.04056 11.7454C7.83216 10.1395 9.0673 8.69607 10.6286 8.07155C11.1414 7.86644 11.7234 8.11586 11.9285 8.62864ZM30.5961 5.94177C30.076 5.75602 29.8049 5.1838 29.9907 4.66369C30.1764 4.14358 30.7486 3.87253 31.2688 4.05829C34.4363 5.18957 36.1916 7.68765 37.099 10.2259C38.0007 12.7479 38.106 15.403 37.9269 17.1047C37.8691 17.654 37.377 18.0524 36.8277 17.9945C36.2785 17.9367 35.8801 17.4446 35.9379 16.8953C36.0921 15.4304 35.9974 13.0855 35.2158 10.8992C34.4399 8.72908 33.0285 6.81049 30.5961 5.94177ZM28.0039 8.62864C27.7988 9.14142 28.0482 9.72339 28.561 9.92851C29.4997 10.304 30.4312 11.2772 31.098 12.6297C31.7569 13.9663 32.0819 15.5276 31.9379 16.8953C31.8801 17.4446 32.2785 17.9367 32.8277 17.9945C33.377 18.0523 33.8691 17.654 33.9269 17.1047C34.1163 15.3058 33.6913 13.3671 32.8918 11.7454C32.1002 10.1395 30.8651 8.69607 29.3038 8.07155C28.791 7.86644 28.209 8.11586 28.0039 8.62864ZM18 8.00003C18 6.89546 18.8954 6.00003 20 6.00003C21.1046 6.00003 22 6.89546 22 8.00003V10.2281C23.2429 10.5265 24.3027 11.1055 25.1901 11.8925C26.5009 13.0549 27.3875 14.6266 27.9796 16.2896C29.0631 19.3332 29.2312 22.883 29.0505 25.4462C29.3285 25.6201 29.6276 25.8308 29.902 26.0668C30.1493 26.2795 30.4062 26.5378 30.6079 26.8342C30.8053 27.1242 31 27.5247 31 28C31 28.3097 30.874 28.5455 30.7699 28.688C30.666 28.8301 30.5457 28.9335 30.4507 29.0045C30.2612 29.146 30.0349 29.259 29.8196 29.3507C29.3777 29.5389 28.7822 29.7202 28.0938 29.8862C26.7059 30.221 24.8082 30.525 22.7005 30.6847C18.5295 31.0007 13.303 30.7696 9.55279 28.8945C9.26528 28.7507 9.0644 28.4775 9.01291 28.1602C8.96142 27.8429 9.0656 27.5202 9.29289 27.2929L10.9626 25.6232C10.7615 23.0482 10.9118 19.4038 12.0204 16.2896C12.6125 14.6266 13.4991 13.0549 14.81 11.8925C15.6974 11.1055 16.7572 10.5265 18 10.2281V8.00003ZM12.9951 25.9005C12.7538 23.4878 12.8585 19.8988 13.9046 16.9604C14.425 15.4985 15.1572 14.2576 16.1369 13.3889C17.0971 12.5374 18.3415 12 20 12C21.6585 12 22.903 12.5374 23.8631 13.3889C24.8428 14.2576 25.575 15.4985 26.0954 16.9604C27.1415 19.8988 27.2462 23.4878 27.005 25.9005C26.9637 26.3134 27.1817 26.7089 27.5528 26.8945C27.8325 27.0343 28.2586 27.2914 28.598 27.5832C28.6234 27.6051 28.6476 27.6265 28.6707 27.6476C28.3963 27.7402 28.0464 27.8403 27.6249 27.942C26.3566 28.2479 24.5668 28.5376 22.5495 28.6904C18.9883 28.9602 14.8976 28.7894 11.7538 27.6604L12.7071 26.7071C12.9192 26.4951 13.0249 26.199 12.9951 25.9005ZM15 36C16.1046 36 17 35.1046 17 34C17 32.8955 16.1046 32 15 32C13.8954 32 13 32.8955 13 34C13 35.1046 13.8954 36 15 36Z", fill: "#0E5F80" })),
    }); },
    Clock: function () { return ({
        height: 80,
        width: 80,
        viewBox: '0 0 80 80',
        icon: (jsxs("g", { children: [jsx("circle", { fill: "none", cx: "40", cy: "40", r: "35", stroke: "#0E5F80", strokeWidth: "6" }), jsx("path", { fill: "none", d: "M42 18.5V40L28.5 52", stroke: "#0E5F80", strokeWidth: "6", strokeLinecap: "round", strokeLinejoin: "round" })] })),
    }); },
    Stopwatch: function () { return ({
        height: 80,
        width: 80,
        viewBox: '0 0 80 80',
        icon: (jsxs("g", __assign$1({ fill: "none" }, { children: [jsx("circle", { cx: "40", cy: "40", r: "34", stroke: "#0E5F80", strokeWidth: "4" }), jsx("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M34 22C34 20.8954 34.8954 20 36 20H40H44C45.1046 20 46 20.8954 46 22C46 23.1046 45.1046 24 44 24H42V26.1238C49.8926 27.108 56 33.8408 56 42C56 50.8366 48.8366 58 40 58C31.1634 58 24 50.8366 24 42C24 33.8408 30.1074 27.108 38 26.1238V24H36C34.8954 24 34 23.1046 34 22ZM28 42C28 35.3726 33.3726 30 40 30C46.6274 30 52 35.3726 52 42C52 48.6274 46.6274 54 40 54C33.3726 54 28 48.6274 28 42ZM42 34C42 32.8954 41.1046 32 40 32C38.8954 32 38 32.8954 38 34V40.8676L33.971 43.285C33.0238 43.8533 32.7167 45.0818 33.285 46.029C33.8533 46.9762 35.0818 47.2833 36.029 46.715L41.029 43.715C41.6314 43.3535 42 42.7025 42 42V34Z", fill: "#0E5F80" }), jsx("path", { d: "M18 25H27", stroke: "#0E5F80", strokeWidth: "4", strokeLinecap: "round" }), jsx("path", { d: "M18 30H24", stroke: "#0E5F80", strokeWidth: "4", strokeLinecap: "round" }), jsx("path", { d: "M18 35H21", stroke: "#0E5F80", strokeWidth: "4", strokeLinecap: "round" })] }))),
    }); },
    ThumbsUp: function () { return ({
        height: 80,
        width: 80,
        viewBox: '0 0 80 80',
        icon: (jsxs(Fragment, { children: [jsx("circle", { cx: "40", cy: "40", r: "34", stroke: "#0E5F80", strokeWidth: "4", fill: "none" }), jsx("path", { d: "M34 37C37 33.3334 42 24.4 42 20C42 20 45.9389 19.091 45.5614 26C45.454 27.9663 44 29 44 34C48.3333 33.6667 57 33.5 57 35.5C57 36 57.5 37.5 56 38.5C58.1667 39.1667 59 42 56 44C57.5 45 58.5 48 55.5 49.5C57 52 57 56 34 54", stroke: "#0E5F80", strokeWidth: "4", strokeLinecap: "round", strokeLinejoin: "round", fill: "none" }), jsx("path", { d: "M21 44C21 40.6863 23.6863 38 27 38H30V54H27C23.6863 54 21 51.3137 21 48V44Z", stroke: "#0E5F80", strokeWidth: "4", fill: "none" })] })),
    }); },
    Star: function () { return ({
        height: 80,
        width: 80,
        viewBox: '0 0 80 80',
        icon: (jsxs(Fragment, { children: [jsx("circle", { cx: "40", cy: "40", r: "34", stroke: "#0E5F80", strokeWidth: "4", fill: "none" }), jsx("path", { d: "M39.1033 21.3361C39.4701 20.5928 40.5299 20.5928 40.8967 21.3361L44.5619 28.7626C45.2902 30.2383 46.6981 31.2612 48.3266 31.4978L56.5223 32.6887C57.3425 32.8079 57.67 33.8159 57.0765 34.3944L51.1461 40.1751C49.9676 41.3238 49.4299 42.9788 49.7081 44.6008L51.1081 52.7633C51.2482 53.5802 50.3907 54.2032 49.6571 53.8175L42.3267 49.9637C40.8701 49.1979 39.1299 49.1979 37.6733 49.9637L30.3429 53.8175C29.6093 54.2032 28.7518 53.5802 28.8919 52.7633L30.2919 44.6008C30.5701 42.9788 30.0324 41.3238 28.8539 40.1751L22.9235 34.3944C22.33 33.8159 22.6575 32.8079 23.4777 32.6887L31.6734 31.4978C33.3019 31.2612 34.7098 30.2383 35.4381 28.7626L39.1033 21.3361Z", stroke: "#0E5F80", strokeWidth: "4", fill: "none" }), jsx("path", { d: "M25 57L22 60", stroke: "#0E5F80", strokeWidth: "4", strokeLinecap: "round", strokeLinejoin: "round" }), jsx("path", { d: "M23.6213 52.1213H20.3787", stroke: "#0E5F80", strokeWidth: "4", strokeLinecap: "round", strokeLinejoin: "round" }), jsx("path", { d: "M30 59.5L30 62.7427", stroke: "#0E5F80", strokeWidth: "4", strokeLinecap: "round", strokeLinejoin: "round" })] })),
    }); },
    Alert: function () { return ({
        height: 80,
        width: 80,
        viewBox: '0 0 80 80',
        icon: (jsxs(Fragment, { children: [jsx("circle", { cx: "40", cy: "40", r: "34", stroke: "#0E5F80", strokeWidth: "4", fill: "none" }), jsx("path", { d: "M40.3463 24.0976L58.9959 53.7172C59.4151 54.3831 58.9366 55.25 58.1496 55.25H20.8504C20.0635 55.25 19.5849 54.3831 20.0042 53.7172L38.6538 24.0976C39.046 23.4747 39.9541 23.4747 40.3463 24.0976Z", stroke: "#0E5F80", strokeWidth: "4", fill: "none" }), jsx("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M39.5 33C38.3954 33 37.5 33.8954 37.5 35V43C37.5 44.1046 38.3954 45 39.5 45C40.6046 45 41.5 44.1046 41.5 43V35C41.5 33.8954 40.6046 33 39.5 33ZM39.5 51C40.6046 51 41.5 50.1046 41.5 49C41.5 47.8954 40.6046 47 39.5 47C38.3954 47 37.5 47.8954 37.5 49C37.5 50.1046 38.3954 51 39.5 51Z", fill: "#0E5F80" }), jsx("path", { d: "M50.6325 25.1026C49.5846 24.7533 48.4519 25.3197 48.1026 26.3675C47.7533 27.4154 48.3197 28.5481 49.3675 28.8974L50.6325 25.1026ZM55.1793 34.8276C55.6363 35.8332 56.822 36.2778 57.8276 35.8207C58.8332 35.3637 59.2778 34.178 58.8207 33.1724L55.1793 34.8276ZM49.3675 28.8974C50.3931 29.2392 53.4316 30.9828 55.1793 34.8276L58.8207 33.1724C56.5684 28.2172 52.6069 25.7608 50.6325 25.1026L49.3675 28.8974Z", fill: "#0E5F80" }), jsx("path", { d: "M52.6325 20.1026C51.5846 19.7533 50.4519 20.3197 50.1026 21.3675C49.7533 22.4154 50.3197 23.5481 51.3675 23.8974L52.6325 20.1026ZM60.091 32.5965C60.4205 33.6508 61.5423 34.2384 62.5965 33.909C63.6508 33.5795 64.2384 32.4577 63.909 31.4035L60.091 32.5965ZM51.3675 23.8974C52.1865 24.1704 53.7506 24.5845 55.4577 25.8115C57.1273 27.0116 58.9781 29.0352 60.091 32.5965L63.909 31.4035C62.5219 26.9648 60.1227 24.2384 57.7923 22.5635C55.4994 20.9155 53.3135 20.3296 52.6325 20.1026L51.3675 23.8974Z", fill: "#0E5F80" }), jsx("path", { d: "M28.3675 25.1026C29.4154 24.7533 30.5481 25.3197 30.8974 26.3675C31.2467 27.4154 30.6803 28.5481 29.6325 28.8974L28.3675 25.1026ZM23.8207 34.8276C23.3637 35.8332 22.178 36.2778 21.1724 35.8207C20.1668 35.3637 19.7222 34.178 20.1793 33.1724L23.8207 34.8276ZM29.6325 28.8974C28.6069 29.2392 25.5684 30.9828 23.8207 34.8276L20.1793 33.1724C22.4316 28.2172 26.3931 25.7608 28.3675 25.1026L29.6325 28.8974Z", fill: "#0E5F80" }), jsx("path", { d: "M26.3675 20.1026C27.4154 19.7533 28.5481 20.3197 28.8974 21.3675C29.2467 22.4154 28.6803 23.5481 27.6325 23.8974L26.3675 20.1026ZM18.909 32.5965C18.5795 33.6508 17.4577 34.2384 16.4035 33.909C15.3492 33.5795 14.7616 32.4577 15.091 31.4035L18.909 32.5965ZM27.6325 23.8974C26.8135 24.1704 25.2494 24.5845 23.5423 25.8115C21.8727 27.0116 20.0219 29.0352 18.909 32.5965L15.091 31.4035C16.4781 26.9648 18.8773 24.2384 21.2077 22.5635C23.5006 20.9155 25.6865 20.3296 26.3675 20.1026L27.6325 23.8974Z", fill: "#0E5F80" })] })),
    }); },
};

var Socials$2 = {
    SocialFacebook: function () { return ({
        height: 40,
        width: 40,
        viewBox: '0 0 40 40',
        icon: (jsxs("g", __assign$1({ id: "ic/40/Social/Facebook", stroke: "none", strokeWidth: "1", fill: "none", fillRule: "evenodd" }, { children: [jsx("circle", { id: "Oval", fill: "#0E5F80", cx: "20", cy: "20", r: "19" }), jsx("path", { d: "M21.484607,30 L21.484607,19.9988291 L24.1471549,19.9988291 L24.5,16.5523702 L21.484607,16.5523702 L21.4891307,14.8273843 C21.4891307,13.9284948 21.5714989,13.4468492 22.8166349,13.4468492 L24.4811514,13.4468492 L24.4811514,10 L21.8182266,10 C18.6196256,10 17.4938009,11.6719034 17.4938009,14.4835191 L17.4938009,16.5527605 L15.5,16.5527605 L15.5,19.9992194 L17.4938009,19.9992194 L17.4938009,30 L21.484607,30 Z", id: "Combined-Shape-path", fill: "#F7F3F0" })] }))),
    }); },
    SocialInstagram: function () { return ({
        height: 40,
        width: 40,
        viewBox: '0 0 40 40',
        icon: (jsxs("g", __assign$1({ id: "ic/40/Social/Instagram", stroke: "none", strokeWidth: "1", fill: "none", fillRule: "evenodd" }, { children: [jsx("circle", { id: "Oval", fill: "#0E5F80", cx: "20", cy: "20", r: "19" }), jsx("path", { d: "M20.0009375,10 C17.2850759,10 16.944239,10.011875 15.8775613,10.0604167 C14.8129668,10.1091667 14.0862926,10.2777084 13.4504526,10.525 C12.7927374,10.7804167 12.2348149,11.1220833 11.6789758,11.678125 C11.12272,12.2339584 10.7810498,12.791875 10.5247971,13.449375 C10.2768779,14.0854167 10.1081261,14.8122917 10.0602089,15.8764584 C10.0125001,16.943125 10,17.2841667 10,20 C10,22.7158334 10.0120834,23.055625 10.0604173,24.1222917 C10.1093761,25.186875 10.2779196,25.9135417 10.5250055,26.549375 C10.7806331,27.2070833 11.1223034,27.765 11.6783508,28.3208334 C12.2339816,28.8770834 12.7919041,29.2195834 13.4492026,29.475 C14.0854593,29.7222917 14.8123418,29.8908334 15.8767279,29.9395834 C16.9434057,29.988125 17.2840342,30 19.9996875,30 C22.7157574,30 23.0555527,29.988125 24.1222304,29.9395834 C25.1868249,29.8908334 25.9143324,29.7222917 26.5505891,29.475 C27.2080959,29.2195834 27.7651851,28.8770834 28.3208158,28.3208334 C28.8770716,27.765 29.2187419,27.2070833 29.4749945,26.5495834 C29.7208304,25.9135417 29.8895822,25.1866667 29.9395827,24.1225 C29.9874999,23.0558333 30,22.7158334 30,20 C30,17.2841667 29.9874999,16.9433334 29.9395827,15.8766667 C29.8895822,14.8120833 29.7208304,14.0854167 29.4749945,13.4495833 C29.2187419,12.791875 28.8770716,12.2339584 28.3208158,11.678125 C27.7645601,11.121875 27.2083042,10.7802083 26.5499641,10.525 C25.9124574,10.2777084 25.1853665,10.1091667 24.1207721,10.0604167 C23.0540943,10.011875 22.7145074,10 19.9978125,10 L20.0009375,10 Z M19.1038448,11.8020834 C19.3700976,11.8016667 19.6671841,11.8020834 20.0009375,11.8020834 C22.6709653,11.8020834 22.9874269,11.8116667 24.0418129,11.8595833 C25.0168231,11.9041667 25.5459952,12.0670833 25.8984989,12.2039584 C26.3651705,12.3852083 26.6978822,12.601875 27.0476776,12.951875 C27.3976812,13.301875 27.6143501,13.6352083 27.7960187,14.101875 C27.9328951,14.4539584 28.0960218,14.983125 28.1403973,15.958125 C28.1883144,17.0122917 28.1987313,17.3289584 28.1987313,19.9977084 C28.1987313,22.6664583 28.1883144,22.983125 28.1403973,24.0372917 C28.0958135,25.0122917 27.9328951,25.5414584 27.7960187,25.8935417 C27.6147668,26.3602083 27.3976812,26.6925 27.0476776,27.0422917 C26.6976739,27.3922917 26.3653788,27.6089583 25.8984989,27.7902083 C25.5464119,27.9277083 25.0168231,28.0902083 24.0418129,28.1347917 C22.9876353,28.1827084 22.6709653,28.193125 20.0009375,28.193125 C17.3307014,28.193125 17.0142397,28.1827084 15.9600621,28.1347917 C14.9850519,28.0897917 14.4558797,27.926875 14.1031678,27.79 C13.6364962,27.60875 13.3031594,27.3920834 12.9531558,27.0420834 C12.6031521,26.6920833 12.3864832,26.3595834 12.2048146,25.8927083 C12.0679382,25.540625 11.9048115,25.0114583 11.8604361,24.0364584 C11.8125189,22.9822917 11.8029354,22.665625 11.8029354,19.9952083 C11.8029354,17.3247916 11.8125189,17.0097917 11.8604361,15.955625 C11.9050199,14.980625 12.0679382,14.4514583 12.2048146,14.0989584 C12.3860665,13.6322917 12.6031521,13.2989583 12.9531558,12.9489583 C13.3031594,12.5989583 13.6364962,12.3822917 14.1031678,12.200625 C14.4556714,12.063125 14.9850519,11.900625 15.9600621,11.8558334 C16.8825717,11.8141667 17.2400754,11.8016667 19.1038448,11.7995833 L19.1038448,11.8020834 Z M25.3389097,13.4625 C24.6764029,13.4625 24.1388972,13.999375 24.1388972,14.6620833 C24.1388972,15.3245834 24.6764029,15.8620833 25.3389097,15.8620833 C26.0014167,15.8620833 26.5389222,15.3245834 26.5389222,14.6620833 C26.5389222,13.9995833 26.0014167,13.4620833 25.3389097,13.4620833 L25.3389097,13.4625 Z M20.0009375,14.8645833 C17.1648663,14.8645833 14.8654674,17.1639583 14.8654674,20 C14.8654674,22.8360417 17.1648663,25.134375 20.0009375,25.134375 C22.8370087,25.134375 25.1355743,22.8360417 25.1355743,20 C25.1355743,17.1639583 22.8368004,14.8645833 20.0007292,14.8645833 L20.0009375,14.8645833 Z M20.0009375,16.6666667 C21.84179,16.6666667 23.3343056,18.1589583 23.3343056,20 C23.3343056,21.8408334 21.84179,23.3333334 20.0009375,23.3333334 C18.1598767,23.3333334 16.6675694,21.8408334 16.6675694,20 C16.6675694,18.1589583 18.1598767,16.6666667 20.0009375,16.6666667 L20.0009375,16.6666667 Z", id: "Shape", fill: "#F7F3F0" })] }))),
    }); },
    SocialLinkedin: function () { return ({
        height: 40,
        width: 40,
        viewBox: '0 0 40 40',
        icon: (jsxs("g", __assign$1({ id: "ic/40/Social/Linkedin", stroke: "none", strokeWidth: "1", fill: "none", fillRule: "evenodd" }, { children: [jsx("circle", { id: "Oval", fill: "#0E5F80", cx: "20", cy: "20", r: "19" }), jsx("path", { d: "M25.0643135,15.5695324 C27.8852072,15.5695324 30,17.305724 30,21.0361298 L30,21.0361298 L29.9998288,27.9996776 L25.7132279,27.9996776 L25.7132279,21.5021181 C25.7132279,19.8700706 25.0929069,18.756406 23.5410772,18.756406 C22.3567659,18.756406 21.6513498,19.5071785 21.3415318,20.232318 C21.2281859,20.4921946 21.200363,20.8542806 21.200363,21.2172534 L21.200363,21.2172534 L21.200363,28 L16.9130772,28 L16.9130772,28 L16.9139085,27.8333984 C16.9169704,27.2101469 16.9277573,24.9277689 16.9338732,22.5125645 L16.9360005,21.5640921 C16.936408,21.3533192 16.936768,21.1430474 16.9370723,20.9342801 L16.9378099,20.3134955 C16.9379023,20.2111182 16.9379787,20.1093679 16.9380381,20.0083701 L16.938087,19.1230939 C16.9380354,19.02774 16.9379638,18.9335149 16.937871,18.8405441 L16.937054,18.2987737 C16.9345198,17.0744455 16.9274594,16.1451271 16.9130772,15.855042 L16.9130772,15.855042 L21.200363,15.855042 L21.200363,17.5752734 C21.7693187,16.7483274 22.7883195,15.5695324 25.0643135,15.5695324 Z M14.5402597,15.8547195 L14.5402597,27.9996776 L10.2534875,27.9996776 L10.2534875,15.8547195 L14.5402597,15.8547195 Z M12.42521,10 C13.8916878,10 14.7947744,10.9069886 14.8225973,12.097794 C14.8225973,13.2639338 13.8916878,14.197039 12.3969592,14.197039 L12.3969592,14.197039 L12.3695644,14.197039 C10.9303958,14.197039 10,13.2639338 10,12.097794 C10,10.9069886 10.9587323,10 12.42521,10 Z", id: "Combined-Shape", fill: "#FFFFFF", fillRule: "nonzero" })] }))),
    }); },
    SocialTwitter: function () { return ({
        height: 40,
        width: 40,
        viewBox: '0 0 40 40',
        icon: (jsxs("g", __assign$1({ id: "ic/40/Social/Twitter", stroke: "none", strokeWidth: "1", fill: "none", fillRule: "evenodd" }, { children: [jsx("circle", { id: "Oval", fill: "#0E5F80", cx: "20", cy: "20", r: "19" }), jsx("path", { d: "M20.7111412,16.2562505 L20.7544945,16.9482957 L20.0319394,16.8635554 C17.4018386,16.5387179 15.1041133,15.4370951 13.1532143,13.5869336 L12.1994415,12.6689146 L11.9537728,13.3468364 C11.4335331,14.858037 11.7659084,16.4539777 12.8497412,17.5273538 C13.4277853,18.1205353 13.2977254,18.2052755 12.3005993,17.8521913 C11.9537728,17.7392043 11.6502996,17.6544641 11.6213974,17.6968343 C11.5202397,17.7956978 11.8670662,19.0809245 12.1416371,19.5893658 C12.5173658,20.2955343 13.2832743,20.9875794 14.1214383,21.3971571 L14.8295423,21.7219947 L13.9913783,21.736118 C13.1821166,21.736118 13.1532143,21.7502414 13.239921,22.0468321 C13.528943,22.9648512 14.6705802,23.9393637 15.9422772,24.3630648 L16.8382456,24.6596555 L16.0578861,25.1116034 C14.9017978,25.7612784 13.5433942,26.128486 12.1849905,26.1567327 C11.5346908,26.1708561 11,26.2273496 11,26.2697197 C11,26.4109534 12.7630345,27.2018621 13.7890629,27.5125763 C16.8671478,28.4305953 20.5232769,28.0351409 23.2689864,26.4674469 C25.2198854,25.3517007 27.1707843,23.1343316 28.0812037,20.9875794 C28.5725412,19.8435865 29.0638788,17.7533277 29.0638788,16.7505685 C29.0638788,16.1008935 29.1072321,16.0161533 29.9164939,15.2393679 C30.3933802,14.7874201 30.8413644,14.2931022 30.9280711,14.1518684 C31.0725821,13.8835244 31.058131,13.8835244 30.3211247,14.1236217 C29.092781,14.5473228 28.9193678,14.4908293 29.5263141,13.8552777 C29.9742983,13.4033298 30.508989,12.5841744 30.508989,12.3440771 C30.508989,12.301707 30.2922225,12.3723238 30.0465538,12.4994342 C29.7864339,12.6406679 29.2083898,12.8525184 28.7748567,12.9796288 L27.9944971,13.219726 L27.2863931,12.7536548 C26.8962133,12.4994342 26.3470714,12.2169668 26.0580493,12.1322266 C25.3210431,11.9344994 24.193857,11.9627461 23.5291063,12.18872 C21.7227185,12.8242717 20.5810813,14.4625826 20.7111412,16.2562505 Z", id: "Combined-Shape-path", fill: "#FFFFFF", fillRule: "nonzero" })] }))),
    }); },
    SocialTikTok: function () { return ({
        height: 40,
        width: 40,
        viewBox: '0 0 40 40',
        icon: (jsxs("g", __assign$1({ id: "ic/40/Social/TikTok", stroke: "none", strokeWidth: "1", fill: "none", fillRule: "evenodd" }, { children: [jsx("circle", { id: "Oval", fill: "#0E5F80", cx: "20", cy: "20", r: "19" }), jsx("path", { d: "M23.7009 10C23.7009 10.2972 23.7288 10.5899 23.7801 10.8731C24.0271 12.1826 24.8053 13.3063 25.8862 14.0074C26.641 14.4996 27.5356 14.7829 28.5 14.7829L28.4998 15.5491V18.2191C26.7107 18.2191 25.0521 17.648 23.7008 16.6821V23.6707C23.7008 27.1581 20.8495 30 17.3504 30C15.9993 30 14.7412 29.5727 13.7115 28.8529C12.0716 27.706 11 25.8114 11 23.6707C11 20.1787 13.8468 17.3416 17.3458 17.3461C17.6393 17.3461 17.9235 17.3694 18.2031 17.4066V18.2191L18.1929 18.224L18.203 18.2238V20.9171C17.9328 20.8335 17.644 20.7824 17.3458 20.7824C15.7477 20.7824 14.4478 22.078 14.4478 23.6707C14.4478 24.7806 15.0814 25.7418 16.0039 26.2294C16.0125 26.2411 16.0213 26.2528 16.03 26.2644L16.0455 26.2847C16.0349 26.2643 16.0226 26.2443 16.0086 26.2247C16.4139 26.4383 16.8705 26.5591 17.3551 26.5591C18.9159 26.5591 20.1926 25.3193 20.2484 23.7776L20.2531 10H23.7009Z", fill: "#F7F3F0", id: "TikTok" })] }))),
    }); },
    SocialYouTube: function () { return ({
        height: 40,
        width: 40,
        viewBox: '0 0 40 40',
        icon: (jsxs("g", __assign$1({ id: "ic/40/Social/YouTube", stroke: "none", strokeWidth: "1", fill: "none", fillRule: "evenodd" }, { children: [jsx("circle", { id: "Oval", fill: "#0E5F80", cx: "20", cy: "20", r: "19" }), jsx("path", { d: "M29.5 15.125C29.25 14.25 28.625 13.625 27.75 13.375C26.25 13 19.875 13 19.875 13C19.875 13 13.625 13 12 13.375C11.125 13.625 10.5 14.25 10.25 15.125C10 16.75 10 20 10 20C10 20 10 23.25 10.375 24.875C10.625 25.75 11.25 26.375 12.125 26.625C13.625 27 20 27 20 27C20 27 26.25 27 27.875 26.625C28.75 26.375 29.375 25.75 29.625 24.875C30 23.25 30 20 30 20C30 20 30 16.75 29.5 15.125ZM18 23V17L23.25 20L18 23Z", fill: "white", id: "YouTube" })] }))),
    }); },
    SocialCalendar: function () { return ({
        height: 40,
        width: 40,
        viewBox: '0 0 40 40',
        icon: (jsxs("g", __assign$1({ id: "ic/40/Social/Calendar", stroke: "none", strokeWidth: "1", fill: "none", fillRule: "evenodd" }, { children: [jsx("path", { d: "M20 39C30.4934 39 39 30.4934 39 20C39 9.50659 30.4934 1 20 1C9.50659 1 1 9.50659 1 20C1 30.4934 9.50659 39 20 39Z", fill: "#0E5F80" }), jsx("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M15 10C14.4477 10 14 10.4477 14 11V13H12C10.8954 13 10 13.8954 10 15V28C10 29.1046 10.8954 30 12 30H28C29.1046 30 30 29.1046 30 28V15C30 13.8954 29.1046 13 28 13H26V11C26 10.4477 25.5523 10 25 10C24.4477 10 24 10.4477 24 11V13H16V11C16 10.4477 15.5523 10 15 10ZM25 16C24.4477 16 24 15.5523 24 15H16C16 15.5523 15.5523 16 15 16C14.4477 16 14 15.5523 14 15H12V18H28V15H26C26 15.5523 25.5523 16 25 16ZM12 28V20H28V28H12ZM23.5 25.5V23.5H25.5V25.5H23.5ZM22 23C22 22.4477 22.4477 22 23 22H26C26.5523 22 27 22.4477 27 23V26C27 26.5523 26.5523 27 26 27H23C22.4477 27 22 26.5523 22 26V23Z", fill: "white" })] }))),
    }); },
    SocialDraw: function () { return ({
        height: 40,
        width: 40,
        viewBox: '0 0 40 40',
        icon: (jsxs("g", __assign$1({ id: "ic/40/Social/Draw", stroke: "none", strokeWidth: "1", fill: "none", fillRule: "evenodd" }, { children: [jsx("path", { d: "M20 39C30.4934 39 39 30.4934 39 20C39 9.50659 30.4934 1 20 1C9.50659 1 1 9.50659 1 20C1 30.4934 9.50659 39 20 39Z", fill: "#0E5F80" }), jsx("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M28 14H12V23H15C15.5523 23 16 23.4477 16 24C16 24.5523 15.5523 25 15 25H12C10.8954 25 10 24.1046 10 23V14C10 12.8954 10.8954 12 12 12H28C29.1046 12 30 12.8954 30 14V23C30 24.1046 29.1046 25 28 25H25C24.4477 25 24 24.5523 24 24C24 23.4477 24.4477 23 25 23H28V14ZM20 19.5C20.5523 19.5 21 19.0523 21 18.5C21 17.9477 20.5523 17.5 20 17.5C19.4477 17.5 19 17.9477 19 18.5C19 19.0523 19.4477 19.5 20 19.5ZM20 21.5C21.6569 21.5 23 20.1569 23 18.5C23 16.8431 21.6569 15.5 20 15.5C18.3431 15.5 17 16.8431 17 18.5C17 20.1569 18.3431 21.5 20 21.5ZM20 23C20.5523 23 21 23.4477 21 24V28.5858L23.2929 26.2929C23.6834 25.9024 24.3166 25.9024 24.7071 26.2929C25.0976 26.6834 25.0976 27.3166 24.7071 27.7071L20.7071 31.7071C20.3166 32.0976 19.6834 32.0976 19.2929 31.7071L15.2929 27.7071C14.9024 27.3166 14.9024 26.6834 15.2929 26.2929C15.6834 25.9024 16.3166 25.9024 16.7071 26.2929L19 28.5858V24C19 23.4477 19.4477 23 20 23Z", fill: "white" })] }))),
    }); },
    SocialRepay: function () { return ({
        height: 40,
        width: 40,
        viewBox: '0 0 40 40',
        icon: (jsxs("g", __assign$1({ id: "ic/40/Social/Repay", stroke: "none", strokeWidth: "1", fill: "none", fillRule: "evenodd" }, { children: [jsx("path", { d: "M20 39C30.4934 39 39 30.4934 39 20C39 9.50659 30.4934 1 20 1C9.50659 1 1 9.50659 1 20C1 30.4934 9.50659 39 20 39Z", fill: "#0E5F80" }), jsx("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M30 27.5C30 28.0523 29.5523 28.5 29 28.5H25.5C24.9477 28.5 24.5 28.0523 24.5 27.5V24C24.5 23.4477 24.9477 23 25.5 23C26.0523 23 26.5 23.4477 26.5 24V24.6637C27.3324 23.5035 27.844 22.1346 27.9696 20.6964C28.1334 18.8224 27.6315 16.9506 26.5522 15.4099C25.4729 13.8693 23.8852 12.7582 22.068 12.2719C20.5744 11.8722 19.0043 11.9157 17.546 12.3857C17.0204 12.5551 16.4264 12.3454 16.1928 11.8449C15.9592 11.3445 16.1743 10.7442 16.6956 10.5617C18.5845 9.90035 20.6368 9.81858 22.585 10.3399C24.8565 10.9477 26.8411 12.3366 28.1902 14.2624C29.5394 16.1883 30.1667 18.528 29.962 20.8705C29.78 22.9537 28.9503 24.9206 27.5994 26.5H29C29.5523 26.5 30 26.9477 30 27.5ZM10 12.5C10 11.9477 10.4477 11.5 11 11.5H14.5C15.0523 11.5 15.5 11.9477 15.5 12.5V16C15.5 16.5523 15.0523 17 14.5 17C13.9477 17 13.5 16.5523 13.5 16V15.3363C12.6676 16.4965 12.156 17.8654 12.0304 19.3036C11.8666 21.1776 12.3685 23.0494 13.4478 24.5901C14.5271 26.1307 16.1148 27.2418 17.932 27.7281C19.4256 28.1278 20.9957 28.0843 22.454 27.6143C22.9796 27.4449 23.5736 27.6546 23.8072 28.1551C24.0408 28.6555 23.8257 29.2558 23.3044 29.4383C21.4155 30.0996 19.3632 30.1814 17.415 29.6601C15.1435 29.0523 13.1589 27.6634 11.8098 25.7376C10.4606 23.8117 9.83329 21.472 10.038 19.1295C10.22 17.0463 11.0497 15.0794 12.4006 13.5H11C10.4477 13.5 10 13.0523 10 12.5Z", fill: "white" })] }))),
    }); },
    SocialHistory: function () { return ({
        height: 40,
        width: 40,
        viewBox: '0 0 40 40',
        icon: (jsxs("g", __assign$1({ id: "ic/40/Social/Repay", stroke: "none", strokeWidth: "1", fill: "none", fillRule: "evenodd" }, { children: [jsx("path", { d: "M20 39C30.4934 39 39 30.4934 39 20C39 9.50659 30.4934 1 20 1C9.50659 1 1 9.50659 1 20C1 30.4934 9.50659 39 20 39Z", fill: "#0E5F80" }), jsx("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M11.5 14H12C10.9079 15.4561 10.2283 17.1907 10.0482 19.0198C9.8214 21.3222 10.4 23.632 11.6853 25.5557C12.9707 27.4793 14.8832 28.8978 17.0972 29.5694C19.3111 30.241 21.6894 30.1241 23.8268 29.2388C25.9643 28.3534 27.7286 26.7543 28.8192 24.714C29.9098 22.6736 30.2592 20.3182 29.8079 18.0491C29.3565 15.78 28.1323 13.7376 26.3439 12.2699C24.8147 11.0148 22.9538 10.2464 21 10.0501C20.4505 9.99488 20 10.4477 20 11C20 11.5523 20.4495 11.9935 20.9974 12.0624C22.4882 12.2497 23.9034 12.8542 25.0752 13.8159C26.5059 14.9901 27.4852 16.624 27.8463 18.4393C28.2074 20.2545 27.9279 22.1389 27.0554 23.7712C26.1829 25.4035 24.7714 26.6827 23.0615 27.391C21.3515 28.0993 19.4489 28.1928 17.6777 27.6555C15.9066 27.1182 14.3765 25.9835 13.3483 24.4446C12.32 22.9056 11.8571 21.0578 12.0385 19.2159C12.1771 17.8092 12.6848 16.4724 13.5 15.3363V16C13.5 16.5523 13.9477 17 14.5 17C15.0523 17 15.5 16.5523 15.5 16V13C15.5 12.4477 15.0523 12 14.5 12H11.5C10.9477 12 10.5 12.4477 10.5 13C10.5 13.5523 10.9477 14 11.5 14ZM21 17C21 16.4477 20.5523 16 20 16C19.4477 16 19 16.4477 19 17V20C19 20.2652 19.1054 20.5196 19.2929 20.7071L21.2929 22.7071C21.6834 23.0976 22.3166 23.0976 22.7071 22.7071C23.0976 22.3166 23.0976 21.6834 22.7071 21.2929L21 19.5858V17Z", fill: "white" })] }))),
    }); },
};

var Stars = {
    StarOn: function () { return ({
        height: 26,
        width: 26,
        viewBox: '0 0 26 26',
        icon: (jsx("g", __assign$1({ id: "ic/star/on", stroke: "none", strokeWidth: "1", fill: "none", fillRule: "evenodd" }, { children: jsx("path", { d: "M13.4795284,2.6219576 C13.6461246,2.71249559 13.783163,2.84830404 13.874521,3.01340488 L16.9471946,8.56629676 L23.1937941,9.77709802 C23.7344472,9.88189479 24.0870091,10.4012014 23.9812632,10.9370018 C23.9457251,11.1170685 23.8603202,11.2837803 23.7346077,11.418477 L19.3867516,16.0770617 L20.1655619,22.391291 C20.2324034,22.9332103 19.8432979,23.426222 19.2964705,23.4924635 C19.1097061,23.5150878 18.9202933,23.4849016 18.7501022,23.4053904 L12.9999085,20.7189696 L7.24971475,23.4053904 C6.75141208,23.6381913 6.15702625,23.4265854 5.92211683,22.9327553 C5.84188547,22.7640917 5.81142589,22.576379 5.83425503,22.391291 L6.61306538,16.0770617 L2.26520921,11.418477 C1.89114353,11.017678 1.91575791,10.3922478 2.32018696,10.0215396 C2.45610364,9.89695552 2.6243253,9.81231712 2.80602287,9.77709802 L9.05262236,8.56629676 L12.1252959,3.01340488 C12.3901827,2.53470526 12.9964933,2.35944835 13.4795284,2.6219576 Z", id: "Path", fill: "#9E5CAB" }) }))),
    }); },
    StarOff: function () { return ({
        height: 26,
        width: 26,
        viewBox: '0 0 26 26',
        icon: (jsx("g", __assign$1({ id: "ic/star/off", stroke: "none", strokeWidth: "1", fill: "none", fillRule: "evenodd" }, { children: jsx("path", { d: "M13.4795284,2.6219576 C13.6461246,2.71249559 13.783163,2.84830404 13.874521,3.01340488 L16.9471946,8.56629676 L23.1937941,9.77709802 C23.7344472,9.88189479 24.0870091,10.4012014 23.9812632,10.9370018 C23.9457251,11.1170685 23.8603202,11.2837803 23.7346077,11.418477 L19.3867516,16.0770617 L20.1655619,22.391291 C20.2324034,22.9332103 19.8432979,23.426222 19.2964705,23.4924635 C19.1097061,23.5150878 18.9202933,23.4849016 18.7501022,23.4053904 L12.9999085,20.7189696 L7.24971475,23.4053904 C6.75141208,23.6381913 6.15702625,23.4265854 5.92211683,22.9327553 C5.84188547,22.7640917 5.81142589,22.576379 5.83425503,22.391291 L6.61306538,16.0770617 L2.26520921,11.418477 C1.89114353,11.017678 1.91575791,10.3922478 2.32018696,10.0215396 C2.45610364,9.89695552 2.6243253,9.81231712 2.80602287,9.77709802 L9.05262236,8.56629676 L12.1252959,3.01340488 C12.3901827,2.53470526 12.9964933,2.35944835 13.4795284,2.6219576 Z M13,5.563 L10.8025755,9.53462729 C10.544582,10.0008695 10.1126008,10.3426672 9.60505257,10.4884978 L9.43320606,10.5297519 L4.977,11.393 L8.07520036,14.7124513 C8.43864867,15.1018742 8.63006341,15.6181779 8.6118939,16.1457678 L8.59802351,16.3218906 L8.042,20.827 L12.1533621,18.9069654 C12.6362406,18.6813705 13.1867523,18.658811 13.6831574,18.839287 L13.8464548,18.9069654 L17.957,20.827 L17.4017934,16.3218906 C17.3365861,15.7932197 17.4851352,15.2629912 17.8098903,14.8467997 L17.9246166,14.7124513 L21.022,11.393 L16.5666109,10.5297519 C16.0434851,10.4283525 15.5849906,10.1230391 15.2895483,9.6853342 L15.1972414,9.53462729 L13,5.563 Z", id: "Path", fill: "#D2D4D6" }) }))),
    }); },
    StarHalf: function () { return ({
        height: 26,
        width: 26,
        viewBox: '0 0 26 26',
        icon: (jsxs("g", __assign$1({ id: "ic/star/half", stroke: "none", strokeWidth: "1", fill: "none", fillRule: "evenodd" }, { children: [jsx("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M13 20.63L18.8947 23.6287C19.6224 23.999 20.4631 23.3882 20.3359 22.5816L19.3055 16.0488L23.979 11.3692C24.556 10.7915 24.2349 9.80325 23.4286 9.675L16.897 8.63622L13.8907 2.74536C13.7051 2.38173 13.3526 2.19992 13 2.19992V5.39984L15.3468 9.99846C15.4921 10.2832 15.7648 10.4813 16.0805 10.5315L21.1792 11.3424L17.5309 14.9954C17.305 15.2216 17.2009 15.5421 17.2507 15.8579L18.055 20.9577L13.4534 18.6167C13.311 18.5443 13.1555 18.508 13 18.508V20.63Z", fill: "#D2D4D6" }), jsx("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M13 2.19992C12.6474 2.19992 12.2949 2.38173 12.1093 2.74536L9.10298 8.63622L2.57144 9.675C1.7651 9.80325 1.44398 10.7915 2.02095 11.3692L6.69449 16.0488L5.66408 22.5816C5.53687 23.3882 6.37756 23.999 7.10529 23.6287L13 20.63L13 2.19992Z", fill: "#9E5CAB" })] }))),
    }); },
    StarSparkling: function () { return ({
        height: 26,
        width: 26,
        viewBox: '0 0 84 94',
        icon: (jsx("g", __assign$1({ id: "ic/star/sparkling", stroke: "none", strokeWidth: "1", fill: "none", fillRule: "evenodd" }, { children: jsx("path", { fill: "#9E5CAB", d: "M79.471 31.023a7.589 7.589 0 0 1-2.208 4.321L65.545 46.733a.97.97 0 0 0-.28.859l2.767 16.081c.705 4.104-2.068 8.017-6.186 8.72a7.62 7.62 0 0 1-4.801-.756l-14.484-7.594a.98.98 0 0 0-.91 0l-14.48 7.592a7.545 7.545 0 0 1-3.512.866c-2.725 0-5.362-1.468-6.716-4.036a7.572 7.572 0 0 1-.762-4.792l2.766-16.079a.969.969 0 0 0-.279-.861L6.951 35.344a7.546 7.546 0 0 1-.137-10.68 7.612 7.612 0 0 1 4.337-2.202l16.188-2.346a.98.98 0 0 0 .736-.534l7.239-14.628c1.85-3.735 6.398-5.273 10.143-3.431a7.613 7.613 0 0 1 3.442 3.431l7.241 14.629a.972.972 0 0 0 .733.533l16.192 2.347a7.527 7.527 0 0 1 4.981 2.956 7.496 7.496 0 0 1 1.425 5.604zm-6.713-1.669a.959.959 0 0 0-.641-.381l-16.193-2.347a7.565 7.565 0 0 1-5.702-4.134L42.983 7.867a.974.974 0 0 0-.444-.444.983.983 0 0 0-1.308.443l-7.24 14.63a7.58 7.58 0 0 1-5.704 4.131l-16.19 2.345a.972.972 0 0 0-.541 1.661L23.272 42.02a7.535 7.535 0 0 1 2.177 6.687l-2.765 16.078a.977.977 0 0 0 .098.617.98.98 0 0 0 1.319.409l14.481-7.592a7.593 7.593 0 0 1 7.048 0l14.482 7.592a.977.977 0 0 0 1.417-1.026l-2.767-16.08a7.538 7.538 0 0 1 2.179-6.685l11.717-11.387a.982.982 0 0 0 .283-.558.947.947 0 0 0-.183-.721zM12.586 68.367a3.294 3.294 0 0 1-3.08 3.493l-5.512.354A3.296 3.296 0 0 1 .49 69.141a3.293 3.293 0 0 1 3.08-3.493l5.512-.354a3.284 3.284 0 0 1 3.504 3.073zm-1.55 6.6a3.308 3.308 0 0 1 4.656-.299 3.283 3.283 0 0 1 .299 4.642l-3.648 4.136a3.295 3.295 0 0 1-2.479 1.118c-.773 0-1.55-.27-2.177-.82a3.283 3.283 0 0 1-.299-4.642l3.648-4.135zm11.319 1.966a3.276 3.276 0 0 1 3.505 3.072l.354 5.496a3.294 3.294 0 0 1-3.296 3.5 3.296 3.296 0 0 1-3.289-3.079l-.354-5.496a3.293 3.293 0 0 1 3.08-3.493z" }) }))),
    }); },
};

var Success = {
    Success: function () { return ({
        height: 98,
        width: 98,
        viewBox: '0 0 98 98',
        icon: (jsx("g", __assign$1({ id: "Desktop", stroke: "none", strokeWidth: "1", fill: "none", fillRule: "evenodd" }, { children: jsx("g", __assign$1({ id: "01.1_Desktop_Home_Sent", transform: "translate(-671.000000, -333.000000)" }, { children: jsx("g", __assign$1({ id: "Group-19", transform: "translate(476.000000, 284.000000)" }, { children: jsxs("g", __assign$1({ id: "Group", transform: "translate(194.000000, 48.000000)" }, { children: [jsx("circle", { cx: "33", cy: "67", r: "32", fill: "#CBEEF2" }), jsx("circle", { cx: "79", cy: "21", r: "20", fill: "#79C9D6" }), jsx("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M58.2826 23.2698C56.0684 17.9789 52.4885 15.1139 47.8644 16.2446C45.1009 16.9204 43.3091 18.7603 42.4557 21.4295C41.8519 23.3182 41.7197 25.4363 41.8507 28.4126C41.8597 28.617 41.8691 28.813 41.8802 29.023L41.9766 30.6657C42.148 33.5242 42.1547 34.7296 41.9552 35.5884L41.9125 35.756C41.0639 38.8141 33.093 46.0897 29.0774 48.3081C27.6735 49.0837 23.637 49.5702 17.3253 49.5702C16.0411 49.5702 15 50.6125 15 51.8981C15 53.1837 16.0411 54.2259 17.3253 54.2259C24.4358 54.2259 28.983 53.6779 31.3245 52.3843C36.3468 49.6096 45.1161 41.6054 46.3935 37.0022C46.8187 35.4701 46.8401 34.1614 46.6397 30.7355L46.5517 29.2698L46.4969 28.2076C46.2812 23.308 46.9344 21.2646 48.968 20.7674C52.7731 19.8369 56.0735 27.4491 56.2509 37.5106C52.7905 39.6888 51.3153 42.5676 52.1324 46.2017C52.6296 48.4132 53.7345 49.9655 55.312 50.9577C54.9828 53.7549 55.657 56.1054 57.3311 57.8278C54.872 62.6656 55.6535 66.626 59.5913 68.8852C58.6208 73.2142 59.1664 76.5824 61.2644 78.6554C55.6461 79.8125 50.3743 80.3443 45.4778 80.3443C42.9468 80.3443 40.8862 79.5719 37.3231 77.5807L35.6268 76.6352C34.7045 76.1246 34.2736 75.8983 33.8177 75.6862L33.098 75.3659C32.2935 75.016 31.5276 74.7464 30.7654 74.5554C27.0484 73.6243 22.8744 74.4178 18.2765 76.8114C17.1371 77.4046 16.6937 78.8101 17.2863 79.9507C17.8788 81.0913 19.2828 81.5351 20.4222 80.942C24.0921 79.0315 27.1526 78.4496 29.6365 79.0719C30.1329 79.1962 30.6617 79.3824 31.2449 79.6361C31.9035 79.9225 32.169 80.0447 33.1839 80.6034L35.4461 81.8623C39.4314 84.0587 42.0497 85 45.4778 85C53.0773 85 61.4795 83.8007 70.5802 81.1092C70.6677 81.0833 70.7526 81.0527 70.8346 81.0178C77.6333 80.5695 83.7833 77.1763 83.7833 72.4623C83.7833 70.2792 83.0541 68.4438 81.6813 67.0335C83.4513 65.6407 84.635 63.9212 84.9018 61.9554C85.3522 58.6365 84.24 56.1256 81.8458 54.6956C82.2278 54.0654 82.5475 53.3877 82.7994 52.6622L82.8643 52.4751L82.9339 52.0434C83.4562 48.5108 82.3672 46.0049 80.0153 44.8238C80.9291 43.1485 81.2135 41.19 80.7372 38.9899C79.5032 33.2895 74.192 31.827 66.0626 33.9693L65.5075 34.1195L64.9113 34.2524C63.4285 34.5921 62.0627 34.9752 60.8191 35.4027C60.5497 30.9538 59.6798 26.6083 58.2826 23.2698ZM77.1827 69.4629C73.2434 70.9089 68.3769 71.3496 63.9879 70.4439C63.1764 74.2552 64.1438 75.8492 66.7808 76.2332C69.8282 76.677 73.1702 76.2623 75.7813 75.1956C77.9782 74.2981 79.1326 73.1567 79.1326 72.4623C79.1326 71.0613 78.5698 70.1404 77.1827 69.4629ZM63.7815 65.6036C60.5052 64.6675 59.7322 63.2772 61.3335 60.1707C64.1974 61.071 68.1662 61.3071 71.7515 60.6949C74.2794 60.2633 76.5273 59.4498 78.3613 58.2466C80.0734 58.6501 80.5408 59.5057 80.2934 61.3287C79.8393 64.6745 70.6714 67.572 63.7815 65.6036ZM75.9732 48.7144C74.1586 49.7575 72.0219 50.4771 69.4781 51.0629L67.8298 51.4452L66.8931 51.6502C65.3684 51.9711 64.033 52.1841 62.7184 52.2885C61.7139 52.3683 60.7594 52.3774 59.8634 52.3093C59.8616 53.8167 60.5147 54.748 61.8285 55.3825C63.7313 56.3015 67.5873 56.683 70.9695 56.1055C74.6483 55.4773 77.2293 53.9032 78.2539 51.5264L78.3411 51.3107L78.3573 51.206C78.6609 49.095 78.1871 48.5392 75.9732 48.7144ZM69.0113 46.3883C74.2899 45.0837 76.8767 43.1391 76.192 39.9759L76.1643 39.8577C75.5754 37.5213 72.851 36.8925 66.7002 38.6204L66.5608 38.6549C58.9105 40.3016 56.0805 42.5595 56.6695 45.1793C57.1207 47.186 58.8748 47.9235 62.3506 47.6474C63.564 47.551 64.8575 47.3334 66.411 46.9919C66.5416 46.9631 66.672 46.934 66.8077 46.9033L69.0113 46.3883Z", fill: "#10465B" }), jsx("path", { d: "M88.2339 67.2663C89.3598 66.6789 90.7593 67.0958 91.3596 68.1974C93.9977 73.0379 93.7523 79.2893 88.4149 84.3723C84.1992 88.3869 74.6514 90.8134 59.4937 91.9928C58.2217 92.0917 57.1086 91.1631 57.0074 89.9187C56.9062 88.6742 57.8554 87.5851 59.1274 87.4861L60.1627 87.4027C73.6657 86.2777 82.0946 84.0822 85.1918 81.1327C88.876 77.6242 89.0362 73.5429 87.2821 70.3244C86.6818 69.2228 87.1079 67.8536 88.2339 67.2663Z", fill: "#F48122" }), jsx("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M7.36849 83.6645C7.36849 83.6645 10.602 79.1093 9.80256 71.6724C12.1578 74.6863 12.7247 79.4008 20.0618 80.4563C17.4747 83.0307 16.0111 87.013 16.3767 91.6428C13.0695 85.5373 7.36849 83.6645 7.36849 83.6645Z", stroke: "#9E5CAB", strokeWidth: "4", strokeLinejoin: "round" }), jsx("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M71.1856 23.2754C71.1856 23.2754 77.4796 20.8969 81.3743 13.237C81.9283 17.6033 79.5224 22.4964 86.5528 28.0714C82.1981 28.9437 78.1268 31.8701 75.5635 36.5567C75.9782 28.6212 71.1856 23.2754 71.1856 23.2754Z", stroke: "#9E5CAB", strokeWidth: "4", strokeLinejoin: "round" }), jsx("path", { d: "M33.783 26.9016L27.7965 29.318", stroke: "#F48122", strokeWidth: "4", strokeLinecap: "round" }), jsx("path", { d: "M38.2443 7.18823L38.7219 13.5196", stroke: "#F48122", strokeWidth: "4", strokeLinecap: "round" }), jsx("path", { d: "M23.1774 13.8621L33.5019 18.946", stroke: "#F48122", strokeWidth: "4", strokeLinecap: "round" })] })) })) })) }))),
    }); },
};

var Icons$1 = __assign$1(__assign$1(__assign$1(__assign$1(__assign$1(__assign$1(__assign$1(__assign$1(__assign$1(__assign$1(__assign$1(__assign$1(__assign$1(__assign$1(__assign$1(__assign$1(__assign$1(__assign$1(__assign$1(__assign$1({}, AppStore), Arrows), Buttons), Check), Checkbox$3), Contacts), Dots$1), Errors), Flags), GetTheApp), Gift), Logos), NumberIcons), PaymentAccount), RoundIcons), Socials$2), Stars), Success), MenuIcons), SelfService);
var SocialIcons$1 = Socials$2;

var iconNames = Object.keys(Icons$1);
var socialIconsNames = Object.keys(SocialIcons$1);
var iconsByHeight = Object.entries(Icons$1).reduce(function (total, current) {
    var _a;
    var _b;
    var name = current[0], icon = current[1];
    var iconNames = (_b = total[icon().height]) !== null && _b !== void 0 ? _b : [];
    return __assign$1(__assign$1({}, total), (_a = {}, _a[icon().height] = __spreadArray$1(__spreadArray$1([], iconNames, true), [name], false), _a));
}, {});

var spacing = {
    0: '0px',
    4: '4px',
    6: '6px',
    8: '8px',
    10: '10px',
    12: '12px',
    16: '16px',
    18: '18px',
    20: '20px',
    24: '24px',
    28: '28px',
    32: '32px',
    40: '40px',
    48: '48px',
    60: '60px',
    70: '70px',
    80: '80px',
    100: '100px',
    140: '140px',
};

var zIndex = {
    primary: 1200,
    secondary: 1500,
    negative: -1,
};

var flexboxgrid = {
    gridSize: {
        xs: 4,
        sm: 8,
        md: 12,
        lg: 12,
        xl: 12,
    },
    gutterSize: {
        xs: 16,
        sm: 24,
        md: 32,
        lg: 32,
        xl: 32,
    },
    outerMargin: {
        xs: '24px',
        sm: '32px',
        md: '0',
        lg: '0',
        xl: '0',
    },
    container: {
        xs: '100%',
        sm: '100%',
        md: '856px',
        lg: '1120px',
        xl: '1120px',
    },
};

var index$2 = /*#__PURE__*/Object.freeze({
    __proto__: null,
    BREAKPOINTS_NAMES: BREAKPOINTS_NAMES,
    BREAKPOINT_VALUES: BREAKPOINT_VALUES,
    customMediaQuery: customMediaQuery,
    breakpoints: breakpoints,
    colors: colors,
    fonts: fonts,
    iconNames: iconNames,
    socialIconsNames: socialIconsNames,
    iconsByHeight: iconsByHeight,
    spacing: spacing,
    zIndex: zIndex,
    flexboxgrid: flexboxgrid,
    crediteaColors: crediteaColors
});

var cssWithGridSpacing = function (theme) {
    return css(templateObject_1$1r || (templateObject_1$1r = __makeTemplateObject(["\n    ", "\n  "], ["\n    ", "\n  "])), BREAKPOINTS_NAMES.map(function (breakpoint) {
        return "\n        ".concat(theme.breakpoints[breakpoint], " {\n          min-height: ").concat(theme.flexboxGrid.gutterSize[breakpoint], "px;\n          min-width: ").concat(theme.flexboxGrid.gutterSize[breakpoint], "px;\n        }\n      ");
    }));
};
var cssResponsive = function (theme, size, sizeMobile) {
    return sizeMobile
        ? css(templateObject_2$1b || (templateObject_2$1b = __makeTemplateObject(["\n        min-height: ", "px;\n        min-width: ", "px;\n        ", " {\n          min-height: ", "px;\n          min-width: ", "px;\n        }\n      "], ["\n        min-height: ", "px;\n        min-width: ", "px;\n        ", " {\n          min-height: ", "px;\n          min-width: ", "px;\n        }\n      "])), sizeMobile, sizeMobile, theme.breakpoints.sm, size, size) : css(templateObject_3$Y || (templateObject_3$Y = __makeTemplateObject(["\n        min-height: ", "px;\n        min-width: ", "px;\n      "], ["\n        min-height: ", "px;\n        min-width: ", "px;\n      "])), size, size);
};
var Gutter$1 = styled.div(templateObject_4$N || (templateObject_4$N = __makeTemplateObject(["\n  ", "\n"], ["\n  ", "\n"])), function (_a) {
    var theme = _a.theme, size = _a.size, sizeMobile = _a.sizeMobile;
    return size === 'grid' ? cssWithGridSpacing(theme) : cssResponsive(theme, size, sizeMobile);
});
var templateObject_1$1r, templateObject_2$1b, templateObject_3$Y, templateObject_4$N;

var Gutter = function (_a) {
    var size = _a.size, sizeMobile = _a.sizeMobile;
    return (jsx(Gutter$1, { size: size, sizeMobile: sizeMobile, "data-testid": "gutter-test" }));
};

var Icon$5 = styled.div(templateObject_1$1q || (templateObject_1$1q = __makeTemplateObject(["\n  display: flex;\n  place-content: center;\n  path {\n    fill: ", ";\n  }\n\n  circle {\n    fill: ", ";\n  }\n"], ["\n  display: flex;\n  place-content: center;\n  path {\n    fill: ", ";\n  }\n\n  circle {\n    fill: ", ";\n  }\n"])), function (_a) {
    var fillColor = _a.fillColor;
    return fillColor;
}, function (_a) {
    var circleColor = _a.circleColor;
    return circleColor;
});
var templateObject_1$1q;

var Icon$4 = function (_a) {
    var width = _a.width, height = _a.height, viewBox = _a.viewBox, title = _a.title, name = _a.name, style = _a.style, props = __rest$1(_a, ["width", "height", "viewBox", "title", "name", "style"]);
    return (jsx(Icon$5, __assign$1({}, props, { children: jsxs("svg", __assign$1({ width: width || Icons$1[name]().width, height: height || Icons$1[name]().height, viewBox: viewBox || Icons$1[name]().viewBox, style: style }, { children: [title && jsx("title", { children: title }), Icons$1[name]().icon] })) })));
};
Icon$4.defaultProps = {
    role: 'img',
};

var theme = {
    colors: colors,
    fonts: fonts,
    breakpoints: breakpoints,
    customZIndex: zIndex,
    space: spacing,
    flexboxGrid: flexboxgrid,
    crediteaColors: crediteaColors,
};

var makeMediaQuery = function (media, style) {
    var breakpoints = theme.breakpoints;
    var result = Object.entries(breakpoints)
        .map(function (breakpoint) {
        return media.indexOf(breakpoint[0]) !== -1 && "".concat(breakpoint[1], " {").concat(style, "}");
    })
        .filter(Boolean);
    return css(templateObject_1$1p || (templateObject_1$1p = __makeTemplateObject(["\n    ", "\n  "], ["\n    ", "\n  "])), result);
};
var templateObject_1$1p;

var textVariantPaddingCss = function (_a) {
    var size = _a.size, withoutHorizontalSpacing = _a.withoutHorizontalSpacing, withoutSurroundingSpacing = _a.withoutSurroundingSpacing;
    if (withoutSurroundingSpacing) {
        return css(templateObject_1$1o || (templateObject_1$1o = __makeTemplateObject(["\n      padding: 0;\n    "], ["\n      padding: 0;\n    "])));
    }
    var verticalPadding = size === 'small' ? '11px' : '16px';
    var horizontalPadding = withoutHorizontalSpacing ? '0' : '16px';
    return css(templateObject_2$1a || (templateObject_2$1a = __makeTemplateObject(["\n    padding: ", " ", ";\n  "], ["\n    padding: ", " ", ";\n  "])), verticalPadding, horizontalPadding);
};
var Button$3 = styled.button.attrs(function (props) { return ({
    type: props.type || 'button',
}); })(templateObject_8$j || (templateObject_8$j = __makeTemplateObject(["\n  cursor: pointer;\n  font-weight: ", ";\n  font-size: ", ";\n  line-height: ", ";\n  ", "\n  border-radius: 1rem;\n  border: none;\n  transition: background-color 0.4s, color 0.4s;\n  width: 100%;\n  ", "\n  margin: 0;\n\n  &:disabled {\n    cursor: default;\n  }\n\n  text-overflow: ellipsis;\n  overflow: hidden;\n  white-space: nowrap;\n  max-width: 100%;\n\n  ", "\n\n  ", "\n\n  ", "\n"], ["\n  cursor: pointer;\n  font-weight: ", ";\n  font-size: ", ";\n  line-height: ", ";\n  ", "\n  border-radius: 1rem;\n  border: none;\n  transition: background-color 0.4s, color 0.4s;\n  width: 100%;\n  ", "\n  margin: 0;\n\n  &:disabled {\n    cursor: default;\n  }\n\n  text-overflow: ellipsis;\n  overflow: hidden;\n  white-space: nowrap;\n  max-width: 100%;\n\n  ", "\n\n  ", "\n\n  ", "\n"])), function (props) { return props.theme.fonts.weight.medium500; }, function (props) { return props.theme.fonts.size.desktop.xs; }, function (props) { return props.theme.fonts.lineHeight.desktop.xs; }, function (props) {
    return !(props.withoutHorizontalSpacing || props.withoutSurroundingSpacing) && css(templateObject_3$X || (templateObject_3$X = __makeTemplateObject(["\n      min-width: 155px;\n    "], ["\n      min-width: 155px;\n    "])));
}, BREAKPOINTS_NAMES.map(function (breakpoint) {
    return function (_a) {
        var autoWidth = _a.autoWidth;
        return css(templateObject_4$M || (templateObject_4$M = __makeTemplateObject(["\n      ", "\n    "], ["\n      ", "\n    "])), breakpoint === autoWidth && makeMediaQuery(autoWidth, 'width: auto;'));
    };
}), function (_a) {
    var variant = _a.variant, colors = _a.theme.colors, size = _a.size, mode = _a.mode;
    return variant === 'primary' && css(templateObject_5$B || (templateObject_5$B = __makeTemplateObject(["\n      background-color: ", ";\n      color: ", ";\n      padding: ", "px 16px;\n\n      &:hover {\n        background-color: ", ";\n        color: ", ";\n      }\n\n      &:disabled {\n        background-color: ", ";\n      }\n    "], ["\n      background-color: ", ";\n      color: ", ";\n      padding: ", "px 16px;\n\n      &:hover {\n        background-color: ", ";\n        color: ", ";\n      }\n\n      &:disabled {\n        background-color: ", ";\n      }\n    "])), colors.button.primary[mode].backgroundColor, colors.button.primary[mode].color, size === 'small' ? '11' : '16', colors.button.primary[mode].hover.backgroundColor, colors.button.primary[mode].hover.color, colors.button.primary[mode].disabled.backgroundColor);
}, function (_a) {
    var colors = _a.theme.colors, variant = _a.variant, mode = _a.mode, size = _a.size;
    return variant === 'secondary' && css(templateObject_6$w || (templateObject_6$w = __makeTemplateObject(["\n      background-color: transparent;\n      border: 2px solid ", ";\n      padding: ", "px 16px;\n      color: ", ";\n\n      &:hover {\n        background-color: ", ";\n        color: ", ";\n      }\n\n      &:disabled {\n        border: 2px solid ", ";\n        color: ", ";\n      }\n    "], ["\n      background-color: transparent;\n      border: 2px solid ", ";\n      padding: ", "px 16px;\n      color: ", ";\n\n      &:hover {\n        background-color: ", ";\n        color: ", ";\n      }\n\n      &:disabled {\n        border: 2px solid ", ";\n        color: ", ";\n      }\n    "])), colors.button.secondary[mode].borderColor, size === 'small' ? '9' : '16', colors.button.secondary[mode].color, colors.button.secondary[mode].hover.backgroundColor, colors.button.secondary[mode].hover.color, colors.button.secondary[mode].disabled.borderColor, colors.button.secondary[mode].disabled.color);
}, function (_a) {
    var colors = _a.theme.colors, mode = _a.mode, variant = _a.variant, size = _a.size, withoutHorizontalSpacing = _a.withoutHorizontalSpacing, withoutSurroundingSpacing = _a.withoutSurroundingSpacing;
    return variant === 'text' && css(templateObject_7$q || (templateObject_7$q = __makeTemplateObject(["\n      display: flex;\n      align-items: self-start;\n      justify-content: space-between;\n      ", "\n\n      div[role='img'] {\n        line-height: 0;\n      }\n\n      background-color: transparent;\n      color: ", ";\n      path:not([fill]) {\n        fill: ", ";\n        transition: fill 0.4s;\n      }\n\n      &:hover {\n        color: ", ";\n        path:not([fill]) {\n          fill: ", ";\n        }\n      }\n\n      &:disabled {\n        color: ", ";\n        path:not([fill]) {\n          fill: ", ";\n        }\n      }\n    "], ["\n      display: flex;\n      align-items: self-start;\n      justify-content: space-between;\n      ", "\n\n      div[role='img'] {\n        line-height: 0;\n      }\n\n      background-color: transparent;\n      color: ", ";\n      path:not([fill]) {\n        fill: ", ";\n        transition: fill 0.4s;\n      }\n\n      &:hover {\n        color: ", ";\n        path:not([fill]) {\n          fill: ", ";\n        }\n      }\n\n      &:disabled {\n        color: ", ";\n        path:not([fill]) {\n          fill: ", ";\n        }\n      }\n    "])), textVariantPaddingCss({ size: size, withoutHorizontalSpacing: withoutHorizontalSpacing, withoutSurroundingSpacing: withoutSurroundingSpacing }), colors.button.text[mode].color, colors.button.text[mode].color, colors.button.text[mode].hover.color, colors.button.text[mode].hover.color, colors.button.text[mode].disabled.color, colors.button.text[mode].disabled.color);
});
var templateObject_1$1o, templateObject_2$1a, templateObject_3$X, templateObject_4$M, templateObject_5$B, templateObject_6$w, templateObject_7$q, templateObject_8$j;

var Button$2 = function (_a) {
    var children = _a.children, _b = _a.size, size = _b === void 0 ? 'standard' : _b, _c = _a.autoWidth, autoWidth = _c === void 0 ? 'sm' : _c, _d = _a.mode, mode = _d === void 0 ? 'dark' : _d, rest = __rest$1(_a, ["children", "size", "autoWidth", "mode"]);
    var variant = rest.variant, prependIcon = rest.prependIcon, appendIcon = rest.appendIcon;
    return (jsxs(Button$3, __assign$1({ size: size, mode: mode, autoWidth: autoWidth }, rest, { children: [variant === 'text' && prependIcon && (jsxs(Fragment, { children: [jsx(Icon$4, { name: prependIcon }), jsx(Gutter, { size: 12 })] })), children, variant === 'text' && appendIcon && (jsxs(Fragment, { children: [jsx(Gutter, { size: 12 }), jsx(Icon$4, { name: appendIcon })] }))] })));
};

var Wrapper$7 = styled.div(templateObject_1$1n || (templateObject_1$1n = __makeTemplateObject(["\n  display: flex;\n  flex-wrap: wrap;\n  gap: 16px;\n"], ["\n  display: flex;\n  flex-wrap: wrap;\n  gap: 16px;\n"])));
var templateObject_1$1n;

var ButtonsLayout = function (_a) {
    var children = _a.children;
    return jsx(Wrapper$7, { children: children });
};

var BaseText = css(templateObject_1$1m || (templateObject_1$1m = __makeTemplateObject(["\n  white-space: pre-line;\n  color: ", ";\n  text-align: ", ";\n"], ["\n  white-space: pre-line;\n  color: ", ";\n  text-align: ", ";\n"])), function (_a) {
    var color = _a.color, theme = _a.theme;
    return color || theme.crediteaColors.primary.neutral[800];
}, function (_a) {
    var align = _a.align;
    return align;
});
var Header1Css = css(templateObject_2$19 || (templateObject_2$19 = __makeTemplateObject(["\n  font-size: ", ";\n  line-height: ", ";\n  min-height: ", ";\n  font-weight: ", ";\n\n  ", " {\n    font-size: ", ";\n    line-height: ", ";\n    min-height: ", ";\n  }\n\n  ", " {\n    font-size: ", ";\n    line-height: ", ";\n    min-height: ", ";\n  }\n"], ["\n  font-size: ", ";\n  line-height: ", ";\n  min-height: ", ";\n  font-weight: ", ";\n\n  ", " {\n    font-size: ", ";\n    line-height: ", ";\n    min-height: ", ";\n  }\n\n  ", " {\n    font-size: ", ";\n    line-height: ", ";\n    min-height: ", ";\n  }\n"])), function (_a) {
    var theme = _a.theme;
    return theme.fonts.size.mobile.xxxxl;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.lineHeight.mobile.xxl;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.lineHeight.mobile.xxl;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.weight.bold700;
}, function (_a) {
    var theme = _a.theme;
    return theme.breakpoints.sm;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.size.tablet.xxxxl;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.lineHeight.tablet.xxxl;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.lineHeight.tablet.xxxl;
}, function (_a) {
    var theme = _a.theme;
    return theme.breakpoints.lg;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.size.desktop.xxxxl;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.lineHeight.desktop.xxxl;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.lineHeight.desktop.xxxl;
});
var Header2Css = css(templateObject_3$W || (templateObject_3$W = __makeTemplateObject(["\n  font-size: ", ";\n  line-height: ", ";\n  min-height: ", ";\n  font-weight: ", ";\n\n  ", " {\n    font-size: ", ";\n    line-height: ", ";\n    min-height: ", ";\n  }\n\n  ", " {\n    font-size: ", ";\n    line-height: ", ";\n    min-height: ", ";\n  }\n"], ["\n  font-size: ", ";\n  line-height: ", ";\n  min-height: ", ";\n  font-weight: ", ";\n\n  ", " {\n    font-size: ", ";\n    line-height: ", ";\n    min-height: ", ";\n  }\n\n  ", " {\n    font-size: ", ";\n    line-height: ", ";\n    min-height: ", ";\n  }\n"])), function (_a) {
    var theme = _a.theme;
    return theme.fonts.size.mobile.xxxl;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.lineHeight.mobile.xl;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.lineHeight.mobile.xl;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.weight.bold700;
}, function (_a) {
    var theme = _a.theme;
    return theme.breakpoints.sm;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.size.tablet.xxxl;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.lineHeight.tablet.xxl;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.lineHeight.tablet.xxl;
}, function (_a) {
    var theme = _a.theme;
    return theme.breakpoints.lg;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.size.desktop.xxxl;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.lineHeight.desktop.xxl;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.lineHeight.desktop.xxl;
});
var Header3Css = css(templateObject_4$L || (templateObject_4$L = __makeTemplateObject(["\n  font-size: ", ";\n  line-height: ", ";\n  min-height: ", ";\n  font-weight: ", ";\n\n  ", " {\n    font-size: ", ";\n    line-height: ", ";\n    min-height: ", ";\n  }\n\n  ", " {\n    font-size: ", ";\n    line-height: ", ";\n    min-height: ", ";\n  }\n"], ["\n  font-size: ", ";\n  line-height: ", ";\n  min-height: ", ";\n  font-weight: ", ";\n\n  ", " {\n    font-size: ", ";\n    line-height: ", ";\n    min-height: ", ";\n  }\n\n  ", " {\n    font-size: ", ";\n    line-height: ", ";\n    min-height: ", ";\n  }\n"])), function (_a) {
    var theme = _a.theme;
    return theme.fonts.size.mobile.xxl;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.lineHeight.mobile.l;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.lineHeight.mobile.l;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.weight.bold700;
}, function (_a) {
    var theme = _a.theme;
    return theme.breakpoints.sm;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.size.tablet.xxl;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.lineHeight.tablet.xl;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.lineHeight.tablet.xl;
}, function (_a) {
    var theme = _a.theme;
    return theme.breakpoints.lg;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.size.desktop.xxl;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.lineHeight.desktop.xl;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.lineHeight.desktop.xl;
});
var Header4Css = css(templateObject_5$A || (templateObject_5$A = __makeTemplateObject(["\n  font-size: ", ";\n  line-height: ", ";\n  min-height: ", ";\n  font-weight: ", ";\n\n  ", " {\n    font-size: ", ";\n    line-height: ", ";\n    min-height: ", ";\n  }\n\n  ", " {\n    font-size: ", ";\n    line-height: ", ";\n    min-height: ", ";\n  }\n"], ["\n  font-size: ", ";\n  line-height: ", ";\n  min-height: ", ";\n  font-weight: ", ";\n\n  ", " {\n    font-size: ", ";\n    line-height: ", ";\n    min-height: ", ";\n  }\n\n  ", " {\n    font-size: ", ";\n    line-height: ", ";\n    min-height: ", ";\n  }\n"])), function (_a) {
    var theme = _a.theme;
    return theme.fonts.size.mobile.xl;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.lineHeight.mobile.m;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.lineHeight.mobile.m;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.weight.bold700;
}, function (_a) {
    var theme = _a.theme;
    return theme.breakpoints.sm;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.size.tablet.xl;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.lineHeight.tablet.l;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.lineHeight.tablet.l;
}, function (_a) {
    var theme = _a.theme;
    return theme.breakpoints.lg;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.size.desktop.xl;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.lineHeight.desktop.l;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.lineHeight.desktop.l;
});
var Header5Css = css(templateObject_6$v || (templateObject_6$v = __makeTemplateObject(["\n  font-size: ", ";\n  line-height: ", ";\n  min-height: ", ";\n  font-weight: ", ";\n\n  ", " {\n    font-size: ", ";\n    line-height: ", ";\n    min-height: ", ";\n  }\n\n  ", " {\n    font-size: ", ";\n    line-height: ", ";\n    min-height: ", ";\n  }\n"], ["\n  font-size: ", ";\n  line-height: ", ";\n  min-height: ", ";\n  font-weight: ", ";\n\n  ", " {\n    font-size: ", ";\n    line-height: ", ";\n    min-height: ", ";\n  }\n\n  ", " {\n    font-size: ", ";\n    line-height: ", ";\n    min-height: ", ";\n  }\n"])), function (_a) {
    var theme = _a.theme;
    return theme.fonts.size.mobile.l;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.lineHeight.mobile.xs;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.lineHeight.mobile.xs;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.weight.bold700;
}, function (_a) {
    var theme = _a.theme;
    return theme.breakpoints.sm;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.size.tablet.l;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.lineHeight.tablet.m;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.lineHeight.tablet.m;
}, function (_a) {
    var theme = _a.theme;
    return theme.breakpoints.lg;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.size.desktop.l;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.lineHeight.desktop.m;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.lineHeight.desktop.m;
});
var Header6Css = css(templateObject_7$p || (templateObject_7$p = __makeTemplateObject(["\n  font-size: ", ";\n  line-height: ", ";\n  min-height: ", ";\n  font-weight: ", ";\n\n  ", " {\n    font-size: ", ";\n    line-height: ", ";\n    min-height: ", ";\n  }\n\n  ", " {\n    font-size: ", ";\n    line-height: ", ";\n    min-height: ", ";\n  }\n"], ["\n  font-size: ", ";\n  line-height: ", ";\n  min-height: ", ";\n  font-weight: ", ";\n\n  ", " {\n    font-size: ", ";\n    line-height: ", ";\n    min-height: ", ";\n  }\n\n  ", " {\n    font-size: ", ";\n    line-height: ", ";\n    min-height: ", ";\n  }\n"])), function (_a) {
    var theme = _a.theme;
    return theme.fonts.size.mobile.m;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.lineHeight.mobile.xxs;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.lineHeight.mobile.xxs;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.weight.bold700;
}, function (_a) {
    var theme = _a.theme;
    return theme.breakpoints.sm;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.size.tablet.m;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.lineHeight.tablet.xs;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.lineHeight.tablet.xs;
}, function (_a) {
    var theme = _a.theme;
    return theme.breakpoints.lg;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.size.desktop.m;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.lineHeight.desktop.xs;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.lineHeight.desktop.xs;
});
var Header7Css = css(templateObject_8$i || (templateObject_8$i = __makeTemplateObject(["\n  font-size: ", ";\n  line-height: ", ";\n  min-height: ", ";\n  font-weight: ", ";\n\n  ", " {\n    font-size: ", ";\n    line-height: ", ";\n    min-height: ", ";\n  }\n\n  ", " {\n    font-size: ", ";\n    line-height: ", ";\n    min-height: ", ";\n  }\n"], ["\n  font-size: ", ";\n  line-height: ", ";\n  min-height: ", ";\n  font-weight: ", ";\n\n  ", " {\n    font-size: ", ";\n    line-height: ", ";\n    min-height: ", ";\n  }\n\n  ", " {\n    font-size: ", ";\n    line-height: ", ";\n    min-height: ", ";\n  }\n"])), function (_a) {
    var theme = _a.theme;
    return theme.fonts.size.mobile.s;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.lineHeight.mobile.xxs;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.lineHeight.mobile.xxs;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.weight.bold700;
}, function (_a) {
    var theme = _a.theme;
    return theme.breakpoints.sm;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.size.tablet.s;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.lineHeight.tablet.xxs;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.lineHeight.tablet.xxs;
}, function (_a) {
    var theme = _a.theme;
    return theme.breakpoints.lg;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.size.desktop.s;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.lineHeight.desktop.xxs;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.lineHeight.desktop.xxs;
});
var Header8Css = css(templateObject_9$f || (templateObject_9$f = __makeTemplateObject(["\n  font-size: ", ";\n  line-height: ", ";\n  min-height: ", ";\n  font-weight: ", ";\n\n  ", " {\n    font-size: ", ";\n    line-height: ", ";\n    min-height: ", ";\n  }\n\n  ", " {\n    font-size: ", ";\n    line-height: ", ";\n    min-height: ", ";\n  }\n"], ["\n  font-size: ", ";\n  line-height: ", ";\n  min-height: ", ";\n  font-weight: ", ";\n\n  ", " {\n    font-size: ", ";\n    line-height: ", ";\n    min-height: ", ";\n  }\n\n  ", " {\n    font-size: ", ";\n    line-height: ", ";\n    min-height: ", ";\n  }\n"])), function (_a) {
    var theme = _a.theme;
    return theme.fonts.size.mobile.xs;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.lineHeight.mobile.xxxs;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.lineHeight.mobile.xxxs;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.weight.bold700;
}, function (_a) {
    var theme = _a.theme;
    return theme.breakpoints.sm;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.size.tablet.xs;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.lineHeight.tablet.xxs;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.lineHeight.tablet.xxs;
}, function (_a) {
    var theme = _a.theme;
    return theme.breakpoints.lg;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.size.desktop.xs;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.lineHeight.desktop.xxs;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.lineHeight.desktop.xxs;
});
var Caption1Css = css(templateObject_10$a || (templateObject_10$a = __makeTemplateObject(["\n  font-size: ", ";\n  line-height: ", ";\n  min-height: ", ";\n  font-weight: ", ";\n\n  ", " {\n    font-size: ", ";\n    line-height: ", ";\n    min-height: ", ";\n  }\n\n  ", " {\n    font-size: ", ";\n    line-height: ", ";\n    min-height: ", ";\n  }\n"], ["\n  font-size: ", ";\n  line-height: ", ";\n  min-height: ", ";\n  font-weight: ", ";\n\n  ", " {\n    font-size: ", ";\n    line-height: ", ";\n    min-height: ", ";\n  }\n\n  ", " {\n    font-size: ", ";\n    line-height: ", ";\n    min-height: ", ";\n  }\n"])), function (_a) {
    var theme = _a.theme;
    return theme.fonts.size.mobile.m;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.lineHeight.mobile.xs;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.lineHeight.mobile.xs;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.weight.medium500;
}, function (_a) {
    var theme = _a.theme;
    return theme.breakpoints.sm;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.size.tablet.m;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.lineHeight.tablet.s;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.lineHeight.tablet.s;
}, function (_a) {
    var theme = _a.theme;
    return theme.breakpoints.lg;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.size.desktop.m;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.lineHeight.desktop.s;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.lineHeight.desktop.s;
});
var Caption2Css = css(templateObject_11$7 || (templateObject_11$7 = __makeTemplateObject(["\n  font-size: ", ";\n  line-height: ", ";\n  min-height: ", ";\n  font-weight: ", ";\n\n  ", " {\n    font-size: ", ";\n    line-height: ", ";\n    min-height: ", ";\n  }\n\n  ", " {\n    font-size: ", ";\n    line-height: ", ";\n    min-height: ", ";\n  }\n"], ["\n  font-size: ", ";\n  line-height: ", ";\n  min-height: ", ";\n  font-weight: ", ";\n\n  ", " {\n    font-size: ", ";\n    line-height: ", ";\n    min-height: ", ";\n  }\n\n  ", " {\n    font-size: ", ";\n    line-height: ", ";\n    min-height: ", ";\n  }\n"])), function (_a) {
    var theme = _a.theme;
    return theme.fonts.size.mobile.s;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.lineHeight.mobile.xs;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.lineHeight.mobile.xs;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.weight.medium500;
}, function (_a) {
    var theme = _a.theme;
    return theme.breakpoints.sm;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.size.tablet.s;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.lineHeight.tablet.xs;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.lineHeight.tablet.xs;
}, function (_a) {
    var theme = _a.theme;
    return theme.breakpoints.lg;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.size.desktop.s;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.lineHeight.desktop.xs;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.lineHeight.desktop.xs;
});
var Caption3Css = css(templateObject_12$5 || (templateObject_12$5 = __makeTemplateObject(["\n  font-size: ", ";\n  line-height: ", ";\n  min-height: ", ";\n  font-weight: ", ";\n\n  ", " {\n    font-size: ", ";\n    line-height: ", ";\n    min-height: ", ";\n  }\n"], ["\n  font-size: ", ";\n  line-height: ", ";\n  min-height: ", ";\n  font-weight: ", ";\n\n  ", " {\n    font-size: ", ";\n    line-height: ", ";\n    min-height: ", ";\n  }\n"])), function (_a) {
    var theme = _a.theme;
    return theme.fonts.size.tablet.xs;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.lineHeight.tablet.xs;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.lineHeight.tablet.xs;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.weight.medium500;
}, function (_a) {
    var theme = _a.theme;
    return theme.breakpoints.lg;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.size.desktop.xs;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.lineHeight.desktop.xs;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.lineHeight.desktop.xs;
});
var Caption4Css = css(templateObject_13$4 || (templateObject_13$4 = __makeTemplateObject(["\n  font-size: ", ";\n  line-height: ", ";\n  min-height: ", ";\n  font-weight: ", ";\n\n  ", " {\n    font-size: ", ";\n    line-height: ", ";\n    min-height: ", ";\n  }\n\n  ", " {\n    font-size: ", ";\n    line-height: ", ";\n    min-height: ", ";\n  }\n"], ["\n  font-size: ", ";\n  line-height: ", ";\n  min-height: ", ";\n  font-weight: ", ";\n\n  ", " {\n    font-size: ", ";\n    line-height: ", ";\n    min-height: ", ";\n  }\n\n  ", " {\n    font-size: ", ";\n    line-height: ", ";\n    min-height: ", ";\n  }\n"])), function (_a) {
    var theme = _a.theme;
    return theme.fonts.size.mobile.xxs;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.lineHeight.mobile.xxs;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.lineHeight.mobile.xxs;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.weight.medium500;
}, function (_a) {
    var theme = _a.theme;
    return theme.breakpoints.sm;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.size.tablet.xxs;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.lineHeight.tablet.xxs;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.lineHeight.tablet.xxs;
}, function (_a) {
    var theme = _a.theme;
    return theme.breakpoints.lg;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.size.desktop.xxs;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.lineHeight.desktop.xxs;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.lineHeight.desktop.xxs;
});
var Caption5Css = css(templateObject_14$1 || (templateObject_14$1 = __makeTemplateObject(["\n  font-size: ", ";\n  line-height: ", ";\n  min-height: ", ";\n  font-weight: ", ";\n\n  ", " {\n    font-size: ", ";\n    line-height: ", ";\n    min-height: ", ";\n  }\n\n  ", " {\n    font-size: ", ";\n    line-height: ", ";\n    min-height: ", ";\n  }\n"], ["\n  font-size: ", ";\n  line-height: ", ";\n  min-height: ", ";\n  font-weight: ", ";\n\n  ", " {\n    font-size: ", ";\n    line-height: ", ";\n    min-height: ", ";\n  }\n\n  ", " {\n    font-size: ", ";\n    line-height: ", ";\n    min-height: ", ";\n  }\n"])), function (_a) {
    var theme = _a.theme;
    return theme.fonts.size.mobile.xxxs;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.lineHeight.mobile.xxxs;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.lineHeight.mobile.xxxs;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.weight.medium500;
}, function (_a) {
    var theme = _a.theme;
    return theme.breakpoints.sm;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.size.tablet.xxxs;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.lineHeight.tablet.xxxs;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.lineHeight.tablet.xxxs;
}, function (_a) {
    var theme = _a.theme;
    return theme.breakpoints.lg;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.size.desktop.xxxs;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.lineHeight.desktop.xxxs;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.lineHeight.desktop.xxxs;
});
var Body1Css = css(templateObject_15$1 || (templateObject_15$1 = __makeTemplateObject(["\n  font-size: ", ";\n  line-height: ", ";\n  min-height: ", ";\n  font-weight: ", ";\n\n  ", " {\n    font-size: ", ";\n    line-height: ", ";\n    min-height: ", ";\n  }\n\n  ", " {\n    font-size: ", ";\n    line-height: ", ";\n    min-height: ", ";\n  }\n"], ["\n  font-size: ", ";\n  line-height: ", ";\n  min-height: ", ";\n  font-weight: ", ";\n\n  ", " {\n    font-size: ", ";\n    line-height: ", ";\n    min-height: ", ";\n  }\n\n  ", " {\n    font-size: ", ";\n    line-height: ", ";\n    min-height: ", ";\n  }\n"])), function (_a) {
    var theme = _a.theme;
    return theme.fonts.size.mobile.s;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.lineHeight.mobile.xs;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.lineHeight.mobile.xs;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.weight.regular400;
}, function (_a) {
    var theme = _a.theme;
    return theme.breakpoints.sm;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.size.tablet.m;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.lineHeight.tablet.s;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.lineHeight.tablet.s;
}, function (_a) {
    var theme = _a.theme;
    return theme.breakpoints.lg;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.size.desktop.m;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.lineHeight.desktop.s;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.lineHeight.desktop.s;
});
var Body2Css = css(templateObject_16$1 || (templateObject_16$1 = __makeTemplateObject(["\n  font-size: ", ";\n  line-height: ", ";\n  min-height: ", ";\n  font-weight: ", ";\n\n  ", " {\n    font-size: ", ";\n    line-height: ", ";\n    min-height: ", ";\n  }\n\n  ", " {\n    font-size: ", ";\n    line-height: ", ";\n    min-height: ", ";\n  }\n"], ["\n  font-size: ", ";\n  line-height: ", ";\n  min-height: ", ";\n  font-weight: ", ";\n\n  ", " {\n    font-size: ", ";\n    line-height: ", ";\n    min-height: ", ";\n  }\n\n  ", " {\n    font-size: ", ";\n    line-height: ", ";\n    min-height: ", ";\n  }\n"])), function (_a) {
    var theme = _a.theme;
    return theme.fonts.size.tablet.xs;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.lineHeight.tablet.xs;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.lineHeight.tablet.xs;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.weight.regular400;
}, function (_a) {
    var theme = _a.theme;
    return theme.breakpoints.sm;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.size.tablet.s;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.lineHeight.tablet.xs;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.lineHeight.tablet.xs;
}, function (_a) {
    var theme = _a.theme;
    return theme.breakpoints.lg;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.size.desktop.s;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.lineHeight.desktop.xs;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.lineHeight.desktop.xs;
});
var Body3Css = css(templateObject_17$1 || (templateObject_17$1 = __makeTemplateObject(["\n  font-size: ", ";\n  line-height: ", ";\n  min-height: ", ";\n  font-weight: ", ";\n\n  ", " {\n    font-size: ", ";\n    line-height: ", ";\n    min-height: ", ";\n  }\n"], ["\n  font-size: ", ";\n  line-height: ", ";\n  min-height: ", ";\n  font-weight: ", ";\n\n  ", " {\n    font-size: ", ";\n    line-height: ", ";\n    min-height: ", ";\n  }\n"])), function (_a) {
    var theme = _a.theme;
    return theme.fonts.size.tablet.xs;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.lineHeight.tablet.xs;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.lineHeight.tablet.xs;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.weight.regular400;
}, function (_a) {
    var theme = _a.theme;
    return theme.breakpoints.lg;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.size.desktop.xs;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.lineHeight.desktop.xs;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.lineHeight.desktop.xs;
});
var Body4Css = css(templateObject_18 || (templateObject_18 = __makeTemplateObject(["\n  font-size: ", ";\n  line-height: ", ";\n  min-height: ", ";\n  font-weight: ", ";\n\n  ", " {\n    font-size: ", ";\n    line-height: ", ";\n    min-height: ", ";\n  }\n\n  ", " {\n    font-size: ", ";\n    line-height: ", ";\n    min-height: ", ";\n  }\n"], ["\n  font-size: ", ";\n  line-height: ", ";\n  min-height: ", ";\n  font-weight: ", ";\n\n  ", " {\n    font-size: ", ";\n    line-height: ", ";\n    min-height: ", ";\n  }\n\n  ", " {\n    font-size: ", ";\n    line-height: ", ";\n    min-height: ", ";\n  }\n"])), function (_a) {
    var theme = _a.theme;
    return theme.fonts.size.mobile.xxs;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.lineHeight.mobile.xxs;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.lineHeight.mobile.xxs;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.weight.regular400;
}, function (_a) {
    var theme = _a.theme;
    return theme.breakpoints.sm;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.size.tablet.xxs;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.lineHeight.tablet.xxs;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.lineHeight.tablet.xxs;
}, function (_a) {
    var theme = _a.theme;
    return theme.breakpoints.lg;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.size.desktop.xxs;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.lineHeight.desktop.xxs;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.lineHeight.desktop.xxs;
});
var Body5Css = css(templateObject_19 || (templateObject_19 = __makeTemplateObject(["\n  font-size: ", ";\n  line-height: ", ";\n  min-height: ", ";\n  font-weight: ", ";\n\n  ", " {\n    font-size: ", ";\n    line-height: ", ";\n    min-height: ", ";\n  }\n\n  ", " {\n    font-size: ", ";\n    line-height: ", ";\n    min-height: ", ";\n  }\n"], ["\n  font-size: ", ";\n  line-height: ", ";\n  min-height: ", ";\n  font-weight: ", ";\n\n  ", " {\n    font-size: ", ";\n    line-height: ", ";\n    min-height: ", ";\n  }\n\n  ", " {\n    font-size: ", ";\n    line-height: ", ";\n    min-height: ", ";\n  }\n"])), function (_a) {
    var theme = _a.theme;
    return theme.fonts.size.mobile.xxxs;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.lineHeight.mobile.xxxs;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.lineHeight.mobile.xxxs;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.weight.regular400;
}, function (_a) {
    var theme = _a.theme;
    return theme.breakpoints.sm;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.size.tablet.xxxs;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.lineHeight.tablet.xxxs;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.lineHeight.tablet.xxxs;
}, function (_a) {
    var theme = _a.theme;
    return theme.breakpoints.md;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.size.desktop.xxxs;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.lineHeight.desktop.xxxs;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.lineHeight.desktop.xxxs;
});
var Header1 = styled.h1(templateObject_20 || (templateObject_20 = __makeTemplateObject(["\n  ", ";\n  ", ";\n"], ["\n  ", ";\n  ", ";\n"])), BaseText, Header1Css);
var Header2 = styled.h2(templateObject_21 || (templateObject_21 = __makeTemplateObject(["\n  ", ";\n  ", ";\n"], ["\n  ", ";\n  ", ";\n"])), BaseText, Header2Css);
var Header3$2 = styled.h3(templateObject_22 || (templateObject_22 = __makeTemplateObject(["\n  ", ";\n  ", "\n"], ["\n  ", ";\n  ", "\n"])), BaseText, Header3Css);
var Header4 = styled.h4(templateObject_23 || (templateObject_23 = __makeTemplateObject(["\n  ", ";\n  ", "\n"], ["\n  ", ";\n  ", "\n"])), BaseText, Header4Css);
var Header5 = styled.h5(templateObject_24 || (templateObject_24 = __makeTemplateObject(["\n  ", ";\n  ", ";\n"], ["\n  ", ";\n  ", ";\n"])), BaseText, Header5Css);
var Header6 = styled.h6(templateObject_25 || (templateObject_25 = __makeTemplateObject(["\n  ", ";\n  ", "\n"], ["\n  ", ";\n  ", "\n"])), BaseText, Header6Css);
var Header7 = styled.p(templateObject_26 || (templateObject_26 = __makeTemplateObject(["\n  ", ";\n  ", "\n"], ["\n  ", ";\n  ", "\n"])), BaseText, Header7Css);
var Header8 = styled.p(templateObject_27 || (templateObject_27 = __makeTemplateObject(["\n  ", ";\n  ", "\n"], ["\n  ", ";\n  ", "\n"])), BaseText, Header8Css);
var Caption1$1 = styled.p(templateObject_28 || (templateObject_28 = __makeTemplateObject(["\n  ", ";\n  ", "\n"], ["\n  ", ";\n  ", "\n"])), BaseText, Caption1Css);
var Caption2$2 = styled.p(templateObject_29 || (templateObject_29 = __makeTemplateObject(["\n  ", ";\n  ", "\n"], ["\n  ", ";\n  ", "\n"])), BaseText, Caption2Css);
var Caption3$1 = styled.p(templateObject_30 || (templateObject_30 = __makeTemplateObject(["\n  ", ";\n  ", "\n"], ["\n  ", ";\n  ", "\n"])), BaseText, Caption3Css);
var Caption4 = styled.p(templateObject_31 || (templateObject_31 = __makeTemplateObject(["\n  ", ";\n  ", "\n"], ["\n  ", ";\n  ", "\n"])), BaseText, Caption4Css);
var Caption5 = styled.p(templateObject_32 || (templateObject_32 = __makeTemplateObject(["\n  ", ";\n  ", "\n"], ["\n  ", ";\n  ", "\n"])), BaseText, Caption5Css);
var Body1$1 = styled.p(templateObject_33 || (templateObject_33 = __makeTemplateObject(["\n  ", ";\n  ", "\n"], ["\n  ", ";\n  ", "\n"])), BaseText, Body1Css);
var Body2 = styled.p(templateObject_34 || (templateObject_34 = __makeTemplateObject(["\n  ", ";\n  ", "\n"], ["\n  ", ";\n  ", "\n"])), BaseText, Body2Css);
var Body3$2 = styled.p(templateObject_35 || (templateObject_35 = __makeTemplateObject(["\n  ", ";\n  ", "\n"], ["\n  ", ";\n  ", "\n"])), BaseText, Body3Css);
var Body4 = styled.p(templateObject_36 || (templateObject_36 = __makeTemplateObject(["\n  ", ";\n  ", "\n"], ["\n  ", ";\n  ", "\n"])), BaseText, Body4Css);
var Body5 = styled.p(templateObject_37 || (templateObject_37 = __makeTemplateObject(["\n  ", ";\n  ", "\n"], ["\n  ", ";\n  ", "\n"])), BaseText, Body5Css);
var Link1 = styled.span(templateObject_38 || (templateObject_38 = __makeTemplateObject(["\n  ", ";\n  ", "\n"], ["\n  ", ";\n  ", "\n"])), BaseText, Caption3Css);
var Link2 = styled.span(templateObject_39 || (templateObject_39 = __makeTemplateObject(["\n  ", ";\n  ", "\n"], ["\n  ", ";\n  ", "\n"])), BaseText, Body3Css);
var Link3$1 = styled.span(templateObject_40 || (templateObject_40 = __makeTemplateObject(["\n  ", ";\n  ", "\n"], ["\n  ", ";\n  ", "\n"])), BaseText, Caption3Css);
var templateObject_1$1m, templateObject_2$19, templateObject_3$W, templateObject_4$L, templateObject_5$A, templateObject_6$v, templateObject_7$p, templateObject_8$i, templateObject_9$f, templateObject_10$a, templateObject_11$7, templateObject_12$5, templateObject_13$4, templateObject_14$1, templateObject_15$1, templateObject_16$1, templateObject_17$1, templateObject_18, templateObject_19, templateObject_20, templateObject_21, templateObject_22, templateObject_23, templateObject_24, templateObject_25, templateObject_26, templateObject_27, templateObject_28, templateObject_29, templateObject_30, templateObject_31, templateObject_32, templateObject_33, templateObject_34, templateObject_35, templateObject_36, templateObject_37, templateObject_38, templateObject_39, templateObject_40;

var Typography_style = /*#__PURE__*/Object.freeze({
    __proto__: null,
    BaseText: BaseText,
    Header1Css: Header1Css,
    Header2Css: Header2Css,
    Header3Css: Header3Css,
    Header4Css: Header4Css,
    Header5Css: Header5Css,
    Header6Css: Header6Css,
    Header7Css: Header7Css,
    Header8Css: Header8Css,
    Caption1Css: Caption1Css,
    Caption2Css: Caption2Css,
    Caption3Css: Caption3Css,
    Caption4Css: Caption4Css,
    Caption5Css: Caption5Css,
    Body1Css: Body1Css,
    Body2Css: Body2Css,
    Body3Css: Body3Css,
    Body4Css: Body4Css,
    Body5Css: Body5Css,
    Header1: Header1,
    Header2: Header2,
    Header3: Header3$2,
    Header4: Header4,
    Header5: Header5,
    Header6: Header6,
    Header7: Header7,
    Header8: Header8,
    Caption1: Caption1$1,
    Caption2: Caption2$2,
    Caption3: Caption3$1,
    Caption4: Caption4,
    Caption5: Caption5,
    Body1: Body1$1,
    Body2: Body2,
    Body3: Body3$2,
    Body4: Body4,
    Body5: Body5,
    Link1: Link1,
    Link2: Link2,
    Link3: Link3$1
});

var Card$1 = styled.span(templateObject_1$1l || (templateObject_1$1l = __makeTemplateObject(["\n  cursor: pointer;\n  border-radius: 14px;\n  background-color: ", ";\n  padding: 20px 17px;\n  width: 100%;\n  display: flex;\n  justify-content: space-between;\n"], ["\n  cursor: pointer;\n  border-radius: 14px;\n  background-color: ", ";\n  padding: 20px 17px;\n  width: 100%;\n  display: flex;\n  justify-content: space-between;\n"])), function (_a) {
    var theme = _a.theme;
    return theme.colors.card.backgroundColor;
});
var CardContainer$5 = styled.div(templateObject_2$18 || (templateObject_2$18 = __makeTemplateObject(["\n  display: flex;\n"], ["\n  display: flex;\n"])));
var TextWrapper$4 = styled.div(templateObject_3$V || (templateObject_3$V = __makeTemplateObject(["\n  display: flex;\n  flex-direction: column;\n  align-items: flex-start;\n  justify-content: flex-start;\n"], ["\n  display: flex;\n  flex-direction: column;\n  align-items: flex-start;\n  justify-content: flex-start;\n"])));
var Arrow$2 = styled.span(templateObject_4$K || (templateObject_4$K = __makeTemplateObject(["\n  svg {\n    width: 26px;\n    height: 26px;\n  }\n"], ["\n  svg {\n    width: 26px;\n    height: 26px;\n  }\n"])));
var Icon$3 = styled.div(templateObject_5$z || (templateObject_5$z = __makeTemplateObject(["\n  display: flex;\n  margin-right: 17px;\n  svg {\n    width: 26px;\n    height: 26px;\n  }\n"], ["\n  display: flex;\n  margin-right: 17px;\n  svg {\n    width: 26px;\n    height: 26px;\n  }\n"])));
var Link3 = styled(Link3$1)(templateObject_6$u || (templateObject_6$u = __makeTemplateObject(["\n  font-size: 18px;\n"], ["\n  font-size: 18px;\n"])));
var templateObject_1$1l, templateObject_2$18, templateObject_3$V, templateObject_4$K, templateObject_5$z, templateObject_6$u;

var Card = forwardRef(function (_a, ref) {
    var name = _a.name, icon = _a.icon, subDescription = _a.subDescription, arrow = _a.arrow, onClick = _a.onClick;
    return (jsxs(Card$1, __assign$1({ ref: ref, onClick: onClick }, { children: [jsxs(CardContainer$5, { children: [icon && (jsx(Icon$3, { children: jsx(Icon$4, { name: icon }) })), jsxs(TextWrapper$4, { children: [jsx(Link3, { children: name }), subDescription && (jsx(Body3$2, __assign$1({ color: colors.card.subDescription }, { children: subDescription })))] })] }), arrow && (jsx(Arrow$2, { children: jsx(Icon$4, { name: "ArrowRight" }) }))] })));
});

var CheckboxOn = function (_a) {
    var mode = _a.mode;
    return (jsx("svg", __assign$1({ width: "26", height: "26", viewBox: "0 0 26 26" }, { children: jsxs("g", __assign$1({ id: "ic/checkbox/on", stroke: "none", strokeWidth: "1", fill: "none", fillRule: "evenodd" }, { children: [jsx("rect", { id: "Rectangle", fill: colors.checkbox[mode].checked.rect, x: "1", y: "1", width: "24", height: "24", rx: "6" }), jsx("path", { d: "M16.7407434,8.84922676 C17.1001655,8.42990091 17.7314655,8.38133938 18.1507914,8.74076153 C18.5378614,9.07253582 18.6090139,9.63596933 18.3349748,10.0504994 L18.2592566,10.1508095 L12.2592566,17.1508095 C11.9072421,17.5614931 11.2997604,17.6131922 10.8849856,17.2886948 L10.7928932,17.2071249 L7.79289322,14.2071249 C7.40236893,13.8166006 7.40236893,13.1834356 7.79289322,12.7929113 C8.15337718,12.4324274 8.72060824,12.4046979 9.11289944,12.7097227 L9.20710678,12.7929113 L11.443,15.0280181 L16.7407434,8.84922676 Z", id: "Line", fill: colors.checkbox[mode].checked.check })] })) })));
};
var CheckboxOff = function (_a) {
    var mode = _a.mode;
    return (jsx("svg", __assign$1({ width: "26", height: "26", viewBox: "0 0 26 26" }, { children: jsx("g", __assign$1({ stroke: "none", strokeWidth: "1", fill: "none", fillRule: "evenodd" }, { children: jsx("path", { d: "M19,1 C22.3137085,1 25,3.6862915 25,7 L25,19 C25,22.3137085 22.3137085,25 19,25 L7,25 C3.6862915,25 1,22.3137085 1,19 L1,7 C1,3.6862915 3.6862915,1 7,1 L19,1 Z M19,3 L7,3 C4.85780461,3 3.10892112,4.68396847 3.00489531,6.80035966 L3,7 L3,19 C3,21.1421954 4.68396847,22.8910789 6.80035966,22.9951047 L7,23 L19,23 C21.1421954,23 22.8910789,21.3160315 22.9951047,19.1996403 L23,19 L23,7 C23,4.85780461 21.3160315,3.10892112 19.1996403,3.00489531 L19,3 Z", id: "Rectangle", fill: colors.checkbox[mode].unchecked }) })) })));
};

var CheckboxWrapper$1 = styled.div(templateObject_1$1k || (templateObject_1$1k = __makeTemplateObject(["\n  position: relative;\n"], ["\n  position: relative;\n"])));
var Checkbox$2 = styled.input(templateObject_2$17 || (templateObject_2$17 = __makeTemplateObject(["\n  display: none;\n"], ["\n  display: none;\n"])));
var templateObject_1$1k, templateObject_2$17;

var Checkbox = function (_a, ref) {
    var name = _a.name, handleChange = _a.handleChange, required = _a.required, checked = _a.checked, _b = _a.mode, mode = _b === void 0 ? 'dark' : _b, rest = __rest$1(_a, ["name", "handleChange", "required", "checked", "mode"]);
    var handleKeyPress = function (e) {
        e.preventDefault();
        if (e.code === 'Enter' || e.code === 'Space')
            handleChange();
    };
    return (jsxs(CheckboxWrapper$1, { children: [jsx("div", __assign$1({ ref: ref, role: "checkbox", "aria-checked": checked, tabIndex: 0, onClick: handleChange, onKeyPress: handleKeyPress, "aria-label": "Checkbox ".concat(checked ? 'checked' : 'not checked') }, { children: checked ? jsx(CheckboxOn, { mode: mode }) : jsx(CheckboxOff, { mode: mode }) })), jsx(Checkbox$2, __assign$1({ required: required, onChange: handleChange, name: name, type: "checkbox", checked: checked }, rest))] }));
};
var Checkbox$1 = forwardRef(Checkbox);

var Col$1 = styled.div(templateObject_2$16 || (templateObject_2$16 = __makeTemplateObject(["\n  box-sizing: border-box;\n  display: flex;\n  flex: 0 0 auto;\n  flex-direction: ", ";\n  ", "\n  ", "\n\n  ", "\n\n  ", "\n"], ["\n  box-sizing: border-box;\n  display: flex;\n  flex: 0 0 auto;\n  flex-direction: ", ";\n  ", "\n  ", "\n\n  ", "\n\n  ", "\n"])), function (_a) {
    var direction = _a.direction, reverse = _a.reverse;
    return (reverse ? "".concat(direction, "-reverse") : direction);
}, function (_a) {
    var align = _a.align;
    return align && "align-items: ".concat(align, ";");
}, function (_a) {
    var justify = _a.justify;
    return justify && "justify-content: ".concat(justify, ";");
}, BREAKPOINTS_NAMES.map(function (breakpoint) {
    return function (_a) {
        var theme = _a.theme, paddingTop = _a.paddingTop, paddingLeft = _a.paddingLeft;
        return css(templateObject_1$1j || (templateObject_1$1j = __makeTemplateObject(["\n      ", " {\n        padding-top: ", "px;\n        padding-left: ", "px;\n      }\n    "], ["\n      ", " {\n        padding-top: ", "px;\n        padding-left: ", "px;\n      }\n    "])), theme.breakpoints[breakpoint], paddingTop !== null && paddingTop !== void 0 ? paddingTop : theme.flexboxGrid.gutterSize[breakpoint], paddingLeft !== null && paddingLeft !== void 0 ? paddingLeft : theme.flexboxGrid.gutterSize[breakpoint]);
    };
}), function (props) {
    var gridSize = props.theme.flexboxGrid.gridSize;
    return Object.keys(props)
        .filter(function (k) { return BREAKPOINTS_NAMES.indexOf(k) !== -1; })
        .sort(function (k1, k2) { return BREAKPOINTS_NAMES.indexOf(k1) - BREAKPOINTS_NAMES.indexOf(k2); })
        .map(function (dimension) {
        return Number.isInteger(props[dimension])
            ? makeMediaQuery(dimension, "\n                flex-basis: ".concat((100 / gridSize[dimension]) * props[dimension], "%;\n                max-width: ").concat((100 / gridSize[dimension]) * props[dimension], "%;\n              "))
            : props[dimension] === true
                ? makeMediaQuery(dimension, "\n                flex-grow: 1;\n                flex-basis: 0;\n                max-width: 100%;\n              ")
                : props[dimension] === false
                    ? makeMediaQuery(dimension, 'display: none')
                    : makeMediaQuery(dimension, "\n                flex: 0 0 auto;\n                max-width: unset;\n              ");
    });
});
var templateObject_1$1j, templateObject_2$16;

var xsCols = [true, false, 1, 2, 3, 4];
var smCols = __spreadArray$1(__spreadArray$1([], xsCols, true), [5, 6, 7, 8], false);
__spreadArray$1(__spreadArray$1([], smCols, true), [9, 10, 11, 12], false);
var AlignItems;
(function (AlignItems) {
    AlignItems["Start"] = "flex-start";
    AlignItems["Center"] = "center";
    AlignItems["End"] = "flex-end";
    AlignItems["Baseline"] = "baseline";
    AlignItems["Stretch"] = "stretch";
})(AlignItems || (AlignItems = {}));
var JustifyContent;
(function (JustifyContent) {
    JustifyContent["Start"] = "flex-start";
    JustifyContent["Center"] = "center";
    JustifyContent["End"] = "flex-end";
    JustifyContent["SpaceBetween"] = "space-between";
    JustifyContent["SpaceAround"] = "space-around";
})(JustifyContent || (JustifyContent = {}));

var Col = function (_a) {
    var children = _a.children, _b = _a.direction, direction = _b === void 0 ? 'column' : _b, _c = _a.reverse, reverse = _c === void 0 ? false : _c, rest = __rest$1(_a, ["children", "direction", "reverse"]);
    return (jsx(Col$1, __assign$1({ direction: direction, reverse: reverse }, rest, { children: children })));
};
Col.Align = AlignItems;
Col.Justify = JustifyContent;

var ContainerWrapper = styled.div(templateObject_3$U || (templateObject_3$U = __makeTemplateObject(["\n  margin-right: auto;\n  margin-left: auto;\n\n  ", "\n"], ["\n  margin-right: auto;\n  margin-left: auto;\n\n  ", "\n"])), BREAKPOINTS_NAMES.map(function (breakpoint) {
    return function (_a) {
        var fluid = _a.fluid, bgColor = _a.bgColor, theme = _a.theme, withoutPadding = _a.withoutPadding, fullWidthOnMobile = _a.fullWidthOnMobile;
        return css(templateObject_2$15 || (templateObject_2$15 = __makeTemplateObject(["\n      ", " {\n        width: ", ";\n        padding-left: ", ";\n        padding-right: ", ";\n      }\n\n      ", "\n    "], ["\n      ", " {\n        width: ", ";\n        padding-left: ", ";\n        padding-right: ", ";\n      }\n\n      ", "\n    "])), theme.breakpoints[breakpoint], fluid ? '100%' : theme.flexboxGrid.container[breakpoint], withoutPadding || (fullWidthOnMobile && breakpoint === 'xs')
            ? 0
            : theme.flexboxGrid.outerMargin[breakpoint], withoutPadding || (fullWidthOnMobile && breakpoint === 'xs')
            ? 0
            : theme.flexboxGrid.outerMargin[breakpoint], bgColor && css(templateObject_1$1i || (templateObject_1$1i = __makeTemplateObject(["\n        background-color: ", ";\n      "], ["\n        background-color: ", ";\n      "])), bgColor));
    };
}));
var templateObject_1$1i, templateObject_2$15, templateObject_3$U;

var Container$1 = function (_a) {
    var children = _a.children, withSpacing = _a.withSpacing, rest = __rest$1(_a, ["children", "withSpacing"]);
    return (jsx(ContainerWrapper, __assign$1({}, rest, { children: jsxs(Fragment, { children: [children, " ", withSpacing && jsx(Gutter, { size: 100 })] }) })));
};

var commonjsGlobal = typeof globalThis !== 'undefined' ? globalThis : typeof window !== 'undefined' ? window : typeof global !== 'undefined' ? global : typeof self !== 'undefined' ? self : {};

function getDefaultExportFromCjs (x) {
	return x && x.__esModule && Object.prototype.hasOwnProperty.call(x, 'default') ? x['default'] : x;
}

function getAugmentedNamespace(n) {
  var f = n.default;
	if (typeof f == "function") {
		var a = function () {
			return f.apply(this, arguments);
		};
		a.prototype = f.prototype;
  } else a = {};
  Object.defineProperty(a, '__esModule', {value: true});
	Object.keys(n).forEach(function (k) {
		var d = Object.getOwnPropertyDescriptor(n, k);
		Object.defineProperty(a, k, d.get ? d : {
			enumerable: true,
			get: function () {
				return n[k];
			}
		});
	});
	return a;
}

var entry_nostyle = {};

var DatePicker$2 = {};

var propTypes$2 = {exports: {}};

var reactIs = {exports: {}};

var reactIs_production_min = {};

/** @license React v16.13.1
 * react-is.production.min.js
 *
 * Copyright (c) Facebook, Inc. and its affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */

var hasRequiredReactIs_production_min;

function requireReactIs_production_min () {
	if (hasRequiredReactIs_production_min) return reactIs_production_min;
	hasRequiredReactIs_production_min = 1;
var b="function"===typeof Symbol&&Symbol.for,c=b?Symbol.for("react.element"):60103,d=b?Symbol.for("react.portal"):60106,e=b?Symbol.for("react.fragment"):60107,f=b?Symbol.for("react.strict_mode"):60108,g=b?Symbol.for("react.profiler"):60114,h=b?Symbol.for("react.provider"):60109,k=b?Symbol.for("react.context"):60110,l=b?Symbol.for("react.async_mode"):60111,m=b?Symbol.for("react.concurrent_mode"):60111,n=b?Symbol.for("react.forward_ref"):60112,p=b?Symbol.for("react.suspense"):60113,q=b?
	Symbol.for("react.suspense_list"):60120,r=b?Symbol.for("react.memo"):60115,t=b?Symbol.for("react.lazy"):60116,v=b?Symbol.for("react.block"):60121,w=b?Symbol.for("react.fundamental"):60117,x=b?Symbol.for("react.responder"):60118,y=b?Symbol.for("react.scope"):60119;
	function z(a){if("object"===typeof a&&null!==a){var u=a.$$typeof;switch(u){case c:switch(a=a.type,a){case l:case m:case e:case g:case f:case p:return a;default:switch(a=a&&a.$$typeof,a){case k:case n:case t:case r:case h:return a;default:return u}}case d:return u}}}function A(a){return z(a)===m}reactIs_production_min.AsyncMode=l;reactIs_production_min.ConcurrentMode=m;reactIs_production_min.ContextConsumer=k;reactIs_production_min.ContextProvider=h;reactIs_production_min.Element=c;reactIs_production_min.ForwardRef=n;reactIs_production_min.Fragment=e;reactIs_production_min.Lazy=t;reactIs_production_min.Memo=r;reactIs_production_min.Portal=d;
	reactIs_production_min.Profiler=g;reactIs_production_min.StrictMode=f;reactIs_production_min.Suspense=p;reactIs_production_min.isAsyncMode=function(a){return A(a)||z(a)===l};reactIs_production_min.isConcurrentMode=A;reactIs_production_min.isContextConsumer=function(a){return z(a)===k};reactIs_production_min.isContextProvider=function(a){return z(a)===h};reactIs_production_min.isElement=function(a){return "object"===typeof a&&null!==a&&a.$$typeof===c};reactIs_production_min.isForwardRef=function(a){return z(a)===n};reactIs_production_min.isFragment=function(a){return z(a)===e};reactIs_production_min.isLazy=function(a){return z(a)===t};
	reactIs_production_min.isMemo=function(a){return z(a)===r};reactIs_production_min.isPortal=function(a){return z(a)===d};reactIs_production_min.isProfiler=function(a){return z(a)===g};reactIs_production_min.isStrictMode=function(a){return z(a)===f};reactIs_production_min.isSuspense=function(a){return z(a)===p};
	reactIs_production_min.isValidElementType=function(a){return "string"===typeof a||"function"===typeof a||a===e||a===m||a===g||a===f||a===p||a===q||"object"===typeof a&&null!==a&&(a.$$typeof===t||a.$$typeof===r||a.$$typeof===h||a.$$typeof===k||a.$$typeof===n||a.$$typeof===w||a.$$typeof===x||a.$$typeof===y||a.$$typeof===v)};reactIs_production_min.typeOf=z;
	return reactIs_production_min;
}

var reactIs_development = {};

/** @license React v16.13.1
 * react-is.development.js
 *
 * Copyright (c) Facebook, Inc. and its affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */

var hasRequiredReactIs_development;

function requireReactIs_development () {
	if (hasRequiredReactIs_development) return reactIs_development;
	hasRequiredReactIs_development = 1;



	if (process.env.NODE_ENV !== "production") {
	  (function() {

	// The Symbol used to tag the ReactElement-like types. If there is no native Symbol
	// nor polyfill, then a plain number is used for performance.
	var hasSymbol = typeof Symbol === 'function' && Symbol.for;
	var REACT_ELEMENT_TYPE = hasSymbol ? Symbol.for('react.element') : 0xeac7;
	var REACT_PORTAL_TYPE = hasSymbol ? Symbol.for('react.portal') : 0xeaca;
	var REACT_FRAGMENT_TYPE = hasSymbol ? Symbol.for('react.fragment') : 0xeacb;
	var REACT_STRICT_MODE_TYPE = hasSymbol ? Symbol.for('react.strict_mode') : 0xeacc;
	var REACT_PROFILER_TYPE = hasSymbol ? Symbol.for('react.profiler') : 0xead2;
	var REACT_PROVIDER_TYPE = hasSymbol ? Symbol.for('react.provider') : 0xeacd;
	var REACT_CONTEXT_TYPE = hasSymbol ? Symbol.for('react.context') : 0xeace; // TODO: We don't use AsyncMode or ConcurrentMode anymore. They were temporary
	// (unstable) APIs that have been removed. Can we remove the symbols?

	var REACT_ASYNC_MODE_TYPE = hasSymbol ? Symbol.for('react.async_mode') : 0xeacf;
	var REACT_CONCURRENT_MODE_TYPE = hasSymbol ? Symbol.for('react.concurrent_mode') : 0xeacf;
	var REACT_FORWARD_REF_TYPE = hasSymbol ? Symbol.for('react.forward_ref') : 0xead0;
	var REACT_SUSPENSE_TYPE = hasSymbol ? Symbol.for('react.suspense') : 0xead1;
	var REACT_SUSPENSE_LIST_TYPE = hasSymbol ? Symbol.for('react.suspense_list') : 0xead8;
	var REACT_MEMO_TYPE = hasSymbol ? Symbol.for('react.memo') : 0xead3;
	var REACT_LAZY_TYPE = hasSymbol ? Symbol.for('react.lazy') : 0xead4;
	var REACT_BLOCK_TYPE = hasSymbol ? Symbol.for('react.block') : 0xead9;
	var REACT_FUNDAMENTAL_TYPE = hasSymbol ? Symbol.for('react.fundamental') : 0xead5;
	var REACT_RESPONDER_TYPE = hasSymbol ? Symbol.for('react.responder') : 0xead6;
	var REACT_SCOPE_TYPE = hasSymbol ? Symbol.for('react.scope') : 0xead7;

	function isValidElementType(type) {
	  return typeof type === 'string' || typeof type === 'function' || // Note: its typeof might be other than 'symbol' or 'number' if it's a polyfill.
	  type === REACT_FRAGMENT_TYPE || type === REACT_CONCURRENT_MODE_TYPE || type === REACT_PROFILER_TYPE || type === REACT_STRICT_MODE_TYPE || type === REACT_SUSPENSE_TYPE || type === REACT_SUSPENSE_LIST_TYPE || typeof type === 'object' && type !== null && (type.$$typeof === REACT_LAZY_TYPE || type.$$typeof === REACT_MEMO_TYPE || type.$$typeof === REACT_PROVIDER_TYPE || type.$$typeof === REACT_CONTEXT_TYPE || type.$$typeof === REACT_FORWARD_REF_TYPE || type.$$typeof === REACT_FUNDAMENTAL_TYPE || type.$$typeof === REACT_RESPONDER_TYPE || type.$$typeof === REACT_SCOPE_TYPE || type.$$typeof === REACT_BLOCK_TYPE);
	}

	function typeOf(object) {
	  if (typeof object === 'object' && object !== null) {
	    var $$typeof = object.$$typeof;

	    switch ($$typeof) {
	      case REACT_ELEMENT_TYPE:
	        var type = object.type;

	        switch (type) {
	          case REACT_ASYNC_MODE_TYPE:
	          case REACT_CONCURRENT_MODE_TYPE:
	          case REACT_FRAGMENT_TYPE:
	          case REACT_PROFILER_TYPE:
	          case REACT_STRICT_MODE_TYPE:
	          case REACT_SUSPENSE_TYPE:
	            return type;

	          default:
	            var $$typeofType = type && type.$$typeof;

	            switch ($$typeofType) {
	              case REACT_CONTEXT_TYPE:
	              case REACT_FORWARD_REF_TYPE:
	              case REACT_LAZY_TYPE:
	              case REACT_MEMO_TYPE:
	              case REACT_PROVIDER_TYPE:
	                return $$typeofType;

	              default:
	                return $$typeof;
	            }

	        }

	      case REACT_PORTAL_TYPE:
	        return $$typeof;
	    }
	  }

	  return undefined;
	} // AsyncMode is deprecated along with isAsyncMode

	var AsyncMode = REACT_ASYNC_MODE_TYPE;
	var ConcurrentMode = REACT_CONCURRENT_MODE_TYPE;
	var ContextConsumer = REACT_CONTEXT_TYPE;
	var ContextProvider = REACT_PROVIDER_TYPE;
	var Element = REACT_ELEMENT_TYPE;
	var ForwardRef = REACT_FORWARD_REF_TYPE;
	var Fragment = REACT_FRAGMENT_TYPE;
	var Lazy = REACT_LAZY_TYPE;
	var Memo = REACT_MEMO_TYPE;
	var Portal = REACT_PORTAL_TYPE;
	var Profiler = REACT_PROFILER_TYPE;
	var StrictMode = REACT_STRICT_MODE_TYPE;
	var Suspense = REACT_SUSPENSE_TYPE;
	var hasWarnedAboutDeprecatedIsAsyncMode = false; // AsyncMode should be deprecated

	function isAsyncMode(object) {
	  {
	    if (!hasWarnedAboutDeprecatedIsAsyncMode) {
	      hasWarnedAboutDeprecatedIsAsyncMode = true; // Using console['warn'] to evade Babel and ESLint

	      console['warn']('The ReactIs.isAsyncMode() alias has been deprecated, ' + 'and will be removed in React 17+. Update your code to use ' + 'ReactIs.isConcurrentMode() instead. It has the exact same API.');
	    }
	  }

	  return isConcurrentMode(object) || typeOf(object) === REACT_ASYNC_MODE_TYPE;
	}
	function isConcurrentMode(object) {
	  return typeOf(object) === REACT_CONCURRENT_MODE_TYPE;
	}
	function isContextConsumer(object) {
	  return typeOf(object) === REACT_CONTEXT_TYPE;
	}
	function isContextProvider(object) {
	  return typeOf(object) === REACT_PROVIDER_TYPE;
	}
	function isElement(object) {
	  return typeof object === 'object' && object !== null && object.$$typeof === REACT_ELEMENT_TYPE;
	}
	function isForwardRef(object) {
	  return typeOf(object) === REACT_FORWARD_REF_TYPE;
	}
	function isFragment(object) {
	  return typeOf(object) === REACT_FRAGMENT_TYPE;
	}
	function isLazy(object) {
	  return typeOf(object) === REACT_LAZY_TYPE;
	}
	function isMemo(object) {
	  return typeOf(object) === REACT_MEMO_TYPE;
	}
	function isPortal(object) {
	  return typeOf(object) === REACT_PORTAL_TYPE;
	}
	function isProfiler(object) {
	  return typeOf(object) === REACT_PROFILER_TYPE;
	}
	function isStrictMode(object) {
	  return typeOf(object) === REACT_STRICT_MODE_TYPE;
	}
	function isSuspense(object) {
	  return typeOf(object) === REACT_SUSPENSE_TYPE;
	}

	reactIs_development.AsyncMode = AsyncMode;
	reactIs_development.ConcurrentMode = ConcurrentMode;
	reactIs_development.ContextConsumer = ContextConsumer;
	reactIs_development.ContextProvider = ContextProvider;
	reactIs_development.Element = Element;
	reactIs_development.ForwardRef = ForwardRef;
	reactIs_development.Fragment = Fragment;
	reactIs_development.Lazy = Lazy;
	reactIs_development.Memo = Memo;
	reactIs_development.Portal = Portal;
	reactIs_development.Profiler = Profiler;
	reactIs_development.StrictMode = StrictMode;
	reactIs_development.Suspense = Suspense;
	reactIs_development.isAsyncMode = isAsyncMode;
	reactIs_development.isConcurrentMode = isConcurrentMode;
	reactIs_development.isContextConsumer = isContextConsumer;
	reactIs_development.isContextProvider = isContextProvider;
	reactIs_development.isElement = isElement;
	reactIs_development.isForwardRef = isForwardRef;
	reactIs_development.isFragment = isFragment;
	reactIs_development.isLazy = isLazy;
	reactIs_development.isMemo = isMemo;
	reactIs_development.isPortal = isPortal;
	reactIs_development.isProfiler = isProfiler;
	reactIs_development.isStrictMode = isStrictMode;
	reactIs_development.isSuspense = isSuspense;
	reactIs_development.isValidElementType = isValidElementType;
	reactIs_development.typeOf = typeOf;
	  })();
	}
	return reactIs_development;
}

var hasRequiredReactIs;

function requireReactIs () {
	if (hasRequiredReactIs) return reactIs.exports;
	hasRequiredReactIs = 1;
	(function (module) {

		if (process.env.NODE_ENV === 'production') {
		  module.exports = requireReactIs_production_min();
		} else {
		  module.exports = requireReactIs_development();
		}
} (reactIs));
	return reactIs.exports;
}

/*
object-assign
(c) Sindre Sorhus
@license MIT
*/

var objectAssign;
var hasRequiredObjectAssign;

function requireObjectAssign () {
	if (hasRequiredObjectAssign) return objectAssign;
	hasRequiredObjectAssign = 1;
	/* eslint-disable no-unused-vars */
	var getOwnPropertySymbols = Object.getOwnPropertySymbols;
	var hasOwnProperty = Object.prototype.hasOwnProperty;
	var propIsEnumerable = Object.prototype.propertyIsEnumerable;

	function toObject(val) {
		if (val === null || val === undefined) {
			throw new TypeError('Object.assign cannot be called with null or undefined');
		}

		return Object(val);
	}

	function shouldUseNative() {
		try {
			if (!Object.assign) {
				return false;
			}

			// Detect buggy property enumeration order in older V8 versions.

			// https://bugs.chromium.org/p/v8/issues/detail?id=4118
			var test1 = new String('abc');  // eslint-disable-line no-new-wrappers
			test1[5] = 'de';
			if (Object.getOwnPropertyNames(test1)[0] === '5') {
				return false;
			}

			// https://bugs.chromium.org/p/v8/issues/detail?id=3056
			var test2 = {};
			for (var i = 0; i < 10; i++) {
				test2['_' + String.fromCharCode(i)] = i;
			}
			var order2 = Object.getOwnPropertyNames(test2).map(function (n) {
				return test2[n];
			});
			if (order2.join('') !== '0123456789') {
				return false;
			}

			// https://bugs.chromium.org/p/v8/issues/detail?id=3056
			var test3 = {};
			'abcdefghijklmnopqrst'.split('').forEach(function (letter) {
				test3[letter] = letter;
			});
			if (Object.keys(Object.assign({}, test3)).join('') !==
					'abcdefghijklmnopqrst') {
				return false;
			}

			return true;
		} catch (err) {
			// We don't expect any of the above to throw, but better to be safe.
			return false;
		}
	}

	objectAssign = shouldUseNative() ? Object.assign : function (target, source) {
		var from;
		var to = toObject(target);
		var symbols;

		for (var s = 1; s < arguments.length; s++) {
			from = Object(arguments[s]);

			for (var key in from) {
				if (hasOwnProperty.call(from, key)) {
					to[key] = from[key];
				}
			}

			if (getOwnPropertySymbols) {
				symbols = getOwnPropertySymbols(from);
				for (var i = 0; i < symbols.length; i++) {
					if (propIsEnumerable.call(from, symbols[i])) {
						to[symbols[i]] = from[symbols[i]];
					}
				}
			}
		}

		return to;
	};
	return objectAssign;
}

/**
 * Copyright (c) 2013-present, Facebook, Inc.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */

var ReactPropTypesSecret_1;
var hasRequiredReactPropTypesSecret;

function requireReactPropTypesSecret () {
	if (hasRequiredReactPropTypesSecret) return ReactPropTypesSecret_1;
	hasRequiredReactPropTypesSecret = 1;

	var ReactPropTypesSecret = 'SECRET_DO_NOT_PASS_THIS_OR_YOU_WILL_BE_FIRED';

	ReactPropTypesSecret_1 = ReactPropTypesSecret;
	return ReactPropTypesSecret_1;
}

var has;
var hasRequiredHas;

function requireHas () {
	if (hasRequiredHas) return has;
	hasRequiredHas = 1;
	has = Function.call.bind(Object.prototype.hasOwnProperty);
	return has;
}

/**
 * Copyright (c) 2013-present, Facebook, Inc.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */

var checkPropTypes_1;
var hasRequiredCheckPropTypes;

function requireCheckPropTypes () {
	if (hasRequiredCheckPropTypes) return checkPropTypes_1;
	hasRequiredCheckPropTypes = 1;

	var printWarning = function() {};

	if (process.env.NODE_ENV !== 'production') {
	  var ReactPropTypesSecret = requireReactPropTypesSecret();
	  var loggedTypeFailures = {};
	  var has = requireHas();

	  printWarning = function(text) {
	    var message = 'Warning: ' + text;
	    if (typeof console !== 'undefined') {
	      console.error(message);
	    }
	    try {
	      // --- Welcome to debugging React ---
	      // This error was thrown as a convenience so that you can use this stack
	      // to find the callsite that caused this warning to fire.
	      throw new Error(message);
	    } catch (x) { /**/ }
	  };
	}

	/**
	 * Assert that the values match with the type specs.
	 * Error messages are memorized and will only be shown once.
	 *
	 * @param {object} typeSpecs Map of name to a ReactPropType
	 * @param {object} values Runtime values that need to be type-checked
	 * @param {string} location e.g. "prop", "context", "child context"
	 * @param {string} componentName Name of the component for error messages.
	 * @param {?Function} getStack Returns the component stack.
	 * @private
	 */
	function checkPropTypes(typeSpecs, values, location, componentName, getStack) {
	  if (process.env.NODE_ENV !== 'production') {
	    for (var typeSpecName in typeSpecs) {
	      if (has(typeSpecs, typeSpecName)) {
	        var error;
	        // Prop type validation may throw. In case they do, we don't want to
	        // fail the render phase where it didn't fail before. So we log it.
	        // After these have been cleaned up, we'll let them throw.
	        try {
	          // This is intentionally an invariant that gets caught. It's the same
	          // behavior as without this statement except with a better message.
	          if (typeof typeSpecs[typeSpecName] !== 'function') {
	            var err = Error(
	              (componentName || 'React class') + ': ' + location + ' type `' + typeSpecName + '` is invalid; ' +
	              'it must be a function, usually from the `prop-types` package, but received `' + typeof typeSpecs[typeSpecName] + '`.' +
	              'This often happens because of typos such as `PropTypes.function` instead of `PropTypes.func`.'
	            );
	            err.name = 'Invariant Violation';
	            throw err;
	          }
	          error = typeSpecs[typeSpecName](values, typeSpecName, componentName, location, null, ReactPropTypesSecret);
	        } catch (ex) {
	          error = ex;
	        }
	        if (error && !(error instanceof Error)) {
	          printWarning(
	            (componentName || 'React class') + ': type specification of ' +
	            location + ' `' + typeSpecName + '` is invalid; the type checker ' +
	            'function must return `null` or an `Error` but returned a ' + typeof error + '. ' +
	            'You may have forgotten to pass an argument to the type checker ' +
	            'creator (arrayOf, instanceOf, objectOf, oneOf, oneOfType, and ' +
	            'shape all require an argument).'
	          );
	        }
	        if (error instanceof Error && !(error.message in loggedTypeFailures)) {
	          // Only monitor this failure once because there tends to be a lot of the
	          // same error.
	          loggedTypeFailures[error.message] = true;

	          var stack = getStack ? getStack() : '';

	          printWarning(
	            'Failed ' + location + ' type: ' + error.message + (stack != null ? stack : '')
	          );
	        }
	      }
	    }
	  }
	}

	/**
	 * Resets warning cache when testing.
	 *
	 * @private
	 */
	checkPropTypes.resetWarningCache = function() {
	  if (process.env.NODE_ENV !== 'production') {
	    loggedTypeFailures = {};
	  }
	};

	checkPropTypes_1 = checkPropTypes;
	return checkPropTypes_1;
}

/**
 * Copyright (c) 2013-present, Facebook, Inc.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */

var factoryWithTypeCheckers;
var hasRequiredFactoryWithTypeCheckers;

function requireFactoryWithTypeCheckers () {
	if (hasRequiredFactoryWithTypeCheckers) return factoryWithTypeCheckers;
	hasRequiredFactoryWithTypeCheckers = 1;

	var ReactIs = requireReactIs();
	var assign = requireObjectAssign();

	var ReactPropTypesSecret = requireReactPropTypesSecret();
	var has = requireHas();
	var checkPropTypes = requireCheckPropTypes();

	var printWarning = function() {};

	if (process.env.NODE_ENV !== 'production') {
	  printWarning = function(text) {
	    var message = 'Warning: ' + text;
	    if (typeof console !== 'undefined') {
	      console.error(message);
	    }
	    try {
	      // --- Welcome to debugging React ---
	      // This error was thrown as a convenience so that you can use this stack
	      // to find the callsite that caused this warning to fire.
	      throw new Error(message);
	    } catch (x) {}
	  };
	}

	function emptyFunctionThatReturnsNull() {
	  return null;
	}

	factoryWithTypeCheckers = function(isValidElement, throwOnDirectAccess) {
	  /* global Symbol */
	  var ITERATOR_SYMBOL = typeof Symbol === 'function' && Symbol.iterator;
	  var FAUX_ITERATOR_SYMBOL = '@@iterator'; // Before Symbol spec.

	  /**
	   * Returns the iterator method function contained on the iterable object.
	   *
	   * Be sure to invoke the function with the iterable as context:
	   *
	   *     var iteratorFn = getIteratorFn(myIterable);
	   *     if (iteratorFn) {
	   *       var iterator = iteratorFn.call(myIterable);
	   *       ...
	   *     }
	   *
	   * @param {?object} maybeIterable
	   * @return {?function}
	   */
	  function getIteratorFn(maybeIterable) {
	    var iteratorFn = maybeIterable && (ITERATOR_SYMBOL && maybeIterable[ITERATOR_SYMBOL] || maybeIterable[FAUX_ITERATOR_SYMBOL]);
	    if (typeof iteratorFn === 'function') {
	      return iteratorFn;
	    }
	  }

	  /**
	   * Collection of methods that allow declaration and validation of props that are
	   * supplied to React components. Example usage:
	   *
	   *   var Props = require('ReactPropTypes');
	   *   var MyArticle = React.createClass({
	   *     propTypes: {
	   *       // An optional string prop named "description".
	   *       description: Props.string,
	   *
	   *       // A required enum prop named "category".
	   *       category: Props.oneOf(['News','Photos']).isRequired,
	   *
	   *       // A prop named "dialog" that requires an instance of Dialog.
	   *       dialog: Props.instanceOf(Dialog).isRequired
	   *     },
	   *     render: function() { ... }
	   *   });
	   *
	   * A more formal specification of how these methods are used:
	   *
	   *   type := array|bool|func|object|number|string|oneOf([...])|instanceOf(...)
	   *   decl := ReactPropTypes.{type}(.isRequired)?
	   *
	   * Each and every declaration produces a function with the same signature. This
	   * allows the creation of custom validation functions. For example:
	   *
	   *  var MyLink = React.createClass({
	   *    propTypes: {
	   *      // An optional string or URI prop named "href".
	   *      href: function(props, propName, componentName) {
	   *        var propValue = props[propName];
	   *        if (propValue != null && typeof propValue !== 'string' &&
	   *            !(propValue instanceof URI)) {
	   *          return new Error(
	   *            'Expected a string or an URI for ' + propName + ' in ' +
	   *            componentName
	   *          );
	   *        }
	   *      }
	   *    },
	   *    render: function() {...}
	   *  });
	   *
	   * @internal
	   */

	  var ANONYMOUS = '<<anonymous>>';

	  // Important!
	  // Keep this list in sync with production version in `./factoryWithThrowingShims.js`.
	  var ReactPropTypes = {
	    array: createPrimitiveTypeChecker('array'),
	    bigint: createPrimitiveTypeChecker('bigint'),
	    bool: createPrimitiveTypeChecker('boolean'),
	    func: createPrimitiveTypeChecker('function'),
	    number: createPrimitiveTypeChecker('number'),
	    object: createPrimitiveTypeChecker('object'),
	    string: createPrimitiveTypeChecker('string'),
	    symbol: createPrimitiveTypeChecker('symbol'),

	    any: createAnyTypeChecker(),
	    arrayOf: createArrayOfTypeChecker,
	    element: createElementTypeChecker(),
	    elementType: createElementTypeTypeChecker(),
	    instanceOf: createInstanceTypeChecker,
	    node: createNodeChecker(),
	    objectOf: createObjectOfTypeChecker,
	    oneOf: createEnumTypeChecker,
	    oneOfType: createUnionTypeChecker,
	    shape: createShapeTypeChecker,
	    exact: createStrictShapeTypeChecker,
	  };

	  /**
	   * inlined Object.is polyfill to avoid requiring consumers ship their own
	   * https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Object/is
	   */
	  /*eslint-disable no-self-compare*/
	  function is(x, y) {
	    // SameValue algorithm
	    if (x === y) {
	      // Steps 1-5, 7-10
	      // Steps 6.b-6.e: +0 != -0
	      return x !== 0 || 1 / x === 1 / y;
	    } else {
	      // Step 6.a: NaN == NaN
	      return x !== x && y !== y;
	    }
	  }
	  /*eslint-enable no-self-compare*/

	  /**
	   * We use an Error-like object for backward compatibility as people may call
	   * PropTypes directly and inspect their output. However, we don't use real
	   * Errors anymore. We don't inspect their stack anyway, and creating them
	   * is prohibitively expensive if they are created too often, such as what
	   * happens in oneOfType() for any type before the one that matched.
	   */
	  function PropTypeError(message, data) {
	    this.message = message;
	    this.data = data && typeof data === 'object' ? data: {};
	    this.stack = '';
	  }
	  // Make `instanceof Error` still work for returned errors.
	  PropTypeError.prototype = Error.prototype;

	  function createChainableTypeChecker(validate) {
	    if (process.env.NODE_ENV !== 'production') {
	      var manualPropTypeCallCache = {};
	      var manualPropTypeWarningCount = 0;
	    }
	    function checkType(isRequired, props, propName, componentName, location, propFullName, secret) {
	      componentName = componentName || ANONYMOUS;
	      propFullName = propFullName || propName;

	      if (secret !== ReactPropTypesSecret) {
	        if (throwOnDirectAccess) {
	          // New behavior only for users of `prop-types` package
	          var err = new Error(
	            'Calling PropTypes validators directly is not supported by the `prop-types` package. ' +
	            'Use `PropTypes.checkPropTypes()` to call them. ' +
	            'Read more at http://fb.me/use-check-prop-types'
	          );
	          err.name = 'Invariant Violation';
	          throw err;
	        } else if (process.env.NODE_ENV !== 'production' && typeof console !== 'undefined') {
	          // Old behavior for people using React.PropTypes
	          var cacheKey = componentName + ':' + propName;
	          if (
	            !manualPropTypeCallCache[cacheKey] &&
	            // Avoid spamming the console because they are often not actionable except for lib authors
	            manualPropTypeWarningCount < 3
	          ) {
	            printWarning(
	              'You are manually calling a React.PropTypes validation ' +
	              'function for the `' + propFullName + '` prop on `' + componentName + '`. This is deprecated ' +
	              'and will throw in the standalone `prop-types` package. ' +
	              'You may be seeing this warning due to a third-party PropTypes ' +
	              'library. See https://fb.me/react-warning-dont-call-proptypes ' + 'for details.'
	            );
	            manualPropTypeCallCache[cacheKey] = true;
	            manualPropTypeWarningCount++;
	          }
	        }
	      }
	      if (props[propName] == null) {
	        if (isRequired) {
	          if (props[propName] === null) {
	            return new PropTypeError('The ' + location + ' `' + propFullName + '` is marked as required ' + ('in `' + componentName + '`, but its value is `null`.'));
	          }
	          return new PropTypeError('The ' + location + ' `' + propFullName + '` is marked as required in ' + ('`' + componentName + '`, but its value is `undefined`.'));
	        }
	        return null;
	      } else {
	        return validate(props, propName, componentName, location, propFullName);
	      }
	    }

	    var chainedCheckType = checkType.bind(null, false);
	    chainedCheckType.isRequired = checkType.bind(null, true);

	    return chainedCheckType;
	  }

	  function createPrimitiveTypeChecker(expectedType) {
	    function validate(props, propName, componentName, location, propFullName, secret) {
	      var propValue = props[propName];
	      var propType = getPropType(propValue);
	      if (propType !== expectedType) {
	        // `propValue` being instance of, say, date/regexp, pass the 'object'
	        // check, but we can offer a more precise error message here rather than
	        // 'of type `object`'.
	        var preciseType = getPreciseType(propValue);

	        return new PropTypeError(
	          'Invalid ' + location + ' `' + propFullName + '` of type ' + ('`' + preciseType + '` supplied to `' + componentName + '`, expected ') + ('`' + expectedType + '`.'),
	          {expectedType: expectedType}
	        );
	      }
	      return null;
	    }
	    return createChainableTypeChecker(validate);
	  }

	  function createAnyTypeChecker() {
	    return createChainableTypeChecker(emptyFunctionThatReturnsNull);
	  }

	  function createArrayOfTypeChecker(typeChecker) {
	    function validate(props, propName, componentName, location, propFullName) {
	      if (typeof typeChecker !== 'function') {
	        return new PropTypeError('Property `' + propFullName + '` of component `' + componentName + '` has invalid PropType notation inside arrayOf.');
	      }
	      var propValue = props[propName];
	      if (!Array.isArray(propValue)) {
	        var propType = getPropType(propValue);
	        return new PropTypeError('Invalid ' + location + ' `' + propFullName + '` of type ' + ('`' + propType + '` supplied to `' + componentName + '`, expected an array.'));
	      }
	      for (var i = 0; i < propValue.length; i++) {
	        var error = typeChecker(propValue, i, componentName, location, propFullName + '[' + i + ']', ReactPropTypesSecret);
	        if (error instanceof Error) {
	          return error;
	        }
	      }
	      return null;
	    }
	    return createChainableTypeChecker(validate);
	  }

	  function createElementTypeChecker() {
	    function validate(props, propName, componentName, location, propFullName) {
	      var propValue = props[propName];
	      if (!isValidElement(propValue)) {
	        var propType = getPropType(propValue);
	        return new PropTypeError('Invalid ' + location + ' `' + propFullName + '` of type ' + ('`' + propType + '` supplied to `' + componentName + '`, expected a single ReactElement.'));
	      }
	      return null;
	    }
	    return createChainableTypeChecker(validate);
	  }

	  function createElementTypeTypeChecker() {
	    function validate(props, propName, componentName, location, propFullName) {
	      var propValue = props[propName];
	      if (!ReactIs.isValidElementType(propValue)) {
	        var propType = getPropType(propValue);
	        return new PropTypeError('Invalid ' + location + ' `' + propFullName + '` of type ' + ('`' + propType + '` supplied to `' + componentName + '`, expected a single ReactElement type.'));
	      }
	      return null;
	    }
	    return createChainableTypeChecker(validate);
	  }

	  function createInstanceTypeChecker(expectedClass) {
	    function validate(props, propName, componentName, location, propFullName) {
	      if (!(props[propName] instanceof expectedClass)) {
	        var expectedClassName = expectedClass.name || ANONYMOUS;
	        var actualClassName = getClassName(props[propName]);
	        return new PropTypeError('Invalid ' + location + ' `' + propFullName + '` of type ' + ('`' + actualClassName + '` supplied to `' + componentName + '`, expected ') + ('instance of `' + expectedClassName + '`.'));
	      }
	      return null;
	    }
	    return createChainableTypeChecker(validate);
	  }

	  function createEnumTypeChecker(expectedValues) {
	    if (!Array.isArray(expectedValues)) {
	      if (process.env.NODE_ENV !== 'production') {
	        if (arguments.length > 1) {
	          printWarning(
	            'Invalid arguments supplied to oneOf, expected an array, got ' + arguments.length + ' arguments. ' +
	            'A common mistake is to write oneOf(x, y, z) instead of oneOf([x, y, z]).'
	          );
	        } else {
	          printWarning('Invalid argument supplied to oneOf, expected an array.');
	        }
	      }
	      return emptyFunctionThatReturnsNull;
	    }

	    function validate(props, propName, componentName, location, propFullName) {
	      var propValue = props[propName];
	      for (var i = 0; i < expectedValues.length; i++) {
	        if (is(propValue, expectedValues[i])) {
	          return null;
	        }
	      }

	      var valuesString = JSON.stringify(expectedValues, function replacer(key, value) {
	        var type = getPreciseType(value);
	        if (type === 'symbol') {
	          return String(value);
	        }
	        return value;
	      });
	      return new PropTypeError('Invalid ' + location + ' `' + propFullName + '` of value `' + String(propValue) + '` ' + ('supplied to `' + componentName + '`, expected one of ' + valuesString + '.'));
	    }
	    return createChainableTypeChecker(validate);
	  }

	  function createObjectOfTypeChecker(typeChecker) {
	    function validate(props, propName, componentName, location, propFullName) {
	      if (typeof typeChecker !== 'function') {
	        return new PropTypeError('Property `' + propFullName + '` of component `' + componentName + '` has invalid PropType notation inside objectOf.');
	      }
	      var propValue = props[propName];
	      var propType = getPropType(propValue);
	      if (propType !== 'object') {
	        return new PropTypeError('Invalid ' + location + ' `' + propFullName + '` of type ' + ('`' + propType + '` supplied to `' + componentName + '`, expected an object.'));
	      }
	      for (var key in propValue) {
	        if (has(propValue, key)) {
	          var error = typeChecker(propValue, key, componentName, location, propFullName + '.' + key, ReactPropTypesSecret);
	          if (error instanceof Error) {
	            return error;
	          }
	        }
	      }
	      return null;
	    }
	    return createChainableTypeChecker(validate);
	  }

	  function createUnionTypeChecker(arrayOfTypeCheckers) {
	    if (!Array.isArray(arrayOfTypeCheckers)) {
	      process.env.NODE_ENV !== 'production' ? printWarning('Invalid argument supplied to oneOfType, expected an instance of array.') : void 0;
	      return emptyFunctionThatReturnsNull;
	    }

	    for (var i = 0; i < arrayOfTypeCheckers.length; i++) {
	      var checker = arrayOfTypeCheckers[i];
	      if (typeof checker !== 'function') {
	        printWarning(
	          'Invalid argument supplied to oneOfType. Expected an array of check functions, but ' +
	          'received ' + getPostfixForTypeWarning(checker) + ' at index ' + i + '.'
	        );
	        return emptyFunctionThatReturnsNull;
	      }
	    }

	    function validate(props, propName, componentName, location, propFullName) {
	      var expectedTypes = [];
	      for (var i = 0; i < arrayOfTypeCheckers.length; i++) {
	        var checker = arrayOfTypeCheckers[i];
	        var checkerResult = checker(props, propName, componentName, location, propFullName, ReactPropTypesSecret);
	        if (checkerResult == null) {
	          return null;
	        }
	        if (checkerResult.data && has(checkerResult.data, 'expectedType')) {
	          expectedTypes.push(checkerResult.data.expectedType);
	        }
	      }
	      var expectedTypesMessage = (expectedTypes.length > 0) ? ', expected one of type [' + expectedTypes.join(', ') + ']': '';
	      return new PropTypeError('Invalid ' + location + ' `' + propFullName + '` supplied to ' + ('`' + componentName + '`' + expectedTypesMessage + '.'));
	    }
	    return createChainableTypeChecker(validate);
	  }

	  function createNodeChecker() {
	    function validate(props, propName, componentName, location, propFullName) {
	      if (!isNode(props[propName])) {
	        return new PropTypeError('Invalid ' + location + ' `' + propFullName + '` supplied to ' + ('`' + componentName + '`, expected a ReactNode.'));
	      }
	      return null;
	    }
	    return createChainableTypeChecker(validate);
	  }

	  function invalidValidatorError(componentName, location, propFullName, key, type) {
	    return new PropTypeError(
	      (componentName || 'React class') + ': ' + location + ' type `' + propFullName + '.' + key + '` is invalid; ' +
	      'it must be a function, usually from the `prop-types` package, but received `' + type + '`.'
	    );
	  }

	  function createShapeTypeChecker(shapeTypes) {
	    function validate(props, propName, componentName, location, propFullName) {
	      var propValue = props[propName];
	      var propType = getPropType(propValue);
	      if (propType !== 'object') {
	        return new PropTypeError('Invalid ' + location + ' `' + propFullName + '` of type `' + propType + '` ' + ('supplied to `' + componentName + '`, expected `object`.'));
	      }
	      for (var key in shapeTypes) {
	        var checker = shapeTypes[key];
	        if (typeof checker !== 'function') {
	          return invalidValidatorError(componentName, location, propFullName, key, getPreciseType(checker));
	        }
	        var error = checker(propValue, key, componentName, location, propFullName + '.' + key, ReactPropTypesSecret);
	        if (error) {
	          return error;
	        }
	      }
	      return null;
	    }
	    return createChainableTypeChecker(validate);
	  }

	  function createStrictShapeTypeChecker(shapeTypes) {
	    function validate(props, propName, componentName, location, propFullName) {
	      var propValue = props[propName];
	      var propType = getPropType(propValue);
	      if (propType !== 'object') {
	        return new PropTypeError('Invalid ' + location + ' `' + propFullName + '` of type `' + propType + '` ' + ('supplied to `' + componentName + '`, expected `object`.'));
	      }
	      // We need to check all keys in case some are required but missing from props.
	      var allKeys = assign({}, props[propName], shapeTypes);
	      for (var key in allKeys) {
	        var checker = shapeTypes[key];
	        if (has(shapeTypes, key) && typeof checker !== 'function') {
	          return invalidValidatorError(componentName, location, propFullName, key, getPreciseType(checker));
	        }
	        if (!checker) {
	          return new PropTypeError(
	            'Invalid ' + location + ' `' + propFullName + '` key `' + key + '` supplied to `' + componentName + '`.' +
	            '\nBad object: ' + JSON.stringify(props[propName], null, '  ') +
	            '\nValid keys: ' + JSON.stringify(Object.keys(shapeTypes), null, '  ')
	          );
	        }
	        var error = checker(propValue, key, componentName, location, propFullName + '.' + key, ReactPropTypesSecret);
	        if (error) {
	          return error;
	        }
	      }
	      return null;
	    }

	    return createChainableTypeChecker(validate);
	  }

	  function isNode(propValue) {
	    switch (typeof propValue) {
	      case 'number':
	      case 'string':
	      case 'undefined':
	        return true;
	      case 'boolean':
	        return !propValue;
	      case 'object':
	        if (Array.isArray(propValue)) {
	          return propValue.every(isNode);
	        }
	        if (propValue === null || isValidElement(propValue)) {
	          return true;
	        }

	        var iteratorFn = getIteratorFn(propValue);
	        if (iteratorFn) {
	          var iterator = iteratorFn.call(propValue);
	          var step;
	          if (iteratorFn !== propValue.entries) {
	            while (!(step = iterator.next()).done) {
	              if (!isNode(step.value)) {
	                return false;
	              }
	            }
	          } else {
	            // Iterator will provide entry [k,v] tuples rather than values.
	            while (!(step = iterator.next()).done) {
	              var entry = step.value;
	              if (entry) {
	                if (!isNode(entry[1])) {
	                  return false;
	                }
	              }
	            }
	          }
	        } else {
	          return false;
	        }

	        return true;
	      default:
	        return false;
	    }
	  }

	  function isSymbol(propType, propValue) {
	    // Native Symbol.
	    if (propType === 'symbol') {
	      return true;
	    }

	    // falsy value can't be a Symbol
	    if (!propValue) {
	      return false;
	    }

	    // 19.4.3.5 Symbol.prototype[@@toStringTag] === 'Symbol'
	    if (propValue['@@toStringTag'] === 'Symbol') {
	      return true;
	    }

	    // Fallback for non-spec compliant Symbols which are polyfilled.
	    if (typeof Symbol === 'function' && propValue instanceof Symbol) {
	      return true;
	    }

	    return false;
	  }

	  // Equivalent of `typeof` but with special handling for array and regexp.
	  function getPropType(propValue) {
	    var propType = typeof propValue;
	    if (Array.isArray(propValue)) {
	      return 'array';
	    }
	    if (propValue instanceof RegExp) {
	      // Old webkits (at least until Android 4.0) return 'function' rather than
	      // 'object' for typeof a RegExp. We'll normalize this here so that /bla/
	      // passes PropTypes.object.
	      return 'object';
	    }
	    if (isSymbol(propType, propValue)) {
	      return 'symbol';
	    }
	    return propType;
	  }

	  // This handles more types than `getPropType`. Only used for error messages.
	  // See `createPrimitiveTypeChecker`.
	  function getPreciseType(propValue) {
	    if (typeof propValue === 'undefined' || propValue === null) {
	      return '' + propValue;
	    }
	    var propType = getPropType(propValue);
	    if (propType === 'object') {
	      if (propValue instanceof Date) {
	        return 'date';
	      } else if (propValue instanceof RegExp) {
	        return 'regexp';
	      }
	    }
	    return propType;
	  }

	  // Returns a string that is postfixed to a warning about an invalid type.
	  // For example, "undefined" or "of type array"
	  function getPostfixForTypeWarning(value) {
	    var type = getPreciseType(value);
	    switch (type) {
	      case 'array':
	      case 'object':
	        return 'an ' + type;
	      case 'boolean':
	      case 'date':
	      case 'regexp':
	        return 'a ' + type;
	      default:
	        return type;
	    }
	  }

	  // Returns class name of the object, if any.
	  function getClassName(propValue) {
	    if (!propValue.constructor || !propValue.constructor.name) {
	      return ANONYMOUS;
	    }
	    return propValue.constructor.name;
	  }

	  ReactPropTypes.checkPropTypes = checkPropTypes;
	  ReactPropTypes.resetWarningCache = checkPropTypes.resetWarningCache;
	  ReactPropTypes.PropTypes = ReactPropTypes;

	  return ReactPropTypes;
	};
	return factoryWithTypeCheckers;
}

/**
 * Copyright (c) 2013-present, Facebook, Inc.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */

var factoryWithThrowingShims;
var hasRequiredFactoryWithThrowingShims;

function requireFactoryWithThrowingShims () {
	if (hasRequiredFactoryWithThrowingShims) return factoryWithThrowingShims;
	hasRequiredFactoryWithThrowingShims = 1;

	var ReactPropTypesSecret = requireReactPropTypesSecret();

	function emptyFunction() {}
	function emptyFunctionWithReset() {}
	emptyFunctionWithReset.resetWarningCache = emptyFunction;

	factoryWithThrowingShims = function() {
	  function shim(props, propName, componentName, location, propFullName, secret) {
	    if (secret === ReactPropTypesSecret) {
	      // It is still safe when called from React.
	      return;
	    }
	    var err = new Error(
	      'Calling PropTypes validators directly is not supported by the `prop-types` package. ' +
	      'Use PropTypes.checkPropTypes() to call them. ' +
	      'Read more at http://fb.me/use-check-prop-types'
	    );
	    err.name = 'Invariant Violation';
	    throw err;
	  }	  shim.isRequired = shim;
	  function getShim() {
	    return shim;
	  }	  // Important!
	  // Keep this list in sync with production version in `./factoryWithTypeCheckers.js`.
	  var ReactPropTypes = {
	    array: shim,
	    bigint: shim,
	    bool: shim,
	    func: shim,
	    number: shim,
	    object: shim,
	    string: shim,
	    symbol: shim,

	    any: shim,
	    arrayOf: getShim,
	    element: shim,
	    elementType: shim,
	    instanceOf: getShim,
	    node: shim,
	    objectOf: getShim,
	    oneOf: getShim,
	    oneOfType: getShim,
	    shape: getShim,
	    exact: getShim,

	    checkPropTypes: emptyFunctionWithReset,
	    resetWarningCache: emptyFunction
	  };

	  ReactPropTypes.PropTypes = ReactPropTypes;

	  return ReactPropTypes;
	};
	return factoryWithThrowingShims;
}

/**
 * Copyright (c) 2013-present, Facebook, Inc.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */

if (process.env.NODE_ENV !== 'production') {
  var ReactIs = requireReactIs();

  // By explicitly using `prop-types` you are opting into new development behavior.
  // http://fb.me/prop-types-in-prod
  var throwOnDirectAccess = true;
  propTypes$2.exports = requireFactoryWithTypeCheckers()(ReactIs.isElement, throwOnDirectAccess);
} else {
  // By explicitly using `prop-types` you are opting into new production behavior.
  // http://fb.me/prop-types-in-prod
  propTypes$2.exports = requireFactoryWithThrowingShims()();
}

var __spreadArray = (undefined && undefined.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
// As defined on the list of supported events: https://reactjs.org/docs/events.html
var clipboardEvents = ['onCopy', 'onCut', 'onPaste'];
var compositionEvents = [
    'onCompositionEnd',
    'onCompositionStart',
    'onCompositionUpdate',
];
var keyboardEvents = ['onKeyDown', 'onKeyPress', 'onKeyUp'];
var focusEvents = ['onFocus', 'onBlur'];
var formEvents = ['onChange', 'onInput', 'onInvalid', 'onReset', 'onSubmit'];
var genericEvents = ['onError', 'onLoad'];
var mouseEvents = [
    'onClick',
    'onContextMenu',
    'onDoubleClick',
    'onDrag',
    'onDragEnd',
    'onDragEnter',
    'onDragExit',
    'onDragLeave',
    'onDragOver',
    'onDragStart',
    'onDrop',
    'onMouseDown',
    'onMouseEnter',
    'onMouseLeave',
    'onMouseMove',
    'onMouseOut',
    'onMouseOver',
    'onMouseUp',
];
var pointerEvents = [
    'onPointerDown',
    'onPointerMove',
    'onPointerUp',
    'onPointerCancel',
    'onGotPointerCapture',
    'onLostPointerCapture',
    'onPointerEnter',
    'onPointerLeave',
    'onPointerOver',
    'onPointerOut',
];
var selectionEvents = ['onSelect'];
var touchEvents = ['onTouchCancel', 'onTouchEnd', 'onTouchMove', 'onTouchStart'];
var uiEvents = ['onScroll'];
var wheelEvents = ['onWheel'];
var mediaEvents = [
    'onAbort',
    'onCanPlay',
    'onCanPlayThrough',
    'onDurationChange',
    'onEmptied',
    'onEncrypted',
    'onEnded',
    'onError',
    'onLoadedData',
    'onLoadedMetadata',
    'onLoadStart',
    'onPause',
    'onPlay',
    'onPlaying',
    'onProgress',
    'onRateChange',
    'onSeeked',
    'onSeeking',
    'onStalled',
    'onSuspend',
    'onTimeUpdate',
    'onVolumeChange',
    'onWaiting',
];
var imageEvents = ['onLoad', 'onError'];
var animationEvents = [
    'onAnimationStart',
    'onAnimationEnd',
    'onAnimationIteration',
];
var transitionEvents = ['onTransitionEnd'];
var otherEvents = ['onToggle'];
var allEvents = __spreadArray(__spreadArray(__spreadArray(__spreadArray(__spreadArray(__spreadArray(__spreadArray(__spreadArray(__spreadArray(__spreadArray(__spreadArray(__spreadArray(__spreadArray(__spreadArray(__spreadArray(__spreadArray(__spreadArray([], clipboardEvents, true), compositionEvents, true), keyboardEvents, true), focusEvents, true), formEvents, true), genericEvents, true), mouseEvents, true), pointerEvents, true), selectionEvents, true), touchEvents, true), uiEvents, true), wheelEvents, true), mediaEvents, true), imageEvents, true), animationEvents, true), transitionEvents, true), otherEvents, true);
/**
 * Returns an object with on-event callback props curried with provided args.
 * @param {Object} props Props passed to a component.
 * @param {Function=} getArgs A function that returns argument(s) on-event callbacks
 *   shall be curried with.
 */
function makeEventProps(props, getArgs) {
    var eventProps = {};
    allEvents.forEach(function (eventName) {
        var eventHandler = props[eventName];
        if (!eventHandler) {
            return;
        }
        if (!getArgs) {
            eventProps[eventName] = eventHandler;
            return;
        }
        eventProps[eventName] = function (event) { return eventHandler(event, getArgs(eventName)); };
    });
    return eventProps;
}

var esm$7 = /*#__PURE__*/Object.freeze({
    __proto__: null,
    clipboardEvents: clipboardEvents,
    compositionEvents: compositionEvents,
    keyboardEvents: keyboardEvents,
    focusEvents: focusEvents,
    formEvents: formEvents,
    genericEvents: genericEvents,
    mouseEvents: mouseEvents,
    pointerEvents: pointerEvents,
    selectionEvents: selectionEvents,
    touchEvents: touchEvents,
    uiEvents: uiEvents,
    wheelEvents: wheelEvents,
    mediaEvents: mediaEvents,
    imageEvents: imageEvents,
    animationEvents: animationEvents,
    transitionEvents: transitionEvents,
    otherEvents: otherEvents,
    allEvents: allEvents,
    'default': makeEventProps
});

var require$$2$2 = /*@__PURE__*/getAugmentedNamespace(esm$7);

function mergeClassNames() {
  return Array.prototype.slice.call(arguments).reduce(function (classList, arg) {
    return classList.concat(arg);
  }, []).filter(function (arg) {
    return typeof arg === 'string';
  }).join(' ');
}

var esm$6 = /*#__PURE__*/Object.freeze({
    __proto__: null,
    'default': mergeClassNames
});

var require$$3$1 = /*@__PURE__*/getAugmentedNamespace(esm$6);

/**
 * lodash (Custom Build) <https://lodash.com/>
 * Build: `lodash modularize exports="npm" -o ./`
 * Copyright jQuery Foundation and other contributors <https://jquery.org/>
 * Released under MIT license <https://lodash.com/license>
 * Based on Underscore.js 1.8.3 <http://underscorejs.org/LICENSE>
 * Copyright Jeremy Ashkenas, DocumentCloud and Investigative Reporters & Editors
 */

/** Used as the `TypeError` message for "Functions" methods. */
var FUNC_ERROR_TEXT$3 = 'Expected a function';

/** Used to stand-in for `undefined` hash values. */
var HASH_UNDEFINED = '__lodash_hash_undefined__';

/** `Object#toString` result references. */
var funcTag = '[object Function]',
    genTag = '[object GeneratorFunction]';

/**
 * Used to match `RegExp`
 * [syntax characters](http://ecma-international.org/ecma-262/7.0/#sec-patterns).
 */
var reRegExpChar = /[\\^$.*+?()[\]{}|]/g;

/** Used to detect host constructors (Safari). */
var reIsHostCtor = /^\[object .+?Constructor\]$/;

/** Detect free variable `global` from Node.js. */
var freeGlobal$3 = typeof commonjsGlobal == 'object' && commonjsGlobal && commonjsGlobal.Object === Object && commonjsGlobal;

/** Detect free variable `self`. */
var freeSelf$2 = typeof self == 'object' && self && self.Object === Object && self;

/** Used as a reference to the global object. */
var root$4 = freeGlobal$3 || freeSelf$2 || Function('return this')();

/**
 * Gets the value at `key` of `object`.
 *
 * @private
 * @param {Object} [object] The object to query.
 * @param {string} key The key of the property to get.
 * @returns {*} Returns the property value.
 */
function getValue$2(object, key) {
  return object == null ? undefined : object[key];
}

/**
 * Checks if `value` is a host object in IE < 9.
 *
 * @private
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a host object, else `false`.
 */
function isHostObject(value) {
  // Many host objects are `Object` objects that can coerce to strings
  // despite having improperly defined `toString` methods.
  var result = false;
  if (value != null && typeof value.toString != 'function') {
    try {
      result = !!(value + '');
    } catch (e) {}
  }
  return result;
}

/** Used for built-in method references. */
var arrayProto = Array.prototype,
    funcProto = Function.prototype,
    objectProto$3 = Object.prototype;

/** Used to detect overreaching core-js shims. */
var coreJsData = root$4['__core-js_shared__'];

/** Used to detect methods masquerading as native. */
var maskSrcKey = (function() {
  var uid = /[^.]+$/.exec(coreJsData && coreJsData.keys && coreJsData.keys.IE_PROTO || '');
  return uid ? ('Symbol(src)_1.' + uid) : '';
}());

/** Used to resolve the decompiled source of functions. */
var funcToString = funcProto.toString;

/** Used to check objects for own properties. */
var hasOwnProperty$1 = objectProto$3.hasOwnProperty;

/**
 * Used to resolve the
 * [`toStringTag`](http://ecma-international.org/ecma-262/7.0/#sec-object.prototype.tostring)
 * of values.
 */
var objectToString$3 = objectProto$3.toString;

/** Used to detect if a method is native. */
var reIsNative = RegExp('^' +
  funcToString.call(hasOwnProperty$1).replace(reRegExpChar, '\\$&')
  .replace(/hasOwnProperty|(function).*?(?=\\\()| for .+?(?=\\\])/g, '$1.*?') + '$'
);

/** Built-in value references. */
var splice = arrayProto.splice;

/* Built-in method references that are verified to be native. */
var Map$1 = getNative(root$4, 'Map'),
    nativeCreate = getNative(Object, 'create');

/**
 * Creates a hash object.
 *
 * @private
 * @constructor
 * @param {Array} [entries] The key-value pairs to cache.
 */
function Hash(entries) {
  var index = -1,
      length = entries ? entries.length : 0;

  this.clear();
  while (++index < length) {
    var entry = entries[index];
    this.set(entry[0], entry[1]);
  }
}

/**
 * Removes all key-value entries from the hash.
 *
 * @private
 * @name clear
 * @memberOf Hash
 */
function hashClear() {
  this.__data__ = nativeCreate ? nativeCreate(null) : {};
}

/**
 * Removes `key` and its value from the hash.
 *
 * @private
 * @name delete
 * @memberOf Hash
 * @param {Object} hash The hash to modify.
 * @param {string} key The key of the value to remove.
 * @returns {boolean} Returns `true` if the entry was removed, else `false`.
 */
function hashDelete(key) {
  return this.has(key) && delete this.__data__[key];
}

/**
 * Gets the hash value for `key`.
 *
 * @private
 * @name get
 * @memberOf Hash
 * @param {string} key The key of the value to get.
 * @returns {*} Returns the entry value.
 */
function hashGet(key) {
  var data = this.__data__;
  if (nativeCreate) {
    var result = data[key];
    return result === HASH_UNDEFINED ? undefined : result;
  }
  return hasOwnProperty$1.call(data, key) ? data[key] : undefined;
}

/**
 * Checks if a hash value for `key` exists.
 *
 * @private
 * @name has
 * @memberOf Hash
 * @param {string} key The key of the entry to check.
 * @returns {boolean} Returns `true` if an entry for `key` exists, else `false`.
 */
function hashHas(key) {
  var data = this.__data__;
  return nativeCreate ? data[key] !== undefined : hasOwnProperty$1.call(data, key);
}

/**
 * Sets the hash `key` to `value`.
 *
 * @private
 * @name set
 * @memberOf Hash
 * @param {string} key The key of the value to set.
 * @param {*} value The value to set.
 * @returns {Object} Returns the hash instance.
 */
function hashSet(key, value) {
  var data = this.__data__;
  data[key] = (nativeCreate && value === undefined) ? HASH_UNDEFINED : value;
  return this;
}

// Add methods to `Hash`.
Hash.prototype.clear = hashClear;
Hash.prototype['delete'] = hashDelete;
Hash.prototype.get = hashGet;
Hash.prototype.has = hashHas;
Hash.prototype.set = hashSet;

/**
 * Creates an list cache object.
 *
 * @private
 * @constructor
 * @param {Array} [entries] The key-value pairs to cache.
 */
function ListCache(entries) {
  var index = -1,
      length = entries ? entries.length : 0;

  this.clear();
  while (++index < length) {
    var entry = entries[index];
    this.set(entry[0], entry[1]);
  }
}

/**
 * Removes all key-value entries from the list cache.
 *
 * @private
 * @name clear
 * @memberOf ListCache
 */
function listCacheClear() {
  this.__data__ = [];
}

/**
 * Removes `key` and its value from the list cache.
 *
 * @private
 * @name delete
 * @memberOf ListCache
 * @param {string} key The key of the value to remove.
 * @returns {boolean} Returns `true` if the entry was removed, else `false`.
 */
function listCacheDelete(key) {
  var data = this.__data__,
      index = assocIndexOf(data, key);

  if (index < 0) {
    return false;
  }
  var lastIndex = data.length - 1;
  if (index == lastIndex) {
    data.pop();
  } else {
    splice.call(data, index, 1);
  }
  return true;
}

/**
 * Gets the list cache value for `key`.
 *
 * @private
 * @name get
 * @memberOf ListCache
 * @param {string} key The key of the value to get.
 * @returns {*} Returns the entry value.
 */
function listCacheGet(key) {
  var data = this.__data__,
      index = assocIndexOf(data, key);

  return index < 0 ? undefined : data[index][1];
}

/**
 * Checks if a list cache value for `key` exists.
 *
 * @private
 * @name has
 * @memberOf ListCache
 * @param {string} key The key of the entry to check.
 * @returns {boolean} Returns `true` if an entry for `key` exists, else `false`.
 */
function listCacheHas(key) {
  return assocIndexOf(this.__data__, key) > -1;
}

/**
 * Sets the list cache `key` to `value`.
 *
 * @private
 * @name set
 * @memberOf ListCache
 * @param {string} key The key of the value to set.
 * @param {*} value The value to set.
 * @returns {Object} Returns the list cache instance.
 */
function listCacheSet(key, value) {
  var data = this.__data__,
      index = assocIndexOf(data, key);

  if (index < 0) {
    data.push([key, value]);
  } else {
    data[index][1] = value;
  }
  return this;
}

// Add methods to `ListCache`.
ListCache.prototype.clear = listCacheClear;
ListCache.prototype['delete'] = listCacheDelete;
ListCache.prototype.get = listCacheGet;
ListCache.prototype.has = listCacheHas;
ListCache.prototype.set = listCacheSet;

/**
 * Creates a map cache object to store key-value pairs.
 *
 * @private
 * @constructor
 * @param {Array} [entries] The key-value pairs to cache.
 */
function MapCache(entries) {
  var index = -1,
      length = entries ? entries.length : 0;

  this.clear();
  while (++index < length) {
    var entry = entries[index];
    this.set(entry[0], entry[1]);
  }
}

/**
 * Removes all key-value entries from the map.
 *
 * @private
 * @name clear
 * @memberOf MapCache
 */
function mapCacheClear() {
  this.__data__ = {
    'hash': new Hash,
    'map': new (Map$1 || ListCache),
    'string': new Hash
  };
}

/**
 * Removes `key` and its value from the map.
 *
 * @private
 * @name delete
 * @memberOf MapCache
 * @param {string} key The key of the value to remove.
 * @returns {boolean} Returns `true` if the entry was removed, else `false`.
 */
function mapCacheDelete(key) {
  return getMapData(this, key)['delete'](key);
}

/**
 * Gets the map value for `key`.
 *
 * @private
 * @name get
 * @memberOf MapCache
 * @param {string} key The key of the value to get.
 * @returns {*} Returns the entry value.
 */
function mapCacheGet(key) {
  return getMapData(this, key).get(key);
}

/**
 * Checks if a map value for `key` exists.
 *
 * @private
 * @name has
 * @memberOf MapCache
 * @param {string} key The key of the entry to check.
 * @returns {boolean} Returns `true` if an entry for `key` exists, else `false`.
 */
function mapCacheHas(key) {
  return getMapData(this, key).has(key);
}

/**
 * Sets the map `key` to `value`.
 *
 * @private
 * @name set
 * @memberOf MapCache
 * @param {string} key The key of the value to set.
 * @param {*} value The value to set.
 * @returns {Object} Returns the map cache instance.
 */
function mapCacheSet(key, value) {
  getMapData(this, key).set(key, value);
  return this;
}

// Add methods to `MapCache`.
MapCache.prototype.clear = mapCacheClear;
MapCache.prototype['delete'] = mapCacheDelete;
MapCache.prototype.get = mapCacheGet;
MapCache.prototype.has = mapCacheHas;
MapCache.prototype.set = mapCacheSet;

/**
 * Gets the index at which the `key` is found in `array` of key-value pairs.
 *
 * @private
 * @param {Array} array The array to inspect.
 * @param {*} key The key to search for.
 * @returns {number} Returns the index of the matched value, else `-1`.
 */
function assocIndexOf(array, key) {
  var length = array.length;
  while (length--) {
    if (eq(array[length][0], key)) {
      return length;
    }
  }
  return -1;
}

/**
 * The base implementation of `_.isNative` without bad shim checks.
 *
 * @private
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a native function,
 *  else `false`.
 */
function baseIsNative(value) {
  if (!isObject$5(value) || isMasked(value)) {
    return false;
  }
  var pattern = (isFunction(value) || isHostObject(value)) ? reIsNative : reIsHostCtor;
  return pattern.test(toSource(value));
}

/**
 * Gets the data for `map`.
 *
 * @private
 * @param {Object} map The map to query.
 * @param {string} key The reference key.
 * @returns {*} Returns the map data.
 */
function getMapData(map, key) {
  var data = map.__data__;
  return isKeyable(key)
    ? data[typeof key == 'string' ? 'string' : 'hash']
    : data.map;
}

/**
 * Gets the native function at `key` of `object`.
 *
 * @private
 * @param {Object} object The object to query.
 * @param {string} key The key of the method to get.
 * @returns {*} Returns the function if it's native, else `undefined`.
 */
function getNative(object, key) {
  var value = getValue$2(object, key);
  return baseIsNative(value) ? value : undefined;
}

/**
 * Checks if `value` is suitable for use as unique object key.
 *
 * @private
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is suitable, else `false`.
 */
function isKeyable(value) {
  var type = typeof value;
  return (type == 'string' || type == 'number' || type == 'symbol' || type == 'boolean')
    ? (value !== '__proto__')
    : (value === null);
}

/**
 * Checks if `func` has its source masked.
 *
 * @private
 * @param {Function} func The function to check.
 * @returns {boolean} Returns `true` if `func` is masked, else `false`.
 */
function isMasked(func) {
  return !!maskSrcKey && (maskSrcKey in func);
}

/**
 * Converts `func` to its source code.
 *
 * @private
 * @param {Function} func The function to process.
 * @returns {string} Returns the source code.
 */
function toSource(func) {
  if (func != null) {
    try {
      return funcToString.call(func);
    } catch (e) {}
    try {
      return (func + '');
    } catch (e) {}
  }
  return '';
}

/**
 * Creates a function that memoizes the result of `func`. If `resolver` is
 * provided, it determines the cache key for storing the result based on the
 * arguments provided to the memoized function. By default, the first argument
 * provided to the memoized function is used as the map cache key. The `func`
 * is invoked with the `this` binding of the memoized function.
 *
 * **Note:** The cache is exposed as the `cache` property on the memoized
 * function. Its creation may be customized by replacing the `_.memoize.Cache`
 * constructor with one whose instances implement the
 * [`Map`](http://ecma-international.org/ecma-262/7.0/#sec-properties-of-the-map-prototype-object)
 * method interface of `delete`, `get`, `has`, and `set`.
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @category Function
 * @param {Function} func The function to have its output memoized.
 * @param {Function} [resolver] The function to resolve the cache key.
 * @returns {Function} Returns the new memoized function.
 * @example
 *
 * var object = { 'a': 1, 'b': 2 };
 * var other = { 'c': 3, 'd': 4 };
 *
 * var values = _.memoize(_.values);
 * values(object);
 * // => [1, 2]
 *
 * values(other);
 * // => [3, 4]
 *
 * object.a = 2;
 * values(object);
 * // => [1, 2]
 *
 * // Modify the result cache.
 * values.cache.set(object, ['a', 'b']);
 * values(object);
 * // => ['a', 'b']
 *
 * // Replace `_.memoize.Cache`.
 * _.memoize.Cache = WeakMap;
 */
function memoize(func, resolver) {
  if (typeof func != 'function' || (resolver && typeof resolver != 'function')) {
    throw new TypeError(FUNC_ERROR_TEXT$3);
  }
  var memoized = function() {
    var args = arguments,
        key = resolver ? resolver.apply(this, args) : args[0],
        cache = memoized.cache;

    if (cache.has(key)) {
      return cache.get(key);
    }
    var result = func.apply(this, args);
    memoized.cache = cache.set(key, result);
    return result;
  };
  memoized.cache = new (memoize.Cache || MapCache);
  return memoized;
}

// Assign cache to `_.memoize`.
memoize.Cache = MapCache;

/**
 * Performs a
 * [`SameValueZero`](http://ecma-international.org/ecma-262/7.0/#sec-samevaluezero)
 * comparison between two values to determine if they are equivalent.
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to compare.
 * @param {*} other The other value to compare.
 * @returns {boolean} Returns `true` if the values are equivalent, else `false`.
 * @example
 *
 * var object = { 'a': 1 };
 * var other = { 'a': 1 };
 *
 * _.eq(object, object);
 * // => true
 *
 * _.eq(object, other);
 * // => false
 *
 * _.eq('a', 'a');
 * // => true
 *
 * _.eq('a', Object('a'));
 * // => false
 *
 * _.eq(NaN, NaN);
 * // => true
 */
function eq(value, other) {
  return value === other || (value !== value && other !== other);
}

/**
 * Checks if `value` is classified as a `Function` object.
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a function, else `false`.
 * @example
 *
 * _.isFunction(_);
 * // => true
 *
 * _.isFunction(/abc/);
 * // => false
 */
function isFunction(value) {
  // The use of `Object#toString` avoids issues with the `typeof` operator
  // in Safari 8-9 which returns 'object' for typed array and other constructors.
  var tag = isObject$5(value) ? objectToString$3.call(value) : '';
  return tag == funcTag || tag == genTag;
}

/**
 * Checks if `value` is the
 * [language type](http://www.ecma-international.org/ecma-262/7.0/#sec-ecmascript-language-types)
 * of `Object`. (e.g. arrays, functions, objects, regexes, `new Number(0)`, and `new String('')`)
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is an object, else `false`.
 * @example
 *
 * _.isObject({});
 * // => true
 *
 * _.isObject([1, 2, 3]);
 * // => true
 *
 * _.isObject(_.noop);
 * // => true
 *
 * _.isObject(null);
 * // => false
 */
function isObject$5(value) {
  var type = typeof value;
  return !!value && (type == 'object' || type == 'function');
}

var lodash_memoize = memoize;

var memoize$1 = lodash_memoize;

function resolver(options) {
  return JSON.stringify(options);
}

function uniqDefined(arr) {
  return arr.filter(function (el, index) {
    return el && arr.indexOf(el) === index;
  });
}

function normalizeLocales(arr) {
  return arr.map(function (el) {
    if (!el || el.indexOf('-') === -1 || el.toLowerCase() !== el) {
      return el;
    }

    var splitEl = el.split('-');
    return splitEl[0] + "-" + splitEl[1].toUpperCase();
  });
}

function getUserLocalesInternal(_temp) {
  var _ref = _temp === void 0 ? {} : _temp,
      _ref$useFallbackLocal = _ref.useFallbackLocale,
      useFallbackLocale = _ref$useFallbackLocal === void 0 ? true : _ref$useFallbackLocal,
      _ref$fallbackLocale = _ref.fallbackLocale,
      fallbackLocale = _ref$fallbackLocale === void 0 ? 'en-US' : _ref$fallbackLocale;

  var languageList = [];

  if (typeof window !== 'undefined') {
    var _window = window,
        navigator = _window.navigator;
    languageList = languageList.concat(navigator.languages, navigator.language, navigator.userLanguage, navigator.browserLanguage, navigator.systemLanguage);
  }

  if (useFallbackLocale) {
    languageList.push(fallbackLocale);
  }

  return normalizeLocales(uniqDefined(languageList));
}

var getUserLocales = memoize$1(getUserLocalesInternal, resolver);

function getUserLocaleInternal(options) {
  return getUserLocales(options)[0] || null;
}

var getUserLocale = memoize$1(getUserLocaleInternal, resolver);

var esm$5 = /*#__PURE__*/Object.freeze({
    __proto__: null,
    getUserLocales: getUserLocales,
    getUserLocale: getUserLocale,
    'default': getUserLocale
});

/**
 * Utils
 */
function makeGetEdgeOfNeighbor(getPeriod, getEdgeOfPeriod, defaultOffset) {
    return function makeGetEdgeOfNeighborInternal(date, offset) {
        if (offset === void 0) { offset = defaultOffset; }
        var previousPeriod = getPeriod(date) + offset;
        return getEdgeOfPeriod(previousPeriod);
    };
}
function makeGetEnd(getBeginOfNextPeriod) {
    return function makeGetEndInternal(date) {
        return new Date(getBeginOfNextPeriod(date).getTime() - 1);
    };
}
function makeGetRange(getStart, getEnd) {
    return function makeGetRangeInternal(date) {
        return [getStart(date), getEnd(date)];
    };
}
/**
 * Simple getters - getting a property of a given point in time
 */
/**
 * Gets year from date.
 *
 * @param {Date|number|string} date Date to get year from.
 */
function getYear(date) {
    if (date instanceof Date) {
        return date.getFullYear();
    }
    if (typeof date === 'number') {
        return date;
    }
    var year = parseInt(date, 10);
    if (typeof date === 'string' && !isNaN(year)) {
        return year;
    }
    throw new Error("Failed to get year from date: ".concat(date, "."));
}
/**
 * Gets month from date.
 *
 * @param {Date} date Date to get month from.
 */
function getMonth(date) {
    if (date instanceof Date) {
        return date.getMonth();
    }
    throw new Error("Failed to get month from date: ".concat(date, "."));
}
/**
 * Gets human-readable month from date.
 *
 * @param {Date} date Date to get human-readable month from.
 */
function getMonthHuman(date) {
    if (date instanceof Date) {
        return date.getMonth() + 1;
    }
    throw new Error("Failed to get human-readable month from date: ".concat(date, "."));
}
/**
 * Gets human-readable day of the month from date.
 *
 * @param {Date} date Date to get day of the month from.
 */
function getDate(date) {
    if (date instanceof Date) {
        return date.getDate();
    }
    throw new Error("Failed to get year from date: ".concat(date, "."));
}
/**
 * Gets hours from date.
 *
 * @param {Date|string} date Date to get hours from.
 */
function getHours(date) {
    if (date instanceof Date) {
        return date.getHours();
    }
    if (typeof date === 'string') {
        var datePieces = date.split(':');
        if (datePieces.length >= 2) {
            var hoursString = datePieces[0];
            if (hoursString) {
                var hours = parseInt(hoursString, 10);
                if (!isNaN(hours)) {
                    return hours;
                }
            }
        }
    }
    throw new Error("Failed to get hours from date: ".concat(date, "."));
}
/**
 * Gets minutes from date.
 *
 * @param {Date|string} date Date to get minutes from.
 */
function getMinutes(date) {
    if (date instanceof Date) {
        return date.getMinutes();
    }
    if (typeof date === 'string') {
        var datePieces = date.split(':');
        if (datePieces.length >= 2) {
            var minutesString = datePieces[1] || '0';
            var minutes = parseInt(minutesString, 10);
            if (!isNaN(minutes)) {
                return minutes;
            }
        }
    }
    throw new Error("Failed to get minutes from date: ".concat(date, "."));
}
/**
 * Gets seconds from date.
 *
 * @param {Date|string} date Date to get seconds from.
 */
function getSeconds(date) {
    if (date instanceof Date) {
        return date.getSeconds();
    }
    if (typeof date === 'string') {
        var datePieces = date.split(':');
        if (datePieces.length >= 2) {
            var secondsString = datePieces[2] || '0';
            var seconds = parseInt(secondsString, 10);
            if (!isNaN(seconds)) {
                return seconds;
            }
        }
    }
    throw new Error("Failed to get seconds from date: ".concat(date, "."));
}
/**
 * Century
 */
function getCenturyStart(date) {
    var year = getYear(date);
    var centuryStartYear = year + ((-year + 1) % 100);
    var centuryStartDate = new Date();
    centuryStartDate.setFullYear(centuryStartYear, 0, 1);
    centuryStartDate.setHours(0, 0, 0, 0);
    return centuryStartDate;
}
var getPreviousCenturyStart = makeGetEdgeOfNeighbor(getYear, getCenturyStart, -100);
var getNextCenturyStart = makeGetEdgeOfNeighbor(getYear, getCenturyStart, 100);
var getCenturyEnd = makeGetEnd(getNextCenturyStart);
var getPreviousCenturyEnd = makeGetEdgeOfNeighbor(getYear, getCenturyEnd, -100);
var getNextCenturyEnd = makeGetEdgeOfNeighbor(getYear, getCenturyEnd, 100);
var getCenturyRange = makeGetRange(getCenturyStart, getCenturyEnd);
/**
 * Decade
 */
function getDecadeStart(date) {
    var year = getYear(date);
    var decadeStartYear = year + ((-year + 1) % 10);
    var decadeStartDate = new Date();
    decadeStartDate.setFullYear(decadeStartYear, 0, 1);
    decadeStartDate.setHours(0, 0, 0, 0);
    return decadeStartDate;
}
var getPreviousDecadeStart = makeGetEdgeOfNeighbor(getYear, getDecadeStart, -10);
var getNextDecadeStart = makeGetEdgeOfNeighbor(getYear, getDecadeStart, 10);
var getDecadeEnd = makeGetEnd(getNextDecadeStart);
var getPreviousDecadeEnd = makeGetEdgeOfNeighbor(getYear, getDecadeEnd, -10);
var getNextDecadeEnd = makeGetEdgeOfNeighbor(getYear, getDecadeEnd, 10);
var getDecadeRange = makeGetRange(getDecadeStart, getDecadeEnd);
/**
 * Year
 */
function getYearStart(date) {
    var year = getYear(date);
    var yearStartDate = new Date();
    yearStartDate.setFullYear(year, 0, 1);
    yearStartDate.setHours(0, 0, 0, 0);
    return yearStartDate;
}
var getPreviousYearStart = makeGetEdgeOfNeighbor(getYear, getYearStart, -1);
var getNextYearStart = makeGetEdgeOfNeighbor(getYear, getYearStart, 1);
var getYearEnd = makeGetEnd(getNextYearStart);
var getPreviousYearEnd = makeGetEdgeOfNeighbor(getYear, getYearEnd, -1);
var getNextYearEnd = makeGetEdgeOfNeighbor(getYear, getYearEnd, 1);
var getYearRange = makeGetRange(getYearStart, getYearEnd);
/**
 * Month
 */
function makeGetEdgeOfNeighborMonth(getEdgeOfPeriod, defaultOffset) {
    return function makeGetEdgeOfNeighborMonthInternal(date, offset) {
        if (offset === void 0) { offset = defaultOffset; }
        var year = getYear(date);
        var month = getMonth(date) + offset;
        var previousPeriod = new Date();
        previousPeriod.setFullYear(year, month, 1);
        previousPeriod.setHours(0, 0, 0, 0);
        return getEdgeOfPeriod(previousPeriod);
    };
}
function getMonthStart(date) {
    var year = getYear(date);
    var month = getMonth(date);
    var monthStartDate = new Date();
    monthStartDate.setFullYear(year, month, 1);
    monthStartDate.setHours(0, 0, 0, 0);
    return monthStartDate;
}
var getPreviousMonthStart = makeGetEdgeOfNeighborMonth(getMonthStart, -1);
var getNextMonthStart = makeGetEdgeOfNeighborMonth(getMonthStart, 1);
var getMonthEnd = makeGetEnd(getNextMonthStart);
var getPreviousMonthEnd = makeGetEdgeOfNeighborMonth(getMonthEnd, -1);
var getNextMonthEnd = makeGetEdgeOfNeighborMonth(getMonthEnd, 1);
var getMonthRange = makeGetRange(getMonthStart, getMonthEnd);
/**
 * Day
 */
function makeGetEdgeOfNeighborDay(getEdgeOfPeriod, defaultOffset) {
    return function makeGetEdgeOfNeighborDayInternal(date, offset) {
        if (offset === void 0) { offset = defaultOffset; }
        var year = getYear(date);
        var month = getMonth(date);
        var day = getDate(date) + offset;
        var previousPeriod = new Date();
        previousPeriod.setFullYear(year, month, day);
        previousPeriod.setHours(0, 0, 0, 0);
        return getEdgeOfPeriod(previousPeriod);
    };
}
function getDayStart(date) {
    var year = getYear(date);
    var month = getMonth(date);
    var day = getDate(date);
    var dayStartDate = new Date();
    dayStartDate.setFullYear(year, month, day);
    dayStartDate.setHours(0, 0, 0, 0);
    return dayStartDate;
}
var getPreviousDayStart = makeGetEdgeOfNeighborDay(getDayStart, -1);
var getNextDayStart = makeGetEdgeOfNeighborDay(getDayStart, 1);
var getDayEnd = makeGetEnd(getNextDayStart);
var getPreviousDayEnd = makeGetEdgeOfNeighborDay(getDayEnd, -1);
var getNextDayEnd = makeGetEdgeOfNeighborDay(getDayEnd, 1);
var getDayRange = makeGetRange(getDayStart, getDayEnd);
/**
 * Other
 */
/**
 * Returns a number of days in a month of a given date.
 *
 * @param {Date} date Date.
 */
function getDaysInMonth(date) {
    return getDate(getMonthEnd(date));
}
function padStart(num, val) {
    if (val === void 0) { val = 2; }
    var numStr = "".concat(num);
    if (numStr.length >= val) {
        return num;
    }
    return "0000".concat(numStr).slice(-val);
}
/**
 * Returns local hours and minutes (hh:mm).
 */
function getHoursMinutes(date) {
    var hours = padStart(getHours(date));
    var minutes = padStart(getMinutes(date));
    return "".concat(hours, ":").concat(minutes);
}
/**
 * Returns local hours, minutes and seconds (hh:mm:ss).
 */
function getHoursMinutesSeconds(date) {
    var hours = padStart(getHours(date));
    var minutes = padStart(getMinutes(date));
    var seconds = padStart(getSeconds(date));
    return "".concat(hours, ":").concat(minutes, ":").concat(seconds);
}
/**
 * Returns local month in ISO-like format (YYYY-MM).
 */
function getISOLocalMonth(date) {
    var year = padStart(getYear(date), 4);
    var month = padStart(getMonthHuman(date));
    return "".concat(year, "-").concat(month);
}
/**
 * Returns local date in ISO-like format (YYYY-MM-DD).
 */
function getISOLocalDate(date) {
    var year = padStart(getYear(date), 4);
    var month = padStart(getMonthHuman(date));
    var day = padStart(getDate(date));
    return "".concat(year, "-").concat(month, "-").concat(day);
}
/**
 * Returns local date & time in ISO-like format (YYYY-MM-DDThh:mm:ss).
 */
function getISOLocalDateTime(date) {
    return "".concat(getISOLocalDate(date), "T").concat(getHoursMinutesSeconds(date));
}

var esm$4 = /*#__PURE__*/Object.freeze({
    __proto__: null,
    getYear: getYear,
    getMonth: getMonth,
    getMonthHuman: getMonthHuman,
    getDate: getDate,
    getHours: getHours,
    getMinutes: getMinutes,
    getSeconds: getSeconds,
    getCenturyStart: getCenturyStart,
    getPreviousCenturyStart: getPreviousCenturyStart,
    getNextCenturyStart: getNextCenturyStart,
    getCenturyEnd: getCenturyEnd,
    getPreviousCenturyEnd: getPreviousCenturyEnd,
    getNextCenturyEnd: getNextCenturyEnd,
    getCenturyRange: getCenturyRange,
    getDecadeStart: getDecadeStart,
    getPreviousDecadeStart: getPreviousDecadeStart,
    getNextDecadeStart: getNextDecadeStart,
    getDecadeEnd: getDecadeEnd,
    getPreviousDecadeEnd: getPreviousDecadeEnd,
    getNextDecadeEnd: getNextDecadeEnd,
    getDecadeRange: getDecadeRange,
    getYearStart: getYearStart,
    getPreviousYearStart: getPreviousYearStart,
    getNextYearStart: getNextYearStart,
    getYearEnd: getYearEnd,
    getPreviousYearEnd: getPreviousYearEnd,
    getNextYearEnd: getNextYearEnd,
    getYearRange: getYearRange,
    getMonthStart: getMonthStart,
    getPreviousMonthStart: getPreviousMonthStart,
    getNextMonthStart: getNextMonthStart,
    getMonthEnd: getMonthEnd,
    getPreviousMonthEnd: getPreviousMonthEnd,
    getNextMonthEnd: getNextMonthEnd,
    getMonthRange: getMonthRange,
    getDayStart: getDayStart,
    getPreviousDayStart: getPreviousDayStart,
    getNextDayStart: getNextDayStart,
    getDayEnd: getDayEnd,
    getPreviousDayEnd: getPreviousDayEnd,
    getNextDayEnd: getNextDayEnd,
    getDayRange: getDayRange,
    getDaysInMonth: getDaysInMonth,
    getHoursMinutes: getHoursMinutes,
    getHoursMinutesSeconds: getHoursMinutesSeconds,
    getISOLocalMonth: getISOLocalMonth,
    getISOLocalDate: getISOLocalDate,
    getISOLocalDateTime: getISOLocalDateTime
});

var _CALENDAR_TYPE_LOCALE;

function _toConsumableArray$5(arr) { return _arrayWithoutHoles$5(arr) || _iterableToArray$5(arr) || _unsupportedIterableToArray$7(arr) || _nonIterableSpread$5(); }

function _nonIterableSpread$5() { throw new TypeError("Invalid attempt to spread non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); }

function _unsupportedIterableToArray$7(o, minLen) { if (!o) return; if (typeof o === "string") return _arrayLikeToArray$7(o, minLen); var n = Object.prototype.toString.call(o).slice(8, -1); if (n === "Object" && o.constructor) n = o.constructor.name; if (n === "Map" || n === "Set") return Array.from(o); if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray$7(o, minLen); }

function _iterableToArray$5(iter) { if (typeof Symbol !== "undefined" && iter[Symbol.iterator] != null || iter["@@iterator"] != null) return Array.from(iter); }

function _arrayWithoutHoles$5(arr) { if (Array.isArray(arr)) return _arrayLikeToArray$7(arr); }

function _arrayLikeToArray$7(arr, len) { if (len == null || len > arr.length) len = arr.length; for (var i = 0, arr2 = new Array(len); i < len; i++) { arr2[i] = arr[i]; } return arr2; }

function _defineProperty$j(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

var CALENDAR_TYPES = {
  ARABIC: 'Arabic',
  HEBREW: 'Hebrew',
  ISO_8601: 'ISO 8601',
  US: 'US'
};
var CALENDAR_TYPE_LOCALES = (_CALENDAR_TYPE_LOCALE = {}, _defineProperty$j(_CALENDAR_TYPE_LOCALE, CALENDAR_TYPES.US, ['en-CA', 'en-US', 'es-AR', 'es-BO', 'es-CL', 'es-CO', 'es-CR', 'es-DO', 'es-EC', 'es-GT', 'es-HN', 'es-MX', 'es-NI', 'es-PA', 'es-PE', 'es-PR', 'es-SV', 'es-VE', 'pt-BR']), _defineProperty$j(_CALENDAR_TYPE_LOCALE, CALENDAR_TYPES.ARABIC, [// ar-LB, ar-MA intentionally missing
'ar', 'ar-AE', 'ar-BH', 'ar-DZ', 'ar-EG', 'ar-IQ', 'ar-JO', 'ar-KW', 'ar-LY', 'ar-OM', 'ar-QA', 'ar-SA', 'ar-SD', 'ar-SY', 'ar-YE', 'dv', 'dv-MV', 'ps', 'ps-AR']), _defineProperty$j(_CALENDAR_TYPE_LOCALE, CALENDAR_TYPES.HEBREW, ['he', 'he-IL']), _CALENDAR_TYPE_LOCALE);
var WEEKDAYS = _toConsumableArray$5(Array(7)).map(function (el, index) {
  return index;
});

var formatterCache$1 = new Map();

function getFormatter$1(options) {
  return function (locale, date) {
    var localeWithDefault = locale || getUserLocale();

    if (!formatterCache$1.has(localeWithDefault)) {
      formatterCache$1.set(localeWithDefault, new Map());
    }

    var formatterCacheLocale = formatterCache$1.get(localeWithDefault);

    if (!formatterCacheLocale.has(options)) {
      formatterCacheLocale.set(options, new Intl.DateTimeFormat(localeWithDefault, options).format);
    }

    return formatterCacheLocale.get(options)(date);
  };
}
/**
 * Changes the hour in a Date to ensure right date formatting even if DST is messed up.
 * Workaround for bug in WebKit and Firefox with historical dates.
 * For more details, see:
 * https://bugs.chromium.org/p/chromium/issues/detail?id=750465
 * https://bugzilla.mozilla.org/show_bug.cgi?id=1385643
 *
 * @param {Date} date Date.
 */


function toSafeHour$1(date) {
  var safeDate = new Date(date);
  return new Date(safeDate.setHours(12));
}

function getSafeFormatter$1(options) {
  return function (locale, date) {
    return getFormatter$1(options)(locale, toSafeHour$1(date));
  };
}
var formatDayOptions = {
  day: 'numeric'
};
var formatLongDateOptions = {
  day: 'numeric',
  month: 'long',
  year: 'numeric'
};
var formatMonthOptions$1 = {
  month: 'long'
};
var formatMonthYearOptions = {
  month: 'long',
  year: 'numeric'
};
var formatShortWeekdayOptions = {
  weekday: 'short'
};
var formatWeekdayOptions = {
  weekday: 'long'
};
var formatYearOptions = {
  year: 'numeric'
};
var formatDay = getSafeFormatter$1(formatDayOptions);
var formatLongDate = getSafeFormatter$1(formatLongDateOptions);
var formatMonth$1 = getSafeFormatter$1(formatMonthOptions$1);
var formatMonthYear = getSafeFormatter$1(formatMonthYearOptions);
var formatShortWeekday = getSafeFormatter$1(formatShortWeekdayOptions);
var formatWeekday = getSafeFormatter$1(formatWeekdayOptions);
var formatYear = getSafeFormatter$1(formatYearOptions);

var SUNDAY = WEEKDAYS[0];
var FRIDAY = WEEKDAYS[5];
var SATURDAY = WEEKDAYS[6];
/* Simple getters - getting a property of a given point in time */

function getDayOfWeek(date) {
  var calendarType = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : CALENDAR_TYPES.ISO_8601;
  var weekday = date.getDay();

  switch (calendarType) {
    case CALENDAR_TYPES.ISO_8601:
      // Shifts days of the week so that Monday is 0, Sunday is 6
      return (weekday + 6) % 7;

    case CALENDAR_TYPES.ARABIC:
      return (weekday + 1) % 7;

    case CALENDAR_TYPES.HEBREW:
    case CALENDAR_TYPES.US:
      return weekday;

    default:
      throw new Error('Unsupported calendar type.');
  }
}
/**
 * Century
 */

function getBeginOfCenturyYear(date) {
  var beginOfCentury = getCenturyStart(date);
  return getYear(beginOfCentury);
}
/**
 * Decade
 */

function getBeginOfDecadeYear(date) {
  var beginOfDecade = getDecadeStart(date);
  return getYear(beginOfDecade);
}
/**
 * Week
 */

/**
 * Returns the beginning of a given week.
 *
 * @param {Date} date Date.
 * @param {string} calendarType Calendar type. Can be ISO 8601 or US.
 */

function getBeginOfWeek(date) {
  var calendarType = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : CALENDAR_TYPES.ISO_8601;
  var year = getYear(date);
  var monthIndex = getMonth(date);
  var day = date.getDate() - getDayOfWeek(date, calendarType);
  return new Date(year, monthIndex, day);
}
/**
 * Gets week number according to ISO 8601 or US standard.
 * In ISO 8601, Arabic and Hebrew week 1 is the one with January 4.
 * In US calendar week 1 is the one with January 1.
 *
 * @param {Date} date Date.
 * @param {string} calendarType Calendar type. Can be ISO 8601 or US.
 */

function getWeekNumber(date) {
  var calendarType = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : CALENDAR_TYPES.ISO_8601;
  var calendarTypeForWeekNumber = calendarType === CALENDAR_TYPES.US ? CALENDAR_TYPES.US : CALENDAR_TYPES.ISO_8601;
  var beginOfWeek = getBeginOfWeek(date, calendarType);
  var year = getYear(date) + 1;
  var dayInWeekOne;
  var beginOfFirstWeek; // Look for the first week one that does not come after a given date

  do {
    dayInWeekOne = new Date(year, 0, calendarTypeForWeekNumber === CALENDAR_TYPES.ISO_8601 ? 4 : 1);
    beginOfFirstWeek = getBeginOfWeek(dayInWeekOne, calendarType);
    year -= 1;
  } while (date < beginOfFirstWeek);

  return Math.round((beginOfWeek - beginOfFirstWeek) / (8.64e7 * 7)) + 1;
}
/**
 * Others
 */

/**
 * Returns the beginning of a given range.
 *
 * @param {string} rangeType Range type (e.g. 'day')
 * @param {Date} date Date.
 */

function getBegin$1(rangeType, date) {
  switch (rangeType) {
    case 'century':
      return getCenturyStart(date);

    case 'decade':
      return getDecadeStart(date);

    case 'year':
      return getYearStart(date);

    case 'month':
      return getMonthStart(date);

    case 'day':
      return getDayStart(date);

    default:
      throw new Error("Invalid rangeType: ".concat(rangeType));
  }
}
function getBeginPrevious(rangeType, date) {
  switch (rangeType) {
    case 'century':
      return getPreviousCenturyStart(date);

    case 'decade':
      return getPreviousDecadeStart(date);

    case 'year':
      return getPreviousYearStart(date);

    case 'month':
      return getPreviousMonthStart(date);

    default:
      throw new Error("Invalid rangeType: ".concat(rangeType));
  }
}
function getBeginNext(rangeType, date) {
  switch (rangeType) {
    case 'century':
      return getNextCenturyStart(date);

    case 'decade':
      return getNextDecadeStart(date);

    case 'year':
      return getNextYearStart(date);

    case 'month':
      return getNextMonthStart(date);

    default:
      throw new Error("Invalid rangeType: ".concat(rangeType));
  }
}
var getBeginPrevious2 = function getBeginPrevious2(rangeType, date) {
  switch (rangeType) {
    case 'decade':
      return getPreviousDecadeStart(date, -100);

    case 'year':
      return getPreviousYearStart(date, -10);

    case 'month':
      return getPreviousMonthStart(date, -12);

    default:
      throw new Error("Invalid rangeType: ".concat(rangeType));
  }
};
var getBeginNext2 = function getBeginNext2(rangeType, date) {
  switch (rangeType) {
    case 'decade':
      return getNextDecadeStart(date, 100);

    case 'year':
      return getNextYearStart(date, 10);

    case 'month':
      return getNextMonthStart(date, 12);

    default:
      throw new Error("Invalid rangeType: ".concat(rangeType));
  }
};
/**
 * Returns the end of a given range.
 *
 * @param {string} rangeType Range type (e.g. 'day')
 * @param {Date} date Date.
 */

function getEnd$1(rangeType, date) {
  switch (rangeType) {
    case 'century':
      return getCenturyEnd(date);

    case 'decade':
      return getDecadeEnd(date);

    case 'year':
      return getYearEnd(date);

    case 'month':
      return getMonthEnd(date);

    case 'day':
      return getDayEnd(date);

    default:
      throw new Error("Invalid rangeType: ".concat(rangeType));
  }
}
function getEndPrevious(rangeType, date) {
  switch (rangeType) {
    case 'century':
      return getPreviousCenturyEnd(date);

    case 'decade':
      return getPreviousDecadeEnd(date);

    case 'year':
      return getPreviousYearEnd(date);

    case 'month':
      return getPreviousMonthEnd(date);

    default:
      throw new Error("Invalid rangeType: ".concat(rangeType));
  }
}
var getEndPrevious2 = function getEndPrevious2(rangeType, date) {
  switch (rangeType) {
    case 'decade':
      return getPreviousDecadeEnd(date, -100);

    case 'year':
      return getPreviousYearEnd(date, -10);

    case 'month':
      return getPreviousMonthEnd(date, -12);

    default:
      throw new Error("Invalid rangeType: ".concat(rangeType));
  }
};
/**
 * Returns an array with the beginning and the end of a given range.
 *
 * @param {string} rangeType Range type (e.g. 'day')
 * @param {Date} date Date.
 */

function getRange(rangeType, date) {
  switch (rangeType) {
    case 'century':
      return getCenturyRange(date);

    case 'decade':
      return getDecadeRange(date);

    case 'year':
      return getYearRange(date);

    case 'month':
      return getMonthRange(date);

    case 'day':
      return getDayRange(date);

    default:
      throw new Error("Invalid rangeType: ".concat(rangeType));
  }
}
/**
 * Creates a range out of two values, ensuring they are in order and covering entire period ranges.
 *
 * @param {string} rangeType Range type (e.g. 'day')
 * @param {Date} date1 First date.
 * @param {Date} date2 Second date.
 */

function getValueRange(rangeType, date1, date2) {
  var rawNextValue = [date1, date2].sort(function (a, b) {
    return a - b;
  });
  return [getBegin$1(rangeType, rawNextValue[0]), getEnd$1(rangeType, rawNextValue[1])];
}

function toYearLabel(locale) {
  var formatYear$1 = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : formatYear;
  var dates = arguments.length > 2 ? arguments[2] : undefined;
  return dates.map(function (date) {
    return formatYear$1(locale, date);
  }).join(' – ');
}
/**
 * Returns a string labelling a century of a given date.
 * For example, for 2017 it will return 2001-2100.
 *
 * @param {Date|String|Number} date Date or a year as a string or as a number.
 */


function getCenturyLabel(locale, formatYear, date) {
  return toYearLabel(locale, formatYear, getCenturyRange(date));
}
/**
 * Returns a string labelling a century of a given date.
 * For example, for 2017 it will return 2011-2020.
 *
 * @param {Date|String|Number} date Date or a year as a string or as a number.
 */

function getDecadeLabel(locale, formatYear, date) {
  return toYearLabel(locale, formatYear, getDecadeRange(date));
}
/**
 * Returns a boolean determining whether a given date is on Saturday or Sunday.
 *
 * @param {Date} date Date.
 */

function isWeekend(date) {
  var calendarType = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : CALENDAR_TYPES.ISO_8601;
  var weekday = date.getDay();

  switch (calendarType) {
    case CALENDAR_TYPES.ARABIC:
    case CALENDAR_TYPES.HEBREW:
      return weekday === FRIDAY || weekday === SATURDAY;

    case CALENDAR_TYPES.ISO_8601:
    case CALENDAR_TYPES.US:
      return weekday === SATURDAY || weekday === SUNDAY;

    default:
      throw new Error('Unsupported calendar type.');
  }
}

function _typeof$8(obj) { "@babel/helpers - typeof"; return _typeof$8 = "function" == typeof Symbol && "symbol" == typeof Symbol.iterator ? function (obj) { return typeof obj; } : function (obj) { return obj && "function" == typeof Symbol && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj; }, _typeof$8(obj); }
var calendarTypes = Object.values(CALENDAR_TYPES);
var allViews$2 = ['century', 'decade', 'year', 'month'];
var isCalendarType = propTypes$2.exports.oneOf(calendarTypes);
var isClassName = propTypes$2.exports.oneOfType([propTypes$2.exports.string, propTypes$2.exports.arrayOf(propTypes$2.exports.string)]);
var isMinDate$1 = function isMinDate(props, propName, componentName) {
  var minDate = props[propName];

  if (!minDate) {
    return null;
  }

  if (!(minDate instanceof Date)) {
    return new Error("Invalid prop `".concat(propName, "` of type `").concat(_typeof$8(minDate), "` supplied to `").concat(componentName, "`, expected instance of `Date`."));
  }

  var maxDate = props.maxDate;

  if (maxDate && minDate > maxDate) {
    return new Error("Invalid prop `".concat(propName, "` of type `").concat(_typeof$8(minDate), "` supplied to `").concat(componentName, "`, minDate cannot be larger than maxDate."));
  }

  return null;
};
var isMaxDate$1 = function isMaxDate(props, propName, componentName) {
  var maxDate = props[propName];

  if (!maxDate) {
    return null;
  }

  if (!(maxDate instanceof Date)) {
    return new Error("Invalid prop `".concat(propName, "` of type `").concat(_typeof$8(maxDate), "` supplied to `").concat(componentName, "`, expected instance of `Date`."));
  }

  var minDate = props.minDate;

  if (minDate && maxDate < minDate) {
    return new Error("Invalid prop `".concat(propName, "` of type `").concat(_typeof$8(maxDate), "` supplied to `").concat(componentName, "`, maxDate cannot be smaller than minDate."));
  }

  return null;
};
var isRef$1 = propTypes$2.exports.oneOfType([propTypes$2.exports.func, propTypes$2.exports.shape({
  current: propTypes$2.exports.any
})]);
var isValue = propTypes$2.exports.oneOfType([propTypes$2.exports.instanceOf(Date), propTypes$2.exports.arrayOf(propTypes$2.exports.instanceOf(Date))]);
var isViews = propTypes$2.exports.arrayOf(propTypes$2.exports.oneOf(allViews$2));
var isView = function isView(props, propName, componentName) {
  var view = props[propName];
  var views = props.views;
  var allowedViews = views || allViews$2;

  if (view !== undefined && allowedViews.indexOf(view) === -1) {
    return new Error("Invalid prop `".concat(propName, "` of value `").concat(view, "` supplied to `").concat(componentName, "`, expected one of [").concat(allowedViews.map(function (a) {
      return "\"".concat(a, "\"");
    }).join(', '), "]."));
  } // Everything is fine


  return null;
};

isView.isRequired = function (props, propName, componentName) {
  var view = props[propName];

  if (!view) {
    return new Error("The prop `".concat(propName, "` is marked as required in `").concat(componentName, "`, but its value is `").concat(view, "`."));
  }

  return isView(props, propName, componentName);
};

var tileGroupProps = {
  activeStartDate: propTypes$2.exports.instanceOf(Date).isRequired,
  hover: propTypes$2.exports.instanceOf(Date),
  locale: propTypes$2.exports.string,
  maxDate: isMaxDate$1,
  minDate: isMinDate$1,
  onClick: propTypes$2.exports.func,
  onMouseOver: propTypes$2.exports.func,
  tileClassName: propTypes$2.exports.oneOfType([propTypes$2.exports.func, isClassName]),
  tileContent: propTypes$2.exports.oneOfType([propTypes$2.exports.func, propTypes$2.exports.node]),
  value: isValue,
  valueType: propTypes$2.exports.string
};
var tileProps = {
  activeStartDate: propTypes$2.exports.instanceOf(Date).isRequired,
  classes: propTypes$2.exports.arrayOf(propTypes$2.exports.string).isRequired,
  date: propTypes$2.exports.instanceOf(Date).isRequired,
  locale: propTypes$2.exports.string,
  maxDate: isMaxDate$1,
  minDate: isMinDate$1,
  onClick: propTypes$2.exports.func,
  onMouseOver: propTypes$2.exports.func,
  style: propTypes$2.exports.objectOf(propTypes$2.exports.oneOfType([propTypes$2.exports.string, propTypes$2.exports.number])),
  tileClassName: propTypes$2.exports.oneOfType([propTypes$2.exports.func, isClassName]),
  tileContent: propTypes$2.exports.oneOfType([propTypes$2.exports.func, propTypes$2.exports.node]),
  tileDisabled: propTypes$2.exports.func
};

var className$6 = 'react-calendar__navigation';
function Navigation(_ref) {
  var activeStartDate = _ref.activeStartDate,
      drillUp = _ref.drillUp,
      _ref$formatMonthYear = _ref.formatMonthYear,
      formatMonthYear$1 = _ref$formatMonthYear === void 0 ? formatMonthYear : _ref$formatMonthYear,
      _ref$formatYear = _ref.formatYear,
      formatYear$1 = _ref$formatYear === void 0 ? formatYear : _ref$formatYear,
      locale = _ref.locale,
      maxDate = _ref.maxDate,
      minDate = _ref.minDate,
      _ref$navigationAriaLa = _ref.navigationAriaLabel,
      navigationAriaLabel = _ref$navigationAriaLa === void 0 ? '' : _ref$navigationAriaLa,
      navigationAriaLive = _ref.navigationAriaLive,
      navigationLabel = _ref.navigationLabel,
      _ref$next2AriaLabel = _ref.next2AriaLabel,
      next2AriaLabel = _ref$next2AriaLabel === void 0 ? '' : _ref$next2AriaLabel,
      _ref$next2Label = _ref.next2Label,
      next2Label = _ref$next2Label === void 0 ? '»' : _ref$next2Label,
      _ref$nextAriaLabel = _ref.nextAriaLabel,
      nextAriaLabel = _ref$nextAriaLabel === void 0 ? '' : _ref$nextAriaLabel,
      _ref$nextLabel = _ref.nextLabel,
      nextLabel = _ref$nextLabel === void 0 ? '›' : _ref$nextLabel,
      _ref$prev2AriaLabel = _ref.prev2AriaLabel,
      prev2AriaLabel = _ref$prev2AriaLabel === void 0 ? '' : _ref$prev2AriaLabel,
      _ref$prev2Label = _ref.prev2Label,
      prev2Label = _ref$prev2Label === void 0 ? '«' : _ref$prev2Label,
      _ref$prevAriaLabel = _ref.prevAriaLabel,
      prevAriaLabel = _ref$prevAriaLabel === void 0 ? '' : _ref$prevAriaLabel,
      _ref$prevLabel = _ref.prevLabel,
      prevLabel = _ref$prevLabel === void 0 ? '‹' : _ref$prevLabel,
      setActiveStartDate = _ref.setActiveStartDate,
      showDoubleView = _ref.showDoubleView,
      view = _ref.view,
      views = _ref.views;
  var drillUpAvailable = views.indexOf(view) > 0;
  var shouldShowPrevNext2Buttons = view !== 'century';
  var previousActiveStartDate = getBeginPrevious(view, activeStartDate);
  var previousActiveStartDate2 = shouldShowPrevNext2Buttons && getBeginPrevious2(view, activeStartDate);
  var nextActiveStartDate = getBeginNext(view, activeStartDate);
  var nextActiveStartDate2 = shouldShowPrevNext2Buttons && getBeginNext2(view, activeStartDate);

  var prevButtonDisabled = function () {
    if (previousActiveStartDate.getFullYear() < 0) {
      return true;
    }

    var previousActiveEndDate = getEndPrevious(view, activeStartDate);
    return minDate && minDate >= previousActiveEndDate;
  }();

  var prev2ButtonDisabled = shouldShowPrevNext2Buttons && function () {
    if (previousActiveStartDate2.getFullYear() < 0) {
      return true;
    }

    var previousActiveEndDate = getEndPrevious2(view, activeStartDate);
    return minDate && minDate >= previousActiveEndDate;
  }();

  var nextButtonDisabled = maxDate && maxDate < nextActiveStartDate;
  var next2ButtonDisabled = shouldShowPrevNext2Buttons && maxDate && maxDate < nextActiveStartDate2;

  function onClickPrevious() {
    setActiveStartDate(previousActiveStartDate, 'prev');
  }

  function onClickPrevious2() {
    setActiveStartDate(previousActiveStartDate2, 'prev2');
  }

  function onClickNext() {
    setActiveStartDate(nextActiveStartDate, 'next');
  }

  function onClickNext2() {
    setActiveStartDate(nextActiveStartDate2, 'next2');
  }

  function renderLabel(date) {
    var label = function () {
      switch (view) {
        case 'century':
          return getCenturyLabel(locale, formatYear$1, date);

        case 'decade':
          return getDecadeLabel(locale, formatYear$1, date);

        case 'year':
          return formatYear$1(locale, date);

        case 'month':
          return formatMonthYear$1(locale, date);

        default:
          throw new Error("Invalid view: ".concat(view, "."));
      }
    }();

    return navigationLabel ? navigationLabel({
      date: date,
      label: label,
      locale: locale || getUserLocale(),
      view: view
    }) : label;
  }

  function renderButton() {
    var labelClassName = "".concat(className$6, "__label");
    return /*#__PURE__*/React.createElement("button", {
      "aria-label": navigationAriaLabel,
      "aria-live": navigationAriaLive,
      className: labelClassName,
      disabled: !drillUpAvailable,
      onClick: drillUp,
      style: {
        flexGrow: 1
      },
      type: "button"
    }, /*#__PURE__*/React.createElement("span", {
      className: "".concat(labelClassName, "__labelText ").concat(labelClassName, "__labelText--from")
    }, renderLabel(activeStartDate)), showDoubleView && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("span", {
      className: "".concat(labelClassName, "__divider")
    }, " \u2013 "), /*#__PURE__*/React.createElement("span", {
      className: "".concat(labelClassName, "__labelText ").concat(labelClassName, "__labelText--to")
    }, renderLabel(nextActiveStartDate))));
  }

  return /*#__PURE__*/React.createElement("div", {
    className: className$6
  }, prev2Label !== null && shouldShowPrevNext2Buttons && /*#__PURE__*/React.createElement("button", {
    "aria-label": prev2AriaLabel,
    className: "".concat(className$6, "__arrow ").concat(className$6, "__prev2-button"),
    disabled: prev2ButtonDisabled,
    onClick: onClickPrevious2,
    type: "button"
  }, prev2Label), prevLabel !== null && /*#__PURE__*/React.createElement("button", {
    "aria-label": prevAriaLabel,
    className: "".concat(className$6, "__arrow ").concat(className$6, "__prev-button"),
    disabled: prevButtonDisabled,
    onClick: onClickPrevious,
    type: "button"
  }, prevLabel), renderButton(), nextLabel !== null && /*#__PURE__*/React.createElement("button", {
    "aria-label": nextAriaLabel,
    className: "".concat(className$6, "__arrow ").concat(className$6, "__next-button"),
    disabled: nextButtonDisabled,
    onClick: onClickNext,
    type: "button"
  }, nextLabel), next2Label !== null && shouldShowPrevNext2Buttons && /*#__PURE__*/React.createElement("button", {
    "aria-label": next2AriaLabel,
    className: "".concat(className$6, "__arrow ").concat(className$6, "__next2-button"),
    disabled: next2ButtonDisabled,
    onClick: onClickNext2,
    type: "button"
  }, next2Label));
}
Navigation.propTypes = {
  activeStartDate: propTypes$2.exports.instanceOf(Date).isRequired,
  drillUp: propTypes$2.exports.func.isRequired,
  formatMonthYear: propTypes$2.exports.func,
  formatYear: propTypes$2.exports.func,
  locale: propTypes$2.exports.string,
  maxDate: propTypes$2.exports.instanceOf(Date),
  minDate: propTypes$2.exports.instanceOf(Date),
  navigationAriaLabel: propTypes$2.exports.string,
  navigationAriaLive: propTypes$2.exports.string,
  navigationLabel: propTypes$2.exports.func,
  next2AriaLabel: propTypes$2.exports.string,
  next2Label: propTypes$2.exports.node,
  nextAriaLabel: propTypes$2.exports.string,
  nextLabel: propTypes$2.exports.node,
  prev2AriaLabel: propTypes$2.exports.string,
  prev2Label: propTypes$2.exports.node,
  prevAriaLabel: propTypes$2.exports.string,
  prevLabel: propTypes$2.exports.node,
  setActiveStartDate: propTypes$2.exports.func.isRequired,
  showDoubleView: propTypes$2.exports.bool,
  view: isView.isRequired,
  views: isViews.isRequired
};

var _excluded$9 = ["children", "className", "direction", "count", "offset", "style", "wrap"];

function _extends$g() { _extends$g = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$g.apply(this, arguments); }

function ownKeys$i(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread$i(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys$i(Object(source), !0).forEach(function (key) { _defineProperty$i(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys$i(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

function _defineProperty$i(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

function _objectWithoutProperties$a(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose$a(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }

function _objectWithoutPropertiesLoose$a(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }

function toPercent(num) {
  return "".concat(num, "%");
}

function Flex(_ref) {
  var children = _ref.children,
      className = _ref.className,
      direction = _ref.direction,
      count = _ref.count,
      offset = _ref.offset,
      style = _ref.style,
      wrap = _ref.wrap,
      otherProps = _objectWithoutProperties$a(_ref, _excluded$9);

  return /*#__PURE__*/React.createElement("div", _extends$g({
    className: className,
    style: _objectSpread$i({
      display: 'flex',
      flexDirection: direction,
      flexWrap: wrap ? 'wrap' : 'no-wrap'
    }, style)
  }, otherProps), React.Children.map(children, function (child, index) {
    return /*#__PURE__*/React.cloneElement(child, _objectSpread$i(_objectSpread$i({}, child.props), {}, {
      style: {
        flexBasis: toPercent(100 / count),
        flexShrink: 0,
        flexGrow: 0,
        overflow: 'hidden',
        marginLeft: offset && index === 0 ? toPercent(100 * offset / count) : null
      }
    }));
  }));
}
Flex.propTypes = {
  children: propTypes$2.exports.node,
  className: propTypes$2.exports.string,
  count: propTypes$2.exports.number.isRequired,
  direction: propTypes$2.exports.string,
  offset: propTypes$2.exports.number,
  style: propTypes$2.exports.objectOf(propTypes$2.exports.oneOfType([propTypes$2.exports.string, propTypes$2.exports.number])),
  wrap: propTypes$2.exports.bool
};

function _toConsumableArray$4(arr) { return _arrayWithoutHoles$4(arr) || _iterableToArray$4(arr) || _unsupportedIterableToArray$6(arr) || _nonIterableSpread$4(); }

function _nonIterableSpread$4() { throw new TypeError("Invalid attempt to spread non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); }

function _unsupportedIterableToArray$6(o, minLen) { if (!o) return; if (typeof o === "string") return _arrayLikeToArray$6(o, minLen); var n = Object.prototype.toString.call(o).slice(8, -1); if (n === "Object" && o.constructor) n = o.constructor.name; if (n === "Map" || n === "Set") return Array.from(o); if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray$6(o, minLen); }

function _iterableToArray$4(iter) { if (typeof Symbol !== "undefined" && iter[Symbol.iterator] != null || iter["@@iterator"] != null) return Array.from(iter); }

function _arrayWithoutHoles$4(arr) { if (Array.isArray(arr)) return _arrayLikeToArray$6(arr); }

function _arrayLikeToArray$6(arr, len) { if (len == null || len > arr.length) len = arr.length; for (var i = 0, arr2 = new Array(len); i < len; i++) { arr2[i] = arr[i]; } return arr2; }
/**
 * Returns a value no smaller than min and no larger than max.
 *
 * @param {*} value Value to return.
 * @param {*} min Minimum return value.
 * @param {*} max Maximum return value.
 */

function between$1(value, min, max) {
  if (min && min > value) {
    return min;
  }

  if (max && max < value) {
    return max;
  }

  return value;
}
function isValueWithinRange(value, range) {
  return range[0] <= value && range[1] >= value;
}
function isRangeWithinRange(greaterRange, smallerRange) {
  return greaterRange[0] <= smallerRange[0] && greaterRange[1] >= smallerRange[1];
}
function doRangesOverlap(range1, range2) {
  return isValueWithinRange(range1[0], range2) || isValueWithinRange(range1[1], range2);
}

function getRangeClassNames(valueRange, dateRange, baseClassName) {
  var isRange = doRangesOverlap(dateRange, valueRange);
  var classes = [];

  if (isRange) {
    classes.push(baseClassName);
    var isRangeStart = isValueWithinRange(valueRange[0], dateRange);
    var isRangeEnd = isValueWithinRange(valueRange[1], dateRange);

    if (isRangeStart) {
      classes.push("".concat(baseClassName, "Start"));
    }

    if (isRangeEnd) {
      classes.push("".concat(baseClassName, "End"));
    }

    if (isRangeStart && isRangeEnd) {
      classes.push("".concat(baseClassName, "BothEnds"));
    }
  }

  return classes;
}

function getTileClasses() {
  var _ref = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
      value = _ref.value,
      valueType = _ref.valueType,
      date = _ref.date,
      dateType = _ref.dateType,
      hover = _ref.hover;

  var className = 'react-calendar__tile';
  var classes = [className];

  if (!date) {
    return classes;
  }

  if (!Array.isArray(date) && !dateType) {
    throw new Error('getTileClasses(): Unable to get tile activity classes because one or more required arguments were not passed.');
  }

  var now = new Date();
  var dateRange = Array.isArray(date) ? date : getRange(dateType, date);

  if (isValueWithinRange(now, dateRange)) {
    classes.push("".concat(className, "--now"));
  }

  if (!value) {
    return classes;
  }

  if (!Array.isArray(value) && !valueType) {
    throw new Error('getTileClasses(): Unable to get tile activity classes because one or more required arguments were not passed.');
  }

  var valueRange = Array.isArray(value) ? value : getRange(valueType, value);

  if (isRangeWithinRange(valueRange, dateRange)) {
    classes.push("".concat(className, "--active"));
  } else if (doRangesOverlap(valueRange, dateRange)) {
    classes.push("".concat(className, "--hasActive"));
  }

  var valueRangeClassNames = getRangeClassNames(valueRange, dateRange, "".concat(className, "--range"));
  classes.push.apply(classes, _toConsumableArray$4(valueRangeClassNames));
  var valueArray = [].concat(value);

  if (hover && valueArray.length === 1) {
    var hoverRange = hover > valueRange[0] ? [valueRange[0], hover] : [hover, valueRange[0]];
    var hoverRangeClassNames = getRangeClassNames(hoverRange, dateRange, "".concat(className, "--hover"));
    classes.push.apply(classes, _toConsumableArray$4(hoverRangeClassNames));
  }

  return classes;
}

var _excluded$8 = ["className", "count", "dateTransform", "dateType", "end", "hover", "offset", "start", "step", "tile", "value", "valueType"];

function ownKeys$h(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread$h(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys$h(Object(source), !0).forEach(function (key) { _defineProperty$h(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys$h(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

function _defineProperty$h(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

function _extends$f() { _extends$f = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$f.apply(this, arguments); }

function _objectWithoutProperties$9(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose$9(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }

function _objectWithoutPropertiesLoose$9(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }
function TileGroup(_ref) {
  var className = _ref.className,
      _ref$count = _ref.count,
      count = _ref$count === void 0 ? 3 : _ref$count,
      dateTransform = _ref.dateTransform,
      dateType = _ref.dateType,
      end = _ref.end,
      hover = _ref.hover,
      offset = _ref.offset,
      start = _ref.start,
      _ref$step = _ref.step,
      step = _ref$step === void 0 ? 1 : _ref$step,
      Tile = _ref.tile,
      value = _ref.value,
      valueType = _ref.valueType,
      tileProps = _objectWithoutProperties$9(_ref, _excluded$8);

  var tiles = [];

  for (var point = start; point <= end; point += step) {
    var date = dateTransform(point);
    tiles.push( /*#__PURE__*/React.createElement(Tile, _extends$f({
      key: date.getTime(),
      classes: getTileClasses({
        value: value,
        valueType: valueType,
        date: date,
        dateType: dateType,
        hover: hover
      }),
      date: date,
      point: point
    }, tileProps)));
  }

  return /*#__PURE__*/React.createElement(Flex, {
    className: className,
    count: count,
    offset: offset,
    wrap: true
  }, tiles);
}
TileGroup.propTypes = _objectSpread$h(_objectSpread$h({}, tileGroupProps), {}, {
  activeStartDate: propTypes$2.exports.instanceOf(Date),
  count: propTypes$2.exports.number,
  dateTransform: propTypes$2.exports.func.isRequired,
  dateType: propTypes$2.exports.string,
  offset: propTypes$2.exports.number,
  step: propTypes$2.exports.number,
  tile: propTypes$2.exports.func.isRequired
});

function _typeof$7(obj) { "@babel/helpers - typeof"; return _typeof$7 = "function" == typeof Symbol && "symbol" == typeof Symbol.iterator ? function (obj) { return typeof obj; } : function (obj) { return obj && "function" == typeof Symbol && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj; }, _typeof$7(obj); }

function ownKeys$g(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread$g(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys$g(Object(source), !0).forEach(function (key) { _defineProperty$g(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys$g(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

function _classCallCheck$6(instance, Constructor) { if (!(instance instanceof Constructor)) { throw new TypeError("Cannot call a class as a function"); } }

function _defineProperties$6(target, props) { for (var i = 0; i < props.length; i++) { var descriptor = props[i]; descriptor.enumerable = descriptor.enumerable || false; descriptor.configurable = true; if ("value" in descriptor) descriptor.writable = true; Object.defineProperty(target, descriptor.key, descriptor); } }

function _createClass$6(Constructor, protoProps, staticProps) { if (protoProps) _defineProperties$6(Constructor.prototype, protoProps); if (staticProps) _defineProperties$6(Constructor, staticProps); Object.defineProperty(Constructor, "prototype", { writable: false }); return Constructor; }

function _inherits$6(subClass, superClass) { if (typeof superClass !== "function" && superClass !== null) { throw new TypeError("Super expression must either be null or a function"); } subClass.prototype = Object.create(superClass && superClass.prototype, { constructor: { value: subClass, writable: true, configurable: true } }); Object.defineProperty(subClass, "prototype", { writable: false }); if (superClass) _setPrototypeOf$7(subClass, superClass); }

function _setPrototypeOf$7(o, p) { _setPrototypeOf$7 = Object.setPrototypeOf ? Object.setPrototypeOf.bind() : function _setPrototypeOf(o, p) { o.__proto__ = p; return o; }; return _setPrototypeOf$7(o, p); }

function _createSuper$6(Derived) { var hasNativeReflectConstruct = _isNativeReflectConstruct$6(); return function _createSuperInternal() { var Super = _getPrototypeOf$6(Derived), result; if (hasNativeReflectConstruct) { var NewTarget = _getPrototypeOf$6(this).constructor; result = Reflect.construct(Super, arguments, NewTarget); } else { result = Super.apply(this, arguments); } return _possibleConstructorReturn$6(this, result); }; }

function _possibleConstructorReturn$6(self, call) { if (call && (_typeof$7(call) === "object" || typeof call === "function")) { return call; } else if (call !== void 0) { throw new TypeError("Derived constructors may only return object or undefined"); } return _assertThisInitialized$6(self); }

function _assertThisInitialized$6(self) { if (self === void 0) { throw new ReferenceError("this hasn't been initialised - super() hasn't been called"); } return self; }

function _isNativeReflectConstruct$6() { if (typeof Reflect === "undefined" || !Reflect.construct) return false; if (Reflect.construct.sham) return false; if (typeof Proxy === "function") return true; try { Boolean.prototype.valueOf.call(Reflect.construct(Boolean, [], function () {})); return true; } catch (e) { return false; } }

function _getPrototypeOf$6(o) { _getPrototypeOf$6 = Object.setPrototypeOf ? Object.getPrototypeOf.bind() : function _getPrototypeOf(o) { return o.__proto__ || Object.getPrototypeOf(o); }; return _getPrototypeOf$6(o); }

function _defineProperty$g(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

function datesAreDifferent(date1, date2) {
  return date1 && !date2 || !date1 && date2 || date1 && date2 && date1.getTime() !== date2.getTime();
}

function getValue$1(nextProps, prop) {
  var activeStartDate = nextProps.activeStartDate,
      date = nextProps.date,
      view = nextProps.view;
  return typeof prop === 'function' ? prop({
    activeStartDate: activeStartDate,
    date: date,
    view: view
  }) : prop;
}

var Tile = /*#__PURE__*/function (_Component) {
  _inherits$6(Tile, _Component);

  var _super = _createSuper$6(Tile);

  function Tile() {
    var _this;

    _classCallCheck$6(this, Tile);

    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    _this = _super.call.apply(_super, [this].concat(args));

    _defineProperty$g(_assertThisInitialized$6(_this), "state", {});

    return _this;
  }

  _createClass$6(Tile, [{
    key: "render",
    value: function render() {
      var _this$props = this.props,
          activeStartDate = _this$props.activeStartDate,
          children = _this$props.children,
          classes = _this$props.classes,
          date = _this$props.date,
          formatAbbr = _this$props.formatAbbr,
          locale = _this$props.locale,
          maxDate = _this$props.maxDate,
          maxDateTransform = _this$props.maxDateTransform,
          minDate = _this$props.minDate,
          minDateTransform = _this$props.minDateTransform,
          onClick = _this$props.onClick,
          onMouseOver = _this$props.onMouseOver,
          style = _this$props.style,
          tileDisabled = _this$props.tileDisabled,
          view = _this$props.view;
      var _this$state = this.state,
          tileClassName = _this$state.tileClassName,
          tileContent = _this$state.tileContent;
      return /*#__PURE__*/React.createElement("button", {
        className: mergeClassNames(classes, tileClassName),
        disabled: minDate && minDateTransform(minDate) > date || maxDate && maxDateTransform(maxDate) < date || tileDisabled && tileDisabled({
          activeStartDate: activeStartDate,
          date: date,
          view: view
        }),
        onClick: onClick && function (event) {
          return onClick(date, event);
        },
        onFocus: onMouseOver && function () {
          return onMouseOver(date);
        },
        onMouseOver: onMouseOver && function () {
          return onMouseOver(date);
        },
        style: style,
        type: "button"
      }, formatAbbr ? /*#__PURE__*/React.createElement("abbr", {
        "aria-label": formatAbbr(locale, date)
      }, children) : children, tileContent);
    }
  }], [{
    key: "getDerivedStateFromProps",
    value: function getDerivedStateFromProps(nextProps, prevState) {
      var activeStartDate = nextProps.activeStartDate,
          tileClassName = nextProps.tileClassName,
          tileContent = nextProps.tileContent;
      var nextState = {};

      if (tileClassName !== prevState.tileClassNameProps || datesAreDifferent(activeStartDate, prevState.activeStartDateProps)) {
        nextState.tileClassName = getValue$1(nextProps, tileClassName);
        nextState.tileClassNameProps = tileClassName;
      }

      if (tileContent !== prevState.tileContentProps || datesAreDifferent(activeStartDate, prevState.activeStartDateProps)) {
        nextState.tileContent = getValue$1(nextProps, tileContent);
        nextState.tileContentProps = tileContent;
      }

      nextState.activeStartDateProps = activeStartDate;
      return nextState;
    }
  }]);

  return Tile;
}(Component);
Tile.propTypes = _objectSpread$g(_objectSpread$g({}, tileProps), {}, {
  children: propTypes$2.exports.node.isRequired,
  formatAbbr: propTypes$2.exports.func,
  maxDateTransform: propTypes$2.exports.func.isRequired,
  minDateTransform: propTypes$2.exports.func.isRequired
});

var _excluded$7 = ["classes", "formatYear"];

function ownKeys$f(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread$f(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys$f(Object(source), !0).forEach(function (key) { _defineProperty$f(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys$f(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

function _defineProperty$f(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

function _extends$e() { _extends$e = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$e.apply(this, arguments); }

function _objectWithoutProperties$8(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose$8(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }

function _objectWithoutPropertiesLoose$8(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }
var className$5 = 'react-calendar__century-view__decades__decade';
function Decade(_ref) {
  var classes = _ref.classes,
      _ref$formatYear = _ref.formatYear,
      formatYear$1 = _ref$formatYear === void 0 ? formatYear : _ref$formatYear,
      otherProps = _objectWithoutProperties$8(_ref, _excluded$7);

  var date = otherProps.date,
      locale = otherProps.locale;
  return /*#__PURE__*/React.createElement(Tile, _extends$e({}, otherProps, {
    classes: [].concat(classes, className$5),
    maxDateTransform: getDecadeEnd,
    minDateTransform: getDecadeStart,
    view: "century"
  }), getDecadeLabel(locale, formatYear$1, date));
}
Decade.propTypes = _objectSpread$f(_objectSpread$f({}, tileProps), {}, {
  formatYear: propTypes$2.exports.func
});

function ownKeys$e(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread$e(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys$e(Object(source), !0).forEach(function (key) { _defineProperty$e(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys$e(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

function _defineProperty$e(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

function _extends$d() { _extends$d = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$d.apply(this, arguments); }
function Decades(props) {
  var activeStartDate = props.activeStartDate;
  var start = getBeginOfCenturyYear(activeStartDate);
  var end = start + 99;
  return /*#__PURE__*/React.createElement(TileGroup, _extends$d({}, props, {
    className: "react-calendar__century-view__decades",
    dateTransform: getDecadeStart,
    dateType: "decade",
    end: end,
    start: start,
    step: 10,
    tile: Decade
  }));
}
Decades.propTypes = _objectSpread$e({}, tileGroupProps);

function CenturyView(props) {
  function renderDecades() {
    return /*#__PURE__*/React.createElement(Decades, props);
  }

  return /*#__PURE__*/React.createElement("div", {
    className: "react-calendar__century-view"
  }, renderDecades());
}

var _excluded$6 = ["classes", "formatYear"];

function ownKeys$d(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread$d(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys$d(Object(source), !0).forEach(function (key) { _defineProperty$d(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys$d(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

function _defineProperty$d(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

function _extends$c() { _extends$c = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$c.apply(this, arguments); }

function _objectWithoutProperties$7(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose$7(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }

function _objectWithoutPropertiesLoose$7(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }
var className$4 = 'react-calendar__decade-view__years__year';
function Year(_ref) {
  var classes = _ref.classes,
      _ref$formatYear = _ref.formatYear,
      formatYear$1 = _ref$formatYear === void 0 ? formatYear : _ref$formatYear,
      otherProps = _objectWithoutProperties$7(_ref, _excluded$6);

  var date = otherProps.date,
      locale = otherProps.locale;
  return /*#__PURE__*/React.createElement(Tile, _extends$c({}, otherProps, {
    classes: [].concat(classes, className$4),
    maxDateTransform: getYearEnd,
    minDateTransform: getYearStart,
    view: "decade"
  }), formatYear$1(locale, date));
}
Year.propTypes = _objectSpread$d(_objectSpread$d({}, tileProps), {}, {
  formatYear: propTypes$2.exports.func
});

function ownKeys$c(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread$c(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys$c(Object(source), !0).forEach(function (key) { _defineProperty$c(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys$c(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

function _defineProperty$c(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

function _extends$b() { _extends$b = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$b.apply(this, arguments); }
function Years(props) {
  var activeStartDate = props.activeStartDate;
  var start = getBeginOfDecadeYear(activeStartDate);
  var end = start + 9;
  return /*#__PURE__*/React.createElement(TileGroup, _extends$b({}, props, {
    className: "react-calendar__decade-view__years",
    dateTransform: function dateTransform(year) {
      var date = new Date();
      date.setFullYear(year, 0, 1);
      date.setHours(0, 0, 0, 0);
      return date;
    },
    dateType: "year",
    end: end,
    start: start,
    tile: Year
  }));
}
Years.propTypes = _objectSpread$c({}, tileGroupProps);

function DecadeView(props) {
  function renderYears() {
    return /*#__PURE__*/React.createElement(Years, props);
  }

  return /*#__PURE__*/React.createElement("div", {
    className: "react-calendar__decade-view"
  }, renderYears());
}

var _excluded$5 = ["classes", "formatMonth", "formatMonthYear"];

function ownKeys$b(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread$b(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys$b(Object(source), !0).forEach(function (key) { _defineProperty$b(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys$b(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

function _defineProperty$b(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

function _extends$a() { _extends$a = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$a.apply(this, arguments); }

function _objectWithoutProperties$6(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose$6(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }

function _objectWithoutPropertiesLoose$6(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }
var className$3 = 'react-calendar__year-view__months__month';
function Month(_ref) {
  var classes = _ref.classes,
      _ref$formatMonth = _ref.formatMonth,
      formatMonth = _ref$formatMonth === void 0 ? formatMonth$1 : _ref$formatMonth,
      _ref$formatMonthYear = _ref.formatMonthYear,
      formatMonthYear$1 = _ref$formatMonthYear === void 0 ? formatMonthYear : _ref$formatMonthYear,
      otherProps = _objectWithoutProperties$6(_ref, _excluded$5);

  var date = otherProps.date,
      locale = otherProps.locale;
  return /*#__PURE__*/React.createElement(Tile, _extends$a({}, otherProps, {
    classes: [].concat(classes, className$3),
    formatAbbr: formatMonthYear$1,
    maxDateTransform: getMonthEnd,
    minDateTransform: getMonthStart,
    view: "year"
  }), formatMonth(locale, date));
}
Month.propTypes = _objectSpread$b(_objectSpread$b({}, tileProps), {}, {
  formatMonth: propTypes$2.exports.func,
  formatMonthYear: propTypes$2.exports.func
});

function ownKeys$a(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread$a(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys$a(Object(source), !0).forEach(function (key) { _defineProperty$a(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys$a(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

function _defineProperty$a(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

function _extends$9() { _extends$9 = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$9.apply(this, arguments); }
function Months(props) {
  var activeStartDate = props.activeStartDate;
  var start = 0;
  var end = 11;
  var year = getYear(activeStartDate);
  return /*#__PURE__*/React.createElement(TileGroup, _extends$9({}, props, {
    className: "react-calendar__year-view__months",
    dateTransform: function dateTransform(monthIndex) {
      var date = new Date();
      date.setFullYear(year, monthIndex, 1);
      date.setHours(0, 0, 0, 0);
      return date;
    },
    dateType: "month",
    end: end,
    start: start,
    tile: Month
  }));
}
Months.propTypes = _objectSpread$a(_objectSpread$a({}, tileGroupProps), {}, {
  locale: propTypes$2.exports.string
});

function YearView(props) {
  function renderMonths() {
    return /*#__PURE__*/React.createElement(Months, props);
  }

  return /*#__PURE__*/React.createElement("div", {
    className: "react-calendar__year-view"
  }, renderMonths());
}

var _excluded$4 = ["formatDay", "formatLongDate", "calendarType", "classes", "currentMonthIndex"];

function ownKeys$9(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread$9(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys$9(Object(source), !0).forEach(function (key) { _defineProperty$9(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys$9(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

function _defineProperty$9(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

function _extends$8() { _extends$8 = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$8.apply(this, arguments); }

function _objectWithoutProperties$5(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose$5(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }

function _objectWithoutPropertiesLoose$5(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }
var className$2 = 'react-calendar__month-view__days__day';
function Day(_ref) {
  var _ref$formatDay = _ref.formatDay,
      formatDay$1 = _ref$formatDay === void 0 ? formatDay : _ref$formatDay,
      _ref$formatLongDate = _ref.formatLongDate,
      formatLongDate$1 = _ref$formatLongDate === void 0 ? formatLongDate : _ref$formatLongDate,
      calendarType = _ref.calendarType,
      classes = _ref.classes,
      currentMonthIndex = _ref.currentMonthIndex,
      otherProps = _objectWithoutProperties$5(_ref, _excluded$4);

  var date = otherProps.date,
      locale = otherProps.locale;
  return /*#__PURE__*/React.createElement(Tile, _extends$8({}, otherProps, {
    classes: [].concat(classes, className$2, isWeekend(date, calendarType) ? "".concat(className$2, "--weekend") : null, date.getMonth() !== currentMonthIndex ? "".concat(className$2, "--neighboringMonth") : null),
    formatAbbr: formatLongDate$1,
    maxDateTransform: getDayEnd,
    minDateTransform: getDayStart,
    view: "month"
  }), formatDay$1(locale, date));
}
Day.propTypes = _objectSpread$9(_objectSpread$9({}, tileProps), {}, {
  currentMonthIndex: propTypes$2.exports.number.isRequired,
  formatDay: propTypes$2.exports.func,
  formatLongDate: propTypes$2.exports.func
});

var _excluded$3 = ["showFixedNumberOfWeeks", "showNeighboringMonth"];

function ownKeys$8(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread$8(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys$8(Object(source), !0).forEach(function (key) { _defineProperty$8(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys$8(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

function _defineProperty$8(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

function _extends$7() { _extends$7 = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$7.apply(this, arguments); }

function _objectWithoutProperties$4(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose$4(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }

function _objectWithoutPropertiesLoose$4(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }
function Days(props) {
  var activeStartDate = props.activeStartDate,
      calendarType = props.calendarType;

  var showFixedNumberOfWeeks = props.showFixedNumberOfWeeks,
      showNeighboringMonth = props.showNeighboringMonth,
      otherProps = _objectWithoutProperties$4(props, _excluded$3);

  var year = getYear(activeStartDate);
  var monthIndex = getMonth(activeStartDate);
  var hasFixedNumberOfWeeks = showFixedNumberOfWeeks || showNeighboringMonth;
  var dayOfWeek = getDayOfWeek(activeStartDate, calendarType);
  var offset = hasFixedNumberOfWeeks ? 0 : dayOfWeek;
  /**
   * Defines on which day of the month the grid shall start. If we simply show current
   * month, we obviously start on day one, but if showNeighboringMonth is set to
   * true, we need to find the beginning of the week the first day of the month is in.
   */

  var start = (hasFixedNumberOfWeeks ? -dayOfWeek : 0) + 1;
  /**
   * Defines on which day of the month the grid shall end. If we simply show current
   * month, we need to stop on the last day of the month, but if showNeighboringMonth
   * is set to true, we need to find the end of the week the last day of the month is in.
   */

  var end = function () {
    if (showFixedNumberOfWeeks) {
      // Always show 6 weeks
      return start + 6 * 7 - 1;
    }

    var daysInMonth = getDaysInMonth(activeStartDate);

    if (showNeighboringMonth) {
      var activeEndDate = new Date();
      activeEndDate.setFullYear(year, monthIndex, daysInMonth);
      activeEndDate.setHours(0, 0, 0, 0);
      var daysUntilEndOfTheWeek = 7 - getDayOfWeek(activeEndDate, calendarType) - 1;
      return daysInMonth + daysUntilEndOfTheWeek;
    }

    return daysInMonth;
  }();

  return /*#__PURE__*/React.createElement(TileGroup, _extends$7({}, otherProps, {
    className: "react-calendar__month-view__days",
    count: 7,
    currentMonthIndex: monthIndex,
    dateTransform: function dateTransform(day) {
      var date = new Date();
      date.setFullYear(year, monthIndex, day);
      date.setHours(0, 0, 0, 0);
      return date;
    },
    dateType: "day",
    end: end,
    offset: offset,
    start: start,
    tile: Day
  }));
}
Days.propTypes = _objectSpread$8({
  calendarType: isCalendarType.isRequired,
  showFixedNumberOfWeeks: propTypes$2.exports.bool,
  showNeighboringMonth: propTypes$2.exports.bool
}, tileGroupProps);

var className$1 = 'react-calendar__month-view__weekdays';
var weekdayClassName = "".concat(className$1, "__weekday");
function Weekdays(props) {
  var calendarType = props.calendarType,
      _props$formatShortWee = props.formatShortWeekday,
      formatShortWeekday$1 = _props$formatShortWee === void 0 ? formatShortWeekday : _props$formatShortWee,
      _props$formatWeekday = props.formatWeekday,
      formatWeekday$1 = _props$formatWeekday === void 0 ? formatWeekday : _props$formatWeekday,
      locale = props.locale,
      onMouseLeave = props.onMouseLeave;
  var anyDate = new Date();
  var beginOfMonth = getMonthStart(anyDate);
  var year = getYear(beginOfMonth);
  var monthIndex = getMonth(beginOfMonth);
  var weekdays = [];

  for (var weekday = 1; weekday <= 7; weekday += 1) {
    var weekdayDate = new Date(year, monthIndex, weekday - getDayOfWeek(beginOfMonth, calendarType));
    var abbr = formatWeekday$1(locale, weekdayDate);
    weekdays.push( /*#__PURE__*/React.createElement("div", {
      key: weekday,
      className: mergeClassNames(weekdayClassName, isWeekend(weekdayDate, calendarType) && "".concat(weekdayClassName, "--weekend"))
    }, /*#__PURE__*/React.createElement("abbr", {
      "aria-label": abbr,
      title: abbr
    }, formatShortWeekday$1(locale, weekdayDate).replace('.', ''))));
  }

  return /*#__PURE__*/React.createElement(Flex, {
    className: className$1,
    count: 7,
    onFocus: onMouseLeave,
    onMouseOver: onMouseLeave
  }, weekdays);
}
Weekdays.propTypes = {
  calendarType: isCalendarType.isRequired,
  formatShortWeekday: propTypes$2.exports.func,
  formatWeekday: propTypes$2.exports.func,
  locale: propTypes$2.exports.string,
  onMouseLeave: propTypes$2.exports.func
};

var _excluded$2 = ["date", "onClickWeekNumber", "weekNumber"];

function _extends$6() { _extends$6 = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$6.apply(this, arguments); }

function ownKeys$7(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread$7(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys$7(Object(source), !0).forEach(function (key) { _defineProperty$7(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys$7(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

function _defineProperty$7(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

function _objectWithoutProperties$3(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose$3(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }

function _objectWithoutPropertiesLoose$3(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }
var className = 'react-calendar__tile';
function WeekNumber(_ref) {
  var date = _ref.date,
      onClickWeekNumber = _ref.onClickWeekNumber,
      weekNumber = _ref.weekNumber,
      otherProps = _objectWithoutProperties$3(_ref, _excluded$2);

  var props = _objectSpread$7({
    className: className
  }, otherProps);

  var children = /*#__PURE__*/React.createElement("span", null, weekNumber);
  return onClickWeekNumber ? /*#__PURE__*/React.createElement("button", _extends$6({}, props, {
    onClick: function onClick(event) {
      return onClickWeekNumber(weekNumber, date, event);
    },
    type: "button"
  }), children) : /*#__PURE__*/React.createElement("div", props, children);
}
WeekNumber.propTypes = {
  date: propTypes$2.exports.instanceOf(Date).isRequired,
  onClickWeekNumber: propTypes$2.exports.func,
  weekNumber: propTypes$2.exports.node.isRequired
};

function WeekNumbers(props) {
  var activeStartDate = props.activeStartDate,
      calendarType = props.calendarType,
      onClickWeekNumber = props.onClickWeekNumber,
      onMouseLeave = props.onMouseLeave,
      showFixedNumberOfWeeks = props.showFixedNumberOfWeeks;

  var numberOfWeeks = function () {
    if (showFixedNumberOfWeeks) {
      return 6;
    }

    var numberOfDays = getDaysInMonth(activeStartDate);
    var startWeekday = getDayOfWeek(activeStartDate, calendarType);
    var days = numberOfDays - (7 - startWeekday);
    return 1 + Math.ceil(days / 7);
  }();

  var dates = function () {
    var year = getYear(activeStartDate);
    var monthIndex = getMonth(activeStartDate);
    var day = getDate(activeStartDate);
    var result = [];

    for (var index = 0; index < numberOfWeeks; index += 1) {
      result.push(getBeginOfWeek(new Date(year, monthIndex, day + index * 7), calendarType));
    }

    return result;
  }();

  var weekNumbers = dates.map(function (date) {
    return getWeekNumber(date, calendarType);
  });
  return /*#__PURE__*/React.createElement(Flex, {
    className: "react-calendar__month-view__weekNumbers",
    count: numberOfWeeks,
    direction: "column",
    onFocus: onMouseLeave,
    onMouseOver: onMouseLeave,
    style: {
      flexBasis: 'calc(100% * (1 / 8)',
      flexShrink: 0
    }
  }, weekNumbers.map(function (weekNumber, weekIndex) {
    return /*#__PURE__*/React.createElement(WeekNumber, {
      key: weekNumber,
      date: dates[weekIndex],
      onClickWeekNumber: onClickWeekNumber,
      weekNumber: weekNumber
    });
  }));
}
WeekNumbers.propTypes = {
  activeStartDate: propTypes$2.exports.instanceOf(Date).isRequired,
  calendarType: isCalendarType.isRequired,
  onClickWeekNumber: propTypes$2.exports.func,
  onMouseLeave: propTypes$2.exports.func,
  showFixedNumberOfWeeks: propTypes$2.exports.bool
};

var _excluded$1 = ["calendarType", "formatShortWeekday", "formatWeekday", "onClickWeekNumber", "showWeekNumbers"];

function _extends$5() { _extends$5 = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$5.apply(this, arguments); }

function _objectWithoutProperties$2(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose$2(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }

function _objectWithoutPropertiesLoose$2(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }

function getCalendarTypeFromLocale(locale) {
  return Object.keys(CALENDAR_TYPE_LOCALES).find(function (calendarType) {
    return CALENDAR_TYPE_LOCALES[calendarType].includes(locale);
  }) || CALENDAR_TYPES.ISO_8601;
}

function MonthView(props) {
  var activeStartDate = props.activeStartDate,
      locale = props.locale,
      onMouseLeave = props.onMouseLeave,
      showFixedNumberOfWeeks = props.showFixedNumberOfWeeks;

  var _props$calendarType = props.calendarType,
      calendarType = _props$calendarType === void 0 ? getCalendarTypeFromLocale(locale) : _props$calendarType,
      formatShortWeekday = props.formatShortWeekday,
      formatWeekday = props.formatWeekday,
      onClickWeekNumber = props.onClickWeekNumber,
      showWeekNumbers = props.showWeekNumbers,
      childProps = _objectWithoutProperties$2(props, _excluded$1);

  function renderWeekdays() {
    return /*#__PURE__*/React.createElement(Weekdays, {
      calendarType: calendarType,
      formatShortWeekday: formatShortWeekday,
      formatWeekday: formatWeekday,
      locale: locale,
      onMouseLeave: onMouseLeave
    });
  }

  function renderWeekNumbers() {
    if (!showWeekNumbers) {
      return null;
    }

    return /*#__PURE__*/React.createElement(WeekNumbers, {
      activeStartDate: activeStartDate,
      calendarType: calendarType,
      onClickWeekNumber: onClickWeekNumber,
      onMouseLeave: onMouseLeave,
      showFixedNumberOfWeeks: showFixedNumberOfWeeks
    });
  }

  function renderDays() {
    return /*#__PURE__*/React.createElement(Days, _extends$5({
      calendarType: calendarType
    }, childProps));
  }

  var className = 'react-calendar__month-view';
  return /*#__PURE__*/React.createElement("div", {
    className: mergeClassNames(className, showWeekNumbers ? "".concat(className, "--weekNumbers") : '')
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      display: 'flex',
      alignItems: 'flex-end'
    }
  }, renderWeekNumbers(), /*#__PURE__*/React.createElement("div", {
    style: {
      flexGrow: 1,
      width: '100%'
    }
  }, renderWeekdays(), renderDays())));
}
MonthView.propTypes = {
  activeStartDate: propTypes$2.exports.instanceOf(Date).isRequired,
  calendarType: isCalendarType,
  formatShortWeekday: propTypes$2.exports.func,
  formatWeekday: propTypes$2.exports.func,
  locale: propTypes$2.exports.string,
  onClickWeekNumber: propTypes$2.exports.func,
  onMouseLeave: propTypes$2.exports.func,
  showFixedNumberOfWeeks: propTypes$2.exports.bool,
  showWeekNumbers: propTypes$2.exports.bool
};

var _excluded = ["activeStartDate", "defaultActiveStartDate", "defaultValue", "defaultView", "maxDetail", "minDetail", "value", "view"];

function _extends$4() { _extends$4 = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$4.apply(this, arguments); }

function _typeof$6(obj) { "@babel/helpers - typeof"; return _typeof$6 = "function" == typeof Symbol && "symbol" == typeof Symbol.iterator ? function (obj) { return typeof obj; } : function (obj) { return obj && "function" == typeof Symbol && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj; }, _typeof$6(obj); }

function _classCallCheck$5(instance, Constructor) { if (!(instance instanceof Constructor)) { throw new TypeError("Cannot call a class as a function"); } }

function _defineProperties$5(target, props) { for (var i = 0; i < props.length; i++) { var descriptor = props[i]; descriptor.enumerable = descriptor.enumerable || false; descriptor.configurable = true; if ("value" in descriptor) descriptor.writable = true; Object.defineProperty(target, descriptor.key, descriptor); } }

function _createClass$5(Constructor, protoProps, staticProps) { if (protoProps) _defineProperties$5(Constructor.prototype, protoProps); if (staticProps) _defineProperties$5(Constructor, staticProps); Object.defineProperty(Constructor, "prototype", { writable: false }); return Constructor; }

function _inherits$5(subClass, superClass) { if (typeof superClass !== "function" && superClass !== null) { throw new TypeError("Super expression must either be null or a function"); } subClass.prototype = Object.create(superClass && superClass.prototype, { constructor: { value: subClass, writable: true, configurable: true } }); Object.defineProperty(subClass, "prototype", { writable: false }); if (superClass) _setPrototypeOf$6(subClass, superClass); }

function _setPrototypeOf$6(o, p) { _setPrototypeOf$6 = Object.setPrototypeOf ? Object.setPrototypeOf.bind() : function _setPrototypeOf(o, p) { o.__proto__ = p; return o; }; return _setPrototypeOf$6(o, p); }

function _createSuper$5(Derived) { var hasNativeReflectConstruct = _isNativeReflectConstruct$5(); return function _createSuperInternal() { var Super = _getPrototypeOf$5(Derived), result; if (hasNativeReflectConstruct) { var NewTarget = _getPrototypeOf$5(this).constructor; result = Reflect.construct(Super, arguments, NewTarget); } else { result = Super.apply(this, arguments); } return _possibleConstructorReturn$5(this, result); }; }

function _possibleConstructorReturn$5(self, call) { if (call && (_typeof$6(call) === "object" || typeof call === "function")) { return call; } else if (call !== void 0) { throw new TypeError("Derived constructors may only return object or undefined"); } return _assertThisInitialized$5(self); }

function _assertThisInitialized$5(self) { if (self === void 0) { throw new ReferenceError("this hasn't been initialised - super() hasn't been called"); } return self; }

function _isNativeReflectConstruct$5() { if (typeof Reflect === "undefined" || !Reflect.construct) return false; if (Reflect.construct.sham) return false; if (typeof Proxy === "function") return true; try { Boolean.prototype.valueOf.call(Reflect.construct(Boolean, [], function () {})); return true; } catch (e) { return false; } }

function _getPrototypeOf$5(o) { _getPrototypeOf$5 = Object.setPrototypeOf ? Object.getPrototypeOf.bind() : function _getPrototypeOf(o) { return o.__proto__ || Object.getPrototypeOf(o); }; return _getPrototypeOf$5(o); }

function ownKeys$6(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread$6(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys$6(Object(source), !0).forEach(function (key) { _defineProperty$6(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys$6(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

function _defineProperty$6(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

function _objectWithoutProperties$1(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose$1(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }

function _objectWithoutPropertiesLoose$1(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }

function _toConsumableArray$3(arr) { return _arrayWithoutHoles$3(arr) || _iterableToArray$3(arr) || _unsupportedIterableToArray$5(arr) || _nonIterableSpread$3(); }

function _nonIterableSpread$3() { throw new TypeError("Invalid attempt to spread non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); }

function _unsupportedIterableToArray$5(o, minLen) { if (!o) return; if (typeof o === "string") return _arrayLikeToArray$5(o, minLen); var n = Object.prototype.toString.call(o).slice(8, -1); if (n === "Object" && o.constructor) n = o.constructor.name; if (n === "Map" || n === "Set") return Array.from(o); if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray$5(o, minLen); }

function _iterableToArray$3(iter) { if (typeof Symbol !== "undefined" && iter[Symbol.iterator] != null || iter["@@iterator"] != null) return Array.from(iter); }

function _arrayWithoutHoles$3(arr) { if (Array.isArray(arr)) return _arrayLikeToArray$5(arr); }

function _arrayLikeToArray$5(arr, len) { if (len == null || len > arr.length) len = arr.length; for (var i = 0, arr2 = new Array(len); i < len; i++) { arr2[i] = arr[i]; } return arr2; }
var defaultMinDate = new Date();
defaultMinDate.setFullYear(1, 0, 1);
defaultMinDate.setHours(0, 0, 0, 0);
var defaultMaxDate = new Date(8.64e15);
var baseClassName = 'react-calendar';
var allViews$1 = ['century', 'decade', 'year', 'month'];
var allValueTypes$1 = [].concat(_toConsumableArray$3(allViews$1.slice(1)), ['day']);

function toDate(value) {
  if (value instanceof Date) {
    return value;
  }

  return new Date(value);
}
/**
 * Returns views array with disallowed values cut off.
 */


function getLimitedViews(minDetail, maxDetail) {
  return allViews$1.slice(allViews$1.indexOf(minDetail), allViews$1.indexOf(maxDetail) + 1);
}
/**
 * Determines whether a given view is allowed with currently applied settings.
 */


function isViewAllowed(view, minDetail, maxDetail) {
  var views = getLimitedViews(minDetail, maxDetail);
  return views.indexOf(view) !== -1;
}
/**
 * Gets either provided view if allowed by minDetail and maxDetail, or gets
 * the default view if not allowed.
 */


function getView(view, minDetail, maxDetail) {
  if (isViewAllowed(view, minDetail, maxDetail)) {
    return view;
  }

  return maxDetail;
}
/**
 * Returns value type that can be returned with currently applied settings.
 */


function getValueType(maxDetail) {
  return allValueTypes$1[allViews$1.indexOf(maxDetail)];
}

function getValue(value, index) {
  if (!value) {
    return null;
  }

  var rawValue = Array.isArray(value) && value.length === 2 ? value[index] : value;

  if (!rawValue) {
    return null;
  }

  var valueDate = toDate(rawValue);

  if (isNaN(valueDate.getTime())) {
    throw new Error("Invalid date: ".concat(value));
  }

  return valueDate;
}

function getDetailValue(_ref, index) {
  var value = _ref.value,
      minDate = _ref.minDate,
      maxDate = _ref.maxDate,
      maxDetail = _ref.maxDetail;
  var valuePiece = getValue(value, index);

  if (!valuePiece) {
    return null;
  }

  var valueType = getValueType(maxDetail);
  var detailValueFrom = [getBegin$1, getEnd$1][index](valueType, valuePiece);
  return between$1(detailValueFrom, minDate, maxDate);
}

var getDetailValueFrom = function getDetailValueFrom(args) {
  return getDetailValue(args, 0);
};

var getDetailValueTo = function getDetailValueTo(args) {
  return getDetailValue(args, 1);
};

var getDetailValueArray = function getDetailValueArray(args) {
  var value = args.value;

  if (Array.isArray(value)) {
    return value;
  }

  return [getDetailValueFrom, getDetailValueTo].map(function (fn) {
    return fn(args);
  });
};

function getActiveStartDate(props) {
  var maxDate = props.maxDate,
      maxDetail = props.maxDetail,
      minDate = props.minDate,
      minDetail = props.minDetail,
      value = props.value,
      view = props.view;
  var rangeType = getView(view, minDetail, maxDetail);
  var valueFrom = getDetailValueFrom({
    value: value,
    minDate: minDate,
    maxDate: maxDate,
    maxDetail: maxDetail
  }) || new Date();
  return getBegin$1(rangeType, valueFrom);
}

function getInitialActiveStartDate(props) {
  var activeStartDate = props.activeStartDate,
      defaultActiveStartDate = props.defaultActiveStartDate,
      defaultValue = props.defaultValue,
      defaultView = props.defaultView,
      maxDetail = props.maxDetail,
      minDetail = props.minDetail,
      value = props.value,
      view = props.view,
      otherProps = _objectWithoutProperties$1(props, _excluded);

  var rangeType = getView(view, minDetail, maxDetail);
  var valueFrom = activeStartDate || defaultActiveStartDate;

  if (valueFrom) {
    return getBegin$1(rangeType, valueFrom);
  }

  return getActiveStartDate(_objectSpread$6({
    maxDetail: maxDetail,
    minDetail: minDetail,
    value: value || defaultValue,
    view: view || defaultView
  }, otherProps));
}

var getIsSingleValue = function getIsSingleValue(value) {
  return value && [].concat(value).length === 1;
};

var Calendar = /*#__PURE__*/function (_Component) {
  _inherits$5(Calendar, _Component);

  var _super = _createSuper$5(Calendar);

  function Calendar() {
    var _this;

    _classCallCheck$5(this, Calendar);

    for (var _len = arguments.length, _args = new Array(_len), _key = 0; _key < _len; _key++) {
      _args[_key] = arguments[_key];
    }

    _this = _super.call.apply(_super, [this].concat(_args));

    _defineProperty$6(_assertThisInitialized$5(_this), "state", {
      activeStartDate: _this.props.defaultActiveStartDate,
      value: _this.props.defaultValue,
      view: _this.props.defaultView
    });

    _defineProperty$6(_assertThisInitialized$5(_this), "setStateAndCallCallbacks", function (nextState, event, callback) {
      var _assertThisInitialize = _assertThisInitialized$5(_this),
          previousActiveStartDate = _assertThisInitialize.activeStartDate,
          previousView = _assertThisInitialize.view;

      var _this$props = _this.props,
          allowPartialRange = _this$props.allowPartialRange,
          onActiveStartDateChange = _this$props.onActiveStartDateChange,
          onChange = _this$props.onChange,
          onViewChange = _this$props.onViewChange,
          selectRange = _this$props.selectRange;
      var prevArgs = {
        activeStartDate: previousActiveStartDate,
        view: previousView
      };

      _this.setState(nextState, function () {
        var args = {
          action: nextState.action,
          activeStartDate: nextState.activeStartDate || _this.activeStartDate,
          value: nextState.value || _this.value,
          view: nextState.view || _this.view
        };

        function shouldUpdate(key) {
          return (// Key must exist, and…
            key in nextState && ( // …key changed from undefined to defined or the other way around, or…
            _typeof$6(nextState[key]) !== _typeof$6(prevArgs[key]) || ( // …value changed.
            nextState[key] instanceof Date ? nextState[key].getTime() !== prevArgs[key].getTime() : nextState[key] !== prevArgs[key]))
          );
        }

        if (shouldUpdate('activeStartDate')) {
          if (onActiveStartDateChange) onActiveStartDateChange(args);
        }

        if (shouldUpdate('view')) {
          if (onViewChange) onViewChange(args);
        }

        if (shouldUpdate('value')) {
          if (onChange) {
            if (selectRange) {
              var isSingleValue = getIsSingleValue(nextState.value);

              if (!isSingleValue) {
                onChange(nextState.value, event);
              } else if (allowPartialRange) {
                onChange([nextState.value], event);
              }
            } else {
              onChange(nextState.value, event);
            }
          }
        }

        if (callback) callback(args);
      });
    });

    _defineProperty$6(_assertThisInitialized$5(_this), "setActiveStartDate", function (nextActiveStartDate, action) {
      _this.setStateAndCallCallbacks({
        action: action,
        activeStartDate: nextActiveStartDate
      });
    });

    _defineProperty$6(_assertThisInitialized$5(_this), "drillDown", function (nextActiveStartDate, event) {
      if (!_this.drillDownAvailable) {
        return;
      }

      _this.onClickTile(nextActiveStartDate, event);

      var _assertThisInitialize2 = _assertThisInitialized$5(_this),
          view = _assertThisInitialize2.view,
          views = _assertThisInitialize2.views;

      var onDrillDown = _this.props.onDrillDown;
      var nextView = views[views.indexOf(view) + 1];

      _this.setStateAndCallCallbacks({
        action: 'drillDown',
        activeStartDate: nextActiveStartDate,
        view: nextView
      }, undefined, onDrillDown);
    });

    _defineProperty$6(_assertThisInitialized$5(_this), "drillUp", function () {
      if (!_this.drillUpAvailable) {
        return;
      }

      var _assertThisInitialize3 = _assertThisInitialized$5(_this),
          activeStartDate = _assertThisInitialize3.activeStartDate,
          view = _assertThisInitialize3.view,
          views = _assertThisInitialize3.views;

      var onDrillUp = _this.props.onDrillUp;
      var nextView = views[views.indexOf(view) - 1];
      var nextActiveStartDate = getBegin$1(nextView, activeStartDate);

      _this.setStateAndCallCallbacks({
        action: 'drillUp',
        activeStartDate: nextActiveStartDate,
        view: nextView
      }, undefined, onDrillUp);
    });

    _defineProperty$6(_assertThisInitialized$5(_this), "onChange", function (value, event) {
      var _assertThisInitialize4 = _assertThisInitialized$5(_this),
          previousValue = _assertThisInitialize4.value;

      var _this$props2 = _this.props,
          goToRangeStartOnSelect = _this$props2.goToRangeStartOnSelect,
          selectRange = _this$props2.selectRange;

      _this.onClickTile(value, event);

      var isFirstValueInRange = selectRange && !getIsSingleValue(previousValue);
      var nextValue;

      if (selectRange) {
        // Range selection turned on
        var _assertThisInitialize5 = _assertThisInitialized$5(_this),
            valueType = _assertThisInitialize5.valueType;

        if (isFirstValueInRange) {
          // Value has 0 or 2 elements - either way we're starting a new array
          // First value
          nextValue = getBegin$1(valueType, value);
        } else {
          // Second value
          nextValue = getValueRange(valueType, previousValue, value);
        }
      } else {
        // Range selection turned off
        nextValue = _this.getProcessedValue(value);
      }

      var nextActiveStartDate = // Range selection turned off
      !selectRange || // Range selection turned on, first value
      isFirstValueInRange || // Range selection turned on, second value, goToRangeStartOnSelect toggled on
      goToRangeStartOnSelect ? getActiveStartDate(_objectSpread$6(_objectSpread$6({}, _this.props), {}, {
        value: nextValue
      })) : null;
      event.persist();

      _this.setStateAndCallCallbacks({
        action: 'onChange',
        activeStartDate: nextActiveStartDate,
        value: nextValue
      }, event);
    });

    _defineProperty$6(_assertThisInitialized$5(_this), "onClickTile", function (value, event) {
      var _assertThisInitialize6 = _assertThisInitialized$5(_this),
          view = _assertThisInitialize6.view;

      var _this$props3 = _this.props,
          onClickDay = _this$props3.onClickDay,
          onClickDecade = _this$props3.onClickDecade,
          onClickMonth = _this$props3.onClickMonth,
          onClickYear = _this$props3.onClickYear;

      var callback = function () {
        switch (view) {
          case 'century':
            return onClickDecade;

          case 'decade':
            return onClickYear;

          case 'year':
            return onClickMonth;

          case 'month':
            return onClickDay;

          default:
            throw new Error("Invalid view: ".concat(view, "."));
        }
      }();

      if (callback) callback(value, event);
    });

    _defineProperty$6(_assertThisInitialized$5(_this), "onMouseOver", function (value) {
      _this.setState(function (prevState) {
        if (prevState.hover && prevState.hover.getTime() === value.getTime()) {
          return null;
        }

        return {
          hover: value
        };
      });
    });

    _defineProperty$6(_assertThisInitialized$5(_this), "onMouseLeave", function () {
      _this.setState({
        hover: null
      });
    });

    return _this;
  }

  _createClass$5(Calendar, [{
    key: "activeStartDate",
    get: function get() {
      var activeStartDateProps = this.props.activeStartDate;
      var activeStartDateState = this.state.activeStartDate;
      return activeStartDateProps || activeStartDateState || getInitialActiveStartDate(this.props);
    }
  }, {
    key: "value",
    get: function get() {
      var _this$props4 = this.props,
          selectRange = _this$props4.selectRange,
          valueProps = _this$props4.value;
      var valueState = this.state.value; // In the middle of range selection, use value from state

      if (selectRange && getIsSingleValue(valueState)) {
        return valueState;
      }

      return valueProps !== undefined ? valueProps : valueState;
    }
  }, {
    key: "valueType",
    get: function get() {
      var maxDetail = this.props.maxDetail;
      return getValueType(maxDetail);
    }
  }, {
    key: "view",
    get: function get() {
      var _this$props5 = this.props,
          minDetail = _this$props5.minDetail,
          maxDetail = _this$props5.maxDetail,
          viewProps = _this$props5.view;
      var viewState = this.state.view;
      return getView(viewProps || viewState, minDetail, maxDetail);
    }
  }, {
    key: "views",
    get: function get() {
      var _this$props6 = this.props,
          minDetail = _this$props6.minDetail,
          maxDetail = _this$props6.maxDetail;
      return getLimitedViews(minDetail, maxDetail);
    }
  }, {
    key: "hover",
    get: function get() {
      var selectRange = this.props.selectRange;
      var hover = this.state.hover;
      return selectRange ? hover : null;
    }
  }, {
    key: "drillDownAvailable",
    get: function get() {
      var view = this.view,
          views = this.views;
      return views.indexOf(view) < views.length - 1;
    }
  }, {
    key: "drillUpAvailable",
    get: function get() {
      var view = this.view,
          views = this.views;
      return views.indexOf(view) > 0;
    }
    /**
     * Gets current value in a desired format.
     */

  }, {
    key: "getProcessedValue",
    value: function getProcessedValue(value) {
      var _this$props7 = this.props,
          minDate = _this$props7.minDate,
          maxDate = _this$props7.maxDate,
          maxDetail = _this$props7.maxDetail,
          returnValue = _this$props7.returnValue;

      var processFunction = function () {
        switch (returnValue) {
          case 'start':
            return getDetailValueFrom;

          case 'end':
            return getDetailValueTo;

          case 'range':
            return getDetailValueArray;

          default:
            throw new Error('Invalid returnValue.');
        }
      }();

      return processFunction({
        value: value,
        minDate: minDate,
        maxDate: maxDate,
        maxDetail: maxDetail
      });
    }
  }, {
    key: "renderContent",
    value: function renderContent(next) {
      var currentActiveStartDate = this.activeStartDate,
          onMouseOver = this.onMouseOver,
          valueType = this.valueType,
          value = this.value,
          view = this.view;
      var _this$props8 = this.props,
          calendarType = _this$props8.calendarType,
          locale = _this$props8.locale,
          maxDate = _this$props8.maxDate,
          minDate = _this$props8.minDate,
          selectRange = _this$props8.selectRange,
          tileClassName = _this$props8.tileClassName,
          tileContent = _this$props8.tileContent,
          tileDisabled = _this$props8.tileDisabled;
      var hover = this.hover;
      var activeStartDate = next ? getBeginNext(view, currentActiveStartDate) : getBegin$1(view, currentActiveStartDate);
      var onClick = this.drillDownAvailable ? this.drillDown : this.onChange;
      var commonProps = {
        activeStartDate: activeStartDate,
        hover: hover,
        locale: locale,
        maxDate: maxDate,
        minDate: minDate,
        onClick: onClick,
        onMouseOver: selectRange ? onMouseOver : null,
        tileClassName: tileClassName,
        tileContent: tileContent,
        tileDisabled: tileDisabled,
        value: value,
        valueType: valueType
      };

      switch (view) {
        case 'century':
          {
            var formatYear = this.props.formatYear;
            return /*#__PURE__*/React.createElement(CenturyView, _extends$4({
              formatYear: formatYear
            }, commonProps));
          }

        case 'decade':
          {
            var _formatYear = this.props.formatYear;
            return /*#__PURE__*/React.createElement(DecadeView, _extends$4({
              formatYear: _formatYear
            }, commonProps));
          }

        case 'year':
          {
            var _this$props9 = this.props,
                formatMonth = _this$props9.formatMonth,
                formatMonthYear = _this$props9.formatMonthYear;
            return /*#__PURE__*/React.createElement(YearView, _extends$4({
              formatMonth: formatMonth,
              formatMonthYear: formatMonthYear
            }, commonProps));
          }

        case 'month':
          {
            var _this$props10 = this.props,
                formatDay = _this$props10.formatDay,
                formatLongDate = _this$props10.formatLongDate,
                formatShortWeekday = _this$props10.formatShortWeekday,
                formatWeekday = _this$props10.formatWeekday,
                onClickWeekNumber = _this$props10.onClickWeekNumber,
                showDoubleView = _this$props10.showDoubleView,
                showFixedNumberOfWeeks = _this$props10.showFixedNumberOfWeeks,
                showNeighboringMonth = _this$props10.showNeighboringMonth,
                showWeekNumbers = _this$props10.showWeekNumbers;
            var onMouseLeave = this.onMouseLeave;
            return /*#__PURE__*/React.createElement(MonthView, _extends$4({
              calendarType: calendarType,
              formatDay: formatDay,
              formatLongDate: formatLongDate,
              formatShortWeekday: formatShortWeekday,
              formatWeekday: formatWeekday,
              onClickWeekNumber: onClickWeekNumber,
              onMouseLeave: selectRange ? onMouseLeave : null,
              showFixedNumberOfWeeks: typeof showFixedNumberOfWeeks !== 'undefined' ? showFixedNumberOfWeeks : showDoubleView,
              showNeighboringMonth: showNeighboringMonth,
              showWeekNumbers: showWeekNumbers
            }, commonProps));
          }

        default:
          throw new Error("Invalid view: ".concat(view, "."));
      }
    }
  }, {
    key: "renderNavigation",
    value: function renderNavigation() {
      var showNavigation = this.props.showNavigation;

      if (!showNavigation) {
        return null;
      }

      var activeStartDate = this.activeStartDate,
          view = this.view,
          views = this.views;
      var _this$props11 = this.props,
          formatMonthYear = _this$props11.formatMonthYear,
          formatYear = _this$props11.formatYear,
          locale = _this$props11.locale,
          maxDate = _this$props11.maxDate,
          minDate = _this$props11.minDate,
          navigationAriaLabel = _this$props11.navigationAriaLabel,
          navigationAriaLive = _this$props11.navigationAriaLive,
          navigationLabel = _this$props11.navigationLabel,
          next2AriaLabel = _this$props11.next2AriaLabel,
          next2Label = _this$props11.next2Label,
          nextAriaLabel = _this$props11.nextAriaLabel,
          nextLabel = _this$props11.nextLabel,
          prev2AriaLabel = _this$props11.prev2AriaLabel,
          prev2Label = _this$props11.prev2Label,
          prevAriaLabel = _this$props11.prevAriaLabel,
          prevLabel = _this$props11.prevLabel,
          showDoubleView = _this$props11.showDoubleView;
      return /*#__PURE__*/React.createElement(Navigation, {
        activeStartDate: activeStartDate,
        drillUp: this.drillUp,
        formatMonthYear: formatMonthYear,
        formatYear: formatYear,
        locale: locale,
        maxDate: maxDate,
        minDate: minDate,
        navigationAriaLabel: navigationAriaLabel,
        navigationAriaLive: navigationAriaLive,
        navigationLabel: navigationLabel,
        next2AriaLabel: next2AriaLabel,
        next2Label: next2Label,
        nextAriaLabel: nextAriaLabel,
        nextLabel: nextLabel,
        prev2AriaLabel: prev2AriaLabel,
        prev2Label: prev2Label,
        prevAriaLabel: prevAriaLabel,
        prevLabel: prevLabel,
        setActiveStartDate: this.setActiveStartDate,
        showDoubleView: showDoubleView,
        view: view,
        views: views
      });
    }
  }, {
    key: "render",
    value: function render() {
      var _this$props12 = this.props,
          className = _this$props12.className,
          inputRef = _this$props12.inputRef,
          selectRange = _this$props12.selectRange,
          showDoubleView = _this$props12.showDoubleView;
      var onMouseLeave = this.onMouseLeave,
          value = this.value;
      var valueArray = [].concat(value);
      return /*#__PURE__*/React.createElement("div", {
        className: mergeClassNames(baseClassName, selectRange && valueArray.length === 1 && "".concat(baseClassName, "--selectRange"), showDoubleView && "".concat(baseClassName, "--doubleView"), className),
        ref: inputRef
      }, this.renderNavigation(), /*#__PURE__*/React.createElement("div", {
        className: "".concat(baseClassName, "__viewContainer"),
        onBlur: selectRange ? onMouseLeave : null,
        onMouseLeave: selectRange ? onMouseLeave : null
      }, this.renderContent(), showDoubleView && this.renderContent(true)));
    }
  }]);

  return Calendar;
}(Component);
Calendar.defaultProps = {
  goToRangeStartOnSelect: true,
  maxDate: defaultMaxDate,
  maxDetail: 'month',
  minDate: defaultMinDate,
  minDetail: 'century',
  returnValue: 'start',
  showNavigation: true,
  showNeighboringMonth: true
};
var isActiveStartDate = propTypes$2.exports.instanceOf(Date);
var isLooseValue = propTypes$2.exports.oneOfType([propTypes$2.exports.string, isValue]);
Calendar.propTypes = {
  activeStartDate: isActiveStartDate,
  allowPartialRange: propTypes$2.exports.bool,
  calendarType: isCalendarType,
  className: isClassName,
  defaultActiveStartDate: isActiveStartDate,
  defaultValue: isLooseValue,
  defaultView: isView,
  formatDay: propTypes$2.exports.func,
  formatLongDate: propTypes$2.exports.func,
  formatMonth: propTypes$2.exports.func,
  formatMonthYear: propTypes$2.exports.func,
  formatShortWeekday: propTypes$2.exports.func,
  formatWeekday: propTypes$2.exports.func,
  formatYear: propTypes$2.exports.func,
  goToRangeStartOnSelect: propTypes$2.exports.bool,
  inputRef: isRef$1,
  locale: propTypes$2.exports.string,
  maxDate: isMaxDate$1,
  maxDetail: propTypes$2.exports.oneOf(allViews$1),
  minDate: isMinDate$1,
  minDetail: propTypes$2.exports.oneOf(allViews$1),
  navigationAriaLabel: propTypes$2.exports.string,
  navigationAriaLive: propTypes$2.exports.oneOf(['off', 'polite', 'assertive']),
  navigationLabel: propTypes$2.exports.func,
  next2AriaLabel: propTypes$2.exports.string,
  next2Label: propTypes$2.exports.node,
  nextAriaLabel: propTypes$2.exports.string,
  nextLabel: propTypes$2.exports.node,
  onActiveStartDateChange: propTypes$2.exports.func,
  onChange: propTypes$2.exports.func,
  onClickDay: propTypes$2.exports.func,
  onClickDecade: propTypes$2.exports.func,
  onClickMonth: propTypes$2.exports.func,
  onClickWeekNumber: propTypes$2.exports.func,
  onClickYear: propTypes$2.exports.func,
  onDrillDown: propTypes$2.exports.func,
  onDrillUp: propTypes$2.exports.func,
  onViewChange: propTypes$2.exports.func,
  prev2AriaLabel: propTypes$2.exports.string,
  prev2Label: propTypes$2.exports.node,
  prevAriaLabel: propTypes$2.exports.string,
  prevLabel: propTypes$2.exports.node,
  returnValue: propTypes$2.exports.oneOf(['start', 'end', 'range']),
  selectRange: propTypes$2.exports.bool,
  showDoubleView: propTypes$2.exports.bool,
  showFixedNumberOfWeeks: propTypes$2.exports.bool,
  showNavigation: propTypes$2.exports.bool,
  showNeighboringMonth: propTypes$2.exports.bool,
  showWeekNumbers: propTypes$2.exports.bool,
  tileClassName: propTypes$2.exports.oneOfType([propTypes$2.exports.func, isClassName]),
  tileContent: propTypes$2.exports.oneOfType([propTypes$2.exports.func, propTypes$2.exports.node]),
  tileDisabled: propTypes$2.exports.func,
  value: isLooseValue,
  view: isView
};

var esm$3 = /*#__PURE__*/Object.freeze({
    __proto__: null,
    'default': Calendar,
    Calendar: Calendar,
    Navigation: Navigation,
    CenturyView: CenturyView,
    DecadeView: DecadeView,
    YearView: YearView,
    MonthView: MonthView
});

var require$$4$1 = /*@__PURE__*/getAugmentedNamespace(esm$3);

function getRect(element) {
    return element.getBoundingClientRect();
}
function detectElementOverflow(element, container) {
    return {
        get collidedTop() {
            return getRect(element).top < getRect(container).top;
        },
        get collidedBottom() {
            return getRect(element).bottom > getRect(container).bottom;
        },
        get collidedLeft() {
            return getRect(element).left < getRect(container).left;
        },
        get collidedRight() {
            return getRect(element).right > getRect(container).right;
        },
        get overflowTop() {
            return getRect(container).top - getRect(element).top;
        },
        get overflowBottom() {
            return getRect(element).bottom - getRect(container).bottom;
        },
        get overflowLeft() {
            return getRect(container).left - getRect(element).left;
        },
        get overflowRight() {
            return getRect(element).right - getRect(container).right;
        },
    };
}

var isProduction = process.env.NODE_ENV === 'production';
function warning(condition, message) {
  if (!isProduction) {
    if (condition) {
      return;
    }

    var text = "Warning: " + message;

    if (typeof console !== 'undefined') {
      console.warn(text);
    }

    try {
      throw Error(text);
    } catch (x) {}
  }
}

var __extends = (undefined && undefined.__extends) || (function () {
    var extendStatics = function (d, b) {
        extendStatics = Object.setPrototypeOf ||
            ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
            function (d, b) { for (var p in b) if (Object.prototype.hasOwnProperty.call(b, p)) d[p] = b[p]; };
        return extendStatics(d, b);
    };
    return function (d, b) {
        if (typeof b !== "function" && b !== null)
            throw new TypeError("Class extends value " + String(b) + " is not a constructor or null");
        extendStatics(d, b);
        function __() { this.constructor = d; }
        d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
    };
})();
var __assign = (undefined && undefined.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (undefined && undefined.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var isBrowser$1 = typeof document !== 'undefined';
var isDisplayContentsSupported = isBrowser$1 && 'CSS' in window && 'supports' in window.CSS && CSS.supports('display', 'contents');
var isMutationObserverSupported = isBrowser$1 && 'MutationObserver' in window;
function capitalize(string) {
    return (string.charAt(0).toUpperCase() + string.slice(1));
}
function findScrollContainer(element) {
    var parent = element.parentElement;
    while (parent) {
        var overflow = window.getComputedStyle(parent).overflow;
        if (overflow.split(' ').every(function (o) { return o === 'auto' || o === 'scroll'; })) {
            return parent;
        }
        parent = parent.parentElement;
    }
    return document.documentElement;
}
function alignAxis(_a) {
    var axis = _a.axis, container = _a.container, element = _a.element, invertAxis = _a.invertAxis, secondary = _a.secondary, scrollContainer = _a.scrollContainer, spacing = _a.spacing;
    var style = window.getComputedStyle(element);
    var parent = container.parentElement;
    if (!parent) {
        return;
    }
    var scrollContainerCollisions = detectElementOverflow(parent, scrollContainer);
    var documentCollisions = detectElementOverflow(parent, document.documentElement);
    var isX = axis === 'x';
    var startProperty = isX ? 'left' : 'top';
    var endProperty = isX ? 'right' : 'bottom';
    var sizeProperty = isX ? 'width' : 'height';
    var overflowStartProperty = "overflow".concat(capitalize(startProperty));
    var overflowEndProperty = "overflow".concat(capitalize(endProperty));
    var scrollProperty = "scroll".concat(capitalize(startProperty));
    var uppercasedSizeProperty = capitalize(sizeProperty);
    var offsetSizeProperty = "offset".concat(uppercasedSizeProperty);
    var clientSizeProperty = "client".concat(uppercasedSizeProperty);
    var minSizeProperty = "min-".concat(sizeProperty);
    var scrollbarWidth = scrollContainer[offsetSizeProperty] - scrollContainer[clientSizeProperty];
    var startSpacing = typeof spacing === 'object' ? spacing[startProperty] : spacing;
    var availableStartSpace = -Math.max(scrollContainerCollisions[overflowStartProperty], documentCollisions[overflowStartProperty] + document.documentElement[scrollProperty]) - startSpacing;
    var endSpacing = typeof spacing === 'object' ? spacing[endProperty] : spacing;
    var availableEndSpace = -Math.max(scrollContainerCollisions[overflowEndProperty], documentCollisions[overflowEndProperty] - document.documentElement[scrollProperty]) -
        endSpacing -
        scrollbarWidth;
    if (secondary) {
        availableStartSpace += parent[clientSizeProperty];
        availableEndSpace += parent[clientSizeProperty];
    }
    var offsetSize = element[offsetSizeProperty];
    function displayStart() {
        element.style[startProperty] = 'auto';
        element.style[endProperty] = secondary ? '0' : '100%';
    }
    function displayEnd() {
        element.style[startProperty] = secondary ? '0' : '100%';
        element.style[endProperty] = 'auto';
    }
    function displayIfFits(availableSpace, display) {
        var fits = offsetSize <= availableSpace;
        if (fits) {
            display();
        }
        return fits;
    }
    function displayStartIfFits() {
        return displayIfFits(availableStartSpace, displayStart);
    }
    function displayEndIfFits() {
        return displayIfFits(availableEndSpace, displayEnd);
    }
    function displayWhereverShrinkedFits() {
        var moreSpaceStart = availableStartSpace > availableEndSpace;
        var rawMinSize = style.getPropertyValue(minSizeProperty);
        var minSize = rawMinSize ? parseInt(rawMinSize, 10) : null;
        function shrinkToSize(size) {
            warning(!minSize || size >= minSize, "<Fit />'s child will not fit anywhere with its current ".concat(minSizeProperty, " of ").concat(minSize, "px."));
            var newSize = Math.max(size, minSize || 0);
            warning(false, "<Fit />'s child needed to have its ".concat(sizeProperty, " decreased to ").concat(newSize, "px."));
            element.style[sizeProperty] = "".concat(newSize, "px");
        }
        if (moreSpaceStart) {
            shrinkToSize(availableStartSpace);
            displayStart();
        }
        else {
            shrinkToSize(availableEndSpace);
            displayEnd();
        }
    }
    var fits;
    if (invertAxis) {
        fits = displayStartIfFits() || displayEndIfFits();
    }
    else {
        fits = displayEndIfFits() || displayStartIfFits();
    }
    if (!fits) {
        displayWhereverShrinkedFits();
    }
}
function alignMainAxis(args) {
    alignAxis(args);
}
function alignSecondaryAxis(args) {
    alignAxis(__assign(__assign({}, args), { axis: args.axis === 'x' ? 'y' : 'x', secondary: true }));
}
function alignBothAxis(args) {
    var invertAxis = args.invertAxis, invertSecondaryAxis = args.invertSecondaryAxis, commonArgs = __rest(args, ["invertAxis", "invertSecondaryAxis"]);
    alignMainAxis(__assign(__assign({}, commonArgs), { invertAxis: invertAxis }));
    alignSecondaryAxis(__assign(__assign({}, commonArgs), { invertAxis: invertSecondaryAxis }));
}
var Fit = /** @class */ (function (_super) {
    __extends(Fit, _super);
    function Fit() {
        var _this = _super !== null && _super.apply(this, arguments) || this;
        _this.fit = function () {
            var _a = _this, scrollContainer = _a.scrollContainer, container = _a.container, element = _a.element;
            if (!scrollContainer || !container || !element) {
                return;
            }
            var elementWidth = element.clientWidth;
            var elementHeight = element.clientHeight;
            // No need to recalculate - already did that for current dimensions
            if (_this.elementWidth === elementWidth && _this.elementHeight === elementHeight) {
                return;
            }
            // Save the dimensions so that we know we don't need to repeat the function if unchanged
            _this.elementWidth = elementWidth;
            _this.elementHeight = elementHeight;
            var parent = container.parentElement;
            // Container was unmounted
            if (!parent) {
                return;
            }
            /**
             * We need to ensure that <Fit />'s child has a absolute position. Otherwise,
             * we wouldn't be able to place the child in the correct position.
             */
            var style = window.getComputedStyle(element);
            var position = style.position;
            if (position !== 'absolute') {
                element.style.position = 'absolute';
            }
            /**
             * We need to ensure that <Fit />'s parent has a relative or absolute position. Otherwise,
             * we wouldn't be able to place the child in the correct position.
             */
            var parentStyle = window.getComputedStyle(parent);
            var parentPosition = parentStyle.position;
            if (parentPosition !== 'relative' && parentPosition !== 'absolute') {
                parent.style.position = 'relative';
            }
            var _b = _this.props, invertAxis = _b.invertAxis, invertSecondaryAxis = _b.invertSecondaryAxis, _c = _b.mainAxis, mainAxis = _c === void 0 ? 'y' : _c, _d = _b.spacing, spacing = _d === void 0 ? 8 : _d;
            alignBothAxis({
                container: container,
                element: element,
                invertAxis: invertAxis,
                invertSecondaryAxis: invertSecondaryAxis,
                axis: mainAxis,
                scrollContainer: scrollContainer,
                spacing: spacing,
            });
        };
        return _this;
    }
    Fit.prototype.componentDidMount = function () {
        var _this = this;
        if (!isDisplayContentsSupported) {
            // eslint-disable-next-line react/no-find-dom-node
            var element = findDOMNode(this);
            if (!element || !(element instanceof HTMLElement)) {
                return;
            }
            this.container = element;
            this.element = element;
            this.scrollContainer = findScrollContainer(element);
        }
        this.fit();
        var onMutation = function () {
            _this.fit();
        };
        if (isMutationObserverSupported && this.element) {
            var mutationObserver = new MutationObserver(onMutation);
            mutationObserver.observe(this.element, {
                attributes: true,
                attributeFilter: ['class', 'style'],
            });
        }
    };
    Fit.prototype.render = function () {
        var _this = this;
        var children = this.props.children;
        var child = React.Children.only(children);
        if (isDisplayContentsSupported) {
            return (React.createElement("span", { ref: function (container) {
                    _this.container = container;
                    var element = container && container.firstElementChild;
                    if (!element || !(element instanceof HTMLElement)) {
                        return;
                    }
                    _this.element = element;
                    _this.scrollContainer = findScrollContainer(element);
                }, style: { display: 'contents' } }, child));
        }
        return child;
    };
    Fit.propTypes = {
        children: propTypes$2.exports.node.isRequired,
        invertAxis: propTypes$2.exports.bool,
        invertSecondaryAxis: propTypes$2.exports.bool,
        mainAxis: propTypes$2.exports.oneOf(['x', 'y']),
        spacing: propTypes$2.exports.oneOfType([
            propTypes$2.exports.number,
            propTypes$2.exports.shape({
                bottom: propTypes$2.exports.number.isRequired,
                left: propTypes$2.exports.number.isRequired,
                right: propTypes$2.exports.number.isRequired,
                top: propTypes$2.exports.number.isRequired,
            }),
        ]),
    };
    return Fit;
}(Component));

var esm$2 = /*#__PURE__*/Object.freeze({
    __proto__: null,
    Fit: Fit,
    'default': Fit
});

var require$$5 = /*@__PURE__*/getAugmentedNamespace(esm$2);

var DateInput = {};

var require$$2$1 = /*@__PURE__*/getAugmentedNamespace(esm$4);

var Divider$2 = {};

(function (exports) {

	Object.defineProperty(exports, "__esModule", {
	  value: true
	});
	exports["default"] = Divider;

	var _react = _interopRequireDefault(React);

	var _propTypes = _interopRequireDefault(propTypes$2.exports);

	function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { "default": obj }; }

	function Divider(_ref) {
	  var children = _ref.children;
	  return /*#__PURE__*/_react["default"].createElement("span", {
	    className: "react-date-picker__inputGroup__divider"
	  }, children);
	}

	Divider.propTypes = {
	  children: _propTypes["default"].node
	};
} (Divider$2));

var DayInput = {};

var Input$3 = {};

/**
 * A function that merges React refs into one.
 * Supports both functions and ref objects created using createRef() and useRef().
 *
 * Usage:
 * ```jsx
 * <div ref={mergeRefs(ref1, ref2, ref3)} />
 * ```
 *
 * @param {(React.Ref<T> | undefined)[]} inputRefs Array of refs
 * @returns {React.Ref<T> | React.RefCallback<T>} Merged refs
 */
function mergeRefs() {
    var inputRefs = [];
    for (var _i = 0; _i < arguments.length; _i++) {
        inputRefs[_i] = arguments[_i];
    }
    var filteredInputRefs = inputRefs.filter(Boolean);
    if (filteredInputRefs.length <= 1) {
        var firstRef = filteredInputRefs[0];
        return firstRef || null;
    }
    return function mergedRefs(ref) {
        filteredInputRefs.forEach(function (inputRef) {
            if (typeof inputRef === 'function') {
                inputRef(ref);
            }
            else if (inputRef) {
                inputRef.current = ref;
            }
        });
    };
}

var esm$1 = /*#__PURE__*/Object.freeze({
    __proto__: null,
    'default': mergeRefs
});

var require$$3 = /*@__PURE__*/getAugmentedNamespace(esm$1);

var allowedVariants = ['normal', 'small-caps'];
/**
 * Gets font CSS shorthand property given element.
 *
 * @param {HTMLElement} element Element to get font CSS shorthand property from
 */
function getFontShorthand(element) {
    if (!element) {
        return '';
    }
    var style = window.getComputedStyle(element);
    if (style.font) {
        return style.font;
    }
    var isFontDefined = style.fontFamily !== '';
    if (!isFontDefined) {
        return '';
    }
    var fontVariant = allowedVariants.includes(style.fontVariant) ? style.fontVariant : 'normal';
    return "".concat(style.fontStyle, " ").concat(fontVariant, " ").concat(style.fontWeight, " ").concat(style.fontSize, " / ").concat(style.lineHeight, " ").concat(style.fontFamily);
}
var cachedCanvas;
/**
 * Measures text width given text and font CSS shorthand.
 *
 * @param {string} text Text to measure
 * @param {string} font Font to use when measuring the text
 */
function measureText(text, font) {
    var canvas = cachedCanvas || (cachedCanvas = document.createElement('canvas'));
    var context = canvas.getContext('2d');
    // Context type not supported
    if (!context) {
        return null;
    }
    context.font = font;
    var width = context.measureText(text).width;
    return Math.ceil(width);
}
/**
 * Updates input element width to fit its content given input element
 * @param {HTMLInputElement} element
 */
function updateInputWidth(element) {
    if (typeof document === 'undefined' || !element) {
        return null;
    }
    var font = getFontShorthand(element);
    var text = element.value || element.placeholder;
    var width = measureText(text, font);
    if (width === null) {
        return null;
    }
    element.style.width = "".concat(width, "px");
    return width;
}

var esm = /*#__PURE__*/Object.freeze({
    __proto__: null,
    getFontShorthand: getFontShorthand,
    measureText: measureText,
    updateInputWidth: updateInputWidth,
    'default': updateInputWidth
});

var require$$4 = /*@__PURE__*/getAugmentedNamespace(esm);

var propTypes$1 = {};

Object.defineProperty(propTypes$1, "__esModule", {
  value: true
});
propTypes$1.isValueType = propTypes$1.isRef = propTypes$1.isMinDate = propTypes$1.isMaxDate = void 0;

var _propTypes$1 = _interopRequireDefault$9(propTypes$2.exports);

function _interopRequireDefault$9(obj) { return obj && obj.__esModule ? obj : { "default": obj }; }

function _typeof$5(obj) { "@babel/helpers - typeof"; return _typeof$5 = "function" == typeof Symbol && "symbol" == typeof Symbol.iterator ? function (obj) { return typeof obj; } : function (obj) { return obj && "function" == typeof Symbol && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj; }, _typeof$5(obj); }

function _toConsumableArray$2(arr) { return _arrayWithoutHoles$2(arr) || _iterableToArray$2(arr) || _unsupportedIterableToArray$4(arr) || _nonIterableSpread$2(); }

function _nonIterableSpread$2() { throw new TypeError("Invalid attempt to spread non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); }

function _unsupportedIterableToArray$4(o, minLen) { if (!o) return; if (typeof o === "string") return _arrayLikeToArray$4(o, minLen); var n = Object.prototype.toString.call(o).slice(8, -1); if (n === "Object" && o.constructor) n = o.constructor.name; if (n === "Map" || n === "Set") return Array.from(o); if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray$4(o, minLen); }

function _iterableToArray$2(iter) { if (typeof Symbol !== "undefined" && iter[Symbol.iterator] != null || iter["@@iterator"] != null) return Array.from(iter); }

function _arrayWithoutHoles$2(arr) { if (Array.isArray(arr)) return _arrayLikeToArray$4(arr); }

function _arrayLikeToArray$4(arr, len) { if (len == null || len > arr.length) len = arr.length; for (var i = 0, arr2 = new Array(len); i < len; i++) { arr2[i] = arr[i]; } return arr2; }

var allViews = ['century', 'decade', 'year', 'month'];
var allValueTypes = [].concat(_toConsumableArray$2(allViews.slice(1)), ['day']);

var isValueType = _propTypes$1["default"].oneOf(allValueTypes);

propTypes$1.isValueType = isValueType;

var isMinDate = function isMinDate(props, propName, componentName) {
  var minDate = props[propName];

  if (!minDate) {
    return null;
  }

  if (!(minDate instanceof Date)) {
    return new Error("Invalid prop `".concat(propName, "` of type `").concat(_typeof$5(minDate), "` supplied to `").concat(componentName, "`, expected instance of `Date`."));
  }

  var maxDate = props.maxDate;

  if (maxDate && minDate > maxDate) {
    return new Error("Invalid prop `".concat(propName, "` of type `").concat(_typeof$5(minDate), "` supplied to `").concat(componentName, "`, minDate cannot be larger than maxDate."));
  }

  return null;
};

propTypes$1.isMinDate = isMinDate;

var isMaxDate = function isMaxDate(props, propName, componentName) {
  var maxDate = props[propName];

  if (!maxDate) {
    return null;
  }

  if (!(maxDate instanceof Date)) {
    return new Error("Invalid prop `".concat(propName, "` of type `").concat(_typeof$5(maxDate), "` supplied to `").concat(componentName, "`, expected instance of `Date`."));
  }

  var minDate = props.minDate;

  if (minDate && maxDate < minDate) {
    return new Error("Invalid prop `".concat(propName, "` of type `").concat(_typeof$5(maxDate), "` supplied to `").concat(componentName, "`, maxDate cannot be smaller than minDate."));
  }

  return null;
};

propTypes$1.isMaxDate = isMaxDate;

var isRef = _propTypes$1["default"].oneOfType([_propTypes$1["default"].func, _propTypes$1["default"].shape({
  current: _propTypes$1["default"].any
})]);

propTypes$1.isRef = isRef;

(function (exports) {

	function _typeof(obj) { "@babel/helpers - typeof"; return _typeof = "function" == typeof Symbol && "symbol" == typeof Symbol.iterator ? function (obj) { return typeof obj; } : function (obj) { return obj && "function" == typeof Symbol && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj; }, _typeof(obj); }

	Object.defineProperty(exports, "__esModule", {
	  value: true
	});
	exports["default"] = Input;

	var _react = _interopRequireDefault(React);

	var _propTypes = _interopRequireDefault(propTypes$2.exports);

	var _mergeClassNames = _interopRequireDefault(require$$3$1);

	var _mergeRefs = _interopRequireDefault(require$$3);

	var _updateInputWidth = _interopRequireWildcard(require$$4);

	var _propTypes2 = propTypes$1;

	function _getRequireWildcardCache(nodeInterop) { if (typeof WeakMap !== "function") return null; var cacheBabelInterop = new WeakMap(); var cacheNodeInterop = new WeakMap(); return (_getRequireWildcardCache = function _getRequireWildcardCache(nodeInterop) { return nodeInterop ? cacheNodeInterop : cacheBabelInterop; })(nodeInterop); }

	function _interopRequireWildcard(obj, nodeInterop) { if (!nodeInterop && obj && obj.__esModule) { return obj; } if (obj === null || _typeof(obj) !== "object" && typeof obj !== "function") { return { "default": obj }; } var cache = _getRequireWildcardCache(nodeInterop); if (cache && cache.has(obj)) { return cache.get(obj); } var newObj = {}; var hasPropertyDescriptor = Object.defineProperty && Object.getOwnPropertyDescriptor; for (var key in obj) { if (key !== "default" && Object.prototype.hasOwnProperty.call(obj, key)) { var desc = hasPropertyDescriptor ? Object.getOwnPropertyDescriptor(obj, key) : null; if (desc && (desc.get || desc.set)) { Object.defineProperty(newObj, key, desc); } else { newObj[key] = obj[key]; } } } newObj["default"] = obj; if (cache) { cache.set(obj, newObj); } return newObj; }

	function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { "default": obj }; }

	/* eslint-disable jsx-a11y/no-autofocus */
	var isIEOrEdgeLegacy = typeof window !== 'undefined' && /(MSIE|Trident\/|Edge\/)/.test(window.navigator.userAgent);
	var isFirefox = typeof window !== 'undefined' && /Firefox/.test(window.navigator.userAgent);

	function onFocus(event) {
	  var target = event.target;

	  if (isIEOrEdgeLegacy) {
	    requestAnimationFrame(function () {
	      return target.select();
	    });
	  } else {
	    target.select();
	  }
	}

	function updateInputWidthOnFontLoad(element) {
	  if (!document.fonts) {
	    return;
	  }

	  var font = (0, _updateInputWidth.getFontShorthand)(element);

	  if (!font) {
	    return;
	  }

	  var isFontLoaded = document.fonts.check(font);

	  if (isFontLoaded) {
	    return;
	  }

	  function onLoadingDone() {
	    (0, _updateInputWidth["default"])(element);
	  }

	  document.fonts.addEventListener('loadingdone', onLoadingDone);
	}

	function getSelectionString(input) {
	  /**
	   * window.getSelection().toString() returns empty string in IE11 and Firefox,
	   * so alternatives come first.
	   */
	  if (input && 'selectionStart' in input && input.selectionStart !== null) {
	    return input.value.slice(input.selectionStart, input.selectionEnd);
	  }

	  if ('getSelection' in window) {
	    return window.getSelection().toString();
	  }

	  return null;
	}

	function makeOnKeyPress(maxLength) {
	  /**
	   * Prevents keystrokes that would not produce a number or when value after keystroke would
	   * exceed maxLength.
	   */
	  return function onKeyPress(event) {
	    if (isFirefox) {
	      // See https://github.com/wojtekmaj/react-time-picker/issues/92
	      return;
	    }

	    var key = event.key,
	        input = event.target;
	    var value = input.value;
	    var isNumberKey = key.length === 1 && /\d/.test(key);
	    var selection = getSelectionString(input);

	    if (!isNumberKey || !(selection || value.length < maxLength)) {
	      event.preventDefault();
	    }
	  };
	}

	function Input(_ref) {
	  var ariaLabel = _ref.ariaLabel,
	      autoFocus = _ref.autoFocus,
	      className = _ref.className,
	      disabled = _ref.disabled,
	      inputRef = _ref.inputRef,
	      max = _ref.max,
	      min = _ref.min,
	      name = _ref.name,
	      nameForClass = _ref.nameForClass,
	      onChange = _ref.onChange,
	      onKeyDown = _ref.onKeyDown,
	      _onKeyUp = _ref.onKeyUp,
	      _ref$placeholder = _ref.placeholder,
	      placeholder = _ref$placeholder === void 0 ? '--' : _ref$placeholder,
	      required = _ref.required,
	      showLeadingZeros = _ref.showLeadingZeros,
	      step = _ref.step,
	      value = _ref.value;
	  var hasLeadingZero = showLeadingZeros && value && value < 10 && (value === '0' || !value.toString().startsWith('0'));
	  var maxLength = max ? max.toString().length : null;
	  return [hasLeadingZero && /*#__PURE__*/_react["default"].createElement("span", {
	    key: "leadingZero",
	    className: "".concat(className, "__leadingZero")
	  }, "0"), /*#__PURE__*/_react["default"].createElement("input", {
	    key: "input",
	    "aria-label": ariaLabel,
	    autoComplete: "off",
	    autoFocus: autoFocus,
	    className: (0, _mergeClassNames["default"])("".concat(className, "__input"), "".concat(className, "__").concat(nameForClass || name), hasLeadingZero && "".concat(className, "__input--hasLeadingZero")),
	    "data-input": "true",
	    disabled: disabled,
	    inputMode: "numeric",
	    max: max,
	    min: min,
	    name: name,
	    onChange: onChange,
	    onFocus: onFocus,
	    onKeyDown: onKeyDown,
	    onKeyPress: makeOnKeyPress(maxLength),
	    onKeyUp: function onKeyUp(event) {
	      (0, _updateInputWidth["default"])(event.target);

	      if (_onKeyUp) {
	        _onKeyUp(event);
	      }
	    },
	    placeholder: placeholder,
	    ref: (0, _mergeRefs["default"])(_updateInputWidth["default"], updateInputWidthOnFontLoad, inputRef),
	    required: required,
	    step: step,
	    type: "number",
	    value: value !== null ? value : ''
	  })];
	}

	Input.propTypes = {
	  ariaLabel: _propTypes["default"].string,
	  autoFocus: _propTypes["default"].bool,
	  className: _propTypes["default"].string.isRequired,
	  disabled: _propTypes["default"].bool,
	  inputRef: _propTypes2.isRef,
	  max: _propTypes["default"].number,
	  min: _propTypes["default"].number,
	  name: _propTypes["default"].string,
	  nameForClass: _propTypes["default"].string,
	  onChange: _propTypes["default"].func,
	  onKeyDown: _propTypes["default"].func,
	  onKeyUp: _propTypes["default"].func,
	  placeholder: _propTypes["default"].string,
	  required: _propTypes["default"].bool,
	  showLeadingZeros: _propTypes["default"].bool,
	  step: _propTypes["default"].number,
	  value: _propTypes["default"].string
	};
} (Input$3));

var utils$1 = {};

Object.defineProperty(utils$1, "__esModule", {
  value: true
});
utils$1.between = between;
utils$1.safeMax = safeMax;
utils$1.safeMin = safeMin;

function _toConsumableArray$1(arr) { return _arrayWithoutHoles$1(arr) || _iterableToArray$1(arr) || _unsupportedIterableToArray$3(arr) || _nonIterableSpread$1(); }

function _nonIterableSpread$1() { throw new TypeError("Invalid attempt to spread non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); }

function _unsupportedIterableToArray$3(o, minLen) { if (!o) return; if (typeof o === "string") return _arrayLikeToArray$3(o, minLen); var n = Object.prototype.toString.call(o).slice(8, -1); if (n === "Object" && o.constructor) n = o.constructor.name; if (n === "Map" || n === "Set") return Array.from(o); if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray$3(o, minLen); }

function _iterableToArray$1(iter) { if (typeof Symbol !== "undefined" && iter[Symbol.iterator] != null || iter["@@iterator"] != null) return Array.from(iter); }

function _arrayWithoutHoles$1(arr) { if (Array.isArray(arr)) return _arrayLikeToArray$3(arr); }

function _arrayLikeToArray$3(arr, len) { if (len == null || len > arr.length) len = arr.length; for (var i = 0, arr2 = new Array(len); i < len; i++) { arr2[i] = arr[i]; } return arr2; }

/**
 * Returns a value no smaller than min and no larger than max.
 *
 * @param {*} value Value to return.
 * @param {*} min Minimum return value.
 * @param {*} max Maximum return value.
 */
function between(value, min, max) {
  if (min && min > value) {
    return min;
  }

  if (max && max < value) {
    return max;
  }

  return value;
}

function isValidNumber(num) {
  return num !== null && num !== false && !Number.isNaN(Number(num));
}

function safeMin() {
  for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
    args[_key] = arguments[_key];
  }

  return Math.min.apply(Math, _toConsumableArray$1(args.filter(isValidNumber)));
}

function safeMax() {
  for (var _len2 = arguments.length, args = new Array(_len2), _key2 = 0; _key2 < _len2; _key2++) {
    args[_key2] = arguments[_key2];
  }

  return Math.max.apply(Math, _toConsumableArray$1(args.filter(isValidNumber)));
}

(function (exports) {

	Object.defineProperty(exports, "__esModule", {
	  value: true
	});
	exports["default"] = DayInput;

	var _react = _interopRequireDefault(React);

	var _propTypes = _interopRequireDefault(propTypes$2.exports);

	var _dateUtils = require$$2$1;

	var _Input = _interopRequireDefault(Input$3);

	var _propTypes2 = propTypes$1;

	var _utils = utils$1;

	var _excluded = ["maxDate", "minDate", "month", "year"];

	function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { "default": obj }; }

	function _extends() { _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }

	function _objectWithoutProperties(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }

	function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }

	function DayInput(_ref) {
	  var maxDate = _ref.maxDate,
	      minDate = _ref.minDate,
	      month = _ref.month,
	      year = _ref.year,
	      otherProps = _objectWithoutProperties(_ref, _excluded);

	  var currentMonthMaxDays = function () {
	    if (!month) {
	      return 31;
	    }

	    return (0, _dateUtils.getDaysInMonth)(new Date(year, month - 1, 1));
	  }();

	  function isSameMonth(date) {
	    return date && year === (0, _dateUtils.getYear)(date).toString() && month === (0, _dateUtils.getMonthHuman)(date).toString();
	  }

	  var maxDay = (0, _utils.safeMin)(currentMonthMaxDays, isSameMonth(maxDate) && (0, _dateUtils.getDate)(maxDate));
	  var minDay = (0, _utils.safeMax)(1, isSameMonth(minDate) && (0, _dateUtils.getDate)(minDate));
	  return /*#__PURE__*/_react["default"].createElement(_Input["default"], _extends({
	    max: maxDay,
	    min: minDay,
	    name: "day"
	  }, otherProps));
	}

	DayInput.propTypes = {
	  ariaLabel: _propTypes["default"].string,
	  className: _propTypes["default"].string.isRequired,
	  disabled: _propTypes["default"].bool,
	  inputRef: _propTypes2.isRef,
	  maxDate: _propTypes2.isMaxDate,
	  minDate: _propTypes2.isMinDate,
	  month: _propTypes["default"].string,
	  onChange: _propTypes["default"].func,
	  onKeyDown: _propTypes["default"].func,
	  onKeyUp: _propTypes["default"].func,
	  placeholder: _propTypes["default"].string,
	  required: _propTypes["default"].bool,
	  showLeadingZeros: _propTypes["default"].bool,
	  value: _propTypes["default"].string,
	  year: _propTypes["default"].string
	};
} (DayInput));

var MonthInput = {};

(function (exports) {

	Object.defineProperty(exports, "__esModule", {
	  value: true
	});
	exports["default"] = MonthInput;

	var _react = _interopRequireDefault(React);

	var _propTypes = _interopRequireDefault(propTypes$2.exports);

	var _dateUtils = require$$2$1;

	var _Input = _interopRequireDefault(Input$3);

	var _propTypes2 = propTypes$1;

	var _utils = utils$1;

	var _excluded = ["maxDate", "minDate", "year"];

	function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { "default": obj }; }

	function _extends() { _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }

	function _objectWithoutProperties(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }

	function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }

	function MonthInput(_ref) {
	  var maxDate = _ref.maxDate,
	      minDate = _ref.minDate,
	      year = _ref.year,
	      otherProps = _objectWithoutProperties(_ref, _excluded);

	  function isSameYear(date) {
	    return date && year === (0, _dateUtils.getYear)(date).toString();
	  }

	  var maxMonth = (0, _utils.safeMin)(12, isSameYear(maxDate) && (0, _dateUtils.getMonthHuman)(maxDate));
	  var minMonth = (0, _utils.safeMax)(1, isSameYear(minDate) && (0, _dateUtils.getMonthHuman)(minDate));
	  return /*#__PURE__*/_react["default"].createElement(_Input["default"], _extends({
	    max: maxMonth,
	    min: minMonth,
	    name: "month"
	  }, otherProps));
	}

	MonthInput.propTypes = {
	  ariaLabel: _propTypes["default"].string,
	  className: _propTypes["default"].string.isRequired,
	  disabled: _propTypes["default"].bool,
	  inputRef: _propTypes2.isRef,
	  maxDate: _propTypes2.isMaxDate,
	  minDate: _propTypes2.isMinDate,
	  onChange: _propTypes["default"].func,
	  onKeyDown: _propTypes["default"].func,
	  onKeyUp: _propTypes["default"].func,
	  placeholder: _propTypes["default"].string,
	  required: _propTypes["default"].bool,
	  showLeadingZeros: _propTypes["default"].bool,
	  value: _propTypes["default"].string,
	  year: _propTypes["default"].string
	};
} (MonthInput));

var MonthSelect = {};

var dateFormatter = {};

var require$$0 = /*@__PURE__*/getAugmentedNamespace(esm$5);

Object.defineProperty(dateFormatter, "__esModule", {
  value: true
});
dateFormatter.formatShortMonth = dateFormatter.formatMonth = void 0;
dateFormatter.getFormatter = getFormatter;

var _getUserLocale = _interopRequireDefault$8(require$$0);

function _interopRequireDefault$8(obj) { return obj && obj.__esModule ? obj : { "default": obj }; }

var formatterCache = new Map();

function getFormatter(options) {
  return function (locale, date) {
    var localeWithDefault = locale || (0, _getUserLocale["default"])();

    if (!formatterCache.has(localeWithDefault)) {
      formatterCache.set(localeWithDefault, new Map());
    }

    var formatterCacheLocale = formatterCache.get(localeWithDefault);

    if (!formatterCacheLocale.has(options)) {
      formatterCacheLocale.set(options, new Intl.DateTimeFormat(localeWithDefault, options).format);
    }

    return formatterCacheLocale.get(options)(date);
  };
}
/**
 * Changes the hour in a Date to ensure right date formatting even if DST is messed up.
 * Workaround for bug in WebKit and Firefox with historical dates.
 * For more details, see:
 * https://bugs.chromium.org/p/chromium/issues/detail?id=750465
 * https://bugzilla.mozilla.org/show_bug.cgi?id=1385643
 *
 * @param {Date} date Date.
 */


function toSafeHour(date) {
  var safeDate = new Date(date);
  return new Date(safeDate.setHours(12));
}

function getSafeFormatter(options) {
  return function (locale, date) {
    return getFormatter(options)(locale, toSafeHour(date));
  };
}

var formatMonthOptions = {
  month: 'long'
};
var formatShortMonthOptions = {
  month: 'short'
};
var formatMonth = getSafeFormatter(formatMonthOptions);
dateFormatter.formatMonth = formatMonth;
var formatShortMonth = getSafeFormatter(formatShortMonthOptions);
dateFormatter.formatShortMonth = formatShortMonth;

(function (exports) {

	Object.defineProperty(exports, "__esModule", {
	  value: true
	});
	exports["default"] = MonthSelect;

	var _react = _interopRequireDefault(React);

	var _propTypes = _interopRequireDefault(propTypes$2.exports);

	var _mergeClassNames = _interopRequireDefault(require$$3$1);

	var _dateUtils = require$$2$1;

	var _dateFormatter = dateFormatter;

	var _propTypes2 = propTypes$1;

	var _utils = utils$1;

	var _excluded = ["ariaLabel", "className", "inputRef", "locale", "maxDate", "minDate", "placeholder", "short", "value", "year"];

	function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { "default": obj }; }

	function _extends() { _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }

	function _toConsumableArray(arr) { return _arrayWithoutHoles(arr) || _iterableToArray(arr) || _unsupportedIterableToArray(arr) || _nonIterableSpread(); }

	function _nonIterableSpread() { throw new TypeError("Invalid attempt to spread non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); }

	function _unsupportedIterableToArray(o, minLen) { if (!o) return; if (typeof o === "string") return _arrayLikeToArray(o, minLen); var n = Object.prototype.toString.call(o).slice(8, -1); if (n === "Object" && o.constructor) n = o.constructor.name; if (n === "Map" || n === "Set") return Array.from(o); if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen); }

	function _iterableToArray(iter) { if (typeof Symbol !== "undefined" && iter[Symbol.iterator] != null || iter["@@iterator"] != null) return Array.from(iter); }

	function _arrayWithoutHoles(arr) { if (Array.isArray(arr)) return _arrayLikeToArray(arr); }

	function _arrayLikeToArray(arr, len) { if (len == null || len > arr.length) len = arr.length; for (var i = 0, arr2 = new Array(len); i < len; i++) { arr2[i] = arr[i]; } return arr2; }

	function _objectWithoutProperties(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }

	function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }

	function MonthSelect(_ref) {
	  var ariaLabel = _ref.ariaLabel,
	      className = _ref.className,
	      inputRef = _ref.inputRef,
	      locale = _ref.locale,
	      maxDate = _ref.maxDate,
	      minDate = _ref.minDate,
	      _ref$placeholder = _ref.placeholder,
	      placeholder = _ref$placeholder === void 0 ? '--' : _ref$placeholder,
	      _short = _ref["short"],
	      value = _ref.value,
	      year = _ref.year,
	      otherProps = _objectWithoutProperties(_ref, _excluded);

	  function isSameYear(date) {
	    return date && year === (0, _dateUtils.getYear)(date).toString();
	  }

	  var maxMonth = (0, _utils.safeMin)(12, isSameYear(maxDate) && (0, _dateUtils.getMonthHuman)(maxDate));
	  var minMonth = (0, _utils.safeMax)(1, isSameYear(minDate) && (0, _dateUtils.getMonthHuman)(minDate));

	  var dates = _toConsumableArray(Array(12)).map(function (el, index) {
	    return new Date(2019, index, 1);
	  });

	  var name = 'month';
	  var formatter = _short ? _dateFormatter.formatShortMonth : _dateFormatter.formatMonth;
	  return /*#__PURE__*/_react["default"].createElement("select", _extends({
	    "aria-label": ariaLabel,
	    className: (0, _mergeClassNames["default"])("".concat(className, "__input"), "".concat(className, "__").concat(name)),
	    "data-input": "true",
	    "data-select": "true",
	    name: name,
	    ref: inputRef,
	    value: value !== null ? value : ''
	  }, otherProps), !value && /*#__PURE__*/_react["default"].createElement("option", {
	    value: ""
	  }, placeholder), dates.map(function (date) {
	    var month = (0, _dateUtils.getMonthHuman)(date);
	    var disabled = month < minMonth || month > maxMonth;
	    return /*#__PURE__*/_react["default"].createElement("option", {
	      key: month,
	      disabled: disabled,
	      value: month
	    }, formatter(locale, date));
	  }));
	}

	MonthSelect.propTypes = {
	  ariaLabel: _propTypes["default"].string,
	  className: _propTypes["default"].string.isRequired,
	  disabled: _propTypes["default"].bool,
	  inputRef: _propTypes2.isRef,
	  locale: _propTypes["default"].string,
	  maxDate: _propTypes2.isMaxDate,
	  minDate: _propTypes2.isMinDate,
	  onChange: _propTypes["default"].func,
	  onKeyDown: _propTypes["default"].func,
	  onKeyUp: _propTypes["default"].func,
	  placeholder: _propTypes["default"].string,
	  required: _propTypes["default"].bool,
	  "short": _propTypes["default"].bool,
	  value: _propTypes["default"].string,
	  year: _propTypes["default"].string
	};
} (MonthSelect));

var YearInput = {};

(function (exports) {

	Object.defineProperty(exports, "__esModule", {
	  value: true
	});
	exports["default"] = YearInput;

	var _react = _interopRequireDefault(React);

	var _propTypes = _interopRequireDefault(propTypes$2.exports);

	var _dateUtils = require$$2$1;

	var _Input = _interopRequireDefault(Input$3);

	var _propTypes2 = propTypes$1;

	var _utils = utils$1;

	var _excluded = ["maxDate", "minDate", "placeholder", "valueType"];

	function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { "default": obj }; }

	function _extends() { _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }

	function _objectWithoutProperties(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }

	function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }

	function YearInput(_ref) {
	  var maxDate = _ref.maxDate,
	      minDate = _ref.minDate,
	      _ref$placeholder = _ref.placeholder,
	      placeholder = _ref$placeholder === void 0 ? '----' : _ref$placeholder,
	      valueType = _ref.valueType,
	      otherProps = _objectWithoutProperties(_ref, _excluded);

	  var maxYear = (0, _utils.safeMin)(275760, maxDate && (0, _dateUtils.getYear)(maxDate));
	  var minYear = (0, _utils.safeMax)(1, minDate && (0, _dateUtils.getYear)(minDate));

	  var yearStep = function () {
	    if (valueType === 'century') {
	      return 10;
	    }

	    return 1;
	  }();

	  return /*#__PURE__*/_react["default"].createElement(_Input["default"], _extends({
	    max: maxYear,
	    min: minYear,
	    name: "year",
	    placeholder: placeholder,
	    step: yearStep
	  }, otherProps));
	}

	YearInput.propTypes = {
	  ariaLabel: _propTypes["default"].string,
	  className: _propTypes["default"].string.isRequired,
	  disabled: _propTypes["default"].bool,
	  inputRef: _propTypes2.isRef,
	  maxDate: _propTypes2.isMaxDate,
	  minDate: _propTypes2.isMinDate,
	  onChange: _propTypes["default"].func,
	  onKeyDown: _propTypes["default"].func,
	  onKeyUp: _propTypes["default"].func,
	  placeholder: _propTypes["default"].string,
	  required: _propTypes["default"].bool,
	  value: _propTypes["default"].string,
	  valueType: _propTypes2.isValueType
	};
} (YearInput));

var NativeInput = {};

(function (exports) {

	Object.defineProperty(exports, "__esModule", {
	  value: true
	});
	exports["default"] = NativeInput;

	var _react = _interopRequireDefault(React);

	var _propTypes = _interopRequireDefault(propTypes$2.exports);

	var _dateUtils = require$$2$1;

	var _propTypes2 = propTypes$1;

	function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { "default": obj }; }

	function NativeInput(_ref) {
	  var ariaLabel = _ref.ariaLabel,
	      disabled = _ref.disabled,
	      maxDate = _ref.maxDate,
	      minDate = _ref.minDate,
	      name = _ref.name,
	      onChange = _ref.onChange,
	      required = _ref.required,
	      value = _ref.value,
	      valueType = _ref.valueType;

	  var nativeInputType = function () {
	    switch (valueType) {
	      case 'decade':
	      case 'year':
	        return 'number';

	      case 'month':
	        return 'month';

	      case 'day':
	        return 'date';

	      default:
	        throw new Error('Invalid valueType.');
	    }
	  }();

	  var nativeValueParser = function () {
	    switch (valueType) {
	      case 'century':
	      case 'decade':
	      case 'year':
	        return _dateUtils.getYear;

	      case 'month':
	        return _dateUtils.getISOLocalMonth;

	      case 'day':
	        return _dateUtils.getISOLocalDate;

	      default:
	        throw new Error('Invalid valueType.');
	    }
	  }();

	  function stopPropagation(event) {
	    event.stopPropagation();
	  }

	  return /*#__PURE__*/_react["default"].createElement("input", {
	    "aria-label": ariaLabel,
	    disabled: disabled,
	    max: maxDate ? nativeValueParser(maxDate) : null,
	    min: minDate ? nativeValueParser(minDate) : null,
	    name: name,
	    onChange: onChange,
	    onFocus: stopPropagation,
	    required: required,
	    style: {
	      visibility: 'hidden',
	      position: 'absolute',
	      zIndex: '-999'
	    },
	    type: nativeInputType,
	    value: value ? nativeValueParser(value) : ''
	  });
	}

	NativeInput.propTypes = {
	  ariaLabel: _propTypes["default"].string,
	  disabled: _propTypes["default"].bool,
	  maxDate: _propTypes2.isMaxDate,
	  minDate: _propTypes2.isMinDate,
	  name: _propTypes["default"].string,
	  onChange: _propTypes["default"].func,
	  required: _propTypes["default"].bool,
	  value: _propTypes["default"].oneOfType([_propTypes["default"].string, _propTypes["default"].instanceOf(Date)]),
	  valueType: _propTypes2.isValueType
	};
} (NativeInput));

var dates = {};

Object.defineProperty(dates, "__esModule", {
  value: true
});
dates.getBegin = getBegin;
dates.getEnd = getEnd;

var _dateUtils = require$$2$1;

/**
 * Returns the beginning of a given range.
 *
 * @param {string} rangeType Range type (e.g. 'day')
 * @param {Date} date Date.
 */
function getBegin(rangeType, date) {
  switch (rangeType) {
    case 'century':
      return (0, _dateUtils.getCenturyStart)(date);

    case 'decade':
      return (0, _dateUtils.getDecadeStart)(date);

    case 'year':
      return (0, _dateUtils.getYearStart)(date);

    case 'month':
      return (0, _dateUtils.getMonthStart)(date);

    case 'day':
      return (0, _dateUtils.getDayStart)(date);

    default:
      throw new Error("Invalid rangeType: ".concat(rangeType));
  }
}
/**
 * Returns the end of a given range.
 *
 * @param {string} rangeType Range type (e.g. 'day')
 * @param {Date} date Date.
 */


function getEnd(rangeType, date) {
  switch (rangeType) {
    case 'century':
      return (0, _dateUtils.getCenturyEnd)(date);

    case 'decade':
      return (0, _dateUtils.getDecadeEnd)(date);

    case 'year':
      return (0, _dateUtils.getYearEnd)(date);

    case 'month':
      return (0, _dateUtils.getMonthEnd)(date);

    case 'day':
      return (0, _dateUtils.getDayEnd)(date);

    default:
      throw new Error("Invalid rangeType: ".concat(rangeType));
  }
}

(function (exports) {

	function _typeof(obj) { "@babel/helpers - typeof"; return _typeof = "function" == typeof Symbol && "symbol" == typeof Symbol.iterator ? function (obj) { return typeof obj; } : function (obj) { return obj && "function" == typeof Symbol && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj; }, _typeof(obj); }

	Object.defineProperty(exports, "__esModule", {
	  value: true
	});
	exports["default"] = void 0;

	var _react = _interopRequireWildcard(React);

	var _propTypes = _interopRequireDefault(propTypes$2.exports);

	var _dateUtils = require$$2$1;

	var _Divider = _interopRequireDefault(Divider$2);

	var _DayInput = _interopRequireDefault(DayInput);

	var _MonthInput = _interopRequireDefault(MonthInput);

	var _MonthSelect = _interopRequireDefault(MonthSelect);

	var _YearInput = _interopRequireDefault(YearInput);

	var _NativeInput = _interopRequireDefault(NativeInput);

	var _dateFormatter = dateFormatter;

	var _dates = dates;

	var _propTypes2 = propTypes$1;

	var _utils = utils$1;

	function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { "default": obj }; }

	function _getRequireWildcardCache(nodeInterop) { if (typeof WeakMap !== "function") return null; var cacheBabelInterop = new WeakMap(); var cacheNodeInterop = new WeakMap(); return (_getRequireWildcardCache = function _getRequireWildcardCache(nodeInterop) { return nodeInterop ? cacheNodeInterop : cacheBabelInterop; })(nodeInterop); }

	function _interopRequireWildcard(obj, nodeInterop) { if (!nodeInterop && obj && obj.__esModule) { return obj; } if (obj === null || _typeof(obj) !== "object" && typeof obj !== "function") { return { "default": obj }; } var cache = _getRequireWildcardCache(nodeInterop); if (cache && cache.has(obj)) { return cache.get(obj); } var newObj = {}; var hasPropertyDescriptor = Object.defineProperty && Object.getOwnPropertyDescriptor; for (var key in obj) { if (key !== "default" && Object.prototype.hasOwnProperty.call(obj, key)) { var desc = hasPropertyDescriptor ? Object.getOwnPropertyDescriptor(obj, key) : null; if (desc && (desc.get || desc.set)) { Object.defineProperty(newObj, key, desc); } else { newObj[key] = obj[key]; } } } newObj["default"] = obj; if (cache) { cache.set(obj, newObj); } return newObj; }

	function _extends() { _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }

	function _slicedToArray(arr, i) { return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _unsupportedIterableToArray(arr, i) || _nonIterableRest(); }

	function _nonIterableRest() { throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); }

	function _iterableToArrayLimit(arr, i) { var _i = arr == null ? null : typeof Symbol !== "undefined" && arr[Symbol.iterator] || arr["@@iterator"]; if (_i == null) return; var _arr = []; var _n = true; var _d = false; var _s, _e; try { for (_i = _i.call(arr); !(_n = (_s = _i.next()).done); _n = true) { _arr.push(_s.value); if (i && _arr.length === i) break; } } catch (err) { _d = true; _e = err; } finally { try { if (!_n && _i["return"] != null) _i["return"](); } finally { if (_d) throw _e; } } return _arr; }

	function _arrayWithHoles(arr) { if (Array.isArray(arr)) return arr; }

	function _classCallCheck(instance, Constructor) { if (!(instance instanceof Constructor)) { throw new TypeError("Cannot call a class as a function"); } }

	function _defineProperties(target, props) { for (var i = 0; i < props.length; i++) { var descriptor = props[i]; descriptor.enumerable = descriptor.enumerable || false; descriptor.configurable = true; if ("value" in descriptor) descriptor.writable = true; Object.defineProperty(target, descriptor.key, descriptor); } }

	function _createClass(Constructor, protoProps, staticProps) { if (protoProps) _defineProperties(Constructor.prototype, protoProps); if (staticProps) _defineProperties(Constructor, staticProps); Object.defineProperty(Constructor, "prototype", { writable: false }); return Constructor; }

	function _inherits(subClass, superClass) { if (typeof superClass !== "function" && superClass !== null) { throw new TypeError("Super expression must either be null or a function"); } subClass.prototype = Object.create(superClass && superClass.prototype, { constructor: { value: subClass, writable: true, configurable: true } }); Object.defineProperty(subClass, "prototype", { writable: false }); if (superClass) _setPrototypeOf(subClass, superClass); }

	function _setPrototypeOf(o, p) { _setPrototypeOf = Object.setPrototypeOf || function _setPrototypeOf(o, p) { o.__proto__ = p; return o; }; return _setPrototypeOf(o, p); }

	function _createSuper(Derived) { var hasNativeReflectConstruct = _isNativeReflectConstruct(); return function _createSuperInternal() { var Super = _getPrototypeOf(Derived), result; if (hasNativeReflectConstruct) { var NewTarget = _getPrototypeOf(this).constructor; result = Reflect.construct(Super, arguments, NewTarget); } else { result = Super.apply(this, arguments); } return _possibleConstructorReturn(this, result); }; }

	function _possibleConstructorReturn(self, call) { if (call && (_typeof(call) === "object" || typeof call === "function")) { return call; } else if (call !== void 0) { throw new TypeError("Derived constructors may only return object or undefined"); } return _assertThisInitialized(self); }

	function _assertThisInitialized(self) { if (self === void 0) { throw new ReferenceError("this hasn't been initialised - super() hasn't been called"); } return self; }

	function _isNativeReflectConstruct() { if (typeof Reflect === "undefined" || !Reflect.construct) return false; if (Reflect.construct.sham) return false; if (typeof Proxy === "function") return true; try { Boolean.prototype.valueOf.call(Reflect.construct(Boolean, [], function () {})); return true; } catch (e) { return false; } }

	function _getPrototypeOf(o) { _getPrototypeOf = Object.setPrototypeOf ? Object.getPrototypeOf : function _getPrototypeOf(o) { return o.__proto__ || Object.getPrototypeOf(o); }; return _getPrototypeOf(o); }

	function _defineProperty(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

	function _toConsumableArray(arr) { return _arrayWithoutHoles(arr) || _iterableToArray(arr) || _unsupportedIterableToArray(arr) || _nonIterableSpread(); }

	function _nonIterableSpread() { throw new TypeError("Invalid attempt to spread non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); }

	function _unsupportedIterableToArray(o, minLen) { if (!o) return; if (typeof o === "string") return _arrayLikeToArray(o, minLen); var n = Object.prototype.toString.call(o).slice(8, -1); if (n === "Object" && o.constructor) n = o.constructor.name; if (n === "Map" || n === "Set") return Array.from(o); if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen); }

	function _iterableToArray(iter) { if (typeof Symbol !== "undefined" && iter[Symbol.iterator] != null || iter["@@iterator"] != null) return Array.from(iter); }

	function _arrayWithoutHoles(arr) { if (Array.isArray(arr)) return _arrayLikeToArray(arr); }

	function _arrayLikeToArray(arr, len) { if (len == null || len > arr.length) len = arr.length; for (var i = 0, arr2 = new Array(len); i < len; i++) { arr2[i] = arr[i]; } return arr2; }

	var defaultMinDate = new Date();
	defaultMinDate.setFullYear(1, 0, 1);
	defaultMinDate.setHours(0, 0, 0, 0);
	var defaultMaxDate = new Date(8.64e15);
	var allViews = ['century', 'decade', 'year', 'month'];
	var allValueTypes = [].concat(_toConsumableArray(allViews.slice(1)), ['day']);

	function toDate(value) {
	  if (value instanceof Date) {
	    return value;
	  }

	  return new Date(value);
	}

	function datesAreDifferent(date1, date2) {
	  return date1 && !date2 || !date1 && date2 || date1 && date2 && date1.getTime() !== date2.getTime();
	}
	/**
	 * Returns value type that can be returned with currently applied settings.
	 */


	function getValueType(maxDetail) {
	  return allValueTypes[allViews.indexOf(maxDetail)];
	}

	function getValue(value, index) {
	  if (!value) {
	    return null;
	  }

	  var rawValue = Array.isArray(value) && value.length === 2 ? value[index] : value;

	  if (!rawValue) {
	    return null;
	  }

	  var valueDate = toDate(rawValue);

	  if (isNaN(valueDate.getTime())) {
	    throw new Error("Invalid date: ".concat(value));
	  }

	  return valueDate;
	}

	function getDetailValue(_ref, index) {
	  var value = _ref.value,
	      minDate = _ref.minDate,
	      maxDate = _ref.maxDate,
	      maxDetail = _ref.maxDetail;
	  var valuePiece = getValue(value, index);

	  if (!valuePiece) {
	    return null;
	  }

	  var valueType = getValueType(maxDetail);
	  var detailValueFrom = [_dates.getBegin, _dates.getEnd][index](valueType, valuePiece);
	  return (0, _utils.between)(detailValueFrom, minDate, maxDate);
	}

	var getDetailValueFrom = function getDetailValueFrom(args) {
	  return getDetailValue(args, 0);
	};

	var getDetailValueTo = function getDetailValueTo(args) {
	  return getDetailValue(args, 1);
	};

	var getDetailValueArray = function getDetailValueArray(args) {
	  var value = args.value;

	  if (Array.isArray(value)) {
	    return value;
	  }

	  return [getDetailValueFrom, getDetailValueTo].map(function (fn) {
	    return fn(args);
	  });
	};

	function isInternalInput(element) {
	  return element.getAttribute('data-input') === 'true';
	}

	function findInput(element, property) {
	  var nextElement = element;

	  do {
	    nextElement = nextElement[property];
	  } while (nextElement && !isInternalInput(nextElement));

	  return nextElement;
	}

	function focus(element) {
	  if (element) {
	    element.focus();
	  }
	}

	function _renderCustomInputs(placeholder, elementFunctions, allowMultipleInstances) {
	  var usedFunctions = [];
	  var pattern = new RegExp(Object.keys(elementFunctions).map(function (el) {
	    return "".concat(el, "+");
	  }).join('|'), 'g');
	  var matches = placeholder.match(pattern);
	  return placeholder.split(pattern).reduce(function (arr, element, index) {
	    var divider = element &&
	    /*#__PURE__*/
	    // eslint-disable-next-line react/no-array-index-key
	    _react["default"].createElement(_Divider["default"], {
	      key: "separator_".concat(index)
	    }, element);

	    var res = [].concat(_toConsumableArray(arr), [divider]);
	    var currentMatch = matches && matches[index];

	    if (currentMatch) {
	      var renderFunction = elementFunctions[currentMatch] || elementFunctions[Object.keys(elementFunctions).find(function (elementFunction) {
	        return currentMatch.match(elementFunction);
	      })];

	      if (!allowMultipleInstances && usedFunctions.includes(renderFunction)) {
	        res.push(currentMatch);
	      } else {
	        res.push(renderFunction(currentMatch, index));
	        usedFunctions.push(renderFunction);
	      }
	    }

	    return res;
	  }, []);
	}

	var DateInput = /*#__PURE__*/function (_PureComponent) {
	  _inherits(DateInput, _PureComponent);

	  var _super = _createSuper(DateInput);

	  function DateInput() {
	    var _this;

	    _classCallCheck(this, DateInput);

	    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
	      args[_key] = arguments[_key];
	    }

	    _this = _super.call.apply(_super, [this].concat(args));

	    _defineProperty(_assertThisInitialized(_this), "state", {
	      year: null,
	      month: null,
	      day: null
	    });

	    _defineProperty(_assertThisInitialized(_this), "dayInput", /*#__PURE__*/(0, _react.createRef)());

	    _defineProperty(_assertThisInitialized(_this), "monthInput", /*#__PURE__*/(0, _react.createRef)());

	    _defineProperty(_assertThisInitialized(_this), "yearInput", /*#__PURE__*/(0, _react.createRef)());

	    _defineProperty(_assertThisInitialized(_this), "onClick", function (event) {
	      if (event.target === event.currentTarget) {
	        // Wrapper was directly clicked
	        var firstInput = event.target.children[1];
	        focus(firstInput);
	      }
	    });

	    _defineProperty(_assertThisInitialized(_this), "onKeyDown", function (event) {
	      switch (event.key) {
	        case 'ArrowLeft':
	        case 'ArrowRight':
	        case _this.divider:
	          {
	            event.preventDefault();
	            var input = event.target;
	            var property = event.key === 'ArrowLeft' ? 'previousElementSibling' : 'nextElementSibling';
	            var nextInput = findInput(input, property);
	            focus(nextInput);
	            break;
	          }
	      }
	    });

	    _defineProperty(_assertThisInitialized(_this), "onKeyUp", function (event) {
	      var key = event.key,
	          input = event.target;
	      var isNumberKey = !isNaN(parseInt(key, 10));

	      if (!isNumberKey) {
	        return;
	      }

	      var value = input.value;
	      var max = input.getAttribute('max');
	      /**
	       * Given 1, the smallest possible number the user could type by adding another digit is 10.
	       * 10 would be a valid value given max = 12, so we won't jump to the next input.
	       * However, given 2, smallers possible number would be 20, and thus keeping the focus in
	       * this field doesn't make sense.
	       */

	      if (value * 10 > max || value.length >= max.length) {
	        var property = 'nextElementSibling';
	        var nextInput = findInput(input, property);
	        focus(nextInput);
	      }
	    });

	    _defineProperty(_assertThisInitialized(_this), "onChange", function (event) {
	      var _event$target = event.target,
	          name = _event$target.name,
	          value = _event$target.value;

	      _this.setState(_defineProperty({}, name, value), _this.onChangeExternal);
	    });

	    _defineProperty(_assertThisInitialized(_this), "onChangeNative", function (event) {
	      var onChange = _this.props.onChange;
	      var value = event.target.value;

	      if (!onChange) {
	        return;
	      }

	      var processedValue = function () {
	        if (!value) {
	          return null;
	        }

	        var _value$split = value.split('-'),
	            _value$split2 = _slicedToArray(_value$split, 3),
	            yearString = _value$split2[0],
	            monthString = _value$split2[1],
	            dayString = _value$split2[2];

	        var year = parseInt(yearString, 10);
	        var monthIndex = parseInt(monthString, 10) - 1 || 0;
	        var day = parseInt(dayString, 10) || 1;
	        var proposedValue = new Date();
	        proposedValue.setFullYear(year, monthIndex, day);
	        proposedValue.setHours(0, 0, 0, 0);
	        return proposedValue;
	      }();

	      onChange(processedValue, false);
	    });

	    _defineProperty(_assertThisInitialized(_this), "onChangeExternal", function () {
	      var onChange = _this.props.onChange;

	      if (!onChange) {
	        return;
	      }

	      var formElements = [_this.dayInput.current, _this.monthInput.current, _this.yearInput.current].filter(Boolean);
	      var values = {};
	      formElements.forEach(function (formElement) {
	        values[formElement.name] = formElement.value;
	      });

	      if (formElements.every(function (formElement) {
	        return !formElement.value;
	      })) {
	        onChange(null, false);
	      } else if (formElements.every(function (formElement) {
	        return formElement.value && formElement.validity.valid;
	      })) {
	        var year = parseInt(values.year, 10) || new Date().getFullYear();
	        var monthIndex = parseInt(values.month || 1, 10) - 1;
	        var day = parseInt(values.day || 1, 10);
	        var proposedValue = new Date();
	        proposedValue.setFullYear(year, monthIndex, day);
	        proposedValue.setHours(0, 0, 0, 0);

	        var processedValue = _this.getProcessedValue(proposedValue);

	        onChange(processedValue, false);
	      }
	    });

	    _defineProperty(_assertThisInitialized(_this), "renderDay", function (currentMatch, index) {
	      var _this$props = _this.props,
	          autoFocus = _this$props.autoFocus,
	          dayAriaLabel = _this$props.dayAriaLabel,
	          dayPlaceholder = _this$props.dayPlaceholder,
	          showLeadingZeros = _this$props.showLeadingZeros;
	      var _this$state = _this.state,
	          day = _this$state.day,
	          month = _this$state.month,
	          year = _this$state.year;

	      if (currentMatch && currentMatch.length > 2) {
	        throw new Error("Unsupported token: ".concat(currentMatch));
	      }

	      var showLeadingZerosFromFormat = currentMatch && currentMatch.length === 2;
	      return /*#__PURE__*/_react["default"].createElement(_DayInput["default"], _extends({
	        key: "day"
	      }, _this.commonInputProps, {
	        ariaLabel: dayAriaLabel // eslint-disable-next-line jsx-a11y/no-autofocus
	        ,
	        autoFocus: index === 0 && autoFocus,
	        inputRef: _this.dayInput,
	        month: month,
	        placeholder: dayPlaceholder,
	        showLeadingZeros: showLeadingZerosFromFormat || showLeadingZeros,
	        value: day,
	        year: year
	      }));
	    });

	    _defineProperty(_assertThisInitialized(_this), "renderMonth", function (currentMatch, index) {
	      var _this$props2 = _this.props,
	          autoFocus = _this$props2.autoFocus,
	          locale = _this$props2.locale,
	          monthAriaLabel = _this$props2.monthAriaLabel,
	          monthPlaceholder = _this$props2.monthPlaceholder,
	          showLeadingZeros = _this$props2.showLeadingZeros;
	      var _this$state2 = _this.state,
	          month = _this$state2.month,
	          year = _this$state2.year;

	      if (currentMatch && currentMatch.length > 4) {
	        throw new Error("Unsupported token: ".concat(currentMatch));
	      }

	      if (currentMatch.length > 2) {
	        return /*#__PURE__*/_react["default"].createElement(_MonthSelect["default"], _extends({
	          key: "month"
	        }, _this.commonInputProps, {
	          ariaLabel: monthAriaLabel // eslint-disable-next-line jsx-a11y/no-autofocus
	          ,
	          autoFocus: index === 0 && autoFocus,
	          inputRef: _this.monthInput,
	          locale: locale,
	          placeholder: monthPlaceholder,
	          "short": currentMatch.length === 3,
	          value: month,
	          year: year
	        }));
	      }

	      var showLeadingZerosFromFormat = currentMatch && currentMatch.length === 2;
	      return /*#__PURE__*/_react["default"].createElement(_MonthInput["default"], _extends({
	        key: "month"
	      }, _this.commonInputProps, {
	        ariaLabel: monthAriaLabel // eslint-disable-next-line jsx-a11y/no-autofocus
	        ,
	        autoFocus: index === 0 && autoFocus,
	        inputRef: _this.monthInput,
	        placeholder: monthPlaceholder,
	        showLeadingZeros: showLeadingZerosFromFormat || showLeadingZeros,
	        value: month,
	        year: year
	      }));
	    });

	    _defineProperty(_assertThisInitialized(_this), "renderYear", function (currentMatch, index) {
	      var _this$props3 = _this.props,
	          autoFocus = _this$props3.autoFocus,
	          yearAriaLabel = _this$props3.yearAriaLabel,
	          yearPlaceholder = _this$props3.yearPlaceholder;
	      var year = _this.state.year;
	      return /*#__PURE__*/_react["default"].createElement(_YearInput["default"], _extends({
	        key: "year"
	      }, _this.commonInputProps, {
	        ariaLabel: yearAriaLabel // eslint-disable-next-line jsx-a11y/no-autofocus
	        ,
	        autoFocus: index === 0 && autoFocus,
	        inputRef: _this.yearInput,
	        placeholder: yearPlaceholder,
	        value: year,
	        valueType: _this.valueType
	      }));
	    });

	    return _this;
	  }

	  _createClass(DateInput, [{
	    key: "formatDate",
	    get: function get() {
	      var maxDetail = this.props.maxDetail;
	      var options = {
	        year: 'numeric'
	      };
	      var level = allViews.indexOf(maxDetail);

	      if (level >= 2) {
	        options.month = 'numeric';
	      }

	      if (level >= 3) {
	        options.day = 'numeric';
	      }

	      return (0, _dateFormatter.getFormatter)(options);
	    }
	    /**
	     * Gets current value in a desired format.
	     */

	  }, {
	    key: "getProcessedValue",
	    value: function getProcessedValue(value) {
	      var _this$props4 = this.props,
	          minDate = _this$props4.minDate,
	          maxDate = _this$props4.maxDate,
	          maxDetail = _this$props4.maxDetail,
	          returnValue = _this$props4.returnValue;

	      var processFunction = function () {
	        switch (returnValue) {
	          case 'start':
	            return getDetailValueFrom;

	          case 'end':
	            return getDetailValueTo;

	          case 'range':
	            return getDetailValueArray;

	          default:
	            throw new Error('Invalid returnValue.');
	        }
	      }();

	      return processFunction({
	        value: value,
	        minDate: minDate,
	        maxDate: maxDate,
	        maxDetail: maxDetail
	      });
	    }
	  }, {
	    key: "divider",
	    get: function get() {
	      var dividers = this.placeholder.match(/[^0-9a-z]/i);
	      return dividers ? dividers[0] : null;
	    }
	  }, {
	    key: "placeholder",
	    get: function get() {
	      var _this$props5 = this.props,
	          format = _this$props5.format,
	          locale = _this$props5.locale;

	      if (format) {
	        return format;
	      }

	      var year = 2017;
	      var monthIndex = 11;
	      var day = 11;
	      var date = new Date(year, monthIndex, day);
	      var formattedDate = this.formatDate(locale, date);
	      var datePieces = ['year', 'month', 'day'];
	      var datePieceReplacements = ['y', 'M', 'd'];

	      function formatDatePiece(name, dateToFormat) {
	        return (0, _dateFormatter.getFormatter)(_defineProperty({
	          useGrouping: false
	        }, name, 'numeric'))(locale, dateToFormat).match(/\d{1,}/);
	      }

	      var placeholder = formattedDate;
	      datePieces.forEach(function (datePiece, index) {
	        var formattedDatePiece = formatDatePiece(datePiece, date);
	        var datePieceReplacement = datePieceReplacements[index];
	        placeholder = placeholder.replace(formattedDatePiece, datePieceReplacement);
	      }); // See: https://github.com/wojtekmaj/react-date-picker/issues/396

	      placeholder = placeholder.replace('17', 'y');
	      return placeholder;
	    }
	  }, {
	    key: "commonInputProps",
	    get: function get() {
	      var _this$props6 = this.props,
	          className = _this$props6.className,
	          disabled = _this$props6.disabled,
	          isCalendarOpen = _this$props6.isCalendarOpen,
	          maxDate = _this$props6.maxDate,
	          minDate = _this$props6.minDate,
	          required = _this$props6.required;
	      return {
	        className: className,
	        disabled: disabled,
	        maxDate: maxDate || defaultMaxDate,
	        minDate: minDate || defaultMinDate,
	        onChange: this.onChange,
	        onKeyDown: this.onKeyDown,
	        onKeyUp: this.onKeyUp,
	        // This is only for showing validity when editing
	        required: required || isCalendarOpen
	      };
	    }
	  }, {
	    key: "valueType",
	    get: function get() {
	      var maxDetail = this.props.maxDetail;
	      return getValueType(maxDetail);
	    }
	  }, {
	    key: "renderCustomInputs",
	    value: function renderCustomInputs() {
	      var placeholder = this.placeholder;
	      var format = this.props.format;
	      var elementFunctions = {
	        d: this.renderDay,
	        M: this.renderMonth,
	        y: this.renderYear
	      };
	      var allowMultipleInstances = typeof format !== 'undefined';
	      return _renderCustomInputs(placeholder, elementFunctions, allowMultipleInstances);
	    }
	  }, {
	    key: "renderNativeInput",
	    value: function renderNativeInput() {
	      var _this$props7 = this.props,
	          disabled = _this$props7.disabled,
	          maxDate = _this$props7.maxDate,
	          minDate = _this$props7.minDate,
	          name = _this$props7.name,
	          nativeInputAriaLabel = _this$props7.nativeInputAriaLabel,
	          required = _this$props7.required;
	      var value = this.state.value;
	      return /*#__PURE__*/_react["default"].createElement(_NativeInput["default"], {
	        key: "date",
	        ariaLabel: nativeInputAriaLabel,
	        disabled: disabled,
	        maxDate: maxDate || defaultMaxDate,
	        minDate: minDate || defaultMinDate,
	        name: name,
	        onChange: this.onChangeNative,
	        required: required,
	        value: value,
	        valueType: this.valueType
	      });
	    }
	  }, {
	    key: "render",
	    value: function render() {
	      var className = this.props.className;
	      return (
	        /*#__PURE__*/
	        // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
	        _react["default"].createElement("div", {
	          className: className,
	          onClick: this.onClick
	        }, this.renderNativeInput(), this.renderCustomInputs())
	      );
	    }
	  }], [{
	    key: "getDerivedStateFromProps",
	    value: function getDerivedStateFromProps(nextProps, prevState) {
	      var minDate = nextProps.minDate,
	          maxDate = nextProps.maxDate,
	          maxDetail = nextProps.maxDetail;
	      var nextState = {};
	      /**
	       * If isCalendarOpen flag has changed, we have to update it.
	       * It's saved in state purely for use in getDerivedStateFromProps.
	       */

	      if (nextProps.isCalendarOpen !== prevState.isCalendarOpen) {
	        nextState.isCalendarOpen = nextProps.isCalendarOpen;
	      }
	      /**
	       * If the next value is different from the current one  (with an exception of situation in
	       * which values provided are limited by minDate and maxDate so that the dates are the same),
	       * get a new one.
	       */


	      var nextValue = getDetailValueFrom({
	        value: nextProps.value,
	        minDate: minDate,
	        maxDate: maxDate,
	        maxDetail: maxDetail
	      });
	      var values = [nextValue, prevState.value];

	      if ( // Toggling calendar visibility resets values
	      nextState.isCalendarOpen || // Flag was toggled
	      datesAreDifferent.apply(void 0, _toConsumableArray(values.map(function (value) {
	        return getDetailValueFrom({
	          value: value,
	          minDate: minDate,
	          maxDate: maxDate,
	          maxDetail: maxDetail
	        });
	      }))) || datesAreDifferent.apply(void 0, _toConsumableArray(values.map(function (value) {
	        return getDetailValueTo({
	          value: value,
	          minDate: minDate,
	          maxDate: maxDate,
	          maxDetail: maxDetail
	        });
	      })))) {
	        if (nextValue) {
	          nextState.year = (0, _dateUtils.getYear)(nextValue).toString();
	          nextState.month = (0, _dateUtils.getMonthHuman)(nextValue).toString();
	          nextState.day = (0, _dateUtils.getDate)(nextValue).toString();
	        } else {
	          nextState.year = null;
	          nextState.month = null;
	          nextState.day = null;
	        }

	        nextState.value = nextValue;
	      }

	      return nextState;
	    }
	  }]);

	  return DateInput;
	}(_react.PureComponent);

	exports["default"] = DateInput;
	DateInput.defaultProps = {
	  maxDetail: 'month',
	  name: 'date',
	  returnValue: 'start'
	};

	var isValue = _propTypes["default"].oneOfType([_propTypes["default"].string, _propTypes["default"].instanceOf(Date)]);

	DateInput.propTypes = {
	  autoFocus: _propTypes["default"].bool,
	  className: _propTypes["default"].string.isRequired,
	  dayAriaLabel: _propTypes["default"].string,
	  dayPlaceholder: _propTypes["default"].string,
	  disabled: _propTypes["default"].bool,
	  format: _propTypes["default"].string,
	  isCalendarOpen: _propTypes["default"].bool,
	  locale: _propTypes["default"].string,
	  maxDate: _propTypes2.isMaxDate,
	  maxDetail: _propTypes["default"].oneOf(allViews),
	  minDate: _propTypes2.isMinDate,
	  monthAriaLabel: _propTypes["default"].string,
	  monthPlaceholder: _propTypes["default"].string,
	  name: _propTypes["default"].string,
	  nativeInputAriaLabel: _propTypes["default"].string,
	  onChange: _propTypes["default"].func,
	  required: _propTypes["default"].bool,
	  returnValue: _propTypes["default"].oneOf(['start', 'end', 'range']),
	  showLeadingZeros: _propTypes["default"].bool,
	  value: _propTypes["default"].oneOfType([isValue, _propTypes["default"].arrayOf(isValue)]),
	  yearAriaLabel: _propTypes["default"].string,
	  yearPlaceholder: _propTypes["default"].string
	};
} (DateInput));

(function (exports) {

	function _typeof(obj) { "@babel/helpers - typeof"; return _typeof = "function" == typeof Symbol && "symbol" == typeof Symbol.iterator ? function (obj) { return typeof obj; } : function (obj) { return obj && "function" == typeof Symbol && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj; }, _typeof(obj); }

	Object.defineProperty(exports, "__esModule", {
	  value: true
	});
	exports["default"] = void 0;

	var _react = _interopRequireWildcard(React);

	var _propTypes = _interopRequireDefault(propTypes$2.exports);

	var _makeEventProps = _interopRequireDefault(require$$2$2);

	var _mergeClassNames = _interopRequireDefault(require$$3$1);

	var _reactCalendar = _interopRequireDefault(require$$4$1);

	var _reactFit = _interopRequireDefault(require$$5);

	var _DateInput = _interopRequireDefault(DateInput);

	var _propTypes2 = propTypes$1;

	var _excluded = ["calendarClassName", "className", "onChange", "value"],
	    _excluded2 = ["onChange"];

	function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { "default": obj }; }

	function _getRequireWildcardCache(nodeInterop) { if (typeof WeakMap !== "function") return null; var cacheBabelInterop = new WeakMap(); var cacheNodeInterop = new WeakMap(); return (_getRequireWildcardCache = function _getRequireWildcardCache(nodeInterop) { return nodeInterop ? cacheNodeInterop : cacheBabelInterop; })(nodeInterop); }

	function _interopRequireWildcard(obj, nodeInterop) { if (!nodeInterop && obj && obj.__esModule) { return obj; } if (obj === null || _typeof(obj) !== "object" && typeof obj !== "function") { return { "default": obj }; } var cache = _getRequireWildcardCache(nodeInterop); if (cache && cache.has(obj)) { return cache.get(obj); } var newObj = {}; var hasPropertyDescriptor = Object.defineProperty && Object.getOwnPropertyDescriptor; for (var key in obj) { if (key !== "default" && Object.prototype.hasOwnProperty.call(obj, key)) { var desc = hasPropertyDescriptor ? Object.getOwnPropertyDescriptor(obj, key) : null; if (desc && (desc.get || desc.set)) { Object.defineProperty(newObj, key, desc); } else { newObj[key] = obj[key]; } } } newObj["default"] = obj; if (cache) { cache.set(obj, newObj); } return newObj; }

	function _objectWithoutProperties(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }

	function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }

	function _extends() { _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }

	function _slicedToArray(arr, i) { return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _unsupportedIterableToArray(arr, i) || _nonIterableRest(); }

	function _nonIterableRest() { throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); }

	function _unsupportedIterableToArray(o, minLen) { if (!o) return; if (typeof o === "string") return _arrayLikeToArray(o, minLen); var n = Object.prototype.toString.call(o).slice(8, -1); if (n === "Object" && o.constructor) n = o.constructor.name; if (n === "Map" || n === "Set") return Array.from(o); if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen); }

	function _arrayLikeToArray(arr, len) { if (len == null || len > arr.length) len = arr.length; for (var i = 0, arr2 = new Array(len); i < len; i++) { arr2[i] = arr[i]; } return arr2; }

	function _iterableToArrayLimit(arr, i) { var _i = arr == null ? null : typeof Symbol !== "undefined" && arr[Symbol.iterator] || arr["@@iterator"]; if (_i == null) return; var _arr = []; var _n = true; var _d = false; var _s, _e; try { for (_i = _i.call(arr); !(_n = (_s = _i.next()).done); _n = true) { _arr.push(_s.value); if (i && _arr.length === i) break; } } catch (err) { _d = true; _e = err; } finally { try { if (!_n && _i["return"] != null) _i["return"](); } finally { if (_d) throw _e; } } return _arr; }

	function _arrayWithHoles(arr) { if (Array.isArray(arr)) return arr; }

	function _classCallCheck(instance, Constructor) { if (!(instance instanceof Constructor)) { throw new TypeError("Cannot call a class as a function"); } }

	function _defineProperties(target, props) { for (var i = 0; i < props.length; i++) { var descriptor = props[i]; descriptor.enumerable = descriptor.enumerable || false; descriptor.configurable = true; if ("value" in descriptor) descriptor.writable = true; Object.defineProperty(target, descriptor.key, descriptor); } }

	function _createClass(Constructor, protoProps, staticProps) { if (protoProps) _defineProperties(Constructor.prototype, protoProps); if (staticProps) _defineProperties(Constructor, staticProps); Object.defineProperty(Constructor, "prototype", { writable: false }); return Constructor; }

	function _inherits(subClass, superClass) { if (typeof superClass !== "function" && superClass !== null) { throw new TypeError("Super expression must either be null or a function"); } subClass.prototype = Object.create(superClass && superClass.prototype, { constructor: { value: subClass, writable: true, configurable: true } }); Object.defineProperty(subClass, "prototype", { writable: false }); if (superClass) _setPrototypeOf(subClass, superClass); }

	function _setPrototypeOf(o, p) { _setPrototypeOf = Object.setPrototypeOf || function _setPrototypeOf(o, p) { o.__proto__ = p; return o; }; return _setPrototypeOf(o, p); }

	function _createSuper(Derived) { var hasNativeReflectConstruct = _isNativeReflectConstruct(); return function _createSuperInternal() { var Super = _getPrototypeOf(Derived), result; if (hasNativeReflectConstruct) { var NewTarget = _getPrototypeOf(this).constructor; result = Reflect.construct(Super, arguments, NewTarget); } else { result = Super.apply(this, arguments); } return _possibleConstructorReturn(this, result); }; }

	function _possibleConstructorReturn(self, call) { if (call && (_typeof(call) === "object" || typeof call === "function")) { return call; } else if (call !== void 0) { throw new TypeError("Derived constructors may only return object or undefined"); } return _assertThisInitialized(self); }

	function _assertThisInitialized(self) { if (self === void 0) { throw new ReferenceError("this hasn't been initialised - super() hasn't been called"); } return self; }

	function _isNativeReflectConstruct() { if (typeof Reflect === "undefined" || !Reflect.construct) return false; if (Reflect.construct.sham) return false; if (typeof Proxy === "function") return true; try { Boolean.prototype.valueOf.call(Reflect.construct(Boolean, [], function () {})); return true; } catch (e) { return false; } }

	function _getPrototypeOf(o) { _getPrototypeOf = Object.setPrototypeOf ? Object.getPrototypeOf : function _getPrototypeOf(o) { return o.__proto__ || Object.getPrototypeOf(o); }; return _getPrototypeOf(o); }

	function _defineProperty(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

	var baseClassName = 'react-date-picker';
	var outsideActionEvents = ['mousedown', 'focusin', 'touchstart'];
	var allViews = ['century', 'decade', 'year', 'month'];

	var DatePicker = /*#__PURE__*/function (_PureComponent) {
	  _inherits(DatePicker, _PureComponent);

	  var _super = _createSuper(DatePicker);

	  function DatePicker() {
	    var _this;

	    _classCallCheck(this, DatePicker);

	    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
	      args[_key] = arguments[_key];
	    }

	    _this = _super.call.apply(_super, [this].concat(args));

	    _defineProperty(_assertThisInitialized(_this), "state", {});

	    _defineProperty(_assertThisInitialized(_this), "onOutsideAction", function (event) {
	      // Try event.composedPath first to handle clicks inside a Shadow DOM.
	      var target = 'composedPath' in event ? event.composedPath()[0] : event.target;

	      if (_this.wrapper && !_this.wrapper.contains(target)) {
	        _this.closeCalendar();
	      }
	    });

	    _defineProperty(_assertThisInitialized(_this), "onChange", function (value) {
	      var closeCalendar = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : _this.props.closeCalendar;
	      var onChange = _this.props.onChange;

	      if (closeCalendar) {
	        _this.closeCalendar();
	      }

	      if (onChange) {
	        onChange(value);
	      }
	    });

	    _defineProperty(_assertThisInitialized(_this), "onFocus", function (event) {
	      var _this$props = _this.props,
	          disabled = _this$props.disabled,
	          onFocus = _this$props.onFocus,
	          openCalendarOnFocus = _this$props.openCalendarOnFocus;

	      if (onFocus) {
	        onFocus(event);
	      } // Internet Explorer still fires onFocus on disabled elements


	      if (disabled) {
	        return;
	      }

	      if (openCalendarOnFocus) {
	        if (event.target.getAttribute('data-select') === 'true') {
	          return;
	        }

	        _this.openCalendar();
	      }
	    });

	    _defineProperty(_assertThisInitialized(_this), "onKeyDown", function (event) {
	      if (event.key === 'Escape') {
	        _this.closeCalendar();
	      }
	    });

	    _defineProperty(_assertThisInitialized(_this), "openCalendar", function () {
	      _this.setState({
	        isOpen: true
	      });
	    });

	    _defineProperty(_assertThisInitialized(_this), "closeCalendar", function () {
	      _this.setState(function (prevState) {
	        if (!prevState.isOpen) {
	          return null;
	        }

	        return {
	          isOpen: false
	        };
	      });
	    });

	    _defineProperty(_assertThisInitialized(_this), "toggleCalendar", function () {
	      _this.setState(function (prevState) {
	        return {
	          isOpen: !prevState.isOpen
	        };
	      });
	    });

	    _defineProperty(_assertThisInitialized(_this), "stopPropagation", function (event) {
	      return event.stopPropagation();
	    });

	    _defineProperty(_assertThisInitialized(_this), "clear", function () {
	      return _this.onChange(null);
	    });

	    return _this;
	  }

	  _createClass(DatePicker, [{
	    key: "componentDidMount",
	    value: function componentDidMount() {
	      this.handleOutsideActionListeners();
	    }
	  }, {
	    key: "componentDidUpdate",
	    value: function componentDidUpdate(prevProps, prevState) {
	      var isOpen = this.state.isOpen;
	      var _this$props2 = this.props,
	          onCalendarClose = _this$props2.onCalendarClose,
	          onCalendarOpen = _this$props2.onCalendarOpen;

	      if (isOpen !== prevState.isOpen) {
	        this.handleOutsideActionListeners();
	        var callback = isOpen ? onCalendarOpen : onCalendarClose;
	        if (callback) callback();
	      }
	    }
	  }, {
	    key: "componentWillUnmount",
	    value: function componentWillUnmount() {
	      this.handleOutsideActionListeners(false);
	    }
	  }, {
	    key: "eventProps",
	    get: function get() {
	      return (0, _makeEventProps["default"])(this.props);
	    }
	  }, {
	    key: "handleOutsideActionListeners",
	    value: function handleOutsideActionListeners(shouldListen) {
	      var _this2 = this;

	      var isOpen = this.state.isOpen;
	      var shouldListenWithFallback = typeof shouldListen !== 'undefined' ? shouldListen : isOpen;
	      var fnName = shouldListenWithFallback ? 'addEventListener' : 'removeEventListener';
	      outsideActionEvents.forEach(function (eventName) {
	        return document[fnName](eventName, _this2.onOutsideAction);
	      });
	      document[fnName]('keydown', this.onKeyDown);
	    }
	  }, {
	    key: "renderInputs",
	    value: function renderInputs() {
	      var _this$props3 = this.props,
	          autoFocus = _this$props3.autoFocus,
	          calendarAriaLabel = _this$props3.calendarAriaLabel,
	          calendarIcon = _this$props3.calendarIcon,
	          clearAriaLabel = _this$props3.clearAriaLabel,
	          clearIcon = _this$props3.clearIcon,
	          dayAriaLabel = _this$props3.dayAriaLabel,
	          dayPlaceholder = _this$props3.dayPlaceholder,
	          disableCalendar = _this$props3.disableCalendar,
	          disabled = _this$props3.disabled,
	          format = _this$props3.format,
	          locale = _this$props3.locale,
	          maxDate = _this$props3.maxDate,
	          maxDetail = _this$props3.maxDetail,
	          minDate = _this$props3.minDate,
	          monthAriaLabel = _this$props3.monthAriaLabel,
	          monthPlaceholder = _this$props3.monthPlaceholder,
	          name = _this$props3.name,
	          nativeInputAriaLabel = _this$props3.nativeInputAriaLabel,
	          required = _this$props3.required,
	          returnValue = _this$props3.returnValue,
	          showLeadingZeros = _this$props3.showLeadingZeros,
	          value = _this$props3.value,
	          yearAriaLabel = _this$props3.yearAriaLabel,
	          yearPlaceholder = _this$props3.yearPlaceholder;
	      var isOpen = this.state.isOpen;

	      var _concat = [].concat(value),
	          _concat2 = _slicedToArray(_concat, 1),
	          valueFrom = _concat2[0];

	      var ariaLabelProps = {
	        dayAriaLabel: dayAriaLabel,
	        monthAriaLabel: monthAriaLabel,
	        nativeInputAriaLabel: nativeInputAriaLabel,
	        yearAriaLabel: yearAriaLabel
	      };
	      var placeholderProps = {
	        dayPlaceholder: dayPlaceholder,
	        monthPlaceholder: monthPlaceholder,
	        yearPlaceholder: yearPlaceholder
	      };
	      return /*#__PURE__*/_react["default"].createElement("div", {
	        className: "".concat(baseClassName, "__wrapper")
	      }, /*#__PURE__*/_react["default"].createElement(_DateInput["default"], _extends({}, ariaLabelProps, placeholderProps, {
	        // eslint-disable-next-line jsx-a11y/no-autofocus
	        autoFocus: autoFocus,
	        className: "".concat(baseClassName, "__inputGroup"),
	        disabled: disabled,
	        format: format,
	        isCalendarOpen: isOpen,
	        locale: locale,
	        maxDate: maxDate,
	        maxDetail: maxDetail,
	        minDate: minDate,
	        name: name,
	        onChange: this.onChange,
	        required: required,
	        returnValue: returnValue,
	        showLeadingZeros: showLeadingZeros,
	        value: valueFrom
	      })), clearIcon !== null && /*#__PURE__*/_react["default"].createElement("button", {
	        "aria-label": clearAriaLabel,
	        className: "".concat(baseClassName, "__clear-button ").concat(baseClassName, "__button"),
	        disabled: disabled,
	        onClick: this.clear,
	        onFocus: this.stopPropagation,
	        type: "button"
	      }, clearIcon), calendarIcon !== null && !disableCalendar && /*#__PURE__*/_react["default"].createElement("button", {
	        "aria-label": calendarAriaLabel,
	        className: "".concat(baseClassName, "__calendar-button ").concat(baseClassName, "__button"),
	        disabled: disabled,
	        onBlur: this.resetValue,
	        onClick: this.toggleCalendar,
	        onFocus: this.stopPropagation,
	        type: "button"
	      }, calendarIcon));
	    }
	  }, {
	    key: "renderCalendar",
	    value: function renderCalendar() {
	      var disableCalendar = this.props.disableCalendar;
	      var isOpen = this.state.isOpen;

	      if (isOpen === null || disableCalendar) {
	        return null;
	      }

	      var _this$props4 = this.props,
	          calendarClassName = _this$props4.calendarClassName;
	          _this$props4.className;
	          _this$props4.onChange;
	          var value = _this$props4.value,
	          calendarProps = _objectWithoutProperties(_this$props4, _excluded);

	      var className = "".concat(baseClassName, "__calendar");
	      return /*#__PURE__*/_react["default"].createElement(_reactFit["default"], null, /*#__PURE__*/_react["default"].createElement("div", {
	        ref: function ref(_ref) {
	          if (_ref && !isOpen) {
	            _ref.removeAttribute('style');
	          }
	        },
	        className: (0, _mergeClassNames["default"])(className, "".concat(className, "--").concat(isOpen ? 'open' : 'closed')),
	        style: isOpen ? undefined : {}
	      }, /*#__PURE__*/_react["default"].createElement(_reactCalendar["default"], _extends({
	        className: calendarClassName,
	        onChange: this.onChange,
	        value: value || null
	      }, calendarProps))));
	    }
	  }, {
	    key: "render",
	    value: function render() {
	      var _this3 = this;

	      var eventProps = this.eventProps;
	      var _this$props5 = this.props,
	          className = _this$props5.className,
	          disabled = _this$props5.disabled;
	      var isOpen = this.state.isOpen;

	      eventProps.onChange;
	          var eventPropsWithoutOnChange = _objectWithoutProperties(eventProps, _excluded2);

	      return /*#__PURE__*/_react["default"].createElement("div", _extends({
	        className: (0, _mergeClassNames["default"])(baseClassName, "".concat(baseClassName, "--").concat(isOpen ? 'open' : 'closed'), "".concat(baseClassName, "--").concat(disabled ? 'disabled' : 'enabled'), className)
	      }, eventPropsWithoutOnChange, {
	        onFocus: this.onFocus,
	        ref: function ref(_ref2) {
	          if (!_ref2) {
	            return;
	          }

	          _this3.wrapper = _ref2;
	        }
	      }), this.renderInputs(), this.renderCalendar());
	    }
	  }], [{
	    key: "getDerivedStateFromProps",
	    value: function getDerivedStateFromProps(nextProps, prevState) {
	      if (nextProps.isOpen !== prevState.isOpenProps) {
	        return {
	          isOpen: nextProps.isOpen,
	          isOpenProps: nextProps.isOpen
	        };
	      }

	      return null;
	    }
	  }]);

	  return DatePicker;
	}(_react.PureComponent);

	exports["default"] = DatePicker;
	var iconProps = {
	  xmlns: 'http://www.w3.org/2000/svg',
	  width: 19,
	  height: 19,
	  viewBox: '0 0 19 19',
	  stroke: 'black',
	  strokeWidth: 2
	};

	var CalendarIcon = /*#__PURE__*/_react["default"].createElement("svg", _extends({}, iconProps, {
	  className: "".concat(baseClassName, "__calendar-button__icon ").concat(baseClassName, "__button__icon")
	}), /*#__PURE__*/_react["default"].createElement("rect", {
	  fill: "none",
	  height: "15",
	  width: "15",
	  x: "2",
	  y: "2"
	}), /*#__PURE__*/_react["default"].createElement("line", {
	  x1: "6",
	  x2: "6",
	  y1: "0",
	  y2: "4"
	}), /*#__PURE__*/_react["default"].createElement("line", {
	  x1: "13",
	  x2: "13",
	  y1: "0",
	  y2: "4"
	}));

	var ClearIcon = /*#__PURE__*/_react["default"].createElement("svg", _extends({}, iconProps, {
	  className: "".concat(baseClassName, "__clear-button__icon ").concat(baseClassName, "__button__icon")
	}), /*#__PURE__*/_react["default"].createElement("line", {
	  x1: "4",
	  x2: "15",
	  y1: "4",
	  y2: "15"
	}), /*#__PURE__*/_react["default"].createElement("line", {
	  x1: "15",
	  x2: "4",
	  y1: "4",
	  y2: "15"
	}));

	DatePicker.defaultProps = {
	  calendarIcon: CalendarIcon,
	  clearIcon: ClearIcon,
	  closeCalendar: true,
	  isOpen: null,
	  openCalendarOnFocus: true,
	  returnValue: 'start'
	};

	var isValue = _propTypes["default"].oneOfType([_propTypes["default"].string, _propTypes["default"].instanceOf(Date)]);

	DatePicker.propTypes = {
	  autoFocus: _propTypes["default"].bool,
	  calendarAriaLabel: _propTypes["default"].string,
	  calendarClassName: _propTypes["default"].oneOfType([_propTypes["default"].string, _propTypes["default"].arrayOf(_propTypes["default"].string)]),
	  calendarIcon: _propTypes["default"].node,
	  className: _propTypes["default"].oneOfType([_propTypes["default"].string, _propTypes["default"].arrayOf(_propTypes["default"].string)]),
	  clearAriaLabel: _propTypes["default"].string,
	  clearIcon: _propTypes["default"].node,
	  closeCalendar: _propTypes["default"].bool,
	  dayAriaLabel: _propTypes["default"].string,
	  dayPlaceholder: _propTypes["default"].string,
	  disableCalendar: _propTypes["default"].bool,
	  disabled: _propTypes["default"].bool,
	  format: _propTypes["default"].string,
	  isOpen: _propTypes["default"].bool,
	  locale: _propTypes["default"].string,
	  maxDate: _propTypes2.isMaxDate,
	  maxDetail: _propTypes["default"].oneOf(allViews),
	  minDate: _propTypes2.isMinDate,
	  monthAriaLabel: _propTypes["default"].string,
	  monthPlaceholder: _propTypes["default"].string,
	  name: _propTypes["default"].string,
	  nativeInputAriaLabel: _propTypes["default"].string,
	  onCalendarClose: _propTypes["default"].func,
	  onCalendarOpen: _propTypes["default"].func,
	  onChange: _propTypes["default"].func,
	  onFocus: _propTypes["default"].func,
	  openCalendarOnFocus: _propTypes["default"].bool,
	  required: _propTypes["default"].bool,
	  returnValue: _propTypes["default"].oneOf(['start', 'end', 'range']),
	  showLeadingZeros: _propTypes["default"].bool,
	  value: _propTypes["default"].oneOfType([isValue, _propTypes["default"].arrayOf(isValue)]),
	  yearAriaLabel: _propTypes["default"].string,
	  yearPlaceholder: _propTypes["default"].string
	};
} (DatePicker$2));

(function (exports) {

	Object.defineProperty(exports, "__esModule", {
	  value: true
	});
	exports["default"] = void 0;

	var _DatePicker = _interopRequireDefault(DatePicker$2);

	function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { "default": obj }; }

	var _default = _DatePicker["default"];
	exports["default"] = _default;
} (entry_nostyle));

var ReactDatePicker = /*@__PURE__*/getDefaultExportFromCjs(entry_nostyle);

var DatePicker$1 = styled(ReactDatePicker)(templateObject_1$1h || (templateObject_1$1h = __makeTemplateObject(["\n  /*\n    THIS IS CSS COPIED FROM react-date-picker AND react-calendar.\n    CUSTOM DATEPICKER CSS IS AT THE BOTTOM\n  */\n  .react-calendar {\n    width: 350px;\n    @media (max-width: 375px) {\n      width: 320px;\n    }\n    max-width: 100%;\n    background: white;\n    border: 1px solid #a0a096;\n    font-family: Arial, Helvetica, sans-serif;\n    line-height: 1.125em;\n  }\n  .react-calendar--doubleView {\n    width: 700px;\n  }\n  .react-calendar--doubleView .react-calendar__viewContainer {\n    display: flex;\n    margin: -0.5em;\n  }\n  .react-calendar--doubleView .react-calendar__viewContainer > * {\n    width: 50%;\n    margin: 0.5em;\n  }\n  .react-calendar,\n  .react-calendar *,\n  .react-calendar *:before,\n  .react-calendar *:after {\n    -moz-box-sizing: border-box;\n    -webkit-box-sizing: border-box;\n    box-sizing: border-box;\n  }\n  .react-calendar button {\n    margin: 0;\n    border: 0;\n    outline: none;\n  }\n  .react-calendar button:enabled:hover {\n    cursor: pointer;\n  }\n  .react-calendar__navigation {\n    display: flex;\n    height: 44px;\n    margin-bottom: 1em;\n  }\n  .react-calendar__navigation button {\n    min-width: 44px;\n    background: none;\n  }\n  .react-calendar__navigation button:disabled {\n    background-color: #f0f0f0;\n  }\n  .react-calendar__navigation button:enabled:hover,\n  .react-calendar__navigation button:enabled:focus {\n    background-color: #e6e6e6;\n  }\n  .react-calendar__month-view__weekdays {\n    text-align: center;\n    text-transform: uppercase;\n    font-weight: bold;\n    font-size: 0.75em;\n  }\n  .react-calendar__month-view__weekdays__weekday {\n    padding: 0.5em;\n  }\n  .react-calendar__month-view__weekNumbers .react-calendar__tile {\n    display: flex;\n    align-items: center;\n    justify-content: center;\n    font-size: 0.75em;\n    font-weight: bold;\n  }\n  .react-calendar__month-view__days__day--weekend {\n    color: #d10000;\n  }\n  .react-calendar__month-view__days__day--neighboringMonth {\n    color: #757575;\n  }\n  .react-calendar__year-view .react-calendar__tile,\n  .react-calendar__decade-view .react-calendar__tile,\n  .react-calendar__century-view .react-calendar__tile {\n    padding: 2em 0.5em;\n  }\n  .react-calendar__tile {\n    max-width: 100%;\n    padding: 10px 6.6667px;\n    background: none;\n    text-align: center;\n    line-height: 16px;\n  }\n  .react-calendar__tile:disabled {\n    background-color: #f0f0f0;\n  }\n  .react-calendar__tile:enabled:hover,\n  .react-calendar__tile:enabled:focus {\n    background-color: #e6e6e6;\n  }\n  .react-calendar__tile--now {\n    background: #ffff76;\n  }\n  .react-calendar__tile--now:enabled:hover,\n  .react-calendar__tile--now:enabled:focus {\n    background: #ffffa9;\n  }\n  .react-calendar__tile--hasActive {\n    background: #76baff;\n  }\n  .react-calendar__tile--hasActive:enabled:hover,\n  .react-calendar__tile--hasActive:enabled:focus {\n    background: #a9d4ff;\n  }\n  .react-calendar__tile--active {\n    background: #006edc;\n    color: white;\n  }\n  .react-calendar__tile--active:enabled:hover,\n  .react-calendar__tile--active:enabled:focus {\n    background: #1087ff;\n  }\n  .react-calendar--selectRange .react-calendar__tile--hover {\n    background-color: #e6e6e6;\n  }\n\n  .react-date-picker {\n    display: inline-flex;\n    position: relative;\n  }\n  .react-date-picker,\n  .react-date-picker *,\n  .react-date-picker *:before,\n  .react-date-picker *:after {\n    -moz-box-sizing: border-box;\n    -webkit-box-sizing: border-box;\n    box-sizing: border-box;\n  }\n  .react-date-picker--disabled {\n    background-color: #f0f0f0;\n    color: #6d6d6d;\n  }\n  .react-date-picker__wrapper {\n    display: flex;\n    flex-grow: 1;\n    flex-shrink: 0;\n    border: thin solid gray;\n  }\n  .react-date-picker__inputGroup {\n    min-width: calc((4px * 3) + 0.54em * 8 + 0.217em * 2);\n    flex-grow: 1;\n    padding: 0 2px;\n    box-sizing: content-box;\n  }\n  .react-date-picker__inputGroup__divider {\n    padding: 1px 0;\n    white-space: pre;\n  }\n  .react-date-picker__inputGroup__input {\n    min-width: 0.54em;\n    height: 100%;\n    position: relative;\n    padding: 0 1px;\n    border: 0;\n    background: none;\n    font: inherit;\n    box-sizing: content-box;\n    -moz-appearance: textfield;\n  }\n  .react-date-picker__inputGroup__input::-webkit-outer-spin-button,\n  .react-date-picker__inputGroup__input::-webkit-inner-spin-button {\n    -webkit-appearance: none;\n    margin: 0;\n  }\n  .react-date-picker__inputGroup__input:invalid {\n    background: rgba(255, 0, 0, 0.1);\n  }\n  .react-date-picker__inputGroup__input--hasLeadingZero {\n    margin-left: -0.54em;\n    padding-left: calc(1px + 0.54em);\n  }\n  .react-date-picker__button {\n    border: 0;\n    background: transparent;\n    padding: 4px 6px;\n  }\n  .react-date-picker__button:enabled {\n    cursor: pointer;\n  }\n  .react-date-picker__button:enabled:hover .react-date-picker__button__icon,\n  .react-date-picker__button:enabled:focus .react-date-picker__button__icon {\n    stroke: #0078d7;\n  }\n  .react-date-picker__button:disabled .react-date-picker__button__icon {\n    stroke: #6d6d6d;\n  }\n  .react-date-picker__button svg {\n    display: inherit;\n  }\n  .react-date-picker__calendar {\n    width: 350px;\n    max-width: 100vw;\n    position: absolute;\n    top: 100%;\n    left: 0;\n    z-index: 1;\n  }\n  .react-date-picker__calendar--closed {\n    display: none;\n  }\n  .react-date-picker__calendar .react-calendar {\n    border-width: thin;\n  }\n\n  /*\n      HERE STARTS CUSTOM DATEPICKER CSS\n  */\n\n  & .react-date-picker {\n    &__wrapper {\n      min-width: 200px;\n      padding: 11px 16px;\n      border-radius: 10px;\n      border: 1px solid ", ";\n      background-color: ", ";\n      color: ", ";\n      font-size: ", ";\n      line-height: ", ";\n      ", "\n      ", "\n    }\n\n    &__clear-button {\n      display: none;\n      visibility: hidden;\n    }\n\n    &__calendar {\n      & .react-calendar {\n        border: none;\n        border-radius: 15px;\n        box-shadow: -1.3px 1.5px 10px 0 rgba(25, 28, 36, 0.3);\n        position: relative;\n        margin-top: 12px;\n\n        &__navigation {\n          height: 60px;\n          background-color: ", ";\n          border-top-right-radius: 15px;\n          border-top-left-radius: 15px;\n          margin-bottom: 0;\n\n          & button,\n          & span {\n            font-size: ", ";\n            line-height: ", ";\n            color: ", ";\n          }\n        }\n\n        &__viewContainer {\n          padding: 10px;\n        }\n\n        &__month-view__weekdays abbr {\n          text-decoration: none;\n\n          font-size: ", ";\n          line-height: ", ";\n          font-weight: ", ";\n          color: ", ";\n        }\n\n        &__month-view__days button {\n          color: ", ";\n\n          font-size: ", ";\n          line-height: ", ";\n\n          &.react-calendar__tile--active {\n            font-weight: ", ";\n            color: ", ";\n            background-color: ", ";\n            border-radius: 5px;\n          }\n\n          &:hover {\n            border-radius: 5px;\n          }\n        }\n      }\n    }\n  }\n"], ["\n  /*\n    THIS IS CSS COPIED FROM react-date-picker AND react-calendar.\n    CUSTOM DATEPICKER CSS IS AT THE BOTTOM\n  */\n  .react-calendar {\n    width: 350px;\n    @media (max-width: 375px) {\n      width: 320px;\n    }\n    max-width: 100%;\n    background: white;\n    border: 1px solid #a0a096;\n    font-family: Arial, Helvetica, sans-serif;\n    line-height: 1.125em;\n  }\n  .react-calendar--doubleView {\n    width: 700px;\n  }\n  .react-calendar--doubleView .react-calendar__viewContainer {\n    display: flex;\n    margin: -0.5em;\n  }\n  .react-calendar--doubleView .react-calendar__viewContainer > * {\n    width: 50%;\n    margin: 0.5em;\n  }\n  .react-calendar,\n  .react-calendar *,\n  .react-calendar *:before,\n  .react-calendar *:after {\n    -moz-box-sizing: border-box;\n    -webkit-box-sizing: border-box;\n    box-sizing: border-box;\n  }\n  .react-calendar button {\n    margin: 0;\n    border: 0;\n    outline: none;\n  }\n  .react-calendar button:enabled:hover {\n    cursor: pointer;\n  }\n  .react-calendar__navigation {\n    display: flex;\n    height: 44px;\n    margin-bottom: 1em;\n  }\n  .react-calendar__navigation button {\n    min-width: 44px;\n    background: none;\n  }\n  .react-calendar__navigation button:disabled {\n    background-color: #f0f0f0;\n  }\n  .react-calendar__navigation button:enabled:hover,\n  .react-calendar__navigation button:enabled:focus {\n    background-color: #e6e6e6;\n  }\n  .react-calendar__month-view__weekdays {\n    text-align: center;\n    text-transform: uppercase;\n    font-weight: bold;\n    font-size: 0.75em;\n  }\n  .react-calendar__month-view__weekdays__weekday {\n    padding: 0.5em;\n  }\n  .react-calendar__month-view__weekNumbers .react-calendar__tile {\n    display: flex;\n    align-items: center;\n    justify-content: center;\n    font-size: 0.75em;\n    font-weight: bold;\n  }\n  .react-calendar__month-view__days__day--weekend {\n    color: #d10000;\n  }\n  .react-calendar__month-view__days__day--neighboringMonth {\n    color: #757575;\n  }\n  .react-calendar__year-view .react-calendar__tile,\n  .react-calendar__decade-view .react-calendar__tile,\n  .react-calendar__century-view .react-calendar__tile {\n    padding: 2em 0.5em;\n  }\n  .react-calendar__tile {\n    max-width: 100%;\n    padding: 10px 6.6667px;\n    background: none;\n    text-align: center;\n    line-height: 16px;\n  }\n  .react-calendar__tile:disabled {\n    background-color: #f0f0f0;\n  }\n  .react-calendar__tile:enabled:hover,\n  .react-calendar__tile:enabled:focus {\n    background-color: #e6e6e6;\n  }\n  .react-calendar__tile--now {\n    background: #ffff76;\n  }\n  .react-calendar__tile--now:enabled:hover,\n  .react-calendar__tile--now:enabled:focus {\n    background: #ffffa9;\n  }\n  .react-calendar__tile--hasActive {\n    background: #76baff;\n  }\n  .react-calendar__tile--hasActive:enabled:hover,\n  .react-calendar__tile--hasActive:enabled:focus {\n    background: #a9d4ff;\n  }\n  .react-calendar__tile--active {\n    background: #006edc;\n    color: white;\n  }\n  .react-calendar__tile--active:enabled:hover,\n  .react-calendar__tile--active:enabled:focus {\n    background: #1087ff;\n  }\n  .react-calendar--selectRange .react-calendar__tile--hover {\n    background-color: #e6e6e6;\n  }\n\n  .react-date-picker {\n    display: inline-flex;\n    position: relative;\n  }\n  .react-date-picker,\n  .react-date-picker *,\n  .react-date-picker *:before,\n  .react-date-picker *:after {\n    -moz-box-sizing: border-box;\n    -webkit-box-sizing: border-box;\n    box-sizing: border-box;\n  }\n  .react-date-picker--disabled {\n    background-color: #f0f0f0;\n    color: #6d6d6d;\n  }\n  .react-date-picker__wrapper {\n    display: flex;\n    flex-grow: 1;\n    flex-shrink: 0;\n    border: thin solid gray;\n  }\n  .react-date-picker__inputGroup {\n    min-width: calc((4px * 3) + 0.54em * 8 + 0.217em * 2);\n    flex-grow: 1;\n    padding: 0 2px;\n    box-sizing: content-box;\n  }\n  .react-date-picker__inputGroup__divider {\n    padding: 1px 0;\n    white-space: pre;\n  }\n  .react-date-picker__inputGroup__input {\n    min-width: 0.54em;\n    height: 100%;\n    position: relative;\n    padding: 0 1px;\n    border: 0;\n    background: none;\n    font: inherit;\n    box-sizing: content-box;\n    -moz-appearance: textfield;\n  }\n  .react-date-picker__inputGroup__input::-webkit-outer-spin-button,\n  .react-date-picker__inputGroup__input::-webkit-inner-spin-button {\n    -webkit-appearance: none;\n    margin: 0;\n  }\n  .react-date-picker__inputGroup__input:invalid {\n    background: rgba(255, 0, 0, 0.1);\n  }\n  .react-date-picker__inputGroup__input--hasLeadingZero {\n    margin-left: -0.54em;\n    padding-left: calc(1px + 0.54em);\n  }\n  .react-date-picker__button {\n    border: 0;\n    background: transparent;\n    padding: 4px 6px;\n  }\n  .react-date-picker__button:enabled {\n    cursor: pointer;\n  }\n  .react-date-picker__button:enabled:hover .react-date-picker__button__icon,\n  .react-date-picker__button:enabled:focus .react-date-picker__button__icon {\n    stroke: #0078d7;\n  }\n  .react-date-picker__button:disabled .react-date-picker__button__icon {\n    stroke: #6d6d6d;\n  }\n  .react-date-picker__button svg {\n    display: inherit;\n  }\n  .react-date-picker__calendar {\n    width: 350px;\n    max-width: 100vw;\n    position: absolute;\n    top: 100%;\n    left: 0;\n    z-index: 1;\n  }\n  .react-date-picker__calendar--closed {\n    display: none;\n  }\n  .react-date-picker__calendar .react-calendar {\n    border-width: thin;\n  }\n\n  /*\n      HERE STARTS CUSTOM DATEPICKER CSS\n  */\n\n  & .react-date-picker {\n    &__wrapper {\n      min-width: 200px;\n      padding: 11px 16px;\n      border-radius: 10px;\n      border: 1px solid ", ";\n      background-color: ", ";\n      color: ", ";\n      font-size: ", ";\n      line-height: ", ";\n      ", "\n      ", "\n    }\n\n    &__clear-button {\n      display: none;\n      visibility: hidden;\n    }\n\n    &__calendar {\n      & .react-calendar {\n        border: none;\n        border-radius: 15px;\n        box-shadow: -1.3px 1.5px 10px 0 rgba(25, 28, 36, 0.3);\n        position: relative;\n        margin-top: 12px;\n\n        &__navigation {\n          height: 60px;\n          background-color: ", ";\n          border-top-right-radius: 15px;\n          border-top-left-radius: 15px;\n          margin-bottom: 0;\n\n          & button,\n          & span {\n            font-size: ", ";\n            line-height: ", ";\n            color: ", ";\n          }\n        }\n\n        &__viewContainer {\n          padding: 10px;\n        }\n\n        &__month-view__weekdays abbr {\n          text-decoration: none;\n\n          font-size: ", ";\n          line-height: ", ";\n          font-weight: ", ";\n          color: ", ";\n        }\n\n        &__month-view__days button {\n          color: ", ";\n\n          font-size: ", ";\n          line-height: ", ";\n\n          &.react-calendar__tile--active {\n            font-weight: ", ";\n            color: ", ";\n            background-color: ", ";\n            border-radius: 5px;\n          }\n\n          &:hover {\n            border-radius: 5px;\n          }\n        }\n      }\n    }\n  }\n"])), function (_a) {
    var theme = _a.theme;
    return theme.colors.datePicker.wrapper.border;
}, function (_a) {
    var theme = _a.theme;
    return theme.colors.datePicker.wrapper.backgroundColor;
}, function (_a) {
    var theme = _a.theme;
    return theme.colors.datePicker.wrapper.color;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.size.desktop.xs;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.lineHeight.desktop.xs;
}, function (_a) {
    var variant = _a.variant;
    return variant === 'left' && makeMediaQuery('sm', 'border-radius: 10px 0 0 10px');
}, function (_a) {
    var variant = _a.variant;
    return variant === 'right' && makeMediaQuery('sm', 'border-radius: 0 10px 10px 0');
}, function (_a) {
    var theme = _a.theme;
    return theme.colors.datePicker.callendar.navigation.backgroundColor;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.size.desktop.xs;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.lineHeight.desktop.xs;
}, function (_a) {
    var theme = _a.theme;
    return theme.colors.datePicker.callendar.button.color;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.size.desktop.xxs;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.lineHeight.desktop.xxs;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.weight.medium500;
}, function (_a) {
    var theme = _a.theme;
    return theme.colors.datePicker.callendar.monthViewWeeks.color;
}, function (_a) {
    var theme = _a.theme;
    return theme.colors.datePicker.callendar.monthViewDays.button.color;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.size.desktop.xxs;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.lineHeight.desktop.xxs;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.weight.bold700;
}, function (_a) {
    var theme = _a.theme;
    return theme.colors.datePicker.callendar.monthViewDays.button.active.color;
}, function (_a) {
    var theme = _a.theme;
    return theme.colors.datePicker.callendar.monthViewDays.button.active.backgroundColor;
});
var templateObject_1$1h;

var DatePicker = function (_a) {
    var value = _a.value, onChange = _a.onChange, min = _a.min, max = _a.max, _b = _a.format, format = _b === void 0 ? 'd/MM/y' : _b, language = _a.language, rest = __rest$1(_a, ["value", "onChange", "min", "max", "format", "language"]);
    var rangeDates = useMemo(function () { return ({
        minDate: min ? new Date(min) : undefined,
        maxDate: max ? new Date(max) : undefined,
    }); }, [min, max]);
    var handleChange = useCallback(function (date) { return onChange(date); }, [onChange]);
    return (jsx(DatePicker$1, __assign$1({ onChange: handleChange, value: value, format: format, locale: language || (navigator === null || navigator === void 0 ? void 0 : navigator.language), calendarIcon: jsx(Icon$4, { name: "Calendar" }) }, rangeDates, rest)));
};

var ListItem$3 = styled.li(templateObject_1$1g || (templateObject_1$1g = __makeTemplateObject(["\n  display: flex;\n  padding: 12px;\n  padding-left: 0;\n"], ["\n  display: flex;\n  padding: 12px;\n  padding-left: 0;\n"])));
var ItemContent = styled.div(templateObject_2$14 || (templateObject_2$14 = __makeTemplateObject(["\n  padding-left: 10px;\n"], ["\n  padding-left: 10px;\n"])));
var Title$a = styled(Caption3$1)(templateObject_3$T || (templateObject_3$T = __makeTemplateObject(["\n  color: ", ";\n"], ["\n  color: ", ";\n"])), function (_a) {
    var theme = _a.theme, mode = _a.mode;
    return theme.colors.descriptiveListItem[mode].title;
});
var Subtitle = styled(Caption3$1)(templateObject_4$J || (templateObject_4$J = __makeTemplateObject(["\n  font-weight: 400;\n  color: ", ";\n"], ["\n  font-weight: 400;\n  color: ", ";\n"])), function (_a) {
    var theme = _a.theme, mode = _a.mode;
    return theme.colors.descriptiveListItem[mode].subtitle;
});
var AditionalInfo = styled(Body4)(templateObject_5$y || (templateObject_5$y = __makeTemplateObject(["\n  color: ", ";\n"], ["\n  color: ", ";\n"])), function (_a) {
    var theme = _a.theme, mode = _a.mode;
    return theme.colors.descriptiveListItem[mode].additionalInfo;
});
var ItemIcon = styled(Icon$4).attrs(function (_a) {
    var mode = _a.mode, name = _a.name, theme = _a.theme;
    return ({
        name: name,
        circleColor: theme.colors.descriptiveListItem[mode].icon[name.toLowerCase()].circle,
        fillColor: theme.colors.descriptiveListItem[mode].icon[name.toLowerCase()].icon,
    });
})(templateObject_6$t || (templateObject_6$t = __makeTemplateObject([""], [""])));
var templateObject_1$1g, templateObject_2$14, templateObject_3$T, templateObject_4$J, templateObject_5$y, templateObject_6$t;

var DescriptiveListItem = function (_a) {
    var title = _a.title, subtitle = _a.subtitle, additionalInfo = _a.additionalInfo, _b = _a.icon, icon = _b === void 0 ? 'Check' : _b, _c = _a.mode, mode = _c === void 0 ? 'dark' : _c;
    var iconColorDefined = descriptiveListItemColors[mode].icon[icon.toLowerCase()];
    return (jsxs(ListItem$3, { children: [jsx(ItemIcon, { name: iconColorDefined ? icon : 'Check', mode: mode }), jsxs(ItemContent, { children: [jsx(Title$a, __assign$1({ mode: mode }, { children: title })), jsx(Subtitle, __assign$1({ mode: mode }, { children: subtitle })), additionalInfo && (jsx(AditionalInfo, __assign$1({ mode: mode }, { children: additionalInfo })))] })] }));
};

var Divider$1 = styled.div(templateObject_1$1f || (templateObject_1$1f = __makeTemplateObject(["\n  ", "\n"], ["\n  ", "\n"])), function (_a) {
    var theme = _a.theme, vertical = _a.vertical, thickness = _a.thickness, length = _a.length;
    return vertical
        ? "\n          width: ".concat(thickness || "1", "px;\n          background-color: ").concat(theme.colors.divider.backgroundColor, ";\n          height: ").concat(length || '100%', ";\n        ")
        : "\n          height: ".concat(thickness || "1", "px;\n          background-color: ").concat(theme.colors.divider.backgroundColor, ";\n          width: ").concat(length || '100%', ";\n        ");
});
var templateObject_1$1f;

var Divider = function (_a) {
    var vertical = _a.vertical, thickness = _a.thickness, length = _a.length, rest = __rest$1(_a, ["vertical", "thickness", "length"]);
    return jsx(Divider$1, __assign$1({ vertical: vertical, thickness: thickness, length: length }, rest));
};

var ErrorMessage$1 = styled(Body5)(templateObject_1$1e || (templateObject_1$1e = __makeTemplateObject(["\n  padding: 8px 16px 0px 16px;\n  color: ", ";\n"], ["\n  padding: 8px 16px 0px 16px;\n  color: ", ";\n"])), function (_a) {
    var theme = _a.theme, mode = _a.mode;
    return theme.colors.errorMessage[mode].color;
});
var templateObject_1$1e;

var ErrorMessage = function (_a) {
    var message = _a.message, _b = _a.mode, mode = _b === void 0 ? 'light' : _b;
    return (jsx(ErrorMessage$1, __assign$1({ mode: mode, role: "alert" }, { children: message })));
};

var FullWidthLink$1 = styled.div(templateObject_1$1d || (templateObject_1$1d = __makeTemplateObject(["\n  text-decoration: none;\n  display: flex;\n  justify-content: space-between;\n  padding: ", ";\n  cursor: pointer;\n\n  &:hover {\n    p {\n      ", "\n    }\n  }\n"], ["\n  text-decoration: none;\n  display: flex;\n  justify-content: space-between;\n  padding: ", ";\n  cursor: pointer;\n\n  &:hover {\n    p {\n      ", "\n    }\n  }\n"])), function (_a) {
    var theme = _a.theme;
    return "".concat(theme.space[18], " 0");
}, function (_a) {
    var theme = _a.theme;
    return "color: ".concat(theme.colors.fullWidthLink.hover.color, ";");
});
var Arrow$1 = styled.span(templateObject_2$13 || (templateObject_2$13 = __makeTemplateObject(["\n  padding-left: 15px;\n\n  svg {\n    width: 26px;\n    height: 26px;\n  }\n"], ["\n  padding-left: 15px;\n\n  svg {\n    width: 26px;\n    height: 26px;\n  }\n"])));
var templateObject_1$1d, templateObject_2$13;

var FullWidthLink = function (_a) {
    var href = _a.href, title = _a.title, color = _a.color, _b = _a.target, target = _b === void 0 ? '_blank' : _b, NextLinkComponent = _a.NextLinkComponent;
    var children = (jsxs(FullWidthLink$1, { children: [jsx(Body2, __assign$1({ color: color }, { children: title })), jsx(Arrow$1, { children: jsx(Icon$4, { name: "ArrowRight", fillColor: color }) })] }));
    return NextLinkComponent && href ? (jsx(NextLinkComponent, __assign$1({ href: href, scroll: false }, { children: children }))) : (jsx("a", __assign$1({ target: target, href: href }, { children: children })));
};

var NotificationVariant;
(function (NotificationVariant) {
    NotificationVariant["Success"] = "Success";
    NotificationVariant["Error"] = "Error";
    NotificationVariant["Info"] = "Info";
})(NotificationVariant || (NotificationVariant = {}));

var _a$2;
var VariantColors = (_a$2 = {},
    _a$2[NotificationVariant.Success] = colors.inlineNotification.success,
    _a$2[NotificationVariant.Error] = colors.inlineNotification.error,
    _a$2[NotificationVariant.Info] = colors.inlineNotification.info,
    _a$2);
var Text$3 = styled(Body2)(templateObject_1$1c || (templateObject_1$1c = __makeTemplateObject(["\n  color: ", ";\n"], ["\n  color: ", ";\n"])), function (_a) {
    var variant = _a.variant;
    return VariantColors[variant];
});
var Wrapper$6 = styled.div(templateObject_2$12 || (templateObject_2$12 = __makeTemplateObject(["\n  display: flex;\n  flex-flow: row nowrap;\n"], ["\n  display: flex;\n  flex-flow: row nowrap;\n"])));
var templateObject_1$1c, templateObject_2$12;

var _a$1;
var VariantIcons = (_a$1 = {},
    _a$1[NotificationVariant.Success] = 'Error',
    _a$1[NotificationVariant.Error] = 'Error',
    _a$1[NotificationVariant.Info] = 'Error',
    _a$1);
var InlineNotification = function (_a) {
    var variant = _a.variant, message = _a.message, _b = _a.showIcon, showIcon = _b === void 0 ? true : _b;
    return (jsxs(Wrapper$6, { children: [showIcon && (jsxs(Fragment, { children: [jsx(Icon$4, { name: VariantIcons[variant], fillColor: VariantColors[variant] }), jsx(Gutter, { size: 8 })] })), jsx(Text$3, __assign$1({ variant: variant }, { children: message }))] }));
};
InlineNotification.Variant = NotificationVariant;

var smallLabelTopValue = css(templateObject_1$1b || (templateObject_1$1b = __makeTemplateObject(["\n  top: 10px;\n  white-space: nowrap;\n"], ["\n  top: 10px;\n  white-space: nowrap;\n"])));
var midLabelTopValue = css(templateObject_2$11 || (templateObject_2$11 = __makeTemplateObject(["\n  top: 20px;\n"], ["\n  top: 20px;\n"])));
var MoveFromTop = keyframes(templateObject_3$S || (templateObject_3$S = __makeTemplateObject(["\n  0% {\n    ", "\n    opacity: 0;\n  }\n  75% {\n    opacity: 0;\n  }\n  100% {\n    ", "\n  }\n"], ["\n  0% {\n    ", "\n    opacity: 0;\n  }\n  75% {\n    opacity: 0;\n  }\n  100% {\n    ", "\n  }\n"])), smallLabelTopValue, midLabelTopValue);
var MoveFromMid = keyframes(templateObject_4$I || (templateObject_4$I = __makeTemplateObject(["\n  0% {\n    ", "\n    opacity: 0;\n  }\n  75% {\n    opacity: 0;\n  }\n  100% {\n    ", "\n}\n"], ["\n  0% {\n    ", "\n    opacity: 0;\n  }\n  75% {\n    opacity: 0;\n  }\n  100% {\n    ", "\n}\n"])), midLabelTopValue, smallLabelTopValue);
var animtionOptions = '0.75s ease-in-out forwards';
var smallLabelFont = css(templateObject_5$x || (templateObject_5$x = __makeTemplateObject(["\n  font-size: ", ";\n"], ["\n  font-size: ", ";\n"])), function (_a) {
    var theme = _a.theme;
    return theme.fonts.size.mobile.xxxs;
});
var smallLabelStylesWithAnimation = css(templateObject_6$s || (templateObject_6$s = __makeTemplateObject(["\n  animation: ", " ", ";\n  font-size: ", ";\n"], ["\n  animation: ", " ", ";\n  font-size: ", ";\n"])), MoveFromTop, animtionOptions, function (_a) {
    var theme = _a.theme;
    return theme.fonts.size.desktop.xs;
});
var midLabelStylesWithAnimation = css(templateObject_7$o || (templateObject_7$o = __makeTemplateObject(["\n  animation: ", " ", ";\n  ", ";\n"], ["\n  animation: ", " ", ";\n  ", ";\n"])), MoveFromMid, animtionOptions, smallLabelFont);
var smallLabelStyles = css(templateObject_8$h || (templateObject_8$h = __makeTemplateObject(["\n  ", ";\n  ", ";\n"], ["\n  ", ";\n  ", ";\n"])), smallLabelTopValue, smallLabelFont);
var createLabelStyles = function (_a) {
    var isInputFocused = _a.isInputFocused, hasInputValue = _a.hasInputValue;
    if (hasInputValue)
        return smallLabelStyles;
    if (isInputFocused)
        return midLabelStylesWithAnimation;
    return smallLabelStylesWithAnimation;
};
var PlaceholderLabel$1 = styled.label(templateObject_9$e || (templateObject_9$e = __makeTemplateObject(["\n  transform: none;\n  transform-origin: bottom;\n  position: absolute;\n  left: 17px;\n  right: ", ";\n  opacity: 1;\n  transition: font-size 0.8s;\n  pointer-events: none;\n  color: ", ";\n  text-overflow: ellipsis;\n  overflow: hidden;\n  white-space: ", ";\n\n  ", "\n"], ["\n  transform: none;\n  transform-origin: bottom;\n  position: absolute;\n  left: 17px;\n  right: ", ";\n  opacity: 1;\n  transition: font-size 0.8s;\n  pointer-events: none;\n  color: ", ";\n  text-overflow: ellipsis;\n  overflow: hidden;\n  white-space: ", ";\n\n  ", "\n"])), function (_a) {
    var hasError = _a.hasError;
    return (hasError ? '38px' : ' 17px');
}, function (_a) {
    var theme = _a.theme;
    return theme.colors.input.placeholder;
}, function (_a) {
    var noWrap = _a.noWrap;
    return (noWrap ? 'nowrap' : ' wrap');
}, function (props) { return createLabelStyles(props); });
var templateObject_1$1b, templateObject_2$11, templateObject_3$S, templateObject_4$I, templateObject_5$x, templateObject_6$s, templateObject_7$o, templateObject_8$h, templateObject_9$e;

var PlaceholderLabel = function (_a) {
    var htmlFor = _a.htmlFor, children = _a.children, isFocused = _a.isFocused, hasValue = _a.hasValue, hasError = _a.hasError, _b = _a.noWrap, noWrap = _b === void 0 ? true : _b;
    return (jsx(PlaceholderLabel$1, __assign$1({ htmlFor: htmlFor, isInputFocused: isFocused, hasInputValue: hasValue, hasError: hasError, noWrap: noWrap }, { children: children })));
};

var Input$2 = styled.input(templateObject_1$1a || (templateObject_1$1a = __makeTemplateObject(["\n  border: 1px solid ", ";\n  border-radius: 10px;\n  font-size: ", ";\n  background-color: ", ";\n  display: block;\n  padding: ", ";\n  height: 60px;\n  width: 100%;\n  transition: all 0.5s ease;\n  outline-style: solid;\n  outline-offset: -1px;\n  outline-width: 1px;\n  outline-color: transparent;\n  :focus {\n    outline-offset: -1px;\n    outline-width: 1px;\n    outline-color: ", ";\n  }\n"], ["\n  border: 1px solid ", ";\n  border-radius: 10px;\n  font-size: ", ";\n  background-color: ", ";\n  display: block;\n  padding: ", ";\n  height: 60px;\n  width: 100%;\n  transition: all 0.5s ease;\n  outline-style: solid;\n  outline-offset: -1px;\n  outline-width: 1px;\n  outline-color: transparent;\n  :focus {\n    outline-offset: -1px;\n    outline-width: 1px;\n    outline-color: ", ";\n  }\n"])), function (_a) {
    var theme = _a.theme;
    return theme.colors.input.borderColor;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.size.desktop.xs;
}, function (_a) {
    var theme = _a.theme;
    return theme.colors.input.backgroundColor;
}, function (_a) {
    var hasError = _a.hasError;
    return (hasError ? '28px 52px 18px 17px' : '28px 17px 18px 17px');
}, function (_a) {
    var theme = _a.theme;
    return theme.colors.input.focus.outlineColor;
});
var Wrapper$5 = styled.div(templateObject_4$H || (templateObject_4$H = __makeTemplateObject(["\n  position: relative;\n\n  ", ";\n\n  ", "\n"], ["\n  position: relative;\n\n  ", ";\n\n  ", "\n"])), function (props) {
    return props.prefix && css(templateObject_2$10 || (templateObject_2$10 = __makeTemplateObject(["\n      :before {\n        content: '", "';\n        position: absolute;\n        top: 20px;\n        left: 10px;\n      }\n\n      label {\n        left: ", "px;\n      }\n\n      input {\n        padding-left: ", "px;\n      }\n    "], ["\n      :before {\n        content: '", "';\n        position: absolute;\n        top: 20px;\n        left: 10px;\n      }\n\n      label {\n        left: ", "px;\n      }\n\n      input {\n        padding-left: ", "px;\n      }\n    "])), props.prefix, props.prefix.length * 9 + 21, props.prefix.length * 9 + 21);
}, function (props) {
    return props.prefix &&
        (props.hasValue || props.isFocused) && css(templateObject_3$R || (templateObject_3$R = __makeTemplateObject(["\n      :before {\n        top: 26px;\n      }\n    "], ["\n      :before {\n        top: 26px;\n      }\n    "])));
});
var SearchContainer = styled.div(templateObject_5$w || (templateObject_5$w = __makeTemplateObject(["\n  position: relative;\n  ", ";\n\n  input {\n    padding: 17px 70px 17px 16px;\n  }\n\n  label {\n    left: 50px;\n  }\n"], ["\n  position: relative;\n  ", ";\n\n  input {\n    padding: 17px 70px 17px 16px;\n  }\n\n  label {\n    left: 50px;\n  }\n"])), function (_a) {
    var theme = _a.theme;
    return "\n    ".concat(theme.breakpoints.lg, " {\n      max-width: 544px;\n    }\n  ");
});
var SeachInputIcon = styled(Icon$4)(templateObject_6$r || (templateObject_6$r = __makeTemplateObject(["\n  position: absolute;\n  top: 18px;\n  width: 18px;\n  height: 18px;\n"], ["\n  position: absolute;\n  top: 18px;\n  width: 18px;\n  height: 18px;\n"])));
var SearchIcon = styled(SeachInputIcon).attrs({ name: 'ButtonSearchOff' })(templateObject_7$n || (templateObject_7$n = __makeTemplateObject(["\n  right: 17px;\n  circle {\n    fill: ", ";\n  }\n"], ["\n  right: 17px;\n  circle {\n    fill: ", ";\n  }\n"])), function (_a) {
    var theme = _a.theme, hasValue = _a.hasValue;
    return hasValue ? theme.colors.input.searchIconOn : theme.colors.input.searchIconOff;
});
var ClearButton = styled.button.attrs({
    role: 'button',
})(templateObject_8$g || (templateObject_8$g = __makeTemplateObject(["\n  position: absolute;\n  right: 37px;\n  top: 16px;\n  width: auto;\n  border: none;\n  background-color: transparent;\n  cursor: pointer;\n"], ["\n  position: absolute;\n  right: 37px;\n  top: 16px;\n  width: auto;\n  border: none;\n  background-color: transparent;\n  cursor: pointer;\n"])));
var VisuallyHiddenLabel = styled.label(templateObject_9$d || (templateObject_9$d = __makeTemplateObject(["\n  display: inline-block;\n  position: absolute;\n  overflow: hidden;\n  clip: rect(0 0 0 0);\n  height: 1;\n  width: 1;\n  margin: -1;\n  padding: 0;\n  border: 0;\n"], ["\n  display: inline-block;\n  position: absolute;\n  overflow: hidden;\n  clip: rect(0 0 0 0);\n  height: 1;\n  width: 1;\n  margin: -1;\n  padding: 0;\n  border: 0;\n"])));
styled.div(templateObject_10$9 || (templateObject_10$9 = __makeTemplateObject(["\n  ", ";\n  background-color: ", ";\n"], ["\n  ", ";\n  background-color: ", ";\n"])), function (_a) {
    var theme = _a.theme;
    return "\n    padding: 10px;\n    ".concat(theme.breakpoints.md, " {\n      padding: 40px;\n    }\n   ");
}, function (_a) {
    var theme = _a.theme;
    return theme.crediteaColors.accent.flamingo[500];
});
var templateObject_1$1a, templateObject_2$10, templateObject_3$R, templateObject_4$H, templateObject_5$w, templateObject_6$r, templateObject_7$n, templateObject_8$g, templateObject_9$d, templateObject_10$9;

var useInputState = function (_a) {
    var _b;
    var onBlur = _a.onBlur, onFocus = _a.onFocus;
    var inputRef = useRef(null);
    var _c = useState(false), isFocused = _c[0], setIsFocused = _c[1];
    var _d = useState(false), hasValue = _d[0], setHasValue = _d[1];
    var onInputFocus = function (event) {
        if (onFocus) {
            onFocus(event);
        }
        setIsFocused(true);
    };
    var onInputBlur = function (event) {
        if (onBlur) {
            onBlur(event);
        }
        setIsFocused(false);
    };
    useEffect(function () {
        var _a;
        setHasValue(!!((_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.value));
    }, [(_b = inputRef.current) === null || _b === void 0 ? void 0 : _b.value]);
    return { inputRef: inputRef, isFocused: isFocused, hasValue: hasValue, setHasValue: setHasValue, onInputBlur: onInputBlur, onInputFocus: onInputFocus };
};

var Input = function (_a, ref) {
    var _b = _a.type, type = _b === void 0 ? 'text' : _b, placeholder = _a.placeholder, required = _a.required, name = _a.name, _c = _a.id, id = _c === void 0 ? name : _c, prefix = _a.prefix, onBlur = _a.onBlur, onFocus = _a.onFocus; _a.isInvalid; var hasError = _a.hasError, rest = __rest$1(_a, ["type", "placeholder", "required", "name", "id", "prefix", "onBlur", "onFocus", "isInvalid", "hasError"]);
    var _d = useInputState({
        onBlur: onBlur,
        onFocus: onFocus,
    }), hasValue = _d.hasValue, isFocused = _d.isFocused, inputRef = _d.inputRef, onInputBlur = _d.onInputBlur, onInputFocus = _d.onInputFocus;
    var placeholderText = required ? "".concat(placeholder, "*") : placeholder;
    useImperativeHandle(ref, function () { return inputRef.current; });
    return (jsxs(Wrapper$5, __assign$1({ prefix: prefix, hasValue: hasValue, isFocused: isFocused }, { children: [placeholderText && (jsx(PlaceholderLabel, __assign$1({ htmlFor: id, isFocused: isFocused, hasValue: hasValue, hasError: hasError }, { children: placeholderText }))), jsx(Input$2, __assign$1({ onKeyPress: function (e) { return type === 'number' && !/[0-9+.]/.test(e.key) && e.preventDefault(); }, required: required, ref: inputRef, onFocus: onInputFocus, onBlur: onInputBlur, name: name, id: id, type: type, hasError: hasError }, rest))] })));
};
var Input$1 = forwardRef(Input);

var crediteaGlobalStyle = "\n*, *::before, *::after {\n  box-sizing: border-box;\n  -webkit-font-smoothing: antialiased;\n  -moz-osx-font-smoothing: grayscale;\n}\n\nhtml {\n  font-size: 62.5%; \n  scroll-behavior: smooth;\n}\n\nbody {\n  margin: 0;\n  font-size: 1.6rem;\n  font-family: Rubik, sans-serif;\n  padding: env(safe-area-inset-top) env(safe-area-inset-right) env(safe-area-inset-bottom) env(safe-area-inset-left);\n  background-color: #FFFFFF;\n}\n\nh1, h2, h3, h4, h5, h6, p, a {\n  margin: 0;\n}\n\ninput, textarea, button {\n  font-family: inherit;\n}\n\n* {\noutline-offset: 6px;\n}\n\n/* Chrome, Safari, Edge, Opera */\ninput::-webkit-outer-spin-button,\ninput::-webkit-inner-spin-button {\n  /* stylelint-disable property-no-vendor-prefix */\n  -webkit-appearance: none;\n  margin: 0;\n}\n\n/* Firefox */\ninput[type='number'] {\n  -moz-appearance: textfield;\n}\n\na, a:link, a:active, a:visited, a:hover {\n  text-decoration: none;\n}\n";

var SearchInput = function (_a) {
    var _b = _a.placeholder, placeholder = _b === void 0 ? 'Search' : _b, onBlur = _a.onBlur, onFocus = _a.onFocus, onChange = _a.onChange, value = _a.value;
    var _c = useInputState({
        onBlur: onBlur,
        onFocus: onFocus,
    }), hasValue = _c.hasValue, inputRef = _c.inputRef, setHasValue = _c.setHasValue, onInputBlur = _c.onInputBlur, onInputFocus = _c.onInputFocus;
    var handleClearInput = function () {
        if (inputRef.current) {
            onChange('');
            inputRef.current.value = '';
            setHasValue(false);
        }
    };
    useEffect(function () {
        if (inputRef.current) {
            inputRef.current.focus();
        }
        var keyDownHandler = function (event) {
            if (event.key === 'Escape') {
                event.preventDefault();
                handleClearInput();
            }
        };
        document.addEventListener('keydown', keyDownHandler);
        return function () {
            document.removeEventListener('keydown', keyDownHandler);
        };
    }, []);
    return (jsxs(SearchContainer, { children: [jsx(VisuallyHiddenLabel, __assign$1({ htmlFor: "search" }, { children: placeholder })), jsx(SearchIcon, { hasValue: hasValue }), jsx(Input$2, { ref: inputRef, onFocus: onInputFocus, onBlur: onInputBlur, placeholder: placeholder, id: "search", type: "text", onChange: function (e) {
                    onChange(e.target.value.replace(/[~`!@#$%^&*()+={}[\];:'"<>.,/\\?-_]/, ''));
                    setHasValue(true);
                }, value: value }), hasValue && (jsx(ClearButton, __assign$1({ onClick: handleClearInput }, { children: jsx(Icon$4, { height: 26, width: 26, name: "ButtonClose", fillColor: theme.colors.input.closeButton }) })))] }));
};

function isSlugPage(internalLink) {
    return Boolean(internalLink && typeof internalLink === 'object' && 'slug' in internalLink);
}

var LinkWrapper$7 = styled.div(templateObject_4$G || (templateObject_4$G = __makeTemplateObject(["\n  width: fit-content;\n  min-width: ", ";\n  cursor: pointer;\n\n  & > * {\n    display: block;\n    border-radius: 10px;\n    text-align: center;\n    font-size: ", ";\n    font-weight: ", ";\n    line-height: ", ";\n    padding: ", ";\n\n    ", "\n    ", "\n      ", "\n  }\n"], ["\n  width: fit-content;\n  min-width: ", ";\n  cursor: pointer;\n\n  & > * {\n    display: block;\n    border-radius: 10px;\n    text-align: center;\n    font-size: ", ";\n    font-weight: ", ";\n    line-height: ", ";\n    padding: ", ";\n\n    ", "\n    ", "\n      ", "\n  }\n"])), function (_a) {
    var width = _a.width;
    return (width ? "".concat(width, "px") : "155px");
}, theme.fonts.size.desktop.xs, theme.fonts.weight.medium500, function (props) { return props.theme.fonts.lineHeight.desktop.xs; }, function (_a) {
    var small = _a.small;
    return "".concat(theme.space[small ? 10 : 16], " ").concat(theme.space[16]);
}, function (_a) {
    var variant = _a.variant, colors = _a.theme.colors, mode = _a.mode;
    return variant === 'primary' && css(templateObject_1$19 || (templateObject_1$19 = __makeTemplateObject(["\n        background-color: ", ";\n        color: ", " !important;\n        &:hover {\n          background-color: ", ";\n          color: ", " !important;\n        }\n        &:disabled {\n          background-color: ", ";\n        }\n      "], ["\n        background-color: ", ";\n        color: ", " !important;\n        &:hover {\n          background-color: ", ";\n          color: ", " !important;\n        }\n        &:disabled {\n          background-color: ", ";\n        }\n      "])), colors.button.primary[mode].backgroundColor, colors.button.primary[mode].color, colors.button.primary[mode].hover.backgroundColor, colors.button.primary[mode].hover.color, colors.button.primary[mode].disabled.backgroundColor);
}, function (_a) {
    var colors = _a.theme.colors, variant = _a.variant, mode = _a.mode;
    return variant === 'secondary' && css(templateObject_2$$ || (templateObject_2$$ = __makeTemplateObject(["\n        background-color: transparent;\n        border: 2px solid ", ";\n        color: ", " !important;\n        &:hover {\n          background-color: ", ";\n          color: ", " !important;\n        }\n        &:disabled {\n          border: 2px solid ", ";\n          color: ", " !important;\n        }\n      "], ["\n        background-color: transparent;\n        border: 2px solid ", ";\n        color: ", " !important;\n        &:hover {\n          background-color: ", ";\n          color: ", " !important;\n        }\n        &:disabled {\n          border: 2px solid ", ";\n          color: ", " !important;\n        }\n      "])), colors.button.secondary[mode].borderColor, colors.button.secondary[mode].color, colors.button.secondary[mode].hover.backgroundColor, colors.button.secondary[mode].hover.color, colors.button.secondary[mode].disabled.borderColor, colors.button.secondary[mode].disabled.color);
}, function (_a) {
    var colors = _a.theme.colors, mode = _a.mode, variant = _a.variant;
    return variant === 'text' && css(templateObject_3$Q || (templateObject_3$Q = __makeTemplateObject(["\n        background-color: transparent;\n        color: ", ";\n        path {\n          fill: ", ";\n          transition: fill 0.4s;\n        }\n        &:hover {\n          color: ", " !important;\n          path {\n            fill: ", ";\n          }\n        }\n        &:disabled {\n          color: ", " !important;\n          path {\n            fill: ", ";\n          }\n        }\n      "], ["\n        background-color: transparent;\n        color: ", ";\n        path {\n          fill: ", ";\n          transition: fill 0.4s;\n        }\n        &:hover {\n          color: ", " !important;\n          path {\n            fill: ", ";\n          }\n        }\n        &:disabled {\n          color: ", " !important;\n          path {\n            fill: ", ";\n          }\n        }\n      "])), colors.button.text[mode].color, colors.button.text[mode].color, colors.button.text[mode].hover.color, colors.button.text[mode].hover.color, colors.button.text[mode].disabled.color, colors.button.text[mode].disabled.color);
});
var templateObject_1$19, templateObject_2$$, templateObject_3$Q, templateObject_4$G;

var LinkAsButton = function (_a) {
    var _b, _c;
    var NextLink = _a.NextLink, linkData = _a.linkData, _d = _a.small, small = _d === void 0 ? false : _d, _e = _a.mode, mode = _e === void 0 ? 'dark' : _e, _f = _a.variant, variant = _f === void 0 ? 'primary' : _f, width = _a.width, id = _a.id;
    var anchorTag = (linkData === null || linkData === void 0 ? void 0 : linkData.anchorID) ? "#".concat(linkData.anchorID) : '';
    if (NextLink && ((_b = linkData === null || linkData === void 0 ? void 0 : linkData.internalLink) === null || _b === void 0 ? void 0 : _b.__typename) === 'HomePage') {
        return (jsx(LinkWrapper$7, __assign$1({}, { mode: mode, small: small, variant: variant, width: width }, { children: jsx(NextLink, __assign$1({ id: id, href: "/".concat(anchorTag), passHref: true }, { children: linkData.linkTitle })) })));
    }
    if (NextLink && (linkData === null || linkData === void 0 ? void 0 : linkData.internalLink) && isSlugPage(linkData === null || linkData === void 0 ? void 0 : linkData.internalLink)) {
        return (jsx(LinkWrapper$7, __assign$1({}, { mode: mode, small: small, variant: variant, width: width }, { children: jsx(NextLink, __assign$1({ id: id, href: "/".concat((_c = linkData.internalLink.slug) === null || _c === void 0 ? void 0 : _c.current).concat(anchorTag) }, { children: linkData.linkTitle })) })));
    }
    if (linkData === null || linkData === void 0 ? void 0 : linkData.externalUrl) {
        return (jsx(LinkWrapper$7, __assign$1({}, { mode: mode, small: small, variant: variant, width: width }, { children: jsx("a", __assign$1({ id: id, href: linkData.externalUrl, target: linkData.anchorTarget ? '_self' : '_blank ', rel: "noreferrer" }, { children: linkData.linkTitle })) })));
    }
    return null;
};

var LinkWithChildren = function (_a) {
    var _b, _c;
    var NextLink = _a.NextLink, linkData = _a.linkData, children = _a.children;
    var anchorTag = (linkData === null || linkData === void 0 ? void 0 : linkData.anchorID) ? "#".concat(linkData.anchorID) : '';
    if (NextLink && ((_b = linkData === null || linkData === void 0 ? void 0 : linkData.internalLink) === null || _b === void 0 ? void 0 : _b.__typename) === 'HomePage') {
        return (jsx(NextLink, __assign$1({ href: "/".concat(anchorTag), passHref: true }, { children: children })));
    }
    if (NextLink && (linkData === null || linkData === void 0 ? void 0 : linkData.internalLink) && isSlugPage(linkData === null || linkData === void 0 ? void 0 : linkData.internalLink)) {
        return (jsx(NextLink, __assign$1({ href: "/".concat((_c = linkData.internalLink.slug) === null || _c === void 0 ? void 0 : _c.current).concat(anchorTag) }, { children: children })));
    }
    if (linkData === null || linkData === void 0 ? void 0 : linkData.externalUrl) {
        return (jsx("a", __assign$1({ href: linkData.externalUrl, target: linkData.anchorTarget ? '_self' : '_blank ', rel: "noreferrer" }, { children: children })));
    }
    return null;
};

var speed = 1.7;
var Loader$1 = styled.div(templateObject_2$_ || (templateObject_2$_ = __makeTemplateObject(["\n  ", "\n"], ["\n  ", "\n"])), function (_a) {
    var radius = _a.radius, size = _a.size, thickness = _a.thickness, theme = _a.theme;
    return css(templateObject_1$18 || (templateObject_1$18 = __makeTemplateObject(["\n    svg {\n      position: relative;\n      height: ", "px;\n      width: ", "px;\n      animation: spin ", "s linear infinite;\n\n      circle {\n        fill: none;\n        stroke: ", ";\n        stroke-linecap: round;\n        stroke-width: ", "px;\n        animation: spring ", "s ease-in-out infinite;\n      }\n    }\n\n    @keyframes spin {\n      0% {\n        transform: rotate(-90deg);\n      }\n      100% {\n        transform: rotate(270deg);\n      }\n    }\n\n    @keyframes spring {\n      0% {\n        stroke-dasharray: 0 1000;\n        stroke-dashoffset: 0;\n      }\n      50% {\n        stroke: ", ";\n        stroke-dasharray: ", " 1000;\n        stroke-dashoffset: ", ";\n      }\n      100% {\n        stroke-dasharray: 0 1000;\n        stroke-dashoffset: ", ";\n      }\n    }\n  "], ["\n    svg {\n      position: relative;\n      height: ", "px;\n      width: ", "px;\n      animation: spin ", "s linear infinite;\n\n      circle {\n        fill: none;\n        stroke: ", ";\n        stroke-linecap: round;\n        stroke-width: ", "px;\n        animation: spring ", "s ease-in-out infinite;\n      }\n    }\n\n    @keyframes spin {\n      0% {\n        transform: rotate(-90deg);\n      }\n      100% {\n        transform: rotate(270deg);\n      }\n    }\n\n    @keyframes spring {\n      0% {\n        stroke-dasharray: 0 1000;\n        stroke-dashoffset: 0;\n      }\n      50% {\n        stroke: ", ";\n        stroke-dasharray: ", " 1000;\n        stroke-dashoffset: ", ";\n      }\n      100% {\n        stroke-dasharray: 0 1000;\n        stroke-dashoffset: ", ";\n      }\n    }\n  "])), size, size, speed, theme.colors.loader.stroke, thickness, speed, theme.colors.loader.strokeAnimated, Math.PI * radius, -(Math.PI / 2) * radius + thickness, -(Math.PI * 2) * radius + thickness);
});
var templateObject_1$18, templateObject_2$_;

var Loader = function (_a) {
    var _b = _a.size, size = _b === void 0 ? 80 : _b;
    var thickness = size / 10;
    var radius = (size - thickness) / 2;
    return (jsx(Loader$1, __assign$1({ radius: radius, size: size, thickness: thickness, role: "progressbar", "aria-busy": true }, { children: jsx("svg", __assign$1({ viewBox: "".concat(size / 2, " ").concat(size / 2, " ").concat(size, " ").concat(size) }, { children: jsx("circle", { cx: size, cy: size, r: radius }) })) })));
};

var fadeIn$4 = keyframes(templateObject_1$17 || (templateObject_1$17 = __makeTemplateObject(["\n  from {\n    opacity: 0;\n  }\n\n  to {\n    opacity: 1;\n  }\n"], ["\n  from {\n    opacity: 0;\n  }\n\n  to {\n    opacity: 1;\n  }\n"])));
var MenuDropDownWrapper = styled.div(templateObject_2$Z || (templateObject_2$Z = __makeTemplateObject(["\n  position: absolute;\n  box-shadow: none;\n  border-radius: 8px;\n  background-color: ", ";\n  margin-top: 20px;\n  ", "\n  visibility: ", ";\n  animation: ", " 0.5s;\n\n  &:before {\n    content: '';\n    display: block;\n    position: absolute;\n    left: 50%;\n    z-index: -1;\n    top: 0;\n    background-color: ", ";\n    width: 20px;\n    height: 20px;\n    transform: rotate(45deg) translate(-50%, 0);\n  }\n"], ["\n  position: absolute;\n  box-shadow: none;\n  border-radius: 8px;\n  background-color: ", ";\n  margin-top: 20px;\n  ", "\n  visibility: ", ";\n  animation: ", " 0.5s;\n\n  &:before {\n    content: '';\n    display: block;\n    position: absolute;\n    left: 50%;\n    z-index: -1;\n    top: 0;\n    background-color: ", ";\n    width: 20px;\n    height: 20px;\n    transform: rotate(45deg) translate(-50%, 0);\n  }\n"])), function (_a) {
    var theme = _a.theme;
    return theme.colors.menuDropdown.backgroundColor;
}, function (_a) {
    var rect = _a.rect, width = _a.width, togglerWidth = _a.togglerWidth, theme = _a.theme;
    if (rect &&
        document.body.clientWidth - rect.right <= 5 &&
        rect.left <= parseFloat(theme.flexboxGrid.container.xl) + theme.flexboxGrid.gutterSize.xl / 2) {
        return 'right: 5px;';
    }
    if (togglerWidth > 0) {
        return "margin-left: ".concat((togglerWidth - width) / 2, "px;");
    }
    return "margin-left: ".concat(rect && rect.left <= 5 ? '5px' : width * -0.33, "px;");
}, function (props) { return (props.visible ? 'visible' : 'hidden'); }, function (props) { return (props.visible ? fadeIn$4 : ''); }, function (_a) {
    var theme = _a.theme;
    return theme.colors.menuDropdown.backgroundColor;
});
var templateObject_1$17, templateObject_2$Z;

var DropDown = function (_a) {
    var _b;
    var children = _a.children, open = _a.open, handleClose = _a.handleClose, togglerRef = _a.togglerRef, _c = _a.autoClose, autoClose = _c === void 0 ? 'outside' : _c;
    var myRef = useRef(null);
    var _d = useState(0), width = _d[0], setWidth = _d[1];
    var _e = useState(0), togglerWidth = _e[0], setTogglerWidth = _e[1];
    var rect = (_b = myRef.current) === null || _b === void 0 ? void 0 : _b.getBoundingClientRect();
    var handleClickOutside = function (event) {
        var _a;
        if (!((_a = myRef.current) === null || _a === void 0 ? void 0 : _a.contains(event.target))) {
            if (togglerRef &&
                togglerRef.current &&
                !togglerRef.current.contains(event.target)) {
                handleClose(event);
            }
        }
    };
    var handleClickInside = function (event) {
        var _a;
        if ((_a = myRef.current) === null || _a === void 0 ? void 0 : _a.contains(event.target)) {
            handleClose(event);
        }
    };
    useEffect(function () {
        if (myRef.current) {
            setWidth(myRef.current.clientWidth);
        }
        if (autoClose && autoClose !== 'inside') {
            document.addEventListener('click', handleClickOutside, true);
        }
        if (autoClose && autoClose !== 'outside') {
            document.addEventListener('click', handleClickInside, true);
        }
        return function () {
            document.removeEventListener('click', handleClickOutside, true);
            document.removeEventListener('click', handleClickInside, true);
        };
    }, [autoClose, myRef]);
    useEffect(function () {
        if (togglerRef === null || togglerRef === void 0 ? void 0 : togglerRef.current) {
            setTogglerWidth(togglerRef === null || togglerRef === void 0 ? void 0 : togglerRef.current.clientWidth);
        }
    }, [togglerRef]);
    return (jsx(MenuDropDownWrapper, __assign$1({ ref: myRef, rect: rect, width: width, togglerWidth: togglerWidth, visible: open }, { children: children })));
};

var snackbarBgColorMap = {
    warning: theme.colors.snackbar.warning,
    success: theme.colors.snackbar.success,
};
var snackbarIconMap = {
    warning: 'WarningTriangle',
    success: 'CheckField',
};
var fadeIn$3 = keyframes(templateObject_1$16 || (templateObject_1$16 = __makeTemplateObject(["\n  from {\n    opacity: 0;\n  }\n\n  to {\n    opacity: 1;\n  }\n"], ["\n  from {\n    opacity: 0;\n  }\n\n  to {\n    opacity: 1;\n  }\n"])));
var SnackbarWrapper = styled.div(templateObject_2$Y || (templateObject_2$Y = __makeTemplateObject(["\n  bottom: 60px;\n  display: flex;\n  align-items: start;\n  justify-content: space-between;\n  background-color: ", ";\n  padding: 16px;\n  border-radius: 16px;\n  z-index: ", ";\n  width: 100%;\n  scroll-margin: 136px;\n\n  visibility: ", ";\n  animation: ", " 0.5s;\n\n  & > div[role='img'] {\n    display: grid;\n    place-content: center;\n  }\n\n  ", " {\n    justify-content: flex-start;\n    max-width: 736px;\n  }\n"], ["\n  bottom: 60px;\n  display: flex;\n  align-items: start;\n  justify-content: space-between;\n  background-color: ", ";\n  padding: 16px;\n  border-radius: 16px;\n  z-index: ", ";\n  width: 100%;\n  scroll-margin: 136px;\n\n  visibility: ", ";\n  animation: ", " 0.5s;\n\n  & > div[role='img'] {\n    display: grid;\n    place-content: center;\n  }\n\n  ", " {\n    justify-content: flex-start;\n    max-width: 736px;\n  }\n"])), function (_a) {
    var variant = _a.variant;
    return snackbarBgColorMap[variant];
}, function (_a) {
    var theme = _a.theme;
    return theme.customZIndex.secondary;
}, function (props) { return (props.isOpen ? 'visible' : 'hidden'); }, function (props) { return (props.isOpen ? fadeIn$3 : ''); }, function (_a) {
    var theme = _a.theme;
    return theme.breakpoints.sm;
});
var CloseButton = styled(Button$2)(templateObject_3$P || (templateObject_3$P = __makeTemplateObject(["\n  flex-shrink: 0;\n  width: auto;\n  margin-top: 3px;\n"], ["\n  flex-shrink: 0;\n  width: auto;\n  margin-top: 3px;\n"])));
var TextWrapper$3 = styled.div(templateObject_4$F || (templateObject_4$F = __makeTemplateObject(["\n  width: 100%;\n"], ["\n  width: 100%;\n"])));
var templateObject_1$16, templateObject_2$Y, templateObject_3$P, templateObject_4$F;

var Snackbar = function (_a) {
    var isOpen = _a.isOpen, onClose = _a.onClose, text = _a.text, variant = _a.variant, dataTestId = _a.dataTestId;
    var ref = useRef(null);
    useEffect(function () {
        if (!(ref && 'current' in ref))
            return;
        if (ref.current) {
            // @ts-ignore behavior "instant" works fine but doesn't exist on types
            ref.current.scrollIntoView({ behavior: 'instant', block: 'start' });
        }
    }, [ref]);
    if (!text)
        return null;
    return (jsxs(SnackbarWrapper, __assign$1({ isOpen: isOpen, variant: variant, "data-testid": dataTestId, ref: ref }, { children: [jsx(Icon$4, { height: 24, width: 24, name: snackbarIconMap[variant] }), jsx(Gutter, { size: 16 }), jsx(TextWrapper$3, { children: jsx(Body3$2, { children: text }) }), onClose !== undefined && (jsx(CloseButton, __assign$1({ variant: "text", onClick: onClose, withoutSurroundingSpacing: true }, { children: jsx(Icon$4, { height: 26, width: 26, name: "ButtonClose", fillColor: "black" }) })))] })));
};

var SnackbarContainer = styled.div(templateObject_1$15 || (templateObject_1$15 = __makeTemplateObject(["\n  position: fixed;\n  left: 0;\n  right: 0;\n  bottom: 60px;\n"], ["\n  position: fixed;\n  left: 0;\n  right: 0;\n  bottom: 60px;\n"])));
var templateObject_1$15;

var SnackbarContext = createContext({
    openSnackbar: function () { },
    closeSnackbar: function () { },
});
var SnackbarProvider = function (_a) {
    var children = _a.children;
    var _b = useState(false), open = _b[0], setOpen = _b[1];
    var _c = useState(0), duration = _c[0], setDuration = _c[1];
    var _d = useState(''), text = _d[0], setText = _d[1];
    var triggerSnackbar = function (text, duration) {
        setText(text);
        setDuration(duration);
        setOpen(true);
    };
    var openSnackbar = function (text, duration) {
        if (duration === void 0) { duration = 0; }
        if (open === true) {
            setOpen(false);
            setTimeout(function () {
                triggerSnackbar(text, duration);
            }, 250);
        }
        else {
            triggerSnackbar(text, duration);
        }
    };
    var closeSnackbar = function () {
        setOpen(false);
    };
    var snackbarContextObject = useMemo(function () {
        return {
            openSnackbar: openSnackbar,
            closeSnackbar: closeSnackbar,
        };
    }, [openSnackbar, closeSnackbar]);
    useEffect(function () {
        if (duration !== 0 && open) {
            setTimeout(closeSnackbar, duration);
        }
    });
    return (jsxs(SnackbarContext.Provider, __assign$1({ value: snackbarContextObject }, { children: [children, jsx(SnackbarContainer, { children: jsx(Snackbar, { variant: "warning", text: text, isOpen: open, onClose: closeSnackbar }) })] })));
};

var PageWrapper$1 = styled.div(templateObject_1$14 || (templateObject_1$14 = __makeTemplateObject(["\n  position: absolute;\n  top: 0;\n  left: 0;\n  right: 0;\n  width: 100%;\n  height: auto;\n  min-height: 100vh;\n  padding-bottom: ", ";\n  ", "\n"], ["\n  position: absolute;\n  top: 0;\n  left: 0;\n  right: 0;\n  width: 100%;\n  height: auto;\n  min-height: 100vh;\n  padding-bottom: ", ";\n  ", "\n"])), function (_a) {
    var footerSpacing = _a.footerSpacing;
    return "".concat(footerSpacing, "px");
}, function (_a) {
    var theme = _a.theme, hasHeader = _a.hasHeader, hasPromoBar = _a.hasPromoBar;
    return hasHeader
        ? "\n      padding-top: ".concat(hasPromoBar ? '150px' : '74px', ";\n      ").concat(theme.breakpoints.sm, " {\n        padding-top: ").concat(hasPromoBar ? '124px' : '74px', ";\n      }\n      ")
        : "\n      padding-top: ".concat(hasPromoBar ? '76px' : '0', ";\n      ").concat(theme.breakpoints.sm, " {\n        padding-top: ").concat(hasPromoBar ? '50px' : '0', ";\n      }\n    ");
});
var templateObject_1$14;

var PageWrapperContext = createContext({});
var PageWrapper = function (_a) {
    var children = _a.children, footerRef = _a.footerRef, rest = __rest$1(_a, ["children", "footerRef"]);
    var _b = useState(0), footerSpacing = _b[0], setFooterSpacing = _b[1];
    var ref = useRef(null);
    var pageWrapperContextObject = useMemo(function () {
        return {
            pageWrapperRef: ref,
        };
    }, [ref]);
    if (footerRef) {
        var updateFooterSpacing_1 = function () {
            if (footerRef.current !== null) {
                setFooterSpacing(footerRef.current.clientHeight);
            }
            else {
                setFooterSpacing(0);
            }
        };
        useEffect(function () {
            updateFooterSpacing_1();
            window.addEventListener('resize', updateFooterSpacing_1);
            return function () { return window.removeEventListener('resize', updateFooterSpacing_1); };
        }, [footerRef]);
    }
    return (jsx(PageWrapperContext.Provider, __assign$1({ value: pageWrapperContextObject }, { children: jsx(SnackbarProvider, { children: jsx(PageWrapper$1, __assign$1({ ref: ref, footerSpacing: footerSpacing }, rest, { children: children })) }) })));
};

var Button$1 = styled.a(templateObject_1$13 || (templateObject_1$13 = __makeTemplateObject(["\n  display: flex;\n  justify-content: left;\n  align-items: center;\n  text-align: right;\n  padding: 12px 0;\n  ", " {\n    padding: 0;\n  }\n"], ["\n  display: flex;\n  justify-content: left;\n  align-items: center;\n  text-align: right;\n  padding: 12px 0;\n  ", " {\n    padding: 0;\n  }\n"])), theme.breakpoints.lg);
var Text$2 = styled(Caption3$1).attrs(function (_a) {
    var mode = _a.mode, color = _a.color;
    return ({
        color: color || theme.colors.phoneButton[mode].color,
    });
})(templateObject_2$X || (templateObject_2$X = __makeTemplateObject([""], [""])));
var CountryCode = styled.span(templateObject_3$O || (templateObject_3$O = __makeTemplateObject(["\n  ", " {\n    display: none;\n  }\n"], ["\n  ", " {\n    display: none;\n  }\n"])), theme.breakpoints.lg);
styled.div(templateObject_4$E || (templateObject_4$E = __makeTemplateObject(["\n  padding: 20px;\n  background-color: ", ";\n"], ["\n  padding: 20px;\n  background-color: ", ";\n"])), function (_a) {
    var mode = _a.mode, theme = _a.theme;
    return mode === 'dark'
        ? theme.crediteaColors.primary.neutral[50]
        : theme.crediteaColors.primary.brandBlue[600];
});
var templateObject_1$13, templateObject_2$X, templateObject_3$O, templateObject_4$E;

var PhoneButton = function (_a) {
    var buttonText = _a.buttonText, _b = _a.countryCode, countryCode = _b === void 0 ? '' : _b, phoneNumber = _a.phoneNumber, iconName = _a.iconName, mode = _a.mode, color = _a.color;
    var parsedPhoneNumber = phoneNumber === null || phoneNumber === void 0 ? void 0 : phoneNumber.replace(/\D/g, '');
    return (jsxs(Button$1, __assign$1({ href: "tel:".concat(countryCode).concat(parsedPhoneNumber) }, { children: [iconName && (jsxs(Fragment, { children: [jsx(Icon$4, { name: iconName, fillColor: theme.crediteaColors.primary.brandBlue[500] }), jsx(Gutter$1, { size: 10 })] })), jsxs("div", { children: [buttonText && (jsxs(Fragment, { children: [jsx(Text$2, __assign$1({ mode: mode }, { children: buttonText })), jsx(Gutter$1, { size: 4 })] })), jsxs(Text$2, __assign$1({ mode: mode, color: color }, { children: [countryCode && jsxs(CountryCode, { children: [countryCode, " "] }), phoneNumber] }))] })] })));
};

var PromoBar$1 = styled.div(templateObject_3$N || (templateObject_3$N = __makeTemplateObject(["\n  display: flex;\n  align-items: center;\n  justify-content: ", ";\n  width: 100%;\n  background-color: ", ";\n  padding: 16px 24px;\n  position: relative;\n\n  ", " {\n    justify-content: center;\n  }\n\n  ", " {\n    padding: 16px 32px;\n  }\n  ", " {\n    padding: 16px 160px;\n  }\n  ", "\n\n  span {\n    display: block;\n    text-align: ", ";\n    font-size: 14px;\n    line-height: 24px;\n    color: ", ";\n\n    ", " {\n      text-align: center;\n      font-size: 16px;\n      line-height: 26px;\n    }\n  }\n"], ["\n  display: flex;\n  align-items: center;\n  justify-content: ", ";\n  width: 100%;\n  background-color: ", ";\n  padding: 16px 24px;\n  position: relative;\n\n  ", " {\n    justify-content: center;\n  }\n\n  ", " {\n    padding: 16px 32px;\n  }\n  ", " {\n    padding: 16px 160px;\n  }\n  ", "\n\n  span {\n    display: block;\n    text-align: ", ";\n    font-size: 14px;\n    line-height: 24px;\n    color: ", ";\n\n    ", " {\n      text-align: center;\n      font-size: 16px;\n      line-height: 26px;\n    }\n  }\n"])), function (_a) {
    var promoBarAlignment = _a.promoBarAlignment;
    return promoBarAlignment === 'CENTER' ? 'center' : 'flex-start';
}, function (_a) {
    var theme = _a.theme, mode = _a.mode;
    return theme.colors.promoBar[mode].backgroundColor;
}, function (_a) {
    var theme = _a.theme;
    return theme.breakpoints.sm;
}, function (_a) {
    var theme = _a.theme;
    return theme.breakpoints.md;
}, function (_a) {
    var theme = _a.theme;
    return theme.breakpoints.lg;
}, function (props) {
    return props.promoBarOnlyMobile
        ? css(templateObject_1$12 || (templateObject_1$12 = __makeTemplateObject(["\n          visibility: visible;\n          ", " {\n            display: none;\n          }\n        "], ["\n          visibility: visible;\n          ", " {\n            display: none;\n          }\n        "])), function (_a) {
            var theme = _a.theme;
            return theme.breakpoints.sm;
        }) : css(templateObject_2$W || (templateObject_2$W = __makeTemplateObject(["\n          visibility: visible;\n        "], ["\n          visibility: visible;\n        "])));
}, function (_a) {
    var promoBarAlignment = _a.promoBarAlignment;
    return (promoBarAlignment === 'CENTER' ? 'center' : 'left');
}, function (_a) {
    var theme = _a.theme, mode = _a.mode;
    return theme.colors.promoBar[mode].color;
}, function (_a) {
    var theme = _a.theme;
    return theme.breakpoints.sm;
});
var Anchor = styled.a(templateObject_4$D || (templateObject_4$D = __makeTemplateObject(["\n  position: absolute;\n  width: 100%;\n  height: 100%;\n"], ["\n  position: absolute;\n  width: 100%;\n  height: 100%;\n"])));
var templateObject_1$12, templateObject_2$W, templateObject_3$N, templateObject_4$D;

var PromoBarComponent = function (_a) {
    var _b = _a.mode, mode = _b === void 0 ? 'dark' : _b, text = _a.text, _c = _a.promoBarAlignment, promoBarAlignment = _c === void 0 ? 'CENTER' : _c, promoBarOnlyMobile = _a.promoBarOnlyMobile, children = _a.children;
    return (jsxs(PromoBar$1, __assign$1({ mode: mode, promoBarAlignment: promoBarAlignment, promoBarOnlyMobile: promoBarOnlyMobile }, { children: [jsx(Link2, __assign$1({ "aria-hidden": true }, { children: text })), children] })));
};

var PromoBar = function (_a) {
    var _b, _c;
    var promoBarItem = _a.promoBarItem, NextLink = _a.NextLink, promoBarAlignment = _a.promoBarAlignment, promoBarOnlyMobile = _a.promoBarOnlyMobile;
    if (!promoBarItem)
        return null;
    var isRedirectionLink = promoBarItem.isRedirectionLink, externalUrl = promoBarItem.externalUrl, internalLink = promoBarItem.internalLink, mode = promoBarItem.mode, title = promoBarItem.title, anchorTarget = promoBarItem.anchorTarget, anchorID = promoBarItem.anchorID;
    var isRedirectingExternally = isRedirectionLink && externalUrl;
    var isRedirectingInternally = isRedirectionLink && internalLink && NextLink;
    var anchorTag = anchorID ? "#".concat(anchorID) : '';
    var titleWithDefault = title !== null && title !== void 0 ? title : '';
    var modeWithDefault = mode !== null && mode !== void 0 ? mode : 'light';
    if (isRedirectingExternally) {
        return (jsx(PromoBarComponent, __assign$1({ text: titleWithDefault, mode: modeWithDefault, promoBarAlignment: promoBarAlignment, promoBarOnlyMobile: Boolean(promoBarOnlyMobile) }, { children: jsx(Anchor, { "aria-label": "Promobar ".concat(titleWithDefault, " redirects to ").concat(externalUrl), href: externalUrl, target: anchorTarget ? '_self' : '_blank', rel: "noreferrer" }) })));
    }
    if (isRedirectingInternally) {
        var slug = internalLink.__typename === 'HomePage'
            ? "/".concat(anchorTag)
            : (_c = "/".concat((_b = internalLink.slug) === null || _b === void 0 ? void 0 : _b.current).concat(anchorTag)) !== null && _c !== void 0 ? _c : '';
        return (jsx(PromoBarComponent, __assign$1({ text: titleWithDefault, mode: modeWithDefault, promoBarAlignment: promoBarAlignment, promoBarOnlyMobile: Boolean(promoBarOnlyMobile) }, { children: jsx(NextLink, { href: slug, passHref: true, style: { position: 'absolute', width: '100%', height: '100%' }, "aria-label": "Promobar ".concat(titleWithDefault, " redirects to ").concat(slug) }) })));
    }
    return (jsx(PromoBarComponent, { text: titleWithDefault, mode: modeWithDefault, promoBarAlignment: promoBarAlignment, promoBarOnlyMobile: Boolean(promoBarOnlyMobile) }));
};

function _setPrototypeOf$5(o, p) {
  _setPrototypeOf$5 = Object.setPrototypeOf ? Object.setPrototypeOf.bind() : function _setPrototypeOf(o, p) {
    o.__proto__ = p;
    return o;
  };
  return _setPrototypeOf$5(o, p);
}

function _inheritsLoose(subClass, superClass) {
  subClass.prototype = Object.create(superClass.prototype);
  subClass.prototype.constructor = subClass;
  _setPrototypeOf$5(subClass, superClass);
}

function s(e){return e&&e.stopPropagation&&e.stopPropagation(),e&&e.preventDefault&&e.preventDefault(),!1}function n(e){return null==e?[]:Array.isArray(e)?e.slice():[e]}function i(e){return null!==e&&1===e.length?e[0]:e.slice()}function o(e){Object.keys(e).forEach((t=>{"undefined"!=typeof document&&document.addEventListener(t,e[t],!1);}));}function r(e,t){return a(function(e,t){let s=e;s<=t.min&&(s=t.min);s>=t.max&&(s=t.max);return s}(e,t),t)}function a(e,t){const s=(e-t.min)%t.step;let n=e-s;return 2*Math.abs(s)>=t.step&&(n+=s>0?t.step:-t.step),parseFloat(n.toFixed(5))}let p=function(p){function u(e){var a;(a=p.call(this,e)||this).onKeyUp=()=>{a.onEnd();},a.onMouseUp=()=>{a.onEnd(a.getMouseEventMap());},a.onTouchEnd=()=>{a.onEnd(a.getTouchEventMap());},a.onBlur=()=>{a.setState({index:-1},a.onEnd(a.getKeyDownEventMap()));},a.onMouseMove=e=>{a.setState({pending:!0});const t=a.getMousePosition(e),s=a.getDiffPosition(t[0]),n=a.getValueFromPosition(s);a.move(n);},a.onTouchMove=e=>{if(e.touches.length>1)return;a.setState({pending:!0});const t=a.getTouchPosition(e);if(void 0===a.isScrolling){const e=t[0]-a.startPosition[0],s=t[1]-a.startPosition[1];a.isScrolling=Math.abs(s)>Math.abs(e);}if(a.isScrolling)return void a.setState({index:-1});const s=a.getDiffPosition(t[0]),n=a.getValueFromPosition(s);a.move(n);},a.onKeyDown=e=>{if(!(e.ctrlKey||e.shiftKey||e.altKey||e.metaKey))switch(a.setState({pending:!0}),e.key){case"ArrowLeft":case"ArrowDown":case"Left":case"Down":e.preventDefault(),a.moveDownByStep();break;case"ArrowRight":case"ArrowUp":case"Right":case"Up":e.preventDefault(),a.moveUpByStep();break;case"Home":e.preventDefault(),a.move(a.props.min);break;case"End":e.preventDefault(),a.move(a.props.max);break;case"PageDown":e.preventDefault(),a.moveDownByStep(a.props.pageFn(a.props.step));break;case"PageUp":e.preventDefault(),a.moveUpByStep(a.props.pageFn(a.props.step));}},a.onSliderMouseDown=e=>{if(!a.props.disabled&&2!==e.button){if(a.setState({pending:!0}),!a.props.snapDragDisabled){const t=a.getMousePosition(e);a.forceValueFromPosition(t[0],(e=>{a.start(e,t[0]),o(a.getMouseEventMap());}));}s(e);}},a.onSliderClick=e=>{if(!a.props.disabled&&a.props.onSliderClick&&!a.hasMoved){const t=a.getMousePosition(e),s=r(a.calcValue(a.calcOffsetFromPosition(t[0])),a.props);a.props.onSliderClick(s);}},a.createOnKeyDown=e=>t=>{a.props.disabled||(a.start(e),o(a.getKeyDownEventMap()),s(t));},a.createOnMouseDown=e=>t=>{if(a.props.disabled||2===t.button)return;a.setState({pending:!0});const n=a.getMousePosition(t);a.start(e,n[0]),o(a.getMouseEventMap()),s(t);},a.createOnTouchStart=e=>t=>{if(a.props.disabled||t.touches.length>1)return;a.setState({pending:!0});const s=a.getTouchPosition(t);a.startPosition=s,a.isScrolling=void 0,a.start(e,s[0]),o(a.getTouchEventMap()),function(e){e.stopPropagation&&e.stopPropagation();}(t);},a.handleResize=()=>{const e=window.setTimeout((()=>{a.pendingResizeTimeouts.shift(),a.resize();}),0);a.pendingResizeTimeouts.push(e);},a.renderThumb=(e,t)=>{const s=a.props.thumbClassName+" "+a.props.thumbClassName+"-"+t+" "+(a.state.index===t?a.props.thumbActiveClassName:""),n={ref:e=>{a["thumb"+t]=e;},key:a.props.thumbClassName+"-"+t,className:s,style:e,onMouseDown:a.createOnMouseDown(t),onTouchStart:a.createOnTouchStart(t),onFocus:a.createOnKeyDown(t),tabIndex:0,role:"slider","aria-orientation":a.props.orientation,"aria-valuenow":a.state.value[t],"aria-valuemin":a.props.min,"aria-valuemax":a.props.max,"aria-label":Array.isArray(a.props.ariaLabel)?a.props.ariaLabel[t]:a.props.ariaLabel,"aria-labelledby":Array.isArray(a.props.ariaLabelledby)?a.props.ariaLabelledby[t]:a.props.ariaLabelledby},o={index:t,value:i(a.state.value),valueNow:a.state.value[t]};return a.props.ariaValuetext&&(n["aria-valuetext"]="string"==typeof a.props.ariaValuetext?a.props.ariaValuetext:a.props.ariaValuetext(o)),a.props.renderThumb(n,o)},a.renderTrack=(e,t,s)=>{const n={key:a.props.trackClassName+"-"+e,className:a.props.trackClassName+" "+a.props.trackClassName+"-"+e,style:a.buildTrackStyle(t,a.state.upperBound-s)},o={index:e,value:i(a.state.value)};return a.props.renderTrack(n,o)};let u=n(e.value);u.length||(u=n(e.defaultValue)),a.pendingResizeTimeouts=[];const h=[];for(let t=0;t<u.length;t+=1)u[t]=r(u[t],e),h.push(t);return a.resizeObserver=null,a.resizeElementRef=React.createRef(),a.state={index:-1,upperBound:0,sliderLength:0,value:u,zIndices:h},a}_inheritsLoose(u,p);var h=u.prototype;return h.componentDidMount=function(){"undefined"!=typeof window&&(this.resizeObserver=new ResizeObserver(this.handleResize),this.resizeObserver.observe(this.resizeElementRef.current),this.resize());},u.getDerivedStateFromProps=function(e,t){const s=n(e.value);return s.length?t.pending?null:{value:s.map((t=>r(t,e)))}:null},h.componentDidUpdate=function(){0===this.state.upperBound&&this.resize();},h.componentWillUnmount=function(){this.clearPendingResizeTimeouts(),this.resizeObserver&&this.resizeObserver.disconnect();},h.onEnd=function(e){e&&function(e){Object.keys(e).forEach((t=>{"undefined"!=typeof document&&document.removeEventListener(t,e[t],!1);}));}(e),this.hasMoved&&this.fireChangeEvent("onAfterChange"),this.setState({pending:!1}),this.hasMoved=!1;},h.getValue=function(){return i(this.state.value)},h.getClosestIndex=function(e){let t=Number.MAX_VALUE,s=-1;const{value:n}=this.state,i=n.length;for(let o=0;o<i;o+=1){const i=this.calcOffset(n[o]),r=Math.abs(e-i);r<t&&(t=r,s=o);}return s},h.getMousePosition=function(e){return [e["page"+this.axisKey()],e["page"+this.orthogonalAxisKey()]]},h.getTouchPosition=function(e){const t=e.touches[0];return [t["page"+this.axisKey()],t["page"+this.orthogonalAxisKey()]]},h.getKeyDownEventMap=function(){return {keydown:this.onKeyDown,keyup:this.onKeyUp,focusout:this.onBlur}},h.getMouseEventMap=function(){return {mousemove:this.onMouseMove,mouseup:this.onMouseUp}},h.getTouchEventMap=function(){return {touchmove:this.onTouchMove,touchend:this.onTouchEnd}},h.getValueFromPosition=function(e){const t=e/(this.state.sliderLength-this.state.thumbSize)*(this.props.max-this.props.min);return r(this.state.startValue+t,this.props)},h.getDiffPosition=function(e){let t=e-this.state.startPosition;return this.props.invert&&(t*=-1),t},h.resize=function(){const{slider:e,thumb0:t}=this;if(!e||!t)return;const s=this.sizeKey(),n=e.getBoundingClientRect(),i=e[s],o=n[this.posMaxKey()],r=n[this.posMinKey()],a=t.getBoundingClientRect()[s.replace("client","").toLowerCase()],p=i-a,u=Math.abs(o-r);this.state.upperBound===p&&this.state.sliderLength===u&&this.state.thumbSize===a||this.setState({upperBound:p,sliderLength:u,thumbSize:a});},h.calcOffset=function(e){const t=this.props.max-this.props.min;if(0===t)return 0;return (e-this.props.min)/t*this.state.upperBound},h.calcValue=function(e){return e/this.state.upperBound*(this.props.max-this.props.min)+this.props.min},h.calcOffsetFromPosition=function(e){const{slider:t}=this,s=t.getBoundingClientRect(),n=s[this.posMaxKey()],i=s[this.posMinKey()];let o=e-(window["page"+this.axisKey()+"Offset"]+(this.props.invert?n:i));return this.props.invert&&(o=this.state.sliderLength-o),o-=this.state.thumbSize/2,o},h.forceValueFromPosition=function(e,t){const s=this.calcOffsetFromPosition(e),n=this.getClosestIndex(s),i=r(this.calcValue(s),this.props),o=this.state.value.slice();o[n]=i;for(let e=0;e<o.length-1;e+=1)if(o[e+1]-o[e]<this.props.minDistance)return;this.fireChangeEvent("onBeforeChange"),this.hasMoved=!0,this.setState({value:o},(()=>{t(n),this.fireChangeEvent("onChange");}));},h.clearPendingResizeTimeouts=function(){do{const e=this.pendingResizeTimeouts.shift();clearTimeout(e);}while(this.pendingResizeTimeouts.length)},h.start=function(e,t){const s=this["thumb"+e];s&&s.focus();const{zIndices:n}=this.state;n.splice(n.indexOf(e),1),n.push(e),this.setState((s=>({startValue:s.value[e],startPosition:void 0!==t?t:s.startPosition,index:e,zIndices:n})));},h.moveUpByStep=function(e){void 0===e&&(e=this.props.step);const t=r(this.state.value[this.state.index]+e,this.props);this.move(Math.min(t,this.props.max));},h.moveDownByStep=function(e){void 0===e&&(e=this.props.step);const t=r(this.state.value[this.state.index]-e,this.props);this.move(Math.max(t,this.props.min));},h.move=function(e){const{index:t,value:s}=this.state,{length:n}=s,i=s[t];if(e===i)return;this.hasMoved||this.fireChangeEvent("onBeforeChange"),this.hasMoved=!0;const{pearling:o,max:r,min:a,minDistance:p}=this.props;if(!o){if(t>0){const n=s[t-1];e<n+p&&(e=n+p);}if(t<n-1){const n=s[t+1];e>n-p&&(e=n-p);}}s[t]=e,o&&n>1&&(e>i?(this.pushSucceeding(s,p,t),function(e,t,s,n){for(let i=0;i<e;i+=1){const o=n-i*s;t[e-1-i]>o&&(t[e-1-i]=o);}}(n,s,p,r)):e<i&&(this.pushPreceding(s,p,t),function(e,t,s,n){for(let i=0;i<e;i+=1){const e=n+i*s;t[i]<e&&(t[i]=e);}}(n,s,p,a))),this.setState({value:s},this.fireChangeEvent.bind(this,"onChange"));},h.pushSucceeding=function(e,t,s){let n,i;for(n=s,i=e[n]+t;null!==e[n+1]&&i>e[n+1];n+=1,i=e[n]+t)e[n+1]=a(i,this.props);},h.pushPreceding=function(e,t,s){for(let n=s,i=e[n]-t;null!==e[n-1]&&i<e[n-1];n-=1,i=e[n]-t)e[n-1]=a(i,this.props);},h.axisKey=function(){return "vertical"===this.props.orientation?"Y":"X"},h.orthogonalAxisKey=function(){return "vertical"===this.props.orientation?"X":"Y"},h.posMinKey=function(){return "vertical"===this.props.orientation?this.props.invert?"bottom":"top":this.props.invert?"right":"left"},h.posMaxKey=function(){return "vertical"===this.props.orientation?this.props.invert?"top":"bottom":this.props.invert?"left":"right"},h.sizeKey=function(){return "vertical"===this.props.orientation?"clientHeight":"clientWidth"},h.fireChangeEvent=function(e){this.props[e]&&this.props[e](i(this.state.value),this.state.index);},h.buildThumbStyle=function(e,t){const s={position:"absolute",touchAction:"none",willChange:this.state.index>=0?this.posMinKey():void 0,zIndex:this.state.zIndices.indexOf(t)+1};return s[this.posMinKey()]=e+"px",s},h.buildTrackStyle=function(e,t){const s={position:"absolute",willChange:this.state.index>=0?this.posMinKey()+","+this.posMaxKey():void 0};return s[this.posMinKey()]=e,s[this.posMaxKey()]=t,s},h.buildMarkStyle=function(e){var t;return (t={position:"absolute"})[this.posMinKey()]=e,t},h.renderThumbs=function(e){const{length:t}=e,s=[];for(let n=0;n<t;n+=1)s[n]=this.buildThumbStyle(e[n],n);const n=[];for(let e=0;e<t;e+=1)n[e]=this.renderThumb(s[e],e);return n},h.renderTracks=function(e){const t=[],s=e.length-1;t.push(this.renderTrack(0,0,e[0]));for(let n=0;n<s;n+=1)t.push(this.renderTrack(n+1,e[n],e[n+1]));return t.push(this.renderTrack(s+1,e[s],this.state.upperBound)),t},h.renderMarks=function(){let{marks:e}=this.props;const t=this.props.max-this.props.min+1;return "boolean"==typeof e?e=Array.from({length:t}).map(((e,t)=>t)):"number"==typeof e&&(e=Array.from({length:t}).map(((e,t)=>t)).filter((t=>t%e==0))),e.map(parseFloat).sort(((e,t)=>e-t)).map((e=>{const t=this.calcOffset(e),s={key:e,className:this.props.markClassName,style:this.buildMarkStyle(t)};return this.props.renderMark(s)}))},h.render=function(){const e=[],{value:s}=this.state,n=s.length;for(let t=0;t<n;t+=1)e[t]=this.calcOffset(s[t],t);const i=this.props.withTracks?this.renderTracks(e):null,o=this.renderThumbs(e),r=this.props.marks?this.renderMarks():null;return React.createElement("div",{ref:e=>{this.slider=e,this.resizeElementRef.current=e;},style:{position:"relative"},className:this.props.className+(this.props.disabled?" disabled":""),onMouseDown:this.onSliderMouseDown,onClick:this.onSliderClick},i,o,r)},u}(React.Component);p.displayName="ReactSlider",p.defaultProps={min:0,max:100,step:1,pageFn:e=>10*e,minDistance:0,defaultValue:0,orientation:"horizontal",className:"slider",thumbClassName:"thumb",thumbActiveClassName:"active",trackClassName:"track",markClassName:"mark",withTracks:!0,pearling:!1,disabled:!1,snapDragDisabled:!1,invert:!1,marks:[],renderThumb:e=>React.createElement("div",e),renderTrack:e=>React.createElement("div",e),renderMark:e=>React.createElement("span",e)};var u=p;

var StyledReactSlider = styled(u)(templateObject_1$11 || (templateObject_1$11 = __makeTemplateObject(["\n  position: relative;\n  height: 32px;\n\n  .range-track {\n    margin: 13px 0;\n    cursor: pointer;\n    height: 6px;\n    border-radius: 5px;\n  }\n  .range-track-0 {\n    background-color: ", ";\n  }\n\n  .range-track-1 {\n    background-color: ", ";\n  }\n\n  .range-thumb-primary {\n    outline: none;\n    display: flex;\n    height: 32px;\n    width: 32px;\n    background-color: ", ";\n    box-shadow: inset 0 0 0 2px ", ";\n    border-radius: 50%;\n    justify-content: center;\n    align-items: center;\n    cursor: pointer;\n  }\n\n  .range-thumb-secondary {\n    outline: none;\n    display: flex;\n    flex-direction: column;\n    height: 78px;\n    width: 78px;\n    border-radius: 50%;\n    cursor: pointer;\n    background-color: ", ";\n    border: solid 2px ", ";\n    padding: 18px 0 16px 0;\n    top: -23px;\n\n    p {\n      color: ", ";\n    }\n  }\n"], ["\n  position: relative;\n  height: 32px;\n\n  .range-track {\n    margin: 13px 0;\n    cursor: pointer;\n    height: 6px;\n    border-radius: 5px;\n  }\n  .range-track-0 {\n    background-color: ", ";\n  }\n\n  .range-track-1 {\n    background-color: ", ";\n  }\n\n  .range-thumb-primary {\n    outline: none;\n    display: flex;\n    height: 32px;\n    width: 32px;\n    background-color: ", ";\n    box-shadow: inset 0 0 0 2px ", ";\n    border-radius: 50%;\n    justify-content: center;\n    align-items: center;\n    cursor: pointer;\n  }\n\n  .range-thumb-secondary {\n    outline: none;\n    display: flex;\n    flex-direction: column;\n    height: 78px;\n    width: 78px;\n    border-radius: 50%;\n    cursor: pointer;\n    background-color: ", ";\n    border: solid 2px ", ";\n    padding: 18px 0 16px 0;\n    top: -23px;\n\n    p {\n      color: ", ";\n    }\n  }\n"])), function (_a) {
    var theme = _a.theme;
    return theme.colors.rangeInput.rangeTrack0.backgroundColor;
}, function (_a) {
    var theme = _a.theme;
    return theme.colors.rangeInput.rangeTrack1.backgroundColor;
}, function (_a) {
    var theme = _a.theme;
    return theme.colors.rangeInput.rangeThumbPrimary.backgroundColor;
}, function (_a) {
    var theme = _a.theme;
    return theme.colors.rangeInput.rangeThumbPrimary.border;
}, function (_a) {
    var theme = _a.theme;
    return theme.colors.rangeInput.rangeThumbSecondary.backgroundColor;
}, function (_a) {
    var theme = _a.theme;
    return theme.colors.rangeInput.rangeThumbSecondary.border;
}, function (_a) {
    var theme = _a.theme;
    return theme.colors.rangeInput.rangeThumbSecondary.color;
});
var MinMaxWrapper = styled.div(templateObject_2$V || (templateObject_2$V = __makeTemplateObject(["\n  display: flex;\n  justify-content: space-between;\n"], ["\n  display: flex;\n  justify-content: space-between;\n"])));
var Body1 = styled(Body1$1)(templateObject_3$M || (templateObject_3$M = __makeTemplateObject(["\n  color: ", ";\n"], ["\n  color: ", ";\n"])), function (_a) {
    var theme = _a.theme;
    return theme.colors.rangeInput.body1.color;
});
var InputTrack = styled.div(templateObject_4$C || (templateObject_4$C = __makeTemplateObject(["\n  position: absolute;\n  background-color: ", ";\n  width: ", "px;\n  cursor: pointer;\n  height: 6px;\n  border-radius: 5px 0px 0px 5px;\n  border-right: 3px solid white;\n  z-index: 1;\n"], ["\n  position: absolute;\n  background-color: ", ";\n  width: ", "px;\n  cursor: pointer;\n  height: 6px;\n  border-radius: 5px 0px 0px 5px;\n  border-right: 3px solid white;\n  z-index: 1;\n"])), function (_a) {
    var theme = _a.theme;
    return theme.colors.rangeInput.inputTrack.backgroundColor;
}, function (props) { return props.promotionWidth; });
var HiddenInput$1 = styled.input(templateObject_5$v || (templateObject_5$v = __makeTemplateObject(["\n  display: none;\n"], ["\n  display: none;\n"])));
var templateObject_1$11, templateObject_2$V, templateObject_3$M, templateObject_4$C, templateObject_5$v;

var RangeInput = function (_a) {
    var _b = _a.step, step = _b === void 0 ? 1 : _b, _c = _a.min, min = _c === void 0 ? 0 : _c, _d = _a.max, max = _d === void 0 ? 100 : _d, value = _a.value, format = _a.format, marks = _a.marks, valueUnit = _a.valueUnit, onSliderChange = _a.onSliderChange, _e = _a.type, type = _e === void 0 ? 'primary' : _e, promotionValue = _a.promotionValue, rest = __rest$1(_a, ["step", "min", "max", "value", "format", "marks", "valueUnit", "onSliderChange", "type", "promotionValue"]);
    var localMin;
    var localMax;
    var localStep;
    var localValue;
    var renderThumb;
    if (marks) {
        localMin = 0;
        localMax = marks.length - 1;
        localStep = 1;
        localValue = marks.indexOf(value);
    }
    else {
        localMin = min;
        localMax = max;
        localStep = step;
        localValue = value;
    }
    var handleChange = function (value) {
        if (typeof value === 'number' && !marks) {
            onSliderChange(value);
        }
        else if (typeof value === 'number' && marks) {
            onSliderChange(marks[value]);
        }
        else {
            throw new Error('Unexpected array of values instead of one value');
        }
    };
    if (type === 'secondary') {
        renderThumb = useCallback(function (props) { return (jsxs("div", __assign$1({}, props, { children: [jsx(Caption3$1, __assign$1({ align: "center" }, { children: format ? format(value) : "".concat(value) })), (valueUnit === null || valueUnit === void 0 ? void 0 : valueUnit.length) === 1 ? (jsx(Body4, __assign$1({ align: "center" }, { children: valueUnit }))) : (jsx(Body5, __assign$1({ align: "center" }, { children: valueUnit })))] }))); }, [value]);
    }
    else {
        renderThumb = useCallback(function (props) { return (jsxs("div", __assign$1({}, props, { children: [jsx(Icon$4, { width: 4, height: 8, fillColor: "white", name: "TriangleLeft" }), jsx(Gutter, { size: 4 }), jsx(Icon$4, { width: 4, height: 8, fillColor: "white", name: "TriangleRight" })] }))); }, []);
    }
    var _f = useState(0), width = _f[0], setWidth = _f[1];
    var promotionWidth;
    if (promotionValue && !marks) {
        promotionWidth = (promotionValue / max) * width;
    }
    else if (promotionValue && marks) {
        promotionWidth = (marks.indexOf(promotionValue) / localMax) * width;
    }
    var ref = useRef(null);
    var updatePromotionWidth = useCallback(function () {
        if (ref && ref.current) {
            var width_1 = ref.current.getBoundingClientRect().width;
            setWidth(width_1);
        }
    }, []);
    useEffect(function () {
        function handleResize() {
            updatePromotionWidth();
        }
        window.addEventListener('resize', handleResize);
        handleResize();
        return function () { return window.removeEventListener('resize', handleResize); };
    }, []);
    var renderTrack = function (props, state) {
        return promotionValue && state.index === 0 ? (jsx("div", __assign$1({}, props, { children: jsx(InputTrack, { promotionWidth: promotionWidth }) }))) : (jsx("div", __assign$1({}, props)));
    };
    var formatWithUnit = function (value) {
        var formatted = format ? format(value) : "".concat(value);
        if (valueUnit) {
            formatted = "".concat(formatted, "\u00A0").concat(valueUnit);
        }
        return formatted;
    };
    return (jsxs("div", __assign$1({ ref: ref, "data-testid": "loan-slider" }, { children: [jsx(StyledReactSlider, { step: localStep, min: localMin, max: localMax, onChange: handleChange, value: localValue, trackClassName: "range-track", renderTrack: renderTrack, thumbClassName: type === 'secondary' ? 'range-thumb-secondary' : 'range-thumb-primary', renderThumb: renderThumb, ariaLabel: "Choose a value" }), type === 'secondary' && (jsxs(MinMaxWrapper, __assign$1({ style: { display: 'flex', justifyContent: 'space-between' } }, { children: [jsx(Body1, { children: formatWithUnit(marks ? marks[localMin] : min) }), jsx(Body1, { children: formatWithUnit(marks ? marks[localMax] : max) })] }))), jsx(HiddenInput$1, __assign$1({ value: localValue, step: localStep, min: localMin, max: localMax, type: "range", onChange: function (e) { return handleChange(e.target.valueAsNumber); } }, rest))] })));
};

var RowWrapper$1 = styled.div(templateObject_3$L || (templateObject_3$L = __makeTemplateObject(["\n  box-sizing: border-box;\n  display: flex;\n  flex: 0 1 auto;\n  flex-wrap: wrap;\n  flex-direction: ", ";\n\n  ", "\n\n  ", "\n"], ["\n  box-sizing: border-box;\n  display: flex;\n  flex: 0 1 auto;\n  flex-wrap: wrap;\n  flex-direction: ", ";\n\n  ", "\n\n  ", "\n"])), function (_a) {
    var reverse = _a.reverse;
    return (reverse ? 'row-reverse' : 'row');
}, BREAKPOINTS_NAMES.map(function (breakpoint) {
    return function (props) { return css(templateObject_1$10 || (templateObject_1$10 = __makeTemplateObject(["\n      ", "\n\n      ", "\n\n      ", "\n\n      ", "\n\n      ", "\n\n      ", "\n\n      ", "\n\n      ", "\n\n      ", "\n\n      ", "\n    "], ["\n      ", "\n\n      ", "\n\n      ", "\n\n      ", "\n\n      ", "\n\n      ", "\n\n      ", "\n\n      ", "\n\n      ", "\n\n      ", "\n    "])), breakpoint === props.start && makeMediaQuery(props.start, 'justify-content: flex-start'), breakpoint === props.center && makeMediaQuery(props.center, 'justify-content: center'), breakpoint === props.end && makeMediaQuery(props.end, 'justify-content: flex-end'), breakpoint === props.top && makeMediaQuery(props.top, 'align-items: flex-start'), breakpoint === props.middle && makeMediaQuery(props.middle, 'align-items: center'), breakpoint === props.bottom && makeMediaQuery(props.bottom, 'align-items: flex-end'), breakpoint === props.around &&
        makeMediaQuery(props.around, 'justify-content: space-around'), breakpoint === props.between &&
        makeMediaQuery(props.between, 'justify-content: space-between'), breakpoint === props.first && makeMediaQuery(props.first, 'order: -1'), breakpoint === props.last && makeMediaQuery(props.last, 'order: 1')); };
}), BREAKPOINTS_NAMES.map(function (breakpoint) {
    return function (_a) {
        var theme = _a.theme;
        return css(templateObject_2$U || (templateObject_2$U = __makeTemplateObject(["\n      ", " {\n        margin-top: ", "px;\n        margin-left: ", "px;\n      }\n    "], ["\n      ", " {\n        margin-top: ", "px;\n        margin-left: ", "px;\n      }\n    "])), theme.breakpoints[breakpoint], theme.flexboxGrid.gutterSize[breakpoint] * -1, theme.flexboxGrid.gutterSize[breakpoint] * -1);
    };
}));
var templateObject_1$10, templateObject_2$U, templateObject_3$L;

var Row$1 = function (props) {
    return jsx(RowWrapper$1, __assign$1({}, props, { children: props.children }));
};

var index$1 = /*#__PURE__*/Object.freeze({
    __proto__: null,
    Col: Col,
    Row: Row$1,
    Container: Container$1
});

var RoundTypes;
(function (RoundTypes) {
    RoundTypes["AllCorners"] = "allCorners";
    RoundTypes["DiagonalCorners"] = "diagonalCorners";
    RoundTypes["EachCornerSeparateValue"] = "eachCornerSeparateValue";
})(RoundTypes || (RoundTypes = {}));

var RoundedBackgroundContainer = styled(Container$1)(templateObject_5$u || (templateObject_5$u = __makeTemplateObject(["\n  &&& {\n    position: relative;\n    padding: 30px 0;\n\n    width: 100%;\n    height: 100%;\n    border-radius: 30px 0 30px 0;\n\n    ", " {\n      border-radius: 60px 0 60px 0;\n    }\n\n    ", ";\n\n    ", ";\n\n    ", ";\n\n    ", ";\n\n    ", " {\n      width: 100%;\n    }\n  }\n"], ["\n  &&& {\n    position: relative;\n    padding: 30px 0;\n\n    width: 100%;\n    height: 100%;\n    border-radius: 30px 0 30px 0;\n\n    ", " {\n      border-radius: 60px 0 60px 0;\n    }\n\n    ", ";\n\n    ", ";\n\n    ", ";\n\n    ", ";\n\n    ", " {\n      width: 100%;\n    }\n  }\n"])), breakpoints.md, function (_a) {
    var color = _a.color;
    return css(templateObject_1$$ || (templateObject_1$$ = __makeTemplateObject(["\n        background: ", ";\n      "], ["\n        background: ", ";\n      "])), color);
}, function (_a) {
    var roundedType = _a.roundedType, fullWidthOnMobile = _a.fullWidthOnMobile;
    return roundedType === RoundTypes.AllCorners && css(templateObject_2$T || (templateObject_2$T = __makeTemplateObject(["\n        border-radius: ", ";\n        ", " {\n          border-radius: 30px;\n        }\n        ", " {\n          border-radius: 60px;\n        }\n      "], ["\n        border-radius: ", ";\n        ", " {\n          border-radius: 30px;\n        }\n        ", " {\n          border-radius: 60px;\n        }\n      "])), fullWidthOnMobile ? '0px' : '30px', breakpoints.sm, breakpoints.md);
}, function (_a) {
    var roundedType = _a.roundedType, fullWidthOnMobile = _a.fullWidthOnMobile;
    return roundedType === RoundTypes.DiagonalCorners && css(templateObject_3$K || (templateObject_3$K = __makeTemplateObject(["\n        border-radius: ", ";\n        ", " {\n          border-radius: 30px 0 30px 0;\n        }\n        ", " {\n          border-radius: 60px 0 60px 0;\n        }\n      "], ["\n        border-radius: ", ";\n        ", " {\n          border-radius: 30px 0 30px 0;\n        }\n        ", " {\n          border-radius: 60px 0 60px 0;\n        }\n      "])), fullWidthOnMobile ? '0px' : '30px 0 30px 0', breakpoints.sm, breakpoints.md);
}, function (_a) {
    var roundedType = _a.roundedType, eachCornerSeparateValues = _a.eachCornerSeparateValues;
    return roundedType === RoundTypes.EachCornerSeparateValue && css(templateObject_4$B || (templateObject_4$B = __makeTemplateObject(["\n        border-radius: ", "\n          ", " ", "\n          ", ";\n      "], ["\n        border-radius: ", "\n          ", " ", "\n          ", ";\n      "])), (eachCornerSeparateValues === null || eachCornerSeparateValues === void 0 ? void 0 : eachCornerSeparateValues.topLeft) || 0, (eachCornerSeparateValues === null || eachCornerSeparateValues === void 0 ? void 0 : eachCornerSeparateValues.topRight) || 0, (eachCornerSeparateValues === null || eachCornerSeparateValues === void 0 ? void 0 : eachCornerSeparateValues.bottomRight) || 0, (eachCornerSeparateValues === null || eachCornerSeparateValues === void 0 ? void 0 : eachCornerSeparateValues.bottomLeft) || 0);
}, function (_a) {
    var theme = _a.theme;
    return theme.breakpoints.md;
});
var templateObject_1$$, templateObject_2$T, templateObject_3$K, templateObject_4$B, templateObject_5$u;

var RoundedBackground = function (_a) {
    var color = _a.color, roundedType = _a.roundedType, eachCornerSeparateValues = _a.eachCornerSeparateValues, children = _a.children, fluid = _a.fluid, _b = _a.fullWidthOnMobile, fullWidthOnMobile = _b === void 0 ? false : _b;
    return (jsx(RoundedBackgroundContainer, __assign$1({ color: color, roundedType: roundedType, eachCornerSeparateValues: eachCornerSeparateValues, fluid: fluid, fullWidthOnMobile: fullWidthOnMobile }, { children: children })));
};
RoundedBackground.RoundTypes = RoundTypes;

var HiddenInput = styled.input(templateObject_1$_ || (templateObject_1$_ = __makeTemplateObject(["\n  position: absolute;\n  width: 1px;\n  height: 1px;\n  padding: 0;\n  margin: -1px;\n  overflow: hidden;\n  clip: rect(0, 0, 0, 0);\n  white-space: nowrap;\n  border: 0;\n"], ["\n  position: absolute;\n  width: 1px;\n  height: 1px;\n  padding: 0;\n  margin: -1px;\n  overflow: hidden;\n  clip: rect(0, 0, 0, 0);\n  white-space: nowrap;\n  border: 0;\n"])));
var templateObject_1$_;

var Variant;
(function (Variant) {
    Variant["Text"] = "text";
    Variant["Icon"] = "icon";
    Variant["Image"] = "image";
    Variant["ImageWithText"] = "imageWithText";
})(Variant || (Variant = {}));
var Size;
(function (Size) {
    Size["Auto"] = "auto";
    Size["Content"] = "content";
    Size["Fixed"] = "fixed";
    Size["Small"] = "small";
})(Size || (Size = {}));

var handleSizeStyle = function (size) {
    switch (size) {
        case Size.Auto:
            return 'width: 100%; height: auto;';
        case Size.Content:
            return "width: max-content; height: max-content;";
        case Size.Fixed:
            return css(templateObject_1$Z || (templateObject_1$Z = __makeTemplateObject(["\n        width: 139px;\n        height: 80px;\n        flex-shrink: 0;\n        ", "\n      "], ["\n        width: 139px;\n        height: 80px;\n        flex-shrink: 0;\n        ", "\n      "])), makeMediaQuery('md', ' width: 198px; height: 100px;'));
        case Size.Small:
            return css(templateObject_2$S || (templateObject_2$S = __makeTemplateObject(["\n        width: max-content;\n        height: max-content;\n        padding: 3px 16px;\n      "], ["\n        width: max-content;\n        height: max-content;\n        padding: 3px 16px;\n      "])));
        default:
            return typeof size === 'object' &&
                typeof size.height === 'number' &&
                typeof size.width === 'number'
                ? "width: ".concat(size.width, "px; height: ").concat(size.height, "px;")
                : "width: 100%;";
    }
};
var Label$1 = styled.label(templateObject_3$J || (templateObject_3$J = __makeTemplateObject(["\n  display: flex;\n  align-items: center;\n  justify-content: center;\n  font-weight: ", ";\n  font-size: ", ";\n  line-height: ", ";\n  color: ", ";\n  text-align: center;\n  border-radius: 16px;\n  border: solid ", ";\n  ", "\n  ", "\n  padding: ", ";\n  transition: all 0.4s;\n  ", "\n  cursor: pointer;\n  overflow: hidden;\n  > * {\n    display: flex;\n    max-width: 100%;\n  }\n"], ["\n  display: flex;\n  align-items: center;\n  justify-content: center;\n  font-weight: ", ";\n  font-size: ", ";\n  line-height: ", ";\n  color: ", ";\n  text-align: center;\n  border-radius: 16px;\n  border: solid ", ";\n  ", "\n  ", "\n  padding: ", ";\n  transition: all 0.4s;\n  ", "\n  cursor: pointer;\n  overflow: hidden;\n  > * {\n    display: flex;\n    max-width: 100%;\n  }\n"])), function (_a) {
    var theme = _a.theme, selected = _a.selected;
    return selected ? theme.fonts.weight.medium500 : theme.fonts.weight.regular400;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.size.desktop.xs;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.lineHeight.desktop.xs;
}, function (_a) {
    var theme = _a.theme, selected = _a.selected;
    return selected
        ? theme.colors.selectButton.textVariant.selected.color
        : theme.colors.selectButton.textVariant.default.color;
}, function (_a) {
    var variant = _a.variant;
    return (variant === Variant.ImageWithText ? '6px' : '3px');
}, function (_a) {
    var theme = _a.theme, selected = _a.selected, variant = _a.variant;
    return variant === Variant.Text
        ? "\n          background-color: ".concat(selected
            ? theme.colors.selectButton.textVariant.selected.backgroundColor
            : theme.colors.selectButton.textVariant.default.backgroundColor, ";\n          border-color: transparent;\n        ")
        : "\n          background-color: ".concat(theme.colors.selectButton.otherVariants.backgroundColor, ";\n          border-color: ").concat(selected
            ? theme.colors.selectButton.otherVariants.borderColor.selected
            : theme.colors.selectButton.otherVariants.borderColor.default, ";\n          color: ").concat(theme.colors.selectButton.otherVariants.color, ";\n        ");
}, function (_a) {
    var theme = _a.theme, selected = _a.selected, size = _a.size;
    return size === Size.Small &&
        "\n      background-color: ".concat(selected
            ? theme.colors.selectButton.textVariant.selected.backgroundColor
            : theme.colors.selectButton.textVariant.default.backgroundColor, ";\n      border-color: transparent;\n      color: ").concat(selected
            ? theme.colors.selectButton.textVariant.selected.color
            : theme.colors.selectButton.textVariant.default.color, ";\n\n    ");
}, function (_a) {
    var variant = _a.variant;
    return (variant === Variant.ImageWithText ? 0 : '11px 16px');
}, function (_a) {
    var size = _a.size;
    return handleSizeStyle(size);
});
var ImageWithTextWrapper = styled.div(templateObject_4$A || (templateObject_4$A = __makeTemplateObject(["\n  position: relative;\n  align-items: flex-end;\n  text-align: left;\n  height: 240px;\n  width: 240px;\n  overflow: hidden;\n  z-index: 1;\n\n  &::after {\n    content: '';\n    position: absolute;\n    background-color: rgba(0, 0, 0, 0.5);\n    inset: 0;\n    z-index: -1;\n  }\n\n  > img {\n    position: absolute;\n    height: 100%;\n    width: 100%;\n    object-fit: cover;\n    z-index: -1;\n  }\n\n  > span {\n    font-weight: ", ";\n    font-size: ", ";\n    color: ", ";\n    padding: ", ";\n  }\n"], ["\n  position: relative;\n  align-items: flex-end;\n  text-align: left;\n  height: 240px;\n  width: 240px;\n  overflow: hidden;\n  z-index: 1;\n\n  &::after {\n    content: '';\n    position: absolute;\n    background-color: rgba(0, 0, 0, 0.5);\n    inset: 0;\n    z-index: -1;\n  }\n\n  > img {\n    position: absolute;\n    height: 100%;\n    width: 100%;\n    object-fit: cover;\n    z-index: -1;\n  }\n\n  > span {\n    font-weight: ", ";\n    font-size: ", ";\n    color: ", ";\n    padding: ", ";\n  }\n"])), function (_a) {
    var theme = _a.theme;
    return theme.fonts.weight.medium500;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.size.desktop.s;
}, function (_a) {
    var theme = _a.theme;
    return theme.colors.selectButton.textVariant.selected.color;
}, function (_a) {
    var theme = _a.theme;
    return "".concat(theme.space[16]);
});
var templateObject_1$Z, templateObject_2$S, templateObject_3$J, templateObject_4$A;

var Content = function (props) {
    var variant = props.variant;
    switch (variant) {
        case Variant.Text:
            return jsx("span", { children: props.label });
        case Variant.Icon:
            return jsx(Icon$4, { name: props.icon });
        case Variant.Image:
            // eslint-disable-next-line react/jsx-no-useless-fragment
            return jsx(Fragment, { children: props.image });
        case Variant.ImageWithText:
            return (jsxs(ImageWithTextWrapper, { children: [props.image, jsx("span", { children: props.label })] }));
        default:
            // eslint-disable-next-line no-case-declarations
            var exhaustiveCheck = variant;
            throw new Error("Unhandled variant case: ".concat(exhaustiveCheck));
    }
};
var SelectButton = function (props) {
    var variant = props.variant, selectedValue = props.selectedValue, value = props.value, onSelect = props.onSelect, size = props.size, rest = __rest$1(props, ["variant", "selectedValue", "value", "onSelect", "size"]);
    var selected = value === selectedValue;
    return (jsxs(Label$1, __assign$1({ selected: selected, variant: variant, size: size }, { children: [jsx(HiddenInput, __assign$1({ type: "radio", value: value, checked: selected, onChange: onSelect }, rest)), jsx(Content, __assign$1({}, props))] })));
};
SelectButton.Variant = Variant;
SelectButton.Size = Size;

var ThemeProvider = function (_a) {
    var children = _a.children;
    return (jsx(ThemeProvider$1, __assign$1({ theme: theme }, { children: children })));
};

var TooltipWrapper = styled.div(templateObject_3$I || (templateObject_3$I = __makeTemplateObject(["\n  position: absolute;\n  bottom: 100%;\n  right: 0;\n  ", " {\n    right: -10px;\n  }\n  background-color: ", ";\n  border-radius: 10px;\n  max-width: 300px;\n  ", " {\n    max-width: 330px;\n  }\n  ", " {\n    max-width: 375px;\n  }\n\n  ", "\n"], ["\n  position: absolute;\n  bottom: 100%;\n  right: 0;\n  ", " {\n    right: -10px;\n  }\n  background-color: ", ";\n  border-radius: 10px;\n  max-width: 300px;\n  ", " {\n    max-width: 330px;\n  }\n  ", " {\n    max-width: 375px;\n  }\n\n  ", "\n"])), function (_a) {
    var theme = _a.theme;
    return theme.breakpoints.lg;
}, theme.colors.tooltip.backgroundColor, function (_a) {
    var theme = _a.theme;
    return theme.breakpoints.sm;
}, function (_a) {
    var theme = _a.theme;
    return theme.breakpoints.lg;
}, function (props) {
    return props.show
        ? css(templateObject_1$Y || (templateObject_1$Y = __makeTemplateObject(["\n          visibility: visible;\n        "], ["\n          visibility: visible;\n        "]))) : css(templateObject_2$R || (templateObject_2$R = __makeTemplateObject(["\n          display: none;\n        "], ["\n          display: none;\n        "])));
});
var TooltipText = styled(Body3$2)(templateObject_4$z || (templateObject_4$z = __makeTemplateObject(["\n  color: ", ";\n  padding: 12px;\n"], ["\n  color: ", ";\n  padding: 12px;\n"])), theme.colors.tooltip.color);
var TooltipInputIcon = styled(Icon$4)(templateObject_5$t || (templateObject_5$t = __makeTemplateObject(["\n  position: absolute;\n  top: 20px;\n"], ["\n  position: absolute;\n  top: 20px;\n"])));
var TooltipIcon = styled(TooltipInputIcon).attrs({
    name: 'ButtonInfoBlueOff',
    width: 26,
    height: 26,
})(templateObject_7$m || (templateObject_7$m = __makeTemplateObject(["\n  right: 13px;\n\n  ", "\n"], ["\n  right: 13px;\n\n  ", "\n"])), function (props) {
    return props.show && css(templateObject_6$q || (templateObject_6$q = __makeTemplateObject(["\n      &:hover {\n        cursor: pointer;\n        svg path {\n          fill: ", ";\n        }\n      }\n    "], ["\n      &:hover {\n        cursor: pointer;\n        svg path {\n          fill: ", ";\n        }\n      }\n    "])), function (_a) {
        var theme = _a.theme;
        return theme.colors.tooltip.iconHover;
    });
});
styled.div(templateObject_8$f || (templateObject_8$f = __makeTemplateObject(["\n  position: relative;\n  top: 200px;\n"], ["\n  position: relative;\n  top: 200px;\n"])));
var templateObject_1$Y, templateObject_2$R, templateObject_3$I, templateObject_4$z, templateObject_5$t, templateObject_6$q, templateObject_7$m, templateObject_8$f;

var Tooltip = function (_a) {
    var tooltipText = _a.tooltipText;
    var _b = React.useState(false), show = _b[0], setShow = _b[1];
    return (jsxs(Fragment, { children: [jsx(TooltipWrapper, __assign$1({ show: show }, { children: jsx(TooltipText, __assign$1({ "data-testid": "tooltip-test-text" }, { children: tooltipText })) })), jsx("div", __assign$1({ onMouseEnter: function () { return setShow(true); }, onMouseLeave: function () { return setShow(false); } }, { children: jsx(TooltipIcon, { show: show, "data-testid": "tooltipIcon" }) }))] }));
};

var ReactPlayer = {};

var isMergeableObject = function isMergeableObject(value) {
	return isNonNullObject(value)
		&& !isSpecial(value)
};

function isNonNullObject(value) {
	return !!value && typeof value === 'object'
}

function isSpecial(value) {
	var stringValue = Object.prototype.toString.call(value);

	return stringValue === '[object RegExp]'
		|| stringValue === '[object Date]'
		|| isReactElement(value)
}

// see https://github.com/facebook/react/blob/b5ac963fb791d1298e7f396236383bc955f916c1/src/isomorphic/classic/element/ReactElement.js#L21-L25
var canUseSymbol = typeof Symbol === 'function' && Symbol.for;
var REACT_ELEMENT_TYPE = canUseSymbol ? Symbol.for('react.element') : 0xeac7;

function isReactElement(value) {
	return value.$$typeof === REACT_ELEMENT_TYPE
}

function emptyTarget(val) {
	return Array.isArray(val) ? [] : {}
}

function cloneUnlessOtherwiseSpecified(value, options) {
	return (options.clone !== false && options.isMergeableObject(value))
		? deepmerge(emptyTarget(value), value, options)
		: value
}

function defaultArrayMerge(target, source, options) {
	return target.concat(source).map(function(element) {
		return cloneUnlessOtherwiseSpecified(element, options)
	})
}

function getMergeFunction(key, options) {
	if (!options.customMerge) {
		return deepmerge
	}
	var customMerge = options.customMerge(key);
	return typeof customMerge === 'function' ? customMerge : deepmerge
}

function getEnumerableOwnPropertySymbols(target) {
	return Object.getOwnPropertySymbols
		? Object.getOwnPropertySymbols(target).filter(function(symbol) {
			return Object.propertyIsEnumerable.call(target, symbol)
		})
		: []
}

function getKeys(target) {
	return Object.keys(target).concat(getEnumerableOwnPropertySymbols(target))
}

function propertyIsOnObject(object, property) {
	try {
		return property in object
	} catch(_) {
		return false
	}
}

// Protects from prototype poisoning and unexpected merging up the prototype chain.
function propertyIsUnsafe(target, key) {
	return propertyIsOnObject(target, key) // Properties are safe to merge if they don't exist in the target yet,
		&& !(Object.hasOwnProperty.call(target, key) // unsafe if they exist up the prototype chain,
			&& Object.propertyIsEnumerable.call(target, key)) // and also unsafe if they're nonenumerable.
}

function mergeObject(target, source, options) {
	var destination = {};
	if (options.isMergeableObject(target)) {
		getKeys(target).forEach(function(key) {
			destination[key] = cloneUnlessOtherwiseSpecified(target[key], options);
		});
	}
	getKeys(source).forEach(function(key) {
		if (propertyIsUnsafe(target, key)) {
			return
		}

		if (propertyIsOnObject(target, key) && options.isMergeableObject(source[key])) {
			destination[key] = getMergeFunction(key, options)(target[key], source[key], options);
		} else {
			destination[key] = cloneUnlessOtherwiseSpecified(source[key], options);
		}
	});
	return destination
}

function deepmerge(target, source, options) {
	options = options || {};
	options.arrayMerge = options.arrayMerge || defaultArrayMerge;
	options.isMergeableObject = options.isMergeableObject || isMergeableObject;
	// cloneUnlessOtherwiseSpecified is added to `options` so that custom arrayMerge()
	// implementations can use it. The caller may not replace it.
	options.cloneUnlessOtherwiseSpecified = cloneUnlessOtherwiseSpecified;

	var sourceIsArray = Array.isArray(source);
	var targetIsArray = Array.isArray(target);
	var sourceAndTargetTypesMatch = sourceIsArray === targetIsArray;

	if (!sourceAndTargetTypesMatch) {
		return cloneUnlessOtherwiseSpecified(source, options)
	} else if (sourceIsArray) {
		return options.arrayMerge(target, source, options)
	} else {
		return mergeObject(target, source, options)
	}
}

deepmerge.all = function deepmergeAll(array, options) {
	if (!Array.isArray(array)) {
		throw new Error('first argument should be an array')
	}

	return array.reduce(function(prev, next) {
		return deepmerge(prev, next, options)
	}, {})
};

var deepmerge_1 = deepmerge;

var cjs = deepmerge_1;

var safeIsNaN = Number.isNaN ||
    function ponyfill(value) {
        return typeof value === 'number' && value !== value;
    };
function isEqual(first, second) {
    if (first === second) {
        return true;
    }
    if (safeIsNaN(first) && safeIsNaN(second)) {
        return true;
    }
    return false;
}
function areInputsEqual(newInputs, lastInputs) {
    if (newInputs.length !== lastInputs.length) {
        return false;
    }
    for (var i = 0; i < newInputs.length; i++) {
        if (!isEqual(newInputs[i], lastInputs[i])) {
            return false;
        }
    }
    return true;
}

function memoizeOne(resultFn, isEqual) {
    if (isEqual === void 0) { isEqual = areInputsEqual; }
    var lastThis;
    var lastArgs = [];
    var lastResult;
    var calledOnce = false;
    function memoized() {
        var newArgs = [];
        for (var _i = 0; _i < arguments.length; _i++) {
            newArgs[_i] = arguments[_i];
        }
        if (calledOnce && lastThis === this && isEqual(newArgs, lastArgs)) {
            return lastResult;
        }
        lastResult = resultFn.apply(this, newArgs);
        calledOnce = true;
        lastThis = this;
        lastArgs = newArgs;
        return lastResult;
    }
    return memoized;
}

var memoizeOne_esm = /*#__PURE__*/Object.freeze({
    __proto__: null,
    'default': memoizeOne
});

var require$$2 = /*@__PURE__*/getAugmentedNamespace(memoizeOne_esm);

/* global Map:readonly, Set:readonly, ArrayBuffer:readonly */

var hasElementType = typeof Element !== 'undefined';
var hasMap = typeof Map === 'function';
var hasSet = typeof Set === 'function';
var hasArrayBuffer = typeof ArrayBuffer === 'function' && !!ArrayBuffer.isView;

// Note: We **don't** need `envHasBigInt64Array` in fde es6/index.js

function equal(a, b) {
  // START: fast-deep-equal es6/index.js 3.1.3
  if (a === b) return true;

  if (a && b && typeof a == 'object' && typeof b == 'object') {
    if (a.constructor !== b.constructor) return false;

    var length, i, keys;
    if (Array.isArray(a)) {
      length = a.length;
      if (length != b.length) return false;
      for (i = length; i-- !== 0;)
        if (!equal(a[i], b[i])) return false;
      return true;
    }

    // START: Modifications:
    // 1. Extra `has<Type> &&` helpers in initial condition allow es6 code
    //    to co-exist with es5.
    // 2. Replace `for of` with es5 compliant iteration using `for`.
    //    Basically, take:
    //
    //    ```js
    //    for (i of a.entries())
    //      if (!b.has(i[0])) return false;
    //    ```
    //
    //    ... and convert to:
    //
    //    ```js
    //    it = a.entries();
    //    while (!(i = it.next()).done)
    //      if (!b.has(i.value[0])) return false;
    //    ```
    //
    //    **Note**: `i` access switches to `i.value`.
    var it;
    if (hasMap && (a instanceof Map) && (b instanceof Map)) {
      if (a.size !== b.size) return false;
      it = a.entries();
      while (!(i = it.next()).done)
        if (!b.has(i.value[0])) return false;
      it = a.entries();
      while (!(i = it.next()).done)
        if (!equal(i.value[1], b.get(i.value[0]))) return false;
      return true;
    }

    if (hasSet && (a instanceof Set) && (b instanceof Set)) {
      if (a.size !== b.size) return false;
      it = a.entries();
      while (!(i = it.next()).done)
        if (!b.has(i.value[0])) return false;
      return true;
    }
    // END: Modifications

    if (hasArrayBuffer && ArrayBuffer.isView(a) && ArrayBuffer.isView(b)) {
      length = a.length;
      if (length != b.length) return false;
      for (i = length; i-- !== 0;)
        if (a[i] !== b[i]) return false;
      return true;
    }

    if (a.constructor === RegExp) return a.source === b.source && a.flags === b.flags;
    // START: Modifications:
    // Apply guards for `Object.create(null)` handling. See:
    // - https://github.com/FormidableLabs/react-fast-compare/issues/64
    // - https://github.com/epoberezkin/fast-deep-equal/issues/49
    if (a.valueOf !== Object.prototype.valueOf && typeof a.valueOf === 'function' && typeof b.valueOf === 'function') return a.valueOf() === b.valueOf();
    if (a.toString !== Object.prototype.toString && typeof a.toString === 'function' && typeof b.toString === 'function') return a.toString() === b.toString();
    // END: Modifications

    keys = Object.keys(a);
    length = keys.length;
    if (length !== Object.keys(b).length) return false;

    for (i = length; i-- !== 0;)
      if (!Object.prototype.hasOwnProperty.call(b, keys[i])) return false;
    // END: fast-deep-equal

    // START: react-fast-compare
    // custom handling for DOM elements
    if (hasElementType && a instanceof Element) return false;

    // custom handling for React/Preact
    for (i = length; i-- !== 0;) {
      if ((keys[i] === '_owner' || keys[i] === '__v' || keys[i] === '__o') && a.$$typeof) {
        // React-specific: avoid traversing React elements' _owner
        // Preact-specific: avoid traversing Preact elements' __v and __o
        //    __v = $_original / $_vnode
        //    __o = $_owner
        // These properties contain circular references and are not needed when
        // comparing the actual elements (and not their owners)
        // .$$typeof and ._store on just reasonable markers of elements

        continue;
      }

      // all other properties should be traversed as usual
      if (!equal(a[keys[i]], b[keys[i]])) return false;
    }
    // END: react-fast-compare

    // START: fast-deep-equal
    return true;
  }

  return a !== a && b !== b;
}
// end fast-deep-equal

var reactFastCompare = function isEqual(a, b) {
  try {
    return equal(a, b);
  } catch (error) {
    if (((error.message || '').match(/stack|recursion/i))) {
      // warn on circular references, don't crash
      // browsers give this different errors name and messages:
      // chrome/safari: "RangeError", "Maximum call stack size exceeded"
      // firefox: "InternalError", too much recursion"
      // edge: "Error", "Out of stack space"
      console.warn('react-fast-compare cannot handle circular refs');
      return false;
    }
    // some other error. we should definitely know about these
    throw error;
  }
};

var props = {};

Object.defineProperty(props, "__esModule", {
  value: true
});
props.defaultProps = props.propTypes = void 0;

var _propTypes = _interopRequireDefault$7(propTypes$2.exports);

function _interopRequireDefault$7(obj) { return obj && obj.__esModule ? obj : { "default": obj }; }

var string = _propTypes["default"].string,
    bool = _propTypes["default"].bool,
    number = _propTypes["default"].number,
    array = _propTypes["default"].array,
    oneOfType = _propTypes["default"].oneOfType,
    shape = _propTypes["default"].shape,
    object = _propTypes["default"].object,
    func = _propTypes["default"].func,
    node = _propTypes["default"].node;
var propTypes = {
  url: oneOfType([string, array, object]),
  playing: bool,
  loop: bool,
  controls: bool,
  volume: number,
  muted: bool,
  playbackRate: number,
  width: oneOfType([string, number]),
  height: oneOfType([string, number]),
  style: object,
  progressInterval: number,
  playsinline: bool,
  pip: bool,
  stopOnUnmount: bool,
  light: oneOfType([bool, string, object]),
  playIcon: node,
  previewTabIndex: number,
  fallback: node,
  oEmbedUrl: string,
  wrapper: oneOfType([string, func, shape({
    render: func.isRequired
  })]),
  config: shape({
    soundcloud: shape({
      options: object
    }),
    youtube: shape({
      playerVars: object,
      embedOptions: object,
      onUnstarted: func
    }),
    facebook: shape({
      appId: string,
      version: string,
      playerId: string,
      attributes: object
    }),
    dailymotion: shape({
      params: object
    }),
    vimeo: shape({
      playerOptions: object,
      title: string
    }),
    file: shape({
      attributes: object,
      tracks: array,
      forceVideo: bool,
      forceAudio: bool,
      forceHLS: bool,
      forceSafariHLS: bool,
      forceDisableHls: bool,
      forceDASH: bool,
      forceFLV: bool,
      hlsOptions: object,
      hlsVersion: string,
      dashVersion: string,
      flvVersion: string
    }),
    wistia: shape({
      options: object,
      playerId: string,
      customControls: array
    }),
    mixcloud: shape({
      options: object
    }),
    twitch: shape({
      options: object,
      playerId: string
    }),
    vidyard: shape({
      options: object
    })
  }),
  onReady: func,
  onStart: func,
  onPlay: func,
  onPause: func,
  onBuffer: func,
  onBufferEnd: func,
  onEnded: func,
  onError: func,
  onDuration: func,
  onSeek: func,
  onPlaybackRateChange: func,
  onPlaybackQualityChange: func,
  onProgress: func,
  onClickPreview: func,
  onEnablePIP: func,
  onDisablePIP: func
};
props.propTypes = propTypes;

var noop = function noop() {};

var defaultProps$1 = {
  playing: false,
  loop: false,
  controls: false,
  volume: null,
  muted: false,
  playbackRate: 1,
  width: '640px',
  height: '360px',
  style: {},
  progressInterval: 1000,
  playsinline: false,
  pip: false,
  stopOnUnmount: true,
  light: false,
  fallback: null,
  wrapper: 'div',
  previewTabIndex: 0,
  oEmbedUrl: 'https://noembed.com/embed?url={url}',
  config: {
    soundcloud: {
      options: {
        visual: true,
        // Undocumented, but makes player fill container and look better
        buying: false,
        liking: false,
        download: false,
        sharing: false,
        show_comments: false,
        show_playcount: false
      }
    },
    youtube: {
      playerVars: {
        playsinline: 1,
        showinfo: 0,
        rel: 0,
        iv_load_policy: 3,
        modestbranding: 1
      },
      embedOptions: {},
      onUnstarted: noop
    },
    facebook: {
      appId: '1309697205772819',
      version: 'v3.3',
      playerId: null,
      attributes: {}
    },
    dailymotion: {
      params: {
        api: 1,
        'endscreen-enable': false
      }
    },
    vimeo: {
      playerOptions: {
        autopause: false,
        byline: false,
        portrait: false,
        title: false
      },
      title: null
    },
    file: {
      attributes: {},
      tracks: [],
      forceVideo: false,
      forceAudio: false,
      forceHLS: false,
      forceDASH: false,
      forceFLV: false,
      hlsOptions: {},
      hlsVersion: '1.1.4',
      dashVersion: '3.1.3',
      flvVersion: '1.5.0',
      forceDisableHls: false
    },
    wistia: {
      options: {},
      playerId: null,
      customControls: null
    },
    mixcloud: {
      options: {
        hide_cover: 1
      }
    },
    twitch: {
      options: {},
      playerId: null
    },
    vidyard: {
      options: {}
    }
  },
  onReady: noop,
  onStart: noop,
  onPlay: noop,
  onPause: noop,
  onBuffer: noop,
  onBufferEnd: noop,
  onEnded: noop,
  onError: noop,
  onDuration: noop,
  onSeek: noop,
  onPlaybackRateChange: noop,
  onPlaybackQualityChange: noop,
  onProgress: noop,
  onClickPreview: noop,
  onEnablePIP: noop,
  onDisablePIP: noop
};
props.defaultProps = defaultProps$1;

var utils = {};

var loadScript = function load (src, opts, cb) {
  var head = document.head || document.getElementsByTagName('head')[0];
  var script = document.createElement('script');

  if (typeof opts === 'function') {
    cb = opts;
    opts = {};
  }

  opts = opts || {};
  cb = cb || function() {};

  script.type = opts.type || 'text/javascript';
  script.charset = opts.charset || 'utf8';
  script.async = 'async' in opts ? !!opts.async : true;
  script.src = src;

  if (opts.attrs) {
    setAttributes(script, opts.attrs);
  }

  if (opts.text) {
    script.text = '' + opts.text;
  }

  var onend = 'onload' in script ? stdOnEnd : ieOnEnd;
  onend(script, cb);

  // some good legacy browsers (firefox) fail the 'in' detection above
  // so as a fallback we always set onload
  // old IE will ignore this and new IE will set onload
  if (!script.onload) {
    stdOnEnd(script, cb);
  }

  head.appendChild(script);
};

function setAttributes(script, attrs) {
  for (var attr in attrs) {
    script.setAttribute(attr, attrs[attr]);
  }
}

function stdOnEnd (script, cb) {
  script.onload = function () {
    this.onerror = this.onload = null;
    cb(null, script);
  };
  script.onerror = function () {
    // this.onload = null here is necessary
    // because even IE9 works not like others
    this.onerror = this.onload = null;
    cb(new Error('Failed to load ' + this.src), script);
  };
}

function ieOnEnd (script, cb) {
  script.onreadystatechange = function () {
    if (this.readyState != 'complete' && this.readyState != 'loaded') return
    this.onreadystatechange = null;
    cb(null, script); // there is no way to catch loading errors in IE8
  };
}

Object.defineProperty(utils, "__esModule", {
  value: true
});
utils.parseStartTime = parseStartTime;
utils.parseEndTime = parseEndTime;
utils.randomString = randomString;
utils.queryString = queryString;
utils.getSDK = getSDK;
utils.getConfig = getConfig;
utils.omit = omit;
utils.callPlayer = callPlayer;
utils.isMediaStream = isMediaStream;
utils.isBlobUrl = isBlobUrl;
utils.supportsWebKitPresentationMode = supportsWebKitPresentationMode;

var _loadScript = _interopRequireDefault$6(loadScript);

var _deepmerge$1 = _interopRequireDefault$6(cjs);

function _interopRequireDefault$6(obj) { return obj && obj.__esModule ? obj : { "default": obj }; }

function _slicedToArray(arr, i) { return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _unsupportedIterableToArray$2(arr, i) || _nonIterableRest(); }

function _nonIterableRest() { throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); }

function _unsupportedIterableToArray$2(o, minLen) { if (!o) return; if (typeof o === "string") return _arrayLikeToArray$2(o, minLen); var n = Object.prototype.toString.call(o).slice(8, -1); if (n === "Object" && o.constructor) n = o.constructor.name; if (n === "Map" || n === "Set") return Array.from(o); if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray$2(o, minLen); }

function _arrayLikeToArray$2(arr, len) { if (len == null || len > arr.length) len = arr.length; for (var i = 0, arr2 = new Array(len); i < len; i++) { arr2[i] = arr[i]; } return arr2; }

function _iterableToArrayLimit(arr, i) { if (typeof Symbol === "undefined" || !(Symbol.iterator in Object(arr))) return; var _arr = []; var _n = true; var _d = false; var _e = undefined; try { for (var _i = arr[Symbol.iterator](), _s; !(_n = (_s = _i.next()).done); _n = true) { _arr.push(_s.value); if (i && _arr.length === i) break; } } catch (err) { _d = true; _e = err; } finally { try { if (!_n && _i["return"] != null) _i["return"](); } finally { if (_d) throw _e; } } return _arr; }

function _arrayWithHoles(arr) { if (Array.isArray(arr)) return arr; }

var MATCH_START_QUERY = /[?&#](?:start|t)=([0-9hms]+)/;
var MATCH_END_QUERY = /[?&#]end=([0-9hms]+)/;
var MATCH_START_STAMP = /(\d+)(h|m|s)/g;
var MATCH_NUMERIC = /^\d+$/; // Parse YouTube URL for a start time param, ie ?t=1h14m30s
// and return the start time in seconds

function parseTimeParam(url, pattern) {
  if (url instanceof Array) {
    return undefined;
  }

  var match = url.match(pattern);

  if (match) {
    var stamp = match[1];

    if (stamp.match(MATCH_START_STAMP)) {
      return parseTimeString(stamp);
    }

    if (MATCH_NUMERIC.test(stamp)) {
      return parseInt(stamp);
    }
  }

  return undefined;
}

function parseTimeString(stamp) {
  var seconds = 0;
  var array = MATCH_START_STAMP.exec(stamp);

  while (array !== null) {
    var _array = array,
        _array2 = _slicedToArray(_array, 3),
        count = _array2[1],
        period = _array2[2];

    if (period === 'h') seconds += parseInt(count, 10) * 60 * 60;
    if (period === 'm') seconds += parseInt(count, 10) * 60;
    if (period === 's') seconds += parseInt(count, 10);
    array = MATCH_START_STAMP.exec(stamp);
  }

  return seconds;
}

function parseStartTime(url) {
  return parseTimeParam(url, MATCH_START_QUERY);
}

function parseEndTime(url) {
  return parseTimeParam(url, MATCH_END_QUERY);
} // http://stackoverflow.com/a/38622545


function randomString() {
  return Math.random().toString(36).substr(2, 5);
}

function queryString(object) {
  return Object.keys(object).map(function (key) {
    return "".concat(key, "=").concat(object[key]);
  }).join('&');
}

function getGlobal(key) {
  if (window[key]) {
    return window[key];
  }

  if (window.exports && window.exports[key]) {
    return window.exports[key];
  }

  if (window.module && window.module.exports && window.module.exports[key]) {
    return window.module.exports[key];
  }

  return null;
} // Util function to load an external SDK
// or return the SDK if it is already loaded


var requests = {};

function getSDK(url, sdkGlobal) {
  var sdkReady = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : null;
  var isLoaded = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : function () {
    return true;
  };
  var fetchScript = arguments.length > 4 && arguments[4] !== undefined ? arguments[4] : _loadScript["default"];
  var existingGlobal = getGlobal(sdkGlobal);

  if (existingGlobal && isLoaded(existingGlobal)) {
    return Promise.resolve(existingGlobal);
  }

  return new Promise(function (resolve, reject) {
    // If we are already loading the SDK, add the resolve and reject
    // functions to the existing array of requests
    if (requests[url]) {
      requests[url].push({
        resolve: resolve,
        reject: reject
      });
      return;
    }

    requests[url] = [{
      resolve: resolve,
      reject: reject
    }];

    var onLoaded = function onLoaded(sdk) {
      // When loaded, resolve all pending request promises
      requests[url].forEach(function (request) {
        return request.resolve(sdk);
      });
    };

    if (sdkReady) {
      var previousOnReady = window[sdkReady];

      window[sdkReady] = function () {
        if (previousOnReady) previousOnReady();
        onLoaded(getGlobal(sdkGlobal));
      };
    }

    fetchScript(url, function (err) {
      if (err) {
        // Loading the SDK failed – reject all requests and
        // reset the array of requests for this SDK
        requests[url].forEach(function (request) {
          return request.reject(err);
        });
        requests[url] = null;
      } else if (!sdkReady) {
        onLoaded(getGlobal(sdkGlobal));
      }
    });
  });
}

function getConfig(props, defaultProps) {
  return (0, _deepmerge$1["default"])(defaultProps.config, props.config);
}

function omit(object) {
  var _ref;

  for (var _len = arguments.length, arrays = new Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
    arrays[_key - 1] = arguments[_key];
  }

  var omitKeys = (_ref = []).concat.apply(_ref, arrays);

  var output = {};
  var keys = Object.keys(object);

  for (var _i2 = 0, _keys = keys; _i2 < _keys.length; _i2++) {
    var key = _keys[_i2];

    if (omitKeys.indexOf(key) === -1) {
      output[key] = object[key];
    }
  }

  return output;
}

function callPlayer(method) {
  var _this$player;

  // Util method for calling a method on this.player
  // but guard against errors and console.warn instead
  if (!this.player || !this.player[method]) {
    var message = "ReactPlayer: ".concat(this.constructor.displayName, " player could not call %c").concat(method, "%c \u2013 ");

    if (!this.player) {
      message += 'The player was not available';
    } else if (!this.player[method]) {
      message += 'The method was not available';
    }

    console.warn(message, 'font-weight: bold', '');
    return null;
  }

  for (var _len2 = arguments.length, args = new Array(_len2 > 1 ? _len2 - 1 : 0), _key2 = 1; _key2 < _len2; _key2++) {
    args[_key2 - 1] = arguments[_key2];
  }

  return (_this$player = this.player)[method].apply(_this$player, args);
}

function isMediaStream(url) {
  return typeof window !== 'undefined' && typeof window.MediaStream !== 'undefined' && url instanceof window.MediaStream;
}

function isBlobUrl(url) {
  return /^blob:/.test(url);
}

function supportsWebKitPresentationMode() {
  var video = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : document.createElement('video');
  // Check if Safari supports PiP, and is not on mobile (other than iPad)
  // iPhone safari appears to "support" PiP through the check, however PiP does not function
  var notMobile = /iPhone|iPod/.test(navigator.userAgent) === false;
  return video.webkitSupportsPresentationMode && typeof video.webkitSetPresentationMode === 'function' && notMobile;
}

var Player$1 = {};

(function (exports) {

	function _typeof(obj) { "@babel/helpers - typeof"; if (typeof Symbol === "function" && typeof Symbol.iterator === "symbol") { _typeof = function _typeof(obj) { return typeof obj; }; } else { _typeof = function _typeof(obj) { return obj && typeof Symbol === "function" && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj; }; } return _typeof(obj); }

	Object.defineProperty(exports, "__esModule", {
	  value: true
	});
	exports["default"] = void 0;

	var _react = _interopRequireWildcard(React);

	var _reactFastCompare = _interopRequireDefault(reactFastCompare);

	var _props = props;

	var _utils = utils;

	function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { "default": obj }; }

	function _getRequireWildcardCache() { if (typeof WeakMap !== "function") return null; var cache = new WeakMap(); _getRequireWildcardCache = function _getRequireWildcardCache() { return cache; }; return cache; }

	function _interopRequireWildcard(obj) { if (obj && obj.__esModule) { return obj; } if (obj === null || _typeof(obj) !== "object" && typeof obj !== "function") { return { "default": obj }; } var cache = _getRequireWildcardCache(); if (cache && cache.has(obj)) { return cache.get(obj); } var newObj = {}; var hasPropertyDescriptor = Object.defineProperty && Object.getOwnPropertyDescriptor; for (var key in obj) { if (Object.prototype.hasOwnProperty.call(obj, key)) { var desc = hasPropertyDescriptor ? Object.getOwnPropertyDescriptor(obj, key) : null; if (desc && (desc.get || desc.set)) { Object.defineProperty(newObj, key, desc); } else { newObj[key] = obj[key]; } } } newObj["default"] = obj; if (cache) { cache.set(obj, newObj); } return newObj; }

	function _extends() { _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }

	function _classCallCheck(instance, Constructor) { if (!(instance instanceof Constructor)) { throw new TypeError("Cannot call a class as a function"); } }

	function _defineProperties(target, props) { for (var i = 0; i < props.length; i++) { var descriptor = props[i]; descriptor.enumerable = descriptor.enumerable || false; descriptor.configurable = true; if ("value" in descriptor) descriptor.writable = true; Object.defineProperty(target, descriptor.key, descriptor); } }

	function _createClass(Constructor, protoProps, staticProps) { if (protoProps) _defineProperties(Constructor.prototype, protoProps); if (staticProps) _defineProperties(Constructor, staticProps); return Constructor; }

	function _inherits(subClass, superClass) { if (typeof superClass !== "function" && superClass !== null) { throw new TypeError("Super expression must either be null or a function"); } subClass.prototype = Object.create(superClass && superClass.prototype, { constructor: { value: subClass, writable: true, configurable: true } }); if (superClass) _setPrototypeOf(subClass, superClass); }

	function _setPrototypeOf(o, p) { _setPrototypeOf = Object.setPrototypeOf || function _setPrototypeOf(o, p) { o.__proto__ = p; return o; }; return _setPrototypeOf(o, p); }

	function _createSuper(Derived) { var hasNativeReflectConstruct = _isNativeReflectConstruct(); return function _createSuperInternal() { var Super = _getPrototypeOf(Derived), result; if (hasNativeReflectConstruct) { var NewTarget = _getPrototypeOf(this).constructor; result = Reflect.construct(Super, arguments, NewTarget); } else { result = Super.apply(this, arguments); } return _possibleConstructorReturn(this, result); }; }

	function _possibleConstructorReturn(self, call) { if (call && (_typeof(call) === "object" || typeof call === "function")) { return call; } return _assertThisInitialized(self); }

	function _assertThisInitialized(self) { if (self === void 0) { throw new ReferenceError("this hasn't been initialised - super() hasn't been called"); } return self; }

	function _isNativeReflectConstruct() { if (typeof Reflect === "undefined" || !Reflect.construct) return false; if (Reflect.construct.sham) return false; if (typeof Proxy === "function") return true; try { Date.prototype.toString.call(Reflect.construct(Date, [], function () {})); return true; } catch (e) { return false; } }

	function _getPrototypeOf(o) { _getPrototypeOf = Object.setPrototypeOf ? Object.getPrototypeOf : function _getPrototypeOf(o) { return o.__proto__ || Object.getPrototypeOf(o); }; return _getPrototypeOf(o); }

	function _defineProperty(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

	var SEEK_ON_PLAY_EXPIRY = 5000;

	var Player = /*#__PURE__*/function (_Component) {
	  _inherits(Player, _Component);

	  var _super = _createSuper(Player);

	  function Player() {
	    var _this;

	    _classCallCheck(this, Player);

	    for (var _len = arguments.length, _args = new Array(_len), _key = 0; _key < _len; _key++) {
	      _args[_key] = arguments[_key];
	    }

	    _this = _super.call.apply(_super, [this].concat(_args));

	    _defineProperty(_assertThisInitialized(_this), "mounted", false);

	    _defineProperty(_assertThisInitialized(_this), "isReady", false);

	    _defineProperty(_assertThisInitialized(_this), "isPlaying", false);

	    _defineProperty(_assertThisInitialized(_this), "isLoading", true);

	    _defineProperty(_assertThisInitialized(_this), "loadOnReady", null);

	    _defineProperty(_assertThisInitialized(_this), "startOnPlay", true);

	    _defineProperty(_assertThisInitialized(_this), "seekOnPlay", null);

	    _defineProperty(_assertThisInitialized(_this), "onDurationCalled", false);

	    _defineProperty(_assertThisInitialized(_this), "handlePlayerMount", function (player) {
	      if (_this.player) {
	        _this.progress(); // Ensure onProgress is still called in strict mode


	        return; // Return here to prevent loading twice in strict mode
	      }

	      _this.player = player;

	      _this.player.load(_this.props.url);

	      _this.progress();
	    });

	    _defineProperty(_assertThisInitialized(_this), "getInternalPlayer", function (key) {
	      if (!_this.player) return null;
	      return _this.player[key];
	    });

	    _defineProperty(_assertThisInitialized(_this), "progress", function () {
	      if (_this.props.url && _this.player && _this.isReady) {
	        var playedSeconds = _this.getCurrentTime() || 0;

	        var loadedSeconds = _this.getSecondsLoaded();

	        var duration = _this.getDuration();

	        if (duration) {
	          var progress = {
	            playedSeconds: playedSeconds,
	            played: playedSeconds / duration
	          };

	          if (loadedSeconds !== null) {
	            progress.loadedSeconds = loadedSeconds;
	            progress.loaded = loadedSeconds / duration;
	          } // Only call onProgress if values have changed


	          if (progress.playedSeconds !== _this.prevPlayed || progress.loadedSeconds !== _this.prevLoaded) {
	            _this.props.onProgress(progress);
	          }

	          _this.prevPlayed = progress.playedSeconds;
	          _this.prevLoaded = progress.loadedSeconds;
	        }
	      }

	      _this.progressTimeout = setTimeout(_this.progress, _this.props.progressFrequency || _this.props.progressInterval);
	    });

	    _defineProperty(_assertThisInitialized(_this), "handleReady", function () {
	      if (!_this.mounted) return;
	      _this.isReady = true;
	      _this.isLoading = false;
	      var _this$props = _this.props,
	          onReady = _this$props.onReady,
	          playing = _this$props.playing,
	          volume = _this$props.volume,
	          muted = _this$props.muted;
	      onReady();

	      if (!muted && volume !== null) {
	        _this.player.setVolume(volume);
	      }

	      if (_this.loadOnReady) {
	        _this.player.load(_this.loadOnReady, true);

	        _this.loadOnReady = null;
	      } else if (playing) {
	        _this.player.play();
	      }

	      _this.handleDurationCheck();
	    });

	    _defineProperty(_assertThisInitialized(_this), "handlePlay", function () {
	      _this.isPlaying = true;
	      _this.isLoading = false;
	      var _this$props2 = _this.props,
	          onStart = _this$props2.onStart,
	          onPlay = _this$props2.onPlay,
	          playbackRate = _this$props2.playbackRate;

	      if (_this.startOnPlay) {
	        if (_this.player.setPlaybackRate && playbackRate !== 1) {
	          _this.player.setPlaybackRate(playbackRate);
	        }

	        onStart();
	        _this.startOnPlay = false;
	      }

	      onPlay();

	      if (_this.seekOnPlay) {
	        _this.seekTo(_this.seekOnPlay);

	        _this.seekOnPlay = null;
	      }

	      _this.handleDurationCheck();
	    });

	    _defineProperty(_assertThisInitialized(_this), "handlePause", function (e) {
	      _this.isPlaying = false;

	      if (!_this.isLoading) {
	        _this.props.onPause(e);
	      }
	    });

	    _defineProperty(_assertThisInitialized(_this), "handleEnded", function () {
	      var _this$props3 = _this.props,
	          activePlayer = _this$props3.activePlayer,
	          loop = _this$props3.loop,
	          onEnded = _this$props3.onEnded;

	      if (activePlayer.loopOnEnded && loop) {
	        _this.seekTo(0);
	      }

	      if (!loop) {
	        _this.isPlaying = false;
	        onEnded();
	      }
	    });

	    _defineProperty(_assertThisInitialized(_this), "handleError", function () {
	      var _this$props4;

	      _this.isLoading = false;

	      (_this$props4 = _this.props).onError.apply(_this$props4, arguments);
	    });

	    _defineProperty(_assertThisInitialized(_this), "handleDurationCheck", function () {
	      clearTimeout(_this.durationCheckTimeout);

	      var duration = _this.getDuration();

	      if (duration) {
	        if (!_this.onDurationCalled) {
	          _this.props.onDuration(duration);

	          _this.onDurationCalled = true;
	        }
	      } else {
	        _this.durationCheckTimeout = setTimeout(_this.handleDurationCheck, 100);
	      }
	    });

	    _defineProperty(_assertThisInitialized(_this), "handleLoaded", function () {
	      // Sometimes we know loading has stopped but onReady/onPlay are never called
	      // so this provides a way for players to avoid getting stuck
	      _this.isLoading = false;
	    });

	    return _this;
	  }

	  _createClass(Player, [{
	    key: "componentDidMount",
	    value: function componentDidMount() {
	      this.mounted = true;
	    }
	  }, {
	    key: "componentWillUnmount",
	    value: function componentWillUnmount() {
	      clearTimeout(this.progressTimeout);
	      clearTimeout(this.durationCheckTimeout);

	      if (this.isReady && this.props.stopOnUnmount) {
	        this.player.stop();

	        if (this.player.disablePIP) {
	          this.player.disablePIP();
	        }
	      }

	      this.mounted = false;
	    }
	  }, {
	    key: "componentDidUpdate",
	    value: function componentDidUpdate(prevProps) {
	      var _this2 = this;

	      // If there isn’t a player available, don’t do anything
	      if (!this.player) {
	        return;
	      } // Invoke player methods based on changed props


	      var _this$props5 = this.props,
	          url = _this$props5.url,
	          playing = _this$props5.playing,
	          volume = _this$props5.volume,
	          muted = _this$props5.muted,
	          playbackRate = _this$props5.playbackRate,
	          pip = _this$props5.pip,
	          loop = _this$props5.loop,
	          activePlayer = _this$props5.activePlayer,
	          disableDeferredLoading = _this$props5.disableDeferredLoading;

	      if (!(0, _reactFastCompare["default"])(prevProps.url, url)) {
	        if (this.isLoading && !activePlayer.forceLoad && !disableDeferredLoading && !(0, _utils.isMediaStream)(url)) {
	          console.warn("ReactPlayer: the attempt to load ".concat(url, " is being deferred until the player has loaded"));
	          this.loadOnReady = url;
	          return;
	        }

	        this.isLoading = true;
	        this.startOnPlay = true;
	        this.onDurationCalled = false;
	        this.player.load(url, this.isReady);
	      }

	      if (!prevProps.playing && playing && !this.isPlaying) {
	        this.player.play();
	      }

	      if (prevProps.playing && !playing && this.isPlaying) {
	        this.player.pause();
	      }

	      if (!prevProps.pip && pip && this.player.enablePIP) {
	        this.player.enablePIP();
	      }

	      if (prevProps.pip && !pip && this.player.disablePIP) {
	        this.player.disablePIP();
	      }

	      if (prevProps.volume !== volume && volume !== null) {
	        this.player.setVolume(volume);
	      }

	      if (prevProps.muted !== muted) {
	        if (muted) {
	          this.player.mute();
	        } else {
	          this.player.unmute();

	          if (volume !== null) {
	            // Set volume next tick to fix a bug with DailyMotion
	            setTimeout(function () {
	              return _this2.player.setVolume(volume);
	            });
	          }
	        }
	      }

	      if (prevProps.playbackRate !== playbackRate && this.player.setPlaybackRate) {
	        this.player.setPlaybackRate(playbackRate);
	      }

	      if (prevProps.loop !== loop && this.player.setLoop) {
	        this.player.setLoop(loop);
	      }
	    }
	  }, {
	    key: "getDuration",
	    value: function getDuration() {
	      if (!this.isReady) return null;
	      return this.player.getDuration();
	    }
	  }, {
	    key: "getCurrentTime",
	    value: function getCurrentTime() {
	      if (!this.isReady) return null;
	      return this.player.getCurrentTime();
	    }
	  }, {
	    key: "getSecondsLoaded",
	    value: function getSecondsLoaded() {
	      if (!this.isReady) return null;
	      return this.player.getSecondsLoaded();
	    }
	  }, {
	    key: "seekTo",
	    value: function seekTo(amount, type, keepPlaying) {
	      var _this3 = this;

	      // When seeking before player is ready, store value and seek later
	      if (!this.isReady) {
	        if (amount !== 0) {
	          this.seekOnPlay = amount;
	          setTimeout(function () {
	            _this3.seekOnPlay = null;
	          }, SEEK_ON_PLAY_EXPIRY);
	        }

	        return;
	      }

	      var isFraction = !type ? amount > 0 && amount < 1 : type === 'fraction';

	      if (isFraction) {
	        // Convert fraction to seconds based on duration
	        var duration = this.player.getDuration();

	        if (!duration) {
	          console.warn('ReactPlayer: could not seek using fraction – duration not yet available');
	          return;
	        }

	        this.player.seekTo(duration * amount, keepPlaying);
	        return;
	      }

	      this.player.seekTo(amount, keepPlaying);
	    }
	  }, {
	    key: "render",
	    value: function render() {
	      var Player = this.props.activePlayer;

	      if (!Player) {
	        return null;
	      }

	      return /*#__PURE__*/_react["default"].createElement(Player, _extends({}, this.props, {
	        onMount: this.handlePlayerMount,
	        onReady: this.handleReady,
	        onPlay: this.handlePlay,
	        onPause: this.handlePause,
	        onEnded: this.handleEnded,
	        onLoaded: this.handleLoaded,
	        onError: this.handleError
	      }));
	    }
	  }]);

	  return Player;
	}(_react.Component);

	exports["default"] = Player;

	_defineProperty(Player, "displayName", 'Player');

	_defineProperty(Player, "propTypes", _props.propTypes);

	_defineProperty(Player, "defaultProps", _props.defaultProps);
} (Player$1));

var Preview$1 = {};

var hasRequiredPreview;

function requirePreview () {
	if (hasRequiredPreview) return Preview$1;
	hasRequiredPreview = 1;
	(function (exports) {

		function _typeof(obj) { "@babel/helpers - typeof"; if (typeof Symbol === "function" && typeof Symbol.iterator === "symbol") { _typeof = function _typeof(obj) { return typeof obj; }; } else { _typeof = function _typeof(obj) { return obj && typeof Symbol === "function" && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj; }; } return _typeof(obj); }

		Object.defineProperty(exports, "__esModule", {
		  value: true
		});
		exports["default"] = void 0;

		var _react = _interopRequireWildcard(React);

		function _getRequireWildcardCache() { if (typeof WeakMap !== "function") return null; var cache = new WeakMap(); _getRequireWildcardCache = function _getRequireWildcardCache() { return cache; }; return cache; }

		function _interopRequireWildcard(obj) { if (obj && obj.__esModule) { return obj; } if (obj === null || _typeof(obj) !== "object" && typeof obj !== "function") { return { "default": obj }; } var cache = _getRequireWildcardCache(); if (cache && cache.has(obj)) { return cache.get(obj); } var newObj = {}; var hasPropertyDescriptor = Object.defineProperty && Object.getOwnPropertyDescriptor; for (var key in obj) { if (Object.prototype.hasOwnProperty.call(obj, key)) { var desc = hasPropertyDescriptor ? Object.getOwnPropertyDescriptor(obj, key) : null; if (desc && (desc.get || desc.set)) { Object.defineProperty(newObj, key, desc); } else { newObj[key] = obj[key]; } } } newObj["default"] = obj; if (cache) { cache.set(obj, newObj); } return newObj; }

		function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

		function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

		function _classCallCheck(instance, Constructor) { if (!(instance instanceof Constructor)) { throw new TypeError("Cannot call a class as a function"); } }

		function _defineProperties(target, props) { for (var i = 0; i < props.length; i++) { var descriptor = props[i]; descriptor.enumerable = descriptor.enumerable || false; descriptor.configurable = true; if ("value" in descriptor) descriptor.writable = true; Object.defineProperty(target, descriptor.key, descriptor); } }

		function _createClass(Constructor, protoProps, staticProps) { if (protoProps) _defineProperties(Constructor.prototype, protoProps); if (staticProps) _defineProperties(Constructor, staticProps); return Constructor; }

		function _inherits(subClass, superClass) { if (typeof superClass !== "function" && superClass !== null) { throw new TypeError("Super expression must either be null or a function"); } subClass.prototype = Object.create(superClass && superClass.prototype, { constructor: { value: subClass, writable: true, configurable: true } }); if (superClass) _setPrototypeOf(subClass, superClass); }

		function _setPrototypeOf(o, p) { _setPrototypeOf = Object.setPrototypeOf || function _setPrototypeOf(o, p) { o.__proto__ = p; return o; }; return _setPrototypeOf(o, p); }

		function _createSuper(Derived) { var hasNativeReflectConstruct = _isNativeReflectConstruct(); return function _createSuperInternal() { var Super = _getPrototypeOf(Derived), result; if (hasNativeReflectConstruct) { var NewTarget = _getPrototypeOf(this).constructor; result = Reflect.construct(Super, arguments, NewTarget); } else { result = Super.apply(this, arguments); } return _possibleConstructorReturn(this, result); }; }

		function _possibleConstructorReturn(self, call) { if (call && (_typeof(call) === "object" || typeof call === "function")) { return call; } return _assertThisInitialized(self); }

		function _assertThisInitialized(self) { if (self === void 0) { throw new ReferenceError("this hasn't been initialised - super() hasn't been called"); } return self; }

		function _isNativeReflectConstruct() { if (typeof Reflect === "undefined" || !Reflect.construct) return false; if (Reflect.construct.sham) return false; if (typeof Proxy === "function") return true; try { Date.prototype.toString.call(Reflect.construct(Date, [], function () {})); return true; } catch (e) { return false; } }

		function _getPrototypeOf(o) { _getPrototypeOf = Object.setPrototypeOf ? Object.getPrototypeOf : function _getPrototypeOf(o) { return o.__proto__ || Object.getPrototypeOf(o); }; return _getPrototypeOf(o); }

		function _defineProperty(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

		var ICON_SIZE = '64px';
		var cache = {};

		var Preview = /*#__PURE__*/function (_Component) {
		  _inherits(Preview, _Component);

		  var _super = _createSuper(Preview);

		  function Preview() {
		    var _this;

		    _classCallCheck(this, Preview);

		    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
		      args[_key] = arguments[_key];
		    }

		    _this = _super.call.apply(_super, [this].concat(args));

		    _defineProperty(_assertThisInitialized(_this), "mounted", false);

		    _defineProperty(_assertThisInitialized(_this), "state", {
		      image: null
		    });

		    _defineProperty(_assertThisInitialized(_this), "handleKeyPress", function (e) {
		      if (e.key === 'Enter' || e.key === ' ') {
		        _this.props.onClick();
		      }
		    });

		    return _this;
		  }

		  _createClass(Preview, [{
		    key: "componentDidMount",
		    value: function componentDidMount() {
		      this.mounted = true;
		      this.fetchImage(this.props);
		    }
		  }, {
		    key: "componentDidUpdate",
		    value: function componentDidUpdate(prevProps) {
		      var _this$props = this.props,
		          url = _this$props.url,
		          light = _this$props.light;

		      if (prevProps.url !== url || prevProps.light !== light) {
		        this.fetchImage(this.props);
		      }
		    }
		  }, {
		    key: "componentWillUnmount",
		    value: function componentWillUnmount() {
		      this.mounted = false;
		    }
		  }, {
		    key: "fetchImage",
		    value: function fetchImage(_ref) {
		      var _this2 = this;

		      var url = _ref.url,
		          light = _ref.light,
		          oEmbedUrl = _ref.oEmbedUrl;

		      if ( /*#__PURE__*/_react["default"].isValidElement(light)) {
		        return;
		      }

		      if (typeof light === 'string') {
		        this.setState({
		          image: light
		        });
		        return;
		      }

		      if (cache[url]) {
		        this.setState({
		          image: cache[url]
		        });
		        return;
		      }

		      this.setState({
		        image: null
		      });
		      return window.fetch(oEmbedUrl.replace('{url}', url)).then(function (response) {
		        return response.json();
		      }).then(function (data) {
		        if (data.thumbnail_url && _this2.mounted) {
		          var image = data.thumbnail_url.replace('height=100', 'height=480').replace('-d_295x166', '-d_640');

		          _this2.setState({
		            image: image
		          });

		          cache[url] = image;
		        }
		      });
		    }
		  }, {
		    key: "render",
		    value: function render() {
		      var _this$props2 = this.props,
		          light = _this$props2.light,
		          onClick = _this$props2.onClick,
		          playIcon = _this$props2.playIcon,
		          previewTabIndex = _this$props2.previewTabIndex;
		      var image = this.state.image;

		      var isElement = /*#__PURE__*/_react["default"].isValidElement(light);

		      var flexCenter = {
		        display: 'flex',
		        alignItems: 'center',
		        justifyContent: 'center'
		      };
		      var styles = {
		        preview: _objectSpread({
		          width: '100%',
		          height: '100%',
		          backgroundImage: image && !isElement ? "url(".concat(image, ")") : undefined,
		          backgroundSize: 'cover',
		          backgroundPosition: 'center',
		          cursor: 'pointer'
		        }, flexCenter),
		        shadow: _objectSpread({
		          background: 'radial-gradient(rgb(0, 0, 0, 0.3), rgba(0, 0, 0, 0) 60%)',
		          borderRadius: ICON_SIZE,
		          width: ICON_SIZE,
		          height: ICON_SIZE,
		          position: isElement ? 'absolute' : undefined
		        }, flexCenter),
		        playIcon: {
		          borderStyle: 'solid',
		          borderWidth: '16px 0 16px 26px',
		          borderColor: 'transparent transparent transparent white',
		          marginLeft: '7px'
		        }
		      };

		      var defaultPlayIcon = /*#__PURE__*/_react["default"].createElement("div", {
		        style: styles.shadow,
		        className: "react-player__shadow"
		      }, /*#__PURE__*/_react["default"].createElement("div", {
		        style: styles.playIcon,
		        className: "react-player__play-icon"
		      }));

		      return /*#__PURE__*/_react["default"].createElement("div", {
		        style: styles.preview,
		        className: "react-player__preview",
		        onClick: onClick,
		        tabIndex: previewTabIndex,
		        onKeyPress: this.handleKeyPress
		      }, isElement ? light : null, playIcon || defaultPlayIcon);
		    }
		  }]);

		  return Preview;
		}(_react.Component);

		exports["default"] = Preview;
} (Preview$1));
	return Preview$1;
}

Object.defineProperty(ReactPlayer, "__esModule", {
  value: true
});
ReactPlayer.createReactPlayer = void 0;

var _react$5 = _interopRequireWildcard(React);

var _deepmerge = _interopRequireDefault$5(cjs);

var _memoizeOne = _interopRequireDefault$5(require$$2);

var _reactFastCompare = _interopRequireDefault$5(reactFastCompare);

var _props = props;

var _utils$1 = utils;

var _Player3 = _interopRequireDefault$5(Player$1);

function _interopRequireDefault$5(obj) { return obj && obj.__esModule ? obj : { "default": obj }; }

function _typeof$4(obj) { "@babel/helpers - typeof"; if (typeof Symbol === "function" && typeof Symbol.iterator === "symbol") { _typeof$4 = function _typeof(obj) { return typeof obj; }; } else { _typeof$4 = function _typeof(obj) { return obj && typeof Symbol === "function" && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj; }; } return _typeof$4(obj); }

function ownKeys$5(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread$5(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys$5(Object(source), true).forEach(function (key) { _defineProperty$5(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys$5(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

function _extends$3() { _extends$3 = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$3.apply(this, arguments); }

function _toConsumableArray(arr) { return _arrayWithoutHoles(arr) || _iterableToArray(arr) || _unsupportedIterableToArray$1(arr) || _nonIterableSpread(); }

function _nonIterableSpread() { throw new TypeError("Invalid attempt to spread non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); }

function _unsupportedIterableToArray$1(o, minLen) { if (!o) return; if (typeof o === "string") return _arrayLikeToArray$1(o, minLen); var n = Object.prototype.toString.call(o).slice(8, -1); if (n === "Object" && o.constructor) n = o.constructor.name; if (n === "Map" || n === "Set") return Array.from(o); if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray$1(o, minLen); }

function _iterableToArray(iter) { if (typeof Symbol !== "undefined" && Symbol.iterator in Object(iter)) return Array.from(iter); }

function _arrayWithoutHoles(arr) { if (Array.isArray(arr)) return _arrayLikeToArray$1(arr); }

function _arrayLikeToArray$1(arr, len) { if (len == null || len > arr.length) len = arr.length; for (var i = 0, arr2 = new Array(len); i < len; i++) { arr2[i] = arr[i]; } return arr2; }

function _classCallCheck$4(instance, Constructor) { if (!(instance instanceof Constructor)) { throw new TypeError("Cannot call a class as a function"); } }

function _defineProperties$4(target, props) { for (var i = 0; i < props.length; i++) { var descriptor = props[i]; descriptor.enumerable = descriptor.enumerable || false; descriptor.configurable = true; if ("value" in descriptor) descriptor.writable = true; Object.defineProperty(target, descriptor.key, descriptor); } }

function _createClass$4(Constructor, protoProps, staticProps) { if (protoProps) _defineProperties$4(Constructor.prototype, protoProps); if (staticProps) _defineProperties$4(Constructor, staticProps); return Constructor; }

function _inherits$4(subClass, superClass) { if (typeof superClass !== "function" && superClass !== null) { throw new TypeError("Super expression must either be null or a function"); } subClass.prototype = Object.create(superClass && superClass.prototype, { constructor: { value: subClass, writable: true, configurable: true } }); if (superClass) _setPrototypeOf$4(subClass, superClass); }

function _setPrototypeOf$4(o, p) { _setPrototypeOf$4 = Object.setPrototypeOf || function _setPrototypeOf(o, p) { o.__proto__ = p; return o; }; return _setPrototypeOf$4(o, p); }

function _createSuper$4(Derived) { var hasNativeReflectConstruct = _isNativeReflectConstruct$4(); return function _createSuperInternal() { var Super = _getPrototypeOf$4(Derived), result; if (hasNativeReflectConstruct) { var NewTarget = _getPrototypeOf$4(this).constructor; result = Reflect.construct(Super, arguments, NewTarget); } else { result = Super.apply(this, arguments); } return _possibleConstructorReturn$4(this, result); }; }

function _possibleConstructorReturn$4(self, call) { if (call && (_typeof$4(call) === "object" || typeof call === "function")) { return call; } return _assertThisInitialized$4(self); }

function _assertThisInitialized$4(self) { if (self === void 0) { throw new ReferenceError("this hasn't been initialised - super() hasn't been called"); } return self; }

function _isNativeReflectConstruct$4() { if (typeof Reflect === "undefined" || !Reflect.construct) return false; if (Reflect.construct.sham) return false; if (typeof Proxy === "function") return true; try { Date.prototype.toString.call(Reflect.construct(Date, [], function () {})); return true; } catch (e) { return false; } }

function _getPrototypeOf$4(o) { _getPrototypeOf$4 = Object.setPrototypeOf ? Object.getPrototypeOf : function _getPrototypeOf(o) { return o.__proto__ || Object.getPrototypeOf(o); }; return _getPrototypeOf$4(o); }

function _defineProperty$5(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

function _getRequireWildcardCache() { if (typeof WeakMap !== "function") return null; var cache = new WeakMap(); _getRequireWildcardCache = function _getRequireWildcardCache() { return cache; }; return cache; }

function _interopRequireWildcard(obj) { if (obj && obj.__esModule) { return obj; } if (obj === null || _typeof$4(obj) !== "object" && typeof obj !== "function") { return { "default": obj }; } var cache = _getRequireWildcardCache(); if (cache && cache.has(obj)) { return cache.get(obj); } var newObj = {}; var hasPropertyDescriptor = Object.defineProperty && Object.getOwnPropertyDescriptor; for (var key in obj) { if (Object.prototype.hasOwnProperty.call(obj, key)) { var desc = hasPropertyDescriptor ? Object.getOwnPropertyDescriptor(obj, key) : null; if (desc && (desc.get || desc.set)) { Object.defineProperty(newObj, key, desc); } else { newObj[key] = obj[key]; } } } newObj["default"] = obj; if (cache) { cache.set(obj, newObj); } return newObj; }

var Preview = /*#__PURE__*/(0, _react$5.lazy)(function () {
  return Promise.resolve().then(function () {
    return _interopRequireWildcard(requirePreview());
  });
});
var IS_BROWSER = typeof window !== 'undefined' && window.document;
var IS_GLOBAL = typeof commonjsGlobal !== 'undefined' && commonjsGlobal.window && commonjsGlobal.window.document;
var SUPPORTED_PROPS = Object.keys(_props.propTypes); // Return null when rendering on the server
// as Suspense is not supported yet

var UniversalSuspense = IS_BROWSER || IS_GLOBAL ? _react$5.Suspense : function () {
  return null;
};
var customPlayers = [];

var createReactPlayer$1 = function createReactPlayer(players, fallback) {
  var _class, _temp;

  return _temp = _class = /*#__PURE__*/function (_Component) {
    _inherits$4(ReactPlayer, _Component);

    var _super = _createSuper$4(ReactPlayer);

    function ReactPlayer() {
      var _this;

      _classCallCheck$4(this, ReactPlayer);

      for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
        args[_key] = arguments[_key];
      }

      _this = _super.call.apply(_super, [this].concat(args));

      _defineProperty$5(_assertThisInitialized$4(_this), "state", {
        showPreview: !!_this.props.light
      });

      _defineProperty$5(_assertThisInitialized$4(_this), "references", {
        wrapper: function wrapper(_wrapper) {
          _this.wrapper = _wrapper;
        },
        player: function player(_player) {
          _this.player = _player;
        }
      });

      _defineProperty$5(_assertThisInitialized$4(_this), "handleClickPreview", function (e) {
        _this.setState({
          showPreview: false
        });

        _this.props.onClickPreview(e);
      });

      _defineProperty$5(_assertThisInitialized$4(_this), "showPreview", function () {
        _this.setState({
          showPreview: true
        });
      });

      _defineProperty$5(_assertThisInitialized$4(_this), "getDuration", function () {
        if (!_this.player) return null;
        return _this.player.getDuration();
      });

      _defineProperty$5(_assertThisInitialized$4(_this), "getCurrentTime", function () {
        if (!_this.player) return null;
        return _this.player.getCurrentTime();
      });

      _defineProperty$5(_assertThisInitialized$4(_this), "getSecondsLoaded", function () {
        if (!_this.player) return null;
        return _this.player.getSecondsLoaded();
      });

      _defineProperty$5(_assertThisInitialized$4(_this), "getInternalPlayer", function () {
        var key = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 'player';
        if (!_this.player) return null;
        return _this.player.getInternalPlayer(key);
      });

      _defineProperty$5(_assertThisInitialized$4(_this), "seekTo", function (fraction, type, keepPlaying) {
        if (!_this.player) return null;

        _this.player.seekTo(fraction, type, keepPlaying);
      });

      _defineProperty$5(_assertThisInitialized$4(_this), "handleReady", function () {
        _this.props.onReady(_assertThisInitialized$4(_this));
      });

      _defineProperty$5(_assertThisInitialized$4(_this), "getActivePlayer", (0, _memoizeOne["default"])(function (url) {
        for (var _i = 0, _arr = [].concat(customPlayers, _toConsumableArray(players)); _i < _arr.length; _i++) {
          var player = _arr[_i];

          if (player.canPlay(url)) {
            return player;
          }
        }

        if (fallback) {
          return fallback;
        }

        return null;
      }));

      _defineProperty$5(_assertThisInitialized$4(_this), "getConfig", (0, _memoizeOne["default"])(function (url, key) {
        var config = _this.props.config;
        return _deepmerge["default"].all([_props.defaultProps.config, _props.defaultProps.config[key] || {}, config, config[key] || {}]);
      }));

      _defineProperty$5(_assertThisInitialized$4(_this), "getAttributes", (0, _memoizeOne["default"])(function (url) {
        return (0, _utils$1.omit)(_this.props, SUPPORTED_PROPS);
      }));

      _defineProperty$5(_assertThisInitialized$4(_this), "renderActivePlayer", function (url) {
        if (!url) return null;

        var player = _this.getActivePlayer(url);

        if (!player) return null;

        var config = _this.getConfig(url, player.key);

        return /*#__PURE__*/_react$5["default"].createElement(_Player3["default"], _extends$3({}, _this.props, {
          key: player.key,
          ref: _this.references.player,
          config: config,
          activePlayer: player.lazyPlayer || player,
          onReady: _this.handleReady
        }));
      });

      return _this;
    }

    _createClass$4(ReactPlayer, [{
      key: "shouldComponentUpdate",
      value: function shouldComponentUpdate(nextProps, nextState) {
        return !(0, _reactFastCompare["default"])(this.props, nextProps) || !(0, _reactFastCompare["default"])(this.state, nextState);
      }
    }, {
      key: "componentDidUpdate",
      value: function componentDidUpdate(prevProps) {
        var light = this.props.light;

        if (!prevProps.light && light) {
          this.setState({
            showPreview: true
          });
        }

        if (prevProps.light && !light) {
          this.setState({
            showPreview: false
          });
        }
      }
    }, {
      key: "renderPreview",
      value: function renderPreview(url) {
        if (!url) return null;
        var _this$props = this.props,
            light = _this$props.light,
            playIcon = _this$props.playIcon,
            previewTabIndex = _this$props.previewTabIndex,
            oEmbedUrl = _this$props.oEmbedUrl;
        return /*#__PURE__*/_react$5["default"].createElement(Preview, {
          url: url,
          light: light,
          playIcon: playIcon,
          previewTabIndex: previewTabIndex,
          oEmbedUrl: oEmbedUrl,
          onClick: this.handleClickPreview
        });
      }
    }, {
      key: "render",
      value: function render() {
        var _this$props2 = this.props,
            url = _this$props2.url,
            style = _this$props2.style,
            width = _this$props2.width,
            height = _this$props2.height,
            fallback = _this$props2.fallback,
            Wrapper = _this$props2.wrapper;
        var showPreview = this.state.showPreview;
        var attributes = this.getAttributes(url);
        var wrapperRef = typeof Wrapper === 'string' ? this.references.wrapper : undefined;
        return /*#__PURE__*/_react$5["default"].createElement(Wrapper, _extends$3({
          ref: wrapperRef,
          style: _objectSpread$5(_objectSpread$5({}, style), {}, {
            width: width,
            height: height
          })
        }, attributes), /*#__PURE__*/_react$5["default"].createElement(UniversalSuspense, {
          fallback: fallback
        }, showPreview ? this.renderPreview(url) : this.renderActivePlayer(url)));
      }
    }]);

    return ReactPlayer;
  }(_react$5.Component), _defineProperty$5(_class, "displayName", 'ReactPlayer'), _defineProperty$5(_class, "propTypes", _props.propTypes), _defineProperty$5(_class, "defaultProps", _props.defaultProps), _defineProperty$5(_class, "addCustomPlayer", function (player) {
    customPlayers.push(player);
  }), _defineProperty$5(_class, "removeCustomPlayers", function () {
    customPlayers.length = 0;
  }), _defineProperty$5(_class, "canPlay", function (url) {
    for (var _i2 = 0, _arr2 = [].concat(customPlayers, _toConsumableArray(players)); _i2 < _arr2.length; _i2++) {
      var _Player = _arr2[_i2];

      if (_Player.canPlay(url)) {
        return true;
      }
    }

    return false;
  }), _defineProperty$5(_class, "canEnablePIP", function (url) {
    for (var _i3 = 0, _arr3 = [].concat(customPlayers, _toConsumableArray(players)); _i3 < _arr3.length; _i3++) {
      var _Player2 = _arr3[_i3];

      if (_Player2.canEnablePIP && _Player2.canEnablePIP(url)) {
        return true;
      }
    }

    return false;
  }), _temp;
};

ReactPlayer.createReactPlayer = createReactPlayer$1;

var FilePlayer = {};

var patterns = {};

Object.defineProperty(patterns, "__esModule", {
  value: true
});
patterns.canPlay = patterns.FLV_EXTENSIONS = patterns.DASH_EXTENSIONS = patterns.HLS_EXTENSIONS = patterns.VIDEO_EXTENSIONS = patterns.AUDIO_EXTENSIONS = patterns.MATCH_URL_KALTURA = patterns.MATCH_URL_VIDYARD = patterns.MATCH_URL_MIXCLOUD = patterns.MATCH_URL_DAILYMOTION = patterns.MATCH_URL_TWITCH_CHANNEL = patterns.MATCH_URL_TWITCH_VIDEO = patterns.MATCH_URL_WISTIA = patterns.MATCH_URL_STREAMABLE = patterns.MATCH_URL_FACEBOOK_WATCH = patterns.MATCH_URL_FACEBOOK = patterns.MATCH_URL_VIMEO = patterns.MATCH_URL_SOUNDCLOUD = patterns.MATCH_URL_YOUTUBE = void 0;

var _utils = utils;

function _createForOfIteratorHelper(o, allowArrayLike) { var it; if (typeof Symbol === "undefined" || o[Symbol.iterator] == null) { if (Array.isArray(o) || (it = _unsupportedIterableToArray(o)) || allowArrayLike && o && typeof o.length === "number") { if (it) o = it; var i = 0; var F = function F() {}; return { s: F, n: function n() { if (i >= o.length) return { done: true }; return { done: false, value: o[i++] }; }, e: function e(_e) { throw _e; }, f: F }; } throw new TypeError("Invalid attempt to iterate non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); } var normalCompletion = true, didErr = false, err; return { s: function s() { it = o[Symbol.iterator](); }, n: function n() { var step = it.next(); normalCompletion = step.done; return step; }, e: function e(_e2) { didErr = true; err = _e2; }, f: function f() { try { if (!normalCompletion && it["return"] != null) it["return"](); } finally { if (didErr) throw err; } } }; }

function _unsupportedIterableToArray(o, minLen) { if (!o) return; if (typeof o === "string") return _arrayLikeToArray(o, minLen); var n = Object.prototype.toString.call(o).slice(8, -1); if (n === "Object" && o.constructor) n = o.constructor.name; if (n === "Map" || n === "Set") return Array.from(o); if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen); }

function _arrayLikeToArray(arr, len) { if (len == null || len > arr.length) len = arr.length; for (var i = 0, arr2 = new Array(len); i < len; i++) { arr2[i] = arr[i]; } return arr2; }

var MATCH_URL_YOUTUBE = /(?:youtu\.be\/|youtube(?:-nocookie|education)?\.com\/(?:embed\/|v\/|watch\/|watch\?v=|watch\?.+&v=|shorts\/|live\/))((\w|-){11})|youtube\.com\/playlist\?list=|youtube\.com\/user\//;
patterns.MATCH_URL_YOUTUBE = MATCH_URL_YOUTUBE;
var MATCH_URL_SOUNDCLOUD = /(?:soundcloud\.com|snd\.sc)\/[^.]+$/;
patterns.MATCH_URL_SOUNDCLOUD = MATCH_URL_SOUNDCLOUD;
var MATCH_URL_VIMEO = /vimeo\.com\/(?!progressive_redirect).+/;
patterns.MATCH_URL_VIMEO = MATCH_URL_VIMEO;
var MATCH_URL_FACEBOOK = /^https?:\/\/(www\.)?facebook\.com.*\/(video(s)?|watch|story)(\.php?|\/).+$/;
patterns.MATCH_URL_FACEBOOK = MATCH_URL_FACEBOOK;
var MATCH_URL_FACEBOOK_WATCH = /^https?:\/\/fb\.watch\/.+$/;
patterns.MATCH_URL_FACEBOOK_WATCH = MATCH_URL_FACEBOOK_WATCH;
var MATCH_URL_STREAMABLE = /streamable\.com\/([a-z0-9]+)$/;
patterns.MATCH_URL_STREAMABLE = MATCH_URL_STREAMABLE;
var MATCH_URL_WISTIA = /(?:wistia\.(?:com|net)|wi\.st)\/(?:medias|embed)\/(?:iframe\/)?([^?]+)/;
patterns.MATCH_URL_WISTIA = MATCH_URL_WISTIA;
var MATCH_URL_TWITCH_VIDEO = /(?:www\.|go\.)?twitch\.tv\/videos\/(\d+)($|\?)/;
patterns.MATCH_URL_TWITCH_VIDEO = MATCH_URL_TWITCH_VIDEO;
var MATCH_URL_TWITCH_CHANNEL = /(?:www\.|go\.)?twitch\.tv\/([a-zA-Z0-9_]+)($|\?)/;
patterns.MATCH_URL_TWITCH_CHANNEL = MATCH_URL_TWITCH_CHANNEL;
var MATCH_URL_DAILYMOTION = /^(?:(?:https?):)?(?:\/\/)?(?:www\.)?(?:(?:dailymotion\.com(?:\/embed)?\/video)|dai\.ly)\/([a-zA-Z0-9]+)(?:_[\w_-]+)?(?:[\w.#_-]+)?/;
patterns.MATCH_URL_DAILYMOTION = MATCH_URL_DAILYMOTION;
var MATCH_URL_MIXCLOUD = /mixcloud\.com\/([^/]+\/[^/]+)/;
patterns.MATCH_URL_MIXCLOUD = MATCH_URL_MIXCLOUD;
var MATCH_URL_VIDYARD = /vidyard.com\/(?:watch\/)?([a-zA-Z0-9-_]+)/;
patterns.MATCH_URL_VIDYARD = MATCH_URL_VIDYARD;
var MATCH_URL_KALTURA = /^https?:\/\/[a-zA-Z]+\.kaltura.(com|org)\/p\/([0-9]+)\/sp\/([0-9]+)00\/embedIframeJs\/uiconf_id\/([0-9]+)\/partner_id\/([0-9]+)(.*)entry_id.([a-zA-Z0-9-_].*)$/;
patterns.MATCH_URL_KALTURA = MATCH_URL_KALTURA;
var AUDIO_EXTENSIONS = /\.(m4a|m4b|mp4a|mpga|mp2|mp2a|mp3|m2a|m3a|wav|weba|aac|oga|spx)($|\?)/i;
patterns.AUDIO_EXTENSIONS = AUDIO_EXTENSIONS;
var VIDEO_EXTENSIONS = /\.(mp4|og[gv]|webm|mov|m4v)(#t=[,\d+]+)?($|\?)/i;
patterns.VIDEO_EXTENSIONS = VIDEO_EXTENSIONS;
var HLS_EXTENSIONS = /\.(m3u8)($|\?)/i;
patterns.HLS_EXTENSIONS = HLS_EXTENSIONS;
var DASH_EXTENSIONS = /\.(mpd)($|\?)/i;
patterns.DASH_EXTENSIONS = DASH_EXTENSIONS;
var FLV_EXTENSIONS = /\.(flv)($|\?)/i;
patterns.FLV_EXTENSIONS = FLV_EXTENSIONS;

var canPlayFile = function canPlayFile(url) {
  if (url instanceof Array) {
    var _iterator = _createForOfIteratorHelper(url),
        _step;

    try {
      for (_iterator.s(); !(_step = _iterator.n()).done;) {
        var item = _step.value;

        if (typeof item === 'string' && canPlayFile(item)) {
          return true;
        }

        if (canPlayFile(item.src)) {
          return true;
        }
      }
    } catch (err) {
      _iterator.e(err);
    } finally {
      _iterator.f();
    }

    return false;
  }

  if ((0, _utils.isMediaStream)(url) || (0, _utils.isBlobUrl)(url)) {
    return true;
  }

  return AUDIO_EXTENSIONS.test(url) || VIDEO_EXTENSIONS.test(url) || HLS_EXTENSIONS.test(url) || DASH_EXTENSIONS.test(url) || FLV_EXTENSIONS.test(url);
};

var canPlay = {
  youtube: function youtube(url) {
    if (url instanceof Array) {
      return url.every(function (item) {
        return MATCH_URL_YOUTUBE.test(item);
      });
    }

    return MATCH_URL_YOUTUBE.test(url);
  },
  soundcloud: function soundcloud(url) {
    return MATCH_URL_SOUNDCLOUD.test(url) && !AUDIO_EXTENSIONS.test(url);
  },
  vimeo: function vimeo(url) {
    return MATCH_URL_VIMEO.test(url) && !VIDEO_EXTENSIONS.test(url) && !HLS_EXTENSIONS.test(url);
  },
  facebook: function facebook(url) {
    return MATCH_URL_FACEBOOK.test(url) || MATCH_URL_FACEBOOK_WATCH.test(url);
  },
  streamable: function streamable(url) {
    return MATCH_URL_STREAMABLE.test(url);
  },
  wistia: function wistia(url) {
    return MATCH_URL_WISTIA.test(url);
  },
  twitch: function twitch(url) {
    return MATCH_URL_TWITCH_VIDEO.test(url) || MATCH_URL_TWITCH_CHANNEL.test(url);
  },
  dailymotion: function dailymotion(url) {
    return MATCH_URL_DAILYMOTION.test(url);
  },
  mixcloud: function mixcloud(url) {
    return MATCH_URL_MIXCLOUD.test(url);
  },
  vidyard: function vidyard(url) {
    return MATCH_URL_VIDYARD.test(url);
  },
  kaltura: function kaltura(url) {
    return MATCH_URL_KALTURA.test(url);
  },
  file: canPlayFile
};
patterns.canPlay = canPlay;

(function (exports) {

	function _typeof(obj) { "@babel/helpers - typeof"; if (typeof Symbol === "function" && typeof Symbol.iterator === "symbol") { _typeof = function _typeof(obj) { return typeof obj; }; } else { _typeof = function _typeof(obj) { return obj && typeof Symbol === "function" && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj; }; } return _typeof(obj); }

	Object.defineProperty(exports, "__esModule", {
	  value: true
	});
	exports["default"] = void 0;

	var _react = _interopRequireWildcard(React);

	var _utils = utils;

	var _patterns = patterns;

	function _getRequireWildcardCache() { if (typeof WeakMap !== "function") return null; var cache = new WeakMap(); _getRequireWildcardCache = function _getRequireWildcardCache() { return cache; }; return cache; }

	function _interopRequireWildcard(obj) { if (obj && obj.__esModule) { return obj; } if (obj === null || _typeof(obj) !== "object" && typeof obj !== "function") { return { "default": obj }; } var cache = _getRequireWildcardCache(); if (cache && cache.has(obj)) { return cache.get(obj); } var newObj = {}; var hasPropertyDescriptor = Object.defineProperty && Object.getOwnPropertyDescriptor; for (var key in obj) { if (Object.prototype.hasOwnProperty.call(obj, key)) { var desc = hasPropertyDescriptor ? Object.getOwnPropertyDescriptor(obj, key) : null; if (desc && (desc.get || desc.set)) { Object.defineProperty(newObj, key, desc); } else { newObj[key] = obj[key]; } } } newObj["default"] = obj; if (cache) { cache.set(obj, newObj); } return newObj; }

	function _extends() { _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }

	function _classCallCheck(instance, Constructor) { if (!(instance instanceof Constructor)) { throw new TypeError("Cannot call a class as a function"); } }

	function _defineProperties(target, props) { for (var i = 0; i < props.length; i++) { var descriptor = props[i]; descriptor.enumerable = descriptor.enumerable || false; descriptor.configurable = true; if ("value" in descriptor) descriptor.writable = true; Object.defineProperty(target, descriptor.key, descriptor); } }

	function _createClass(Constructor, protoProps, staticProps) { if (protoProps) _defineProperties(Constructor.prototype, protoProps); if (staticProps) _defineProperties(Constructor, staticProps); return Constructor; }

	function _inherits(subClass, superClass) { if (typeof superClass !== "function" && superClass !== null) { throw new TypeError("Super expression must either be null or a function"); } subClass.prototype = Object.create(superClass && superClass.prototype, { constructor: { value: subClass, writable: true, configurable: true } }); if (superClass) _setPrototypeOf(subClass, superClass); }

	function _setPrototypeOf(o, p) { _setPrototypeOf = Object.setPrototypeOf || function _setPrototypeOf(o, p) { o.__proto__ = p; return o; }; return _setPrototypeOf(o, p); }

	function _createSuper(Derived) { var hasNativeReflectConstruct = _isNativeReflectConstruct(); return function _createSuperInternal() { var Super = _getPrototypeOf(Derived), result; if (hasNativeReflectConstruct) { var NewTarget = _getPrototypeOf(this).constructor; result = Reflect.construct(Super, arguments, NewTarget); } else { result = Super.apply(this, arguments); } return _possibleConstructorReturn(this, result); }; }

	function _possibleConstructorReturn(self, call) { if (call && (_typeof(call) === "object" || typeof call === "function")) { return call; } return _assertThisInitialized(self); }

	function _assertThisInitialized(self) { if (self === void 0) { throw new ReferenceError("this hasn't been initialised - super() hasn't been called"); } return self; }

	function _isNativeReflectConstruct() { if (typeof Reflect === "undefined" || !Reflect.construct) return false; if (Reflect.construct.sham) return false; if (typeof Proxy === "function") return true; try { Date.prototype.toString.call(Reflect.construct(Date, [], function () {})); return true; } catch (e) { return false; } }

	function _getPrototypeOf(o) { _getPrototypeOf = Object.setPrototypeOf ? Object.getPrototypeOf : function _getPrototypeOf(o) { return o.__proto__ || Object.getPrototypeOf(o); }; return _getPrototypeOf(o); }

	function _defineProperty(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

	var HAS_NAVIGATOR = typeof navigator !== 'undefined';
	var IS_IPAD_PRO = HAS_NAVIGATOR && navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
	var IS_IOS = HAS_NAVIGATOR && (/iPad|iPhone|iPod/.test(navigator.userAgent) || IS_IPAD_PRO) && !window.MSStream;
	var IS_SAFARI = HAS_NAVIGATOR && /^((?!chrome|android).)*safari/i.test(navigator.userAgent) && !window.MSStream;
	var HLS_SDK_URL = 'https://cdn.jsdelivr.net/npm/hls.js@VERSION/dist/hls.min.js';
	var HLS_GLOBAL = 'Hls';
	var DASH_SDK_URL = 'https://cdnjs.cloudflare.com/ajax/libs/dashjs/VERSION/dash.all.min.js';
	var DASH_GLOBAL = 'dashjs';
	var FLV_SDK_URL = 'https://cdn.jsdelivr.net/npm/flv.js@VERSION/dist/flv.min.js';
	var FLV_GLOBAL = 'flvjs';
	var MATCH_DROPBOX_URL = /www\.dropbox\.com\/.+/;
	var MATCH_CLOUDFLARE_STREAM = /https:\/\/watch\.cloudflarestream\.com\/([a-z0-9]+)/;
	var REPLACE_CLOUDFLARE_STREAM = 'https://videodelivery.net/{id}/manifest/video.m3u8';

	var FilePlayer = /*#__PURE__*/function (_Component) {
	  _inherits(FilePlayer, _Component);

	  var _super = _createSuper(FilePlayer);

	  function FilePlayer() {
	    var _this;

	    _classCallCheck(this, FilePlayer);

	    for (var _len = arguments.length, _args = new Array(_len), _key = 0; _key < _len; _key++) {
	      _args[_key] = arguments[_key];
	    }

	    _this = _super.call.apply(_super, [this].concat(_args));

	    _defineProperty(_assertThisInitialized(_this), "onReady", function () {
	      var _this$props;

	      return (_this$props = _this.props).onReady.apply(_this$props, arguments);
	    });

	    _defineProperty(_assertThisInitialized(_this), "onPlay", function () {
	      var _this$props2;

	      return (_this$props2 = _this.props).onPlay.apply(_this$props2, arguments);
	    });

	    _defineProperty(_assertThisInitialized(_this), "onBuffer", function () {
	      var _this$props3;

	      return (_this$props3 = _this.props).onBuffer.apply(_this$props3, arguments);
	    });

	    _defineProperty(_assertThisInitialized(_this), "onBufferEnd", function () {
	      var _this$props4;

	      return (_this$props4 = _this.props).onBufferEnd.apply(_this$props4, arguments);
	    });

	    _defineProperty(_assertThisInitialized(_this), "onPause", function () {
	      var _this$props5;

	      return (_this$props5 = _this.props).onPause.apply(_this$props5, arguments);
	    });

	    _defineProperty(_assertThisInitialized(_this), "onEnded", function () {
	      var _this$props6;

	      return (_this$props6 = _this.props).onEnded.apply(_this$props6, arguments);
	    });

	    _defineProperty(_assertThisInitialized(_this), "onError", function () {
	      var _this$props7;

	      return (_this$props7 = _this.props).onError.apply(_this$props7, arguments);
	    });

	    _defineProperty(_assertThisInitialized(_this), "onPlayBackRateChange", function (event) {
	      return _this.props.onPlaybackRateChange(event.target.playbackRate);
	    });

	    _defineProperty(_assertThisInitialized(_this), "onEnablePIP", function () {
	      var _this$props8;

	      return (_this$props8 = _this.props).onEnablePIP.apply(_this$props8, arguments);
	    });

	    _defineProperty(_assertThisInitialized(_this), "onDisablePIP", function (e) {
	      var _this$props9 = _this.props,
	          onDisablePIP = _this$props9.onDisablePIP,
	          playing = _this$props9.playing;
	      onDisablePIP(e);

	      if (playing) {
	        _this.play();
	      }
	    });

	    _defineProperty(_assertThisInitialized(_this), "onPresentationModeChange", function (e) {
	      if (_this.player && (0, _utils.supportsWebKitPresentationMode)(_this.player)) {
	        var webkitPresentationMode = _this.player.webkitPresentationMode;

	        if (webkitPresentationMode === 'picture-in-picture') {
	          _this.onEnablePIP(e);
	        } else if (webkitPresentationMode === 'inline') {
	          _this.onDisablePIP(e);
	        }
	      }
	    });

	    _defineProperty(_assertThisInitialized(_this), "onSeek", function (e) {
	      _this.props.onSeek(e.target.currentTime);
	    });

	    _defineProperty(_assertThisInitialized(_this), "mute", function () {
	      _this.player.muted = true;
	    });

	    _defineProperty(_assertThisInitialized(_this), "unmute", function () {
	      _this.player.muted = false;
	    });

	    _defineProperty(_assertThisInitialized(_this), "renderSourceElement", function (source, index) {
	      if (typeof source === 'string') {
	        return /*#__PURE__*/_react["default"].createElement("source", {
	          key: index,
	          src: source
	        });
	      }

	      return /*#__PURE__*/_react["default"].createElement("source", _extends({
	        key: index
	      }, source));
	    });

	    _defineProperty(_assertThisInitialized(_this), "renderTrack", function (track, index) {
	      return /*#__PURE__*/_react["default"].createElement("track", _extends({
	        key: index
	      }, track));
	    });

	    _defineProperty(_assertThisInitialized(_this), "ref", function (player) {
	      if (_this.player) {
	        // Store previous player to be used by removeListeners()
	        _this.prevPlayer = _this.player;
	      }

	      _this.player = player;
	    });

	    return _this;
	  }

	  _createClass(FilePlayer, [{
	    key: "componentDidMount",
	    value: function componentDidMount() {
	      this.props.onMount && this.props.onMount(this);
	      this.addListeners(this.player);
	      var src = this.getSource(this.props.url); // Ensure src is set in strict mode

	      if (src) {
	        this.player.src = src;
	      }

	      if (IS_IOS || this.props.config.forceDisableHls) {
	        this.player.load();
	      }
	    }
	  }, {
	    key: "componentDidUpdate",
	    value: function componentDidUpdate(prevProps) {
	      if (this.shouldUseAudio(this.props) !== this.shouldUseAudio(prevProps)) {
	        this.removeListeners(this.prevPlayer, prevProps.url);
	        this.addListeners(this.player);
	      }

	      if (this.props.url !== prevProps.url && !(0, _utils.isMediaStream)(this.props.url) && !(this.props.url instanceof Array) // Avoid infinite loop
	      ) {
	          this.player.srcObject = null;
	        }
	    }
	  }, {
	    key: "componentWillUnmount",
	    value: function componentWillUnmount() {
	      this.player.removeAttribute('src');
	      this.removeListeners(this.player);

	      if (this.hls) {
	        this.hls.destroy();
	      }
	    }
	  }, {
	    key: "addListeners",
	    value: function addListeners(player) {
	      var _this$props10 = this.props,
	          url = _this$props10.url,
	          playsinline = _this$props10.playsinline;
	      player.addEventListener('play', this.onPlay);
	      player.addEventListener('waiting', this.onBuffer);
	      player.addEventListener('playing', this.onBufferEnd);
	      player.addEventListener('pause', this.onPause);
	      player.addEventListener('seeked', this.onSeek);
	      player.addEventListener('ended', this.onEnded);
	      player.addEventListener('error', this.onError);
	      player.addEventListener('ratechange', this.onPlayBackRateChange);
	      player.addEventListener('enterpictureinpicture', this.onEnablePIP);
	      player.addEventListener('leavepictureinpicture', this.onDisablePIP);
	      player.addEventListener('webkitpresentationmodechanged', this.onPresentationModeChange);

	      if (!this.shouldUseHLS(url)) {
	        // onReady is handled by hls.js
	        player.addEventListener('canplay', this.onReady);
	      }

	      if (playsinline) {
	        player.setAttribute('playsinline', '');
	        player.setAttribute('webkit-playsinline', '');
	        player.setAttribute('x5-playsinline', '');
	      }
	    }
	  }, {
	    key: "removeListeners",
	    value: function removeListeners(player, url) {
	      player.removeEventListener('canplay', this.onReady);
	      player.removeEventListener('play', this.onPlay);
	      player.removeEventListener('waiting', this.onBuffer);
	      player.removeEventListener('playing', this.onBufferEnd);
	      player.removeEventListener('pause', this.onPause);
	      player.removeEventListener('seeked', this.onSeek);
	      player.removeEventListener('ended', this.onEnded);
	      player.removeEventListener('error', this.onError);
	      player.removeEventListener('ratechange', this.onPlayBackRateChange);
	      player.removeEventListener('enterpictureinpicture', this.onEnablePIP);
	      player.removeEventListener('leavepictureinpicture', this.onDisablePIP);
	      player.removeEventListener('webkitpresentationmodechanged', this.onPresentationModeChange);

	      if (!this.shouldUseHLS(url)) {
	        // onReady is handled by hls.js
	        player.removeEventListener('canplay', this.onReady);
	      }
	    } // Proxy methods to prevent listener leaks

	  }, {
	    key: "shouldUseAudio",
	    value: function shouldUseAudio(props) {
	      if (props.config.forceVideo) {
	        return false;
	      }

	      if (props.config.attributes.poster) {
	        return false; // Use <video> so that poster is shown
	      }

	      return _patterns.AUDIO_EXTENSIONS.test(props.url) || props.config.forceAudio;
	    }
	  }, {
	    key: "shouldUseHLS",
	    value: function shouldUseHLS(url) {
	      if (IS_SAFARI && this.props.config.forceSafariHLS || this.props.config.forceHLS) {
	        return true;
	      }

	      if (IS_IOS || this.props.config.forceDisableHls) {
	        return false;
	      }

	      return _patterns.HLS_EXTENSIONS.test(url) || MATCH_CLOUDFLARE_STREAM.test(url);
	    }
	  }, {
	    key: "shouldUseDASH",
	    value: function shouldUseDASH(url) {
	      return _patterns.DASH_EXTENSIONS.test(url) || this.props.config.forceDASH;
	    }
	  }, {
	    key: "shouldUseFLV",
	    value: function shouldUseFLV(url) {
	      return _patterns.FLV_EXTENSIONS.test(url) || this.props.config.forceFLV;
	    }
	  }, {
	    key: "load",
	    value: function load(url) {
	      var _this2 = this;

	      var _this$props$config = this.props.config,
	          hlsVersion = _this$props$config.hlsVersion,
	          hlsOptions = _this$props$config.hlsOptions,
	          dashVersion = _this$props$config.dashVersion,
	          flvVersion = _this$props$config.flvVersion;

	      if (this.hls) {
	        this.hls.destroy();
	      }

	      if (this.dash) {
	        this.dash.reset();
	      }

	      if (this.shouldUseHLS(url)) {
	        (0, _utils.getSDK)(HLS_SDK_URL.replace('VERSION', hlsVersion), HLS_GLOBAL).then(function (Hls) {
	          _this2.hls = new Hls(hlsOptions);

	          _this2.hls.on(Hls.Events.MANIFEST_PARSED, function () {
	            _this2.props.onReady();
	          });

	          _this2.hls.on(Hls.Events.ERROR, function (e, data) {
	            _this2.props.onError(e, data, _this2.hls, Hls);
	          });

	          if (MATCH_CLOUDFLARE_STREAM.test(url)) {
	            var id = url.match(MATCH_CLOUDFLARE_STREAM)[1];

	            _this2.hls.loadSource(REPLACE_CLOUDFLARE_STREAM.replace('{id}', id));
	          } else {
	            _this2.hls.loadSource(url);
	          }

	          _this2.hls.attachMedia(_this2.player);

	          _this2.props.onLoaded();
	        });
	      }

	      if (this.shouldUseDASH(url)) {
	        (0, _utils.getSDK)(DASH_SDK_URL.replace('VERSION', dashVersion), DASH_GLOBAL).then(function (dashjs) {
	          _this2.dash = dashjs.MediaPlayer().create();

	          _this2.dash.initialize(_this2.player, url, _this2.props.playing);

	          _this2.dash.on('error', _this2.props.onError);

	          if (parseInt(dashVersion) < 3) {
	            _this2.dash.getDebug().setLogToBrowserConsole(false);
	          } else {
	            _this2.dash.updateSettings({
	              debug: {
	                logLevel: dashjs.Debug.LOG_LEVEL_NONE
	              }
	            });
	          }

	          _this2.props.onLoaded();
	        });
	      }

	      if (this.shouldUseFLV(url)) {
	        (0, _utils.getSDK)(FLV_SDK_URL.replace('VERSION', flvVersion), FLV_GLOBAL).then(function (flvjs) {
	          _this2.flv = flvjs.createPlayer({
	            type: 'flv',
	            url: url
	          });

	          _this2.flv.attachMediaElement(_this2.player);

	          _this2.flv.on(flvjs.Events.ERROR, function (e, data) {
	            _this2.props.onError(e, data, _this2.flv, flvjs);
	          });

	          _this2.flv.load();

	          _this2.props.onLoaded();
	        });
	      }

	      if (url instanceof Array) {
	        // When setting new urls (<source>) on an already loaded video,
	        // HTMLMediaElement.load() is needed to reset the media element
	        // and restart the media resource. Just replacing children source
	        // dom nodes is not enough
	        this.player.load();
	      } else if ((0, _utils.isMediaStream)(url)) {
	        try {
	          this.player.srcObject = url;
	        } catch (e) {
	          this.player.src = window.URL.createObjectURL(url);
	        }
	      }
	    }
	  }, {
	    key: "play",
	    value: function play() {
	      var promise = this.player.play();

	      if (promise) {
	        promise["catch"](this.props.onError);
	      }
	    }
	  }, {
	    key: "pause",
	    value: function pause() {
	      this.player.pause();
	    }
	  }, {
	    key: "stop",
	    value: function stop() {
	      this.player.removeAttribute('src');

	      if (this.dash) {
	        this.dash.reset();
	      }
	    }
	  }, {
	    key: "seekTo",
	    value: function seekTo(seconds) {
	      var keepPlaying = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : true;
	      this.player.currentTime = seconds;

	      if (!keepPlaying) {
	        this.pause();
	      }
	    }
	  }, {
	    key: "setVolume",
	    value: function setVolume(fraction) {
	      this.player.volume = fraction;
	    }
	  }, {
	    key: "enablePIP",
	    value: function enablePIP() {
	      if (this.player.requestPictureInPicture && document.pictureInPictureElement !== this.player) {
	        this.player.requestPictureInPicture();
	      } else if ((0, _utils.supportsWebKitPresentationMode)(this.player) && this.player.webkitPresentationMode !== 'picture-in-picture') {
	        this.player.webkitSetPresentationMode('picture-in-picture');
	      }
	    }
	  }, {
	    key: "disablePIP",
	    value: function disablePIP() {
	      if (document.exitPictureInPicture && document.pictureInPictureElement === this.player) {
	        document.exitPictureInPicture();
	      } else if ((0, _utils.supportsWebKitPresentationMode)(this.player) && this.player.webkitPresentationMode !== 'inline') {
	        this.player.webkitSetPresentationMode('inline');
	      }
	    }
	  }, {
	    key: "setPlaybackRate",
	    value: function setPlaybackRate(rate) {
	      try {
	        this.player.playbackRate = rate;
	      } catch (error) {
	        this.props.onError(error);
	      }
	    }
	  }, {
	    key: "getDuration",
	    value: function getDuration() {
	      if (!this.player) return null;
	      var _this$player = this.player,
	          duration = _this$player.duration,
	          seekable = _this$player.seekable; // on iOS, live streams return Infinity for the duration
	      // so instead we use the end of the seekable timerange

	      if (duration === Infinity && seekable.length > 0) {
	        return seekable.end(seekable.length - 1);
	      }

	      return duration;
	    }
	  }, {
	    key: "getCurrentTime",
	    value: function getCurrentTime() {
	      if (!this.player) return null;
	      return this.player.currentTime;
	    }
	  }, {
	    key: "getSecondsLoaded",
	    value: function getSecondsLoaded() {
	      if (!this.player) return null;
	      var buffered = this.player.buffered;

	      if (buffered.length === 0) {
	        return 0;
	      }

	      var end = buffered.end(buffered.length - 1);
	      var duration = this.getDuration();

	      if (end > duration) {
	        return duration;
	      }

	      return end;
	    }
	  }, {
	    key: "getSource",
	    value: function getSource(url) {
	      var useHLS = this.shouldUseHLS(url);
	      var useDASH = this.shouldUseDASH(url);
	      var useFLV = this.shouldUseFLV(url);

	      if (url instanceof Array || (0, _utils.isMediaStream)(url) || useHLS || useDASH || useFLV) {
	        return undefined;
	      }

	      if (MATCH_DROPBOX_URL.test(url)) {
	        return url.replace('www.dropbox.com', 'dl.dropboxusercontent.com');
	      }

	      return url;
	    }
	  }, {
	    key: "render",
	    value: function render() {
	      var _this$props11 = this.props,
	          url = _this$props11.url,
	          playing = _this$props11.playing,
	          loop = _this$props11.loop,
	          controls = _this$props11.controls,
	          muted = _this$props11.muted,
	          config = _this$props11.config,
	          width = _this$props11.width,
	          height = _this$props11.height;
	      var useAudio = this.shouldUseAudio(this.props);
	      var Element = useAudio ? 'audio' : 'video';
	      var style = {
	        width: width === 'auto' ? width : '100%',
	        height: height === 'auto' ? height : '100%'
	      };
	      return /*#__PURE__*/_react["default"].createElement(Element, _extends({
	        ref: this.ref,
	        src: this.getSource(url),
	        style: style,
	        preload: "auto",
	        autoPlay: playing || undefined,
	        controls: controls,
	        muted: muted,
	        loop: loop
	      }, config.attributes), url instanceof Array && url.map(this.renderSourceElement), config.tracks.map(this.renderTrack));
	    }
	  }]);

	  return FilePlayer;
	}(_react.Component);

	exports["default"] = FilePlayer;

	_defineProperty(FilePlayer, "displayName", 'FilePlayer');

	_defineProperty(FilePlayer, "canPlay", _patterns.canPlay.file);
} (FilePlayer));

var createReactPlayer = ReactPlayer.createReactPlayer;
      var Player = FilePlayer.default;
      var file = createReactPlayer([{
        key: 'file',
        canPlay: Player.canPlay,
        lazyPlayer: Player
      }]);

var PlayerWrapper = styled.div.attrs(function (_a) {
    var ref = _a.ref;
    return ({
        ref: ref,
    });
})(templateObject_1$X || (templateObject_1$X = __makeTemplateObject(["\n  margin-left: auto;\n  margin-right: auto;\n  position: relative;\n  padding-top: 56.25%; /* Player ratio: 100 / (1280 / 720) */\n"], ["\n  margin-left: auto;\n  margin-right: auto;\n  position: relative;\n  padding-top: 56.25%; /* Player ratio: 100 / (1280 / 720) */\n"])));
var templateObject_1$X;

var VideoPlayer = function (_a) {
    var url = _a.url;
    var _b = useState(false), clientRender = _b[0], setClientRender = _b[1];
    var _c = useState(false), playing = _c[0], setPLaying = _c[1];
    var playerRef = useRef(null);
    var observerCallback = function (entries) {
        var video = entries[0];
        if (video.intersectionRatio <= 0)
            return;
        setPLaying(video.isIntersecting);
    };
    useEffect(function () {
        setClientRender(true);
    }, []);
    useEffect(function () {
        if (!playerRef.current)
            return;
        var intersectionObserver = new IntersectionObserver(observerCallback, { threshold: 0.1 });
        intersectionObserver.observe(playerRef.current);
        return function () {
            if (playerRef.current) {
                intersectionObserver.unobserve(playerRef.current);
            }
        };
    }, [playerRef]);
    return (jsx(PlayerWrapper, __assign$1({ ref: playerRef }, { children: clientRender && url && (jsx(file, { playing: playing, url: url, muted: true, playsinline: true, controls: true, width: "100%", height: "100%", style: { position: 'absolute', top: 0, left: 0 }, config: { attributes: { controlsList: 'nodownload' } } })) })));
};

var Accordion$1 = styled.div(templateObject_2$Q || (templateObject_2$Q = __makeTemplateObject(["\n  box-shadow: none;\n  min-height: auto;\n  ", "\n"], ["\n  box-shadow: none;\n  min-height: auto;\n  ", "\n"])), function (_a) {
    var variant = _a.variant;
    return variant === 'footer' && css(templateObject_1$W || (templateObject_1$W = __makeTemplateObject(["\n      background: ", ";\n    "], ["\n      background: ", ";\n    "])), function (_a) {
        var theme = _a.theme;
        return theme.colors.accordion.backgroundColor;
    });
});
var AccordionSummary = styled.div(templateObject_4$y || (templateObject_4$y = __makeTemplateObject(["\n  display: flex;\n  flex-direction: ", ";\n  justify-content: ", ";\n  align-items: center;\n  cursor: pointer;\n  ", "\n\n  svg {\n    display: block;\n  }\n"], ["\n  display: flex;\n  flex-direction: ", ";\n  justify-content: ", ";\n  align-items: center;\n  cursor: pointer;\n  ", "\n\n  svg {\n    display: block;\n  }\n"])), function (_a) {
    var reversed = _a.reversed;
    return (reversed ? 'row-reverse' : 'row');
}, function (_a) {
    var reversed = _a.reversed;
    return (reversed ? 'flex-end' : 'space-between');
}, function (_a) {
    var variant = _a.variant;
    return variant === 'footer' && css(templateObject_3$H || (templateObject_3$H = __makeTemplateObject(["\n      padding: 17px 0;\n    "], ["\n      padding: 17px 0;\n    "])));
});
var AccordionDetails = styled.div(templateObject_7$l || (templateObject_7$l = __makeTemplateObject(["\n  max-height: ", ";\n  transition: max-height 0.15s ease-in-out, height 0.15s ease-in-out;\n  height: ", ";\n  overflow: hidden;\n  > div {\n    padding: 16px 24px 0;\n    ", "\n    ", "\n  }\n"], ["\n  max-height: ", ";\n  transition: max-height 0.15s ease-in-out, height 0.15s ease-in-out;\n  height: ", ";\n  overflow: hidden;\n  > div {\n    padding: 16px 24px 0;\n    ", "\n    ", "\n  }\n"])), function (_a) {
    var open = _a.open, maxHeight = _a.maxHeight;
    return (open ? "".concat(maxHeight, "px") : '0');
}, function (_a) {
    var open = _a.open, maxHeight = _a.maxHeight;
    return (open ? "".concat(maxHeight, "px") : '0px');
}, function (_a) {
    var variant = _a.variant;
    return variant === 'footer' && css(templateObject_5$s || (templateObject_5$s = __makeTemplateObject(["\n        padding: 0;\n      "], ["\n        padding: 0;\n      "])));
}, function (_a) {
    var variant = _a.variant;
    return variant === 'shortFaq' && css(templateObject_6$p || (templateObject_6$p = __makeTemplateObject(["\n        padding: 16px 0 0;\n      "], ["\n        padding: 16px 0 0;\n      "])));
});
var IconContainer = styled.button(templateObject_8$e || (templateObject_8$e = __makeTemplateObject(["\n  display: flex;\n  justify-content: center;\n  align-items: center;\n  transform: ", ";\n  transition: transform 0.15s ease-in-out;\n  ", ";\n  background-color: transparent;\n  border: 0;\n"], ["\n  display: flex;\n  justify-content: center;\n  align-items: center;\n  transform: ", ";\n  transition: transform 0.15s ease-in-out;\n  ", ";\n  background-color: transparent;\n  border: 0;\n"])), function (_a) {
    var open = _a.open;
    return (open ? 'rotateZ(180deg)' : 'rotateZ(0deg)');
}, function (_a) {
    var reversed = _a.reversed;
    return (reversed ? 'margin-right: 24px' : 'margin-left: 24px');
});
var HeaderWithIconWrapper = styled.div(templateObject_9$c || (templateObject_9$c = __makeTemplateObject(["\n  display: flex;\n  width: 100%;\n"], ["\n  display: flex;\n  width: 100%;\n"])));
var HeaderTitleWrapper = styled.div(templateObject_10$8 || (templateObject_10$8 = __makeTemplateObject(["\n  display: flex;\n  justify-content: space-between;\n  width: 100%;\n"], ["\n  display: flex;\n  justify-content: space-between;\n  width: 100%;\n"])));
var AlertText = styled(Header7)(templateObject_11$6 || (templateObject_11$6 = __makeTemplateObject(["\n  color: ", ";\n  margin-right: 24px;\n"], ["\n  color: ", ";\n  margin-right: 24px;\n"])), function (_a) {
    var theme = _a.theme;
    return theme.crediteaColors.accent.red[500];
});
var templateObject_1$W, templateObject_2$Q, templateObject_3$H, templateObject_4$y, templateObject_5$s, templateObject_6$p, templateObject_7$l, templateObject_8$e, templateObject_9$c, templateObject_10$8, templateObject_11$6;

var AccordionHeaderTitle = function (_a) {
    var title = _a.title, color = _a.color, alert = _a.alert, variant = _a.variant;
    return (jsxs(HeaderTitleWrapper, { children: [variant === 'footer' ? (jsx(Header7, __assign$1({ color: color }, { children: title }))) : (jsx(Caption2$2, __assign$1({ color: color }, { children: title }))), alert && jsx(AlertText, { children: alert })] }));
};
var AccordionHeaderWithIcon = function (_a) {
    var title = _a.title, icon = _a.icon, color = _a.color, alert = _a.alert;
    return (jsxs(HeaderWithIconWrapper, { children: [icon && jsx(Icon$4, { name: icon, width: 26, height: 26, fillColor: color }), jsx(Gutter, { size: 12 }), jsx(AccordionHeaderTitle, { color: color, title: title, alert: alert })] }));
};
var AccordionHeader = function (_a) {
    var title = _a.title, icon = _a.icon, color = _a.color, alert = _a.alert, variant = _a.variant;
    return icon ? (jsx(AccordionHeaderWithIcon, { icon: icon, title: title, color: color, alert: alert })) : (jsx(AccordionHeaderTitle, { title: title, color: color, alert: alert, variant: variant }));
};

var Accordion = function (_a) {
    var title = _a.title, icon = _a.icon, children = _a.children, variant = _a.variant, reversed = _a.reversed, color = _a.color, alert = _a.alert;
    var _b = useState(false), open = _b[0], setOpen = _b[1];
    var _c = useState(0), maxHeight = _c[0], setMaxHeight = _c[1];
    var accordionDetailsRef = useRef(null);
    var setAccordionHeight = function () {
        var _a;
        var accordionDetailsElements = (_a = accordionDetailsRef.current) === null || _a === void 0 ? void 0 : _a.childNodes;
        if (accordionDetailsElements) {
            var height_1 = 0;
            accordionDetailsElements.forEach(function (element) {
                height_1 += element.scrollHeight;
            });
            setMaxHeight(height_1);
        }
    };
    useEffect(function () {
        setAccordionHeight();
        window.addEventListener('resize', setAccordionHeight);
        return function () { return window.removeEventListener('resize', setAccordionHeight); };
    }, [accordionDetailsRef]);
    var toggleAccordion = function () {
        setOpen(!open);
    };
    return (jsxs(Accordion$1, __assign$1({ variant: variant, open: open }, { children: [jsxs(AccordionSummary, __assign$1({ variant: variant, onClick: toggleAccordion, reversed: reversed }, { children: [jsx(AccordionHeader, { icon: icon, title: title, color: color, alert: alert, variant: variant }), jsx(IconContainer, __assign$1({ open: open, reversed: reversed, "aria-label": "Open accordion ".concat(title) }, { children: jsx(Icon$4, { name: "ArrowDown", width: 24, fillColor: color }) }))] })), jsx(AccordionDetails, __assign$1({ ref: accordionDetailsRef, maxHeight: maxHeight, open: open, variant: variant }, { children: jsx("div", { children: children }) }))] })));
};

function useDelayUnmount(isMounted) {
    var _a = useState(false), showDiv = _a[0], setShowDiv = _a[1];
    useEffect(function () {
        var timeoutId;
        if (isMounted && !showDiv) {
            setShowDiv(true);
        }
        else if (!isMounted && showDiv) {
            timeoutId = setTimeout(function () { return setShowDiv(false); }, 500);
        }
        return function () { return clearTimeout(timeoutId); };
    }, [isMounted, showDiv]);
    return showDiv;
}

function hasCurrentProperty(obj) {
    return obj && 'current' in obj;
}
var getRefElement = function (element) {
    if (element && hasCurrentProperty(element)) {
        return element.current;
    }
    return element;
};

var _a;
var isSSR = !(typeof window !== 'undefined' && ((_a = window.document) === null || _a === void 0 ? void 0 : _a.createElement));

var useEventListener = function (_a) {
    var type = _a.type, listener = _a.listener, _b = _a.element, element = _b === void 0 ? isSSR ? undefined : window : _b, options = _a.options;
    var savedListener = useRef();
    useEffect(function () {
        savedListener.current = listener;
    }, [listener]);
    var handleEventListener = useCallback(function (event) {
        var _a;
        (_a = savedListener.current) === null || _a === void 0 ? void 0 : _a.call(savedListener, event);
    }, []);
    useEffect(function () {
        var target = getRefElement(element);
        target === null || target === void 0 ? void 0 : target.addEventListener(type, handleEventListener, options);
        return function () { return target === null || target === void 0 ? void 0 : target.removeEventListener(type, handleEventListener); };
    }, [type, element, options, handleEventListener]);
};

var useIsScreenSize = function () {
    var _a = useState(BREAKPOINT_VALUES.xl), width = _a[0], setWidth = _a[1];
    var handleWindowSizeChange = function () {
        setWidth(window.innerWidth);
    };
    useEffect(function () {
        setTimeout(handleWindowSizeChange);
        window.addEventListener('resize', handleWindowSizeChange);
        return function () {
            window.removeEventListener('resize', handleWindowSizeChange);
        };
    }, []);
    var isScreenXS = width >= 0 && width < BREAKPOINT_VALUES.sm;
    var isScreenSM = width >= BREAKPOINT_VALUES.sm && width < BREAKPOINT_VALUES.md;
    var isScreenMD = width >= BREAKPOINT_VALUES.md && width < BREAKPOINT_VALUES.lg;
    var isScreenLG = width >= BREAKPOINT_VALUES.lg && width < BREAKPOINT_VALUES.xl;
    var isScreenXL = width >= BREAKPOINT_VALUES.xl;
    var isScreenDesktop = isScreenLG || isScreenXL;
    var isScreenAboveSM = isScreenMD || isScreenLG || isScreenXL;
    return {
        isScreenXS: isScreenXS,
        isScreenSM: isScreenSM,
        isScreenMD: isScreenMD,
        isScreenLG: isScreenLG,
        isScreenDesktop: isScreenDesktop,
        isScreenXL: isScreenXL,
        isScreenAboveSM: isScreenAboveSM,
    };
};

/**
 * Checks if `value` is the
 * [language type](http://www.ecma-international.org/ecma-262/7.0/#sec-ecmascript-language-types)
 * of `Object`. (e.g. arrays, functions, objects, regexes, `new Number(0)`, and `new String('')`)
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is an object, else `false`.
 * @example
 *
 * _.isObject({});
 * // => true
 *
 * _.isObject([1, 2, 3]);
 * // => true
 *
 * _.isObject(_.noop);
 * // => true
 *
 * _.isObject(null);
 * // => false
 */

function isObject$4(value) {
  var type = typeof value;
  return value != null && (type == 'object' || type == 'function');
}

var isObject_1 = isObject$4;

/** Detect free variable `global` from Node.js. */

var freeGlobal$2 = typeof commonjsGlobal == 'object' && commonjsGlobal && commonjsGlobal.Object === Object && commonjsGlobal;

var _freeGlobal = freeGlobal$2;

var freeGlobal$1 = _freeGlobal;

/** Detect free variable `self`. */
var freeSelf$1 = typeof self == 'object' && self && self.Object === Object && self;

/** Used as a reference to the global object. */
var root$3 = freeGlobal$1 || freeSelf$1 || Function('return this')();

var _root = root$3;

var root$2 = _root;

/**
 * Gets the timestamp of the number of milliseconds that have elapsed since
 * the Unix epoch (1 January 1970 00:00:00 UTC).
 *
 * @static
 * @memberOf _
 * @since 2.4.0
 * @category Date
 * @returns {number} Returns the timestamp.
 * @example
 *
 * _.defer(function(stamp) {
 *   console.log(_.now() - stamp);
 * }, _.now());
 * // => Logs the number of milliseconds it took for the deferred invocation.
 */
var now$2 = function() {
  return root$2.Date.now();
};

var now_1 = now$2;

/** Used to match a single whitespace character. */

var reWhitespace = /\s/;

/**
 * Used by `_.trim` and `_.trimEnd` to get the index of the last non-whitespace
 * character of `string`.
 *
 * @private
 * @param {string} string The string to inspect.
 * @returns {number} Returns the index of the last non-whitespace character.
 */
function trimmedEndIndex$1(string) {
  var index = string.length;

  while (index-- && reWhitespace.test(string.charAt(index))) {}
  return index;
}

var _trimmedEndIndex = trimmedEndIndex$1;

var trimmedEndIndex = _trimmedEndIndex;

/** Used to match leading whitespace. */
var reTrimStart = /^\s+/;

/**
 * The base implementation of `_.trim`.
 *
 * @private
 * @param {string} string The string to trim.
 * @returns {string} Returns the trimmed string.
 */
function baseTrim$1(string) {
  return string
    ? string.slice(0, trimmedEndIndex(string) + 1).replace(reTrimStart, '')
    : string;
}

var _baseTrim = baseTrim$1;

var root$1 = _root;

/** Built-in value references. */
var Symbol$3 = root$1.Symbol;

var _Symbol = Symbol$3;

var Symbol$2 = _Symbol;

/** Used for built-in method references. */
var objectProto$2 = Object.prototype;

/** Used to check objects for own properties. */
var hasOwnProperty = objectProto$2.hasOwnProperty;

/**
 * Used to resolve the
 * [`toStringTag`](http://ecma-international.org/ecma-262/7.0/#sec-object.prototype.tostring)
 * of values.
 */
var nativeObjectToString$1 = objectProto$2.toString;

/** Built-in value references. */
var symToStringTag$1 = Symbol$2 ? Symbol$2.toStringTag : undefined;

/**
 * A specialized version of `baseGetTag` which ignores `Symbol.toStringTag` values.
 *
 * @private
 * @param {*} value The value to query.
 * @returns {string} Returns the raw `toStringTag`.
 */
function getRawTag$1(value) {
  var isOwn = hasOwnProperty.call(value, symToStringTag$1),
      tag = value[symToStringTag$1];

  try {
    value[symToStringTag$1] = undefined;
    var unmasked = true;
  } catch (e) {}

  var result = nativeObjectToString$1.call(value);
  if (unmasked) {
    if (isOwn) {
      value[symToStringTag$1] = tag;
    } else {
      delete value[symToStringTag$1];
    }
  }
  return result;
}

var _getRawTag = getRawTag$1;

/** Used for built-in method references. */

var objectProto$1 = Object.prototype;

/**
 * Used to resolve the
 * [`toStringTag`](http://ecma-international.org/ecma-262/7.0/#sec-object.prototype.tostring)
 * of values.
 */
var nativeObjectToString = objectProto$1.toString;

/**
 * Converts `value` to a string using `Object.prototype.toString`.
 *
 * @private
 * @param {*} value The value to convert.
 * @returns {string} Returns the converted string.
 */
function objectToString$2(value) {
  return nativeObjectToString.call(value);
}

var _objectToString = objectToString$2;

var Symbol$1 = _Symbol,
    getRawTag = _getRawTag,
    objectToString$1 = _objectToString;

/** `Object#toString` result references. */
var nullTag = '[object Null]',
    undefinedTag = '[object Undefined]';

/** Built-in value references. */
var symToStringTag = Symbol$1 ? Symbol$1.toStringTag : undefined;

/**
 * The base implementation of `getTag` without fallbacks for buggy environments.
 *
 * @private
 * @param {*} value The value to query.
 * @returns {string} Returns the `toStringTag`.
 */
function baseGetTag$1(value) {
  if (value == null) {
    return value === undefined ? undefinedTag : nullTag;
  }
  return (symToStringTag && symToStringTag in Object(value))
    ? getRawTag(value)
    : objectToString$1(value);
}

var _baseGetTag = baseGetTag$1;

/**
 * Checks if `value` is object-like. A value is object-like if it's not `null`
 * and has a `typeof` result of "object".
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is object-like, else `false`.
 * @example
 *
 * _.isObjectLike({});
 * // => true
 *
 * _.isObjectLike([1, 2, 3]);
 * // => true
 *
 * _.isObjectLike(_.noop);
 * // => false
 *
 * _.isObjectLike(null);
 * // => false
 */

function isObjectLike$2(value) {
  return value != null && typeof value == 'object';
}

var isObjectLike_1 = isObjectLike$2;

var baseGetTag = _baseGetTag,
    isObjectLike$1 = isObjectLike_1;

/** `Object#toString` result references. */
var symbolTag$1 = '[object Symbol]';

/**
 * Checks if `value` is classified as a `Symbol` primitive or object.
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a symbol, else `false`.
 * @example
 *
 * _.isSymbol(Symbol.iterator);
 * // => true
 *
 * _.isSymbol('abc');
 * // => false
 */
function isSymbol$2(value) {
  return typeof value == 'symbol' ||
    (isObjectLike$1(value) && baseGetTag(value) == symbolTag$1);
}

var isSymbol_1 = isSymbol$2;

var baseTrim = _baseTrim,
    isObject$3 = isObject_1,
    isSymbol$1 = isSymbol_1;

/** Used as references for various `Number` constants. */
var NAN$1 = 0 / 0;

/** Used to detect bad signed hexadecimal string values. */
var reIsBadHex$1 = /^[-+]0x[0-9a-f]+$/i;

/** Used to detect binary string values. */
var reIsBinary$1 = /^0b[01]+$/i;

/** Used to detect octal string values. */
var reIsOctal$1 = /^0o[0-7]+$/i;

/** Built-in method references without a dependency on `root`. */
var freeParseInt$1 = parseInt;

/**
 * Converts `value` to a number.
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to process.
 * @returns {number} Returns the number.
 * @example
 *
 * _.toNumber(3.2);
 * // => 3.2
 *
 * _.toNumber(Number.MIN_VALUE);
 * // => 5e-324
 *
 * _.toNumber(Infinity);
 * // => Infinity
 *
 * _.toNumber('3.2');
 * // => 3.2
 */
function toNumber$2(value) {
  if (typeof value == 'number') {
    return value;
  }
  if (isSymbol$1(value)) {
    return NAN$1;
  }
  if (isObject$3(value)) {
    var other = typeof value.valueOf == 'function' ? value.valueOf() : value;
    value = isObject$3(other) ? (other + '') : other;
  }
  if (typeof value != 'string') {
    return value === 0 ? value : +value;
  }
  value = baseTrim(value);
  var isBinary = reIsBinary$1.test(value);
  return (isBinary || reIsOctal$1.test(value))
    ? freeParseInt$1(value.slice(2), isBinary ? 2 : 8)
    : (reIsBadHex$1.test(value) ? NAN$1 : +value);
}

var toNumber_1 = toNumber$2;

var isObject$2 = isObject_1,
    now$1 = now_1,
    toNumber$1 = toNumber_1;

/** Error message constants. */
var FUNC_ERROR_TEXT$2 = 'Expected a function';

/* Built-in method references for those with the same name as other `lodash` methods. */
var nativeMax$1 = Math.max,
    nativeMin$1 = Math.min;

/**
 * Creates a debounced function that delays invoking `func` until after `wait`
 * milliseconds have elapsed since the last time the debounced function was
 * invoked. The debounced function comes with a `cancel` method to cancel
 * delayed `func` invocations and a `flush` method to immediately invoke them.
 * Provide `options` to indicate whether `func` should be invoked on the
 * leading and/or trailing edge of the `wait` timeout. The `func` is invoked
 * with the last arguments provided to the debounced function. Subsequent
 * calls to the debounced function return the result of the last `func`
 * invocation.
 *
 * **Note:** If `leading` and `trailing` options are `true`, `func` is
 * invoked on the trailing edge of the timeout only if the debounced function
 * is invoked more than once during the `wait` timeout.
 *
 * If `wait` is `0` and `leading` is `false`, `func` invocation is deferred
 * until to the next tick, similar to `setTimeout` with a timeout of `0`.
 *
 * See [David Corbacho's article](https://css-tricks.com/debouncing-throttling-explained-examples/)
 * for details over the differences between `_.debounce` and `_.throttle`.
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @category Function
 * @param {Function} func The function to debounce.
 * @param {number} [wait=0] The number of milliseconds to delay.
 * @param {Object} [options={}] The options object.
 * @param {boolean} [options.leading=false]
 *  Specify invoking on the leading edge of the timeout.
 * @param {number} [options.maxWait]
 *  The maximum time `func` is allowed to be delayed before it's invoked.
 * @param {boolean} [options.trailing=true]
 *  Specify invoking on the trailing edge of the timeout.
 * @returns {Function} Returns the new debounced function.
 * @example
 *
 * // Avoid costly calculations while the window size is in flux.
 * jQuery(window).on('resize', _.debounce(calculateLayout, 150));
 *
 * // Invoke `sendMail` when clicked, debouncing subsequent calls.
 * jQuery(element).on('click', _.debounce(sendMail, 300, {
 *   'leading': true,
 *   'trailing': false
 * }));
 *
 * // Ensure `batchLog` is invoked once after 1 second of debounced calls.
 * var debounced = _.debounce(batchLog, 250, { 'maxWait': 1000 });
 * var source = new EventSource('/stream');
 * jQuery(source).on('message', debounced);
 *
 * // Cancel the trailing debounced invocation.
 * jQuery(window).on('popstate', debounced.cancel);
 */
function debounce$2(func, wait, options) {
  var lastArgs,
      lastThis,
      maxWait,
      result,
      timerId,
      lastCallTime,
      lastInvokeTime = 0,
      leading = false,
      maxing = false,
      trailing = true;

  if (typeof func != 'function') {
    throw new TypeError(FUNC_ERROR_TEXT$2);
  }
  wait = toNumber$1(wait) || 0;
  if (isObject$2(options)) {
    leading = !!options.leading;
    maxing = 'maxWait' in options;
    maxWait = maxing ? nativeMax$1(toNumber$1(options.maxWait) || 0, wait) : maxWait;
    trailing = 'trailing' in options ? !!options.trailing : trailing;
  }

  function invokeFunc(time) {
    var args = lastArgs,
        thisArg = lastThis;

    lastArgs = lastThis = undefined;
    lastInvokeTime = time;
    result = func.apply(thisArg, args);
    return result;
  }

  function leadingEdge(time) {
    // Reset any `maxWait` timer.
    lastInvokeTime = time;
    // Start the timer for the trailing edge.
    timerId = setTimeout(timerExpired, wait);
    // Invoke the leading edge.
    return leading ? invokeFunc(time) : result;
  }

  function remainingWait(time) {
    var timeSinceLastCall = time - lastCallTime,
        timeSinceLastInvoke = time - lastInvokeTime,
        timeWaiting = wait - timeSinceLastCall;

    return maxing
      ? nativeMin$1(timeWaiting, maxWait - timeSinceLastInvoke)
      : timeWaiting;
  }

  function shouldInvoke(time) {
    var timeSinceLastCall = time - lastCallTime,
        timeSinceLastInvoke = time - lastInvokeTime;

    // Either this is the first call, activity has stopped and we're at the
    // trailing edge, the system time has gone backwards and we're treating
    // it as the trailing edge, or we've hit the `maxWait` limit.
    return (lastCallTime === undefined || (timeSinceLastCall >= wait) ||
      (timeSinceLastCall < 0) || (maxing && timeSinceLastInvoke >= maxWait));
  }

  function timerExpired() {
    var time = now$1();
    if (shouldInvoke(time)) {
      return trailingEdge(time);
    }
    // Restart the timer.
    timerId = setTimeout(timerExpired, remainingWait(time));
  }

  function trailingEdge(time) {
    timerId = undefined;

    // Only invoke if we have `lastArgs` which means `func` has been
    // debounced at least once.
    if (trailing && lastArgs) {
      return invokeFunc(time);
    }
    lastArgs = lastThis = undefined;
    return result;
  }

  function cancel() {
    if (timerId !== undefined) {
      clearTimeout(timerId);
    }
    lastInvokeTime = 0;
    lastArgs = lastCallTime = lastThis = timerId = undefined;
  }

  function flush() {
    return timerId === undefined ? result : trailingEdge(now$1());
  }

  function debounced() {
    var time = now$1(),
        isInvoking = shouldInvoke(time);

    lastArgs = arguments;
    lastThis = this;
    lastCallTime = time;

    if (isInvoking) {
      if (timerId === undefined) {
        return leadingEdge(lastCallTime);
      }
      if (maxing) {
        // Handle invocations in a tight loop.
        clearTimeout(timerId);
        timerId = setTimeout(timerExpired, wait);
        return invokeFunc(lastCallTime);
      }
    }
    if (timerId === undefined) {
      timerId = setTimeout(timerExpired, wait);
    }
    return result;
  }
  debounced.cancel = cancel;
  debounced.flush = flush;
  return debounced;
}

var debounce_1 = debounce$2;

var debounce$1 = debounce_1,
    isObject$1 = isObject_1;

/** Error message constants. */
var FUNC_ERROR_TEXT$1 = 'Expected a function';

/**
 * Creates a throttled function that only invokes `func` at most once per
 * every `wait` milliseconds. The throttled function comes with a `cancel`
 * method to cancel delayed `func` invocations and a `flush` method to
 * immediately invoke them. Provide `options` to indicate whether `func`
 * should be invoked on the leading and/or trailing edge of the `wait`
 * timeout. The `func` is invoked with the last arguments provided to the
 * throttled function. Subsequent calls to the throttled function return the
 * result of the last `func` invocation.
 *
 * **Note:** If `leading` and `trailing` options are `true`, `func` is
 * invoked on the trailing edge of the timeout only if the throttled function
 * is invoked more than once during the `wait` timeout.
 *
 * If `wait` is `0` and `leading` is `false`, `func` invocation is deferred
 * until to the next tick, similar to `setTimeout` with a timeout of `0`.
 *
 * See [David Corbacho's article](https://css-tricks.com/debouncing-throttling-explained-examples/)
 * for details over the differences between `_.throttle` and `_.debounce`.
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @category Function
 * @param {Function} func The function to throttle.
 * @param {number} [wait=0] The number of milliseconds to throttle invocations to.
 * @param {Object} [options={}] The options object.
 * @param {boolean} [options.leading=true]
 *  Specify invoking on the leading edge of the timeout.
 * @param {boolean} [options.trailing=true]
 *  Specify invoking on the trailing edge of the timeout.
 * @returns {Function} Returns the new throttled function.
 * @example
 *
 * // Avoid excessively updating the position while scrolling.
 * jQuery(window).on('scroll', _.throttle(updatePosition, 100));
 *
 * // Invoke `renewToken` when the click event is fired, but not more than once every 5 minutes.
 * var throttled = _.throttle(renewToken, 300000, { 'trailing': false });
 * jQuery(element).on('click', throttled);
 *
 * // Cancel the trailing throttled invocation.
 * jQuery(window).on('popstate', throttled.cancel);
 */
function throttle$1(func, wait, options) {
  var leading = true,
      trailing = true;

  if (typeof func != 'function') {
    throw new TypeError(FUNC_ERROR_TEXT$1);
  }
  if (isObject$1(options)) {
    leading = 'leading' in options ? !!options.leading : leading;
    trailing = 'trailing' in options ? !!options.trailing : trailing;
  }
  return debounce$1(func, wait, {
    'leading': leading,
    'maxWait': wait,
    'trailing': trailing
  });
}

var throttle_1 = throttle$1;

var useScroll = function (wait) {
    if (wait === void 0) { wait = 25; }
    var _a = useState({
        y: 0,
        direction: undefined,
    }), scroll = _a[0], setScroll = _a[1];
    var scrollFunc = useCallback(function () {
        var pageYOffset = window.pageYOffset;
        var setDirection = function (prev) {
            if (prev.y !== undefined) {
                return prev.y > pageYOffset ? 'up' : 'down';
            }
            return undefined;
        };
        setScroll(function (prev) { return ({
            y: pageYOffset,
            direction: setDirection(prev),
        }); });
    }, []);
    var handleScroll = useMemo(function () { return (wait !== 0 ? throttle_1(function () { return scrollFunc(); }, wait) : function () { return scrollFunc(); }); }, [wait, scrollFunc]);
    useEventListener({
        type: 'scroll',
        listener: handleScroll,
        options: { passive: true },
    });
    return scroll;
};

var useSnackbar = function () {
    var _a = useContext(SnackbarContext), openSnackbar = _a.openSnackbar, closeSnackbar = _a.closeSnackbar;
    return [openSnackbar, closeSnackbar];
};

function slugifySpanish(text) {
    return text
        .toString()
        .toLowerCase()
        .normalize('NFD') // normalize string
        .replace(/[\u0300-\u036f]/g, '') // remove diacritics (accents)
        .replace(/ñ/g, 'n') // replace ñ with n
        .replace(/\s+/g, '-') // replace spaces with -
        .replace(/[^\w\-]+/g, '') // remove all non-word chars
        .replace(/\-\-+/g, '-') // replace multiple - with single -
        .replace(/^-+/, '') // trim - from start of text
        .replace(/-+$/, ''); // trim - from end of text
}
function slugifyEstonian(text) {
    return text
        .toString()
        .toLowerCase()
        .normalize('NFD') // normalize string
        .replace(/[\u0300-\u036f]/g, '') // remove diacritics (accents)
        .replace(/ä/g, 'a') // replace ä with a
        .replace(/ö/g, 'o') // replace ö with o
        .replace(/ü/g, 'u') // replace ü with u
        .replace(/õ/g, 'o') // replace õ with o
        .replace(/\s+/g, '-') // replace spaces with -
        .replace(/[^\w\-]+/g, '') // remove all non-word chars
        .replace(/\-\-+/g, '-') // replace multiple - with single -
        .replace(/^-+/, '') // trim - from start of text
        .replace(/-+$/, ''); // trim - from end of text
}
function slugifyLatvian(text) {
    return text
        .toString()
        .toLowerCase()
        .normalize('NFD') // normalize string
        .replace(/[\u0300-\u036f]/g, '') // remove diacritics (accents)
        .replace(/ā/g, 'a') // replace ā with a
        .replace(/č/g, 'c') // replace č with c
        .replace(/ē/g, 'e') // replace ē with e
        .replace(/ģ/g, 'g') // replace ģ with g
        .replace(/ī/g, 'i') // replace ī with i
        .replace(/ķ/g, 'k') // replace ķ with k
        .replace(/ļ/g, 'l') // replace ļ with l
        .replace(/ņ/g, 'n') // replace ņ with n
        .replace(/š/g, 's') // replace š with s
        .replace(/ū/g, 'u') // replace ū with u
        .replace(/ž/g, 'z') // replace ž with z
        .replace(/\s+/g, '-') // replace spaces with -
        .replace(/[^\w\-]+/g, '') // remove all non-word chars
        .replace(/\-\-+/g, '-') // replace multiple - with single -
        .replace(/^-+/, '') // trim - from start of text
        .replace(/-+$/, ''); // trim - from end of text
}
function slugifyLithuanian(text) {
    return text
        .toString()
        .toLowerCase()
        .normalize('NFD') // normalize string
        .replace(/[\u0300-\u036f]/g, '') // remove diacritics (accents)
        .replace(/ą/g, 'a') // replace ą with a
        .replace(/č/g, 'c') // replace č with c
        .replace(/ę/g, 'e') // replace ę with e
        .replace(/ė/g, 'e') // replace ė with e
        .replace(/į/g, 'i') // replace į with i
        .replace(/š/g, 's') // replace š with s
        .replace(/ų/g, 'u') // replace ų with u
        .replace(/ū/g, 'u') // replace ū with u
        .replace(/ž/g, 'z') // replace ž with z
        .replace(/\s+/g, '-') // replace spaces with -
        .replace(/[^\w\-]+/g, '') // remove all non-word chars
        .replace(/\-\-+/g, '-') // replace multiple - with single -
        .replace(/^-+/, '') // trim - from start of text
        .replace(/-+$/, ''); // trim - from end of text
}
function slugifyCzech(text) {
    return text
        .toString()
        .toLowerCase()
        .normalize('NFD') // normalize string
        .replace(/[\u0300-\u036f]/g, '') // remove diacritics (accents)
        .replace(/č/g, 'c') // replace č with c
        .replace(/ď/g, 'd') // replace ď with d
        .replace(/ě/g, 'e') // replace ě with e
        .replace(/ň/g, 'n') // replace ň with n
        .replace(/ř/g, 'r') // replace ř with r
        .replace(/š/g, 's') // replace š with s
        .replace(/ť/g, 't') // replace ť with t
        .replace(/ů/g, 'u') // replace ů with u
        .replace(/ž/g, 'z') // replace ž with z
        .replace(/\s+/g, '-') // replace spaces with -
        .replace(/[^\w\-]+/g, '') // remove all non-word chars
        .replace(/\-\-+/g, '-') // replace multiple - with single -
        .replace(/^-+/, '') // trim - from start of text
        .replace(/-+$/, ''); // trim - from end of text
}
function slugifyRussian(text) {
    var cyrillicToLatinMap = {
        а: 'a',
        б: 'b',
        в: 'v',
        г: 'g',
        д: 'd',
        е: 'e',
        ё: 'e',
        ж: 'zh',
        з: 'z',
        и: 'i',
        й: 'j',
        к: 'k',
        л: 'l',
        м: 'm',
        н: 'n',
        о: 'o',
        п: 'p',
        р: 'r',
        с: 's',
        т: 't',
        у: 'u',
        ф: 'f',
        х: 'h',
        ц: 'c',
        ч: 'ch',
        ш: 'sh',
        щ: 'shch',
        ы: 'y',
        э: 'e',
        ю: 'yu',
        я: 'ya',
        А: 'A',
        Б: 'B',
        В: 'V',
        Г: 'G',
        Д: 'D',
        Е: 'E',
        Ё: 'E',
        Ж: 'Zh',
        З: 'Z',
        И: 'I',
        Й: 'J',
        К: 'K',
        Л: 'L',
        М: 'M',
        Н: 'N',
        О: 'O',
        П: 'P',
        Р: 'R',
        С: 'S',
        Т: 'T',
        У: 'U',
        Ф: 'F',
        Х: 'H',
        Ц: 'C',
        Ч: 'Ch',
        Ш: 'Sh',
        Щ: 'Shch',
        Ы: 'Y',
        Э: 'E',
        Ю: 'Yu',
        Я: 'Ya',
        ь: '',
        ъ: '',
        Ь: '',
        Ъ: '',
    };
    return text
        .split('')
        .map(function (char) { return cyrillicToLatinMap[char] || char; })
        .join('')
        .toLowerCase()
        .replace(/\s+/g, '-') // replace spaces with -
        .replace(/[^\w\-]+/g, '') // remove all non-word chars
        .replace(/\-\-+/g, '-') // replace multiple - with single -
        .replace(/^-+/, '') // trim - from start of text
        .replace(/-+$/, ''); // trim - from end of text
}
function slugifyEnglish(str) {
    return str
        .toLowerCase()
        .replace(/\s+/g, '-') // Replace spaces with -
        .replace(/[^\w\-]+/g, '') // Remove all non-word chars
        .replace(/\-\-+/g, '-') // Replace multiple - with single -
        .replace(/^-+/, '') // Trim - from start of text
        .replace(/-+$/, ''); // Trim - from end of text
}
function slugify(str, lang) {
    switch (lang.toUpperCase()) {
        case 'ET': {
            return slugifyEstonian(str);
        }
        case 'LV': {
            return slugifyLatvian(str);
        }
        case 'LT': {
            return slugifyLithuanian(str);
        }
        case 'CZ': {
            return slugifyCzech(str);
        }
        case 'ES': {
            return slugifySpanish(str);
        }
        case 'RU': {
            return slugifyRussian(str);
        }
        case 'EN': {
            return slugifyEnglish(str);
        }
        default:
            throw new Error('Please provide valid language code');
    }
}

var Url = styled.span(templateObject_1$V || (templateObject_1$V = __makeTemplateObject(["\n  width: 100%;\n  &:hover {\n    cursor: pointer;\n  }\n"], ["\n  width: 100%;\n  &:hover {\n    cursor: pointer;\n  }\n"])));
var marginBottom16 = css(templateObject_2$P || (templateObject_2$P = __makeTemplateObject(["\n  margin-bottom: 16px;\n"], ["\n  margin-bottom: 16px;\n"])));
var CardContainer$4 = styled.div(templateObject_3$G || (templateObject_3$G = __makeTemplateObject(["\n  max-width: 100%;\n  gap: 16px;\n  ", "\n  ", "\n  \n  ", "\n  ", "\n"], ["\n  max-width: 100%;\n  gap: 16px;\n  ", "\n  ", "\n  \n  ", "\n  ", "\n"])), function (_a) {
    var type = _a.type, theme = _a.theme;
    return type === 'small' &&
        "\n    min-height: 110px;\n    display: flex;\n    align-items: center;\n    flex-direction: row;\n\n    ".concat(theme.breakpoints.sm, " {\n      flex-direction: column;\n    }\n    ").concat(theme.breakpoints.lg, " {\n      flex-direction: row;\n    }\n  ");
}, function (_a) {
    var type = _a.type, theme = _a.theme;
    return type === 'medium' &&
        "\n      padding-bottom: 60px;\n    ".concat(theme.breakpoints.sm, " {\n      padding-bottom: 36px;\n    }\n    ").concat(theme.breakpoints.lg, " {\n      padding-bottom: 68px;\n    }\n  ");
}, function (_a) {
    var type = _a.type, theme = _a.theme;
    return type === 'large' &&
        "\n    ".concat(theme.breakpoints.xs, " {\n      display: flex;\n      flex-direction: column;\n      align-items: center;\n    }\n    ").concat(theme.breakpoints.sm, " {\n      display: grid;\n      grid-template-columns: 1fr 1fr;\n      gap: 24px;\n      justify-items: center;\n    }\n    ").concat(theme.breakpoints.lg, " {\n      display: flex;\n      flex-direction: column;\n      align-items: baseline;\n      gap: 0px;\n    }\n  ");
}, function (_a) {
    var type = _a.type, theme = _a.theme, language = _a.language;
    return type === 'large' &&
        language === 'CZ' &&
        "\n    ".concat(theme.breakpoints.lg, " {\n      display: grid;\n      grid-template-columns: 1fr 1fr;\n      align-items: center;\n      gap: 48px\n    }\n  ");
});
var ImageBox$3 = styled.div(templateObject_4$x || (templateObject_4$x = __makeTemplateObject(["\n  position: relative;\n  overflow: hidden;\n  width: 100%;\n\n  ", "\n  ", "\n\n  ", "\n  ", "\n"], ["\n  position: relative;\n  overflow: hidden;\n  width: 100%;\n\n  ", "\n  ", "\n\n  ", "\n  ", "\n"])), function (_a) {
    var type = _a.type, theme = _a.theme;
    return type === 'small' &&
        "\n    flex: 0 0 30%;\n    border-radius: 16px;\n    min-height: 120px;\n    ".concat(theme.breakpoints.sm, " {\n      flex: 0 0 50%;\n      min-height: 157px;\n\n    }\n    ").concat(theme.breakpoints.lg, " {\n      flex: 0 0 30%;\n      min-height: 120px;\n    }\n  ");
}, function (_a) {
    var type = _a.type, theme = _a.theme;
    return type === 'medium' &&
        "\n    border-radius: 32px 32px 0px 0px; \n    height: 245px;\n    \n    ".concat(theme.breakpoints.sm, " {\n      height: 255px;\n    }\n\n    ").concat(theme.breakpoints.lg, " {\n      height: 264px;\n    }\n  ");
}, function (_a) {
    var type = _a.type, theme = _a.theme;
    return type === 'large' &&
        "\n    border-radius: 32px;\n    width: 640px;\n    height: 430px;\n    min-height: 430px;\n    ".concat(theme.breakpoints.xs, " {\n      min-height: 327px;\n      min-width: 327px;\n      height: 100%;\n      width: 100%;\n    }\n    ").concat(theme.breakpoints.sm, " {\n      min-height: 270px;\n      min-width: 270px;\n    }\n    ").concat(theme.breakpoints.md, " {\n      min-height: 340px;\n      min-width: 340px;\n    }\n    ").concat(theme.breakpoints.lg, " {\n      min-height: 430px;\n      min-width: 640px;\n      max-height: 480px;\n      max-width: 100%;\n    \n    }\n  ");
}, function (_a) {
    var type = _a.type, theme = _a.theme, language = _a.language;
    return type === 'large' &&
        language === 'CZ' &&
        "\n    ".concat(theme.breakpoints.lg, " {\n      min-height: 536px;\n      min-width: 536px;\n      max-width: 100%;\n    }\n  ");
});
var TextWrapper$2 = styled.div(templateObject_5$r || (templateObject_5$r = __makeTemplateObject(["\n  margin-top: ", ";\n\n  ", ";\n\n  ", "\n\n  ", ";\n"], ["\n  margin-top: ", ";\n\n  ", ";\n\n  ", "\n\n  ", ";\n"])), function (_a) {
    var theme = _a.theme, type = _a.type;
    return type === 'medium' && theme.space[24];
}, function (_a) {
    var type = _a.type, theme = _a.theme;
    return type === 'large' &&
        "\n     margin-top: 24px;\n    ".concat(theme.breakpoints.sm, " {\n      margin-top: 0;\n    }\n    ").concat(theme.breakpoints.lg, " {\n      margin-top: 48px;\n    }\n  ");
}, function (_a) {
    var type = _a.type, theme = _a.theme;
    return type === 'small' &&
        "\n    ".concat(theme.breakpoints.sm, " {\n      flex: 0 0 50%;\n    }\n    ").concat(theme.breakpoints.lg, " {\n      flex: 0 0 70%;\n    }\n  ");
}, function (_a) {
    var type = _a.type, theme = _a.theme, language = _a.language;
    return type === 'large' &&
        language === 'CZ' &&
        "\n    ".concat(theme.breakpoints.lg, " {\n      margin-top: 0;\n      width: 100%;\n    }\n  ");
});
var Header3$1 = styled(Header3$2)(templateObject_6$o || (templateObject_6$o = __makeTemplateObject(["\n  ", ";\n  overflow: hidden;\n  display: -webkit-box;\n  -webkit-box-orient: vertical;\n  -webkit-line-clamp: 2;\n  ", " {\n    -webkit-line-clamp: 3;\n  }\n  ", " {\n    -webkit-line-clamp: 2;\n  }\n"], ["\n  ", ";\n  overflow: hidden;\n  display: -webkit-box;\n  -webkit-box-orient: vertical;\n  -webkit-line-clamp: 2;\n  ", " {\n    -webkit-line-clamp: 3;\n  }\n  ", " {\n    -webkit-line-clamp: 2;\n  }\n"])), marginBottom16, function (_a) {
    var theme = _a.theme;
    return theme.breakpoints.sm;
}, function (_a) {
    var theme = _a.theme;
    return theme.breakpoints.lg;
});
var Caption1 = styled(Caption1$1)(templateObject_7$k || (templateObject_7$k = __makeTemplateObject(["\n  overflow: hidden;\n  display: -webkit-box;\n  -webkit-line-clamp: 2;\n  -webkit-box-orient: vertical;\n  font-size: ", ";\n  line-height: 30px;\n  ", "\n\n  ", "\n"], ["\n  overflow: hidden;\n  display: -webkit-box;\n  -webkit-line-clamp: 2;\n  -webkit-box-orient: vertical;\n  font-size: ", ";\n  line-height: 30px;\n  ", "\n\n  ", "\n"])), function (_a) {
    var theme = _a.theme;
    return theme.fonts.size.mobile.l;
}, marginBottom16, function (_a) {
    var type = _a.type;
    return type === 'small' &&
        "\n    padding-top: 4px;\n  ";
});
var Body3$1 = styled(Body3$2)(templateObject_8$d || (templateObject_8$d = __makeTemplateObject(["\n  overflow: hidden;\n  display: -webkit-box;\n  -webkit-line-clamp: 3;\n  -webkit-box-orient: vertical;\n  margin-bottom: 16px;\n\n  ", "\n\n  ", ";\n"], ["\n  overflow: hidden;\n  display: -webkit-box;\n  -webkit-line-clamp: 3;\n  -webkit-box-orient: vertical;\n  margin-bottom: 16px;\n\n  ", "\n\n  ", ";\n"])), function (_a) {
    var type = _a.type, theme = _a.theme;
    return type === 'large' &&
        "\n    ".concat(theme.breakpoints.lg, " {\n      margin-bottom: 24px;\n    }\n    ");
}, function (_a) {
    var type = _a.type, theme = _a.theme, language = _a.language;
    return type === 'large' &&
        language === 'CZ' &&
        "\n     -webkit-line-clamp: 10;\n    ".concat(theme.breakpoints.sm, " {\n      -webkit-line-clamp: 6;\n    } \n    ").concat(theme.breakpoints.md, " {\n      -webkit-line-clamp: 10;\n    } \n  ");
});
var LargeCardHeading = styled(Header3$1).attrs({
    color: theme.colors.blogCard.title,
})(templateObject_9$b || (templateObject_9$b = __makeTemplateObject(["\n  cursor: pointer;\n"], ["\n  cursor: pointer;\n"])));
var CardHeading = styled(Caption1).attrs({
    color: theme.colors.blogCard.title,
})(templateObject_10$7 || (templateObject_10$7 = __makeTemplateObject(["\n  cursor: pointer;\n"], ["\n  cursor: pointer;\n"])));
var Description$2 = styled(Body3$1).attrs({
    color: theme.colors.blogCard.description,
})(templateObject_11$5 || (templateObject_11$5 = __makeTemplateObject([""], [""])));
var DateTimeDisplay = styled(Body3$1).attrs({
    color: theme.colors.blogCard.date,
})(templateObject_12$4 || (templateObject_12$4 = __makeTemplateObject(["\n  ", "\n"], ["\n  ", "\n"])), function (_a) {
    var type = _a.type;
    return type === 'small' &&
        " \n    margin-bottom: 0px;\n  ";
});
var Caption2$1 = styled(Caption2$2)(templateObject_13$3 || (templateObject_13$3 = __makeTemplateObject(["\n  ", " {\n    font-size: ", ";\n  }\n"], ["\n  ", " {\n    font-size: ", ";\n  }\n"])), function (_a) {
    var theme = _a.theme;
    return theme.breakpoints.sm;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.size.tablet.xs;
});
var templateObject_1$V, templateObject_2$P, templateObject_3$G, templateObject_4$x, templateObject_5$r, templateObject_6$o, templateObject_7$k, templateObject_8$d, templateObject_9$b, templateObject_10$7, templateObject_11$5, templateObject_12$4, templateObject_13$3;

var CategoryBlock = function (_a) {
    var category = _a.category, categorySlug = _a.categorySlug, NextLink = _a.NextLink, language = _a.language;
    return category && categorySlug && NextLink && language ? (jsx(NextLink, __assign$1({ href: "/blog/".concat(slugify(categorySlug, language)) }, { children: jsx(Caption2$1, __assign$1({ color: theme.colors.blogCard.category }, { children: category })) }))) : (jsx(Gutter, { size: 24 }));
};
var BlogCard = function (_a) {
    var image = _a.image, description = _a.description, title = _a.title, category = _a.category, categorySlug = _a.categorySlug, dateAndReadTime = _a.dateAndReadTime, onClickLinkAction = _a.onClickLinkAction, _b = _a.type, type = _b === void 0 ? 'medium' : _b, NextLink = _a.NextLink, language = _a.language;
    var isScreenDesktop = useIsScreenSize().isScreenDesktop;
    return (jsxs(CardContainer$4, __assign$1({ type: type, language: language }, { children: [jsx(ImageBox$3, __assign$1({ type: type, language: language }, { children: jsx(Url, __assign$1({ onClick: onClickLinkAction }, { children: image })) })), jsxs(TextWrapper$2, __assign$1({ type: type, language: language }, { children: [type !== 'small' && NextLink && (jsx(CategoryBlock, { category: category, categorySlug: categorySlug, NextLink: NextLink, language: language })), isScreenDesktop && type === 'large' ? jsx(Gutter, { size: 16 }) : '', type === 'medium' || (type === 'large' && !isScreenDesktop) ? jsx(Gutter, { size: 8 }) : '', type === 'large' ? (jsx(LargeCardHeading, __assign$1({ onClick: onClickLinkAction }, { children: title }))) : (jsx(CardHeading, __assign$1({ onClick: onClickLinkAction }, { children: title }))), type !== 'small' && (jsx(Description$2, __assign$1({ color: theme.colors.blogCard.description }, { children: description }))), jsx(DateTimeDisplay, __assign$1({ type: type }, { children: dateAndReadTime }))] }))] })));
};

var List$2 = styled.ul(templateObject_1$U || (templateObject_1$U = __makeTemplateObject(["\n  margin: 0;\n  padding: 0;\n  list-style: none;\n"], ["\n  margin: 0;\n  padding: 0;\n  list-style: none;\n"])));
var ListItem$2 = styled.li(templateObject_2$O || (templateObject_2$O = __makeTemplateObject(["\n  padding: 0;\n  display: inline-block;\n  width: 100%;\n  margin-top: 24px;\n"], ["\n  padding: 0;\n  display: inline-block;\n  width: 100%;\n  margin-top: 24px;\n"])));
var IconAndText = styled.div(templateObject_3$F || (templateObject_3$F = __makeTemplateObject(["\n  display: flex;\n"], ["\n  display: flex;\n"])));
var templateObject_1$U, templateObject_2$O, templateObject_3$F;

var BulletList = function (_a) {
    var children = _a.children;
    return jsx(List$2, { children: children });
};
var BulletListItem = function (_a) {
    var children = _a.children;
    return (jsx(ListItem$2, { children: jsxs(IconAndText, { children: [jsx(Icon$4, { name: "BulletPoint" }), jsx(Body3$2, __assign$1({ as: "div" }, { children: children }))] }) }));
};

var FileSaver_min = {exports: {}};

(function (module, exports) {
	(function(a,b){b();})(commonjsGlobal,function(){function b(a,b){return "undefined"==typeof b?b={autoBom:!1}:"object"!=typeof b&&(console.warn("Deprecated: Expected third argument to be a object"),b={autoBom:!b}),b.autoBom&&/^\s*(?:text\/\S*|application\/xml|\S*\/\S*\+xml)\s*;.*charset\s*=\s*utf-8/i.test(a.type)?new Blob(["\uFEFF",a],{type:a.type}):a}function c(a,b,c){var d=new XMLHttpRequest;d.open("GET",a),d.responseType="blob",d.onload=function(){g(d.response,b,c);},d.onerror=function(){console.error("could not download file");},d.send();}function d(a){var b=new XMLHttpRequest;b.open("HEAD",a,!1);try{b.send();}catch(a){}return 200<=b.status&&299>=b.status}function e(a){try{a.dispatchEvent(new MouseEvent("click"));}catch(c){var b=document.createEvent("MouseEvents");b.initMouseEvent("click",!0,!0,window,0,0,0,80,20,!1,!1,!1,!1,0,null),a.dispatchEvent(b);}}var f="object"==typeof window&&window.window===window?window:"object"==typeof self&&self.self===self?self:"object"==typeof commonjsGlobal&&commonjsGlobal.global===commonjsGlobal?commonjsGlobal:void 0,a=f.navigator&&/Macintosh/.test(navigator.userAgent)&&/AppleWebKit/.test(navigator.userAgent)&&!/Safari/.test(navigator.userAgent),g=f.saveAs||("object"!=typeof window||window!==f?function(){}:"download"in HTMLAnchorElement.prototype&&!a?function(b,g,h){var i=f.URL||f.webkitURL,j=document.createElement("a");g=g||b.name||"download",j.download=g,j.rel="noopener","string"==typeof b?(j.href=b,j.origin===location.origin?e(j):d(j.href)?c(b,g,h):e(j,j.target="_blank")):(j.href=i.createObjectURL(b),setTimeout(function(){i.revokeObjectURL(j.href);},4E4),setTimeout(function(){e(j);},0));}:"msSaveOrOpenBlob"in navigator?function(f,g,h){if(g=g||f.name||"download","string"!=typeof f)navigator.msSaveOrOpenBlob(b(f,h),g);else if(d(f))c(f,g,h);else {var i=document.createElement("a");i.href=f,i.target="_blank",setTimeout(function(){e(i);});}}:function(b,d,e,g){if(g=g||open("","_blank"),g&&(g.document.title=g.document.body.innerText="downloading..."),"string"==typeof b)return c(b,d,e);var h="application/octet-stream"===b.type,i=/constructor/i.test(f.HTMLElement)||f.safari,j=/CriOS\/[\d]+/.test(navigator.userAgent);if((j||h&&i||a)&&"undefined"!=typeof FileReader){var k=new FileReader;k.onloadend=function(){var a=k.result;a=j?a:a.replace(/^data:[^;]*;/,"data:attachment/file;"),g?g.location.href=a:location=a,g=null;},k.readAsDataURL(b);}else {var l=f.URL||f.webkitURL,m=l.createObjectURL(b);g?g.location=m:location.href=m,g=null,setTimeout(function(){l.revokeObjectURL(m);},4E4);}});f.saveAs=g.saveAs=g,(module.exports=g);});

	
} (FileSaver_min));

var ButtonOrDownloadFile = function (_a) {
    var _b, _c, _d, _e;
    var nextLink = _a.nextLink, buttonConfig = _a.buttonConfig, linkData = _a.linkData, autoWidth = _a.autoWidth;
    if ((linkData === null || linkData === void 0 ? void 0 : linkData.__typename) === 'Link') {
        return (jsx(LinkAsButton, { NextLink: nextLink, linkData: linkData, variant: (_b = buttonConfig === null || buttonConfig === void 0 ? void 0 : buttonConfig.variant) !== null && _b !== void 0 ? _b : 'primary', mode: (_c = buttonConfig === null || buttonConfig === void 0 ? void 0 : buttonConfig.colorMode) !== null && _c !== void 0 ? _c : 'dark' }));
    }
    if ((linkData === null || linkData === void 0 ? void 0 : linkData.__typename) === 'DownloadFileButton') {
        var saveFile = function () {
            var _a, _b;
            if ('file' in linkData) {
                FileSaver_min.exports.saveAs(((_b = (_a = linkData.file) === null || _a === void 0 ? void 0 : _a.asset) === null || _b === void 0 ? void 0 : _b.url) || '');
            }
        };
        return (jsx(Button$2, __assign$1({ onClick: saveFile, variant: (_d = buttonConfig === null || buttonConfig === void 0 ? void 0 : buttonConfig.variant) !== null && _d !== void 0 ? _d : 'primary', mode: (_e = buttonConfig === null || buttonConfig === void 0 ? void 0 : buttonConfig.colorMode) !== null && _e !== void 0 ? _e : 'dark', autoWidth: autoWidth }, { children: linkData.linkTitle })));
    }
    return null;
};

var CardContainer$3 = styled.div(templateObject_1$T || (templateObject_1$T = __makeTemplateObject(["\n  height: ", ";\n  width: calc(100vw - 48px);\n  display: flex;\n  flex-direction: column;\n  margin-right: ", ";\n  margin-bottom: ", ";\n  ", " {\n    width: 340px;\n  }\n  ", " {\n    margin-right: ", ";\n  }\n  ", " {\n    width: 352px;\n  }\n"], ["\n  height: ", ";\n  width: calc(100vw - 48px);\n  display: flex;\n  flex-direction: column;\n  margin-right: ", ";\n  margin-bottom: ", ";\n  ", " {\n    width: 340px;\n  }\n  ", " {\n    margin-right: ", ";\n  }\n  ", " {\n    width: 352px;\n  }\n"])), function (_a) {
    var type = _a.type;
    return (type === 'TestimonialCard' ? '350px' : '546px');
}, function (_a) {
    var theme = _a.theme;
    return theme.space[24];
}, function (_a) {
    var theme = _a.theme;
    return theme.space[40];
}, theme.breakpoints.sm, theme.breakpoints.md, function (_a) {
    var theme = _a.theme;
    return theme.space[32];
}, theme.breakpoints.lg);
var ImageBox$2 = styled.div(templateObject_2$N || (templateObject_2$N = __makeTemplateObject(["\n  position: relative;\n  min-height: 220px;\n  background-repeat: no-repeat;\n  background-size: auto;\n  border-top-right-radius: 60px;\n  overflow: hidden;\n\n  > img {\n    object-fit: contain;\n  }\n"], ["\n  position: relative;\n  min-height: 220px;\n  background-repeat: no-repeat;\n  background-size: auto;\n  border-top-right-radius: 60px;\n  overflow: hidden;\n\n  > img {\n    object-fit: contain;\n  }\n"])));
var TextBox = styled.div(templateObject_3$E || (templateObject_3$E = __makeTemplateObject(["\n  background-color: ", ";\n  border-radius: ", ";\n  display: flex;\n  flex-direction: column;\n  justify-content: space-between;\n  padding: ", ";\n  height: 100%;\n  row-gap: ", ";\n"], ["\n  background-color: ", ";\n  border-radius: ", ";\n  display: flex;\n  flex-direction: column;\n  justify-content: space-between;\n  padding: ", ";\n  height: 100%;\n  row-gap: ", ";\n"])), function (_a) {
    var theme = _a.theme;
    return theme.colors.carouselCard.backgroundColor;
}, function (_a) {
    var type = _a.type;
    return (type === 'TestimonialCard' ? '16px' : '0');
}, function (_a) {
    var theme = _a.theme;
    return "".concat(theme.space[24], " ").concat(theme.space[16]);
}, function (_a) {
    var theme = _a.theme;
    return theme.space[4];
});
var Text$1 = styled(Body3$2).attrs({
    color: theme.colors.carouselCard.text,
})(templateObject_4$w || (templateObject_4$w = __makeTemplateObject(["\n  display: -webkit-box;\n  line-clamp: 5;\n  -webkit-line-clamp: 5;\n  -webkit-box-orient: vertical;\n\n  text-overflow: ellipsis;\n  overflow: hidden;\n  line-height: ", ";\n"], ["\n  display: -webkit-box;\n  line-clamp: 5;\n  -webkit-line-clamp: 5;\n  -webkit-box-orient: vertical;\n\n  text-overflow: ellipsis;\n  overflow: hidden;\n  line-height: ", ";\n"])), function (_a) {
    var theme = _a.theme;
    return theme.fonts.lineHeight.mobile.xxs;
});
var Name = styled(Caption3$1).attrs({ color: theme.colors.carouselCard.title })(templateObject_5$q || (templateObject_5$q = __makeTemplateObject(["\n  font-weight: 700;\n  padding-bottom: 8px;\n"], ["\n  font-weight: 700;\n  padding-bottom: 8px;\n"])));
var SocialMediaWrapper = styled.div(templateObject_6$n || (templateObject_6$n = __makeTemplateObject(["\n  display: flex;\n  align-items: center;\n  margin-top: ", ";\n\n  a {\n    margin-right: ", ";\n  }\n"], ["\n  display: flex;\n  align-items: center;\n  margin-top: ", ";\n\n  a {\n    margin-right: ", ";\n  }\n"])), function (_a) {
    var theme = _a.theme;
    return theme.space[8];
}, function (_a) {
    var theme = _a.theme;
    return theme.space[8];
});
var Icon$2 = styled.div(templateObject_7$j || (templateObject_7$j = __makeTemplateObject(["\n  display: flex;\n  justify-content: left;\n  align-items: center;\n  padding-bottom: ", ";\n"], ["\n  display: flex;\n  justify-content: left;\n  align-items: center;\n  padding-bottom: ", ";\n"])), function (_a) {
    var theme = _a.theme;
    return theme.space[24];
});
var templateObject_1$T, templateObject_2$N, templateObject_3$E, templateObject_4$w, templateObject_5$q, templateObject_6$n, templateObject_7$j;

var CarouselCard = function (_a) {
    var iconName = _a.iconName, iconBackgroundColor = _a.iconBackgroundColor, image = _a.image, description = _a.description, name = _a.name, position = _a.position, country = _a.country, socialMedia = _a.socialMedia, year = _a.year, _b = _a.type, type = _b === void 0 ? 'TestimonialCard' : _b;
    return (jsxs(CardContainer$3, __assign$1({ type: type }, { children: [image && jsx(ImageBox$2, { children: image }), jsxs(TextBox, __assign$1({ type: type }, { children: [jsxs("div", { children: [iconName && (jsx(Icon$2, { children: jsx(Icon$4, { name: iconName, "data-testid": iconName, circleColor: iconBackgroundColor }) })), jsx(Text$1, { children: description })] }), jsxs("div", { children: [jsx(Name, { children: name }), position && jsx(Text$1, { children: position }), country && jsx(Text$1, { children: country }), year && jsx(Text$1, { children: year }), socialMedia && socialMedia.length > 0 && (jsx(SocialMediaWrapper, { children: socialMedia.map(function (_a, index) {
                                    var href = _a.href, icon = _a.icon;
                                    return (jsx("a", __assign$1({ href: href, target: "_blank", rel: "noopener noreferrer", "aria-label": "Link to ".concat(icon) }, { children: icon && jsx(Icon$4, { name: icon }) }), index));
                                }) }))] })] }))] })));
};

var limitCharacters$1 = css(templateObject_1$S || (templateObject_1$S = __makeTemplateObject(["\n  display: -webkit-box;\n  line-clamp: 1;\n  -webkit-line-clamp: 1;\n  -webkit-box-orient: vertical;\n  text-overflow: ellipsis;\n  overflow: hidden;\n  text-align: center;\n"], ["\n  display: -webkit-box;\n  line-clamp: 1;\n  -webkit-line-clamp: 1;\n  -webkit-box-orient: vertical;\n  text-overflow: ellipsis;\n  overflow: hidden;\n  text-align: center;\n"])));
var CardContainer$2 = styled.div(templateObject_2$M || (templateObject_2$M = __makeTemplateObject(["\n  height: 330px;\n  width: calc(100vw - 48px);\n  margin-right: ", ";\n  margin-bottom: ", ";\n  ", " {\n    width: 330px;\n  }\n  ", " {\n    margin-right: ", ";\n  }\n"], ["\n  height: 330px;\n  width: calc(100vw - 48px);\n  margin-right: ", ";\n  margin-bottom: ", ";\n  ", " {\n    width: 330px;\n  }\n  ", " {\n    margin-right: ", ";\n  }\n"])), function (_a) {
    var theme = _a.theme;
    return theme.space[24];
}, function (_a) {
    var theme = _a.theme;
    return theme.space[40];
}, theme.breakpoints.sm, theme.breakpoints.md, function (_a) {
    var theme = _a.theme;
    return theme.space[32];
});
var BackgroundImage$1 = styled.div(templateObject_3$D || (templateObject_3$D = __makeTemplateObject(["\n  position: relative;\n  height: 330px;\n  border-radius: ", ";\n  overflow: hidden;\n\n  &::after {\n    content: '';\n    position: absolute;\n    background-color: rgba(0, 0, 0, 0.5);\n    inset: 0;\n    z-index: 1;\n  }\n\n  > img {\n    position: absolute;\n    height: 330px;\n    width: 100%;\n    object-fit: cover;\n  }\n"], ["\n  position: relative;\n  height: 330px;\n  border-radius: ", ";\n  overflow: hidden;\n\n  &::after {\n    content: '';\n    position: absolute;\n    background-color: rgba(0, 0, 0, 0.5);\n    inset: 0;\n    z-index: 1;\n  }\n\n  > img {\n    position: absolute;\n    height: 330px;\n    width: 100%;\n    object-fit: cover;\n  }\n"])), function (_a) {
    var theme = _a.theme;
    return theme.space[16];
});
var ContentWrapper$2 = styled.div(templateObject_4$v || (templateObject_4$v = __makeTemplateObject(["\n  position: absolute;\n  display: flex;\n  flex-direction: column;\n  justify-content: center;\n  align-items: center;\n  width: 100%;\n  height: 100%;\n  padding: ", ";\n  row-gap: ", ";\n"], ["\n  position: absolute;\n  display: flex;\n  flex-direction: column;\n  justify-content: center;\n  align-items: center;\n  width: 100%;\n  height: 100%;\n  padding: ", ";\n  row-gap: ", ";\n"])), function (_a) {
    var theme = _a.theme;
    return "".concat(theme.space[24], " ").concat(theme.space[16]);
}, function (_a) {
    var theme = _a.theme;
    return theme.space[8];
});
var LogoImage = styled.div(templateObject_5$p || (templateObject_5$p = __makeTemplateObject(["\n  position: relative;\n  height: 140px;\n  width: 100%;\n  overflow: hidden;\n  z-index: 2;\n\n  > img {\n    position: absolute;\n    object-fit: contain;\n    height: 140px;\n    width: 100%;\n  }\n"], ["\n  position: relative;\n  height: 140px;\n  width: 100%;\n  overflow: hidden;\n  z-index: 2;\n\n  > img {\n    position: absolute;\n    object-fit: contain;\n    height: 140px;\n    width: 100%;\n  }\n"])));
var FirstLineText = styled(Body1$1)(templateObject_6$m || (templateObject_6$m = __makeTemplateObject(["\n  color: ", ";\n  ", ";\n  position: relative;\n  z-index: 2;\n"], ["\n  color: ", ";\n  ", ";\n  position: relative;\n  z-index: 2;\n"])), function (_a) {
    var textColor = _a.textColor;
    return textColor;
}, limitCharacters$1);
var SecondLineText = styled(Body4)(templateObject_7$i || (templateObject_7$i = __makeTemplateObject(["\n  color: ", ";\n  ", ";\n  position: relative;\n  z-index: 2;\n"], ["\n  color: ", ";\n  ", ";\n  position: relative;\n  z-index: 2;\n"])), function (_a) {
    var textColor = _a.textColor;
    return textColor;
}, limitCharacters$1);
var UppercaseText = styled(Header5)(templateObject_8$c || (templateObject_8$c = __makeTemplateObject(["\n  color: ", ";\n  text-transform: uppercase;\n  ", ";\n  z-index: 2;\n"], ["\n  color: ", ";\n  text-transform: uppercase;\n  ", ";\n  z-index: 2;\n"])), function (_a) {
    var textColor = _a.textColor;
    return textColor;
}, limitCharacters$1);
var SmallPrint = styled(Body5)(templateObject_9$a || (templateObject_9$a = __makeTemplateObject(["\n  color: ", ";\n  position: absolute;\n  left: ", ";\n  right: ", ";\n  bottom: ", ";\n  ", ";\n  z-index: 2;\n"], ["\n  color: ", ";\n  position: absolute;\n  left: ", ";\n  right: ", ";\n  bottom: ", ";\n  ", ";\n  z-index: 2;\n"])), function (_a) {
    var textColor = _a.textColor;
    return textColor;
}, function (_a) {
    var theme = _a.theme;
    return "".concat(theme.space[24]);
}, function (_a) {
    var theme = _a.theme;
    return "".concat(theme.space[24]);
}, function (_a) {
    var theme = _a.theme;
    return "".concat(theme.space[16]);
}, limitCharacters$1);
var templateObject_1$S, templateObject_2$M, templateObject_3$D, templateObject_4$v, templateObject_5$p, templateObject_6$m, templateObject_7$i, templateObject_8$c, templateObject_9$a;

var PromotionCardContent = function (_a) {
    var backgroundImage = _a.backgroundImage, logoImage = _a.logoImage, textColor = _a.textColor, firstLineText = _a.firstLineText, secondLineText = _a.secondLineText, uppercaseText = _a.uppercaseText, smallPrint = _a.smallPrint;
    return (jsx(CardContainer$2, { children: jsxs(BackgroundImage$1, { children: [backgroundImage, jsxs(ContentWrapper$2, { children: [logoImage && jsx(LogoImage, { children: logoImage }), jsxs("div", { children: [firstLineText && (jsx(FirstLineText, __assign$1({ textColor: textColor }, { children: firstLineText }))), secondLineText && (jsx(SecondLineText, __assign$1({ textColor: textColor }, { children: secondLineText })))] }), uppercaseText && (jsx(UppercaseText, __assign$1({ textColor: textColor }, { children: uppercaseText })))] }), smallPrint && jsx(SmallPrint, __assign$1({ textColor: textColor }, { children: smallPrint }))] }) }));
};
var CarouselPromotionCard = function (_a) {
    var backgroundImage = _a.backgroundImage, logoImage = _a.logoImage, textColor = _a.textColor, firstLineText = _a.firstLineText, secondLineText = _a.secondLineText, uppercaseText = _a.uppercaseText, smallPrint = _a.smallPrint, link = _a.link, NextLink = _a.NextLink;
    var hasLink = Boolean((link === null || link === void 0 ? void 0 : link.internalLink) || (link === null || link === void 0 ? void 0 : link.externalUrl));
    return (jsx("div", { children: hasLink ? (jsx(LinkWithChildren, __assign$1({ NextLink: NextLink, linkData: link }, { children: jsx(PromotionCardContent, { backgroundImage: backgroundImage, firstLineText: firstLineText, secondLineText: secondLineText, uppercaseText: uppercaseText, smallPrint: smallPrint, logoImage: logoImage, textColor: textColor }) }))) : (jsx(PromotionCardContent, { backgroundImage: backgroundImage, firstLineText: firstLineText, secondLineText: secondLineText, uppercaseText: uppercaseText, smallPrint: smallPrint, logoImage: logoImage, textColor: textColor })) }));
};

var getElementOffset = function (el) {
    var offset = {};
    if (el) {
        var rect = el.getBoundingClientRect();
        offset = {
            top: rect.top + window.scrollY,
            right: rect.right + window.scrollX,
            bottom: rect.bottom + window.scrollY,
            left: rect.left + window.scrollX,
        };
    }
    return offset;
};

var Wrapper$4 = styled.div(templateObject_1$R || (templateObject_1$R = __makeTemplateObject(["\n  position: relative;\n  background: none;\n  font-size: ", ";\n  width: 100%;\n"], ["\n  position: relative;\n  background: none;\n  font-size: ", ";\n  width: 100%;\n"])), function (_a) {
    var theme = _a.theme;
    return theme.fonts.size.desktop.xs;
});
var Header$2 = styled.div(templateObject_2$L || (templateObject_2$L = __makeTemplateObject(["\n  position: relative;\n  background: ", ";\n  border: 1px solid ", ";\n  border-radius: 10px;\n  display: flex;\n  align-items: center;\n  justify-content: space-between;\n  padding: 28px 17px 18px;\n  width: 100%;\n  min-width: 110px;\n  height: 60px;\n  cursor: pointer;\n  outline-style: solid;\n  outline-offset: -1px;\n  outline-width: 1px;\n  outline-color: ", ";\n  :focus {\n    outline-offset: -1px;\n    outline-width: 1px;\n    outline-color: black;\n  }\n"], ["\n  position: relative;\n  background: ", ";\n  border: 1px solid ", ";\n  border-radius: 10px;\n  display: flex;\n  align-items: center;\n  justify-content: space-between;\n  padding: 28px 17px 18px;\n  width: 100%;\n  min-width: 110px;\n  height: 60px;\n  cursor: pointer;\n  outline-style: solid;\n  outline-offset: -1px;\n  outline-width: 1px;\n  outline-color: ", ";\n  :focus {\n    outline-offset: -1px;\n    outline-width: 1px;\n    outline-color: black;\n  }\n"])), function (_a) {
    var theme = _a.theme;
    return theme.colors.selectItem.input.backgroundColor;
}, function (_a) {
    var theme = _a.theme;
    return theme.colors.selectItem.input.border;
}, function (_a) {
    var theme = _a.theme, isOpen = _a.isOpen;
    return isOpen ? theme.colors.selectItem.input.outlineColor : 'transparent';
});
var SelectedInput = styled.input(templateObject_3$C || (templateObject_3$C = __makeTemplateObject(["\n  font-size: ", ";\n  outline: none;\n  appearance: none;\n  border: none;\n  width: ", ";\n  visibility: ", ";\n  cursor: pointer;\n  caret-color: transparent;\n  padding: 0;\n  color: ", ";\n"], ["\n  font-size: ", ";\n  outline: none;\n  appearance: none;\n  border: none;\n  width: ", ";\n  visibility: ", ";\n  cursor: pointer;\n  caret-color: transparent;\n  padding: 0;\n  color: ", ";\n"])), function (_a) {
    var theme = _a.theme;
    return theme.fonts.size.desktop.xs;
}, function (_a) {
    var isSelected = _a.isSelected;
    return (isSelected ? '100%' : '0');
}, function (_a) {
    var isSelected = _a.isSelected;
    return (isSelected ? 'visible' : 'hidden');
}, function (_a) {
    var isDisabled = _a.isDisabled, theme = _a.theme;
    return isDisabled
        ? theme.colors.selectItem.disabled.textInput
        : theme.colors.selectItem.input.textInput;
});
var IconWrapper$1 = styled.div(templateObject_4$u || (templateObject_4$u = __makeTemplateObject(["\n  transform: ", ";\n  transition: transform 150ms ease-in-out;\n"], ["\n  transform: ", ";\n  transition: transform 150ms ease-in-out;\n"])), function (_a) {
    var isOpen = _a.isOpen;
    return (isOpen ? 'rotateZ(180deg)' : 'rotateZ(0deg)');
});
var List$1 = styled.ul(templateObject_5$o || (templateObject_5$o = __makeTemplateObject(["\n  position: absolute;\n  left: 0;\n  ", ": 100%;\n  width: 100%;\n  ", "\n  border: 1px solid ", ";\n  background: ", ";\n  border-radius: 3px;\n  padding: 0;\n  margin: 0;\n  box-shadow: rgb(0 0 0 / 20%) 0px 5px 5px -3px, rgb(0 0 0 / 14%) 0px 8px 10px 1px,\n    rgb(0 0 0 / 12%) 0px 3px 14px 2px;\n  display: flex;\n  flex-direction: column;\n  align-items: flex-start;\n  z-index: ", ";\n"], ["\n  position: absolute;\n  left: 0;\n  ", ": 100%;\n  width: 100%;\n  ", "\n  border: 1px solid ", ";\n  background: ", ";\n  border-radius: 3px;\n  padding: 0;\n  margin: 0;\n  box-shadow: rgb(0 0 0 / 20%) 0px 5px 5px -3px, rgb(0 0 0 / 14%) 0px 8px 10px 1px,\n    rgb(0 0 0 / 12%) 0px 3px 14px 2px;\n  display: flex;\n  flex-direction: column;\n  align-items: flex-start;\n  z-index: ", ";\n"])), function (_a) {
    var dropdownPosition = _a.dropdownPosition;
    return (dropdownPosition === 'top' ? 'bottom' : 'top');
}, function (_a) {
    var isOpen = _a.isOpen, scrollable = _a.scrollable, menuHeight = _a.menuHeight;
    return "\n    height: ".concat(isOpen ? "".concat(menuHeight, "px") : '0', ";\n    opacity: ").concat(isOpen ? '1' : ' 0', ";\n    pointer-events: ").concat(isOpen ? 'auto' : 'none', ";\n    transition:  ").concat(isOpen ? 'opacity 150ms ease-in-out' : 'none', ";\n    overflow-y: ").concat(scrollable ? 'scroll' : 'hidden', ";\n  ");
}, function (_a) {
    var theme = _a.theme;
    return theme.colors.selectItem.list.border;
}, function (_a) {
    var theme = _a.theme;
    return theme.colors.selectItem.list.backgroundColor;
}, function (_a) {
    var theme = _a.theme;
    return theme.customZIndex.secondary;
});
var ListItem$1 = styled.li(templateObject_6$l || (templateObject_6$l = __makeTemplateObject(["\n  cursor: pointer;\n\n  width: 100%;\n\n  outline: none;\n  list-style: none;\n"], ["\n  cursor: pointer;\n\n  width: 100%;\n\n  outline: none;\n  list-style: none;\n"])));
var OptionElement = styled.button(templateObject_7$h || (templateObject_7$h = __makeTemplateObject(["\n  font-size: ", ";\n  text-align: left;\n\n  border: none;\n  height: 100%;\n  width: 100%;\n  padding: 10px 17px;\n  outline: none;\n  background: ", ";\n\n  &:focus {\n    background-color: ", ";\n  }\n\n  &:hover {\n    background-color: ", ";\n  }\n"], ["\n  font-size: ", ";\n  text-align: left;\n\n  border: none;\n  height: 100%;\n  width: 100%;\n  padding: 10px 17px;\n  outline: none;\n  background: ", ";\n\n  &:focus {\n    background-color: ", ";\n  }\n\n  &:hover {\n    background-color: ", ";\n  }\n"])), function (_a) {
    var theme = _a.theme;
    return theme.fonts.size.desktop.xs;
}, function (_a) {
    var theme = _a.theme, isSelected = _a.isSelected;
    return isSelected
        ? theme.colors.selectItem.option.selected.backgroundColor
        : theme.colors.selectItem.option.backgroundColor;
}, function (_a) {
    var theme = _a.theme, isSelected = _a.isSelected;
    return isSelected
        ? theme.colors.selectItem.option.selected.backgroundColor
        : theme.colors.selectItem.option.hover.backgroundColor;
}, function (_a) {
    var theme = _a.theme, isSelected = _a.isSelected;
    return isSelected
        ? theme.colors.selectItem.option.selected.hover.backgroundColor
        : theme.colors.selectItem.option.hover.backgroundColor;
});
var templateObject_1$R, templateObject_2$L, templateObject_3$C, templateObject_4$u, templateObject_5$o, templateObject_6$l, templateObject_7$h;

var SelectItem = function (_a, ref) {
    var inputId = _a.inputId, title = _a.title, name = _a.name, _b = _a.options, options = _b === void 0 ? [] : _b, onChange = _a.onChange, onSelectItem = _a.onSelectItem, _c = _a.displayValue, displayValue = _c === void 0 ? 'label' : _c, defaultOption = _a.defaultOption, _d = _a.required, required = _d === void 0 ? false : _d, errorMessage = _a.errorMessage, inputProps = _a.inputProps, mode = _a.mode, menuHeightValue = _a.menuHeight, value = _a.value, _e = _a.isDisabled, isDisabled = _e === void 0 ? false : _e;
    var _f = useState(false), isOpen = _f[0], setIsOpen = _f[1];
    var _g = useState(value !== null && value !== void 0 ? value : defaultOption), selectedOption = _g[0], setSelectedOption = _g[1];
    var _h = useState(0), cursor = _h[0], setCursor = _h[1];
    var _j = useState('bottom'), dropdownPosition = _j[0], setDropdownPosition = _j[1];
    var _k = useState(false), scrollable = _k[0], setScrollable = _k[1];
    var _l = useState(menuHeightValue || 'auto'), menuHeight = _l[0], setMenuHeight = _l[1];
    var optionsListRef = useRef([]);
    var selectRef = useRef(null);
    var menuRef = useRef(null);
    var _m = useState(false), isFocused = _m[0], setIsFocused = _m[1];
    var pageWrapper = useContext(PageWrapperContext).pageWrapperRef;
    useImperativeHandle(ref, function () { return selectRef.current; });
    var onFocus = function () {
        setIsFocused(true);
    };
    var onBlur = function () {
        setIsFocused(false);
    };
    var toggleList = useCallback(function () {
        setIsOpen(!isOpen);
        if (isOpen) {
            onFocus();
        }
        else {
            onBlur();
        }
    }, [isOpen, onFocus, onBlur]);
    var onHeaderClick = function (event) {
        if (options.length === 0) {
            return;
        }
        event.preventDefault();
        toggleList();
    };
    var handleKeyOnHeader = function (event) {
        event.preventDefault();
        if (event.code === 'Space' || event.code === 'Enter')
            toggleList();
    };
    var selectItem = function (event) {
        event.stopPropagation();
        var item = event.currentTarget;
        var selected = options === null || options === void 0 ? void 0 : options.find(function (option) { return item.id === (option === null || option === void 0 ? void 0 : option.id); });
        onChange(event);
        toggleList();
        setSelectedOption(selected);
        if (onSelectItem && selected) {
            onSelectItem(selected);
        }
    };
    var handleKeyDown = function (event) {
        var _a, _b;
        event.stopPropagation();
        if (event.key === 'ArrowUp' && cursor > 0) {
            (_a = optionsListRef.current[cursor - 1]) === null || _a === void 0 ? void 0 : _a.focus();
            setCursor(cursor - 1);
        }
        else if (event.key === 'ArrowDown' && cursor < options.length - 1) {
            (_b = optionsListRef.current[cursor + 1]) === null || _b === void 0 ? void 0 : _b.focus();
            setCursor(cursor + 1);
        }
    };
    var handleEnterOnOption = function (event) {
        event.stopPropagation();
        if (event.key === 'Enter') {
            setSelectedOption(options[cursor]);
        }
    };
    var handleEscKeyPress = function (event) {
        event.stopPropagation();
        if (event.key === 'Escape') {
            toggleList();
        }
    };
    useEffect(function () {
        setSelectedOption(value);
    }, [value === null || value === void 0 ? void 0 : value.id]);
    useEffect(function () {
        if (isOpen) {
            setTimeout(function () {
                document.addEventListener('click', toggleList);
                document.addEventListener('keyup', handleEscKeyPress);
            });
        }
        return function () {
            document.removeEventListener('click', toggleList);
            document.removeEventListener('keyup', handleEscKeyPress);
        };
    }, [isOpen]);
    useEffect(function () {
        if (isOpen &&
            pageWrapper &&
            menuRef.current !== null &&
            selectRef.current !== null &&
            pageWrapper.current !== null) {
            var selectOffset = getElementOffset(selectRef.current);
            if (menuRef.current.clientHeight > selectOffset.top &&
                menuRef.current.clientHeight + selectOffset.bottom > pageWrapper.current.clientHeight) {
                setScrollable(true);
                setMenuHeight(pageWrapper.current.clientHeight - selectOffset.bottom);
            }
            else if (menuRef.current.clientHeight < selectOffset.top &&
                menuRef.current.clientHeight + selectOffset.bottom > pageWrapper.current.clientHeight) {
                setDropdownPosition('top');
            }
        }
        else if (isOpen) {
            setDropdownPosition('bottom');
            if (menuHeightValue) {
                setScrollable(true);
                setMenuHeight(menuHeight);
            }
            else {
                setScrollable(false);
                setMenuHeight('auto');
            }
        }
    }, [isOpen]);
    useEffect(function () {
        var _a, _b;
        if (isOpen && selectedOption) {
            (_a = optionsListRef.current[options.indexOf(selectedOption)]) === null || _a === void 0 ? void 0 : _a.focus();
            setCursor(options.indexOf(selectedOption));
        }
        else if (isOpen) {
            (_b = optionsListRef.current[0]) === null || _b === void 0 ? void 0 : _b.focus();
            setCursor(0);
        }
    }, [selectedOption, isOpen]);
    return (jsxs(Wrapper$4, { children: [jsxs(Header$2, __assign$1({ ref: selectRef, isOpen: isOpen, onClick: onHeaderClick, onKeyPress: handleKeyOnHeader, tabIndex: 0 }, { children: [jsx(PlaceholderLabel, __assign$1({ htmlFor: inputId || name, hasValue: !!selectedOption, isFocused: isFocused }, { children: required ? "".concat(title, " *") : title })), jsx(SelectedInput, __assign$1({ tabIndex: -1, id: inputId || name, name: name, readOnly: true, value: selectedOption ? selectedOption[displayValue] : '', isSelected: !!selectedOption, required: required, isDisabled: isDisabled }, inputProps)), jsx(IconWrapper$1, __assign$1({ isOpen: isOpen }, { children: jsx(Icon$4, { name: "ArrowDown", fillColor: isDisabled
                                ? theme.colors.selectItem.disabled.icon
                                : theme.colors.selectItem.input.icon }) }))] })), jsx(List$1, __assign$1({ tabIndex: -1, role: "menu", ref: menuRef, isOpen: isOpen, dropdownPosition: dropdownPosition, scrollable: scrollable, menuHeight: menuHeight }, { children: options === null || options === void 0 ? void 0 : options.map(function (option, index) { return (jsx(ListItem$1, __assign$1({ role: "menuitem" }, { children: jsx(OptionElement, __assign$1({ tabIndex: -1, id: option === null || option === void 0 ? void 0 : option.id, value: option === null || option === void 0 ? void 0 : option.value, type: "button", ref: function (el) { return (optionsListRef.current[index] = el); }, onKeyUp: handleEnterOnOption, onKeyDown: handleKeyDown, onFocus: function () {
                            if (!isOpen)
                                toggleList();
                        }, onClick: selectItem, isSelected: (option === null || option === void 0 ? void 0 : option.id) === (selectedOption === null || selectedOption === void 0 ? void 0 : selectedOption.id) }, { children: option === null || option === void 0 ? void 0 : option.label })) }), option === null || option === void 0 ? void 0 : option.id)); }) })), errorMessage && !isOpen ? jsx(ErrorMessage, { message: errorMessage, mode: mode }) : null] }));
};
var SelectItem$1 = forwardRef(SelectItem);

var SectionWrapper$5 = styled.section(templateObject_1$Q || (templateObject_1$Q = __makeTemplateObject(["\n  margin-top: ", ";\n  margin-bottom: ", ";\n  ", " {\n    margin-top: ", ";\n    margin-bottom: ", ";\n  }\n"], ["\n  margin-top: ", ";\n  margin-bottom: ", ";\n  ", " {\n    margin-top: ", ";\n    margin-bottom: ", ";\n  }\n"])), function (_a) {
    var theme = _a.theme;
    return theme.space['48'];
}, function (_a) {
    var theme = _a.theme;
    return theme.space['48'];
}, theme.breakpoints.sm, function (_a) {
    var theme = _a.theme, buttonVariant = _a.buttonVariant;
    return buttonVariant === SelectButton.Variant.ImageWithText
        ? theme.space['48']
        : theme.space['100'];
}, function (_a) {
    var theme = _a.theme;
    return theme.space['60'];
});
var SelectButtonContainer = styled(Container$1)(templateObject_2$K || (templateObject_2$K = __makeTemplateObject(["\n  display: none;\n\n  ", " {\n    display: block;\n  }\n"], ["\n  display: none;\n\n  ", " {\n    display: block;\n  }\n"])), function (_a) {
    var theme = _a.theme;
    return theme.breakpoints.lg;
});
var SelectItemContainer = styled.div(templateObject_3$B || (templateObject_3$B = __makeTemplateObject(["\n  ", " {\n    display: none;\n  }\n"], ["\n  ", " {\n    display: none;\n  }\n"])), function (_a) {
    var theme = _a.theme;
    return theme.breakpoints.lg;
});
var templateObject_1$Q, templateObject_2$K, templateObject_3$B;

var CategoryFilter = function (_a) {
    var currentCategoryId = _a.currentCategoryId, handleSelect = _a.handleSelect, options = _a.options, _b = _a.mobileSelectTitle, mobileSelectTitle = _b === void 0 ? 'Category' : _b, centerFilters = _a.centerFilters, _c = _a.buttonVariant, buttonVariant = _c === void 0 ? SelectButton.Variant.Text : _c;
    return (jsxs(SectionWrapper$5, __assign$1({ buttonVariant: buttonVariant }, { children: [jsx(SelectButtonContainer, { children: jsx(Row$1, __assign$1({ center: centerFilters ? 'lg' : undefined }, { children: options.map(function (category, key) {
                        return (jsx(Col, { children: jsx(SelectButton, { name: category.label, value: category.value, size: buttonVariant === SelectButton.Variant.ImageWithText
                                    ? SelectButton.Size.Content
                                    : SelectButton.Size.Small, variant: buttonVariant, label: category.label, image: category.image, selectedValue: currentCategoryId, onSelect: handleSelect }) }, key));
                    }) })) }), jsx(SelectItemContainer, { children: jsx(SelectItem$1, { title: mobileSelectTitle, name: "Name", options: options, defaultOption: options.find(function (option) { return option.id === currentCategoryId; }) || options[0], onChange: handleSelect }) })] })));
};

var CircleDiagram$1 = styled.figure(templateObject_1$P || (templateObject_1$P = __makeTemplateObject(["\n  ", ";\n  padding: 6px;\n  margin: 0;\n  border-radius: 50%;\n  display: flex;\n  justify-content: center;\n  align-items: center;\n"], ["\n  ", ";\n  padding: 6px;\n  margin: 0;\n  border-radius: 50%;\n  display: flex;\n  justify-content: center;\n  align-items: center;\n"])), function (_a) {
    var theme = _a.theme, diagramSize = _a.diagramSize;
    return "\n    background-color: ".concat(theme.colors.circleDiagram.backgroundColor, ";\n    height: ").concat(diagramSize + 12, "px;\n    width: ").concat(diagramSize + 12, "px;\n  ");
});
var Svg = styled.svg(templateObject_2$J || (templateObject_2$J = __makeTemplateObject(["\n  width: 100%;\n  height: 100%;\n\n  circle {\n    fill: none;\n    stroke-linecap: butt;\n    transform-origin: center;\n    transform: rotate(90deg) scaleX(-1);\n\n    ", "\n  }\n"], ["\n  width: 100%;\n  height: 100%;\n\n  circle {\n    fill: none;\n    stroke-linecap: butt;\n    transform-origin: center;\n    transform: rotate(90deg) scaleX(-1);\n\n    ", "\n  }\n"])), function (_a) {
    var theme = _a.theme, diagramStroke = _a.diagramStroke, circleLength = _a.circleLength, circleOffset = _a.circleOffset;
    return "\n      stroke: ".concat(theme.colors.circleDiagram.stroke, ";\n      stroke-width: ").concat(diagramStroke, ";\n      \n      &.foreground {\n        stroke: ").concat(theme.colors.circleDiagram.foregroundStroke, ";\n        stroke-dasharray: ").concat(circleOffset, " ").concat(circleLength, ";\n        stroke-dashoffset: 0;\n      }\n    ");
});
var CircleDiagramCaption = styled.figcaption(templateObject_3$A || (templateObject_3$A = __makeTemplateObject(["\n  position: absolute;\n  display: inline-block;\n  font-size: 27px;\n  font-weight: 500;\n  color: ", ";\n"], ["\n  position: absolute;\n  display: inline-block;\n  font-size: 27px;\n  font-weight: 500;\n  color: ", ";\n"])), function (_a) {
    var theme = _a.theme;
    return theme.colors.circleDiagram.figcaption;
});
var templateObject_1$P, templateObject_2$J, templateObject_3$A;

var CircleDiagram = function (_a) {
    var children = _a.children, accessibleTitle = _a.accessibleTitle, accessibleDescription = _a.accessibleDescription, percentageValue = _a.percentageValue, diagramSize = _a.diagramSize, diagramStroke = _a.diagramStroke;
    var circleDiameter = diagramSize - diagramStroke;
    var circleRadius = circleDiameter * 0.5;
    var circleLength = circleDiameter * Math.PI;
    var circleOffset = (percentageValue / 100) * circleLength;
    return (jsxs(CircleDiagram$1, __assign$1({ diagramSize: diagramSize }, { children: [jsxs(Svg, __assign$1({ role: "img", xmlns: "http://www.w3.org/2000/svg", diagramStroke: diagramStroke, circleLength: circleLength, circleOffset: circleOffset }, { children: [accessibleTitle && jsx("title", { children: accessibleTitle }), accessibleDescription && jsx("desc", { children: accessibleDescription }), jsx("circle", { r: circleRadius, cx: "50%", cy: "50%" }), jsx("circle", { r: circleRadius, cx: "50%", cy: "50%", className: "foreground", style: {
                            strokeDasharray: "".concat(circleOffset, " ").concat(circleLength),
                        } })] })), jsx(CircleDiagramCaption, { children: children })] })));
};

var Container = styled.div(templateObject_1$O || (templateObject_1$O = __makeTemplateObject(["\n  margin: 18px 24px;\n  ", " {\n    margin: 18px 32px;\n  }\n  ", " {\n    margin: 18px 48px;\n  }\n"], ["\n  margin: 18px 24px;\n  ", " {\n    margin: 18px 32px;\n  }\n  ", " {\n    margin: 18px 48px;\n  }\n"])), function (_a) {
    var theme = _a.theme;
    return theme.breakpoints.sm;
}, function (_a) {
    var theme = _a.theme;
    return theme.breakpoints.lg;
});
var LinkComponent$2 = styled.div(templateObject_2$I || (templateObject_2$I = __makeTemplateObject(["\n  padding: 8px 0 8px 0;\n  display: flex;\n  align-items: center;\n  justify-content: space-between;\n  ", " {\n    justify-content: flex-start;\n  }\n  cursor: pointer;\n"], ["\n  padding: 8px 0 8px 0;\n  display: flex;\n  align-items: center;\n  justify-content: space-between;\n  ", " {\n    justify-content: flex-start;\n  }\n  cursor: pointer;\n"])), function (_a) {
    var theme = _a.theme;
    return theme.breakpoints.sm;
});
var ArrowIcon$1 = styled(Icon$4).attrs({
    name: 'ArrowRight',
    height: 18,
    width: 22,
    fillColor: theme.colors.contentList.linkColor,
})(templateObject_3$z || (templateObject_3$z = __makeTemplateObject(["\n  display: flex;\n  justify-content: center;\n  margin-left: 10px;\n"], ["\n  display: flex;\n  justify-content: center;\n  margin-left: 10px;\n"])));
var templateObject_1$O, templateObject_2$I, templateObject_3$z;

var ContentList = function (_a) {
    var contentTitle = _a.contentTitle, anchors = _a.anchors, offset = _a.offset;
    var scrolltoHash = function (element_id) {
        var element = document.getElementById(element_id);
        var headerOffset = 74 - offset;
        var elementPosition = element === null || element === void 0 ? void 0 : element.getBoundingClientRect().top;
        if (elementPosition) {
            var offsetPosition = elementPosition - headerOffset;
            window.scrollTo({
                top: offsetPosition,
                behavior: 'smooth',
            });
        }
    };
    return (jsx("div", __assign$1({ style: { height: 'auto' } }, { children: jsx(RoundedBackground, __assign$1({ color: theme.colors.contentList.backgroundColor, roundedType: RoundedBackground.RoundTypes.EachCornerSeparateValue, eachCornerSeparateValues: {
                topLeft: '60px',
                topRight: '0',
                bottomLeft: '0',
                bottomRight: '60px',
            } }, { children: jsxs(Container, { children: [contentTitle && (jsxs(Fragment, { children: [jsx(Header5, { children: contentTitle }), jsx(Gutter, { size: 24 })] })), jsx("div", __assign$1({ "data-testid": "contentListWrapper" }, { children: anchors === null || anchors === void 0 ? void 0 : anchors.map(function (_a, index) {
                            var anchor = _a.anchor, title = _a.title;
                            return (jsxs(LinkComponent$2, __assign$1({ onClick: function () { return scrolltoHash(anchor); } }, { children: [jsx(Caption3$1, __assign$1({ color: theme.colors.contentList.linkColor }, { children: title !== null && title !== void 0 ? title : index })), jsx(ArrowIcon$1, {})] }), title));
                        }) }))] }) })) })));
};

var DateRangePickerWrapper = styled.div(templateObject_1$N || (templateObject_1$N = __makeTemplateObject(["\n  display: flex;\n  flex-direction: column;\n  gap: 20px;\n  ", "\n  ", "\n  .react-date-picker {\n    width: 100%;\n  }\n"], ["\n  display: flex;\n  flex-direction: column;\n  gap: 20px;\n  ", "\n  ", "\n  .react-date-picker {\n    width: 100%;\n  }\n"])), makeMediaQuery('sm', 'flex-direction: row'), makeMediaQuery('sm', 'gap: 0'));
var templateObject_1$N;

var DateRangePicker = function (_a) {
    var _b = _a.value, value = _b === void 0 ? [new Date(), new Date()] : _b, onChange = _a.onChange, min = _a.min, _c = _a.max, max = _c === void 0 ? new Date() : _c, _d = _a.format, format = _d === void 0 ? 'd/MM/yy' : _d, language = _a.language, rest = __rest$1(_a, ["value", "onChange", "min", "max", "format", "language"]);
    var onChangeFrom = function (valueFrom) {
        onChange([valueFrom, value[1]]);
    };
    var onChangeTo = function (valueTo) {
        onChange([value[0], valueTo]);
    };
    return (jsxs(DateRangePickerWrapper, { children: [jsx(DatePicker, __assign$1({ value: value[0], onChange: onChangeFrom, format: format, language: language, min: min, max: max }, rest, { variant: "left" })), jsx(DatePicker, __assign$1({ value: value[1], onChange: onChangeTo, format: format, language: language, min: min, max: max }, rest, { variant: "right" }))] }));
};

var ButtonsWrapper = styled.div(templateObject_3$y || (templateObject_3$y = __makeTemplateObject(["\n  ", "\n"], ["\n  ", "\n"])), function (_a) {
    var showMobileButton = _a.showMobileButton;
    return showMobileButton
        ? css(templateObject_1$M || (templateObject_1$M = __makeTemplateObject(["\n          display: none;\n\n          ", " {\n            display: flex;\n          }\n        "], ["\n          display: none;\n\n          ", " {\n            display: flex;\n          }\n        "])), function (_a) {
            var theme = _a.theme;
            return theme.breakpoints.lg;
        }) : css(templateObject_2$H || (templateObject_2$H = __makeTemplateObject(["\n          display: flex;\n        "], ["\n          display: flex;\n        "])));
});
var AppButton = styled(Button$2)(templateObject_6$k || (templateObject_6$k = __makeTemplateObject(["\n  ", "\n"], ["\n  ", "\n"])), function (_a) {
    var showMobileButton = _a.showMobileButton;
    return showMobileButton
        ? css(templateObject_4$t || (templateObject_4$t = __makeTemplateObject(["\n          display: block;\n\n          ", " {\n            margin-left: auto;\n            margin-right: auto;\n          }\n          ", " {\n            display: none;\n          }\n        "], ["\n          display: block;\n\n          ", " {\n            margin-left: auto;\n            margin-right: auto;\n          }\n          ", " {\n            display: none;\n          }\n        "])), function (_a) {
            var theme = _a.theme;
            return theme.breakpoints.xs;
        }, function (_a) {
            var theme = _a.theme;
            return theme.breakpoints.lg;
        }) : css(templateObject_5$n || (templateObject_5$n = __makeTemplateObject(["\n          display: none;\n        "], ["\n          display: none;\n        "])));
});
var templateObject_1$M, templateObject_2$H, templateObject_3$y, templateObject_4$t, templateObject_5$n, templateObject_6$k;

var DownloadAppButtons = React.memo(function (props) {
    var showMobileButton = props.showMobileButton, iconVariant = props.iconVariant, appLinks = props.appLinks;
    var renderAppButton = function () {
        if (showMobileButton) {
            var buttonTitle = props.buttonTitle, handleButtonClick = props.handleButtonClick, buttonId = props.buttonId, _a = props.size, size = _a === void 0 ? 'standard' : _a;
            return (jsx(AppButton, __assign$1({ showMobileButton: showMobileButton, variant: "primary", onClick: handleButtonClick, id: buttonId, size: size, autoWidth: "xs" }, { children: buttonTitle })));
        }
        return null;
    };
    return (jsxs(Fragment, { children: [renderAppButton(), jsxs(ButtonsWrapper, __assign$1({ showMobileButton: showMobileButton }, { children: [jsx("a", __assign$1({ target: "_blank", href: appLinks.iOS, rel: "noreferrer", id: "apple-app-store", "aria-label": "Link to Download App on the App Store" }, { children: jsx(Icon$4, { name: iconVariant === 'light' ? 'AppStoreLight' : 'AppStoreDark' }) })), jsx(Gutter, { size: 24 }), jsx("a", __assign$1({ target: "_blank", href: appLinks.android, rel: "noreferrer", id: "google-play-store", "aria-label": "Link to get App on Google Play" }, { children: jsx(Icon$4, { name: iconVariant === 'light' ? 'GooglePlayLight' : 'GooglePlayDark' }) }))] }))] }));
});

var LinkWrapper$6 = styled.span(templateObject_1$L || (templateObject_1$L = __makeTemplateObject(["\n  a {\n    display: flex;\n    align-items: center;\n    font-weight: 500;\n    color: ", ";\n  }\n"], ["\n  a {\n    display: flex;\n    align-items: center;\n    font-weight: 500;\n    color: ", ";\n  }\n"])), function (_a) {
    var theme = _a.theme, mode = _a.mode;
    return theme.colors.linkWithArrow[mode].textColor;
});
var ArrowIcon = styled(Icon$4).attrs({
    name: 'ArrowRightOn',
    height: 18,
    width: 22,
})(templateObject_2$G || (templateObject_2$G = __makeTemplateObject(["\n  display: flex;\n  justify-content: center;\n  margin-left: 12px;\n  transform: translateY(1px);\n  g {\n    fill: ", ";\n  }\n"], ["\n  display: flex;\n  justify-content: center;\n  margin-left: 12px;\n  transform: translateY(1px);\n  g {\n    fill: ", ";\n  }\n"])), function (_a) {
    var theme = _a.theme, mode = _a.mode;
    return theme.colors.linkWithArrow[mode].fillColor;
});
var templateObject_1$L, templateObject_2$G;

var LinkWithArrow = function (_a) {
    var _b, _c;
    var NextLink = _a.NextLink, linkData = _a.linkData, _d = _a.mode, mode = _d === void 0 ? 'dark' : _d;
    var anchorTag = (linkData === null || linkData === void 0 ? void 0 : linkData.anchorID) ? "#".concat(linkData.anchorID) : '';
    if (NextLink && ((_b = linkData === null || linkData === void 0 ? void 0 : linkData.internalLink) === null || _b === void 0 ? void 0 : _b.__typename) === 'HomePage') {
        return (jsx(LinkWrapper$6, __assign$1({ mode: mode }, { children: jsxs(NextLink, __assign$1({ href: "/".concat(anchorTag), passHref: true }, { children: [linkData.linkTitle, jsx(ArrowIcon, { mode: mode })] })) })));
    }
    if (NextLink && (linkData === null || linkData === void 0 ? void 0 : linkData.internalLink) && isSlugPage(linkData === null || linkData === void 0 ? void 0 : linkData.internalLink)) {
        return (jsx(LinkWrapper$6, __assign$1({ mode: mode }, { children: jsxs(NextLink, __assign$1({ href: "/".concat((_c = linkData.internalLink.slug) === null || _c === void 0 ? void 0 : _c.current).concat(anchorTag) }, { children: [linkData.linkTitle, " ", jsx(ArrowIcon, { mode: mode })] })) })));
    }
    if (linkData === null || linkData === void 0 ? void 0 : linkData.externalUrl) {
        return (jsx(LinkWrapper$6, __assign$1({ mode: mode }, { children: jsxs("a", __assign$1({ href: linkData.externalUrl, target: linkData.anchorTarget ? '_self' : '_blank ', rel: "noreferrer" }, { children: [linkData.linkTitle, jsx(ArrowIcon, { mode: mode })] })) })));
    }
    return null;
};

var Holder = styled.article(templateObject_4$s || (templateObject_4$s = __makeTemplateObject(["\n  display: flex;\n  flex-direction: column;\n  justify-content: flex-start;\n\n  ", "\n\n  ", "\n\n  ", "\n"], ["\n  display: flex;\n  flex-direction: column;\n  justify-content: flex-start;\n\n  ", "\n\n  ", "\n\n  ", "\n"])), function (_a) {
    var alignment = _a.alignment;
    return alignment === 'left' && css(templateObject_1$K || (templateObject_1$K = __makeTemplateObject(["\n      align-items: flex-start;\n      text-align: left;\n    "], ["\n      align-items: flex-start;\n      text-align: left;\n    "])));
}, function (_a) {
    var alignment = _a.alignment;
    return alignment === 'center' && css(templateObject_2$F || (templateObject_2$F = __makeTemplateObject(["\n      align-items: center;\n      text-align: center;\n    "], ["\n      align-items: center;\n      text-align: center;\n    "])));
}, function (_a) {
    var alignment = _a.alignment;
    return alignment === 'right' && css(templateObject_3$x || (templateObject_3$x = __makeTemplateObject(["\n      align-items: flex-end;\n      text-align: right;\n    "], ["\n      align-items: flex-end;\n      text-align: right;\n    "])));
});
var Icon$1 = styled.div(templateObject_5$m || (templateObject_5$m = __makeTemplateObject(["\n  display: flex;\n  justify-content: center;\n  align-items: center;\n  margin-bottom: 25px;\n"], ["\n  display: flex;\n  justify-content: center;\n  align-items: center;\n  margin-bottom: 25px;\n"])));
var InfoLabel = styled(Caption4)(templateObject_6$j || (templateObject_6$j = __makeTemplateObject(["\n  text-transform: uppercase;\n  background: ", ";\n  color: ", ";\n  padding: 8px 16px;\n  border-radius: 8px;\n  margin-left: 25px;\n  min-width: 130px;\n  display: flex;\n  justify-content: center;\n  align-items: center;\n"], ["\n  text-transform: uppercase;\n  background: ", ";\n  color: ", ";\n  padding: 8px 16px;\n  border-radius: 8px;\n  margin-left: 25px;\n  min-width: 130px;\n  display: flex;\n  justify-content: center;\n  align-items: center;\n"])), function (_a) {
    var theme = _a.theme, mode = _a.mode;
    return theme.colors.featureCard[mode].infoLabel.backgroundColor;
}, function (_a) {
    var theme = _a.theme, mode = _a.mode;
    return theme.colors.featureCard[mode].infoLabel.color;
});
var Title$9 = styled.div(templateObject_10$6 || (templateObject_10$6 = __makeTemplateObject(["\n  margin-bottom: 16px;\n\n  ", "\n\n  ", "\n\n  ", "\n"], ["\n  margin-bottom: 16px;\n\n  ", "\n\n  ", "\n\n  ", "\n"])), function (_a) {
    var alignment = _a.alignment;
    return alignment === 'left' && css(templateObject_7$g || (templateObject_7$g = __makeTemplateObject(["\n      text-align: left;\n    "], ["\n      text-align: left;\n    "])));
}, function (_a) {
    var alignment = _a.alignment;
    return alignment === 'center' && css(templateObject_8$b || (templateObject_8$b = __makeTemplateObject(["\n      text-align: center;\n    "], ["\n      text-align: center;\n    "])));
}, function (_a) {
    var alignment = _a.alignment;
    return alignment === 'right' && css(templateObject_9$9 || (templateObject_9$9 = __makeTemplateObject(["\n      text-align: right;\n    "], ["\n      text-align: right;\n    "])));
});
styled(Body1$1)(templateObject_11$4 || (templateObject_11$4 = __makeTemplateObject(["\n  ", " {\n    font-size: 16px;\n    line-height: 26px;\n  }\n"], ["\n  ", " {\n    font-size: 16px;\n    line-height: 26px;\n  }\n"])), function (_a) {
    var theme = _a.theme;
    return theme.breakpoints.sm;
});
var TextContent$3 = styled.div(templateObject_12$3 || (templateObject_12$3 = __makeTemplateObject(["\n  > :is(h1, h2, h3, h4, h5, h6, p, ul) {\n    color: ", ";\n    *:not(a) {\n      color: inherit;\n    }\n  }\n"], ["\n  > :is(h1, h2, h3, h4, h5, h6, p, ul) {\n    color: ", ";\n    *:not(a) {\n      color: inherit;\n    }\n  }\n"])), function (_a) {
    var textColor = _a.textColor;
    return textColor;
});
var LinkWrapper$5 = styled.div(templateObject_13$2 || (templateObject_13$2 = __makeTemplateObject(["\n  margin-top: 24px;\n"], ["\n  margin-top: 24px;\n"])));
var templateObject_1$K, templateObject_2$F, templateObject_3$x, templateObject_4$s, templateObject_5$m, templateObject_6$j, templateObject_7$g, templateObject_8$b, templateObject_9$9, templateObject_10$6, templateObject_11$4, templateObject_12$3, templateObject_13$2;

var FeatureCard = function (_a) {
    var icon = _a.icon, iconBackgroundColor = _a.iconBackgroundColor, title = _a.title, textContent = _a.textContent, _b = _a.alignment, alignment = _b === void 0 ? 'left' : _b, _c = _a.label, label = _c === void 0 ? false : _c, _d = _a.labelText, labelText = _d === void 0 ? '' : _d, linkData = _a.linkData, nextLink = _a.nextLink, fontSizeVariant = _a.fontSizeVariant, _e = _a.mode, mode = _e === void 0 ? 'dark' : _e, textColor = _a.textColor, hasLink = _a.hasLink;
    var shouldShowLink = hasLink && (linkData === null || linkData === void 0 ? void 0 : linkData.linkTitle) && (linkData.externalUrl || linkData.internalLink);
    return (jsxs(Holder, __assign$1({ alignment: alignment }, { children: [icon && (jsxs(Icon$1, { children: [jsx(Icon$4, { name: icon, "data-testid": icon, circleColor: iconBackgroundColor }), label && jsx(InfoLabel, __assign$1({ mode: mode }, { children: labelText }))] })), jsx(Title$9, __assign$1({ alignment: alignment }, { children: fontSizeVariant === 'H5' ? (jsx(Header5, __assign$1({ color: textColor }, { children: title }))) : (jsx(Header6, __assign$1({ color: textColor }, { children: title }))) })), jsxs(TextContent$3, __assign$1({ textColor: textColor }, { children: [textContent, " "] })), shouldShowLink && (jsx(LinkWrapper$5, { children: jsx(LinkWithArrow, { NextLink: nextLink, linkData: linkData, mode: mode }) }))] })));
};

var WrapperCard = styled.div(templateObject_1$J || (templateObject_1$J = __makeTemplateObject(["\n  height: 245px;\n  position: relative;\n  transform-style: preserve-3d;\n  transition: all 0.8s ease;\n  transform: ", ";\n"], ["\n  height: 245px;\n  position: relative;\n  transform-style: preserve-3d;\n  transition: all 0.8s ease;\n  transform: ", ";\n"])), function (props) {
    return props.flip ? 'rotateY(180deg)' : 0;
});
var FrontCardContainer = styled.button(templateObject_2$E || (templateObject_2$E = __makeTemplateObject(["\n  display: flex;\n  flex-direction: column;\n  justify-content: flex-start;\n  align-items: flex-start;\n  text-align: left;\n  border: 1px solid ", ";\n  border-radius: 16px;\n  background-color: ", ";\n  cursor: pointer;\n  backface-visibility: hidden;\n  position: absolute;\n  width: 100%;\n"], ["\n  display: flex;\n  flex-direction: column;\n  justify-content: flex-start;\n  align-items: flex-start;\n  text-align: left;\n  border: 1px solid ", ";\n  border-radius: 16px;\n  background-color: ", ";\n  cursor: pointer;\n  backface-visibility: hidden;\n  position: absolute;\n  width: 100%;\n"])), theme.colors.flippingCard.border, function (_a) {
    var backgroundColor = _a.backgroundColor;
    return backgroundColor;
});
var Icon = styled.div(templateObject_3$w || (templateObject_3$w = __makeTemplateObject(["\n  margin: 17px 0 24px 17px;\n"], ["\n  margin: 17px 0 24px 17px;\n"])));
var IconPlus = styled.div(templateObject_4$r || (templateObject_4$r = __makeTemplateObject(["\n  margin: 0 16px 12px auto;\n"], ["\n  margin: 0 16px 12px auto;\n"])));
var Title$8 = styled(Header5)(templateObject_5$l || (templateObject_5$l = __makeTemplateObject(["\n  color: ", ";\n  margin: 0 16px;\n  text-align: left;\n  overflow: hidden;\n  display: -webkit-box;\n  -webkit-line-clamp: 3;\n  -webkit-box-orient: vertical;\n  height: 100px;\n"], ["\n  color: ", ";\n  margin: 0 16px;\n  text-align: left;\n  overflow: hidden;\n  display: -webkit-box;\n  -webkit-line-clamp: 3;\n  -webkit-box-orient: vertical;\n  height: 100px;\n"])), function (_a) {
    var textColor = _a.textColor;
    return textColor;
});
var ReversedCardContainer = styled.div(templateObject_6$i || (templateObject_6$i = __makeTemplateObject(["\n  background-color: ", ";\n  border-radius: 16px;\n  display: flex;\n  flex-direction: column;\n  justify-content: flex-start;\n  align-items: flex-start;\n  text-align: left;\n  height: 245px;\n  transform: rotateY(180deg);\n  backface-visibility: hidden;\n  position: absolute;\n"], ["\n  background-color: ", ";\n  border-radius: 16px;\n  display: flex;\n  flex-direction: column;\n  justify-content: flex-start;\n  align-items: flex-start;\n  text-align: left;\n  height: 245px;\n  transform: rotateY(180deg);\n  backface-visibility: hidden;\n  position: absolute;\n"])), theme.colors.flippingCard.backgroundReversedCard);
var IconClose = styled.div(templateObject_7$f || (templateObject_7$f = __makeTemplateObject(["\n  cursor: pointer;\n  margin: 16px 16px 20px auto;\n  ", " {\n    margin-bottom: 6px;\n  }\n"], ["\n  cursor: pointer;\n  margin: 16px 16px 20px auto;\n  ", " {\n    margin-bottom: 6px;\n  }\n"])), theme.breakpoints.lg);
var ReversedText = styled(Body3$2)(templateObject_8$a || (templateObject_8$a = __makeTemplateObject(["\n  color: ", ";\n  margin: 0 16px;\n  text-align: left;\n  overflow: hidden;\n  display: -webkit-box;\n  -webkit-line-clamp: 7;\n  -webkit-box-orient: vertical;\n"], ["\n  color: ", ";\n  margin: 0 16px;\n  text-align: left;\n  overflow: hidden;\n  display: -webkit-box;\n  -webkit-line-clamp: 7;\n  -webkit-box-orient: vertical;\n"])), theme.colors.flippingCard.defaultTextColor);
var templateObject_1$J, templateObject_2$E, templateObject_3$w, templateObject_4$r, templateObject_5$l, templateObject_6$i, templateObject_7$f, templateObject_8$a;

var FrontCard = function (_a) {
    var backgroundColor = _a.backgroundColor, onClick = _a.onClick, icon = _a.icon, iconBackgroundColor = _a.iconBackgroundColor, textColor = _a.textColor, title = _a.title;
    return (jsxs(FrontCardContainer, __assign$1({ backgroundColor: backgroundColor, onClick: onClick, "data-testid": "frontCard", tabIndex: 0, "aria-label": "Show more details about ".concat(title) }, { children: [icon && (jsx(Icon, { children: jsx(Icon$4, { name: icon, "data-testid": icon, circleColor: iconBackgroundColor }) })), jsx(Title$8, __assign$1({ textColor: textColor }, { children: title })), jsx(IconPlus, { children: jsx(Icon$4, { name: "ButtonPlus" }) })] })));
};
var ReversedCard = function (_a) {
    var reversedText = _a.reversedText, onClick = _a.onClick;
    return (jsxs(ReversedCardContainer, { children: [jsx(IconClose, __assign$1({ onClick: onClick }, { children: jsx(Icon$4, { name: "ButtonClose" }) })), jsx(ReversedText, { children: reversedText })] }));
};

var FlippingCard = function (_a) {
    var _b = _a.backgroundColor, backgroundColor = _b === void 0 ? theme.colors.flippingCard.defaultBackground : _b, icon = _a.icon, _c = _a.iconBackgroundColor, iconBackgroundColor = _c === void 0 ? theme.colors.flippingCard.iconColor : _c, title = _a.title, _d = _a.textColor, textColor = _d === void 0 ? theme.colors.flippingCard.titleColor : _d, reversedText = _a.reversedText;
    var _e = useState(false), flip = _e[0], setFlip = _e[1];
    return (jsxs(WrapperCard, __assign$1({ flip: flip }, { children: [jsx(FrontCard, { backgroundColor: backgroundColor, onClick: function () { return setFlip(function (prev) { return !prev; }); }, icon: icon, textColor: textColor, title: title, iconBackgroundColor: iconBackgroundColor }), jsx(ReversedCard, { reversedText: reversedText, onClick: function () { return setFlip(function (prev) { return !prev; }); } })] })));
};

var CheckboxWrapper = styled.div(templateObject_1$I || (templateObject_1$I = __makeTemplateObject(["\n  position: relative;\n  max-width: 100%;\n  display: flex;\n"], ["\n  position: relative;\n  max-width: 100%;\n  display: flex;\n"])));
var LabelWrapper = styled.div(templateObject_2$D || (templateObject_2$D = __makeTemplateObject(["\n  label {\n    cursor: pointer;\n  }\n  p {\n    margin-left: 16px;\n  }\n\n  p[role='alert'] {\n    margin-left: 0;\n  }\n"], ["\n  label {\n    cursor: pointer;\n  }\n  p {\n    margin-left: 16px;\n  }\n\n  p[role='alert'] {\n    margin-left: 0;\n  }\n"])));
styled.span(templateObject_3$v || (templateObject_3$v = __makeTemplateObject(["\n  position: absolute;\n  right: 16px;\n  transform: translateY(-50%);\n  top: 50%;\n"], ["\n  position: absolute;\n  right: 16px;\n  transform: translateY(-50%);\n  top: 50%;\n"])));
var templateObject_1$I, templateObject_2$D, templateObject_3$v;

var CheckboxItem = function (_a, ref) {
    var label = _a.label, labelFromTextEditor = _a.labelFromTextEditor, name = _a.name, _b = _a.id, id = _b === void 0 ? name : _b, required = _a.required, onChange = _a.onChange; _a.onBlur; var value = _a.value, _c = _a.mode, mode = _c === void 0 ? 'light' : _c, errorMessage = _a.errorMessage, rest = __rest$1(_a, ["label", "labelFromTextEditor", "name", "id", "required", "onChange", "onBlur", "value", "mode", "errorMessage"]);
    var handleChange = function () {
        onChange(!value);
    };
    var checkboxRef = useRef(null);
    useImperativeHandle(ref, function () { return checkboxRef.current; });
    return (jsx("div", { children: jsxs(CheckboxWrapper, { children: [jsx(Checkbox$1, __assign$1({ ref: ref, name: name, id: id, checked: value, handleChange: handleChange, mode: mode, required: required }, rest)), jsxs(LabelWrapper, { children: [jsxs("label", __assign$1({ htmlFor: id }, { children: [label && (jsx(Caption4, { color: theme.colors.checkboxItem[mode].label, dangerouslySetInnerHTML: {
                                        __html: "".concat(label).concat(required
                                            ? "<span style=\"color: ".concat(theme.colors.checkboxItem[mode].label, "\">&nbsp*</span>")
                                            : ''),
                                    } })), labelFromTextEditor] })), errorMessage && jsx(ErrorMessage, { message: errorMessage, mode: mode })] })] }) }));
};
var CheckboxItem$1 = forwardRef(CheckboxItem);

var InputWrapper = styled.div(templateObject_1$H || (templateObject_1$H = __makeTemplateObject(["\n  position: relative;\n  max-width: 100%;\n"], ["\n  position: relative;\n  max-width: 100%;\n"])));
var Error$1 = styled.span(templateObject_2$C || (templateObject_2$C = __makeTemplateObject(["\n  position: absolute;\n  right: 16px;\n  transform: translateY(-50%);\n  top: 50%;\n"], ["\n  position: absolute;\n  right: 16px;\n  transform: translateY(-50%);\n  top: 50%;\n"])));
var FormItemWrapper = styled.div(templateObject_3$u || (templateObject_3$u = __makeTemplateObject(["\n  position: relative;\n"], ["\n  position: relative;\n"])));
var templateObject_1$H, templateObject_2$C, templateObject_3$u;

var InputItem = function (_a, ref) {
    var errorMessage = _a.errorMessage, required = _a.required, onChange = _a.onChange, name = _a.name, tooltip = _a.tooltip, _b = _a.mode, mode = _b === void 0 ? 'light' : _b, props = __rest$1(_a, ["errorMessage", "required", "onChange", "name", "tooltip", "mode"]);
    return (jsxs(FormItemWrapper, { children: [jsxs(InputWrapper, { children: [jsx(Input$1, __assign$1({ ref: ref, required: required, onChange: onChange, name: name, hasError: !!errorMessage }, props)), errorMessage ? (jsx(Error$1, { children: jsx(Icon$4, { name: "Error", fillColor: theme.colors.errorMessage.light.color }) })) : null] }), errorMessage ? jsx(ErrorMessage, { message: errorMessage, mode: mode }) : null, !errorMessage && tooltip && jsx(Tooltip, { tooltipText: tooltip })] }));
};
var InputItem$1 = forwardRef(InputItem);

var TextAreaWrapper = styled.div(templateObject_1$G || (templateObject_1$G = __makeTemplateObject(["\n  position: relative;\n  max-width: 100%;\n  > label {\n    background-color: white;\n    width: 93%;\n    top: 1px !important;\n    padding-top: 10px;\n    padding-bottom: 5px;\n    ", " {\n      width: 92%;\n    }\n    ", " {\n      width: 94%;\n    }\n  }\n"], ["\n  position: relative;\n  max-width: 100%;\n  > label {\n    background-color: white;\n    width: 93%;\n    top: 1px !important;\n    padding-top: 10px;\n    padding-bottom: 5px;\n    ", " {\n      width: 92%;\n    }\n    ", " {\n      width: 94%;\n    }\n  }\n"])), theme.breakpoints.sm, theme.breakpoints.md);
var Textarea = styled.textarea(templateObject_2$B || (templateObject_2$B = __makeTemplateObject(["\n  border: 1px solid ", ";\n  border-radius: 10px;\n  font-size: ", ";\n  background-color: white;\n  display: block;\n  padding: 28px 17px 18px;\n  width: 100%;\n  height: 164px;\n  resize: vertical;\n  overflow-y: auto;\n\n  &:focus {\n    border: 1px solid ", ";\n    outline: 1px;\n  }\n"], ["\n  border: 1px solid ", ";\n  border-radius: 10px;\n  font-size: ", ";\n  background-color: white;\n  display: block;\n  padding: 28px 17px 18px;\n  width: 100%;\n  height: 164px;\n  resize: vertical;\n  overflow-y: auto;\n\n  &:focus {\n    border: 1px solid ", ";\n    outline: 1px;\n  }\n"])), function (_a) {
    var theme = _a.theme;
    return theme.colors.textAreaItem.border;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.size.desktop.xs;
}, function (_a) {
    var theme = _a.theme;
    return theme.colors.textAreaItem.focus.border;
});
var templateObject_1$G, templateObject_2$B;

var TextAreaItem = function (_a) {
    var name = _a.name, id = _a.id, onChange = _a.onChange, placeholder = _a.placeholder, errorMessage = _a.errorMessage, required = _a.required, rest = __rest$1(_a, ["name", "id", "onChange", "placeholder", "errorMessage", "required"]);
    var inputRef = useRef(null);
    var placeholderText = required ? "".concat(placeholder, "*") : placeholder;
    var _b = useState(false), isFocused = _b[0], setIsFocused = _b[1];
    var _c = useState(false), hasValue = _c[0], setHasValue = _c[1];
    var onFocus = function () {
        setIsFocused(true);
    };
    var onBlur = function () {
        setIsFocused(false);
    };
    useEffect(function () {
        var _a;
        setHasValue(!!((_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.value));
    });
    return (jsxs(TextAreaWrapper, { children: [jsxs(TextAreaWrapper, { children: [placeholderText && (jsx(PlaceholderLabel, __assign$1({ htmlFor: id || name, hasValue: hasValue, isFocused: isFocused, noWrap: false }, { children: placeholderText }))), jsx(Textarea, __assign$1({ required: true, ref: inputRef, onChange: onChange, onFocus: onFocus, onBlur: onBlur, name: name, id: id || name, type: "text" }, rest))] }), errorMessage ? jsx(ErrorMessage, { message: errorMessage }) : null] }));
};

var CardContainer$1 = styled.div(templateObject_1$F || (templateObject_1$F = __makeTemplateObject(["\n  display: flex;\n  flex-direction: column;\n  justify-content: flex-start;\n"], ["\n  display: flex;\n  flex-direction: column;\n  justify-content: flex-start;\n"])));
var ImageBox$1 = styled.div(templateObject_2$A || (templateObject_2$A = __makeTemplateObject(["\n  position: relative;\n  overflow: hidden;\n  border-radius: 32px 32px 0 0;\n  height: 245px;\n  width: 100%;\n\n  ", " {\n    height: 255px;\n  }\n\n  ", " {\n    height: 264px;\n  }\n"], ["\n  position: relative;\n  overflow: hidden;\n  border-radius: 32px 32px 0 0;\n  height: 245px;\n  width: 100%;\n\n  ", " {\n    height: 255px;\n  }\n\n  ", " {\n    height: 264px;\n  }\n"])), theme.breakpoints.sm, theme.breakpoints.lg);
var templateObject_1$F, templateObject_2$A;

var ImageCard = function (_a) {
    var title = _a.title, image = _a.image, description = _a.description;
    return (jsxs(CardContainer$1, { children: [jsx(ImageBox$1, { children: image }), jsx(Gutter, { size: 24 }), jsx(Caption1$1, { children: title }), jsx(Gutter, { size: 16 }), description] }));
};

var LanguagePicker$1 = styled.div(templateObject_1$E || (templateObject_1$E = __makeTemplateObject([""], [""])));
var LanguageList = styled.ul(templateObject_2$z || (templateObject_2$z = __makeTemplateObject(["\n  margin: 0;\n  padding: 0;\n  display: flex;\n  flex-wrap: wrap;\n  flex-direction: row;\n"], ["\n  margin: 0;\n  padding: 0;\n  display: flex;\n  flex-wrap: wrap;\n  flex-direction: row;\n"])));
var LanguageListItem = styled.li.attrs(function (_a) {
    var dataTestid = _a.dataTestid;
    return ({
        'data-testid': dataTestid,
    });
})(templateObject_3$t || (templateObject_3$t = __makeTemplateObject(["\n  margin: 0;\n  padding: 0;\n  display: flex;\n  align-items: center;\n  min-width: 167px;\n  cursor: pointer;\n  border-radius: 10px;\n  transition: background-color 0.4s;\n  a,\n  button {\n    display: flex;\n    padding: 16px 24px 16px 24px;\n    align-items: center;\n  }\n\n  &:hover {\n    background-color: ", ";\n  }\n"], ["\n  margin: 0;\n  padding: 0;\n  display: flex;\n  align-items: center;\n  min-width: 167px;\n  cursor: pointer;\n  border-radius: 10px;\n  transition: background-color 0.4s;\n  a,\n  button {\n    display: flex;\n    padding: 16px 24px 16px 24px;\n    align-items: center;\n  }\n\n  &:hover {\n    background-color: ", ";\n  }\n"])), function (_a) {
    var theme = _a.theme;
    return theme.colors.languagePicker.hover.backgroundColor;
});
var LanguageDropdown = styled.div(templateObject_4$q || (templateObject_4$q = __makeTemplateObject(["\n  display: flex;\n  flex-direction: column;\n  padding: 24px 0px 0px;\n  width: 334px;\n  background: ", ";\n  border-radius: 10px;\n  margin: 0px 0px;\n"], ["\n  display: flex;\n  flex-direction: column;\n  padding: 24px 0px 0px;\n  width: 334px;\n  background: ", ";\n  border-radius: 10px;\n  margin: 0px 0px;\n"])), function (_a) {
    var theme = _a.theme;
    return theme.colors.languagePicker.languageDropdownColor;
});
var LanguageButton = styled(Button$2).attrs({
    'data-testid': 'languageButton',
})(templateObject_6$h || (templateObject_6$h = __makeTemplateObject(["\n  display: flex;\n  align-items: self-start;\n  color: ", ";\n  &:hover {\n    color: ", ";\n  }\n\n  div[role='img'] {\n    line-height: 0;\n\n    path {\n      ", "\n    }\n  }\n"], ["\n  display: flex;\n  align-items: self-start;\n  color: ", ";\n  &:hover {\n    color: ", ";\n  }\n\n  div[role='img'] {\n    line-height: 0;\n\n    path {\n      ", "\n    }\n  }\n"])), function (_a) {
    var theme = _a.theme, mode = _a.mode;
    return mode === 'dark'
        ? theme.crediteaColors.primary.neutral[50]
        : theme.crediteaColors.primary.neutral[800];
}, function (_a) {
    var theme = _a.theme, mode = _a.mode;
    return mode === 'dark'
        ? theme.crediteaColors.secondary.lightBlue[500]
        : theme.crediteaColors.primary.brandBlue[500];
}, function (props) {
    return props.prependIcon === 'FlagGlobal' && css(templateObject_5$k || (templateObject_5$k = __makeTemplateObject(["\n          fill: currentColor;\n        "], ["\n          fill: currentColor;\n        "])));
});
var LanguageAnchor = styled.a(templateObject_7$e || (templateObject_7$e = __makeTemplateObject(["\n  flex: 1;\n"], ["\n  flex: 1;\n"])));
var Caption3 = styled(Caption3$1)(templateObject_8$9 || (templateObject_8$9 = __makeTemplateObject(["\n  margin-left: 24px;\n"], ["\n  margin-left: 24px;\n"])));
var StyledIcon = styled(Icon$4)(templateObject_9$8 || (templateObject_9$8 = __makeTemplateObject(["\n  margin-right: 12px;\n"], ["\n  margin-right: 12px;\n"])));
var templateObject_1$E, templateObject_2$z, templateObject_3$t, templateObject_4$q, templateObject_5$k, templateObject_6$h, templateObject_7$e, templateObject_8$9, templateObject_9$8;

var LanguagePicker = function (_a) {
    var languageList = _a.languageList, countryList = _a.countryList, _b = _a.countryTitle, countryTitle = _b === void 0 ? 'Select your country' : _b, _c = _a.languageTitle, languageTitle = _c === void 0 ? 'Select your language' : _c, currentLanguageText = _a.currentLanguageText, buttonFlag = _a.buttonFlag, _d = _a.hideFlagsIcon, hideFlagsIcon = _d === void 0 ? false : _d, _e = _a.mode, mode = _e === void 0 ? 'light' : _e;
    var buttonRef = useRef(null);
    var _f = useState(false), isOpen = _f[0], setOpen = _f[1];
    var handleClick = function () {
        setOpen(function (prev) { return !prev; });
    };
    var handleClose = function () {
        setOpen(false);
    };
    return (jsxs(LanguagePicker$1, { children: [jsx("div", __assign$1({ ref: buttonRef }, { children: jsxs(LanguageButton, __assign$1({ withoutSurroundingSpacing: true, onClick: handleClick, prependIcon: buttonFlag, variant: "text", mode: mode }, { children: [!hideFlagsIcon && (jsxs(Fragment, { children: [jsx(Icon$4, { name: "FlagsChange" }), jsx(Gutter, { size: 10 })] })), currentLanguageText] })) })), jsx(DropDown, __assign$1({ handleClose: handleClose, open: isOpen, togglerRef: buttonRef }, { children: jsxs(LanguageDropdown, { children: [(countryList === null || countryList === void 0 ? void 0 : countryList.length) ? (jsxs(Fragment, { children: [jsx(Caption3, { children: countryTitle }), jsx(LanguageList, { children: countryList.map(function (item) { return (jsx(LanguageListItem, { children: jsxs(LanguageAnchor, __assign$1({ href: item.href, target: item.anchorTarget === false ? '_blank ' : '_self', rel: "noreferrer" }, { children: [item.icon ? (jsx(StyledIcon, { width: 20, height: 20, name: item.icon })) : null, jsx(Body4, { children: item.name })] })) }, item.name)); }) })] })) : null, (languageList === null || languageList === void 0 ? void 0 : languageList.length) ? (jsxs(Fragment, { children: [jsx(Caption3, { children: languageTitle }), jsx(LanguageList, { children: languageList.map(function (item, index) { return (jsx(LanguageListItem, __assign$1({ dataTestid: index === 0 ? 'primaryLanguage' : 'secondaryLanguage', onClick: handleClose }, { children: jsx(Button$2, __assign$1({ variant: "text", onClick: item.onClick }, { children: jsx(Body4, { children: item.name }) })) }), item.name)); }) })] })) : null] }) }))] }));
};

var LinkStyle = styled.a(templateObject_3$s || (templateObject_3$s = __makeTemplateObject(["\n  text-decoration: none;\n  display: flex;\n  align-items: center;\n  cursor: pointer;\n\n  ", "\n\n  span {\n    color: ", ";\n  }\n\n  &:hover {\n    span {\n      color: ", ";\n    }\n  }\n"], ["\n  text-decoration: none;\n  display: flex;\n  align-items: center;\n  cursor: pointer;\n\n  ", "\n\n  span {\n    color: ", ";\n  }\n\n  &:hover {\n    span {\n      color: ", ";\n    }\n  }\n"])), function (_a) {
    var variant = _a.variant;
    return variant === 'ultraLight'
        ? css(templateObject_1$D || (templateObject_1$D = __makeTemplateObject(["\n          padding: 5px 0;\n\n          ", " {\n            padding: 7px 0;\n          }\n        "], ["\n          padding: 5px 0;\n\n          ", " {\n            padding: 7px 0;\n          }\n        "])), function (_a) {
            var theme = _a.theme;
            return theme.breakpoints.md;
        }) : css(templateObject_2$y || (templateObject_2$y = __makeTemplateObject(["\n          padding: 15px 0;\n        "], ["\n          padding: 15px 0;\n        "])));
}, function (_a) {
    var color = _a.color, theme = _a.theme;
    return "".concat(color) || "".concat(theme.colors.linkBody.color);
}, function (_a) {
    var color = _a.color, theme = _a.theme;
    return "".concat(color) || "".concat(theme.colors.linkBody.hover.color);
});
var Arrow = styled.span(templateObject_4$p || (templateObject_4$p = __makeTemplateObject(["\n  position: relative;\n  top: 2px;\n  ", "\n"], ["\n  position: relative;\n  top: 2px;\n  ", "\n"])), function (_a) {
    var arrowLeft = _a.arrowLeft;
    return (arrowLeft ? "padding-right: 15px;" : "padding-left: 15px;");
});
var templateObject_1$D, templateObject_2$y, templateObject_3$s, templateObject_4$p;

var LinkBody = function (_a) {
    var name = _a.name, variant = _a.variant, color = _a.color, href = _a.href, arrowLeft = _a.arrowLeft, arrowRight = _a.arrowRight, target = _a.target;
    return (jsxs(LinkStyle, __assign$1({ variant: variant, color: color, href: href, target: target }, { children: [arrowLeft && (jsx(Arrow, __assign$1({ arrowLeft: true }, { children: jsx(Icon$4, { name: "ArrowLeftOn", fillColor: color }) }))), variant === 'bold' && jsx(Link1, { children: name }), variant === 'light' && jsx(Link2, { children: name }), variant === 'ultraLight' && jsx(Body4, { children: name }), arrowRight && (jsx(Arrow, { children: jsx(Icon$4, { name: "ArrowRightOn", fillColor: color }) }))] })));
};

var LinkSection$1 = styled.section(templateObject_3$r || (templateObject_3$r = __makeTemplateObject(["\n  margin: 0;\n\n  ", "\n\n  ", "\n"], ["\n  margin: 0;\n\n  ", "\n\n  ", "\n"])), function (_a) {
    var $isSidebar = _a.$isSidebar, theme = _a.theme;
    return $isSidebar && css(templateObject_1$C || (templateObject_1$C = __makeTemplateObject(["\n      margin: ", " 0 ", ";\n\n      ", " {\n        margin: ", " 0 ", ";\n      }\n    "], ["\n      margin: ", " 0 ", ";\n\n      ", " {\n        margin: ", " 0 ", ";\n      }\n    "])), function (_a) {
        var theme = _a.theme;
        return theme.space[60];
    }, function (_a) {
        var theme = _a.theme;
        return theme.space[100];
    }, theme.breakpoints.sm, function (_a) {
        var theme = _a.theme;
        return theme.space[60];
    }, function (_a) {
        var theme = _a.theme;
        return theme.space[140];
    });
}, function (_a) {
    var $isMultiLine = _a.$isMultiLine, theme = _a.theme;
    return $isMultiLine && css(templateObject_2$x || (templateObject_2$x = __makeTemplateObject(["\n      margin: ", " 0;\n\n      ", " {\n        margin: ", " 0;\n      }\n    "], ["\n      margin: ", " 0;\n\n      ", " {\n        margin: ", " 0;\n      }\n    "])), function (_a) {
        var theme = _a.theme;
        return theme.space[20];
    }, theme.breakpoints.sm, function (_a) {
        var theme = _a.theme;
        return theme.space[40];
    });
});
var ContainerHolder = styled(Row$1)(templateObject_5$j || (templateObject_5$j = __makeTemplateObject(["\n  &&& {\n    ", "\n  }\n"], ["\n  &&& {\n    ", "\n  }\n"])), function (_a) {
    var $isSidebar = _a.$isSidebar;
    return $isSidebar && css(templateObject_4$o || (templateObject_4$o = __makeTemplateObject(["\n        width: calc(100% + 36px);\n      "], ["\n        width: calc(100% + 36px);\n      "])));
});
var GridHolder = styled(Col)(templateObject_7$d || (templateObject_7$d = __makeTemplateObject(["\n  &&& {\n    ", " {\n      max-width: 50%;\n    }\n    ", "\n  }\n"], ["\n  &&& {\n    ", " {\n      max-width: 50%;\n    }\n    ", "\n  }\n"])), function (_a) {
    var theme = _a.theme;
    return theme.breakpoints.sm;
}, function (_a) {
    var $isSidebar = _a.$isSidebar;
    return $isSidebar && css(templateObject_6$g || (templateObject_6$g = __makeTemplateObject(["\n        width: calc(100% + 24px);\n        &:nth-child(odd) {\n          padding-left: ", ";\n\n          ", " {\n            padding-left: 0;\n          }\n        }\n\n        &:nth-child(even) {\n          padding-left: ", ";\n\n          ", " {\n            padding-left: ", ";\n          }\n        }\n      "], ["\n        width: calc(100% + 24px);\n        &:nth-child(odd) {\n          padding-left: ", ";\n\n          ", " {\n            padding-left: 0;\n          }\n        }\n\n        &:nth-child(even) {\n          padding-left: ", ";\n\n          ", " {\n            padding-left: ", ";\n          }\n        }\n      "])), function (_a) {
        var theme = _a.theme;
        return theme.space[10];
    }, function (_a) {
        var theme = _a.theme;
        return theme.breakpoints.sm;
    }, function (_a) {
        var theme = _a.theme;
        return theme.space[10];
    }, function (_a) {
        var theme = _a.theme;
        return theme.breakpoints.sm;
    }, function (_a) {
        var theme = _a.theme;
        return theme.space[24];
    });
});
var templateObject_1$C, templateObject_2$x, templateObject_3$r, templateObject_4$o, templateObject_5$j, templateObject_6$g, templateObject_7$d;

var LinkSection = function (_a) {
    var links = _a.links, _b = _a.isSidebar, isSidebar = _b === void 0 ? false : _b, _c = _a.isMultiLine, isMultiLine = _c === void 0 ? false : _c, NextLinkComponent = _a.NextLinkComponent;
    return (jsx(LinkSection$1, __assign$1({ "$isMultiLine": isMultiLine, "$isSidebar": isSidebar }, { children: jsx(ContainerHolder, __assign$1({ "$isSidebar": isSidebar }, { children: links === null || links === void 0 ? void 0 : links.map(function (link, i) {
                var _a;
                return (jsx(GridHolder, __assign$1({ "$isSidebar": isSidebar, xs: 4, sm: 6, md: isSidebar ? 6 : 4 }, { children: NextLinkComponent ? (jsx(NextLinkComponent, __assign$1({ href: (_a = link.href) !== null && _a !== void 0 ? _a : '', passHref: true, target: link.anchorTarget === false ? '_blank ' : '_self' }, { children: jsx(Card, { name: link.name, icon: link.icon, subDescription: link.subDescription, arrow: true }) }))) : (jsx("a", __assign$1({ href: link.href, target: link.anchorTarget === false ? '_blank ' : '_self', rel: "noreferrer" }, { children: jsx(Card, { name: link.name, icon: link.icon, subDescription: link.subDescription, arrow: true }) }))) }), i));
            }) })) })));
};

var LinksWithTitle$1 = styled.div(templateObject_1$B || (templateObject_1$B = __makeTemplateObject(["\n  padding-top: ", ";\n  ", " {\n    padding-top: ", ";\n  }\n  border-top: ", ";\n  margin: ", ";\n  ", " {\n    margin: ", ";\n  }\n"], ["\n  padding-top: ", ";\n  ", " {\n    padding-top: ", ";\n  }\n  border-top: ", ";\n  margin: ", ";\n  ", " {\n    margin: ", ";\n  }\n"])), function (_a) {
    var theme = _a.theme;
    return theme.space[24];
}, function (_a) {
    var theme = _a.theme;
    return theme.breakpoints.sm;
}, function (_a) {
    var theme = _a.theme;
    return theme.space[32];
}, function (_a) {
    var theme = _a.theme, borderTop = _a.borderTop;
    return borderTop ? "1px solid ".concat(theme.colors.linksWidthTitle.borderTop) : 'none';
}, function (_a) {
    var theme = _a.theme;
    return "".concat(theme.space[24], " 0");
}, function (_a) {
    var theme = _a.theme;
    return theme.breakpoints.sm;
}, function (_a) {
    var theme = _a.theme;
    return "".concat(theme.space[32], " 0");
});
var Title$7 = styled(Caption2$2)(templateObject_2$w || (templateObject_2$w = __makeTemplateObject(["\n  font-size: ", ";\n  padding-bottom: ", ";\n"], ["\n  font-size: ", ";\n  padding-bottom: ", ";\n"])), function (_a) {
    var theme = _a.theme;
    return theme.fonts.size.mobile.m;
}, function (_a) {
    var theme = _a.theme;
    return "".concat(theme.space[16]);
});
var templateObject_1$B, templateObject_2$w;

var LinksWithTitle = function (_a) {
    var title = _a.title, links = _a.links, linksTarget = _a.linksTarget, _b = _a.borderTop, borderTop = _b === void 0 ? true : _b, NextLinkComponent = _a.NextLinkComponent;
    return (jsxs(LinksWithTitle$1, __assign$1({ borderTop: borderTop }, { children: [title && jsx(Title$7, { children: title }), links === null || links === void 0 ? void 0 : links.map(function (link, index) { return (jsxs("div", { children: [jsx(FullWidthLink, { title: link.title, href: link.href, target: linksTarget, NextLinkComponent: link.internalLink ? NextLinkComponent : undefined }), link.description && (jsxs(Fragment, { children: [jsx(Body3$2, __assign$1({ color: theme.colors.linksWidthTitle.description }, { children: link.description })), jsx(Gutter, { size: 40 })] }))] }, index)); })] })));
};

var MenuItemBody = styled.div(templateObject_1$A || (templateObject_1$A = __makeTemplateObject(["\n  position: relative;\n  padding: 20px 0;\n  display: flex;\n  flex-direction: row;\n  align-items: center;\n\n  ", "\n"], ["\n  position: relative;\n  padding: 20px 0;\n  display: flex;\n  flex-direction: row;\n  align-items: center;\n\n  ", "\n"])), function (_a) {
    var active = _a.active, theme = _a.theme;
    return active &&
        "\n      &:before {\n        content: '';\n        position: absolute;\n        left: 0;\n        top: 0;\n        display: block;\n        height: 100%;\n        width: 5px;\n        background-color: ".concat(theme.colors.menuItemBody.backgroundColor, ";\n      }\n    ");
});
var Title$6 = styled(Body2)(templateObject_2$v || (templateObject_2$v = __makeTemplateObject(["\n  color: ", ";\n"], ["\n  color: ", ";\n"])), function (_a) {
    var active = _a.active, theme = _a.theme;
    return active ? theme.colors.menuItemBody.active.color : theme.colors.menuItemBody.color;
});
var Icons = styled(Icon$4)(templateObject_3$q || (templateObject_3$q = __makeTemplateObject(["\n  display: flex;\n"], ["\n  display: flex;\n"])));
var IconWrapper = styled.div(templateObject_4$n || (templateObject_4$n = __makeTemplateObject(["\n  display: flex;\n  width: 84px;\n  justify-content: center;\n"], ["\n  display: flex;\n  width: 84px;\n  justify-content: center;\n"])));
var Spacer = styled.span(templateObject_5$i || (templateObject_5$i = __makeTemplateObject(["\n  width: 25px;\n"], ["\n  width: 25px;\n"])));
var templateObject_1$A, templateObject_2$v, templateObject_3$q, templateObject_4$n, templateObject_5$i;

var MenuBodyItem = function (_a) {
    var icon = _a.icon, title = _a.title, active = _a.active;
    return (jsxs(MenuItemBody, __assign$1({ active: active }, { children: [icon ? (jsx(IconWrapper, { children: jsx(Icons, { name: icon }) })) : (jsx(Spacer, {})), jsx(Title$6, __assign$1({ active: active }, { children: title }))] })));
};

var fadeIn$2 = keyframes(templateObject_1$z || (templateObject_1$z = __makeTemplateObject(["\n  0%, 40% {\n    transform: scaleX(0);\n  }\n  50%, 100% {\n    transform: scaleX(1);\n  }\n"], ["\n  0%, 40% {\n    transform: scaleX(0);\n  }\n  50%, 100% {\n    transform: scaleX(1);\n  }\n"])));
var fadeOut$2 = keyframes(templateObject_2$u || (templateObject_2$u = __makeTemplateObject(["\n  0%, 40% {\n    transform: scaleX(1);\n  }\n  50%, 100% {\n    transform: scaleX(0);\n}\n"], ["\n  0%, 40% {\n    transform: scaleX(1);\n  }\n  50%, 100% {\n    transform: scaleX(0);\n}\n"])));
var Drawer = styled.div(templateObject_6$f || (templateObject_6$f = __makeTemplateObject(["\n  transform: none;\n  transform-origin: right;\n  overflow-y: auto;\n  display: flex;\n  flex-direction: column;\n  height: 100%;\n  width: 100%;\n  flex: 1 0 auto;\n  z-index: ", ";\n  position: fixed;\n  padding-bottom: 50%;\n  top: 0px;\n  outline: 0px;\n  right: 0px;\n  background-color: ", ";\n  padding-top: 114px;\n  ", "\n  ", ";\n\n  animation: ", ";\n\n  ", " {\n    display: none;\n  }\n"], ["\n  transform: none;\n  transform-origin: right;\n  overflow-y: auto;\n  display: flex;\n  flex-direction: column;\n  height: 100%;\n  width: 100%;\n  flex: 1 0 auto;\n  z-index: ", ";\n  position: fixed;\n  padding-bottom: 50%;\n  top: 0px;\n  outline: 0px;\n  right: 0px;\n  background-color: ", ";\n  padding-top: 114px;\n  ", "\n  ", ";\n\n  animation: ", ";\n\n  ", " {\n    display: none;\n  }\n"])), function (_a) {
    var theme = _a.theme;
    return theme.customZIndex.primary;
}, function (_a) {
    var theme = _a.theme;
    return theme.colors.mobileDrawer.backgroundColor;
}, function (_a) {
    var hasPromoBar = _a.hasPromoBar, promoBarOnlyMobile = _a.promoBarOnlyMobile;
    return hasPromoBar &&
        promoBarOnlyMobile && css(templateObject_3$p || (templateObject_3$p = __makeTemplateObject(["\n     padding-top: 194px;\n        ", " {\n          padding-top: 114px;\n       "], ["\n     padding-top: 194px;\n        ", " {\n          padding-top: 114px;\n       "])), function (_a) {
        var theme = _a.theme;
        return theme.breakpoints.sm;
    });
}, function (_a) {
    var hasPromoBar = _a.hasPromoBar, promoBarOnlyMobile = _a.promoBarOnlyMobile;
    return hasPromoBar &&
        !promoBarOnlyMobile && css(templateObject_4$m || (templateObject_4$m = __makeTemplateObject(["\n      padding-top: 194px;\n      ", " {\n        padding-top: 172px;\n    "], ["\n      padding-top: 194px;\n      ", " {\n        padding-top: 172px;\n    "])), function (_a) {
        var theme = _a.theme;
        return theme.breakpoints.sm;
    });
}, function (_a) {
    var isOpen = _a.isOpen;
    return css(templateObject_5$h || (templateObject_5$h = __makeTemplateObject(["\n      ", " 0.5s ease alternate\n    "], ["\n      ", " 0.5s ease alternate\n    "])), isOpen ? fadeIn$2 : fadeOut$2);
}, function (_a) {
    var theme = _a.theme;
    return theme.breakpoints.lg;
});
var templateObject_1$z, templateObject_2$u, templateObject_3$p, templateObject_4$m, templateObject_5$h, templateObject_6$f;

var MobileDrawer = function (_a) {
    var children = _a.children, isOpen = _a.isOpen, rest = __rest$1(_a, ["children", "isOpen"]);
    var isDrawerShown = useDelayUnmount(isOpen);
    useEffect(function () {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        }
        else {
            document.body.style.overflow = 'auto';
        }
    }, [isOpen]);
    return isDrawerShown ? (jsx(Drawer, __assign$1({ isOpen: isOpen }, rest, { children: jsx(Container$1, { children: children }) }))) : null;
};

var fadeIn$1 = keyframes(templateObject_1$y || (templateObject_1$y = __makeTemplateObject(["\n  from {\n    opacity: 0;\n  }\n\n  to {\n    opacity: 1;\n  }\n"], ["\n  from {\n    opacity: 0;\n  }\n\n  to {\n    opacity: 1;\n  }\n"])));
var fadeOut$1 = keyframes(templateObject_2$t || (templateObject_2$t = __makeTemplateObject(["\n  from {\n    opacity: 1;\n  }\n\n  to {\n    opacity: 0;\n  }\n"], ["\n  from {\n    opacity: 1;\n  }\n\n  to {\n    opacity: 0;\n  }\n"])));
var minWidthCss = function (theme) {
    return css(templateObject_3$o || (templateObject_3$o = __makeTemplateObject(["\n    ", " {\n      min-width: 320px;\n    }\n\n    ", " {\n      min-width: 560px;\n    }\n  "], ["\n    ", " {\n      min-width: 320px;\n    }\n\n    ", " {\n      min-width: 560px;\n    }\n  "])), theme.breakpoints.xs, theme.breakpoints.sm);
};
var maxHeightCss = function (theme, buttonsCount) {
    return css(templateObject_4$l || (templateObject_4$l = __makeTemplateObject(["\n    ", " {\n      max-height: calc(100vh - ", "px);\n    }\n\n    ", " {\n      max-height: calc(100vh - ", "px);\n    }\n  "], ["\n    ", " {\n      max-height: calc(100vh - ", "px);\n    }\n\n    ", " {\n      max-height: calc(100vh - ", "px);\n    }\n  "])), theme.breakpoints.xs, buttonsCount * 76 + 180, theme.breakpoints.sm, 76 + 180);
};
var Modal$1 = styled.div(templateObject_6$e || (templateObject_6$e = __makeTemplateObject(["\n  position: fixed;\n  top: 0;\n  left: 0;\n  right: 0;\n  bottom: 0;\n  background-color: ", ";\n  z-index: ", ";\n  display: flex;\n  align-items: center;\n  justify-content: center;\n  animation: ", ";\n"], ["\n  position: fixed;\n  top: 0;\n  left: 0;\n  right: 0;\n  bottom: 0;\n  background-color: ", ";\n  z-index: ", ";\n  display: flex;\n  align-items: center;\n  justify-content: center;\n  animation: ", ";\n"])), function (_a) {
    var theme = _a.theme;
    return "".concat(theme.colors.modal.overlay.backgroundColor, "99");
}, function (_a) {
    var theme = _a.theme;
    return theme.customZIndex.secondary;
}, function (_a) {
    var isOpen = _a.isOpen;
    return css(templateObject_5$g || (templateObject_5$g = __makeTemplateObject(["\n    ", " 1s ease alternate\n  "], ["\n    ", " 1s ease alternate\n  "])), isOpen ? fadeIn$1 : fadeOut$1);
});
var ModalContainer = styled.div(templateObject_7$c || (templateObject_7$c = __makeTemplateObject(["\n  background-color: ", ";\n  border-radius: 20px;\n  width: auto;\n  height: auto;\n  max-width: 800px;\n  ", "\n  padding: 30px;\n  margin: 20px;\n"], ["\n  background-color: ", ";\n  border-radius: 20px;\n  width: auto;\n  height: auto;\n  max-width: 800px;\n  ", "\n  padding: 30px;\n  margin: 20px;\n"])), function (_a) {
    var theme = _a.theme;
    return theme.colors.modal.container.backgroundColor;
}, function (_a) {
    var theme = _a.theme;
    return minWidthCss(theme);
});
var ModalContent = styled.div(templateObject_8$8 || (templateObject_8$8 = __makeTemplateObject(["\n  overflow-y: auto;\n  ", "\n"], ["\n  overflow-y: auto;\n  ", "\n"])), function (_a) {
    var theme = _a.theme, buttonsCount = _a.buttonsCount;
    return maxHeightCss(theme, buttonsCount);
});
var ModalClose = styled.button(templateObject_9$7 || (templateObject_9$7 = __makeTemplateObject(["\n  cursor: pointer;\n  background: none;\n  padding: 0;\n  border: none;\n"], ["\n  cursor: pointer;\n  background: none;\n  padding: 0;\n  border: none;\n"])));
var templateObject_1$y, templateObject_2$t, templateObject_3$o, templateObject_4$l, templateObject_5$g, templateObject_6$e, templateObject_7$c, templateObject_8$8, templateObject_9$7;

var Modal = function (_a) {
    var buttons = _a.buttons, isOpen = _a.isOpen, children = _a.children, handleClose = _a.handleClose, modalCloseButton = _a.modalCloseButton, title = _a.title;
    var isModalShown = useDelayUnmount(isOpen);
    // No document is available for SSR
    if (typeof window !== 'object') {
        return null;
    }
    var targetDocumentElement = document.querySelector('main') || document.body;
    return (jsx(Fragment, { children: createPortal(isModalShown ? (jsx(Modal$1, __assign$1({ isOpen: isOpen, onClick: function () { return handleClose && handleClose(); } }, { children: jsxs(ModalContainer, __assign$1({ onClick: function (event) { return event.stopPropagation(); } }, { children: [jsxs(Row$1, { children: [jsx(Col, __assign$1({ xs: true }, { children: jsx(Header3$2, { children: title }) })), modalCloseButton && (jsx(Col, { children: jsx(ModalClose, __assign$1({ onClick: function () { return handleClose && handleClose(); } }, { children: jsx(Icon$4, { name: "ButtonModalClose" }) })) }))] }), jsx(Gutter, { size: 16 }), jsx(ModalContent, __assign$1({ buttonsCount: (buttons === null || buttons === void 0 ? void 0 : buttons.length) || 0 }, { children: children })), jsx(Gutter, { size: 24 }), buttons && jsx(ButtonsLayout, { children: buttons })] })) }))) : null, targetDocumentElement) }));
};

var PaginationWrapper = styled.div(templateObject_1$x || (templateObject_1$x = __makeTemplateObject(["\n  border-top: 1px solid ", ";\n  margin-top: 32px;\n  margin-bottom: 100px;\n  padding-top: 15px;\n"], ["\n  border-top: 1px solid ", ";\n  margin-top: 32px;\n  margin-bottom: 100px;\n  padding-top: 15px;\n"])), function (_a) {
    var theme = _a.theme;
    return theme.colors.pagination.wrapper.borderTop;
});
var PagesCol = styled(Col)(templateObject_2$s || (templateObject_2$s = __makeTemplateObject(["\n  &&& {\n    padding-left: ", ";\n    ", " {\n      padding-left: 24px;\n    }\n    ", " {\n      padding-left: 32px;\n    }\n  }\n"], ["\n  &&& {\n    padding-left: ", ";\n    ", " {\n      padding-left: 24px;\n    }\n    ", " {\n      padding-left: 32px;\n    }\n  }\n"])), function (_a) {
    var language = _a.language;
    return (language === ('LV' ) ? 0 : '16px');
}, function (_a) {
    var theme = _a.theme;
    return theme.breakpoints.sm;
}, function (_a) {
    var theme = _a.theme;
    return theme.breakpoints.md;
});
var PagesWrapper = styled.ul(templateObject_3$n || (templateObject_3$n = __makeTemplateObject(["\n  display: flex;\n  flex-direction: row;\n  padding-left: 0;\n  align-self: center;\n"], ["\n  display: flex;\n  flex-direction: row;\n  padding-left: 0;\n  align-self: center;\n"])));
var Page = styled.li(templateObject_5$f || (templateObject_5$f = __makeTemplateObject(["\n  margin: 0 8px;\n  width: 26px;\n  height: 26px;\n  border-radius: 26px;\n  display: block;\n  text-align: center;\n  color: ", ";\n  font-weight: ", ";\n  font-size: ", ";\n  line-height: ", ";\n  ", "\n\n  &:hover {\n    cursor: pointer;\n    color: ", ";\n    background-color: ", ";\n  }\n"], ["\n  margin: 0 8px;\n  width: 26px;\n  height: 26px;\n  border-radius: 26px;\n  display: block;\n  text-align: center;\n  color: ", ";\n  font-weight: ", ";\n  font-size: ", ";\n  line-height: ", ";\n  ", "\n\n  &:hover {\n    cursor: pointer;\n    color: ", ";\n    background-color: ", ";\n  }\n"])), function (_a) {
    var theme = _a.theme;
    return theme.colors.pagination.pageControl.color;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.weight.medium500;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.size.desktop.xs;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.lineHeight.desktop.xs;
}, function (props) {
    return props.isActive === true && css(templateObject_4$k || (templateObject_4$k = __makeTemplateObject(["\n      background-color: ", ";\n      color: ", ";\n    "], ["\n      background-color: ", ";\n      color: ", ";\n    "])), function (_a) {
        var theme = _a.theme;
        return theme.colors.pagination.pageControl.active.backgroundColor;
    }, function (_a) {
        var theme = _a.theme;
        return theme.colors.pagination.pageControl.active.color;
    });
}, function (_a) {
    var theme = _a.theme;
    return theme.colors.pagination.pageControl.active.color;
}, function (_a) {
    var theme = _a.theme;
    return theme.colors.pagination.pageControl.active.backgroundColor;
});
var PreviousPage = styled(Col)(templateObject_6$d || (templateObject_6$d = __makeTemplateObject(["\n  &&& {\n    padding-left: ", ";\n    ", " {\n      padding-left: 24px;\n    }\n    ", " {\n      padding-left: 32px;\n    }\n\n    button {\n      align-self: flex-end;\n      min-width: auto;\n      width: auto;\n      padding: 17px 0;\n    }\n  }\n"], ["\n  &&& {\n    padding-left: ", ";\n    ", " {\n      padding-left: 24px;\n    }\n    ", " {\n      padding-left: 32px;\n    }\n\n    button {\n      align-self: flex-end;\n      min-width: auto;\n      width: auto;\n      padding: 17px 0;\n    }\n  }\n"])), function (_a) {
    var language = _a.language;
    return (language === ('LV' ) ? 0 : '16px');
}, function (_a) {
    var theme = _a.theme;
    return theme.breakpoints.sm;
}, function (_a) {
    var theme = _a.theme;
    return theme.breakpoints.md;
});
var NextPage = styled(Col)(templateObject_7$b || (templateObject_7$b = __makeTemplateObject(["\n  &&& {\n    padding-left: ", ";\n    ", " {\n      padding-left: 24px;\n    }\n    ", " {\n      padding-left: 32px;\n    }\n\n    button {\n      align-self: flex-start;\n      min-width: auto;\n      width: auto;\n      padding: 17px 0;\n    }\n  }\n"], ["\n  &&& {\n    padding-left: ", ";\n    ", " {\n      padding-left: 24px;\n    }\n    ", " {\n      padding-left: 32px;\n    }\n\n    button {\n      align-self: flex-start;\n      min-width: auto;\n      width: auto;\n      padding: 17px 0;\n    }\n  }\n"])), function (_a) {
    var language = _a.language;
    return (language === ('LV' ) ? 0 : '16px');
}, function (_a) {
    var theme = _a.theme;
    return theme.breakpoints.sm;
}, function (_a) {
    var theme = _a.theme;
    return theme.breakpoints.md;
});
var templateObject_1$x, templateObject_2$s, templateObject_3$n, templateObject_4$k, templateObject_5$f, templateObject_6$d, templateObject_7$b;

var Pagination = function (_a) {
    var handlePage = _a.handlePage, currentPage = _a.currentPage, resourcesCount = _a.resourcesCount, perPage = _a.perPage, previousText = _a.previousText, nextText = _a.nextText, offset = _a.offset, language = _a.language;
    var totalPages = Math.ceil((resourcesCount - offset) / perPage + 1);
    var limitPages = totalPages > 3 ? 3 : totalPages;
    var pages = Array.from({ length: limitPages }, function (_, page) {
        if (currentPage + 1 === totalPages) {
            return currentPage - page + 1;
        }
        if (currentPage > totalPages - 2) {
            return currentPage - page;
        }
        return page + currentPage;
    }).sort(function (a, b) { return a - b; });
    return (jsx(Container$1, __assign$1({ "aria-label": "pagination" }, { children: jsx(PaginationWrapper, { children: jsxs(Row$1, __assign$1({ center: "xs" }, { children: [jsx(PreviousPage, __assign$1({ md: 4, language: language }, { children: jsx(Button$2, __assign$1({ variant: "text", disabled: currentPage === 1, onClick: function () { return handlePage(currentPage - 1); } }, { children: previousText }), "previous") })), jsx(PagesCol, __assign$1({ md: 4, language: language }, { children: jsx(PagesWrapper, { children: pages.map(function (page) { return (jsx(Page, __assign$1({ id: "page".concat(page), isActive: page === currentPage, onClick: function () { return handlePage(page); } }, { children: page }), page)); }) }) })), jsx(NextPage, __assign$1({ md: 4, language: language }, { children: jsx(Button$2, __assign$1({ variant: "text", disabled: currentPage === totalPages, onClick: function () { return handlePage(currentPage + 1); } }, { children: nextText }), "next") }))] })) }) })));
};

var Wrapper$3 = styled.div(templateObject_1$w || (templateObject_1$w = __makeTemplateObject(["\n  display: flex;\n  gap: 5px;\n  align-items: center;\n  height: 31px;\n"], ["\n  display: flex;\n  gap: 5px;\n  align-items: center;\n  height: 31px;\n"])));
var Text = styled.span(templateObject_2$r || (templateObject_2$r = __makeTemplateObject(["\n  font-size: ", ";\n  color: ", ";\n"], ["\n  font-size: ", ";\n  color: ", ";\n"])), function (_a) {
    var theme = _a.theme;
    return theme.fonts.size.mobile.xs;
}, function (_a) {
    var theme = _a.theme, mode = _a.mode;
    return theme.colors.providentTextLogo[mode].color;
});
styled.div(templateObject_3$m || (templateObject_3$m = __makeTemplateObject(["\n  padding: 20px;\n  background-color: ", ";\n"], ["\n  padding: 20px;\n  background-color: ", ";\n"])), function (_a) {
    var mode = _a.mode, theme = _a.theme;
    return mode === 'dark'
        ? theme.crediteaColors.primary.neutral[50]
        : theme.crediteaColors.primary.brandBlue[600];
});
var templateObject_1$w, templateObject_2$r, templateObject_3$m;

var ProvidentTextLogo = function (_a) {
    var text = _a.text, mode = _a.mode, url = _a.url;
    return (jsxs(Wrapper$3, { children: [jsx(Text, __assign$1({ mode: mode }, { children: text })), jsx("a", __assign$1({ href: url, target: "_blank", rel: "noreferrer", "aria-label": "Link to Provident Page" }, { children: jsx(Icon$4, { name: "LogoProvident".concat(mode === 'dark' ? 'Color' : 'Light'), style: { display: 'flex' } }) }))] }));
};

var SliderWrapper = styled.div(templateObject_1$v || (templateObject_1$v = __makeTemplateObject(["\n  ", "\n  ", "\n"], ["\n  ", "\n  ", "\n"])), function (_a) {
    var theme = _a.theme, type = _a.type;
    return type === 'labeled' &&
        "\n    display: grid;\n    grid-column-gap: 24px;\n    grid-row-gap: 12px;\n    grid-template-columns: repeat(2, auto);\n    grid-template-rows: repeat(2, 1fr);\n    margin: 0 0 ".concat(theme.space[24], ";\n\n    ").concat(theme.breakpoints.md, " {\n      margin: ").concat(theme.space[40], " 0 ").concat(theme.space[20], ";\n      align-items: center;\n      grid-template-rows: 1fr;\n      grid-template-columns: 108px 1fr 108px;\n    }\n    > :last-child {\n      text-align: right;\n      ").concat(theme.breakpoints.md, " {\n        font-size: ").concat(theme.fonts.size.tablet.l, ";\n      }\n    }\n  ");
}, function (_a) {
    var theme = _a.theme, type = _a.type;
    return type === 'compact' &&
        "\n    display: grid;\n    grid-column-gap: 24px;\n    grid-row-gap: 12px;\n    grid-template-columns: repeat(2, auto);\n    grid-template-rows: repeat(2, 0.5fr);\n    margin: ".concat(theme.space[24], " 0 0};\n\n    ").concat(theme.breakpoints.sm, " {\n      margin: ").concat(theme.space[16], " 0 0;\n    }\n    \n    ").concat(theme.breakpoints.md, " {\n      align-items: center;\n      grid-template-rows: 1fr;\n      grid-template-columns: 110px 1fr 110px;\n    }\n    \n    ").concat(theme.breakpoints.xl, " {\n      grid-template-columns: 160px 1fr 160px;\n    }\n    > :first-child {\n      font-size: ").concat(theme.fonts.size.tablet.xs, ";\n    }\n    > :last-child {\n      text-align: right;\n      ").concat(theme.breakpoints.md, " {\n        font-size: ").concat(theme.fonts.size.tablet.s, ";\n      }\n    }\n  ");
});
var RangeWrapper = styled.div(templateObject_2$q || (templateObject_2$q = __makeTemplateObject(["\n  position: relative;\n  ", "\n"], ["\n  position: relative;\n  ", "\n"])), function (_a) {
    var theme = _a.theme, type = _a.type;
    return (type === 'labeled' || type === 'compact') &&
        "\n    grid-area: 2 / 1 / 3 / 3;\n    ".concat(theme.breakpoints.md, " {\n      grid-area: 1 / 2 / 2 / 3;\n    }\n  ");
});
var ValueWrapper = styled.div(templateObject_3$l || (templateObject_3$l = __makeTemplateObject(["\n  display: flex;\n  justify-content: space-between;\n  align-items: center;\n  margin-bottom: 20px;\n"], ["\n  display: flex;\n  justify-content: space-between;\n  align-items: center;\n  margin-bottom: 20px;\n"])));
var SliderButton = styled(Button$2)(templateObject_4$j || (templateObject_4$j = __makeTemplateObject(["\n  height: 40px;\n  width: 40px;\n  padding: 0;\n  min-width: auto;\n  border-radius: 50%;\n  border-color: ", ";\n  color: ", ";\n"], ["\n  height: 40px;\n  width: 40px;\n  padding: 0;\n  min-width: auto;\n  border-radius: 50%;\n  border-color: ", ";\n  color: ", ";\n"])), function (_a) {
    var theme = _a.theme;
    return theme.colors.slider.sliderButton.border;
}, function (_a) {
    var theme = _a.theme;
    return theme.colors.slider.sliderButton.color;
});
var InfoPill = styled.div(templateObject_5$e || (templateObject_5$e = __makeTemplateObject(["\n  position: absolute;\n  left: 0;\n  top: -25px;\n  padding: ", ";\n  background-color: ", ";\n  color: ", ";\n  font-size: ", ";\n  line-height: 1;\n  border-radius: 50em;\n\n  ", "\n"], ["\n  position: absolute;\n  left: 0;\n  top: -25px;\n  padding: ", ";\n  background-color: ", ";\n  color: ", ";\n  font-size: ", ";\n  line-height: 1;\n  border-radius: 50em;\n\n  ", "\n"])), function (_a) {
    var theme = _a.theme;
    return "".concat(theme.space[4], " ").concat(theme.space[16]);
}, function (_a) {
    var theme = _a.theme;
    return theme.colors.slider.infoPill.backgroundColor;
}, function (_a) {
    var theme = _a.theme;
    return theme.colors.slider.infoPill.color;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.size.mobile.xxs;
}, function (_a) {
    var type = _a.type;
    return type === 'compact' &&
        "\n    top: -15px\n  \n  ";
});
var SliderValue = styled.span(templateObject_6$c || (templateObject_6$c = __makeTemplateObject(["\n  position: absolute;\n  left: 50%;\n  transform: translateX(-50%);\n  font-size: ", ";\n  line-height: ", ";\n  min-height: ", ";\n  font-weight: ", ";\n  color: ", ";\n  ", " {\n    font-size: ", ";\n    line-height: ", ";\n    min-height: ", ";\n  }\n  ", " {\n    font-size: ", ";\n  }\n"], ["\n  position: absolute;\n  left: 50%;\n  transform: translateX(-50%);\n  font-size: ", ";\n  line-height: ", ";\n  min-height: ", ";\n  font-weight: ", ";\n  color: ", ";\n  ", " {\n    font-size: ", ";\n    line-height: ", ";\n    min-height: ", ";\n  }\n  ", " {\n    font-size: ", ";\n  }\n"])), function (_a) {
    var theme = _a.theme;
    return theme.fonts.size.mobile.xxxl;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.lineHeight.mobile.xl;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.lineHeight.mobile.xl;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.weight.bold700;
}, function (_a) {
    var theme = _a.theme;
    return theme.colors.slider.title;
}, theme.breakpoints.sm, function (_a) {
    var theme = _a.theme;
    return theme.fonts.size.tablet.xxxl;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.lineHeight.tablet.xxl;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.lineHeight.tablet.xxl;
}, theme.breakpoints.lg, function (_a) {
    var theme = _a.theme;
    return theme.fonts.size.desktop.xxxl;
});
var templateObject_1$v, templateObject_2$q, templateObject_3$l, templateObject_4$j, templateObject_5$e, templateObject_6$c;

var SliderTypes;
(function (SliderTypes) {
    SliderTypes["Primary"] = "primary";
    SliderTypes["Secondary"] = "secondary";
    SliderTypes["Labeled"] = "labeled";
    SliderTypes["Compact"] = "compact";
})(SliderTypes || (SliderTypes = {}));

var Slider$1 = function (_a) {
    var value = _a.value, title = _a.title, format = _a.format, decimalPlaces = _a.decimalPlaces, suffixLabel = _a.suffixLabel, _b = _a.step, step = _b === void 0 ? 1 : _b, _c = _a.min, min = _c === void 0 ? 0 : _c, _d = _a.max, max = _d === void 0 ? 100 : _d, marks = _a.marks, _e = _a.type, type = _e === void 0 ? SliderTypes.Primary : _e, prefixLabel = _a.prefixLabel, infoPill = _a.infoPill, promotionValue = _a.promotionValue, onChange = _a.onChange, dataTestId = _a.dataTestId;
    var handleDecrease = function () {
        if (marks) {
            var newIndex = marks.indexOf(value) - 1;
            var newValue = marks[newIndex];
            if (newIndex >= 0) {
                onChange(newValue);
            }
        }
        else {
            var newValue = value - step;
            if (newValue >= min) {
                onChange(newValue);
            }
        }
    };
    var handleIncrease = function () {
        if (marks) {
            var newIndex = marks.indexOf(value) + 1;
            var newValue = marks[newIndex];
            if (newIndex < marks.length) {
                onChange(newValue);
            }
        }
        else {
            var newValue = value + step;
            if (newValue <= max) {
                onChange(newValue);
            }
        }
    };
    var valueAsString;
    if (format) {
        valueAsString = format(value);
    }
    else if (decimalPlaces) {
        valueAsString = value.toFixed(decimalPlaces > 0 ? decimalPlaces : 0);
    }
    else {
        valueAsString = "".concat(value);
    }
    var valueAsStringWithUnit = valueAsString;
    if (suffixLabel) {
        valueAsStringWithUnit = "".concat(valueAsStringWithUnit, " ").concat(suffixLabel);
    }
    return (jsxs(SliderWrapper, __assign$1({ type: type, "data-testid": dataTestId }, { children: [type === 'primary' && (jsxs(Fragment, { children: [jsx(Caption1$1, __assign$1({ align: "center" }, { children: title })), jsxs(ValueWrapper, { children: [jsx(SliderButton, __assign$1({ variant: "secondary", onClick: handleDecrease, "data-testid": "slider_minus_button" }, { children: "-" })), jsx(SliderValue, __assign$1({ "data-testid": "slider_amount" }, { children: valueAsStringWithUnit })), jsx(SliderButton, __assign$1({ variant: "secondary", onClick: handleIncrease, "data-testid": "slider_plus_button" }, { children: "+" }))] })] })), prefixLabel && jsx(Body2, { children: prefixLabel }), jsxs(RangeWrapper, __assign$1({ type: type }, { children: [infoPill && (jsxs(Fragment, { children: [jsx(InfoPill, __assign$1({ type: type }, { children: infoPill })), jsx(Gutter, { size: 10 })] })), jsx(RangeInput, { type: type === 'primary' || type === 'labeled' || type === 'compact' ? 'primary' : type, value: value, format: format, valueUnit: suffixLabel, step: step, min: min, max: max, onSliderChange: onChange, marks: marks, promotionValue: promotionValue })] })), type === 'labeled' && (jsxs(Header7, __assign$1({ as: "p" }, { children: [valueAsString, ' ', suffixLabel && suffixLabel.length > 3 ? (jsx(Header8, __assign$1({ as: "span" }, { children: suffixLabel }))) : (suffixLabel), ' '] }))), type === 'compact' && (jsxs(Header8, __assign$1({ as: "p" }, { children: [valueAsString, ' ', suffixLabel && suffixLabel.length > 3 ? (jsx(Header7, __assign$1({ as: "span" }, { children: suffixLabel }))) : (suffixLabel), ' '] })))] })));
};
Slider$1.Types = SliderTypes;

var Socials$1 = styled.div(templateObject_1$u || (templateObject_1$u = __makeTemplateObject(["\n  display: flex;\n  flex-direction: row;\n  justify-content: flex-start;\n  flex-wrap: wrap;\n  gap: ", ";\n  ", "\n"], ["\n  display: flex;\n  flex-direction: row;\n  justify-content: flex-start;\n  flex-wrap: wrap;\n  gap: ", ";\n  ", "\n"])), function (_a) {
    var theme = _a.theme;
    return theme.space[16];
}, function (_a) {
    var theme = _a.theme, withMargin = _a.withMargin;
    return withMargin &&
        "\n      margin-top: 15px;\n\n      ".concat(theme.breakpoints.md, " {\n        margin-top: 25px;\n      }\n    ");
});
var Item = styled.a(templateObject_2$p || (templateObject_2$p = __makeTemplateObject(["\n  margin-right: ", ";\n\n  circle {\n    transition: fill ease-in-out 0.3s;\n  }\n\n  &:hover {\n    circle {\n      fill: ", ";\n    }\n  }\n"], ["\n  margin-right: ", ";\n\n  circle {\n    transition: fill ease-in-out 0.3s;\n  }\n\n  &:hover {\n    circle {\n      fill: ", ";\n    }\n  }\n"])), function (_a) {
    var iconBackgroundColor = _a.iconBackgroundColor;
    return (iconBackgroundColor ? '0px' : '16px');
}, function (_a) {
    var theme = _a.theme, iconBackgroundColor = _a.iconBackgroundColor;
    return iconBackgroundColor || theme.colors.socials.circle;
});
var templateObject_1$u, templateObject_2$p;

var Socials = function (_a) {
    var socialList = _a.socialList, _b = _a.withMargin, withMargin = _b === void 0 ? true : _b;
    return (jsx(Socials$1, __assign$1({ "data-testid": "socials", withMargin: withMargin }, { children: socialList === null || socialList === void 0 ? void 0 : socialList.map(function (_a) {
            var icon = _a.icon, href = _a.href, iconBackgroundColor = _a.iconBackgroundColor, iconFillColor = _a.iconFillColor;
            if (icon) {
                return (jsx(Item, __assign$1({ href: href, target: "_blank", iconBackgroundColor: iconBackgroundColor === null || iconBackgroundColor === void 0 ? void 0 : iconBackgroundColor.value, "aria-label": "Link to ".concat(icon) }, { children: jsx(Icon$4, { name: icon, fillColor: iconFillColor === null || iconFillColor === void 0 ? void 0 : iconFillColor.value, circleColor: iconBackgroundColor === null || iconBackgroundColor === void 0 ? void 0 : iconBackgroundColor.value }) }), "".concat(icon, "-").concat(Math.random())));
            }
            return null;
        }) })));
};

var StepperWrapper = styled.div(templateObject_1$t || (templateObject_1$t = __makeTemplateObject(["\n  display: flex;\n  flex-direction: row;\n  position: relative;\n"], ["\n  display: flex;\n  flex-direction: row;\n  position: relative;\n"])));
var fadeIn = function (theme) { return keyframes(templateObject_2$o || (templateObject_2$o = __makeTemplateObject(["\n  0%, 40% {\n    background-color: ", ";\n  }\n  50%, 100% {\n    background-color: ", ";\n  }\n"], ["\n  0%, 40% {\n    background-color: ", ";\n  }\n  50%, 100% {\n    background-color: ", ";\n  }\n"])), theme.colors.stepper.step, theme.colors.stepper.activeStep); };
var fadeOut = function (theme) { return keyframes(templateObject_3$k || (templateObject_3$k = __makeTemplateObject(["\n  0%, 40% {\n    background-color: ", "; \n  }\n  50%, 100% {\n    background-color: ", ";\n}\n"], ["\n  0%, 40% {\n    background-color: ", "; \n  }\n  50%, 100% {\n    background-color: ", ";\n}\n"])), theme.colors.stepper.activeStep, theme.colors.stepper.step); };
var StepperItem = styled.span(templateObject_4$i || (templateObject_4$i = __makeTemplateObject(["\n  height: 12px;\n  width: 12px;\n  margin-left: 9px;\n  margin-right: 8px;\n  border-radius: 50%;\n  display: inline-block;\n  background-color: ", ";\n  animation-duration: 1s;\n  animation-name: ", ";\n"], ["\n  height: 12px;\n  width: 12px;\n  margin-left: 9px;\n  margin-right: 8px;\n  border-radius: 50%;\n  display: inline-block;\n  background-color: ", ";\n  animation-duration: 1s;\n  animation-name: ", ";\n"])), function (props) { return props.theme.colors.stepper.step; }, function (props) {
    if (props.isActiveStep) {
        return function (props) { return fadeIn(props.theme); };
    }
    if (props.isPreviousStep) {
        return function (props) { return fadeOut(props.theme); };
    }
    return 'none';
});
var indicatorMoveForward = keyframes(templateObject_5$d || (templateObject_5$d = __makeTemplateObject(["\n  0%, 20% {\n    transform: translateX(-29px);\n  }\n  30% {\n    transform: translateX(-20px) scale(1.2, 0.75);\n  }\n  50% {\n    transform: translateX(-15px) scale(1.6, 0.4);\n  }\n  80%, 100% {\n    transform: translateX(0);\n  }\n"], ["\n  0%, 20% {\n    transform: translateX(-29px);\n  }\n  30% {\n    transform: translateX(-20px) scale(1.2, 0.75);\n  }\n  50% {\n    transform: translateX(-15px) scale(1.6, 0.4);\n  }\n  80%, 100% {\n    transform: translateX(0);\n  }\n"])));
var indicatorMoveBack = keyframes(templateObject_6$b || (templateObject_6$b = __makeTemplateObject(["\n0%, 20% {\n  transform: translateX(29px);\n}\n30% {\n  transform: translateX(20px) scale(1.2, 0.75);\n}\n50% {\n  transform: translateX(15px) scale(1.6, 0.4);\n}\n80%, 100% {\n  transform: translateX(0);\n}\n"], ["\n0%, 20% {\n  transform: translateX(29px);\n}\n30% {\n  transform: translateX(20px) scale(1.2, 0.75);\n}\n50% {\n  transform: translateX(15px) scale(1.6, 0.4);\n}\n80%, 100% {\n  transform: translateX(0);\n}\n"])));
var ActiveIndicator = styled.span(templateObject_7$a || (templateObject_7$a = __makeTemplateObject(["\n  height: 12px;\n  width: 12px;\n  border-radius: 50%;\n  margin-left: 9px;\n  margin-right: 8px;\n  display: inline-block;\n  position: absolute;\n  left: ", "px;\n  animation-duration: 1s;\n  background-color: ", ";\n  animation-name: ", ";\n"], ["\n  height: 12px;\n  width: 12px;\n  border-radius: 50%;\n  margin-left: 9px;\n  margin-right: 8px;\n  display: inline-block;\n  position: absolute;\n  left: ", "px;\n  animation-duration: 1s;\n  background-color: ", ";\n  animation-name: ", ";\n"])), function (props) { return (props.activeStep - 1) * (12 + 8 + 9); }, function (props) { return props.theme.colors.stepper.activeStep; }, function (props) {
    if (props.direction === 'none') {
        return 'none';
    }
    if (props.direction === 'forward') {
        return indicatorMoveForward;
    }
    return indicatorMoveBack;
});
var templateObject_1$t, templateObject_2$o, templateObject_3$k, templateObject_4$i, templateObject_5$d, templateObject_6$b, templateObject_7$a;

var Stepper = function (_a) {
    var activeStep = _a.activeStep, count = _a.count, testId = _a.testId;
    var _b = useState('none'), moveDirection = _b[0], setMoveDirection = _b[1];
    var _c = useState(null), previousStep = _c[0], setPreviousStep = _c[1];
    useEffect(function () {
        if (!previousStep || Math.abs(activeStep - previousStep) > 1) {
            setMoveDirection('none');
        }
        else {
            setMoveDirection(activeStep > previousStep ? 'forward' : 'backward');
        }
        setPreviousStep(activeStep);
    }, [activeStep]);
    return (jsx(StepperWrapper, __assign$1({ "data-testid": testId }, { children: count > 0 &&
            Array.from(Array(count).keys()).map(function (e, i) { return (jsxs(Fragment$1, { children: [jsx(StepperItem, { id: "step", isActiveStep: i + 1 === activeStep, isPreviousStep: (moveDirection === 'forward' && i + 2 === activeStep) ||
                            (moveDirection === 'backward' && i === activeStep) }), i + 1 === activeStep && (jsx(ActiveIndicator, { id: "indicator", direction: moveDirection, activeStep: activeStep }))] }, e)); }) })));
};

var Title$5 = styled(Caption3$1)(templateObject_1$s || (templateObject_1$s = __makeTemplateObject(["\n  ", "\n"], ["\n  ", "\n"])), makeMediaQuery('md', 'font-size: 20px'));
var List = styled.ul(templateObject_2$n || (templateObject_2$n = __makeTemplateObject(["\n  padding: 0;\n  margin: 0;\n  list-style: none;\n"], ["\n  padding: 0;\n  margin: 0;\n  list-style: none;\n"])));
var ListItem = styled.li(templateObject_3$j || (templateObject_3$j = __makeTemplateObject(["\n  position: relative;\n  display: flex;\n  justify-content: center;\n  align-items: center;\n  gap: 15px;\n  ", "\n"], ["\n  position: relative;\n  display: flex;\n  justify-content: center;\n  align-items: center;\n  gap: 15px;\n  ", "\n"])), function (_a) {
    var theme = _a.theme, flush = _a.flush;
    return !flush &&
        "\n      ".concat(makeMediaQuery('md', 'padding-right: 50px'), "\n      margin-bottom: 10px;\n      padding: 20px;\n      border-radius: 5px;\n      &:nth-child(odd) {\n        background-color: ").concat(theme.colors.styledList.oddChild.backgroundColor, ";\n      }\n  ");
});
var Label = styled(Caption4)(templateObject_4$h || (templateObject_4$h = __makeTemplateObject(["\n  ", "\n  flex: 1 1 auto;\n  max-width: 55%;\n"], ["\n  ", "\n  flex: 1 1 auto;\n  max-width: 55%;\n"])), makeMediaQuery('md', 'font-size: 18px'));
var Description$1 = styled(Body4)(templateObject_5$c || (templateObject_5$c = __makeTemplateObject(["\n  ", "\n  text-align: right;\n  align-self: flex-start;\n  flex: 1 1 auto;\n"], ["\n  ", "\n  text-align: right;\n  align-self: flex-start;\n  flex: 1 1 auto;\n"])), makeMediaQuery('md', 'font-size: 18px'));
var TooltipTrigger = styled.div(templateObject_6$a || (templateObject_6$a = __makeTemplateObject(["\n  ", "\n  display: none;\n  position: absolute;\n  right: 15px;\n  bottom: 50%;\n  margin-bottom: -9px;\n  border: 0;\n  padding: 0;\n  width: 18px;\n  height: 18px;\n  background-color: transparent;\n  cursor: pointer;\n"], ["\n  ", "\n  display: none;\n  position: absolute;\n  right: 15px;\n  bottom: 50%;\n  margin-bottom: -9px;\n  border: 0;\n  padding: 0;\n  width: 18px;\n  height: 18px;\n  background-color: transparent;\n  cursor: pointer;\n"])), makeMediaQuery('md', 'display: flex'));
var templateObject_1$s, templateObject_2$n, templateObject_3$j, templateObject_4$h, templateObject_5$c, templateObject_6$a;

var StyledList = function (_a) {
    var listTitle = _a.listTitle, list = _a.list, flush = _a.flush;
    return (jsxs(Fragment, { children: [listTitle && (jsxs(Fragment, { children: [jsx(Title$5, __assign$1({ as: listTitle.headingLevel ? "h".concat(listTitle.headingLevel) : 'p' }, { children: listTitle.title })), jsx(Gutter, { size: 24 })] })), jsx(List, { children: list.map(function (_a, index) {
                    var label = _a.label, description = _a.description, tooltip = _a.tooltip;
                    return (jsxs(ListItem, __assign$1({ flush: flush }, { children: [jsx(Label, { children: label }), jsx(Description$1, { children: description }), tooltip && (jsx(TooltipTrigger, __assign$1({ title: tooltip }, { children: jsx(Icon$4, { name: "ButtonTooltip", width: 18, height: 18 }) })))] }), index));
                }) })] }));
};

var Table$2 = styled.table(templateObject_1$r || (templateObject_1$r = __makeTemplateObject(["\n  width: 100%;\n  border-collapse: collapse;\n  overflow-x: auto;\n\n  tbody {\n    width: 100%;\n    display: table;\n  }\n\n  ", "\n"], ["\n  width: 100%;\n  border-collapse: collapse;\n  overflow-x: auto;\n\n  tbody {\n    width: 100%;\n    display: table;\n  }\n\n  ", "\n"])), function (_a) {
    var variant = _a.variant, theme = _a.theme;
    return variant &&
        "\n      border: 1px solid;\n      border-color: ".concat(theme.colors.table.border, ";\n      border-radius: 30px;\n      display: block;");
});
var Row = styled.tr(templateObject_2$m || (templateObject_2$m = __makeTemplateObject(["\n  padding: 0;\n  position: sticky;\n\n  ", "\n\n  ", "\n\n   ", "\n\n  &:nth-child(odd) {\n    td,\n    th {\n      background-color: ", ";\n    }\n  }\n"], ["\n  padding: 0;\n  position: sticky;\n\n  ", "\n\n  ", "\n\n   ", "\n\n  &:nth-child(odd) {\n    td,\n    th {\n      background-color: ", ";\n    }\n  }\n"])), function (_a) {
    var variant = _a.variant, theme = _a.theme;
    return variant === 'simple' &&
        "\n      &:not(&:last-child) {\n        border-bottom: 1px solid ".concat(theme.colors.table.border, "; \n  }  ");
}, function (_a) {
    var variant = _a.variant, theme = _a.theme;
    return variant === 'styled' &&
        "\n      &:not(&:first-child):not(&:last-child) {\n        border-bottom: 1px solid ".concat(theme.colors.table.border, ";\n      }\n      &:first-child {\n        background-color: ").concat(theme.colors.table.header.backgroundColor, ";\n        td, th {\n          font-size: 16px;\n        }\n        td:not(:last-child) {\n          border-right: 1px solid ").concat(theme.colors.table.border, "; \n        }\n      } \n   ");
}, function (_a) {
    var variant = _a.variant;
    return (variant === 'styled' || variant === 'simple') &&
        "\n         th {\n          font-weight: 500;\n\n          &[scope=\"row\"] {\n            vertical-align: top;\n          }\n        }\n   ";
}, function (_a) {
    var theme = _a.theme, variant = _a.variant;
    return !variant && theme.colors.table.loanAppTable.oddRows;
});
var Cell = styled.td(templateObject_3$i || (templateObject_3$i = __makeTemplateObject(["\n  ", "\n  ", "\n\n  ", "\n\n  ", "\n"], ["\n  ", "\n  ", "\n\n  ", "\n\n  ", "\n"])), function (_a) {
    var align = _a.align;
    return "text-align: ".concat(align || 'left', ";");
}, function (_a) {
    var theme = _a.theme, mobileLabel = _a.mobileLabel, variant = _a.variant;
    return !variant &&
        "\n    ".concat(mobileLabel
            ? "\n          position: absolute;\n          inset: 9px auto auto 9px;\n          padding: 0;\n          font-size: ".concat(theme.fonts.size.mobile.xxxxs, ";\n        }")
            : "    \n          font-size: ".concat(theme.fonts.size.mobile.xxs, ";\n          padding: 26px 10px 10px;\n        "), "\n    ").concat(theme.breakpoints.sm, " {\n      font-size: ").concat(theme.fonts.size.desktop.s, ";\n      padding: 20px;\n      ").concat(mobileLabel
            ? "\n            position: relative;\n            inset: initial;\n          }"
            : '', "\n    }\n  ");
}, function (_a) {
    var variant = _a.variant;
    return (variant === 'styled' || variant === 'simple') &&
        "\n      padding: 24px 16px;\n      font-size: 14px;\n      font-weight: 400;\n      line-height: 24px;\n      min-width: 100px;\n  ";
}, function (_a) {
    var variant = _a.variant, theme = _a.theme;
    return variant === 'simple' &&
        "\n      &:not(&:first-child) {\n        border-left: 1px solid ".concat(theme.colors.table.border, ";\n      }\n  ");
});
var AccessibilityCaption = styled.caption(templateObject_4$g || (templateObject_4$g = __makeTemplateObject(["\n  width: 1px;\n  height: 1px;\n  padding: 0;\n  margin: -1px;\n  overflow: hidden;\n  clip: rect(0, 0, 0, 0);\n  white-space: nowrap;\n  border: 0;\n"], ["\n  width: 1px;\n  height: 1px;\n  padding: 0;\n  margin: -1px;\n  overflow: hidden;\n  clip: rect(0, 0, 0, 0);\n  white-space: nowrap;\n  border: 0;\n"])));
var templateObject_1$r, templateObject_2$m, templateObject_3$i, templateObject_4$g;

function mapDataForComplexMobileTable(rows) {
    // create  one group of mapped data less than rows in the regular table because heading is spread across group cells
    return Array.from({ length: rows.length - 1 }, function (_, index) {
        var arrayOfMappedRows = [];
        // skip the heading
        rows[index + 1].cells.forEach(function (_, i) {
            if (i === 0) {
                // in headings the values use always this same cells
                arrayOfMappedRows.push({
                    cells: [rows[0].cells[0], rows[index + 1].cells[0]],
                });
            }
            else {
                // the value from heading row becomes the "title" of relevant "regular" cell from given row
                arrayOfMappedRows.push({
                    cells: [rows[0].cells[i], rows[index + 1].cells[i]],
                });
            }
        });
        return arrayOfMappedRows;
    });
}

var TableWrapper = styled.div(templateObject_1$q || (templateObject_1$q = __makeTemplateObject(["\n  width: 100%;\n  border: ", ";\n  border-radius: 30px;\n  overflow: hidden;\n"], ["\n  width: 100%;\n  border: ", ";\n  border-radius: 30px;\n  overflow: hidden;\n"])), function (_a) {
    var theme = _a.theme;
    return "1px solid ".concat(theme.colors.table.border);
});
var Table$1 = styled.table(templateObject_2$l || (templateObject_2$l = __makeTemplateObject(["\n  width: 100%;\n  padding: 0;\n  border-collapse: collapse;\n"], ["\n  width: 100%;\n  padding: 0;\n  border-collapse: collapse;\n"])));
var TableRow = styled.tr(templateObject_3$h || (templateObject_3$h = __makeTemplateObject(["\n  display: block;\n  padding: 24px 16px;\n\n  td {\n    display: block;\n    font-size: 14px;\n    font-weight: 400;\n    line-height: 24px;\n  }\n\n  td:first-child {\n    font-weight: 500;\n    margin-bottom: 8px;\n  }\n\n  &:not(:first-child) {\n    border-top: ", ";\n  }\n"], ["\n  display: block;\n  padding: 24px 16px;\n\n  td {\n    display: block;\n    font-size: 14px;\n    font-weight: 400;\n    line-height: 24px;\n  }\n\n  td:first-child {\n    font-weight: 500;\n    margin-bottom: 8px;\n  }\n\n  &:not(:first-child) {\n    border-top: ", ";\n  }\n"])), function (_a) {
    var theme = _a.theme;
    return "1px solid ".concat(theme.colors.table.border);
});
var HeadingRow = styled.tr(templateObject_4$f || (templateObject_4$f = __makeTemplateObject(["\n  display: flex;\n  justify-content: space-between;\n  align-items: center;\n  background-color: ", ";\n\n  td {\n    font-size: 14px;\n  }\n  td:first-child {\n    font-weight: 700;\n  }\n  td:last-child {\n    font-weight: 500;\n  }\n"], ["\n  display: flex;\n  justify-content: space-between;\n  align-items: center;\n  background-color: ", ";\n\n  td {\n    font-size: 14px;\n  }\n  td:first-child {\n    font-weight: 700;\n  }\n  td:last-child {\n    font-weight: 500;\n  }\n"])), function (_a) {
    var theme = _a.theme;
    return theme.colors.table.header.backgroundColor;
});
var TableData = styled.td(templateObject_5$b || (templateObject_5$b = __makeTemplateObject(["\n  padding: 24px 16px;\n"], ["\n  padding: 24px 16px;\n"])));
var ComplexMobileTable = function (_a) {
    var data = _a.data;
    return (jsx(TableWrapper, { children: data.map(function (tableUnit, index) { return (jsxs(Table$1, { children: [jsx("thead", { children: jsxs(HeadingRow, { children: [jsx(TableData, { children: tableUnit[0].cells[0].children }), jsx(TableData, { children: tableUnit[0].cells[1].children })] }) }), jsx("tbody", { children: tableUnit.map(function (row, i) {
                        if (i === 0)
                            return null;
                        return (jsxs(TableRow, { children: [jsx("td", { children: row.cells[0].children }), jsx("td", { children: row.cells[1].children })] }, "TableRow-".concat(Math.random(), "-").concat(index)));
                    }) })] }, "Table-".concat(Math.random(), "-").concat(index))); }) }));
};
var templateObject_1$q, templateObject_2$l, templateObject_3$h, templateObject_4$f, templateObject_5$b;

var Table = function (_a) {
    var _b = _a.rows, rows = _b === void 0 ? [] : _b, variant = _a.variant, caption = _a.caption;
    var isScreenXS = useIsScreenSize().isScreenXS;
    if (variant === 'styled' && isScreenXS) {
        var mappedData = mapDataForComplexMobileTable(rows);
        return jsx(ComplexMobileTable, { data: mappedData });
    }
    return (jsxs(Table$2, __assign$1({ variant: variant }, { children: [caption && jsx(AccessibilityCaption, { children: caption }), jsx("tbody", { children: rows.map(function (_a, index) {
                    var cells = _a.cells, headerRow = _a.header;
                    return (jsx(Row, __assign$1({ variant: variant }, { children: cells.map(function (_a, index) {
                            var children = _a.children, headerCell = _a.header, scope = _a.scope, colspan = _a.colspan, align = _a.align, mobileLabel = _a.mobileLabel;
                            return (jsx(Cell, __assign$1({ as: headerRow || headerCell ? 'th' : 'td', scope: scope, colSpan: colspan, align: align, mobileLabel: mobileLabel, variant: variant }, { children: children }), index));
                        }) }), index));
                }) })] })));
};

var TabsContainer = styled.div(templateObject_1$p || (templateObject_1$p = __makeTemplateObject(["\n  width: 100%;\n"], ["\n  width: 100%;\n"])));
var TabsLabelsRow = styled.div(templateObject_2$k || (templateObject_2$k = __makeTemplateObject(["\n  display: flex;\n  flex-flow: row nowrap;\n  width: 100%;\n"], ["\n  display: flex;\n  flex-flow: row nowrap;\n  width: 100%;\n"])));
var TabLabelContainer = styled.button(templateObject_3$g || (templateObject_3$g = __makeTemplateObject(["\n  background-color: ", ";\n  border: none;\n  cursor: pointer;\n  font-weight: ", ";\n  color: ", ";\n  font-size: ", ";\n  line-height: ", ";\n  padding: 1.2rem 1.5rem 1rem;\n  flex: 1;\n\n  ", "\n  ", "\n\n  ", " {\n    font-size: ", ";\n    line-height: ", ";\n    padding: 2rem 10px 1.8rem;\n    ", "\n    ", "\n  }\n"], ["\n  background-color: ", ";\n  border: none;\n  cursor: pointer;\n  font-weight: ", ";\n  color: ", ";\n  font-size: ", ";\n  line-height: ", ";\n  padding: 1.2rem 1.5rem 1rem;\n  flex: 1;\n\n  ", "\n  ", "\n\n  ", " {\n    font-size: ", ";\n    line-height: ", ";\n    padding: 2rem 10px 1.8rem;\n    ", "\n    ", "\n  }\n"])), function (_a) {
    var theme = _a.theme, isActive = _a.isActive;
    return isActive ? theme.colors.tabs.active.backgroundColor : theme.colors.tabs.backgroundColor;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.weight.medium500;
}, function (_a) {
    var theme = _a.theme;
    return theme.colors.tabs.color;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.size.mobile.xxs;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.lineHeight.mobile.xxs;
}, function (_a) {
    var isFirst = _a.isFirst;
    return isFirst && "border-top-left-radius: 10px;";
}, function (_a) {
    var isLast = _a.isLast;
    return isLast && "border-top-right-radius: 10px;";
}, function (_a) {
    var theme = _a.theme;
    return theme.breakpoints.md;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.size.mobile.xs;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.lineHeight.mobile.xs;
}, function (_a) {
    var isFirst = _a.isFirst;
    return isFirst && "border-top-left-radius: 30px;";
}, function (_a) {
    var isLast = _a.isLast;
    return isLast && "border-top-right-radius: 30px;";
});
var ContentContainer = styled.div(templateObject_5$a || (templateObject_5$a = __makeTemplateObject(["\n  ", "\n"], ["\n  ", "\n"])), function (_a) {
    var defaultPadding = _a.defaultPadding, theme = _a.theme;
    return defaultPadding && css(templateObject_4$e || (templateObject_4$e = __makeTemplateObject(["\n      ", " {\n        padding-left: ", ";\n        padding-right: ", ";\n      }\n      ", " {\n        padding-left: ", ";\n        padding-right: ", ";\n      }\n      ", " {\n        padding: 10px 60px 30px 40px;\n      }\n    "], ["\n      ", " {\n        padding-left: ", ";\n        padding-right: ", ";\n      }\n      ", " {\n        padding-left: ", ";\n        padding-right: ", ";\n      }\n      ", " {\n        padding: 10px 60px 30px 40px;\n      }\n    "])), theme.breakpoints.xs, theme.flexboxGrid.outerMargin.xs, theme.flexboxGrid.outerMargin.xs, theme.breakpoints.sm, theme.flexboxGrid.outerMargin.sm, theme.flexboxGrid.outerMargin.sm, theme.breakpoints.md);
});
var templateObject_1$p, templateObject_2$k, templateObject_3$g, templateObject_4$e, templateObject_5$a;

var TabLabels = function (_a) {
    var _b, _c;
    var elements = _a.elements, activeTab = _a.activeTab, handleTabChange = _a.handleTabChange;
    var firstElementId = (_b = elements[0]) === null || _b === void 0 ? void 0 : _b.id;
    var lastElementId = (_c = elements[elements.length - 1]) === null || _c === void 0 ? void 0 : _c.id;
    return (jsx(TabsLabelsRow, { children: elements.map(function (element) { return (jsxs(Fragment$1, { children: [jsx(TabLabelContainer, __assign$1({ isFirst: firstElementId === element.id, isLast: lastElementId === element.id, isActive: activeTab === element.id, onClick: function (e) { return handleTabChange(e, element); }, role: "tab", "aria-controls": "".concat(element.id), "aria-selected": activeTab === element.id }, { children: element.label })), lastElementId !== element.id && jsx(Divider, { vertical: true, length: "auto" })] }, element.id)); }) }));
};
var Tabs = function (_a) {
    var _b;
    var elements = _a.elements, activeOnMount = _a.activeOnMount, onTabChange = _a.onTabChange, _c = _a.contentDefaultPadding, contentDefaultPadding = _c === void 0 ? true : _c;
    var mountActiveTab = activeOnMount || ((_b = elements[0]) === null || _b === void 0 ? void 0 : _b.id);
    var _d = useState(mountActiveTab), activeTab = _d[0], setActiveTab = _d[1];
    var _e = useIsScreenSize(), isScreenSM = _e.isScreenSM, isScreenXS = _e.isScreenXS;
    var activeElement = useMemo(function () { return elements.find(function (element) { return activeTab === element.id; }); }, [activeTab, elements]);
    var cornerRadiusValues = {
        topLeft: '0',
        topRight: '0',
        bottomRight: '160px',
        bottomLeft: '30px',
    };
    if (isScreenXS) {
        cornerRadiusValues.bottomLeft = '10px';
        cornerRadiusValues.bottomRight = '60px';
    }
    if (isScreenSM) {
        cornerRadiusValues.bottomLeft = '10px';
        cornerRadiusValues.bottomRight = '80px';
    }
    var activeTabContent = useMemo(function () {
        return activeElement === null || activeElement === void 0 ? void 0 : activeElement.content;
    }, [activeElement]);
    var handleTabChange = function (e, element) {
        setActiveTab(element.id);
        if (onTabChange) {
            onTabChange(e, element);
        }
    };
    return (jsxs(TabsContainer, __assign$1({ role: "tablist" }, { children: [jsx(TabLabels, { elements: elements, activeTab: activeTab, handleTabChange: handleTabChange }), jsx("div", __assign$1({ role: "tabpanel", "aria-labelledby": "".concat(activeElement === null || activeElement === void 0 ? void 0 : activeElement.id) }, { children: jsx(RoundedBackground, __assign$1({ color: theme.colors.tabs.contentContainer.backgroundColor, roundedType: RoundTypes.EachCornerSeparateValue, eachCornerSeparateValues: cornerRadiusValues }, { children: jsx(ContentContainer, __assign$1({ defaultPadding: contentDefaultPadding }, { children: activeTabContent })) })) }))] })));
};

var ColumnsWrapper = styled.div(templateObject_1$o || (templateObject_1$o = __makeTemplateObject(["\n  column-gap: 42px;\n  ", ";\n"], ["\n  column-gap: 42px;\n  ", ";\n"])), function (_a) {
    var twoCol = _a.twoCol;
    return twoCol && makeMediaQuery('sm', 'column-count: 2');
});
var Title$4 = styled(Header3$2)(templateObject_2$j || (templateObject_2$j = __makeTemplateObject(["\n  ", ";\n"], ["\n  ", ";\n"])), makeMediaQuery('sm', 'text-align: center'));
var templateObject_1$o, templateObject_2$j;

var TwoColumnText = function (_a) {
    var title = _a.title, twoColumns = _a.twoColumns, children = _a.children;
    return (jsxs(Fragment, { children: [title && jsx(Title$4, { children: title }), jsx(ColumnsWrapper, __assign$1({ "data-testid": "columns-wrapper", twoCol: twoColumns }, { children: children }))] }));
};

var LinkWrapper$4 = styled.div(templateObject_1$n || (templateObject_1$n = __makeTemplateObject(["\n  padding: 17px 0;\n  margin-top: 16px;\n  ", " {\n    margin-top: 26px;\n  }\n"], ["\n  padding: 17px 0;\n  margin-top: 16px;\n  ", " {\n    margin-top: 26px;\n  }\n"])), function (_a) {
    var theme = _a.theme;
    return theme.breakpoints.sm;
});
var templateObject_1$n;

var hasEmptyTextContent = function (textContent) {
    if (!(textContent === null || textContent === void 0 ? void 0 : textContent.textRaw)) {
        return true;
    }
    var hasEmptyText = textContent.textRaw[0].children.every(function (child) {
        return child.text.trim().length === 0;
    });
    return (textContent === null || textContent === void 0 ? void 0 : textContent.textRaw.length) === 1 && hasEmptyText;
};

var ShortFaq = function (_a) {
    var title = _a.title, nextLink = _a.nextLink, linkData = _a.linkData, accordions = _a.accordions, PortableTextWrapper = _a.PortableTextWrapper;
    var shouldShowLink = (linkData === null || linkData === void 0 ? void 0 : linkData.linkTitle) && (linkData.externalUrl || linkData.internalLink);
    return (jsxs(Fragment, { children: [jsx(Header2, { children: title }), jsx(Gutter, { size: 10 }), accordions === null || accordions === void 0 ? void 0 : accordions.map(function (accordion, index) {
                var _a;
                return (jsxs(Fragment$1, { children: [jsx(Gutter, { size: 32 }), jsx(Accordion, __assign$1({ title: accordion.title, variant: "shortFaq" }, { children: ((_a = accordion.content) === null || _a === void 0 ? void 0 : _a.textRaw) &&
                                !hasEmptyTextContent(accordion.content) &&
                                PortableTextWrapper ? (jsx(PortableTextWrapper, { portableText: accordion.content.textRaw })) : ('') }), index)] }, "key=".concat(accordion.title)));
            }), shouldShowLink && (jsx(LinkWrapper$4, { children: jsx(LinkWithArrow, { NextLink: nextLink, linkData: linkData }) }))] }));
};

var AccordionsGroup = function (_a) {
    var title = _a.title, accordions = _a.accordions;
    return (jsxs(Fragment, { children: [title && (jsxs(Fragment, { children: [jsx(Header3$2, { children: title }), jsx(Gutter, { size: 40 })] })), accordions &&
                accordions.map(function (accordion, index) {
                    var _a;
                    return (jsxs(Fragment$1, { children: [jsx(Accordion, __assign$1({ title: accordion.title, reversed: accordion.reversed, alert: accordion.alert, icon: accordion.icon, color: (_a = accordion.color) === null || _a === void 0 ? void 0 : _a.value }, { children: jsx(Body2, { children: accordion.textString }) })), index !== accordions.length - 1 && jsx(Gutter, { size: 32 })] }, index));
                })] }));
};

var SectionWrapper$4 = styled.div(templateObject_1$m || (templateObject_1$m = __makeTemplateObject(["\n  background-color: ", ";\n  padding: 60px 0;\n"], ["\n  background-color: ", ";\n  padding: 60px 0;\n"])), function (_a) {
    var backgroundColor = _a.backgroundColor;
    return backgroundColor;
});
var CardsContainer$3 = styled.div(templateObject_3$f || (templateObject_3$f = __makeTemplateObject(["\n  display: flex;\n  flex-direction: column;\n  gap: 49px 24px;\n\n  ", "\n"], ["\n  display: flex;\n  flex-direction: column;\n  gap: 49px 24px;\n\n  ", "\n"])), function (_a) {
    var theme = _a.theme, elementCount = _a.elementCount, horizontal = _a.horizontal;
    return css(templateObject_2$i || (templateObject_2$i = __makeTemplateObject(["\n    ", " {\n      display: grid;\n      grid-template-columns: repeat(2, 1fr);\n      gap: 40px 32px;\n    }\n\n    ", " {\n      grid-template-columns: repeat(", ", 1fr);\n    }\n  "], ["\n    ", " {\n      display: grid;\n      grid-template-columns: repeat(2, 1fr);\n      gap: 40px 32px;\n    }\n\n    ", " {\n      grid-template-columns: repeat(", ", 1fr);\n    }\n  "])), theme.breakpoints.sm, theme.breakpoints.md, horizontal ? elementCount : 2);
});
var templateObject_1$m, templateObject_2$i, templateObject_3$f;

var AlternativeFeatures = function (_a) {
    var _b;
    var elements = _a.elements, title = _a.title, PortableTextWrapper = _a.PortableTextWrapper, Link = _a.Link, description = _a.description, alignment = _a.alignment, _c = _a.textColor, textColor = _c === void 0 ? theme.colors.alternativeFeatures.defaultColor : _c, _d = _a.backgroundColor, backgroundColor = _d === void 0 ? theme.colors.alternativeFeatures.defaultBackground : _d, _e = _a.horizontal, horizontal = _e === void 0 ? false : _e;
    var mode = textColor === theme.crediteaColors.primary.neutral[50] ? 'light' : 'dark';
    return (jsx(SectionWrapper$4, __assign$1({ backgroundColor: backgroundColor }, { children: jsxs(Container$1, { children: [title && (jsx(Header2, __assign$1({ align: alignment, color: textColor }, { children: title }))), description && (jsx(Body1$1, __assign$1({ align: alignment, color: textColor }, { children: description }))), (title || description) && jsx(Gutter, { size: 48 }), jsx(CardsContainer$3, __assign$1({ elementCount: (_b = elements === null || elements === void 0 ? void 0 : elements.length) !== null && _b !== void 0 ? _b : 0, horizontal: horizontal, "data-testid": "alt-feat-cards-container" }, { children: elements === null || elements === void 0 ? void 0 : elements.map(function (item, i) {
                        var _a, _b;
                        return (jsx(FeatureCard, { icon: item.iconName, iconBackgroundColor: (_a = item.iconColor) === null || _a === void 0 ? void 0 : _a.value, title: item.title, textColor: textColor, textContent: item.textContent &&
                                !hasEmptyTextContent(item.textContent) &&
                                PortableTextWrapper && (jsx(PortableTextWrapper, { portableText: (_b = item.textContent) === null || _b === void 0 ? void 0 : _b.textRaw, mode: mode })), alignment: alignment, label: item.hasLabel, labelText: item.labelText, linkData: item.link, nextLink: Link, fontSizeVariant: "H5", mode: mode, hasLink: item.hasLink }, i));
                    }) }))] }) })));
};

var BlogArticles = function (_a) {
    var allArticles = _a.allArticles, nextLink = _a.nextLink, language = _a.language;
    return (jsx(Container$1, __assign$1({ "aria-label": "Blog Articles" }, { children: jsx(Row$1, { children: allArticles.map(function (article) { return (jsx(Col, __assign$1({ xs: 4, sm: 4, md: 6, lg: 4, "aria-label": "Blog post" }, { children: jsx(BlogCard, __assign$1({}, article, { type: "medium", NextLink: nextLink, language: language })) }), article._id)); }) }) })));
};

var ImageBox = styled.div(templateObject_1$l || (templateObject_1$l = __makeTemplateObject(["\n  position: relative;\n  background-repeat: no-repeat;\n  background-size: auto;\n  border-radius: 30px;\n  overflow: hidden;\n  width: 100%;\n  ", " {\n    height: 325px;\n  }\n  ", " {\n    height: 500px;\n  }\n  margin-top: ", ";\n  img {\n    min-width: 100%;\n    max-width: 100%;\n    min-height: 100%;\n    max-height: 100%;\n    object-fit: cover;\n  }\n"], ["\n  position: relative;\n  background-repeat: no-repeat;\n  background-size: auto;\n  border-radius: 30px;\n  overflow: hidden;\n  width: 100%;\n  ", " {\n    height: 325px;\n  }\n  ", " {\n    height: 500px;\n  }\n  margin-top: ", ";\n  img {\n    min-width: 100%;\n    max-width: 100%;\n    min-height: 100%;\n    max-height: 100%;\n    object-fit: cover;\n  }\n"])), function (_a) {
    var theme = _a.theme;
    return theme.breakpoints.xs;
}, function (_a) {
    var theme = _a.theme;
    return theme.breakpoints.sm;
}, function (_a) {
    var theme = _a.theme;
    return theme.space[60];
});
var Caption2 = styled(Caption2$2)(templateObject_2$h || (templateObject_2$h = __makeTemplateObject(["\n  color: ", ";\n  margin-bottom: ", ";\n  ", " {\n    font-size: ", ";\n  }\n"], ["\n  color: ", ";\n  margin-bottom: ", ";\n  ", " {\n    font-size: ", ";\n  }\n"])), function (_a) {
    var theme = _a.theme;
    return theme.colors.blogHero.category;
}, function (_a) {
    var theme = _a.theme;
    return theme.space[24];
}, function (_a) {
    var theme = _a.theme;
    return theme.breakpoints.sm;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.size.tablet.xs;
});
var Body3 = styled(Body3$2)(templateObject_3$e || (templateObject_3$e = __makeTemplateObject(["\n  margin-top: ", ";\n"], ["\n  margin-top: ", ";\n"])), function (_a) {
    var theme = _a.theme;
    return theme.space[24];
});
var SocialWrapper = styled.div(templateObject_4$d || (templateObject_4$d = __makeTemplateObject(["\n  flex-direction: row;\n  display: flex;\n  //padding-top: 34px;\n\n  a {\n    // margin-right: ", ";\n  }\n\n  ", " {\n    display: flex;\n    flex-direction: column;\n    align-items: flex-end;\n    // padding-top: ", ";\n\n    a + a {\n      // margin-top: ", ";\n    }\n  }\n"], ["\n  flex-direction: row;\n  display: flex;\n  //padding-top: 34px;\n\n  a {\n    // margin-right: ", ";\n  }\n\n  ", " {\n    display: flex;\n    flex-direction: column;\n    align-items: flex-end;\n    // padding-top: ", ";\n\n    a + a {\n      // margin-top: ", ";\n    }\n  }\n"])), function (_a) {
    var theme = _a.theme;
    return theme.space[24];
}, function (_a) {
    var theme = _a.theme;
    return theme.breakpoints.lg;
}, function (_a) {
    var theme = _a.theme;
    return theme.space[70];
}, function (_a) {
    var theme = _a.theme;
    return theme.space[24];
});
var templateObject_1$l, templateObject_2$h, templateObject_3$e, templateObject_4$d;

var SocialIcons = function (_a) {
    _a.path;
    return (jsx(SocialWrapper, {}));
};
var BlogHero = function (_a) {
    var NextLink = _a.NextLink, article = __rest$1(_a, ["NextLink"]);
    var isScreenDesktop = useIsScreenSize().isScreenDesktop;
    if (!article)
        return null;
    var category = article.category, categorySlug = article.categorySlug, dateAndReadTime = article.dateAndReadTime, description = article.description, image = article.image, slug = article.slug, title = article.title, lang = article.lang, showContentList = article.showContentList, contentTitle = article.contentTitle, anchors = article.anchors, offset = article.offset;
    return (jsx(Container$1, { children: jsxs(Row$1, { children: [isScreenDesktop && (jsx(Col, __assign$1({ xs: 4, lg: 2 }, { children: jsx(SocialIcons, { path: article === null || article === void 0 ? void 0 : article.slug }) }))), jsxs(Col, __assign$1({ xs: 4, md: 12, lg: 8 }, { children: [category && categorySlug && NextLink && (jsx(NextLink, __assign$1({ href: "/blog/".concat(slugify(categorySlug, lang)) }, { children: jsx(Caption2, { children: category }) }))), jsx(Header2, { children: title }), jsx(Body3, { children: description }), jsx(Body3, __assign$1({ color: theme.colors.blogHero.date }, { children: dateAndReadTime })), !isScreenDesktop && jsx(SocialIcons, { path: slug }), showContentList && (jsxs(Fragment, { children: [jsx(Gutter, { size: 60 }), jsx(ContentList, { contentTitle: contentTitle, anchors: anchors, offset: offset })] })), jsx(ImageBox, { children: image })] }))] }) }));
};

var Wrapper$2 = styled.section(templateObject_1$k || (templateObject_1$k = __makeTemplateObject(["\n  padding-bottom: ", ";\n  background-color: ", ";\n"], ["\n  padding-bottom: ", ";\n  background-color: ", ";\n"])), function (_a) {
    var theme = _a.theme;
    return theme.space[32];
}, function (_a) {
    var backgroundColor = _a.backgroundColor;
    return backgroundColor;
});
var TextWrapper$1 = styled(Col)(templateObject_4$c || (templateObject_4$c = __makeTemplateObject(["\n  &&& {\n    ", ";\n    ", ";\n  }\n"], ["\n  &&& {\n    ", ";\n    ", ";\n  }\n"])), function (_a) {
    var DABOnlyMobile = _a.DABOnlyMobile;
    return DABOnlyMobile && css(templateObject_2$g || (templateObject_2$g = __makeTemplateObject(["\n        align-items: flex-start;\n        ", " {\n          align-items: center;\n        }\n        & > * {\n          text-align: left;\n          ", " {\n            text-align: center;\n          }\n        }\n      "], ["\n        align-items: flex-start;\n        ", " {\n          align-items: center;\n        }\n        & > * {\n          text-align: left;\n          ", " {\n            text-align: center;\n          }\n        }\n      "])), function (_a) {
        var theme = _a.theme;
        return theme.breakpoints.sm;
    }, function (_a) {
        var theme = _a.theme;
        return theme.breakpoints.sm;
    });
}, function (_a) {
    var CTAVariant = _a.CTAVariant;
    return CTAVariant === 'DSR' && css(templateObject_3$d || (templateObject_3$d = __makeTemplateObject(["\n        padding-top: 0;\n      "], ["\n        padding-top: 0;\n      "])));
});
var CallToActionWrapper = styled(Col)(templateObject_6$9 || (templateObject_6$9 = __makeTemplateObject(["\n  &&& {\n    ", ";\n  }\n"], ["\n  &&& {\n    ", ";\n  }\n"])), function (_a) {
    var DABOnlyMobile = _a.DABOnlyMobile;
    return DABOnlyMobile && css(templateObject_5$9 || (templateObject_5$9 = __makeTemplateObject(["\n      ", " {\n        display: none;\n      "], ["\n      ", " {\n        display: none;\n      "])), function (_a) {
        var theme = _a.theme;
        return theme.breakpoints.sm;
    });
});
var Button = styled(Col)(templateObject_7$9 || (templateObject_7$9 = __makeTemplateObject(["\n  &&& {\n    align-items: start;\n\n    ", " {\n      align-items: center;\n    }\n  }\n"], ["\n  &&& {\n    align-items: start;\n\n    ", " {\n      align-items: center;\n    }\n  }\n"])), function (_a) {
    var theme = _a.theme;
    return theme.breakpoints.md;
});
var DynamicStarsWrapper$1 = styled(Col)(templateObject_8$7 || (templateObject_8$7 = __makeTemplateObject(["\n  &&& {\n    justify-content: flex-start;\n    padding-top: 36px;\n\n    ", " {\n      padding-top: 56px;\n    }\n\n    ", " {\n      justify-content: flex-end;\n      padding-top: ", ";\n    }\n  }\n"], ["\n  &&& {\n    justify-content: flex-start;\n    padding-top: 36px;\n\n    ", " {\n      padding-top: 56px;\n    }\n\n    ", " {\n      justify-content: flex-end;\n      padding-top: ", ";\n    }\n  }\n"])), function (_a) {
    var theme = _a.theme;
    return theme.breakpoints.sm;
}, function (_a) {
    var theme = _a.theme;
    return theme.breakpoints.md;
}, function (_a) {
    var theme = _a.theme;
    return theme.space[32];
});
var DynamicStarsContainer = styled.div(templateObject_9$6 || (templateObject_9$6 = __makeTemplateObject(["\n  &&& {\n    width: 160px;\n\n    & > div[role='img'] {\n      place-content: flex-start;\n    }\n  }\n"], ["\n  &&& {\n    width: 160px;\n\n    & > div[role='img'] {\n      place-content: flex-start;\n    }\n  }\n"])));
var RatingNumber = styled(Caption1$1)(templateObject_10$5 || (templateObject_10$5 = __makeTemplateObject(["\n  &&& {\n    font-weight: 700;\n    line-height: 26px;\n    min-height: 26px;\n  }\n"], ["\n  &&& {\n    font-weight: 700;\n    line-height: 26px;\n    min-height: 26px;\n  }\n"])));
var Numbers = styled.div(templateObject_11$3 || (templateObject_11$3 = __makeTemplateObject(["\n  &&& {\n    display: flex;\n    align-items: baseline;\n  }\n"], ["\n  &&& {\n    display: flex;\n    align-items: baseline;\n  }\n"])));
var BoxOfStars = styled.div(templateObject_12$2 || (templateObject_12$2 = __makeTemplateObject(["\n  &&& {\n    display: flex;\n    flex-direction: row;\n    gap: 2px;\n  }\n"], ["\n  &&& {\n    display: flex;\n    flex-direction: row;\n    gap: 2px;\n  }\n"])));
var templateObject_1$k, templateObject_2$g, templateObject_3$d, templateObject_4$c, templateObject_5$9, templateObject_6$9, templateObject_7$9, templateObject_8$7, templateObject_9$6, templateObject_10$5, templateObject_11$3, templateObject_12$2;

var StarRating = function (_a) {
    var rating = _a.rating;
    var fullStar = jsx(Icon$4, { name: "StarOn" });
    var halfStar = jsx(Icon$4, { name: "StarHalf" });
    var emptyStar = jsx(Icon$4, { name: "StarOff" });
    var fullStars = Math.floor(rating);
    var decimalParts = rating - fullStars;
    var stars = [];
    // Add full stars
    for (var i = 0; i < fullStars; i += 1) {
        stars.push(fullStar);
    }
    // Add half star if necessary
    if (decimalParts >= 0.5) {
        stars.push(halfStar);
    }
    else if (decimalParts !== 0 && decimalParts < 0.5) {
        stars.push(emptyStar);
    }
    // Add empty stars to complete 5 stars when there is no decimal point
    var emptyStars = 5 - Math.ceil(rating);
    for (var i = 0; i < emptyStars; i += 1) {
        stars.push(emptyStar);
    }
    return (jsx(BoxOfStars, { children: stars.map(function (star, index) { return (jsx("span", { children: star }, index)); }) }));
};
var DynamicStarsWrapper = function (_a) {
    var _b, _c;
    var googlePlayRating = _a.googlePlayRating, appStoreRating = _a.appStoreRating, appLinks = _a.appLinks;
    return (jsxs(DynamicStarsWrapper$1, __assign$1({ xs: true, sm: true, md: 5, direction: "row" }, { children: [googlePlayRating && (jsx("a", __assign$1({ target: "_blank", id: "google-play-store-1", "data-testid": "google-play-store-1", href: (_b = appLinks === null || appLinks === void 0 ? void 0 : appLinks.android) !== null && _b !== void 0 ? _b : '', rel: "noreferrer", "aria-label": "Link to get App on Google Play" }, { children: jsxs(DynamicStarsContainer, { children: [jsx(Icon$4, { name: "GooglePlayBlack" }), jsx(Gutter, { size: 10 }), jsxs(Numbers, { children: [jsx(RatingNumber, { children: googlePlayRating }), jsx(Caption4, { children: "/5" })] }), jsx(Gutter, { size: 16 }), jsx(StarRating, { rating: googlePlayRating })] }) }))), jsx(Gutter, { size: "grid" }), appStoreRating && (jsx("a", __assign$1({ target: "_blank", id: "apple-app-store-1", "data-testid": "apple-app-store-1", href: (_c = appLinks === null || appLinks === void 0 ? void 0 : appLinks.iOS) !== null && _c !== void 0 ? _c : '', rel: "noreferrer", "aria-label": "Link to Download App on the App Store" }, { children: jsxs(DynamicStarsContainer, { children: [jsx(Icon$4, { name: "AppStoreBlack" }), jsx(Gutter, { size: 10 }), jsxs(Numbers, { children: [jsx(RatingNumber, { children: appStoreRating }), jsx(Caption4, { children: "/5" })] }), jsx(Gutter, { size: 16 }), jsx(StarRating, { rating: appStoreRating })] }) })))] })));
};

var CTABanner = function (_a) {
    var _b, _c;
    var title = _a.title, description = _a.description, _d = _a.color, color = _d === void 0 ? theme.colors.ctaBanner.defaultColor : _d, _e = _a.backgroundColor, backgroundColor = _e === void 0 ? theme.colors.ctaBanner.defaultBackground : _e, CallToAction = _a.CallToAction, DABOnlyMobile = _a.DABOnlyMobile, CTAVariant = _a.CTAVariant, linkData = _a.linkData, nextLink = _a.nextLink, buttonConfig = _a.buttonConfig, googlePlayRating = _a.googlePlayRating, appStoreRating = _a.appStoreRating, appLinks = _a.appLinks;
    var mdProportion = DABOnlyMobile ? 12 : 8;
    return (jsxs(Wrapper$2, __assign$1({ backgroundColor: backgroundColor }, { children: [jsx(Gutter, { size: "grid" }), jsx(Container$1, { children: jsxs(Row$1, __assign$1({ between: "xs", middle: "xs" }, { children: [jsxs(TextWrapper$1, __assign$1({ xs: 4, sm: 8, md: CTAVariant === 'DSR' ? 7 : mdProportion, DABOnlyMobile: DABOnlyMobile, CTAVariant: CTAVariant }, { children: [CTAVariant === 'DSR' ? (jsx(Header2, __assign$1({ color: color }, { children: title }))) : (jsx(Header5, __assign$1({ color: color }, { children: title }))), CTAVariant !== 'DSR' && description && (jsx(Body3$2, __assign$1({ color: color }, { children: description })))] })), CTAVariant === 'button' && (jsx(Button, __assign$1({ xs: true, sm: true, md: 4 }, { children: jsx(LinkAsButton, { NextLink: nextLink, linkData: linkData, mode: (_b = buttonConfig === null || buttonConfig === void 0 ? void 0 : buttonConfig.colorMode) !== null && _b !== void 0 ? _b : 'dark', variant: (_c = buttonConfig === null || buttonConfig === void 0 ? void 0 : buttonConfig.variant) !== null && _c !== void 0 ? _c : 'primary', width: 250 }) }))), CTAVariant === 'DAB' && (jsx(CallToActionWrapper, __assign$1({ xs: true, sm: true, md: 4, DABOnlyMobile: DABOnlyMobile }, { children: CallToAction }))), CTAVariant === 'DSR' && (jsx(DynamicStarsWrapper, { googlePlayRating: googlePlayRating, appStoreRating: appStoreRating, appLinks: appLinks }))] })) })] })));
};

var SectionWrapper$3 = styled.div(templateObject_1$j || (templateObject_1$j = __makeTemplateObject(["\n  background-color: ", ";\n"], ["\n  background-color: ", ";\n"])), function (_a) {
    var backgroundColor = _a.backgroundColor;
    return backgroundColor;
});
var CTAWrapper = styled.section(templateObject_3$c || (templateObject_3$c = __makeTemplateObject(["\n  text-align: center;\n  padding: ", " 0;\n\n  ", " {\n    padding: ", " 0;\n  }\n\n  ", "\n"], ["\n  text-align: center;\n  padding: ", " 0;\n\n  ", " {\n    padding: ", " 0;\n  }\n\n  ", "\n"])), function (_a) {
    var theme = _a.theme;
    return theme.space[32];
}, function (_a) {
    var theme = _a.theme;
    return theme.breakpoints.sm;
}, function (_a) {
    var theme = _a.theme;
    return theme.space[40];
}, function (_a) {
    var hasDescription = _a.hasDescription, title = _a.title;
    return (title || hasDescription) && css(templateObject_2$f || (templateObject_2$f = __makeTemplateObject(["\n      padding: ", " 0;\n\n      ", " {\n        padding: ", " 0;\n      }\n    "], ["\n      padding: ", " 0;\n\n      ", " {\n        padding: ", " 0;\n      }\n    "])), function (_a) {
        var theme = _a.theme;
        return theme.space[40];
    }, function (_a) {
        var theme = _a.theme;
        return theme.breakpoints.md;
    }, function (_a) {
        var theme = _a.theme;
        return theme.space[48];
    });
});
var Header3 = styled(Header3$2)(templateObject_5$8 || (templateObject_5$8 = __makeTemplateObject(["\n  ", "\n  color: ", ";\n"], ["\n  ", "\n  color: ", ";\n"])), function (_a) {
    var hasDescription = _a.hasDescription;
    return hasDescription && css(templateObject_4$b || (templateObject_4$b = __makeTemplateObject(["\n      margin-bottom: 8px;\n\n      ", " {\n        margin-bottom: 16px;\n      }\n    "], ["\n      margin-bottom: 8px;\n\n      ", " {\n        margin-bottom: 16px;\n      }\n    "])), function (_a) {
        var theme = _a.theme;
        return theme.breakpoints.sm;
    });
}, function (_a) {
    var textColor = _a.textColor;
    return textColor;
});
var LinkWrapper$3 = styled.div(templateObject_7$8 || (templateObject_7$8 = __makeTemplateObject(["\n  display: flex;\n  justify-content: center;\n  ", "\n"], ["\n  display: flex;\n  justify-content: center;\n  ", "\n"])), function (_a) {
    var title = _a.title, hasDescription = _a.hasDescription;
    return (title || hasDescription) && css(templateObject_6$8 || (templateObject_6$8 = __makeTemplateObject(["\n      margin-top: 24px;\n\n      ", " {\n        margin-top: 32px;\n      }\n    "], ["\n      margin-top: 24px;\n\n      ", " {\n        margin-top: 32px;\n      }\n    "])), function (_a) {
        var theme = _a.theme;
        return theme.breakpoints.lg;
    });
});
var TextContent$2 = styled.div(templateObject_8$6 || (templateObject_8$6 = __makeTemplateObject(["\n  > :is(h1, h2, h3, h4, h5, h6, p, ul) {\n    color: ", ";\n    *:not(a) {\n      color: inherit;\n    }\n  }\n"], ["\n  > :is(h1, h2, h3, h4, h5, h6, p, ul) {\n    color: ", ";\n    *:not(a) {\n      color: inherit;\n    }\n  }\n"])), function (_a) {
    var textColor = _a.textColor;
    return textColor;
});
var templateObject_1$j, templateObject_2$f, templateObject_3$c, templateObject_4$b, templateObject_5$8, templateObject_6$8, templateObject_7$8, templateObject_8$6;

var CTATextSection = function (_a) {
    var _b, _c;
    var title = _a.title, description = _a.description, _d = _a.textColor, textColor = _d === void 0 ? theme.colors.ctaTextSection.defaultColor : _d, _e = _a.backgroundColor, backgroundColor = _e === void 0 ? theme.colors.ctaTextSection.defaultBackground : _e, linkData = _a.linkData, nextLink = _a.nextLink, buttonConfig = _a.buttonConfig, PortableTextWrapper = _a.PortableTextWrapper;
    var hasDescription = description && !hasEmptyTextContent(description);
    return (jsx(SectionWrapper$3, __assign$1({ backgroundColor: backgroundColor }, { children: jsx(Container$1, { children: jsxs(CTAWrapper, __assign$1({ title: title, hasDescription: hasDescription }, { children: [title && (jsx(Header3, __assign$1({ hasDescription: hasDescription, textColor: textColor }, { children: title }))), hasDescription && PortableTextWrapper && (jsx(TextContent$2, __assign$1({ textColor: textColor }, { children: jsx(PortableTextWrapper, { portableText: description.textRaw, mode: textColor === theme.crediteaColors.primary.neutral[50] ? 'light' : 'dark' }) }))), jsx(LinkWrapper$3, __assign$1({ title: title, hasDescription: hasDescription }, { children: jsx(LinkAsButton, { NextLink: nextLink, linkData: linkData, mode: (_b = buttonConfig === null || buttonConfig === void 0 ? void 0 : buttonConfig.colorMode) !== null && _b !== void 0 ? _b : 'dark', variant: (_c = buttonConfig === null || buttonConfig === void 0 ? void 0 : buttonConfig.variant) !== null && _c !== void 0 ? _c : 'primary' }) }))] })) }) })));
};

var countryOptions$6 = function () { return ({ label: '+420', value: 'Czech Republic', id: '10' }); };
var regexpOptions$6 = function () { return /^\d{9}$/; };
var czTranslations = {
    language: 'CZ',
    requiredError: 'Povinné pole',
    maxLengthError: 'Překročili jste maximální délku znaků',
    firstName: {
        placeholder: 'Jméno',
    },
    lastName: {
        placeholder: 'Příjmení',
    },
    email: {
        placeholder: 'E-mail',
        error: 'E-mailová adresa je neplatná. Prosíme uveďte správnou e-mailovou adresu.',
    },
    country: {
        label: 'Země',
        options: countryOptions$6,
    },
    phoneNumber: {
        placeholder: 'Telefonní číslo',
        error: 'Telefonní číslo je neplatné. Prosíme uveďte správné telefonní číslo.',
        regexp: regexpOptions$6,
    },
    submitButtonLabel: 'Odeslat',
    mandatoryFieldsInfo: 'Pole označená * jsou povinná',
    formSuccess: 'Děkujeme! Vaše informace jsme odeslali.',
    formWarning: 'Omlouváme se, něco se pokazilo!',
};

var countryOptions$5 = function () { return ({ label: '+44', value: 'United Kingdom', id: '4' }); };
var regexpOptions$5 = function () { return /^\d{7,10}$/; };
var enTranslations = {
    language: 'EN',
    requiredError: 'This field is mandatory',
    maxLengthError: 'This input exceed max length',
    firstName: {
        placeholder: 'Name',
    },
    lastName: {
        placeholder: 'Surname',
    },
    email: {
        placeholder: 'E-mail',
        error: 'The email format is incorrect. Please provide a valid email.',
    },
    country: {
        label: 'Country',
        options: countryOptions$5,
    },
    phoneNumber: {
        placeholder: 'Phone number',
        error: 'Phone number is invalid. Please provide a valid one.',
        regexp: regexpOptions$5,
    },
    submitButtonLabel: 'Send',
    mandatoryFieldsInfo: 'The fields marked with * are mandatory',
    formSuccess: 'Thank you! Your information has been sent successfully.',
    formWarning: 'Sorry, something went wrong.',
};

var countryOptions$4 = function () { return ({ label: '+52', value: 'Mexico', id: '3' }); };
var regexpOptions$4 = function () { return /^\d{10}$/; };
var esTranslations = {
    language: 'ES',
    requiredError: 'Este campo es mandatorio',
    maxLengthError: 'Has excedido el número máximo de caracteres',
    firstName: {
        placeholder: 'Nombre',
    },
    lastName: {
        placeholder: 'Apellido',
    },
    email: {
        placeholder: 'Correo electrónico',
        error: 'Formato de correo incorrecto. Por favor proporciona un correo válido.',
    },
    country: {
        label: 'País',
        options: countryOptions$4,
    },
    phoneNumber: {
        placeholder: 'Número telefónico',
        error: 'Número telefónico inválido',
        regexp: regexpOptions$4,
    },
    submitButtonLabel: 'Enviar',
    mandatoryFieldsInfo: 'Los campos marcados con * son obligatorios',
    formSuccess: '¡Gracias! Tu información ha sido enviada exitosamente.',
    formWarning: 'Lo sentimos, algo salió mal.',
};

var countryOptions$3 = function () { return ({ label: '+372', value: 'Estonia', id: '9' }); };
var regexpOptions$3 = function () { return /^\d{7,8}$/; };
var etTranslations = {
    language: 'ET',
    requiredError: 'Selle välja täitmine on kohustuslik',
    maxLengthError: 'Oled ületanud maksimaalse tähemärgi pikkuse',
    firstName: {
        placeholder: 'Eesnimi',
    },
    lastName: {
        placeholder: 'Perekonnanimi',
    },
    email: {
        placeholder: 'Meiliaadress',
        error: 'Meili vorming on vale. Sisesta kehtiv meiliaadress.',
    },
    country: {
        label: 'Riik',
        options: countryOptions$3,
    },
    phoneNumber: {
        placeholder: 'Telefoninumber',
        error: 'Telefoninumber on vale. Palun märgi õige telefoninumber.',
        regexp: regexpOptions$3,
    },
    submitButtonLabel: 'Esita',
    mandatoryFieldsInfo: '*-ga tähistatud väljad on kohustuslikud',
    formSuccess: 'Aitäh! Sinu andmed on edukalt saadetud.',
    formWarning: 'Vabandust, midagi läks valesti!',
};

var countryOptions$2 = function () { return ({ label: '+370', value: 'Lithuania', id: '7' }); };
var regexpOptions$2 = function () { return /^\d{8}$/; };
var ltTranslations = {
    language: 'LT',
    requiredError: 'Šis laukas yra privalomas',
    maxLengthError: 'Jūsų pateiktas tekstas yra per ilgas',
    firstName: {
        placeholder: 'Vardas',
    },
    lastName: {
        placeholder: 'Pavardė',
    },
    email: {
        placeholder: 'El. paštas',
        error: 'El. pašto formatas neteisingas. Prašome pateikti galiojantį el. pašto adresą.',
    },
    country: {
        label: 'Šalis',
        options: countryOptions$2,
    },
    phoneNumber: {
        placeholder: 'Telefono numeris',
        error: 'Telefono numeris yra neteisingas. Prašome nurodyti teisingą tel.',
        regexp: regexpOptions$2,
    },
    submitButtonLabel: 'Pateikti',
    mandatoryFieldsInfo: '* pažymėti laukai yra privalomi',
    formSuccess: 'Ačiū! Jūsų informacija sėkmingai išsiųsta.',
    formWarning: 'Atsiprašome, įvyko klaida.',
};

var countryOptions$1 = function () { return ({ label: '+371', value: 'Latvia', id: '8' }); };
var regexpOptions$1 = function () { return /^\d{8}$/; };
var lvTranslations = {
    language: 'LV',
    requiredError: 'Šis lauks ir obligāti aizpildams',
    maxLengthError: 'Pārsniegts maksimālais ievades garums',
    firstName: {
        placeholder: 'Vārds',
    },
    lastName: {
        placeholder: 'Uzvārds',
    },
    email: {
        placeholder: 'E-pasts',
        error: 'E-pasta formāts ir nepareizs. Lūdzu, norādi derīgu e-pasta adresi.',
    },
    country: {
        label: 'Valsts',
        options: countryOptions$1,
    },
    phoneNumber: {
        placeholder: 'Tālruņa numurs',
        error: 'Tālruņa numurs nav derīgs. Lūdzu, norādi derīgu.',
        regexp: regexpOptions$1,
    },
    submitButtonLabel: 'Nosūtīt',
    mandatoryFieldsInfo: 'Ar * atzīmētie lauki ir obligāti',
    formSuccess: 'Paldies! Tava informācija ir veiksmīgi nosūtīta.',
    formWarning: 'Atvaino, ir radusies kļūda.',
};

var countryOptions = function (market) {
    if (market === 'crediteaEE') {
        return { label: '+372', value: 'Estonia', id: '9' };
    }
    if (market === 'crediteaLV') {
        return { label: '+371', value: 'Latvia', id: '8' };
    }
    return { label: '', value: '', id: '' };
};
var regexpOptions = function (market) {
    if (market === 'crediteaEE') {
        return /^\d{7,8}$/;
    }
    if (market === 'crediteaLV') {
        return /^\d{8}$/;
    }
    return /^\d{7,8}$/;
};
var ruTranslations = {
    language: 'RU',
    requiredError: 'Это поле обязательно для заполнения',
    maxLengthError: 'Превышена максимальная длина ввода',
    firstName: {
        placeholder: 'Имя',
    },
    lastName: {
        placeholder: 'Фамилия',
    },
    email: {
        placeholder: 'Эл. почта',
        error: 'Формат эл. почты неверен. Пожалуйста, укажи правильную электронную почту.',
    },
    country: {
        label: 'Страна',
        options: countryOptions,
    },
    phoneNumber: {
        placeholder: 'Номер телефона',
        error: 'Номер телефона недействителен. Пожалуйста, укажи действительный.',
        regexp: regexpOptions,
    },
    submitButtonLabel: 'Отправить',
    mandatoryFieldsInfo: 'Поля, отмеченные *, являются обязательными для заполнения',
    formSuccess: 'Спасибо! Твоя информация успешно отправлена.',
    formWarning: 'Извини, произошла ошибка.',
};

var formValidationPattern = {
    EMAIL_REGEXP: /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
    NAME_REGEXP: /^[^±!@£$%^&*_+§¡€#¢§¶•ªº«\\/<>?:;|=.,]{1,20}$/,
    SURNAME_REGEXP: /^[^±!@£$%^&*_+§¡€#¢§¶•ªº«\\/<>?:;|=.,]{1,100}$/,
    PHONE_REGEXP: /^\d{6,11}$/,
    REQUIRED_REGEXP: /^.+$/g,
};

var localeTranslationMap = {
    EN: enTranslations,
    ET: etTranslations,
    LV: lvTranslations,
    RU: ruTranslations,
    CZ: czTranslations,
    ES: esTranslations,
    LT: ltTranslations,
};
var CampaignForm = function (_a) {
    var _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s;
    var languageCode = _a.languageCode, onSubmit = _a.onSubmit, market = _a.market, _t = _a.addPhoneNumber, addPhoneNumber = _t === void 0 ? false : _t, commentField = _a.commentField, commentPlaceholder = _a.commentPlaceholder, requiredComment = _a.requiredComment, additionalInputs = _a.additionalInputs, consentsArray = _a.consentsArray, PortableTextWrapper = _a.PortableTextWrapper, success = _a.success, resetSuccess = _a.resetSuccess, error = _a.error, overWriteSendButton = _a.overWriteSendButton, downloadButton = _a.downloadButton;
    var translations = localeTranslationMap[languageCode];
    var countryOptions = translations === null || translations === void 0 ? void 0 : translations.country.options(market);
    var defaultValues = {
        firstName: '',
        lastName: '',
        email: '',
        countrySelector: countryOptions,
        phoneNumber: undefined,
        longText: '',
        shortText1: '',
        shortText2: '',
        shortText3: '',
        consent1Value: false,
        consent2Value: false,
        consent3Value: false,
    };
    var form = useForm({
        defaultValues: defaultValues,
        mode: 'onTouched',
    });
    var control = form.control, register = form.register, handleSubmit = form.handleSubmit, _u = form.formState, errors = _u.errors, isValid = _u.isValid, reset = form.reset;
    useEffect(function () {
        if (success) {
            reset();
        }
    }, [success]);
    if (!translations)
        return null;
    var saveFile = function () {
        var _a, _b;
        if (downloadButton && 'file' in downloadButton) {
            FileSaver_min.exports.saveAs(((_b = (_a = downloadButton.file) === null || _a === void 0 ? void 0 : _a.asset) === null || _b === void 0 ? void 0 : _b.url) || '');
        }
    };
    var onClick = overWriteSendButton && downloadButton
        ? handleSubmit(onSubmit) && saveFile
        : handleSubmit(onSubmit);
    return (jsxs("form", __assign$1({ onSubmit: handleSubmit(onSubmit) }, { children: [success && (jsxs(Fragment, { children: [jsx(Snackbar, { variant: "success", isOpen: true, text: translations.formSuccess, dataTestId: "success-info" }), jsx(Gutter, { size: 24 })] })), error && (jsxs(Fragment, { children: [jsx(Snackbar, { variant: "warning", isOpen: true, text: translations.formWarning, dataTestId: "warning-info" }), jsx(Gutter, { size: 24 })] })), jsx(Body3$2, { children: translations.mandatoryFieldsInfo }), jsx(Gutter, { size: 24 }), jsxs(Row$1, { children: [jsx(Col, __assign$1({ xs: 4, sm: 4 }, { children: jsx(InputItem$1, __assign$1({}, register('firstName', {
                            required: (_b = translations.requiredError) !== null && _b !== void 0 ? _b : '',
                            pattern: {
                                value: formValidationPattern.SURNAME_REGEXP,
                                message: (_c = translations.requiredError) !== null && _c !== void 0 ? _c : '',
                            },
                            maxLength: { value: 100, message: (_d = translations.maxLengthError) !== null && _d !== void 0 ? _d : '' },
                        }), { type: "text", placeholder: translations.firstName.placeholder, errorMessage: (_e = errors.firstName) === null || _e === void 0 ? void 0 : _e.message, onKeyDown: resetSuccess, required: true, "data-testid": "firstName" })) })), jsx(Col, __assign$1({ xs: 4, sm: 4 }, { children: jsx(InputItem$1, __assign$1({}, register('lastName', {
                            required: (_f = translations.requiredError) !== null && _f !== void 0 ? _f : '',
                            pattern: {
                                value: formValidationPattern.SURNAME_REGEXP,
                                message: (_g = translations.requiredError) !== null && _g !== void 0 ? _g : '',
                            },
                            maxLength: { value: 100, message: (_h = translations.maxLengthError) !== null && _h !== void 0 ? _h : '' },
                        }), { type: "text", placeholder: translations.lastName.placeholder, errorMessage: (_j = errors.lastName) === null || _j === void 0 ? void 0 : _j.message, onKeyDown: resetSuccess, required: true, "data-testid": "lastName" })) }))] }), jsx(Gutter, { size: 16 }), jsx(InputItem$1, __assign$1({}, register('email', {
                required: (_k = translations.requiredError) !== null && _k !== void 0 ? _k : '',
                pattern: {
                    value: formValidationPattern.EMAIL_REGEXP,
                    message: (_l = translations.email.error) !== null && _l !== void 0 ? _l : '',
                },
            }), { type: "email", placeholder: translations.email.placeholder, errorMessage: (_m = errors.email) === null || _m === void 0 ? void 0 : _m.message, onKeyDown: resetSuccess, required: true, "data-testid": "email" })), addPhoneNumber && (jsxs(Fragment, { children: [jsx(Gutter, { size: 16 }), jsxs(Row$1, { children: [countryOptions && (jsx(Col, __assign$1({ xs: 2, sm: 3, md: 4 }, { children: jsx(Controller, { name: "countrySelector.value", control: control, render: function (_a) {
                                        var field = _a.field;
                                        return (jsx(SelectItem$1, __assign$1({}, field, { title: translations === null || translations === void 0 ? void 0 : translations.country.label, value: countryOptions, isDisabled: true })));
                                    } }) }))), jsx(Col, __assign$1({ xs: 2, sm: 5, md: 8 }, { children: jsx(InputItem$1, __assign$1({}, register('phoneNumber', {
                                    required: (_o = translations.requiredError) !== null && _o !== void 0 ? _o : '',
                                    pattern: {
                                        value: translations.phoneNumber.regexp(market),
                                        message: (_p = translations.phoneNumber.error) !== null && _p !== void 0 ? _p : '',
                                    },
                                }), { type: "number", placeholder: translations.phoneNumber.placeholder, errorMessage: (_q = errors.phoneNumber) === null || _q === void 0 ? void 0 : _q.message, onKeyDown: resetSuccess, required: true, "data-testid": "phoneNumber" })) }))] })] })), commentField && (jsxs(Fragment, { children: [jsx(Gutter, { size: 16 }), jsx(Controller, { name: "longText", control: control, rules: {
                            required: requiredComment && ((_r = translations === null || translations === void 0 ? void 0 : translations.requiredError) !== null && _r !== void 0 ? _r : ''),
                            maxLength: { value: 2000, message: (_s = translations.maxLengthError) !== null && _s !== void 0 ? _s : '' },
                        }, render: function (_a) {
                            var _b;
                            var _c = _a.field; _c.ref; var field = __rest$1(_c, ["ref"]);
                            return (jsx(TextAreaItem, __assign$1({}, field, { placeholder: commentPlaceholder, errorMessage: (_b = errors.longText) === null || _b === void 0 ? void 0 : _b.message, onKeyDown: resetSuccess, required: requiredComment, "data-testid": "longText" })));
                        } })] })), additionalInputs && (additionalInputs === null || additionalInputs === void 0 ? void 0 : additionalInputs.length) > 0 && (jsxs(Fragment, { children: [jsx(Gutter, { size: 32, sizeMobile: 16 }), jsx(Row$1, __assign$1({ "data-testid": "additionalInputsContainer" }, { children: additionalInputs.map(function (input, index) {
                            var _a, _b, _c, _d;
                            var nameWithIndex = "shortText".concat(index + 1);
                            return (jsx(Col, __assign$1({ xs: 4, sm: input.type === 'small' ? 4 : 8, paddingTop: 16 }, { children: jsx(InputItem$1, __assign$1({}, register("".concat(nameWithIndex), {
                                    required: input.required && ((_a = translations.requiredError) !== null && _a !== void 0 ? _a : ''),
                                    pattern: {
                                        value: /^.+$/g,
                                        message: (_b = translations.requiredError) !== null && _b !== void 0 ? _b : '',
                                    },
                                    maxLength: {
                                        value: 100,
                                        message: (_c = translations.maxLengthError) !== null && _c !== void 0 ? _c : '',
                                    },
                                }), { placeholder: input.placeholder, errorMessage: (_d = errors[nameWithIndex]) === null || _d === void 0 ? void 0 : _d.message, onKeyDown: resetSuccess, required: input.required, "data-testid": nameWithIndex })) }), nameWithIndex));
                        }) }))] })), jsx(Gutter, { size: 24 }), consentsArray &&
                (consentsArray === null || consentsArray === void 0 ? void 0 : consentsArray.length) > 0 &&
                consentsArray.map(function (consent, index) {
                    var _a;
                    var nameWithIndex = "consent".concat(index + 1, "Value");
                    return (jsxs(Fragment$1, { children: [jsx(Controller, { control: control, name: nameWithIndex, rules: { required: consent.required && ((_a = translations === null || translations === void 0 ? void 0 : translations.requiredError) !== null && _a !== void 0 ? _a : '') }, render: function (_a) {
                                    var _b, _c;
                                    var field = _a.field;
                                    return (jsx(CheckboxItem$1, __assign$1({}, field, { required: consent.required, labelFromTextEditor: consent.consentText &&
                                            !hasEmptyTextContent(consent.consentText) &&
                                            PortableTextWrapper && (jsx(PortableTextWrapper, { portableText: (_b = consent.consentText) === null || _b === void 0 ? void 0 : _b.textRaw })), errorMessage: (_c = errors[nameWithIndex]) === null || _c === void 0 ? void 0 : _c.message, "data-testid": nameWithIndex })));
                                } }), jsx(Gutter, { size: 16 })] }, nameWithIndex));
                }), jsx(Gutter, { size: 24 }), jsx(Button$2, __assign$1({ type: "submit", variant: "primary", onClick: onClick, "data-testid": "submit-button", disabled: !isValid, size: "small" }, { children: overWriteSendButton ? downloadButton === null || downloadButton === void 0 ? void 0 : downloadButton.linkTitle : translations.submitButtonLabel }))] })));
};

var CardsContainer$2 = styled.div(templateObject_2$e || (templateObject_2$e = __makeTemplateObject(["\n  display: flex;\n  flex-direction: column;\n  gap: 16px;\n\n  ", "\n"], ["\n  display: flex;\n  flex-direction: column;\n  gap: 16px;\n\n  ", "\n"])), function (_a) {
    var theme = _a.theme, numberInRow = _a.numberInRow;
    return css(templateObject_1$i || (templateObject_1$i = __makeTemplateObject(["\n    ", " {\n      display: grid;\n      grid-template-columns: repeat(2, 1fr);\n      gap: 24px;\n    }\n\n    ", " {\n      grid-template-columns: repeat(", ", 1fr);\n      gap: 32px;\n    }\n  "], ["\n    ", " {\n      display: grid;\n      grid-template-columns: repeat(2, 1fr);\n      gap: 24px;\n    }\n\n    ", " {\n      grid-template-columns: repeat(", ", 1fr);\n      gap: 32px;\n    }\n  "])), theme.breakpoints.sm, theme.breakpoints.md, numberInRow === 2 ? 2 : 3);
});
var TitleSection$1 = styled.div(templateObject_3$b || (templateObject_3$b = __makeTemplateObject(["\n  text-align: center;\n"], ["\n  text-align: center;\n"])));
var CardWrapper = styled.div(templateObject_4$a || (templateObject_4$a = __makeTemplateObject(["\n  background-color: ", ";\n  padding: 24px 16px;\n  border-radius: 16px;\n  display: flex;\n  flex-direction: column;\n  height: 100%;\n  justify-content: space-between;\n\n  & > button {\n    width: fit-content;\n  }\n"], ["\n  background-color: ", ";\n  padding: 24px 16px;\n  border-radius: 16px;\n  display: flex;\n  flex-direction: column;\n  height: 100%;\n  justify-content: space-between;\n\n  & > button {\n    width: fit-content;\n  }\n"])), function (_a) {
    var backgroundColor = _a.backgroundColor;
    return backgroundColor;
});
var templateObject_1$i, templateObject_2$e, templateObject_3$b, templateObject_4$a;

var FeatureCardWrapper = function (_a) {
    var element = _a.element, backgroundColor = _a.backgroundColor, textColor = _a.textColor, PortableTextWrapper = _a.PortableTextWrapper, Link = _a.Link, mode = _a.mode;
    var hasLink = element.hasLink, linkData = element.linkData, iconName = element.iconName, iconColor = element.iconColor, title = element.title, textContent = element.textContent, buttonConfig = element.buttonConfig;
    var shouldShowLink = (hasLink &&
        (linkData === null || linkData === void 0 ? void 0 : linkData.__typename) === 'Link' &&
        ((linkData === null || linkData === void 0 ? void 0 : linkData.externalUrl) || (linkData === null || linkData === void 0 ? void 0 : linkData.internalLink))) ||
        (hasLink && (linkData === null || linkData === void 0 ? void 0 : linkData.__typename) === 'DownloadFileButton');
    return (jsxs(CardWrapper, __assign$1({ backgroundColor: backgroundColor }, { children: [jsx(FeatureCard, { icon: iconName, iconBackgroundColor: iconColor === null || iconColor === void 0 ? void 0 : iconColor.value, title: title, textColor: textColor, textContent: textContent &&
                    !hasEmptyTextContent(element.textContent) &&
                    PortableTextWrapper && (jsx(PortableTextWrapper, { portableText: textContent === null || textContent === void 0 ? void 0 : textContent.textRaw, mode: mode })), alignment: "left", fontSizeVariant: "H6", mode: mode }), shouldShowLink && (jsxs(Fragment, { children: [jsx(Gutter, { size: 24 }), jsx(ButtonOrDownloadFile, { nextLink: Link, linkData: linkData, buttonConfig: buttonConfig, autoWidth: "xs" })] }))] })));
};
var CardsSection = function (_a) {
    var title = _a.title, description = _a.description, numberInRow = _a.numberInRow, elements = _a.elements, PortableTextWrapper = _a.PortableTextWrapper, Link = _a.Link, _b = _a.textColor, textColor = _b === void 0 ? theme.colors.cardsSection.defaultColor : _b, _c = _a.backgroundColor, backgroundColor = _c === void 0 ? theme.colors.cardsSection.defaultBackground : _c;
    var mode = textColor === theme.crediteaColors.primary.neutral[50] ? 'light' : 'dark';
    var hasDescription = description && !hasEmptyTextContent(description);
    return (jsxs(Fragment, { children: [jsxs(TitleSection$1, { children: [title && jsx(Header2, { children: title }), hasDescription && PortableTextWrapper && (jsxs(Fragment, { children: [jsx(Gutter, { size: 16 }), description.textRaw && (jsx(PortableTextWrapper, { portableText: description.textRaw, mode: mode }))] }))] }), (title || hasDescription) && jsx(Gutter, { size: 24 }), jsx(CardsContainer$2, __assign$1({ numberInRow: numberInRow, "data-testid": "cards-container" }, { children: elements === null || elements === void 0 ? void 0 : elements.map(function (element, index) { return (jsx(React.Fragment, { children: jsx(FeatureCardWrapper, { element: element, backgroundColor: backgroundColor, textColor: textColor, PortableTextWrapper: PortableTextWrapper, Link: Link, mode: mode }) }, index)); }) }))] }));
};

var CardsContainer$1 = styled.div(templateObject_2$d || (templateObject_2$d = __makeTemplateObject(["\n  display: flex;\n  flex-direction: column;\n  gap: 35px 24px;\n  ", ";\n"], ["\n  display: flex;\n  flex-direction: column;\n  gap: 35px 24px;\n  ", ";\n"])), function (_a) {
    var theme = _a.theme, elementCount = _a.elementCount;
    return css(templateObject_1$h || (templateObject_1$h = __makeTemplateObject(["\n    ", " {\n      display: grid;\n      grid-template-columns: repeat(2, 1fr);\n      gap: 25px 24px;\n    }\n\n    ", " {\n      grid-template-columns: repeat(", ", 1fr);\n      gap: 25px 32px;\n    }\n  "], ["\n    ", " {\n      display: grid;\n      grid-template-columns: repeat(2, 1fr);\n      gap: 25px 24px;\n    }\n\n    ", " {\n      grid-template-columns: repeat(", ", 1fr);\n      gap: 25px 32px;\n    }\n  "])), theme.breakpoints.sm, theme.breakpoints.md, elementCount === 3 ? 3 : 2);
});
var templateObject_1$h, templateObject_2$d;

var CardsWithImage = function (_a) {
    var allCards = _a.allCards, PortableTextWrapper = _a.PortableTextWrapper;
    return (jsx(CardsContainer$1, __assign$1({ elementCount: (allCards === null || allCards === void 0 ? void 0 : allCards.length) || 0, "data-testid": "cards-container" }, { children: allCards === null || allCards === void 0 ? void 0 : allCards.map(function (card, i) {
            return (jsx(ImageCard, { title: card.title, image: card.image, description: card.description &&
                    !hasEmptyTextContent(card.description) &&
                    PortableTextWrapper && jsx(PortableTextWrapper, { portableText: card.description.textRaw }) }, i));
        }) })));
};

function styleInject(css, ref) {
  if ( ref === void 0 ) ref = {};
  var insertAt = ref.insertAt;

  if (!css || typeof document === 'undefined') { return; }

  var head = document.head || document.getElementsByTagName('head')[0];
  var style = document.createElement('style');
  style.type = 'text/css';

  if (insertAt === 'top') {
    if (head.firstChild) {
      head.insertBefore(style, head.firstChild);
    } else {
      head.appendChild(style);
    }
  } else {
    head.appendChild(style);
  }

  if (style.styleSheet) {
    style.styleSheet.cssText = css;
  } else {
    style.appendChild(document.createTextNode(css));
  }
}

var css_248z$1 = "@charset 'UTF-8';\n/* Slider */\n.slick-loading .slick-list\n{\n    background: #fff url('./ajax-loader.gif') center center no-repeat;\n}\n\n/* Icons */\n@font-face\n{\n    font-family: 'slick';\n    font-weight: normal;\n    font-style: normal;\n\n    src: url('./fonts/slick.eot');\n    src: url('./fonts/slick.eot?#iefix') format('embedded-opentype'), url('./fonts/slick.woff') format('woff'), url('./fonts/slick.ttf') format('truetype'), url('./fonts/slick.svg#slick') format('svg');\n}\n/* Arrows */\n.slick-prev,\n.slick-next\n{\n    font-size: 0;\n    line-height: 0;\n\n    position: absolute;\n    top: 50%;\n\n    display: block;\n\n    width: 20px;\n    height: 20px;\n    padding: 0;\n    -webkit-transform: translate(0, -50%);\n    -ms-transform: translate(0, -50%);\n    transform: translate(0, -50%);\n\n    cursor: pointer;\n\n    color: transparent;\n    border: none;\n    outline: none;\n    background: transparent;\n}\n.slick-prev:hover,\n.slick-prev:focus,\n.slick-next:hover,\n.slick-next:focus\n{\n    color: transparent;\n    outline: none;\n    background: transparent;\n}\n.slick-prev:hover:before,\n.slick-prev:focus:before,\n.slick-next:hover:before,\n.slick-next:focus:before\n{\n    opacity: 1;\n}\n.slick-prev.slick-disabled:before,\n.slick-next.slick-disabled:before\n{\n    opacity: .25;\n}\n\n.slick-prev:before,\n.slick-next:before\n{\n    font-family: 'slick';\n    font-size: 20px;\n    line-height: 1;\n\n    opacity: .75;\n    color: white;\n\n    -webkit-font-smoothing: antialiased;\n    -moz-osx-font-smoothing: grayscale;\n}\n\n.slick-prev\n{\n    left: -25px;\n}\n[dir='rtl'] .slick-prev\n{\n    right: -25px;\n    left: auto;\n}\n.slick-prev:before\n{\n    content: '←';\n}\n[dir='rtl'] .slick-prev:before\n{\n    content: '→';\n}\n\n.slick-next\n{\n    right: -25px;\n}\n[dir='rtl'] .slick-next\n{\n    right: auto;\n    left: -25px;\n}\n.slick-next:before\n{\n    content: '→';\n}\n[dir='rtl'] .slick-next:before\n{\n    content: '←';\n}\n\n/* Dots */\n.slick-dotted.slick-slider\n{\n    margin-bottom: 30px;\n}\n\n.slick-dots\n{\n    position: absolute;\n    bottom: -25px;\n\n    display: block;\n\n    width: 100%;\n    padding: 0;\n    margin: 0;\n\n    list-style: none;\n\n    text-align: center;\n}\n.slick-dots li\n{\n    position: relative;\n\n    display: inline-block;\n\n    width: 20px;\n    height: 20px;\n    margin: 0 5px;\n    padding: 0;\n\n    cursor: pointer;\n}\n.slick-dots li button\n{\n    font-size: 0;\n    line-height: 0;\n\n    display: block;\n\n    width: 20px;\n    height: 20px;\n    padding: 5px;\n\n    cursor: pointer;\n\n    color: transparent;\n    border: 0;\n    outline: none;\n    background: transparent;\n}\n.slick-dots li button:hover,\n.slick-dots li button:focus\n{\n    outline: none;\n}\n.slick-dots li button:hover:before,\n.slick-dots li button:focus:before\n{\n    opacity: 1;\n}\n.slick-dots li button:before\n{\n    font-family: 'slick';\n    font-size: 6px;\n    line-height: 20px;\n\n    position: absolute;\n    top: 0;\n    left: 0;\n\n    width: 20px;\n    height: 20px;\n\n    content: '•';\n    text-align: center;\n\n    opacity: .25;\n    color: black;\n\n    -webkit-font-smoothing: antialiased;\n    -moz-osx-font-smoothing: grayscale;\n}\n.slick-dots li.slick-active button:before\n{\n    opacity: .75;\n    color: black;\n}\n";
styleInject(css_248z$1);

var css_248z = "/* Slider */\n.slick-slider\n{\n    position: relative;\n\n    display: block;\n    box-sizing: border-box;\n\n    -webkit-user-select: none;\n       -moz-user-select: none;\n        -ms-user-select: none;\n            user-select: none;\n\n    -webkit-touch-callout: none;\n    -khtml-user-select: none;\n    -ms-touch-action: pan-y;\n        touch-action: pan-y;\n    -webkit-tap-highlight-color: transparent;\n}\n\n.slick-list\n{\n    position: relative;\n\n    display: block;\n    overflow: hidden;\n\n    margin: 0;\n    padding: 0;\n}\n.slick-list:focus\n{\n    outline: none;\n}\n.slick-list.dragging\n{\n    cursor: pointer;\n    cursor: hand;\n}\n\n.slick-slider .slick-track,\n.slick-slider .slick-list\n{\n    -webkit-transform: translate3d(0, 0, 0);\n       -moz-transform: translate3d(0, 0, 0);\n        -ms-transform: translate3d(0, 0, 0);\n         -o-transform: translate3d(0, 0, 0);\n            transform: translate3d(0, 0, 0);\n}\n\n.slick-track\n{\n    position: relative;\n    top: 0;\n    left: 0;\n\n    display: block;\n    margin-left: auto;\n    margin-right: auto;\n}\n.slick-track:before,\n.slick-track:after\n{\n    display: table;\n\n    content: '';\n}\n.slick-track:after\n{\n    clear: both;\n}\n.slick-loading .slick-track\n{\n    visibility: hidden;\n}\n\n.slick-slide\n{\n    display: none;\n    float: left;\n\n    height: 100%;\n    min-height: 1px;\n}\n[dir='rtl'] .slick-slide\n{\n    float: right;\n}\n.slick-slide img\n{\n    display: block;\n}\n.slick-slide.slick-loading img\n{\n    display: none;\n}\n.slick-slide.dragging img\n{\n    pointer-events: none;\n}\n.slick-initialized .slick-slide\n{\n    display: block;\n}\n.slick-loading .slick-slide\n{\n    visibility: hidden;\n}\n.slick-vertical .slick-slide\n{\n    display: block;\n\n    height: auto;\n\n    border: 1px solid transparent;\n}\n.slick-arrow.slick-hidden {\n    display: none;\n}\n";
styleInject(css_248z);

var ArrowsContainer = styled.div(templateObject_1$g || (templateObject_1$g = __makeTemplateObject(["\n  display: flex;\n  margin-bottom: ", ";\n"], ["\n  display: flex;\n  margin-bottom: ", ";\n"])), function (_a) {
    var theme = _a.theme;
    return theme.space[24];
});
var ArrowWrapper = styled.div(templateObject_2$c || (templateObject_2$c = __makeTemplateObject(["\n  cursor: pointer;\n  margin-right: ", ";\n"], ["\n  cursor: pointer;\n  margin-right: ", ";\n"])), function (_a) {
    var theme = _a.theme;
    return theme.space[20];
});
var templateObject_1$g, templateObject_2$c;

var SliderArrows = function (_a) {
    var sliderRef = _a.sliderRef, disableLeftArrow = _a.disableLeftArrow, disableRightArrow = _a.disableRightArrow;
    var forwardSlide = function () {
        var _a;
        (_a = sliderRef === null || sliderRef === void 0 ? void 0 : sliderRef.current) === null || _a === void 0 ? void 0 : _a.slickNext();
    };
    var rewindSlide = function () {
        var _a;
        (_a = sliderRef === null || sliderRef === void 0 ? void 0 : sliderRef.current) === null || _a === void 0 ? void 0 : _a.slickPrev();
    };
    return (jsxs(ArrowsContainer, __assign$1({ "data-testid": "slider-arrows" }, { children: [jsx(ArrowWrapper, __assign$1({ onClick: disableLeftArrow ? undefined : rewindSlide }, { children: disableLeftArrow ? (jsx(Icon$4, { fillColor: theme.colors.sliderArrows.disabledIconColor, name: "ArrowLeftOn" })) : (jsx(Icon$4, { name: "ArrowLeftOn", fillColor: theme.colors.sliderArrows.activeIconColor })) })), jsx(ArrowWrapper, __assign$1({ onClick: disableRightArrow ? undefined : forwardSlide }, { children: disableRightArrow ? (jsx(Icon$4, { fillColor: theme.colors.sliderArrows.disabledIconColor, name: "ArrowRightOn" })) : (jsx(Icon$4, { name: "ArrowRightOn", fillColor: theme.colors.sliderArrows.activeIconColor })) }))] })));
};

var lib = {};

var slider = {};

var innerSlider = {};

var initialState = {};

(function (exports) {

	Object.defineProperty(exports, "__esModule", {
	  value: true
	});
	exports["default"] = void 0;
	var initialState = {
	  animating: false,
	  autoplaying: null,
	  currentDirection: 0,
	  currentLeft: null,
	  currentSlide: 0,
	  direction: 1,
	  dragging: false,
	  edgeDragged: false,
	  initialized: false,
	  lazyLoadedList: [],
	  listHeight: null,
	  listWidth: null,
	  scrolling: false,
	  slideCount: null,
	  slideHeight: null,
	  slideWidth: null,
	  swipeLeft: null,
	  swiped: false,
	  // used by swipeEvent. differentites between touch and swipe.
	  swiping: false,
	  touchObject: {
	    startX: 0,
	    startY: 0,
	    curX: 0,
	    curY: 0
	  },
	  trackStyle: {},
	  trackWidth: 0,
	  targetSlide: 0
	};
	var _default = initialState;
	exports["default"] = _default;
} (initialState));

/**
 * lodash (Custom Build) <https://lodash.com/>
 * Build: `lodash modularize exports="npm" -o ./`
 * Copyright jQuery Foundation and other contributors <https://jquery.org/>
 * Released under MIT license <https://lodash.com/license>
 * Based on Underscore.js 1.8.3 <http://underscorejs.org/LICENSE>
 * Copyright Jeremy Ashkenas, DocumentCloud and Investigative Reporters & Editors
 */

/** Used as the `TypeError` message for "Functions" methods. */
var FUNC_ERROR_TEXT = 'Expected a function';

/** Used as references for various `Number` constants. */
var NAN = 0 / 0;

/** `Object#toString` result references. */
var symbolTag = '[object Symbol]';

/** Used to match leading and trailing whitespace. */
var reTrim = /^\s+|\s+$/g;

/** Used to detect bad signed hexadecimal string values. */
var reIsBadHex = /^[-+]0x[0-9a-f]+$/i;

/** Used to detect binary string values. */
var reIsBinary = /^0b[01]+$/i;

/** Used to detect octal string values. */
var reIsOctal = /^0o[0-7]+$/i;

/** Built-in method references without a dependency on `root`. */
var freeParseInt = parseInt;

/** Detect free variable `global` from Node.js. */
var freeGlobal = typeof commonjsGlobal == 'object' && commonjsGlobal && commonjsGlobal.Object === Object && commonjsGlobal;

/** Detect free variable `self`. */
var freeSelf = typeof self == 'object' && self && self.Object === Object && self;

/** Used as a reference to the global object. */
var root = freeGlobal || freeSelf || Function('return this')();

/** Used for built-in method references. */
var objectProto = Object.prototype;

/**
 * Used to resolve the
 * [`toStringTag`](http://ecma-international.org/ecma-262/7.0/#sec-object.prototype.tostring)
 * of values.
 */
var objectToString = objectProto.toString;

/* Built-in method references for those with the same name as other `lodash` methods. */
var nativeMax = Math.max,
    nativeMin = Math.min;

/**
 * Gets the timestamp of the number of milliseconds that have elapsed since
 * the Unix epoch (1 January 1970 00:00:00 UTC).
 *
 * @static
 * @memberOf _
 * @since 2.4.0
 * @category Date
 * @returns {number} Returns the timestamp.
 * @example
 *
 * _.defer(function(stamp) {
 *   console.log(_.now() - stamp);
 * }, _.now());
 * // => Logs the number of milliseconds it took for the deferred invocation.
 */
var now = function() {
  return root.Date.now();
};

/**
 * Creates a debounced function that delays invoking `func` until after `wait`
 * milliseconds have elapsed since the last time the debounced function was
 * invoked. The debounced function comes with a `cancel` method to cancel
 * delayed `func` invocations and a `flush` method to immediately invoke them.
 * Provide `options` to indicate whether `func` should be invoked on the
 * leading and/or trailing edge of the `wait` timeout. The `func` is invoked
 * with the last arguments provided to the debounced function. Subsequent
 * calls to the debounced function return the result of the last `func`
 * invocation.
 *
 * **Note:** If `leading` and `trailing` options are `true`, `func` is
 * invoked on the trailing edge of the timeout only if the debounced function
 * is invoked more than once during the `wait` timeout.
 *
 * If `wait` is `0` and `leading` is `false`, `func` invocation is deferred
 * until to the next tick, similar to `setTimeout` with a timeout of `0`.
 *
 * See [David Corbacho's article](https://css-tricks.com/debouncing-throttling-explained-examples/)
 * for details over the differences between `_.debounce` and `_.throttle`.
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @category Function
 * @param {Function} func The function to debounce.
 * @param {number} [wait=0] The number of milliseconds to delay.
 * @param {Object} [options={}] The options object.
 * @param {boolean} [options.leading=false]
 *  Specify invoking on the leading edge of the timeout.
 * @param {number} [options.maxWait]
 *  The maximum time `func` is allowed to be delayed before it's invoked.
 * @param {boolean} [options.trailing=true]
 *  Specify invoking on the trailing edge of the timeout.
 * @returns {Function} Returns the new debounced function.
 * @example
 *
 * // Avoid costly calculations while the window size is in flux.
 * jQuery(window).on('resize', _.debounce(calculateLayout, 150));
 *
 * // Invoke `sendMail` when clicked, debouncing subsequent calls.
 * jQuery(element).on('click', _.debounce(sendMail, 300, {
 *   'leading': true,
 *   'trailing': false
 * }));
 *
 * // Ensure `batchLog` is invoked once after 1 second of debounced calls.
 * var debounced = _.debounce(batchLog, 250, { 'maxWait': 1000 });
 * var source = new EventSource('/stream');
 * jQuery(source).on('message', debounced);
 *
 * // Cancel the trailing debounced invocation.
 * jQuery(window).on('popstate', debounced.cancel);
 */
function debounce(func, wait, options) {
  var lastArgs,
      lastThis,
      maxWait,
      result,
      timerId,
      lastCallTime,
      lastInvokeTime = 0,
      leading = false,
      maxing = false,
      trailing = true;

  if (typeof func != 'function') {
    throw new TypeError(FUNC_ERROR_TEXT);
  }
  wait = toNumber(wait) || 0;
  if (isObject(options)) {
    leading = !!options.leading;
    maxing = 'maxWait' in options;
    maxWait = maxing ? nativeMax(toNumber(options.maxWait) || 0, wait) : maxWait;
    trailing = 'trailing' in options ? !!options.trailing : trailing;
  }

  function invokeFunc(time) {
    var args = lastArgs,
        thisArg = lastThis;

    lastArgs = lastThis = undefined;
    lastInvokeTime = time;
    result = func.apply(thisArg, args);
    return result;
  }

  function leadingEdge(time) {
    // Reset any `maxWait` timer.
    lastInvokeTime = time;
    // Start the timer for the trailing edge.
    timerId = setTimeout(timerExpired, wait);
    // Invoke the leading edge.
    return leading ? invokeFunc(time) : result;
  }

  function remainingWait(time) {
    var timeSinceLastCall = time - lastCallTime,
        timeSinceLastInvoke = time - lastInvokeTime,
        result = wait - timeSinceLastCall;

    return maxing ? nativeMin(result, maxWait - timeSinceLastInvoke) : result;
  }

  function shouldInvoke(time) {
    var timeSinceLastCall = time - lastCallTime,
        timeSinceLastInvoke = time - lastInvokeTime;

    // Either this is the first call, activity has stopped and we're at the
    // trailing edge, the system time has gone backwards and we're treating
    // it as the trailing edge, or we've hit the `maxWait` limit.
    return (lastCallTime === undefined || (timeSinceLastCall >= wait) ||
      (timeSinceLastCall < 0) || (maxing && timeSinceLastInvoke >= maxWait));
  }

  function timerExpired() {
    var time = now();
    if (shouldInvoke(time)) {
      return trailingEdge(time);
    }
    // Restart the timer.
    timerId = setTimeout(timerExpired, remainingWait(time));
  }

  function trailingEdge(time) {
    timerId = undefined;

    // Only invoke if we have `lastArgs` which means `func` has been
    // debounced at least once.
    if (trailing && lastArgs) {
      return invokeFunc(time);
    }
    lastArgs = lastThis = undefined;
    return result;
  }

  function cancel() {
    if (timerId !== undefined) {
      clearTimeout(timerId);
    }
    lastInvokeTime = 0;
    lastArgs = lastCallTime = lastThis = timerId = undefined;
  }

  function flush() {
    return timerId === undefined ? result : trailingEdge(now());
  }

  function debounced() {
    var time = now(),
        isInvoking = shouldInvoke(time);

    lastArgs = arguments;
    lastThis = this;
    lastCallTime = time;

    if (isInvoking) {
      if (timerId === undefined) {
        return leadingEdge(lastCallTime);
      }
      if (maxing) {
        // Handle invocations in a tight loop.
        timerId = setTimeout(timerExpired, wait);
        return invokeFunc(lastCallTime);
      }
    }
    if (timerId === undefined) {
      timerId = setTimeout(timerExpired, wait);
    }
    return result;
  }
  debounced.cancel = cancel;
  debounced.flush = flush;
  return debounced;
}

/**
 * Checks if `value` is the
 * [language type](http://www.ecma-international.org/ecma-262/7.0/#sec-ecmascript-language-types)
 * of `Object`. (e.g. arrays, functions, objects, regexes, `new Number(0)`, and `new String('')`)
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is an object, else `false`.
 * @example
 *
 * _.isObject({});
 * // => true
 *
 * _.isObject([1, 2, 3]);
 * // => true
 *
 * _.isObject(_.noop);
 * // => true
 *
 * _.isObject(null);
 * // => false
 */
function isObject(value) {
  var type = typeof value;
  return !!value && (type == 'object' || type == 'function');
}

/**
 * Checks if `value` is object-like. A value is object-like if it's not `null`
 * and has a `typeof` result of "object".
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is object-like, else `false`.
 * @example
 *
 * _.isObjectLike({});
 * // => true
 *
 * _.isObjectLike([1, 2, 3]);
 * // => true
 *
 * _.isObjectLike(_.noop);
 * // => false
 *
 * _.isObjectLike(null);
 * // => false
 */
function isObjectLike(value) {
  return !!value && typeof value == 'object';
}

/**
 * Checks if `value` is classified as a `Symbol` primitive or object.
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a symbol, else `false`.
 * @example
 *
 * _.isSymbol(Symbol.iterator);
 * // => true
 *
 * _.isSymbol('abc');
 * // => false
 */
function isSymbol(value) {
  return typeof value == 'symbol' ||
    (isObjectLike(value) && objectToString.call(value) == symbolTag);
}

/**
 * Converts `value` to a number.
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to process.
 * @returns {number} Returns the number.
 * @example
 *
 * _.toNumber(3.2);
 * // => 3.2
 *
 * _.toNumber(Number.MIN_VALUE);
 * // => 5e-324
 *
 * _.toNumber(Infinity);
 * // => Infinity
 *
 * _.toNumber('3.2');
 * // => 3.2
 */
function toNumber(value) {
  if (typeof value == 'number') {
    return value;
  }
  if (isSymbol(value)) {
    return NAN;
  }
  if (isObject(value)) {
    var other = typeof value.valueOf == 'function' ? value.valueOf() : value;
    value = isObject(other) ? (other + '') : other;
  }
  if (typeof value != 'string') {
    return value === 0 ? value : +value;
  }
  value = value.replace(reTrim, '');
  var isBinary = reIsBinary.test(value);
  return (isBinary || reIsOctal.test(value))
    ? freeParseInt(value.slice(2), isBinary ? 2 : 8)
    : (reIsBadHex.test(value) ? NAN : +value);
}

var lodash_debounce = debounce;

var classnames = {exports: {}};

/*!
	Copyright (c) 2018 Jed Watson.
	Licensed under the MIT License (MIT), see
	http://jedwatson.github.io/classnames
*/

(function (module) {
	/* global define */

	(function () {

		var hasOwn = {}.hasOwnProperty;

		function classNames() {
			var classes = [];

			for (var i = 0; i < arguments.length; i++) {
				var arg = arguments[i];
				if (!arg) continue;

				var argType = typeof arg;

				if (argType === 'string' || argType === 'number') {
					classes.push(arg);
				} else if (Array.isArray(arg)) {
					if (arg.length) {
						var inner = classNames.apply(null, arg);
						if (inner) {
							classes.push(inner);
						}
					}
				} else if (argType === 'object') {
					if (arg.toString !== Object.prototype.toString && !arg.toString.toString().includes('[native code]')) {
						classes.push(arg.toString());
						continue;
					}

					for (var key in arg) {
						if (hasOwn.call(arg, key) && arg[key]) {
							classes.push(key);
						}
					}
				}
			}

			return classes.join(' ');
		}

		if (module.exports) {
			classNames.default = classNames;
			module.exports = classNames;
		} else {
			window.classNames = classNames;
		}
	}());
} (classnames));

var innerSliderUtils = {};

Object.defineProperty(innerSliderUtils, "__esModule", {
  value: true
});
innerSliderUtils.checkSpecKeys = innerSliderUtils.checkNavigable = innerSliderUtils.changeSlide = innerSliderUtils.canUseDOM = innerSliderUtils.canGoNext = void 0;
innerSliderUtils.clamp = clamp;
innerSliderUtils.swipeStart = innerSliderUtils.swipeMove = innerSliderUtils.swipeEnd = innerSliderUtils.slidesOnRight = innerSliderUtils.slidesOnLeft = innerSliderUtils.slideHandler = innerSliderUtils.siblingDirection = innerSliderUtils.safePreventDefault = innerSliderUtils.lazyStartIndex = innerSliderUtils.lazySlidesOnRight = innerSliderUtils.lazySlidesOnLeft = innerSliderUtils.lazyEndIndex = innerSliderUtils.keyHandler = innerSliderUtils.initializedState = innerSliderUtils.getWidth = innerSliderUtils.getTrackLeft = innerSliderUtils.getTrackCSS = innerSliderUtils.getTrackAnimateCSS = innerSliderUtils.getTotalSlides = innerSliderUtils.getSwipeDirection = innerSliderUtils.getSlideCount = innerSliderUtils.getRequiredLazySlides = innerSliderUtils.getPreClones = innerSliderUtils.getPostClones = innerSliderUtils.getOnDemandLazySlides = innerSliderUtils.getNavigableIndexes = innerSliderUtils.getHeight = innerSliderUtils.extractObject = void 0;

var _react$4 = _interopRequireDefault$4(React);

function _interopRequireDefault$4(obj) { return obj && obj.__esModule ? obj : { "default": obj }; }

function ownKeys$4(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread$4(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys$4(Object(source), !0).forEach(function (key) { _defineProperty$4(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys$4(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

function _defineProperty$4(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

function clamp(number, lowerBound, upperBound) {
  return Math.max(lowerBound, Math.min(number, upperBound));
}

var safePreventDefault = function safePreventDefault(event) {
  var passiveEvents = ["onTouchStart", "onTouchMove", "onWheel"];

  if (!passiveEvents.includes(event._reactName)) {
    event.preventDefault();
  }
};

innerSliderUtils.safePreventDefault = safePreventDefault;

var getOnDemandLazySlides = function getOnDemandLazySlides(spec) {
  var onDemandSlides = [];
  var startIndex = lazyStartIndex(spec);
  var endIndex = lazyEndIndex(spec);

  for (var slideIndex = startIndex; slideIndex < endIndex; slideIndex++) {
    if (spec.lazyLoadedList.indexOf(slideIndex) < 0) {
      onDemandSlides.push(slideIndex);
    }
  }

  return onDemandSlides;
}; // return list of slides that need to be present


innerSliderUtils.getOnDemandLazySlides = getOnDemandLazySlides;

var getRequiredLazySlides = function getRequiredLazySlides(spec) {
  var requiredSlides = [];
  var startIndex = lazyStartIndex(spec);
  var endIndex = lazyEndIndex(spec);

  for (var slideIndex = startIndex; slideIndex < endIndex; slideIndex++) {
    requiredSlides.push(slideIndex);
  }

  return requiredSlides;
}; // startIndex that needs to be present


innerSliderUtils.getRequiredLazySlides = getRequiredLazySlides;

var lazyStartIndex = function lazyStartIndex(spec) {
  return spec.currentSlide - lazySlidesOnLeft(spec);
};

innerSliderUtils.lazyStartIndex = lazyStartIndex;

var lazyEndIndex = function lazyEndIndex(spec) {
  return spec.currentSlide + lazySlidesOnRight(spec);
};

innerSliderUtils.lazyEndIndex = lazyEndIndex;

var lazySlidesOnLeft = function lazySlidesOnLeft(spec) {
  return spec.centerMode ? Math.floor(spec.slidesToShow / 2) + (parseInt(spec.centerPadding) > 0 ? 1 : 0) : 0;
};

innerSliderUtils.lazySlidesOnLeft = lazySlidesOnLeft;

var lazySlidesOnRight = function lazySlidesOnRight(spec) {
  return spec.centerMode ? Math.floor((spec.slidesToShow - 1) / 2) + 1 + (parseInt(spec.centerPadding) > 0 ? 1 : 0) : spec.slidesToShow;
}; // get width of an element


innerSliderUtils.lazySlidesOnRight = lazySlidesOnRight;

var getWidth = function getWidth(elem) {
  return elem && elem.offsetWidth || 0;
};

innerSliderUtils.getWidth = getWidth;

var getHeight = function getHeight(elem) {
  return elem && elem.offsetHeight || 0;
};

innerSliderUtils.getHeight = getHeight;

var getSwipeDirection = function getSwipeDirection(touchObject) {
  var verticalSwiping = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;
  var xDist, yDist, r, swipeAngle;
  xDist = touchObject.startX - touchObject.curX;
  yDist = touchObject.startY - touchObject.curY;
  r = Math.atan2(yDist, xDist);
  swipeAngle = Math.round(r * 180 / Math.PI);

  if (swipeAngle < 0) {
    swipeAngle = 360 - Math.abs(swipeAngle);
  }

  if (swipeAngle <= 45 && swipeAngle >= 0 || swipeAngle <= 360 && swipeAngle >= 315) {
    return "left";
  }

  if (swipeAngle >= 135 && swipeAngle <= 225) {
    return "right";
  }

  if (verticalSwiping === true) {
    if (swipeAngle >= 35 && swipeAngle <= 135) {
      return "up";
    } else {
      return "down";
    }
  }

  return "vertical";
}; // whether or not we can go next


innerSliderUtils.getSwipeDirection = getSwipeDirection;

var canGoNext = function canGoNext(spec) {
  var canGo = true;

  if (!spec.infinite) {
    if (spec.centerMode && spec.currentSlide >= spec.slideCount - 1) {
      canGo = false;
    } else if (spec.slideCount <= spec.slidesToShow || spec.currentSlide >= spec.slideCount - spec.slidesToShow) {
      canGo = false;
    }
  }

  return canGo;
}; // given an object and a list of keys, return new object with given keys


innerSliderUtils.canGoNext = canGoNext;

var extractObject = function extractObject(spec, keys) {
  var newObject = {};
  keys.forEach(function (key) {
    return newObject[key] = spec[key];
  });
  return newObject;
}; // get initialized state


innerSliderUtils.extractObject = extractObject;

var initializedState = function initializedState(spec) {
  // spec also contains listRef, trackRef
  var slideCount = _react$4["default"].Children.count(spec.children);

  var listNode = spec.listRef;
  var listWidth = Math.ceil(getWidth(listNode));
  var trackNode = spec.trackRef && spec.trackRef.node;
  var trackWidth = Math.ceil(getWidth(trackNode));
  var slideWidth;

  if (!spec.vertical) {
    var centerPaddingAdj = spec.centerMode && parseInt(spec.centerPadding) * 2;

    if (typeof spec.centerPadding === "string" && spec.centerPadding.slice(-1) === "%") {
      centerPaddingAdj *= listWidth / 100;
    }

    slideWidth = Math.ceil((listWidth - centerPaddingAdj) / spec.slidesToShow);
  } else {
    slideWidth = listWidth;
  }

  var slideHeight = listNode && getHeight(listNode.querySelector('[data-index="0"]'));
  var listHeight = slideHeight * spec.slidesToShow;
  var currentSlide = spec.currentSlide === undefined ? spec.initialSlide : spec.currentSlide;

  if (spec.rtl && spec.currentSlide === undefined) {
    currentSlide = slideCount - 1 - spec.initialSlide;
  }

  var lazyLoadedList = spec.lazyLoadedList || [];
  var slidesToLoad = getOnDemandLazySlides(_objectSpread$4(_objectSpread$4({}, spec), {}, {
    currentSlide: currentSlide,
    lazyLoadedList: lazyLoadedList
  }));
  lazyLoadedList = lazyLoadedList.concat(slidesToLoad);
  var state = {
    slideCount: slideCount,
    slideWidth: slideWidth,
    listWidth: listWidth,
    trackWidth: trackWidth,
    currentSlide: currentSlide,
    slideHeight: slideHeight,
    listHeight: listHeight,
    lazyLoadedList: lazyLoadedList
  };

  if (spec.autoplaying === null && spec.autoplay) {
    state["autoplaying"] = "playing";
  }

  return state;
};

innerSliderUtils.initializedState = initializedState;

var slideHandler = function slideHandler(spec) {
  var waitForAnimate = spec.waitForAnimate,
      animating = spec.animating,
      fade = spec.fade,
      infinite = spec.infinite,
      index = spec.index,
      slideCount = spec.slideCount,
      lazyLoad = spec.lazyLoad,
      currentSlide = spec.currentSlide,
      centerMode = spec.centerMode,
      slidesToScroll = spec.slidesToScroll,
      slidesToShow = spec.slidesToShow,
      useCSS = spec.useCSS;
  var lazyLoadedList = spec.lazyLoadedList;
  if (waitForAnimate && animating) return {};
  var animationSlide = index,
      finalSlide,
      animationLeft,
      finalLeft;
  var state = {},
      nextState = {};
  var targetSlide = infinite ? index : clamp(index, 0, slideCount - 1);

  if (fade) {
    if (!infinite && (index < 0 || index >= slideCount)) return {};

    if (index < 0) {
      animationSlide = index + slideCount;
    } else if (index >= slideCount) {
      animationSlide = index - slideCount;
    }

    if (lazyLoad && lazyLoadedList.indexOf(animationSlide) < 0) {
      lazyLoadedList = lazyLoadedList.concat(animationSlide);
    }

    state = {
      animating: true,
      currentSlide: animationSlide,
      lazyLoadedList: lazyLoadedList,
      targetSlide: animationSlide
    };
    nextState = {
      animating: false,
      targetSlide: animationSlide
    };
  } else {
    finalSlide = animationSlide;

    if (animationSlide < 0) {
      finalSlide = animationSlide + slideCount;
      if (!infinite) finalSlide = 0;else if (slideCount % slidesToScroll !== 0) finalSlide = slideCount - slideCount % slidesToScroll;
    } else if (!canGoNext(spec) && animationSlide > currentSlide) {
      animationSlide = finalSlide = currentSlide;
    } else if (centerMode && animationSlide >= slideCount) {
      animationSlide = infinite ? slideCount : slideCount - 1;
      finalSlide = infinite ? 0 : slideCount - 1;
    } else if (animationSlide >= slideCount) {
      finalSlide = animationSlide - slideCount;
      if (!infinite) finalSlide = slideCount - slidesToShow;else if (slideCount % slidesToScroll !== 0) finalSlide = 0;
    }

    if (!infinite && animationSlide + slidesToShow >= slideCount) {
      finalSlide = slideCount - slidesToShow;
    }

    animationLeft = getTrackLeft(_objectSpread$4(_objectSpread$4({}, spec), {}, {
      slideIndex: animationSlide
    }));
    finalLeft = getTrackLeft(_objectSpread$4(_objectSpread$4({}, spec), {}, {
      slideIndex: finalSlide
    }));

    if (!infinite) {
      if (animationLeft === finalLeft) animationSlide = finalSlide;
      animationLeft = finalLeft;
    }

    if (lazyLoad) {
      lazyLoadedList = lazyLoadedList.concat(getOnDemandLazySlides(_objectSpread$4(_objectSpread$4({}, spec), {}, {
        currentSlide: animationSlide
      })));
    }

    if (!useCSS) {
      state = {
        currentSlide: finalSlide,
        trackStyle: getTrackCSS(_objectSpread$4(_objectSpread$4({}, spec), {}, {
          left: finalLeft
        })),
        lazyLoadedList: lazyLoadedList,
        targetSlide: targetSlide
      };
    } else {
      state = {
        animating: true,
        currentSlide: finalSlide,
        trackStyle: getTrackAnimateCSS(_objectSpread$4(_objectSpread$4({}, spec), {}, {
          left: animationLeft
        })),
        lazyLoadedList: lazyLoadedList,
        targetSlide: targetSlide
      };
      nextState = {
        animating: false,
        currentSlide: finalSlide,
        trackStyle: getTrackCSS(_objectSpread$4(_objectSpread$4({}, spec), {}, {
          left: finalLeft
        })),
        swipeLeft: null,
        targetSlide: targetSlide
      };
    }
  }

  return {
    state: state,
    nextState: nextState
  };
};

innerSliderUtils.slideHandler = slideHandler;

var changeSlide = function changeSlide(spec, options) {
  var indexOffset, previousInt, slideOffset, unevenOffset, targetSlide;
  var slidesToScroll = spec.slidesToScroll,
      slidesToShow = spec.slidesToShow,
      slideCount = spec.slideCount,
      currentSlide = spec.currentSlide,
      previousTargetSlide = spec.targetSlide,
      lazyLoad = spec.lazyLoad,
      infinite = spec.infinite;
  unevenOffset = slideCount % slidesToScroll !== 0;
  indexOffset = unevenOffset ? 0 : (slideCount - currentSlide) % slidesToScroll;

  if (options.message === "previous") {
    slideOffset = indexOffset === 0 ? slidesToScroll : slidesToShow - indexOffset;
    targetSlide = currentSlide - slideOffset;

    if (lazyLoad && !infinite) {
      previousInt = currentSlide - slideOffset;
      targetSlide = previousInt === -1 ? slideCount - 1 : previousInt;
    }

    if (!infinite) {
      targetSlide = previousTargetSlide - slidesToScroll;
    }
  } else if (options.message === "next") {
    slideOffset = indexOffset === 0 ? slidesToScroll : indexOffset;
    targetSlide = currentSlide + slideOffset;

    if (lazyLoad && !infinite) {
      targetSlide = (currentSlide + slidesToScroll) % slideCount + indexOffset;
    }

    if (!infinite) {
      targetSlide = previousTargetSlide + slidesToScroll;
    }
  } else if (options.message === "dots") {
    // Click on dots
    targetSlide = options.index * options.slidesToScroll;
  } else if (options.message === "children") {
    // Click on the slides
    targetSlide = options.index;

    if (infinite) {
      var direction = siblingDirection(_objectSpread$4(_objectSpread$4({}, spec), {}, {
        targetSlide: targetSlide
      }));

      if (targetSlide > options.currentSlide && direction === "left") {
        targetSlide = targetSlide - slideCount;
      } else if (targetSlide < options.currentSlide && direction === "right") {
        targetSlide = targetSlide + slideCount;
      }
    }
  } else if (options.message === "index") {
    targetSlide = Number(options.index);
  }

  return targetSlide;
};

innerSliderUtils.changeSlide = changeSlide;

var keyHandler = function keyHandler(e, accessibility, rtl) {
  if (e.target.tagName.match("TEXTAREA|INPUT|SELECT") || !accessibility) return "";
  if (e.keyCode === 37) return rtl ? "next" : "previous";
  if (e.keyCode === 39) return rtl ? "previous" : "next";
  return "";
};

innerSliderUtils.keyHandler = keyHandler;

var swipeStart = function swipeStart(e, swipe, draggable) {
  e.target.tagName === "IMG" && safePreventDefault(e);
  if (!swipe || !draggable && e.type.indexOf("mouse") !== -1) return "";
  return {
    dragging: true,
    touchObject: {
      startX: e.touches ? e.touches[0].pageX : e.clientX,
      startY: e.touches ? e.touches[0].pageY : e.clientY,
      curX: e.touches ? e.touches[0].pageX : e.clientX,
      curY: e.touches ? e.touches[0].pageY : e.clientY
    }
  };
};

innerSliderUtils.swipeStart = swipeStart;

var swipeMove = function swipeMove(e, spec) {
  // spec also contains, trackRef and slideIndex
  var scrolling = spec.scrolling,
      animating = spec.animating,
      vertical = spec.vertical,
      swipeToSlide = spec.swipeToSlide,
      verticalSwiping = spec.verticalSwiping,
      rtl = spec.rtl,
      currentSlide = spec.currentSlide,
      edgeFriction = spec.edgeFriction,
      edgeDragged = spec.edgeDragged,
      onEdge = spec.onEdge,
      swiped = spec.swiped,
      swiping = spec.swiping,
      slideCount = spec.slideCount,
      slidesToScroll = spec.slidesToScroll,
      infinite = spec.infinite,
      touchObject = spec.touchObject,
      swipeEvent = spec.swipeEvent,
      listHeight = spec.listHeight,
      listWidth = spec.listWidth;
  if (scrolling) return;
  if (animating) return safePreventDefault(e);
  if (vertical && swipeToSlide && verticalSwiping) safePreventDefault(e);
  var swipeLeft,
      state = {};
  var curLeft = getTrackLeft(spec);
  touchObject.curX = e.touches ? e.touches[0].pageX : e.clientX;
  touchObject.curY = e.touches ? e.touches[0].pageY : e.clientY;
  touchObject.swipeLength = Math.round(Math.sqrt(Math.pow(touchObject.curX - touchObject.startX, 2)));
  var verticalSwipeLength = Math.round(Math.sqrt(Math.pow(touchObject.curY - touchObject.startY, 2)));

  if (!verticalSwiping && !swiping && verticalSwipeLength > 10) {
    return {
      scrolling: true
    };
  }

  if (verticalSwiping) touchObject.swipeLength = verticalSwipeLength;
  var positionOffset = (!rtl ? 1 : -1) * (touchObject.curX > touchObject.startX ? 1 : -1);
  if (verticalSwiping) positionOffset = touchObject.curY > touchObject.startY ? 1 : -1;
  var dotCount = Math.ceil(slideCount / slidesToScroll);
  var swipeDirection = getSwipeDirection(spec.touchObject, verticalSwiping);
  var touchSwipeLength = touchObject.swipeLength;

  if (!infinite) {
    if (currentSlide === 0 && (swipeDirection === "right" || swipeDirection === "down") || currentSlide + 1 >= dotCount && (swipeDirection === "left" || swipeDirection === "up") || !canGoNext(spec) && (swipeDirection === "left" || swipeDirection === "up")) {
      touchSwipeLength = touchObject.swipeLength * edgeFriction;

      if (edgeDragged === false && onEdge) {
        onEdge(swipeDirection);
        state["edgeDragged"] = true;
      }
    }
  }

  if (!swiped && swipeEvent) {
    swipeEvent(swipeDirection);
    state["swiped"] = true;
  }

  if (!vertical) {
    if (!rtl) {
      swipeLeft = curLeft + touchSwipeLength * positionOffset;
    } else {
      swipeLeft = curLeft - touchSwipeLength * positionOffset;
    }
  } else {
    swipeLeft = curLeft + touchSwipeLength * (listHeight / listWidth) * positionOffset;
  }

  if (verticalSwiping) {
    swipeLeft = curLeft + touchSwipeLength * positionOffset;
  }

  state = _objectSpread$4(_objectSpread$4({}, state), {}, {
    touchObject: touchObject,
    swipeLeft: swipeLeft,
    trackStyle: getTrackCSS(_objectSpread$4(_objectSpread$4({}, spec), {}, {
      left: swipeLeft
    }))
  });

  if (Math.abs(touchObject.curX - touchObject.startX) < Math.abs(touchObject.curY - touchObject.startY) * 0.8) {
    return state;
  }

  if (touchObject.swipeLength > 10) {
    state["swiping"] = true;
    safePreventDefault(e);
  }

  return state;
};

innerSliderUtils.swipeMove = swipeMove;

var swipeEnd = function swipeEnd(e, spec) {
  var dragging = spec.dragging,
      swipe = spec.swipe,
      touchObject = spec.touchObject,
      listWidth = spec.listWidth,
      touchThreshold = spec.touchThreshold,
      verticalSwiping = spec.verticalSwiping,
      listHeight = spec.listHeight,
      swipeToSlide = spec.swipeToSlide,
      scrolling = spec.scrolling,
      onSwipe = spec.onSwipe,
      targetSlide = spec.targetSlide,
      currentSlide = spec.currentSlide,
      infinite = spec.infinite;

  if (!dragging) {
    if (swipe) safePreventDefault(e);
    return {};
  }

  var minSwipe = verticalSwiping ? listHeight / touchThreshold : listWidth / touchThreshold;
  var swipeDirection = getSwipeDirection(touchObject, verticalSwiping); // reset the state of touch related state variables.

  var state = {
    dragging: false,
    edgeDragged: false,
    scrolling: false,
    swiping: false,
    swiped: false,
    swipeLeft: null,
    touchObject: {}
  };

  if (scrolling) {
    return state;
  }

  if (!touchObject.swipeLength) {
    return state;
  }

  if (touchObject.swipeLength > minSwipe) {
    safePreventDefault(e);

    if (onSwipe) {
      onSwipe(swipeDirection);
    }

    var slideCount, newSlide;
    var activeSlide = infinite ? currentSlide : targetSlide;

    switch (swipeDirection) {
      case "left":
      case "up":
        newSlide = activeSlide + getSlideCount(spec);
        slideCount = swipeToSlide ? checkNavigable(spec, newSlide) : newSlide;
        state["currentDirection"] = 0;
        break;

      case "right":
      case "down":
        newSlide = activeSlide - getSlideCount(spec);
        slideCount = swipeToSlide ? checkNavigable(spec, newSlide) : newSlide;
        state["currentDirection"] = 1;
        break;

      default:
        slideCount = activeSlide;
    }

    state["triggerSlideHandler"] = slideCount;
  } else {
    // Adjust the track back to it's original position.
    var currentLeft = getTrackLeft(spec);
    state["trackStyle"] = getTrackAnimateCSS(_objectSpread$4(_objectSpread$4({}, spec), {}, {
      left: currentLeft
    }));
  }

  return state;
};

innerSliderUtils.swipeEnd = swipeEnd;

var getNavigableIndexes = function getNavigableIndexes(spec) {
  var max = spec.infinite ? spec.slideCount * 2 : spec.slideCount;
  var breakpoint = spec.infinite ? spec.slidesToShow * -1 : 0;
  var counter = spec.infinite ? spec.slidesToShow * -1 : 0;
  var indexes = [];

  while (breakpoint < max) {
    indexes.push(breakpoint);
    breakpoint = counter + spec.slidesToScroll;
    counter += Math.min(spec.slidesToScroll, spec.slidesToShow);
  }

  return indexes;
};

innerSliderUtils.getNavigableIndexes = getNavigableIndexes;

var checkNavigable = function checkNavigable(spec, index) {
  var navigables = getNavigableIndexes(spec);
  var prevNavigable = 0;

  if (index > navigables[navigables.length - 1]) {
    index = navigables[navigables.length - 1];
  } else {
    for (var n in navigables) {
      if (index < navigables[n]) {
        index = prevNavigable;
        break;
      }

      prevNavigable = navigables[n];
    }
  }

  return index;
};

innerSliderUtils.checkNavigable = checkNavigable;

var getSlideCount = function getSlideCount(spec) {
  var centerOffset = spec.centerMode ? spec.slideWidth * Math.floor(spec.slidesToShow / 2) : 0;

  if (spec.swipeToSlide) {
    var swipedSlide;
    var slickList = spec.listRef;
    var slides = slickList.querySelectorAll && slickList.querySelectorAll(".slick-slide") || [];
    Array.from(slides).every(function (slide) {
      if (!spec.vertical) {
        if (slide.offsetLeft - centerOffset + getWidth(slide) / 2 > spec.swipeLeft * -1) {
          swipedSlide = slide;
          return false;
        }
      } else {
        if (slide.offsetTop + getHeight(slide) / 2 > spec.swipeLeft * -1) {
          swipedSlide = slide;
          return false;
        }
      }

      return true;
    });

    if (!swipedSlide) {
      return 0;
    }

    var currentIndex = spec.rtl === true ? spec.slideCount - spec.currentSlide : spec.currentSlide;
    var slidesTraversed = Math.abs(swipedSlide.dataset.index - currentIndex) || 1;
    return slidesTraversed;
  } else {
    return spec.slidesToScroll;
  }
};

innerSliderUtils.getSlideCount = getSlideCount;

var checkSpecKeys = function checkSpecKeys(spec, keysArray) {
  return keysArray.reduce(function (value, key) {
    return value && spec.hasOwnProperty(key);
  }, true) ? null : console.error("Keys Missing:", spec);
};

innerSliderUtils.checkSpecKeys = checkSpecKeys;

var getTrackCSS = function getTrackCSS(spec) {
  checkSpecKeys(spec, ["left", "variableWidth", "slideCount", "slidesToShow", "slideWidth"]);
  var trackWidth, trackHeight;
  var trackChildren = spec.slideCount + 2 * spec.slidesToShow;

  if (!spec.vertical) {
    trackWidth = getTotalSlides(spec) * spec.slideWidth;
  } else {
    trackHeight = trackChildren * spec.slideHeight;
  }

  var style = {
    opacity: 1,
    transition: "",
    WebkitTransition: ""
  };

  if (spec.useTransform) {
    var WebkitTransform = !spec.vertical ? "translate3d(" + spec.left + "px, 0px, 0px)" : "translate3d(0px, " + spec.left + "px, 0px)";
    var transform = !spec.vertical ? "translate3d(" + spec.left + "px, 0px, 0px)" : "translate3d(0px, " + spec.left + "px, 0px)";
    var msTransform = !spec.vertical ? "translateX(" + spec.left + "px)" : "translateY(" + spec.left + "px)";
    style = _objectSpread$4(_objectSpread$4({}, style), {}, {
      WebkitTransform: WebkitTransform,
      transform: transform,
      msTransform: msTransform
    });
  } else {
    if (spec.vertical) {
      style["top"] = spec.left;
    } else {
      style["left"] = spec.left;
    }
  }

  if (spec.fade) style = {
    opacity: 1
  };
  if (trackWidth) style.width = trackWidth;
  if (trackHeight) style.height = trackHeight; // Fallback for IE8

  if (window && !window.addEventListener && window.attachEvent) {
    if (!spec.vertical) {
      style.marginLeft = spec.left + "px";
    } else {
      style.marginTop = spec.left + "px";
    }
  }

  return style;
};

innerSliderUtils.getTrackCSS = getTrackCSS;

var getTrackAnimateCSS = function getTrackAnimateCSS(spec) {
  checkSpecKeys(spec, ["left", "variableWidth", "slideCount", "slidesToShow", "slideWidth", "speed", "cssEase"]);
  var style = getTrackCSS(spec); // useCSS is true by default so it can be undefined

  if (spec.useTransform) {
    style.WebkitTransition = "-webkit-transform " + spec.speed + "ms " + spec.cssEase;
    style.transition = "transform " + spec.speed + "ms " + spec.cssEase;
  } else {
    if (spec.vertical) {
      style.transition = "top " + spec.speed + "ms " + spec.cssEase;
    } else {
      style.transition = "left " + spec.speed + "ms " + spec.cssEase;
    }
  }

  return style;
};

innerSliderUtils.getTrackAnimateCSS = getTrackAnimateCSS;

var getTrackLeft = function getTrackLeft(spec) {
  if (spec.unslick) {
    return 0;
  }

  checkSpecKeys(spec, ["slideIndex", "trackRef", "infinite", "centerMode", "slideCount", "slidesToShow", "slidesToScroll", "slideWidth", "listWidth", "variableWidth", "slideHeight"]);
  var slideIndex = spec.slideIndex,
      trackRef = spec.trackRef,
      infinite = spec.infinite,
      centerMode = spec.centerMode,
      slideCount = spec.slideCount,
      slidesToShow = spec.slidesToShow,
      slidesToScroll = spec.slidesToScroll,
      slideWidth = spec.slideWidth,
      listWidth = spec.listWidth,
      variableWidth = spec.variableWidth,
      slideHeight = spec.slideHeight,
      fade = spec.fade,
      vertical = spec.vertical;
  var slideOffset = 0;
  var targetLeft;
  var targetSlide;
  var verticalOffset = 0;

  if (fade || spec.slideCount === 1) {
    return 0;
  }

  var slidesToOffset = 0;

  if (infinite) {
    slidesToOffset = -getPreClones(spec); // bring active slide to the beginning of visual area
    // if next scroll doesn't have enough children, just reach till the end of original slides instead of shifting slidesToScroll children

    if (slideCount % slidesToScroll !== 0 && slideIndex + slidesToScroll > slideCount) {
      slidesToOffset = -(slideIndex > slideCount ? slidesToShow - (slideIndex - slideCount) : slideCount % slidesToScroll);
    } // shift current slide to center of the frame


    if (centerMode) {
      slidesToOffset += parseInt(slidesToShow / 2);
    }
  } else {
    if (slideCount % slidesToScroll !== 0 && slideIndex + slidesToScroll > slideCount) {
      slidesToOffset = slidesToShow - slideCount % slidesToScroll;
    }

    if (centerMode) {
      slidesToOffset = parseInt(slidesToShow / 2);
    }
  }

  slideOffset = slidesToOffset * slideWidth;
  verticalOffset = slidesToOffset * slideHeight;

  if (!vertical) {
    targetLeft = slideIndex * slideWidth * -1 + slideOffset;
  } else {
    targetLeft = slideIndex * slideHeight * -1 + verticalOffset;
  }

  if (variableWidth === true) {
    var targetSlideIndex;
    var trackElem = trackRef && trackRef.node;
    targetSlideIndex = slideIndex + getPreClones(spec);
    targetSlide = trackElem && trackElem.childNodes[targetSlideIndex];
    targetLeft = targetSlide ? targetSlide.offsetLeft * -1 : 0;

    if (centerMode === true) {
      targetSlideIndex = infinite ? slideIndex + getPreClones(spec) : slideIndex;
      targetSlide = trackElem && trackElem.children[targetSlideIndex];
      targetLeft = 0;

      for (var slide = 0; slide < targetSlideIndex; slide++) {
        targetLeft -= trackElem && trackElem.children[slide] && trackElem.children[slide].offsetWidth;
      }

      targetLeft -= parseInt(spec.centerPadding);
      targetLeft += targetSlide && (listWidth - targetSlide.offsetWidth) / 2;
    }
  }

  return targetLeft;
};

innerSliderUtils.getTrackLeft = getTrackLeft;

var getPreClones = function getPreClones(spec) {
  if (spec.unslick || !spec.infinite) {
    return 0;
  }

  if (spec.variableWidth) {
    return spec.slideCount;
  }

  return spec.slidesToShow + (spec.centerMode ? 1 : 0);
};

innerSliderUtils.getPreClones = getPreClones;

var getPostClones = function getPostClones(spec) {
  if (spec.unslick || !spec.infinite) {
    return 0;
  }

  return spec.slideCount;
};

innerSliderUtils.getPostClones = getPostClones;

var getTotalSlides = function getTotalSlides(spec) {
  return spec.slideCount === 1 ? 1 : getPreClones(spec) + spec.slideCount + getPostClones(spec);
};

innerSliderUtils.getTotalSlides = getTotalSlides;

var siblingDirection = function siblingDirection(spec) {
  if (spec.targetSlide > spec.currentSlide) {
    if (spec.targetSlide > spec.currentSlide + slidesOnRight(spec)) {
      return "left";
    }

    return "right";
  } else {
    if (spec.targetSlide < spec.currentSlide - slidesOnLeft(spec)) {
      return "right";
    }

    return "left";
  }
};

innerSliderUtils.siblingDirection = siblingDirection;

var slidesOnRight = function slidesOnRight(_ref) {
  var slidesToShow = _ref.slidesToShow,
      centerMode = _ref.centerMode,
      rtl = _ref.rtl,
      centerPadding = _ref.centerPadding;

  // returns no of slides on the right of active slide
  if (centerMode) {
    var right = (slidesToShow - 1) / 2 + 1;
    if (parseInt(centerPadding) > 0) right += 1;
    if (rtl && slidesToShow % 2 === 0) right += 1;
    return right;
  }

  if (rtl) {
    return 0;
  }

  return slidesToShow - 1;
};

innerSliderUtils.slidesOnRight = slidesOnRight;

var slidesOnLeft = function slidesOnLeft(_ref2) {
  var slidesToShow = _ref2.slidesToShow,
      centerMode = _ref2.centerMode,
      rtl = _ref2.rtl,
      centerPadding = _ref2.centerPadding;

  // returns no of slides on the left of active slide
  if (centerMode) {
    var left = (slidesToShow - 1) / 2 + 1;
    if (parseInt(centerPadding) > 0) left += 1;
    if (!rtl && slidesToShow % 2 === 0) left += 1;
    return left;
  }

  if (rtl) {
    return slidesToShow - 1;
  }

  return 0;
};

innerSliderUtils.slidesOnLeft = slidesOnLeft;

var canUseDOM = function canUseDOM() {
  return !!(typeof window !== "undefined" && window.document && window.document.createElement);
};

innerSliderUtils.canUseDOM = canUseDOM;

var track = {};

function _typeof$3(obj) { "@babel/helpers - typeof"; return _typeof$3 = "function" == typeof Symbol && "symbol" == typeof Symbol.iterator ? function (obj) { return typeof obj; } : function (obj) { return obj && "function" == typeof Symbol && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj; }, _typeof$3(obj); }

Object.defineProperty(track, "__esModule", {
  value: true
});
track.Track = void 0;

var _react$3 = _interopRequireDefault$3(React);

var _classnames$3 = _interopRequireDefault$3(classnames.exports);

var _innerSliderUtils$3 = innerSliderUtils;

function _interopRequireDefault$3(obj) { return obj && obj.__esModule ? obj : { "default": obj }; }

function _extends$2() { _extends$2 = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$2.apply(this, arguments); }

function _classCallCheck$3(instance, Constructor) { if (!(instance instanceof Constructor)) { throw new TypeError("Cannot call a class as a function"); } }

function _defineProperties$3(target, props) { for (var i = 0; i < props.length; i++) { var descriptor = props[i]; descriptor.enumerable = descriptor.enumerable || false; descriptor.configurable = true; if ("value" in descriptor) descriptor.writable = true; Object.defineProperty(target, descriptor.key, descriptor); } }

function _createClass$3(Constructor, protoProps, staticProps) { if (protoProps) _defineProperties$3(Constructor.prototype, protoProps); if (staticProps) _defineProperties$3(Constructor, staticProps); Object.defineProperty(Constructor, "prototype", { writable: false }); return Constructor; }

function _inherits$3(subClass, superClass) { if (typeof superClass !== "function" && superClass !== null) { throw new TypeError("Super expression must either be null or a function"); } subClass.prototype = Object.create(superClass && superClass.prototype, { constructor: { value: subClass, writable: true, configurable: true } }); Object.defineProperty(subClass, "prototype", { writable: false }); if (superClass) _setPrototypeOf$3(subClass, superClass); }

function _setPrototypeOf$3(o, p) { _setPrototypeOf$3 = Object.setPrototypeOf || function _setPrototypeOf(o, p) { o.__proto__ = p; return o; }; return _setPrototypeOf$3(o, p); }

function _createSuper$3(Derived) { var hasNativeReflectConstruct = _isNativeReflectConstruct$3(); return function _createSuperInternal() { var Super = _getPrototypeOf$3(Derived), result; if (hasNativeReflectConstruct) { var NewTarget = _getPrototypeOf$3(this).constructor; result = Reflect.construct(Super, arguments, NewTarget); } else { result = Super.apply(this, arguments); } return _possibleConstructorReturn$3(this, result); }; }

function _possibleConstructorReturn$3(self, call) { if (call && (_typeof$3(call) === "object" || typeof call === "function")) { return call; } else if (call !== void 0) { throw new TypeError("Derived constructors may only return object or undefined"); } return _assertThisInitialized$3(self); }

function _assertThisInitialized$3(self) { if (self === void 0) { throw new ReferenceError("this hasn't been initialised - super() hasn't been called"); } return self; }

function _isNativeReflectConstruct$3() { if (typeof Reflect === "undefined" || !Reflect.construct) return false; if (Reflect.construct.sham) return false; if (typeof Proxy === "function") return true; try { Boolean.prototype.valueOf.call(Reflect.construct(Boolean, [], function () {})); return true; } catch (e) { return false; } }

function _getPrototypeOf$3(o) { _getPrototypeOf$3 = Object.setPrototypeOf ? Object.getPrototypeOf : function _getPrototypeOf(o) { return o.__proto__ || Object.getPrototypeOf(o); }; return _getPrototypeOf$3(o); }

function ownKeys$3(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread$3(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys$3(Object(source), !0).forEach(function (key) { _defineProperty$3(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys$3(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

function _defineProperty$3(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

// given specifications/props for a slide, fetch all the classes that need to be applied to the slide
var getSlideClasses = function getSlideClasses(spec) {
  var slickActive, slickCenter, slickCloned;
  var centerOffset, index;

  if (spec.rtl) {
    index = spec.slideCount - 1 - spec.index;
  } else {
    index = spec.index;
  }

  slickCloned = index < 0 || index >= spec.slideCount;

  if (spec.centerMode) {
    centerOffset = Math.floor(spec.slidesToShow / 2);
    slickCenter = (index - spec.currentSlide) % spec.slideCount === 0;

    if (index > spec.currentSlide - centerOffset - 1 && index <= spec.currentSlide + centerOffset) {
      slickActive = true;
    }
  } else {
    slickActive = spec.currentSlide <= index && index < spec.currentSlide + spec.slidesToShow;
  }

  var focusedSlide;

  if (spec.targetSlide < 0) {
    focusedSlide = spec.targetSlide + spec.slideCount;
  } else if (spec.targetSlide >= spec.slideCount) {
    focusedSlide = spec.targetSlide - spec.slideCount;
  } else {
    focusedSlide = spec.targetSlide;
  }

  var slickCurrent = index === focusedSlide;
  return {
    "slick-slide": true,
    "slick-active": slickActive,
    "slick-center": slickCenter,
    "slick-cloned": slickCloned,
    "slick-current": slickCurrent // dubious in case of RTL

  };
};

var getSlideStyle = function getSlideStyle(spec) {
  var style = {};

  if (spec.variableWidth === undefined || spec.variableWidth === false) {
    style.width = spec.slideWidth;
  }

  if (spec.fade) {
    style.position = "relative";

    if (spec.vertical) {
      style.top = -spec.index * parseInt(spec.slideHeight);
    } else {
      style.left = -spec.index * parseInt(spec.slideWidth);
    }

    style.opacity = spec.currentSlide === spec.index ? 1 : 0;

    if (spec.useCSS) {
      style.transition = "opacity " + spec.speed + "ms " + spec.cssEase + ", " + "visibility " + spec.speed + "ms " + spec.cssEase;
    }
  }

  return style;
};

var getKey = function getKey(child, fallbackKey) {
  return child.key || fallbackKey;
};

var renderSlides = function renderSlides(spec) {
  var key;
  var slides = [];
  var preCloneSlides = [];
  var postCloneSlides = [];

  var childrenCount = _react$3["default"].Children.count(spec.children);

  var startIndex = (0, _innerSliderUtils$3.lazyStartIndex)(spec);
  var endIndex = (0, _innerSliderUtils$3.lazyEndIndex)(spec);

  _react$3["default"].Children.forEach(spec.children, function (elem, index) {
    var child;
    var childOnClickOptions = {
      message: "children",
      index: index,
      slidesToScroll: spec.slidesToScroll,
      currentSlide: spec.currentSlide
    }; // in case of lazyLoad, whether or not we want to fetch the slide

    if (!spec.lazyLoad || spec.lazyLoad && spec.lazyLoadedList.indexOf(index) >= 0) {
      child = elem;
    } else {
      child = /*#__PURE__*/_react$3["default"].createElement("div", null);
    }

    var childStyle = getSlideStyle(_objectSpread$3(_objectSpread$3({}, spec), {}, {
      index: index
    }));
    var slideClass = child.props.className || "";
    var slideClasses = getSlideClasses(_objectSpread$3(_objectSpread$3({}, spec), {}, {
      index: index
    })); // push a cloned element of the desired slide

    slides.push( /*#__PURE__*/_react$3["default"].cloneElement(child, {
      key: "original" + getKey(child, index),
      "data-index": index,
      className: (0, _classnames$3["default"])(slideClasses, slideClass),
      tabIndex: "-1",
      "aria-hidden": !slideClasses["slick-active"],
      style: _objectSpread$3(_objectSpread$3({
        outline: "none"
      }, child.props.style || {}), childStyle),
      onClick: function onClick(e) {
        child.props && child.props.onClick && child.props.onClick(e);

        if (spec.focusOnSelect) {
          spec.focusOnSelect(childOnClickOptions);
        }
      }
    })); // if slide needs to be precloned or postcloned

    if (spec.infinite && spec.fade === false) {
      var preCloneNo = childrenCount - index;

      if (preCloneNo <= (0, _innerSliderUtils$3.getPreClones)(spec) && childrenCount !== spec.slidesToShow) {
        key = -preCloneNo;

        if (key >= startIndex) {
          child = elem;
        }

        slideClasses = getSlideClasses(_objectSpread$3(_objectSpread$3({}, spec), {}, {
          index: key
        }));
        preCloneSlides.push( /*#__PURE__*/_react$3["default"].cloneElement(child, {
          key: "precloned" + getKey(child, key),
          "data-index": key,
          tabIndex: "-1",
          className: (0, _classnames$3["default"])(slideClasses, slideClass),
          "aria-hidden": !slideClasses["slick-active"],
          style: _objectSpread$3(_objectSpread$3({}, child.props.style || {}), childStyle),
          onClick: function onClick(e) {
            child.props && child.props.onClick && child.props.onClick(e);

            if (spec.focusOnSelect) {
              spec.focusOnSelect(childOnClickOptions);
            }
          }
        }));
      }

      if (childrenCount !== spec.slidesToShow) {
        key = childrenCount + index;

        if (key < endIndex) {
          child = elem;
        }

        slideClasses = getSlideClasses(_objectSpread$3(_objectSpread$3({}, spec), {}, {
          index: key
        }));
        postCloneSlides.push( /*#__PURE__*/_react$3["default"].cloneElement(child, {
          key: "postcloned" + getKey(child, key),
          "data-index": key,
          tabIndex: "-1",
          className: (0, _classnames$3["default"])(slideClasses, slideClass),
          "aria-hidden": !slideClasses["slick-active"],
          style: _objectSpread$3(_objectSpread$3({}, child.props.style || {}), childStyle),
          onClick: function onClick(e) {
            child.props && child.props.onClick && child.props.onClick(e);

            if (spec.focusOnSelect) {
              spec.focusOnSelect(childOnClickOptions);
            }
          }
        }));
      }
    }
  });

  if (spec.rtl) {
    return preCloneSlides.concat(slides, postCloneSlides).reverse();
  } else {
    return preCloneSlides.concat(slides, postCloneSlides);
  }
};

var Track = /*#__PURE__*/function (_React$PureComponent) {
  _inherits$3(Track, _React$PureComponent);

  var _super = _createSuper$3(Track);

  function Track() {
    var _this;

    _classCallCheck$3(this, Track);

    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    _this = _super.call.apply(_super, [this].concat(args));

    _defineProperty$3(_assertThisInitialized$3(_this), "node", null);

    _defineProperty$3(_assertThisInitialized$3(_this), "handleRef", function (ref) {
      _this.node = ref;
    });

    return _this;
  }

  _createClass$3(Track, [{
    key: "render",
    value: function render() {
      var slides = renderSlides(this.props);
      var _this$props = this.props,
          onMouseEnter = _this$props.onMouseEnter,
          onMouseOver = _this$props.onMouseOver,
          onMouseLeave = _this$props.onMouseLeave;
      var mouseEvents = {
        onMouseEnter: onMouseEnter,
        onMouseOver: onMouseOver,
        onMouseLeave: onMouseLeave
      };
      return /*#__PURE__*/_react$3["default"].createElement("div", _extends$2({
        ref: this.handleRef,
        className: "slick-track",
        style: this.props.trackStyle
      }, mouseEvents), slides);
    }
  }]);

  return Track;
}(_react$3["default"].PureComponent);

track.Track = Track;

var dots = {};

function _typeof$2(obj) { "@babel/helpers - typeof"; return _typeof$2 = "function" == typeof Symbol && "symbol" == typeof Symbol.iterator ? function (obj) { return typeof obj; } : function (obj) { return obj && "function" == typeof Symbol && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj; }, _typeof$2(obj); }

Object.defineProperty(dots, "__esModule", {
  value: true
});
dots.Dots = void 0;

var _react$2 = _interopRequireDefault$2(React);

var _classnames$2 = _interopRequireDefault$2(classnames.exports);

var _innerSliderUtils$2 = innerSliderUtils;

function _interopRequireDefault$2(obj) { return obj && obj.__esModule ? obj : { "default": obj }; }

function ownKeys$2(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread$2(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys$2(Object(source), !0).forEach(function (key) { _defineProperty$2(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys$2(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

function _defineProperty$2(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

function _classCallCheck$2(instance, Constructor) { if (!(instance instanceof Constructor)) { throw new TypeError("Cannot call a class as a function"); } }

function _defineProperties$2(target, props) { for (var i = 0; i < props.length; i++) { var descriptor = props[i]; descriptor.enumerable = descriptor.enumerable || false; descriptor.configurable = true; if ("value" in descriptor) descriptor.writable = true; Object.defineProperty(target, descriptor.key, descriptor); } }

function _createClass$2(Constructor, protoProps, staticProps) { if (protoProps) _defineProperties$2(Constructor.prototype, protoProps); if (staticProps) _defineProperties$2(Constructor, staticProps); Object.defineProperty(Constructor, "prototype", { writable: false }); return Constructor; }

function _inherits$2(subClass, superClass) { if (typeof superClass !== "function" && superClass !== null) { throw new TypeError("Super expression must either be null or a function"); } subClass.prototype = Object.create(superClass && superClass.prototype, { constructor: { value: subClass, writable: true, configurable: true } }); Object.defineProperty(subClass, "prototype", { writable: false }); if (superClass) _setPrototypeOf$2(subClass, superClass); }

function _setPrototypeOf$2(o, p) { _setPrototypeOf$2 = Object.setPrototypeOf || function _setPrototypeOf(o, p) { o.__proto__ = p; return o; }; return _setPrototypeOf$2(o, p); }

function _createSuper$2(Derived) { var hasNativeReflectConstruct = _isNativeReflectConstruct$2(); return function _createSuperInternal() { var Super = _getPrototypeOf$2(Derived), result; if (hasNativeReflectConstruct) { var NewTarget = _getPrototypeOf$2(this).constructor; result = Reflect.construct(Super, arguments, NewTarget); } else { result = Super.apply(this, arguments); } return _possibleConstructorReturn$2(this, result); }; }

function _possibleConstructorReturn$2(self, call) { if (call && (_typeof$2(call) === "object" || typeof call === "function")) { return call; } else if (call !== void 0) { throw new TypeError("Derived constructors may only return object or undefined"); } return _assertThisInitialized$2(self); }

function _assertThisInitialized$2(self) { if (self === void 0) { throw new ReferenceError("this hasn't been initialised - super() hasn't been called"); } return self; }

function _isNativeReflectConstruct$2() { if (typeof Reflect === "undefined" || !Reflect.construct) return false; if (Reflect.construct.sham) return false; if (typeof Proxy === "function") return true; try { Boolean.prototype.valueOf.call(Reflect.construct(Boolean, [], function () {})); return true; } catch (e) { return false; } }

function _getPrototypeOf$2(o) { _getPrototypeOf$2 = Object.setPrototypeOf ? Object.getPrototypeOf : function _getPrototypeOf(o) { return o.__proto__ || Object.getPrototypeOf(o); }; return _getPrototypeOf$2(o); }

var getDotCount = function getDotCount(spec) {
  var dots;

  if (spec.infinite) {
    dots = Math.ceil(spec.slideCount / spec.slidesToScroll);
  } else {
    dots = Math.ceil((spec.slideCount - spec.slidesToShow) / spec.slidesToScroll) + 1;
  }

  return dots;
};

var Dots = /*#__PURE__*/function (_React$PureComponent) {
  _inherits$2(Dots, _React$PureComponent);

  var _super = _createSuper$2(Dots);

  function Dots() {
    _classCallCheck$2(this, Dots);

    return _super.apply(this, arguments);
  }

  _createClass$2(Dots, [{
    key: "clickHandler",
    value: function clickHandler(options, e) {
      // In Autoplay the focus stays on clicked button even after transition
      // to next slide. That only goes away by click somewhere outside
      e.preventDefault();
      this.props.clickHandler(options);
    }
  }, {
    key: "render",
    value: function render() {
      var _this$props = this.props,
          onMouseEnter = _this$props.onMouseEnter,
          onMouseOver = _this$props.onMouseOver,
          onMouseLeave = _this$props.onMouseLeave,
          infinite = _this$props.infinite,
          slidesToScroll = _this$props.slidesToScroll,
          slidesToShow = _this$props.slidesToShow,
          slideCount = _this$props.slideCount,
          currentSlide = _this$props.currentSlide;
      var dotCount = getDotCount({
        slideCount: slideCount,
        slidesToScroll: slidesToScroll,
        slidesToShow: slidesToShow,
        infinite: infinite
      });
      var mouseEvents = {
        onMouseEnter: onMouseEnter,
        onMouseOver: onMouseOver,
        onMouseLeave: onMouseLeave
      };
      var dots = [];

      for (var i = 0; i < dotCount; i++) {
        var _rightBound = (i + 1) * slidesToScroll - 1;

        var rightBound = infinite ? _rightBound : (0, _innerSliderUtils$2.clamp)(_rightBound, 0, slideCount - 1);

        var _leftBound = rightBound - (slidesToScroll - 1);

        var leftBound = infinite ? _leftBound : (0, _innerSliderUtils$2.clamp)(_leftBound, 0, slideCount - 1);
        var className = (0, _classnames$2["default"])({
          "slick-active": infinite ? currentSlide >= leftBound && currentSlide <= rightBound : currentSlide === leftBound
        });
        var dotOptions = {
          message: "dots",
          index: i,
          slidesToScroll: slidesToScroll,
          currentSlide: currentSlide
        };
        var onClick = this.clickHandler.bind(this, dotOptions);
        dots = dots.concat( /*#__PURE__*/_react$2["default"].createElement("li", {
          key: i,
          className: className
        }, /*#__PURE__*/_react$2["default"].cloneElement(this.props.customPaging(i), {
          onClick: onClick
        })));
      }

      return /*#__PURE__*/_react$2["default"].cloneElement(this.props.appendDots(dots), _objectSpread$2({
        className: this.props.dotsClass
      }, mouseEvents));
    }
  }]);

  return Dots;
}(_react$2["default"].PureComponent);

dots.Dots = Dots;

var arrows = {};

function _typeof$1(obj) { "@babel/helpers - typeof"; return _typeof$1 = "function" == typeof Symbol && "symbol" == typeof Symbol.iterator ? function (obj) { return typeof obj; } : function (obj) { return obj && "function" == typeof Symbol && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj; }, _typeof$1(obj); }

Object.defineProperty(arrows, "__esModule", {
  value: true
});
arrows.PrevArrow = arrows.NextArrow = void 0;

var _react$1 = _interopRequireDefault$1(React);

var _classnames$1 = _interopRequireDefault$1(classnames.exports);

var _innerSliderUtils$1 = innerSliderUtils;

function _interopRequireDefault$1(obj) { return obj && obj.__esModule ? obj : { "default": obj }; }

function _extends$1() { _extends$1 = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$1.apply(this, arguments); }

function ownKeys$1(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread$1(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys$1(Object(source), !0).forEach(function (key) { _defineProperty$1(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys$1(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

function _defineProperty$1(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

function _classCallCheck$1(instance, Constructor) { if (!(instance instanceof Constructor)) { throw new TypeError("Cannot call a class as a function"); } }

function _defineProperties$1(target, props) { for (var i = 0; i < props.length; i++) { var descriptor = props[i]; descriptor.enumerable = descriptor.enumerable || false; descriptor.configurable = true; if ("value" in descriptor) descriptor.writable = true; Object.defineProperty(target, descriptor.key, descriptor); } }

function _createClass$1(Constructor, protoProps, staticProps) { if (protoProps) _defineProperties$1(Constructor.prototype, protoProps); if (staticProps) _defineProperties$1(Constructor, staticProps); Object.defineProperty(Constructor, "prototype", { writable: false }); return Constructor; }

function _inherits$1(subClass, superClass) { if (typeof superClass !== "function" && superClass !== null) { throw new TypeError("Super expression must either be null or a function"); } subClass.prototype = Object.create(superClass && superClass.prototype, { constructor: { value: subClass, writable: true, configurable: true } }); Object.defineProperty(subClass, "prototype", { writable: false }); if (superClass) _setPrototypeOf$1(subClass, superClass); }

function _setPrototypeOf$1(o, p) { _setPrototypeOf$1 = Object.setPrototypeOf || function _setPrototypeOf(o, p) { o.__proto__ = p; return o; }; return _setPrototypeOf$1(o, p); }

function _createSuper$1(Derived) { var hasNativeReflectConstruct = _isNativeReflectConstruct$1(); return function _createSuperInternal() { var Super = _getPrototypeOf$1(Derived), result; if (hasNativeReflectConstruct) { var NewTarget = _getPrototypeOf$1(this).constructor; result = Reflect.construct(Super, arguments, NewTarget); } else { result = Super.apply(this, arguments); } return _possibleConstructorReturn$1(this, result); }; }

function _possibleConstructorReturn$1(self, call) { if (call && (_typeof$1(call) === "object" || typeof call === "function")) { return call; } else if (call !== void 0) { throw new TypeError("Derived constructors may only return object or undefined"); } return _assertThisInitialized$1(self); }

function _assertThisInitialized$1(self) { if (self === void 0) { throw new ReferenceError("this hasn't been initialised - super() hasn't been called"); } return self; }

function _isNativeReflectConstruct$1() { if (typeof Reflect === "undefined" || !Reflect.construct) return false; if (Reflect.construct.sham) return false; if (typeof Proxy === "function") return true; try { Boolean.prototype.valueOf.call(Reflect.construct(Boolean, [], function () {})); return true; } catch (e) { return false; } }

function _getPrototypeOf$1(o) { _getPrototypeOf$1 = Object.setPrototypeOf ? Object.getPrototypeOf : function _getPrototypeOf(o) { return o.__proto__ || Object.getPrototypeOf(o); }; return _getPrototypeOf$1(o); }

var PrevArrow = /*#__PURE__*/function (_React$PureComponent) {
  _inherits$1(PrevArrow, _React$PureComponent);

  var _super = _createSuper$1(PrevArrow);

  function PrevArrow() {
    _classCallCheck$1(this, PrevArrow);

    return _super.apply(this, arguments);
  }

  _createClass$1(PrevArrow, [{
    key: "clickHandler",
    value: function clickHandler(options, e) {
      if (e) {
        e.preventDefault();
      }

      this.props.clickHandler(options, e);
    }
  }, {
    key: "render",
    value: function render() {
      var prevClasses = {
        "slick-arrow": true,
        "slick-prev": true
      };
      var prevHandler = this.clickHandler.bind(this, {
        message: "previous"
      });

      if (!this.props.infinite && (this.props.currentSlide === 0 || this.props.slideCount <= this.props.slidesToShow)) {
        prevClasses["slick-disabled"] = true;
        prevHandler = null;
      }

      var prevArrowProps = {
        key: "0",
        "data-role": "none",
        className: (0, _classnames$1["default"])(prevClasses),
        style: {
          display: "block"
        },
        onClick: prevHandler
      };
      var customProps = {
        currentSlide: this.props.currentSlide,
        slideCount: this.props.slideCount
      };
      var prevArrow;

      if (this.props.prevArrow) {
        prevArrow = /*#__PURE__*/_react$1["default"].cloneElement(this.props.prevArrow, _objectSpread$1(_objectSpread$1({}, prevArrowProps), customProps));
      } else {
        prevArrow = /*#__PURE__*/_react$1["default"].createElement("button", _extends$1({
          key: "0",
          type: "button"
        }, prevArrowProps), " ", "Previous");
      }

      return prevArrow;
    }
  }]);

  return PrevArrow;
}(_react$1["default"].PureComponent);

arrows.PrevArrow = PrevArrow;

var NextArrow = /*#__PURE__*/function (_React$PureComponent2) {
  _inherits$1(NextArrow, _React$PureComponent2);

  var _super2 = _createSuper$1(NextArrow);

  function NextArrow() {
    _classCallCheck$1(this, NextArrow);

    return _super2.apply(this, arguments);
  }

  _createClass$1(NextArrow, [{
    key: "clickHandler",
    value: function clickHandler(options, e) {
      if (e) {
        e.preventDefault();
      }

      this.props.clickHandler(options, e);
    }
  }, {
    key: "render",
    value: function render() {
      var nextClasses = {
        "slick-arrow": true,
        "slick-next": true
      };
      var nextHandler = this.clickHandler.bind(this, {
        message: "next"
      });

      if (!(0, _innerSliderUtils$1.canGoNext)(this.props)) {
        nextClasses["slick-disabled"] = true;
        nextHandler = null;
      }

      var nextArrowProps = {
        key: "1",
        "data-role": "none",
        className: (0, _classnames$1["default"])(nextClasses),
        style: {
          display: "block"
        },
        onClick: nextHandler
      };
      var customProps = {
        currentSlide: this.props.currentSlide,
        slideCount: this.props.slideCount
      };
      var nextArrow;

      if (this.props.nextArrow) {
        nextArrow = /*#__PURE__*/_react$1["default"].cloneElement(this.props.nextArrow, _objectSpread$1(_objectSpread$1({}, nextArrowProps), customProps));
      } else {
        nextArrow = /*#__PURE__*/_react$1["default"].createElement("button", _extends$1({
          key: "1",
          type: "button"
        }, nextArrowProps), " ", "Next");
      }

      return nextArrow;
    }
  }]);

  return NextArrow;
}(_react$1["default"].PureComponent);

arrows.NextArrow = NextArrow;

/**
 * A collection of shims that provide minimal functionality of the ES6 collections.
 *
 * These implementations are not meant to be used outside of the ResizeObserver
 * modules as they cover only a limited range of use cases.
 */
/* eslint-disable require-jsdoc, valid-jsdoc */
var MapShim = (function () {
    if (typeof Map !== 'undefined') {
        return Map;
    }
    /**
     * Returns index in provided array that matches the specified key.
     *
     * @param {Array<Array>} arr
     * @param {*} key
     * @returns {number}
     */
    function getIndex(arr, key) {
        var result = -1;
        arr.some(function (entry, index) {
            if (entry[0] === key) {
                result = index;
                return true;
            }
            return false;
        });
        return result;
    }
    return /** @class */ (function () {
        function class_1() {
            this.__entries__ = [];
        }
        Object.defineProperty(class_1.prototype, "size", {
            /**
             * @returns {boolean}
             */
            get: function () {
                return this.__entries__.length;
            },
            enumerable: true,
            configurable: true
        });
        /**
         * @param {*} key
         * @returns {*}
         */
        class_1.prototype.get = function (key) {
            var index = getIndex(this.__entries__, key);
            var entry = this.__entries__[index];
            return entry && entry[1];
        };
        /**
         * @param {*} key
         * @param {*} value
         * @returns {void}
         */
        class_1.prototype.set = function (key, value) {
            var index = getIndex(this.__entries__, key);
            if (~index) {
                this.__entries__[index][1] = value;
            }
            else {
                this.__entries__.push([key, value]);
            }
        };
        /**
         * @param {*} key
         * @returns {void}
         */
        class_1.prototype.delete = function (key) {
            var entries = this.__entries__;
            var index = getIndex(entries, key);
            if (~index) {
                entries.splice(index, 1);
            }
        };
        /**
         * @param {*} key
         * @returns {void}
         */
        class_1.prototype.has = function (key) {
            return !!~getIndex(this.__entries__, key);
        };
        /**
         * @returns {void}
         */
        class_1.prototype.clear = function () {
            this.__entries__.splice(0);
        };
        /**
         * @param {Function} callback
         * @param {*} [ctx=null]
         * @returns {void}
         */
        class_1.prototype.forEach = function (callback, ctx) {
            if (ctx === void 0) { ctx = null; }
            for (var _i = 0, _a = this.__entries__; _i < _a.length; _i++) {
                var entry = _a[_i];
                callback.call(ctx, entry[1], entry[0]);
            }
        };
        return class_1;
    }());
})();

/**
 * Detects whether window and document objects are available in current environment.
 */
var isBrowser = typeof window !== 'undefined' && typeof document !== 'undefined' && window.document === document;

// Returns global object of a current environment.
var global$1 = (function () {
    if (typeof global !== 'undefined' && global.Math === Math) {
        return global;
    }
    if (typeof self !== 'undefined' && self.Math === Math) {
        return self;
    }
    if (typeof window !== 'undefined' && window.Math === Math) {
        return window;
    }
    // eslint-disable-next-line no-new-func
    return Function('return this')();
})();

/**
 * A shim for the requestAnimationFrame which falls back to the setTimeout if
 * first one is not supported.
 *
 * @returns {number} Requests' identifier.
 */
var requestAnimationFrame$1 = (function () {
    if (typeof requestAnimationFrame === 'function') {
        // It's required to use a bounded function because IE sometimes throws
        // an "Invalid calling object" error if rAF is invoked without the global
        // object on the left hand side.
        return requestAnimationFrame.bind(global$1);
    }
    return function (callback) { return setTimeout(function () { return callback(Date.now()); }, 1000 / 60); };
})();

// Defines minimum timeout before adding a trailing call.
var trailingTimeout = 2;
/**
 * Creates a wrapper function which ensures that provided callback will be
 * invoked only once during the specified delay period.
 *
 * @param {Function} callback - Function to be invoked after the delay period.
 * @param {number} delay - Delay after which to invoke callback.
 * @returns {Function}
 */
function throttle (callback, delay) {
    var leadingCall = false, trailingCall = false, lastCallTime = 0;
    /**
     * Invokes the original callback function and schedules new invocation if
     * the "proxy" was called during current request.
     *
     * @returns {void}
     */
    function resolvePending() {
        if (leadingCall) {
            leadingCall = false;
            callback();
        }
        if (trailingCall) {
            proxy();
        }
    }
    /**
     * Callback invoked after the specified delay. It will further postpone
     * invocation of the original function delegating it to the
     * requestAnimationFrame.
     *
     * @returns {void}
     */
    function timeoutCallback() {
        requestAnimationFrame$1(resolvePending);
    }
    /**
     * Schedules invocation of the original function.
     *
     * @returns {void}
     */
    function proxy() {
        var timeStamp = Date.now();
        if (leadingCall) {
            // Reject immediately following calls.
            if (timeStamp - lastCallTime < trailingTimeout) {
                return;
            }
            // Schedule new call to be in invoked when the pending one is resolved.
            // This is important for "transitions" which never actually start
            // immediately so there is a chance that we might miss one if change
            // happens amids the pending invocation.
            trailingCall = true;
        }
        else {
            leadingCall = true;
            trailingCall = false;
            setTimeout(timeoutCallback, delay);
        }
        lastCallTime = timeStamp;
    }
    return proxy;
}

// Minimum delay before invoking the update of observers.
var REFRESH_DELAY = 20;
// A list of substrings of CSS properties used to find transition events that
// might affect dimensions of observed elements.
var transitionKeys = ['top', 'right', 'bottom', 'left', 'width', 'height', 'size', 'weight'];
// Check if MutationObserver is available.
var mutationObserverSupported = typeof MutationObserver !== 'undefined';
/**
 * Singleton controller class which handles updates of ResizeObserver instances.
 */
var ResizeObserverController = /** @class */ (function () {
    /**
     * Creates a new instance of ResizeObserverController.
     *
     * @private
     */
    function ResizeObserverController() {
        /**
         * Indicates whether DOM listeners have been added.
         *
         * @private {boolean}
         */
        this.connected_ = false;
        /**
         * Tells that controller has subscribed for Mutation Events.
         *
         * @private {boolean}
         */
        this.mutationEventsAdded_ = false;
        /**
         * Keeps reference to the instance of MutationObserver.
         *
         * @private {MutationObserver}
         */
        this.mutationsObserver_ = null;
        /**
         * A list of connected observers.
         *
         * @private {Array<ResizeObserverSPI>}
         */
        this.observers_ = [];
        this.onTransitionEnd_ = this.onTransitionEnd_.bind(this);
        this.refresh = throttle(this.refresh.bind(this), REFRESH_DELAY);
    }
    /**
     * Adds observer to observers list.
     *
     * @param {ResizeObserverSPI} observer - Observer to be added.
     * @returns {void}
     */
    ResizeObserverController.prototype.addObserver = function (observer) {
        if (!~this.observers_.indexOf(observer)) {
            this.observers_.push(observer);
        }
        // Add listeners if they haven't been added yet.
        if (!this.connected_) {
            this.connect_();
        }
    };
    /**
     * Removes observer from observers list.
     *
     * @param {ResizeObserverSPI} observer - Observer to be removed.
     * @returns {void}
     */
    ResizeObserverController.prototype.removeObserver = function (observer) {
        var observers = this.observers_;
        var index = observers.indexOf(observer);
        // Remove observer if it's present in registry.
        if (~index) {
            observers.splice(index, 1);
        }
        // Remove listeners if controller has no connected observers.
        if (!observers.length && this.connected_) {
            this.disconnect_();
        }
    };
    /**
     * Invokes the update of observers. It will continue running updates insofar
     * it detects changes.
     *
     * @returns {void}
     */
    ResizeObserverController.prototype.refresh = function () {
        var changesDetected = this.updateObservers_();
        // Continue running updates if changes have been detected as there might
        // be future ones caused by CSS transitions.
        if (changesDetected) {
            this.refresh();
        }
    };
    /**
     * Updates every observer from observers list and notifies them of queued
     * entries.
     *
     * @private
     * @returns {boolean} Returns "true" if any observer has detected changes in
     *      dimensions of it's elements.
     */
    ResizeObserverController.prototype.updateObservers_ = function () {
        // Collect observers that have active observations.
        var activeObservers = this.observers_.filter(function (observer) {
            return observer.gatherActive(), observer.hasActive();
        });
        // Deliver notifications in a separate cycle in order to avoid any
        // collisions between observers, e.g. when multiple instances of
        // ResizeObserver are tracking the same element and the callback of one
        // of them changes content dimensions of the observed target. Sometimes
        // this may result in notifications being blocked for the rest of observers.
        activeObservers.forEach(function (observer) { return observer.broadcastActive(); });
        return activeObservers.length > 0;
    };
    /**
     * Initializes DOM listeners.
     *
     * @private
     * @returns {void}
     */
    ResizeObserverController.prototype.connect_ = function () {
        // Do nothing if running in a non-browser environment or if listeners
        // have been already added.
        if (!isBrowser || this.connected_) {
            return;
        }
        // Subscription to the "Transitionend" event is used as a workaround for
        // delayed transitions. This way it's possible to capture at least the
        // final state of an element.
        document.addEventListener('transitionend', this.onTransitionEnd_);
        window.addEventListener('resize', this.refresh);
        if (mutationObserverSupported) {
            this.mutationsObserver_ = new MutationObserver(this.refresh);
            this.mutationsObserver_.observe(document, {
                attributes: true,
                childList: true,
                characterData: true,
                subtree: true
            });
        }
        else {
            document.addEventListener('DOMSubtreeModified', this.refresh);
            this.mutationEventsAdded_ = true;
        }
        this.connected_ = true;
    };
    /**
     * Removes DOM listeners.
     *
     * @private
     * @returns {void}
     */
    ResizeObserverController.prototype.disconnect_ = function () {
        // Do nothing if running in a non-browser environment or if listeners
        // have been already removed.
        if (!isBrowser || !this.connected_) {
            return;
        }
        document.removeEventListener('transitionend', this.onTransitionEnd_);
        window.removeEventListener('resize', this.refresh);
        if (this.mutationsObserver_) {
            this.mutationsObserver_.disconnect();
        }
        if (this.mutationEventsAdded_) {
            document.removeEventListener('DOMSubtreeModified', this.refresh);
        }
        this.mutationsObserver_ = null;
        this.mutationEventsAdded_ = false;
        this.connected_ = false;
    };
    /**
     * "Transitionend" event handler.
     *
     * @private
     * @param {TransitionEvent} event
     * @returns {void}
     */
    ResizeObserverController.prototype.onTransitionEnd_ = function (_a) {
        var _b = _a.propertyName, propertyName = _b === void 0 ? '' : _b;
        // Detect whether transition may affect dimensions of an element.
        var isReflowProperty = transitionKeys.some(function (key) {
            return !!~propertyName.indexOf(key);
        });
        if (isReflowProperty) {
            this.refresh();
        }
    };
    /**
     * Returns instance of the ResizeObserverController.
     *
     * @returns {ResizeObserverController}
     */
    ResizeObserverController.getInstance = function () {
        if (!this.instance_) {
            this.instance_ = new ResizeObserverController();
        }
        return this.instance_;
    };
    /**
     * Holds reference to the controller's instance.
     *
     * @private {ResizeObserverController}
     */
    ResizeObserverController.instance_ = null;
    return ResizeObserverController;
}());

/**
 * Defines non-writable/enumerable properties of the provided target object.
 *
 * @param {Object} target - Object for which to define properties.
 * @param {Object} props - Properties to be defined.
 * @returns {Object} Target object.
 */
var defineConfigurable = (function (target, props) {
    for (var _i = 0, _a = Object.keys(props); _i < _a.length; _i++) {
        var key = _a[_i];
        Object.defineProperty(target, key, {
            value: props[key],
            enumerable: false,
            writable: false,
            configurable: true
        });
    }
    return target;
});

/**
 * Returns the global object associated with provided element.
 *
 * @param {Object} target
 * @returns {Object}
 */
var getWindowOf = (function (target) {
    // Assume that the element is an instance of Node, which means that it
    // has the "ownerDocument" property from which we can retrieve a
    // corresponding global object.
    var ownerGlobal = target && target.ownerDocument && target.ownerDocument.defaultView;
    // Return the local global object if it's not possible extract one from
    // provided element.
    return ownerGlobal || global$1;
});

// Placeholder of an empty content rectangle.
var emptyRect = createRectInit(0, 0, 0, 0);
/**
 * Converts provided string to a number.
 *
 * @param {number|string} value
 * @returns {number}
 */
function toFloat(value) {
    return parseFloat(value) || 0;
}
/**
 * Extracts borders size from provided styles.
 *
 * @param {CSSStyleDeclaration} styles
 * @param {...string} positions - Borders positions (top, right, ...)
 * @returns {number}
 */
function getBordersSize(styles) {
    var positions = [];
    for (var _i = 1; _i < arguments.length; _i++) {
        positions[_i - 1] = arguments[_i];
    }
    return positions.reduce(function (size, position) {
        var value = styles['border-' + position + '-width'];
        return size + toFloat(value);
    }, 0);
}
/**
 * Extracts paddings sizes from provided styles.
 *
 * @param {CSSStyleDeclaration} styles
 * @returns {Object} Paddings box.
 */
function getPaddings(styles) {
    var positions = ['top', 'right', 'bottom', 'left'];
    var paddings = {};
    for (var _i = 0, positions_1 = positions; _i < positions_1.length; _i++) {
        var position = positions_1[_i];
        var value = styles['padding-' + position];
        paddings[position] = toFloat(value);
    }
    return paddings;
}
/**
 * Calculates content rectangle of provided SVG element.
 *
 * @param {SVGGraphicsElement} target - Element content rectangle of which needs
 *      to be calculated.
 * @returns {DOMRectInit}
 */
function getSVGContentRect(target) {
    var bbox = target.getBBox();
    return createRectInit(0, 0, bbox.width, bbox.height);
}
/**
 * Calculates content rectangle of provided HTMLElement.
 *
 * @param {HTMLElement} target - Element for which to calculate the content rectangle.
 * @returns {DOMRectInit}
 */
function getHTMLElementContentRect(target) {
    // Client width & height properties can't be
    // used exclusively as they provide rounded values.
    var clientWidth = target.clientWidth, clientHeight = target.clientHeight;
    // By this condition we can catch all non-replaced inline, hidden and
    // detached elements. Though elements with width & height properties less
    // than 0.5 will be discarded as well.
    //
    // Without it we would need to implement separate methods for each of
    // those cases and it's not possible to perform a precise and performance
    // effective test for hidden elements. E.g. even jQuery's ':visible' filter
    // gives wrong results for elements with width & height less than 0.5.
    if (!clientWidth && !clientHeight) {
        return emptyRect;
    }
    var styles = getWindowOf(target).getComputedStyle(target);
    var paddings = getPaddings(styles);
    var horizPad = paddings.left + paddings.right;
    var vertPad = paddings.top + paddings.bottom;
    // Computed styles of width & height are being used because they are the
    // only dimensions available to JS that contain non-rounded values. It could
    // be possible to utilize the getBoundingClientRect if only it's data wasn't
    // affected by CSS transformations let alone paddings, borders and scroll bars.
    var width = toFloat(styles.width), height = toFloat(styles.height);
    // Width & height include paddings and borders when the 'border-box' box
    // model is applied (except for IE).
    if (styles.boxSizing === 'border-box') {
        // Following conditions are required to handle Internet Explorer which
        // doesn't include paddings and borders to computed CSS dimensions.
        //
        // We can say that if CSS dimensions + paddings are equal to the "client"
        // properties then it's either IE, and thus we don't need to subtract
        // anything, or an element merely doesn't have paddings/borders styles.
        if (Math.round(width + horizPad) !== clientWidth) {
            width -= getBordersSize(styles, 'left', 'right') + horizPad;
        }
        if (Math.round(height + vertPad) !== clientHeight) {
            height -= getBordersSize(styles, 'top', 'bottom') + vertPad;
        }
    }
    // Following steps can't be applied to the document's root element as its
    // client[Width/Height] properties represent viewport area of the window.
    // Besides, it's as well not necessary as the <html> itself neither has
    // rendered scroll bars nor it can be clipped.
    if (!isDocumentElement(target)) {
        // In some browsers (only in Firefox, actually) CSS width & height
        // include scroll bars size which can be removed at this step as scroll
        // bars are the only difference between rounded dimensions + paddings
        // and "client" properties, though that is not always true in Chrome.
        var vertScrollbar = Math.round(width + horizPad) - clientWidth;
        var horizScrollbar = Math.round(height + vertPad) - clientHeight;
        // Chrome has a rather weird rounding of "client" properties.
        // E.g. for an element with content width of 314.2px it sometimes gives
        // the client width of 315px and for the width of 314.7px it may give
        // 314px. And it doesn't happen all the time. So just ignore this delta
        // as a non-relevant.
        if (Math.abs(vertScrollbar) !== 1) {
            width -= vertScrollbar;
        }
        if (Math.abs(horizScrollbar) !== 1) {
            height -= horizScrollbar;
        }
    }
    return createRectInit(paddings.left, paddings.top, width, height);
}
/**
 * Checks whether provided element is an instance of the SVGGraphicsElement.
 *
 * @param {Element} target - Element to be checked.
 * @returns {boolean}
 */
var isSVGGraphicsElement = (function () {
    // Some browsers, namely IE and Edge, don't have the SVGGraphicsElement
    // interface.
    if (typeof SVGGraphicsElement !== 'undefined') {
        return function (target) { return target instanceof getWindowOf(target).SVGGraphicsElement; };
    }
    // If it's so, then check that element is at least an instance of the
    // SVGElement and that it has the "getBBox" method.
    // eslint-disable-next-line no-extra-parens
    return function (target) { return (target instanceof getWindowOf(target).SVGElement &&
        typeof target.getBBox === 'function'); };
})();
/**
 * Checks whether provided element is a document element (<html>).
 *
 * @param {Element} target - Element to be checked.
 * @returns {boolean}
 */
function isDocumentElement(target) {
    return target === getWindowOf(target).document.documentElement;
}
/**
 * Calculates an appropriate content rectangle for provided html or svg element.
 *
 * @param {Element} target - Element content rectangle of which needs to be calculated.
 * @returns {DOMRectInit}
 */
function getContentRect(target) {
    if (!isBrowser) {
        return emptyRect;
    }
    if (isSVGGraphicsElement(target)) {
        return getSVGContentRect(target);
    }
    return getHTMLElementContentRect(target);
}
/**
 * Creates rectangle with an interface of the DOMRectReadOnly.
 * Spec: https://drafts.fxtf.org/geometry/#domrectreadonly
 *
 * @param {DOMRectInit} rectInit - Object with rectangle's x/y coordinates and dimensions.
 * @returns {DOMRectReadOnly}
 */
function createReadOnlyRect(_a) {
    var x = _a.x, y = _a.y, width = _a.width, height = _a.height;
    // If DOMRectReadOnly is available use it as a prototype for the rectangle.
    var Constr = typeof DOMRectReadOnly !== 'undefined' ? DOMRectReadOnly : Object;
    var rect = Object.create(Constr.prototype);
    // Rectangle's properties are not writable and non-enumerable.
    defineConfigurable(rect, {
        x: x, y: y, width: width, height: height,
        top: y,
        right: x + width,
        bottom: height + y,
        left: x
    });
    return rect;
}
/**
 * Creates DOMRectInit object based on the provided dimensions and the x/y coordinates.
 * Spec: https://drafts.fxtf.org/geometry/#dictdef-domrectinit
 *
 * @param {number} x - X coordinate.
 * @param {number} y - Y coordinate.
 * @param {number} width - Rectangle's width.
 * @param {number} height - Rectangle's height.
 * @returns {DOMRectInit}
 */
function createRectInit(x, y, width, height) {
    return { x: x, y: y, width: width, height: height };
}

/**
 * Class that is responsible for computations of the content rectangle of
 * provided DOM element and for keeping track of it's changes.
 */
var ResizeObservation = /** @class */ (function () {
    /**
     * Creates an instance of ResizeObservation.
     *
     * @param {Element} target - Element to be observed.
     */
    function ResizeObservation(target) {
        /**
         * Broadcasted width of content rectangle.
         *
         * @type {number}
         */
        this.broadcastWidth = 0;
        /**
         * Broadcasted height of content rectangle.
         *
         * @type {number}
         */
        this.broadcastHeight = 0;
        /**
         * Reference to the last observed content rectangle.
         *
         * @private {DOMRectInit}
         */
        this.contentRect_ = createRectInit(0, 0, 0, 0);
        this.target = target;
    }
    /**
     * Updates content rectangle and tells whether it's width or height properties
     * have changed since the last broadcast.
     *
     * @returns {boolean}
     */
    ResizeObservation.prototype.isActive = function () {
        var rect = getContentRect(this.target);
        this.contentRect_ = rect;
        return (rect.width !== this.broadcastWidth ||
            rect.height !== this.broadcastHeight);
    };
    /**
     * Updates 'broadcastWidth' and 'broadcastHeight' properties with a data
     * from the corresponding properties of the last observed content rectangle.
     *
     * @returns {DOMRectInit} Last observed content rectangle.
     */
    ResizeObservation.prototype.broadcastRect = function () {
        var rect = this.contentRect_;
        this.broadcastWidth = rect.width;
        this.broadcastHeight = rect.height;
        return rect;
    };
    return ResizeObservation;
}());

var ResizeObserverEntry = /** @class */ (function () {
    /**
     * Creates an instance of ResizeObserverEntry.
     *
     * @param {Element} target - Element that is being observed.
     * @param {DOMRectInit} rectInit - Data of the element's content rectangle.
     */
    function ResizeObserverEntry(target, rectInit) {
        var contentRect = createReadOnlyRect(rectInit);
        // According to the specification following properties are not writable
        // and are also not enumerable in the native implementation.
        //
        // Property accessors are not being used as they'd require to define a
        // private WeakMap storage which may cause memory leaks in browsers that
        // don't support this type of collections.
        defineConfigurable(this, { target: target, contentRect: contentRect });
    }
    return ResizeObserverEntry;
}());

var ResizeObserverSPI = /** @class */ (function () {
    /**
     * Creates a new instance of ResizeObserver.
     *
     * @param {ResizeObserverCallback} callback - Callback function that is invoked
     *      when one of the observed elements changes it's content dimensions.
     * @param {ResizeObserverController} controller - Controller instance which
     *      is responsible for the updates of observer.
     * @param {ResizeObserver} callbackCtx - Reference to the public
     *      ResizeObserver instance which will be passed to callback function.
     */
    function ResizeObserverSPI(callback, controller, callbackCtx) {
        /**
         * Collection of resize observations that have detected changes in dimensions
         * of elements.
         *
         * @private {Array<ResizeObservation>}
         */
        this.activeObservations_ = [];
        /**
         * Registry of the ResizeObservation instances.
         *
         * @private {Map<Element, ResizeObservation>}
         */
        this.observations_ = new MapShim();
        if (typeof callback !== 'function') {
            throw new TypeError('The callback provided as parameter 1 is not a function.');
        }
        this.callback_ = callback;
        this.controller_ = controller;
        this.callbackCtx_ = callbackCtx;
    }
    /**
     * Starts observing provided element.
     *
     * @param {Element} target - Element to be observed.
     * @returns {void}
     */
    ResizeObserverSPI.prototype.observe = function (target) {
        if (!arguments.length) {
            throw new TypeError('1 argument required, but only 0 present.');
        }
        // Do nothing if current environment doesn't have the Element interface.
        if (typeof Element === 'undefined' || !(Element instanceof Object)) {
            return;
        }
        if (!(target instanceof getWindowOf(target).Element)) {
            throw new TypeError('parameter 1 is not of type "Element".');
        }
        var observations = this.observations_;
        // Do nothing if element is already being observed.
        if (observations.has(target)) {
            return;
        }
        observations.set(target, new ResizeObservation(target));
        this.controller_.addObserver(this);
        // Force the update of observations.
        this.controller_.refresh();
    };
    /**
     * Stops observing provided element.
     *
     * @param {Element} target - Element to stop observing.
     * @returns {void}
     */
    ResizeObserverSPI.prototype.unobserve = function (target) {
        if (!arguments.length) {
            throw new TypeError('1 argument required, but only 0 present.');
        }
        // Do nothing if current environment doesn't have the Element interface.
        if (typeof Element === 'undefined' || !(Element instanceof Object)) {
            return;
        }
        if (!(target instanceof getWindowOf(target).Element)) {
            throw new TypeError('parameter 1 is not of type "Element".');
        }
        var observations = this.observations_;
        // Do nothing if element is not being observed.
        if (!observations.has(target)) {
            return;
        }
        observations.delete(target);
        if (!observations.size) {
            this.controller_.removeObserver(this);
        }
    };
    /**
     * Stops observing all elements.
     *
     * @returns {void}
     */
    ResizeObserverSPI.prototype.disconnect = function () {
        this.clearActive();
        this.observations_.clear();
        this.controller_.removeObserver(this);
    };
    /**
     * Collects observation instances the associated element of which has changed
     * it's content rectangle.
     *
     * @returns {void}
     */
    ResizeObserverSPI.prototype.gatherActive = function () {
        var _this = this;
        this.clearActive();
        this.observations_.forEach(function (observation) {
            if (observation.isActive()) {
                _this.activeObservations_.push(observation);
            }
        });
    };
    /**
     * Invokes initial callback function with a list of ResizeObserverEntry
     * instances collected from active resize observations.
     *
     * @returns {void}
     */
    ResizeObserverSPI.prototype.broadcastActive = function () {
        // Do nothing if observer doesn't have active observations.
        if (!this.hasActive()) {
            return;
        }
        var ctx = this.callbackCtx_;
        // Create ResizeObserverEntry instance for every active observation.
        var entries = this.activeObservations_.map(function (observation) {
            return new ResizeObserverEntry(observation.target, observation.broadcastRect());
        });
        this.callback_.call(ctx, entries, ctx);
        this.clearActive();
    };
    /**
     * Clears the collection of active observations.
     *
     * @returns {void}
     */
    ResizeObserverSPI.prototype.clearActive = function () {
        this.activeObservations_.splice(0);
    };
    /**
     * Tells whether observer has active observations.
     *
     * @returns {boolean}
     */
    ResizeObserverSPI.prototype.hasActive = function () {
        return this.activeObservations_.length > 0;
    };
    return ResizeObserverSPI;
}());

// Registry of internal observers. If WeakMap is not available use current shim
// for the Map collection as it has all required methods and because WeakMap
// can't be fully polyfilled anyway.
var observers = typeof WeakMap !== 'undefined' ? new WeakMap() : new MapShim();
/**
 * ResizeObserver API. Encapsulates the ResizeObserver SPI implementation
 * exposing only those methods and properties that are defined in the spec.
 */
var ResizeObserver$1 = /** @class */ (function () {
    /**
     * Creates a new instance of ResizeObserver.
     *
     * @param {ResizeObserverCallback} callback - Callback that is invoked when
     *      dimensions of the observed elements change.
     */
    function ResizeObserver(callback) {
        if (!(this instanceof ResizeObserver)) {
            throw new TypeError('Cannot call a class as a function.');
        }
        if (!arguments.length) {
            throw new TypeError('1 argument required, but only 0 present.');
        }
        var controller = ResizeObserverController.getInstance();
        var observer = new ResizeObserverSPI(callback, controller, this);
        observers.set(this, observer);
    }
    return ResizeObserver;
}());
// Expose public methods of ResizeObserver.
[
    'observe',
    'unobserve',
    'disconnect'
].forEach(function (method) {
    ResizeObserver$1.prototype[method] = function () {
        var _a;
        return (_a = observers.get(this))[method].apply(_a, arguments);
    };
});

var index = (function () {
    // Export existing implementation if available.
    if (typeof global$1.ResizeObserver !== 'undefined') {
        return global$1.ResizeObserver;
    }
    return ResizeObserver$1;
})();

var ResizeObserver_es = /*#__PURE__*/Object.freeze({
    __proto__: null,
    'default': index
});

var require$$8 = /*@__PURE__*/getAugmentedNamespace(ResizeObserver_es);

Object.defineProperty(innerSlider, "__esModule", {
  value: true
});
innerSlider.InnerSlider = void 0;

var _react = _interopRequireDefault(React);

var _initialState = _interopRequireDefault(initialState);

var _lodash = _interopRequireDefault(lodash_debounce);

var _classnames = _interopRequireDefault(classnames.exports);

var _innerSliderUtils = innerSliderUtils;

var _track = track;

var _dots = dots;

var _arrows = arrows;

var _resizeObserverPolyfill = _interopRequireDefault(require$$8);

function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { "default": obj }; }

function _typeof(obj) { "@babel/helpers - typeof"; return _typeof = "function" == typeof Symbol && "symbol" == typeof Symbol.iterator ? function (obj) { return typeof obj; } : function (obj) { return obj && "function" == typeof Symbol && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj; }, _typeof(obj); }

function _extends() { _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }

function _objectWithoutProperties(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }

function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

function _classCallCheck(instance, Constructor) { if (!(instance instanceof Constructor)) { throw new TypeError("Cannot call a class as a function"); } }

function _defineProperties(target, props) { for (var i = 0; i < props.length; i++) { var descriptor = props[i]; descriptor.enumerable = descriptor.enumerable || false; descriptor.configurable = true; if ("value" in descriptor) descriptor.writable = true; Object.defineProperty(target, descriptor.key, descriptor); } }

function _createClass(Constructor, protoProps, staticProps) { if (protoProps) _defineProperties(Constructor.prototype, protoProps); if (staticProps) _defineProperties(Constructor, staticProps); Object.defineProperty(Constructor, "prototype", { writable: false }); return Constructor; }

function _inherits(subClass, superClass) { if (typeof superClass !== "function" && superClass !== null) { throw new TypeError("Super expression must either be null or a function"); } subClass.prototype = Object.create(superClass && superClass.prototype, { constructor: { value: subClass, writable: true, configurable: true } }); Object.defineProperty(subClass, "prototype", { writable: false }); if (superClass) _setPrototypeOf(subClass, superClass); }

function _setPrototypeOf(o, p) { _setPrototypeOf = Object.setPrototypeOf || function _setPrototypeOf(o, p) { o.__proto__ = p; return o; }; return _setPrototypeOf(o, p); }

function _createSuper(Derived) { var hasNativeReflectConstruct = _isNativeReflectConstruct(); return function _createSuperInternal() { var Super = _getPrototypeOf(Derived), result; if (hasNativeReflectConstruct) { var NewTarget = _getPrototypeOf(this).constructor; result = Reflect.construct(Super, arguments, NewTarget); } else { result = Super.apply(this, arguments); } return _possibleConstructorReturn(this, result); }; }

function _possibleConstructorReturn(self, call) { if (call && (_typeof(call) === "object" || typeof call === "function")) { return call; } else if (call !== void 0) { throw new TypeError("Derived constructors may only return object or undefined"); } return _assertThisInitialized(self); }

function _assertThisInitialized(self) { if (self === void 0) { throw new ReferenceError("this hasn't been initialised - super() hasn't been called"); } return self; }

function _isNativeReflectConstruct() { if (typeof Reflect === "undefined" || !Reflect.construct) return false; if (Reflect.construct.sham) return false; if (typeof Proxy === "function") return true; try { Boolean.prototype.valueOf.call(Reflect.construct(Boolean, [], function () {})); return true; } catch (e) { return false; } }

function _getPrototypeOf(o) { _getPrototypeOf = Object.setPrototypeOf ? Object.getPrototypeOf : function _getPrototypeOf(o) { return o.__proto__ || Object.getPrototypeOf(o); }; return _getPrototypeOf(o); }

function _defineProperty(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

var InnerSlider = /*#__PURE__*/function (_React$Component) {
  _inherits(InnerSlider, _React$Component);

  var _super = _createSuper(InnerSlider);

  function InnerSlider(props) {
    var _this;

    _classCallCheck(this, InnerSlider);

    _this = _super.call(this, props);

    _defineProperty(_assertThisInitialized(_this), "listRefHandler", function (ref) {
      return _this.list = ref;
    });

    _defineProperty(_assertThisInitialized(_this), "trackRefHandler", function (ref) {
      return _this.track = ref;
    });

    _defineProperty(_assertThisInitialized(_this), "adaptHeight", function () {
      if (_this.props.adaptiveHeight && _this.list) {
        var elem = _this.list.querySelector("[data-index=\"".concat(_this.state.currentSlide, "\"]"));

        _this.list.style.height = (0, _innerSliderUtils.getHeight)(elem) + "px";
      }
    });

    _defineProperty(_assertThisInitialized(_this), "componentDidMount", function () {
      _this.props.onInit && _this.props.onInit();

      if (_this.props.lazyLoad) {
        var slidesToLoad = (0, _innerSliderUtils.getOnDemandLazySlides)(_objectSpread(_objectSpread({}, _this.props), _this.state));

        if (slidesToLoad.length > 0) {
          _this.setState(function (prevState) {
            return {
              lazyLoadedList: prevState.lazyLoadedList.concat(slidesToLoad)
            };
          });

          if (_this.props.onLazyLoad) {
            _this.props.onLazyLoad(slidesToLoad);
          }
        }
      }

      var spec = _objectSpread({
        listRef: _this.list,
        trackRef: _this.track
      }, _this.props);

      _this.updateState(spec, true, function () {
        _this.adaptHeight();

        _this.props.autoplay && _this.autoPlay("update");
      });

      if (_this.props.lazyLoad === "progressive") {
        _this.lazyLoadTimer = setInterval(_this.progressiveLazyLoad, 1000);
      }

      _this.ro = new _resizeObserverPolyfill["default"](function () {
        if (_this.state.animating) {
          _this.onWindowResized(false); // don't set trackStyle hence don't break animation


          _this.callbackTimers.push(setTimeout(function () {
            return _this.onWindowResized();
          }, _this.props.speed));
        } else {
          _this.onWindowResized();
        }
      });

      _this.ro.observe(_this.list);

      document.querySelectorAll && Array.prototype.forEach.call(document.querySelectorAll(".slick-slide"), function (slide) {
        slide.onfocus = _this.props.pauseOnFocus ? _this.onSlideFocus : null;
        slide.onblur = _this.props.pauseOnFocus ? _this.onSlideBlur : null;
      });

      if (window.addEventListener) {
        window.addEventListener("resize", _this.onWindowResized);
      } else {
        window.attachEvent("onresize", _this.onWindowResized);
      }
    });

    _defineProperty(_assertThisInitialized(_this), "componentWillUnmount", function () {
      if (_this.animationEndCallback) {
        clearTimeout(_this.animationEndCallback);
      }

      if (_this.lazyLoadTimer) {
        clearInterval(_this.lazyLoadTimer);
      }

      if (_this.callbackTimers.length) {
        _this.callbackTimers.forEach(function (timer) {
          return clearTimeout(timer);
        });

        _this.callbackTimers = [];
      }

      if (window.addEventListener) {
        window.removeEventListener("resize", _this.onWindowResized);
      } else {
        window.detachEvent("onresize", _this.onWindowResized);
      }

      if (_this.autoplayTimer) {
        clearInterval(_this.autoplayTimer);
      }

      _this.ro.disconnect();
    });

    _defineProperty(_assertThisInitialized(_this), "componentDidUpdate", function (prevProps) {
      _this.checkImagesLoad();

      _this.props.onReInit && _this.props.onReInit();

      if (_this.props.lazyLoad) {
        var slidesToLoad = (0, _innerSliderUtils.getOnDemandLazySlides)(_objectSpread(_objectSpread({}, _this.props), _this.state));

        if (slidesToLoad.length > 0) {
          _this.setState(function (prevState) {
            return {
              lazyLoadedList: prevState.lazyLoadedList.concat(slidesToLoad)
            };
          });

          if (_this.props.onLazyLoad) {
            _this.props.onLazyLoad(slidesToLoad);
          }
        }
      } // if (this.props.onLazyLoad) {
      //   this.props.onLazyLoad([leftMostSlide])
      // }


      _this.adaptHeight();

      var spec = _objectSpread(_objectSpread({
        listRef: _this.list,
        trackRef: _this.track
      }, _this.props), _this.state);

      var setTrackStyle = _this.didPropsChange(prevProps);

      setTrackStyle && _this.updateState(spec, setTrackStyle, function () {
        if (_this.state.currentSlide >= _react["default"].Children.count(_this.props.children)) {
          _this.changeSlide({
            message: "index",
            index: _react["default"].Children.count(_this.props.children) - _this.props.slidesToShow,
            currentSlide: _this.state.currentSlide
          });
        }

        if (_this.props.autoplay) {
          _this.autoPlay("update");
        } else {
          _this.pause("paused");
        }
      });
    });

    _defineProperty(_assertThisInitialized(_this), "onWindowResized", function (setTrackStyle) {
      if (_this.debouncedResize) _this.debouncedResize.cancel();
      _this.debouncedResize = (0, _lodash["default"])(function () {
        return _this.resizeWindow(setTrackStyle);
      }, 50);

      _this.debouncedResize();
    });

    _defineProperty(_assertThisInitialized(_this), "resizeWindow", function () {
      var setTrackStyle = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : true;
      var isTrackMounted = Boolean(_this.track && _this.track.node); // prevent warning: setting state on unmounted component (server side rendering)

      if (!isTrackMounted) return;

      var spec = _objectSpread(_objectSpread({
        listRef: _this.list,
        trackRef: _this.track
      }, _this.props), _this.state);

      _this.updateState(spec, setTrackStyle, function () {
        if (_this.props.autoplay) _this.autoPlay("update");else _this.pause("paused");
      }); // animating state should be cleared while resizing, otherwise autoplay stops working


      _this.setState({
        animating: false
      });

      clearTimeout(_this.animationEndCallback);
      delete _this.animationEndCallback;
    });

    _defineProperty(_assertThisInitialized(_this), "updateState", function (spec, setTrackStyle, callback) {
      var updatedState = (0, _innerSliderUtils.initializedState)(spec);
      spec = _objectSpread(_objectSpread(_objectSpread({}, spec), updatedState), {}, {
        slideIndex: updatedState.currentSlide
      });
      var targetLeft = (0, _innerSliderUtils.getTrackLeft)(spec);
      spec = _objectSpread(_objectSpread({}, spec), {}, {
        left: targetLeft
      });
      var trackStyle = (0, _innerSliderUtils.getTrackCSS)(spec);

      if (setTrackStyle || _react["default"].Children.count(_this.props.children) !== _react["default"].Children.count(spec.children)) {
        updatedState["trackStyle"] = trackStyle;
      }

      _this.setState(updatedState, callback);
    });

    _defineProperty(_assertThisInitialized(_this), "ssrInit", function () {
      if (_this.props.variableWidth) {
        var _trackWidth = 0,
            _trackLeft = 0;
        var childrenWidths = [];
        var preClones = (0, _innerSliderUtils.getPreClones)(_objectSpread(_objectSpread(_objectSpread({}, _this.props), _this.state), {}, {
          slideCount: _this.props.children.length
        }));
        var postClones = (0, _innerSliderUtils.getPostClones)(_objectSpread(_objectSpread(_objectSpread({}, _this.props), _this.state), {}, {
          slideCount: _this.props.children.length
        }));

        _this.props.children.forEach(function (child) {
          childrenWidths.push(child.props.style.width);
          _trackWidth += child.props.style.width;
        });

        for (var i = 0; i < preClones; i++) {
          _trackLeft += childrenWidths[childrenWidths.length - 1 - i];
          _trackWidth += childrenWidths[childrenWidths.length - 1 - i];
        }

        for (var _i = 0; _i < postClones; _i++) {
          _trackWidth += childrenWidths[_i];
        }

        for (var _i2 = 0; _i2 < _this.state.currentSlide; _i2++) {
          _trackLeft += childrenWidths[_i2];
        }

        var _trackStyle = {
          width: _trackWidth + "px",
          left: -_trackLeft + "px"
        };

        if (_this.props.centerMode) {
          var currentWidth = "".concat(childrenWidths[_this.state.currentSlide], "px");
          _trackStyle.left = "calc(".concat(_trackStyle.left, " + (100% - ").concat(currentWidth, ") / 2 ) ");
        }

        return {
          trackStyle: _trackStyle
        };
      }

      var childrenCount = _react["default"].Children.count(_this.props.children);

      var spec = _objectSpread(_objectSpread(_objectSpread({}, _this.props), _this.state), {}, {
        slideCount: childrenCount
      });

      var slideCount = (0, _innerSliderUtils.getPreClones)(spec) + (0, _innerSliderUtils.getPostClones)(spec) + childrenCount;
      var trackWidth = 100 / _this.props.slidesToShow * slideCount;
      var slideWidth = 100 / slideCount;
      var trackLeft = -slideWidth * ((0, _innerSliderUtils.getPreClones)(spec) + _this.state.currentSlide) * trackWidth / 100;

      if (_this.props.centerMode) {
        trackLeft += (100 - slideWidth * trackWidth / 100) / 2;
      }

      var trackStyle = {
        width: trackWidth + "%",
        left: trackLeft + "%"
      };
      return {
        slideWidth: slideWidth + "%",
        trackStyle: trackStyle
      };
    });

    _defineProperty(_assertThisInitialized(_this), "checkImagesLoad", function () {
      var images = _this.list && _this.list.querySelectorAll && _this.list.querySelectorAll(".slick-slide img") || [];
      var imagesCount = images.length,
          loadedCount = 0;
      Array.prototype.forEach.call(images, function (image) {
        var handler = function handler() {
          return ++loadedCount && loadedCount >= imagesCount && _this.onWindowResized();
        };

        if (!image.onclick) {
          image.onclick = function () {
            return image.parentNode.focus();
          };
        } else {
          var prevClickHandler = image.onclick;

          image.onclick = function () {
            prevClickHandler();
            image.parentNode.focus();
          };
        }

        if (!image.onload) {
          if (_this.props.lazyLoad) {
            image.onload = function () {
              _this.adaptHeight();

              _this.callbackTimers.push(setTimeout(_this.onWindowResized, _this.props.speed));
            };
          } else {
            image.onload = handler;

            image.onerror = function () {
              handler();
              _this.props.onLazyLoadError && _this.props.onLazyLoadError();
            };
          }
        }
      });
    });

    _defineProperty(_assertThisInitialized(_this), "progressiveLazyLoad", function () {
      var slidesToLoad = [];

      var spec = _objectSpread(_objectSpread({}, _this.props), _this.state);

      for (var index = _this.state.currentSlide; index < _this.state.slideCount + (0, _innerSliderUtils.getPostClones)(spec); index++) {
        if (_this.state.lazyLoadedList.indexOf(index) < 0) {
          slidesToLoad.push(index);
          break;
        }
      }

      for (var _index = _this.state.currentSlide - 1; _index >= -(0, _innerSliderUtils.getPreClones)(spec); _index--) {
        if (_this.state.lazyLoadedList.indexOf(_index) < 0) {
          slidesToLoad.push(_index);
          break;
        }
      }

      if (slidesToLoad.length > 0) {
        _this.setState(function (state) {
          return {
            lazyLoadedList: state.lazyLoadedList.concat(slidesToLoad)
          };
        });

        if (_this.props.onLazyLoad) {
          _this.props.onLazyLoad(slidesToLoad);
        }
      } else {
        if (_this.lazyLoadTimer) {
          clearInterval(_this.lazyLoadTimer);
          delete _this.lazyLoadTimer;
        }
      }
    });

    _defineProperty(_assertThisInitialized(_this), "slideHandler", function (index) {
      var dontAnimate = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;
      var _this$props = _this.props,
          asNavFor = _this$props.asNavFor,
          beforeChange = _this$props.beforeChange,
          onLazyLoad = _this$props.onLazyLoad,
          speed = _this$props.speed,
          afterChange = _this$props.afterChange; // capture currentslide before state is updated

      var currentSlide = _this.state.currentSlide;

      var _slideHandler = (0, _innerSliderUtils.slideHandler)(_objectSpread(_objectSpread(_objectSpread({
        index: index
      }, _this.props), _this.state), {}, {
        trackRef: _this.track,
        useCSS: _this.props.useCSS && !dontAnimate
      })),
          state = _slideHandler.state,
          nextState = _slideHandler.nextState;

      if (!state) return;
      beforeChange && beforeChange(currentSlide, state.currentSlide);
      var slidesToLoad = state.lazyLoadedList.filter(function (value) {
        return _this.state.lazyLoadedList.indexOf(value) < 0;
      });
      onLazyLoad && slidesToLoad.length > 0 && onLazyLoad(slidesToLoad);

      if (!_this.props.waitForAnimate && _this.animationEndCallback) {
        clearTimeout(_this.animationEndCallback);
        afterChange && afterChange(currentSlide);
        delete _this.animationEndCallback;
      }

      _this.setState(state, function () {
        // asNavForIndex check is to avoid recursive calls of slideHandler in waitForAnimate=false mode
        if (asNavFor && _this.asNavForIndex !== index) {
          _this.asNavForIndex = index;
          asNavFor.innerSlider.slideHandler(index);
        }

        if (!nextState) return;
        _this.animationEndCallback = setTimeout(function () {
          var animating = nextState.animating,
              firstBatch = _objectWithoutProperties(nextState, ["animating"]);

          _this.setState(firstBatch, function () {
            _this.callbackTimers.push(setTimeout(function () {
              return _this.setState({
                animating: animating
              });
            }, 10));

            afterChange && afterChange(state.currentSlide);
            delete _this.animationEndCallback;
          });
        }, speed);
      });
    });

    _defineProperty(_assertThisInitialized(_this), "changeSlide", function (options) {
      var dontAnimate = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;

      var spec = _objectSpread(_objectSpread({}, _this.props), _this.state);

      var targetSlide = (0, _innerSliderUtils.changeSlide)(spec, options);
      if (targetSlide !== 0 && !targetSlide) return;

      if (dontAnimate === true) {
        _this.slideHandler(targetSlide, dontAnimate);
      } else {
        _this.slideHandler(targetSlide);
      }

      _this.props.autoplay && _this.autoPlay("update");

      if (_this.props.focusOnSelect) {
        var nodes = _this.list.querySelectorAll(".slick-current");

        nodes[0] && nodes[0].focus();
      }
    });

    _defineProperty(_assertThisInitialized(_this), "clickHandler", function (e) {
      if (_this.clickable === false) {
        e.stopPropagation();
        e.preventDefault();
      }

      _this.clickable = true;
    });

    _defineProperty(_assertThisInitialized(_this), "keyHandler", function (e) {
      var dir = (0, _innerSliderUtils.keyHandler)(e, _this.props.accessibility, _this.props.rtl);
      dir !== "" && _this.changeSlide({
        message: dir
      });
    });

    _defineProperty(_assertThisInitialized(_this), "selectHandler", function (options) {
      _this.changeSlide(options);
    });

    _defineProperty(_assertThisInitialized(_this), "disableBodyScroll", function () {
      var preventDefault = function preventDefault(e) {
        e = e || window.event;
        if (e.preventDefault) e.preventDefault();
        e.returnValue = false;
      };

      window.ontouchmove = preventDefault;
    });

    _defineProperty(_assertThisInitialized(_this), "enableBodyScroll", function () {
      window.ontouchmove = null;
    });

    _defineProperty(_assertThisInitialized(_this), "swipeStart", function (e) {
      if (_this.props.verticalSwiping) {
        _this.disableBodyScroll();
      }

      var state = (0, _innerSliderUtils.swipeStart)(e, _this.props.swipe, _this.props.draggable);
      state !== "" && _this.setState(state);
    });

    _defineProperty(_assertThisInitialized(_this), "swipeMove", function (e) {
      var state = (0, _innerSliderUtils.swipeMove)(e, _objectSpread(_objectSpread(_objectSpread({}, _this.props), _this.state), {}, {
        trackRef: _this.track,
        listRef: _this.list,
        slideIndex: _this.state.currentSlide
      }));
      if (!state) return;

      if (state["swiping"]) {
        _this.clickable = false;
      }

      _this.setState(state);
    });

    _defineProperty(_assertThisInitialized(_this), "swipeEnd", function (e) {
      var state = (0, _innerSliderUtils.swipeEnd)(e, _objectSpread(_objectSpread(_objectSpread({}, _this.props), _this.state), {}, {
        trackRef: _this.track,
        listRef: _this.list,
        slideIndex: _this.state.currentSlide
      }));
      if (!state) return;
      var triggerSlideHandler = state["triggerSlideHandler"];
      delete state["triggerSlideHandler"];

      _this.setState(state);

      if (triggerSlideHandler === undefined) return;

      _this.slideHandler(triggerSlideHandler);

      if (_this.props.verticalSwiping) {
        _this.enableBodyScroll();
      }
    });

    _defineProperty(_assertThisInitialized(_this), "touchEnd", function (e) {
      _this.swipeEnd(e);

      _this.clickable = true;
    });

    _defineProperty(_assertThisInitialized(_this), "slickPrev", function () {
      // this and fellow methods are wrapped in setTimeout
      // to make sure initialize setState has happened before
      // any of such methods are called
      _this.callbackTimers.push(setTimeout(function () {
        return _this.changeSlide({
          message: "previous"
        });
      }, 0));
    });

    _defineProperty(_assertThisInitialized(_this), "slickNext", function () {
      _this.callbackTimers.push(setTimeout(function () {
        return _this.changeSlide({
          message: "next"
        });
      }, 0));
    });

    _defineProperty(_assertThisInitialized(_this), "slickGoTo", function (slide) {
      var dontAnimate = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;
      slide = Number(slide);
      if (isNaN(slide)) return "";

      _this.callbackTimers.push(setTimeout(function () {
        return _this.changeSlide({
          message: "index",
          index: slide,
          currentSlide: _this.state.currentSlide
        }, dontAnimate);
      }, 0));
    });

    _defineProperty(_assertThisInitialized(_this), "play", function () {
      var nextIndex;

      if (_this.props.rtl) {
        nextIndex = _this.state.currentSlide - _this.props.slidesToScroll;
      } else {
        if ((0, _innerSliderUtils.canGoNext)(_objectSpread(_objectSpread({}, _this.props), _this.state))) {
          nextIndex = _this.state.currentSlide + _this.props.slidesToScroll;
        } else {
          return false;
        }
      }

      _this.slideHandler(nextIndex);
    });

    _defineProperty(_assertThisInitialized(_this), "autoPlay", function (playType) {
      if (_this.autoplayTimer) {
        clearInterval(_this.autoplayTimer);
      }

      var autoplaying = _this.state.autoplaying;

      if (playType === "update") {
        if (autoplaying === "hovered" || autoplaying === "focused" || autoplaying === "paused") {
          return;
        }
      } else if (playType === "leave") {
        if (autoplaying === "paused" || autoplaying === "focused") {
          return;
        }
      } else if (playType === "blur") {
        if (autoplaying === "paused" || autoplaying === "hovered") {
          return;
        }
      }

      _this.autoplayTimer = setInterval(_this.play, _this.props.autoplaySpeed + 50);

      _this.setState({
        autoplaying: "playing"
      });
    });

    _defineProperty(_assertThisInitialized(_this), "pause", function (pauseType) {
      if (_this.autoplayTimer) {
        clearInterval(_this.autoplayTimer);
        _this.autoplayTimer = null;
      }

      var autoplaying = _this.state.autoplaying;

      if (pauseType === "paused") {
        _this.setState({
          autoplaying: "paused"
        });
      } else if (pauseType === "focused") {
        if (autoplaying === "hovered" || autoplaying === "playing") {
          _this.setState({
            autoplaying: "focused"
          });
        }
      } else {
        // pauseType  is 'hovered'
        if (autoplaying === "playing") {
          _this.setState({
            autoplaying: "hovered"
          });
        }
      }
    });

    _defineProperty(_assertThisInitialized(_this), "onDotsOver", function () {
      return _this.props.autoplay && _this.pause("hovered");
    });

    _defineProperty(_assertThisInitialized(_this), "onDotsLeave", function () {
      return _this.props.autoplay && _this.state.autoplaying === "hovered" && _this.autoPlay("leave");
    });

    _defineProperty(_assertThisInitialized(_this), "onTrackOver", function () {
      return _this.props.autoplay && _this.pause("hovered");
    });

    _defineProperty(_assertThisInitialized(_this), "onTrackLeave", function () {
      return _this.props.autoplay && _this.state.autoplaying === "hovered" && _this.autoPlay("leave");
    });

    _defineProperty(_assertThisInitialized(_this), "onSlideFocus", function () {
      return _this.props.autoplay && _this.pause("focused");
    });

    _defineProperty(_assertThisInitialized(_this), "onSlideBlur", function () {
      return _this.props.autoplay && _this.state.autoplaying === "focused" && _this.autoPlay("blur");
    });

    _defineProperty(_assertThisInitialized(_this), "render", function () {
      var className = (0, _classnames["default"])("slick-slider", _this.props.className, {
        "slick-vertical": _this.props.vertical,
        "slick-initialized": true
      });

      var spec = _objectSpread(_objectSpread({}, _this.props), _this.state);

      var trackProps = (0, _innerSliderUtils.extractObject)(spec, ["fade", "cssEase", "speed", "infinite", "centerMode", "focusOnSelect", "currentSlide", "lazyLoad", "lazyLoadedList", "rtl", "slideWidth", "slideHeight", "listHeight", "vertical", "slidesToShow", "slidesToScroll", "slideCount", "trackStyle", "variableWidth", "unslick", "centerPadding", "targetSlide", "useCSS"]);
      var pauseOnHover = _this.props.pauseOnHover;
      trackProps = _objectSpread(_objectSpread({}, trackProps), {}, {
        onMouseEnter: pauseOnHover ? _this.onTrackOver : null,
        onMouseLeave: pauseOnHover ? _this.onTrackLeave : null,
        onMouseOver: pauseOnHover ? _this.onTrackOver : null,
        focusOnSelect: _this.props.focusOnSelect && _this.clickable ? _this.selectHandler : null
      });
      var dots;

      if (_this.props.dots === true && _this.state.slideCount >= _this.props.slidesToShow) {
        var dotProps = (0, _innerSliderUtils.extractObject)(spec, ["dotsClass", "slideCount", "slidesToShow", "currentSlide", "slidesToScroll", "clickHandler", "children", "customPaging", "infinite", "appendDots"]);
        var pauseOnDotsHover = _this.props.pauseOnDotsHover;
        dotProps = _objectSpread(_objectSpread({}, dotProps), {}, {
          clickHandler: _this.changeSlide,
          onMouseEnter: pauseOnDotsHover ? _this.onDotsLeave : null,
          onMouseOver: pauseOnDotsHover ? _this.onDotsOver : null,
          onMouseLeave: pauseOnDotsHover ? _this.onDotsLeave : null
        });
        dots = /*#__PURE__*/_react["default"].createElement(_dots.Dots, dotProps);
      }

      var prevArrow, nextArrow;
      var arrowProps = (0, _innerSliderUtils.extractObject)(spec, ["infinite", "centerMode", "currentSlide", "slideCount", "slidesToShow", "prevArrow", "nextArrow"]);
      arrowProps.clickHandler = _this.changeSlide;

      if (_this.props.arrows) {
        prevArrow = /*#__PURE__*/_react["default"].createElement(_arrows.PrevArrow, arrowProps);
        nextArrow = /*#__PURE__*/_react["default"].createElement(_arrows.NextArrow, arrowProps);
      }

      var verticalHeightStyle = null;

      if (_this.props.vertical) {
        verticalHeightStyle = {
          height: _this.state.listHeight
        };
      }

      var centerPaddingStyle = null;

      if (_this.props.vertical === false) {
        if (_this.props.centerMode === true) {
          centerPaddingStyle = {
            padding: "0px " + _this.props.centerPadding
          };
        }
      } else {
        if (_this.props.centerMode === true) {
          centerPaddingStyle = {
            padding: _this.props.centerPadding + " 0px"
          };
        }
      }

      var listStyle = _objectSpread(_objectSpread({}, verticalHeightStyle), centerPaddingStyle);

      var touchMove = _this.props.touchMove;
      var listProps = {
        className: "slick-list",
        style: listStyle,
        onClick: _this.clickHandler,
        onMouseDown: touchMove ? _this.swipeStart : null,
        onMouseMove: _this.state.dragging && touchMove ? _this.swipeMove : null,
        onMouseUp: touchMove ? _this.swipeEnd : null,
        onMouseLeave: _this.state.dragging && touchMove ? _this.swipeEnd : null,
        onTouchStart: touchMove ? _this.swipeStart : null,
        onTouchMove: _this.state.dragging && touchMove ? _this.swipeMove : null,
        onTouchEnd: touchMove ? _this.touchEnd : null,
        onTouchCancel: _this.state.dragging && touchMove ? _this.swipeEnd : null,
        onKeyDown: _this.props.accessibility ? _this.keyHandler : null
      };
      var innerSliderProps = {
        className: className,
        dir: "ltr",
        style: _this.props.style
      };

      if (_this.props.unslick) {
        listProps = {
          className: "slick-list"
        };
        innerSliderProps = {
          className: className
        };
      }

      return /*#__PURE__*/_react["default"].createElement("div", innerSliderProps, !_this.props.unslick ? prevArrow : "", /*#__PURE__*/_react["default"].createElement("div", _extends({
        ref: _this.listRefHandler
      }, listProps), /*#__PURE__*/_react["default"].createElement(_track.Track, _extends({
        ref: _this.trackRefHandler
      }, trackProps), _this.props.children)), !_this.props.unslick ? nextArrow : "", !_this.props.unslick ? dots : "");
    });

    _this.list = null;
    _this.track = null;
    _this.state = _objectSpread(_objectSpread({}, _initialState["default"]), {}, {
      currentSlide: _this.props.initialSlide,
      slideCount: _react["default"].Children.count(_this.props.children)
    });
    _this.callbackTimers = [];
    _this.clickable = true;
    _this.debouncedResize = null;

    var ssrState = _this.ssrInit();

    _this.state = _objectSpread(_objectSpread({}, _this.state), ssrState);
    return _this;
  }

  _createClass(InnerSlider, [{
    key: "didPropsChange",
    value: function didPropsChange(prevProps) {
      var setTrackStyle = false;

      for (var _i3 = 0, _Object$keys = Object.keys(this.props); _i3 < _Object$keys.length; _i3++) {
        var key = _Object$keys[_i3];

        if (!prevProps.hasOwnProperty(key)) {
          setTrackStyle = true;
          break;
        }

        if (_typeof(prevProps[key]) === "object" || typeof prevProps[key] === "function") {
          continue;
        }

        if (prevProps[key] !== this.props[key]) {
          setTrackStyle = true;
          break;
        }
      }

      return setTrackStyle || _react["default"].Children.count(this.props.children) !== _react["default"].Children.count(prevProps.children);
    }
  }]);

  return InnerSlider;
}(_react["default"].Component);

innerSlider.InnerSlider = InnerSlider;

var camel2hyphen$1 = function (str) {
  return str
          .replace(/[A-Z]/g, function (match) {
            return '-' + match.toLowerCase();
          })
          .toLowerCase();
};

var camel2hyphen_1 = camel2hyphen$1;

var camel2hyphen = camel2hyphen_1;

var isDimension = function (feature) {
  var re = /[height|width]$/;
  return re.test(feature);
};

var obj2mq = function (obj) {
  var mq = '';
  var features = Object.keys(obj);
  features.forEach(function (feature, index) {
    var value = obj[feature];
    feature = camel2hyphen(feature);
    // Add px to dimension features
    if (isDimension(feature) && typeof value === 'number') {
      value = value + 'px';
    }
    if (value === true) {
      mq += feature;
    } else if (value === false) {
      mq += 'not ' + feature;
    } else {
      mq += '(' + feature + ': ' + value + ')';
    }
    if (index < features.length-1) {
      mq += ' and ';
    }
  });
  return mq;
};

var json2mq = function (query) {
  var mq = '';
  if (typeof query === 'string') {
    return query;
  }
  // Handling array of media queries
  if (query instanceof Array) {
    query.forEach(function (q, index) {
      mq += obj2mq(q);
      if (index < query.length-1) {
        mq += ', ';
      }
    });
    return mq;
  }
  // Handling single media query
  return obj2mq(query);
};

var json2mq_1 = json2mq;

var defaultProps = {};

(function (exports) {

	Object.defineProperty(exports, "__esModule", {
	  value: true
	});
	exports["default"] = void 0;

	var _react = _interopRequireDefault(React);

	function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { "default": obj }; }

	var defaultProps = {
	  accessibility: true,
	  adaptiveHeight: false,
	  afterChange: null,
	  appendDots: function appendDots(dots) {
	    return /*#__PURE__*/_react["default"].createElement("ul", {
	      style: {
	        display: "block"
	      }
	    }, dots);
	  },
	  arrows: true,
	  autoplay: false,
	  autoplaySpeed: 3000,
	  beforeChange: null,
	  centerMode: false,
	  centerPadding: "50px",
	  className: "",
	  cssEase: "ease",
	  customPaging: function customPaging(i) {
	    return /*#__PURE__*/_react["default"].createElement("button", null, i + 1);
	  },
	  dots: false,
	  dotsClass: "slick-dots",
	  draggable: true,
	  easing: "linear",
	  edgeFriction: 0.35,
	  fade: false,
	  focusOnSelect: false,
	  infinite: true,
	  initialSlide: 0,
	  lazyLoad: null,
	  nextArrow: null,
	  onEdge: null,
	  onInit: null,
	  onLazyLoadError: null,
	  onReInit: null,
	  pauseOnDotsHover: false,
	  pauseOnFocus: false,
	  pauseOnHover: true,
	  prevArrow: null,
	  responsive: null,
	  rows: 1,
	  rtl: false,
	  slide: "div",
	  slidesPerRow: 1,
	  slidesToScroll: 1,
	  slidesToShow: 1,
	  speed: 500,
	  swipe: true,
	  swipeEvent: null,
	  swipeToSlide: false,
	  touchMove: true,
	  touchThreshold: 5,
	  useCSS: true,
	  useTransform: true,
	  variableWidth: false,
	  vertical: false,
	  waitForAnimate: true
	};
	var _default = defaultProps;
	exports["default"] = _default;
} (defaultProps));

/**
 * Delegate to handle a media query being matched and unmatched.
 *
 * @param {object} options
 * @param {function} options.match callback for when the media query is matched
 * @param {function} [options.unmatch] callback for when the media query is unmatched
 * @param {function} [options.setup] one-time callback triggered the first time a query is matched
 * @param {boolean} [options.deferSetup=false] should the setup callback be run immediately, rather than first time query is matched?
 * @constructor
 */

var QueryHandler_1;
var hasRequiredQueryHandler;

function requireQueryHandler () {
	if (hasRequiredQueryHandler) return QueryHandler_1;
	hasRequiredQueryHandler = 1;
	function QueryHandler(options) {
	    this.options = options;
	    !options.deferSetup && this.setup();
	}

	QueryHandler.prototype = {

	    constructor : QueryHandler,

	    /**
	     * coordinates setup of the handler
	     *
	     * @function
	     */
	    setup : function() {
	        if(this.options.setup) {
	            this.options.setup();
	        }
	        this.initialised = true;
	    },

	    /**
	     * coordinates setup and triggering of the handler
	     *
	     * @function
	     */
	    on : function() {
	        !this.initialised && this.setup();
	        this.options.match && this.options.match();
	    },

	    /**
	     * coordinates the unmatch event for the handler
	     *
	     * @function
	     */
	    off : function() {
	        this.options.unmatch && this.options.unmatch();
	    },

	    /**
	     * called when a handler is to be destroyed.
	     * delegates to the destroy or unmatch callbacks, depending on availability.
	     *
	     * @function
	     */
	    destroy : function() {
	        this.options.destroy ? this.options.destroy() : this.off();
	    },

	    /**
	     * determines equality by reference.
	     * if object is supplied compare options, if function, compare match callback
	     *
	     * @function
	     * @param {object || function} [target] the target for comparison
	     */
	    equals : function(target) {
	        return this.options === target || this.options.match === target;
	    }

	};

	QueryHandler_1 = QueryHandler;
	return QueryHandler_1;
}

/**
 * Helper function for iterating over a collection
 *
 * @param collection
 * @param fn
 */

var Util;
var hasRequiredUtil;

function requireUtil () {
	if (hasRequiredUtil) return Util;
	hasRequiredUtil = 1;
	function each(collection, fn) {
	    var i      = 0,
	        length = collection.length,
	        cont;

	    for(i; i < length; i++) {
	        cont = fn(collection[i], i);
	        if(cont === false) {
	            break; //allow early exit
	        }
	    }
	}

	/**
	 * Helper function for determining whether target object is an array
	 *
	 * @param target the object under test
	 * @return {Boolean} true if array, false otherwise
	 */
	function isArray(target) {
	    return Object.prototype.toString.apply(target) === '[object Array]';
	}

	/**
	 * Helper function for determining whether target object is a function
	 *
	 * @param target the object under test
	 * @return {Boolean} true if function, false otherwise
	 */
	function isFunction(target) {
	    return typeof target === 'function';
	}

	Util = {
	    isFunction : isFunction,
	    isArray : isArray,
	    each : each
	};
	return Util;
}

var MediaQuery_1;
var hasRequiredMediaQuery;

function requireMediaQuery () {
	if (hasRequiredMediaQuery) return MediaQuery_1;
	hasRequiredMediaQuery = 1;
	var QueryHandler = requireQueryHandler();
	var each = requireUtil().each;

	/**
	 * Represents a single media query, manages it's state and registered handlers for this query
	 *
	 * @constructor
	 * @param {string} query the media query string
	 * @param {boolean} [isUnconditional=false] whether the media query should run regardless of whether the conditions are met. Primarily for helping older browsers deal with mobile-first design
	 */
	function MediaQuery(query, isUnconditional) {
	    this.query = query;
	    this.isUnconditional = isUnconditional;
	    this.handlers = [];
	    this.mql = window.matchMedia(query);

	    var self = this;
	    this.listener = function(mql) {
	        // Chrome passes an MediaQueryListEvent object, while other browsers pass MediaQueryList directly
	        self.mql = mql.currentTarget || mql;
	        self.assess();
	    };
	    this.mql.addListener(this.listener);
	}

	MediaQuery.prototype = {

	    constuctor : MediaQuery,

	    /**
	     * add a handler for this query, triggering if already active
	     *
	     * @param {object} handler
	     * @param {function} handler.match callback for when query is activated
	     * @param {function} [handler.unmatch] callback for when query is deactivated
	     * @param {function} [handler.setup] callback for immediate execution when a query handler is registered
	     * @param {boolean} [handler.deferSetup=false] should the setup callback be deferred until the first time the handler is matched?
	     */
	    addHandler : function(handler) {
	        var qh = new QueryHandler(handler);
	        this.handlers.push(qh);

	        this.matches() && qh.on();
	    },

	    /**
	     * removes the given handler from the collection, and calls it's destroy methods
	     *
	     * @param {object || function} handler the handler to remove
	     */
	    removeHandler : function(handler) {
	        var handlers = this.handlers;
	        each(handlers, function(h, i) {
	            if(h.equals(handler)) {
	                h.destroy();
	                return !handlers.splice(i,1); //remove from array and exit each early
	            }
	        });
	    },

	    /**
	     * Determine whether the media query should be considered a match
	     *
	     * @return {Boolean} true if media query can be considered a match, false otherwise
	     */
	    matches : function() {
	        return this.mql.matches || this.isUnconditional;
	    },

	    /**
	     * Clears all handlers and unbinds events
	     */
	    clear : function() {
	        each(this.handlers, function(handler) {
	            handler.destroy();
	        });
	        this.mql.removeListener(this.listener);
	        this.handlers.length = 0; //clear array
	    },

	    /*
	        * Assesses the query, turning on all handlers if it matches, turning them off if it doesn't match
	        */
	    assess : function() {
	        var action = this.matches() ? 'on' : 'off';

	        each(this.handlers, function(handler) {
	            handler[action]();
	        });
	    }
	};

	MediaQuery_1 = MediaQuery;
	return MediaQuery_1;
}

var MediaQueryDispatch_1;
var hasRequiredMediaQueryDispatch;

function requireMediaQueryDispatch () {
	if (hasRequiredMediaQueryDispatch) return MediaQueryDispatch_1;
	hasRequiredMediaQueryDispatch = 1;
	var MediaQuery = requireMediaQuery();
	var Util = requireUtil();
	var each = Util.each;
	var isFunction = Util.isFunction;
	var isArray = Util.isArray;

	/**
	 * Allows for registration of query handlers.
	 * Manages the query handler's state and is responsible for wiring up browser events
	 *
	 * @constructor
	 */
	function MediaQueryDispatch () {
	    if(!window.matchMedia) {
	        throw new Error('matchMedia not present, legacy browsers require a polyfill');
	    }

	    this.queries = {};
	    this.browserIsIncapable = !window.matchMedia('only all').matches;
	}

	MediaQueryDispatch.prototype = {

	    constructor : MediaQueryDispatch,

	    /**
	     * Registers a handler for the given media query
	     *
	     * @param {string} q the media query
	     * @param {object || Array || Function} options either a single query handler object, a function, or an array of query handlers
	     * @param {function} options.match fired when query matched
	     * @param {function} [options.unmatch] fired when a query is no longer matched
	     * @param {function} [options.setup] fired when handler first triggered
	     * @param {boolean} [options.deferSetup=false] whether setup should be run immediately or deferred until query is first matched
	     * @param {boolean} [shouldDegrade=false] whether this particular media query should always run on incapable browsers
	     */
	    register : function(q, options, shouldDegrade) {
	        var queries         = this.queries,
	            isUnconditional = shouldDegrade && this.browserIsIncapable;

	        if(!queries[q]) {
	            queries[q] = new MediaQuery(q, isUnconditional);
	        }

	        //normalise to object in an array
	        if(isFunction(options)) {
	            options = { match : options };
	        }
	        if(!isArray(options)) {
	            options = [options];
	        }
	        each(options, function(handler) {
	            if (isFunction(handler)) {
	                handler = { match : handler };
	            }
	            queries[q].addHandler(handler);
	        });

	        return this;
	    },

	    /**
	     * unregisters a query and all it's handlers, or a specific handler for a query
	     *
	     * @param {string} q the media query to target
	     * @param {object || function} [handler] specific handler to unregister
	     */
	    unregister : function(q, handler) {
	        var query = this.queries[q];

	        if(query) {
	            if(handler) {
	                query.removeHandler(handler);
	            }
	            else {
	                query.clear();
	                delete this.queries[q];
	            }
	        }

	        return this;
	    }
	};

	MediaQueryDispatch_1 = MediaQueryDispatch;
	return MediaQueryDispatch_1;
}

var src;
var hasRequiredSrc;

function requireSrc () {
	if (hasRequiredSrc) return src;
	hasRequiredSrc = 1;
	var MediaQueryDispatch = requireMediaQueryDispatch();
	src = new MediaQueryDispatch();
	return src;
}

(function (exports) {

	function _typeof(obj) { "@babel/helpers - typeof"; return _typeof = "function" == typeof Symbol && "symbol" == typeof Symbol.iterator ? function (obj) { return typeof obj; } : function (obj) { return obj && "function" == typeof Symbol && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj; }, _typeof(obj); }

	Object.defineProperty(exports, "__esModule", {
	  value: true
	});
	exports["default"] = void 0;

	var _react = _interopRequireDefault(React);

	var _innerSlider = innerSlider;

	var _json2mq = _interopRequireDefault(json2mq_1);

	var _defaultProps = _interopRequireDefault(defaultProps);

	var _innerSliderUtils = innerSliderUtils;

	function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { "default": obj }; }

	function _extends() { _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }

	function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

	function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

	function _classCallCheck(instance, Constructor) { if (!(instance instanceof Constructor)) { throw new TypeError("Cannot call a class as a function"); } }

	function _defineProperties(target, props) { for (var i = 0; i < props.length; i++) { var descriptor = props[i]; descriptor.enumerable = descriptor.enumerable || false; descriptor.configurable = true; if ("value" in descriptor) descriptor.writable = true; Object.defineProperty(target, descriptor.key, descriptor); } }

	function _createClass(Constructor, protoProps, staticProps) { if (protoProps) _defineProperties(Constructor.prototype, protoProps); if (staticProps) _defineProperties(Constructor, staticProps); Object.defineProperty(Constructor, "prototype", { writable: false }); return Constructor; }

	function _inherits(subClass, superClass) { if (typeof superClass !== "function" && superClass !== null) { throw new TypeError("Super expression must either be null or a function"); } subClass.prototype = Object.create(superClass && superClass.prototype, { constructor: { value: subClass, writable: true, configurable: true } }); Object.defineProperty(subClass, "prototype", { writable: false }); if (superClass) _setPrototypeOf(subClass, superClass); }

	function _setPrototypeOf(o, p) { _setPrototypeOf = Object.setPrototypeOf || function _setPrototypeOf(o, p) { o.__proto__ = p; return o; }; return _setPrototypeOf(o, p); }

	function _createSuper(Derived) { var hasNativeReflectConstruct = _isNativeReflectConstruct(); return function _createSuperInternal() { var Super = _getPrototypeOf(Derived), result; if (hasNativeReflectConstruct) { var NewTarget = _getPrototypeOf(this).constructor; result = Reflect.construct(Super, arguments, NewTarget); } else { result = Super.apply(this, arguments); } return _possibleConstructorReturn(this, result); }; }

	function _possibleConstructorReturn(self, call) { if (call && (_typeof(call) === "object" || typeof call === "function")) { return call; } else if (call !== void 0) { throw new TypeError("Derived constructors may only return object or undefined"); } return _assertThisInitialized(self); }

	function _assertThisInitialized(self) { if (self === void 0) { throw new ReferenceError("this hasn't been initialised - super() hasn't been called"); } return self; }

	function _isNativeReflectConstruct() { if (typeof Reflect === "undefined" || !Reflect.construct) return false; if (Reflect.construct.sham) return false; if (typeof Proxy === "function") return true; try { Boolean.prototype.valueOf.call(Reflect.construct(Boolean, [], function () {})); return true; } catch (e) { return false; } }

	function _getPrototypeOf(o) { _getPrototypeOf = Object.setPrototypeOf ? Object.getPrototypeOf : function _getPrototypeOf(o) { return o.__proto__ || Object.getPrototypeOf(o); }; return _getPrototypeOf(o); }

	function _defineProperty(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

	var enquire = (0, _innerSliderUtils.canUseDOM)() && requireSrc();

	var Slider = /*#__PURE__*/function (_React$Component) {
	  _inherits(Slider, _React$Component);

	  var _super = _createSuper(Slider);

	  function Slider(props) {
	    var _this;

	    _classCallCheck(this, Slider);

	    _this = _super.call(this, props);

	    _defineProperty(_assertThisInitialized(_this), "innerSliderRefHandler", function (ref) {
	      return _this.innerSlider = ref;
	    });

	    _defineProperty(_assertThisInitialized(_this), "slickPrev", function () {
	      return _this.innerSlider.slickPrev();
	    });

	    _defineProperty(_assertThisInitialized(_this), "slickNext", function () {
	      return _this.innerSlider.slickNext();
	    });

	    _defineProperty(_assertThisInitialized(_this), "slickGoTo", function (slide) {
	      var dontAnimate = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;
	      return _this.innerSlider.slickGoTo(slide, dontAnimate);
	    });

	    _defineProperty(_assertThisInitialized(_this), "slickPause", function () {
	      return _this.innerSlider.pause("paused");
	    });

	    _defineProperty(_assertThisInitialized(_this), "slickPlay", function () {
	      return _this.innerSlider.autoPlay("play");
	    });

	    _this.state = {
	      breakpoint: null
	    };
	    _this._responsiveMediaHandlers = [];
	    return _this;
	  }

	  _createClass(Slider, [{
	    key: "media",
	    value: function media(query, handler) {
	      // javascript handler for  css media query
	      enquire.register(query, handler);

	      this._responsiveMediaHandlers.push({
	        query: query,
	        handler: handler
	      });
	    } // handles responsive breakpoints

	  }, {
	    key: "componentDidMount",
	    value: function componentDidMount() {
	      var _this2 = this;

	      // performance monitoring
	      //if (process.env.NODE_ENV !== 'production') {
	      //const { whyDidYouUpdate } = require('why-did-you-update')
	      //whyDidYouUpdate(React)
	      //}
	      if (this.props.responsive) {
	        var breakpoints = this.props.responsive.map(function (breakpt) {
	          return breakpt.breakpoint;
	        }); // sort them in increasing order of their numerical value

	        breakpoints.sort(function (x, y) {
	          return x - y;
	        });
	        breakpoints.forEach(function (breakpoint, index) {
	          // media query for each breakpoint
	          var bQuery;

	          if (index === 0) {
	            bQuery = (0, _json2mq["default"])({
	              minWidth: 0,
	              maxWidth: breakpoint
	            });
	          } else {
	            bQuery = (0, _json2mq["default"])({
	              minWidth: breakpoints[index - 1] + 1,
	              maxWidth: breakpoint
	            });
	          } // when not using server side rendering


	          (0, _innerSliderUtils.canUseDOM)() && _this2.media(bQuery, function () {
	            _this2.setState({
	              breakpoint: breakpoint
	            });
	          });
	        }); // Register media query for full screen. Need to support resize from small to large
	        // convert javascript object to media query string

	        var query = (0, _json2mq["default"])({
	          minWidth: breakpoints.slice(-1)[0]
	        });
	        (0, _innerSliderUtils.canUseDOM)() && this.media(query, function () {
	          _this2.setState({
	            breakpoint: null
	          });
	        });
	      }
	    }
	  }, {
	    key: "componentWillUnmount",
	    value: function componentWillUnmount() {
	      this._responsiveMediaHandlers.forEach(function (obj) {
	        enquire.unregister(obj.query, obj.handler);
	      });
	    }
	  }, {
	    key: "render",
	    value: function render() {
	      var _this3 = this;

	      var settings;
	      var newProps;

	      if (this.state.breakpoint) {
	        newProps = this.props.responsive.filter(function (resp) {
	          return resp.breakpoint === _this3.state.breakpoint;
	        });
	        settings = newProps[0].settings === "unslick" ? "unslick" : _objectSpread(_objectSpread(_objectSpread({}, _defaultProps["default"]), this.props), newProps[0].settings);
	      } else {
	        settings = _objectSpread(_objectSpread({}, _defaultProps["default"]), this.props);
	      } // force scrolling by one if centerMode is on


	      if (settings.centerMode) {
	        if (settings.slidesToScroll > 1 && process.env.NODE_ENV !== "production") {
	          console.warn("slidesToScroll should be equal to 1 in centerMode, you are using ".concat(settings.slidesToScroll));
	        }

	        settings.slidesToScroll = 1;
	      } // force showing one slide and scrolling by one if the fade mode is on


	      if (settings.fade) {
	        if (settings.slidesToShow > 1 && process.env.NODE_ENV !== "production") {
	          console.warn("slidesToShow should be equal to 1 when fade is true, you're using ".concat(settings.slidesToShow));
	        }

	        if (settings.slidesToScroll > 1 && process.env.NODE_ENV !== "production") {
	          console.warn("slidesToScroll should be equal to 1 when fade is true, you're using ".concat(settings.slidesToScroll));
	        }

	        settings.slidesToShow = 1;
	        settings.slidesToScroll = 1;
	      } // makes sure that children is an array, even when there is only 1 child


	      var children = _react["default"].Children.toArray(this.props.children); // Children may contain false or null, so we should filter them
	      // children may also contain string filled with spaces (in certain cases where we use jsx strings)


	      children = children.filter(function (child) {
	        if (typeof child === "string") {
	          return !!child.trim();
	        }

	        return !!child;
	      }); // rows and slidesPerRow logic is handled here

	      if (settings.variableWidth && (settings.rows > 1 || settings.slidesPerRow > 1)) {
	        console.warn("variableWidth is not supported in case of rows > 1 or slidesPerRow > 1");
	        settings.variableWidth = false;
	      }

	      var newChildren = [];
	      var currentWidth = null;

	      for (var i = 0; i < children.length; i += settings.rows * settings.slidesPerRow) {
	        var newSlide = [];

	        for (var j = i; j < i + settings.rows * settings.slidesPerRow; j += settings.slidesPerRow) {
	          var row = [];

	          for (var k = j; k < j + settings.slidesPerRow; k += 1) {
	            if (settings.variableWidth && children[k].props.style) {
	              currentWidth = children[k].props.style.width;
	            }

	            if (k >= children.length) break;
	            row.push( /*#__PURE__*/_react["default"].cloneElement(children[k], {
	              key: 100 * i + 10 * j + k,
	              tabIndex: -1,
	              style: {
	                width: "".concat(100 / settings.slidesPerRow, "%"),
	                display: "inline-block"
	              }
	            }));
	          }

	          newSlide.push( /*#__PURE__*/_react["default"].createElement("div", {
	            key: 10 * i + j
	          }, row));
	        }

	        if (settings.variableWidth) {
	          newChildren.push( /*#__PURE__*/_react["default"].createElement("div", {
	            key: i,
	            style: {
	              width: currentWidth
	            }
	          }, newSlide));
	        } else {
	          newChildren.push( /*#__PURE__*/_react["default"].createElement("div", {
	            key: i
	          }, newSlide));
	        }
	      }

	      if (settings === "unslick") {
	        var className = "regular slider " + (this.props.className || "");
	        return /*#__PURE__*/_react["default"].createElement("div", {
	          className: className
	        }, children);
	      } else if (newChildren.length <= settings.slidesToShow) {
	        settings.unslick = true;
	      }

	      return /*#__PURE__*/_react["default"].createElement(_innerSlider.InnerSlider, _extends({
	        style: this.props.style,
	        ref: this.innerSliderRefHandler
	      }, settings), newChildren);
	    }
	  }]);

	  return Slider;
	}(_react["default"].Component);

	exports["default"] = Slider;
} (slider));

(function (exports) {

	Object.defineProperty(exports, "__esModule", {
	  value: true
	});
	exports["default"] = void 0;

	var _slider = _interopRequireDefault(slider);

	function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { "default": obj }; }

	var _default = _slider["default"];
	exports["default"] = _default;
} (lib));

var ReactSlickSlider = /*@__PURE__*/getDefaultExportFromCjs(lib);

var Slider = styled(ReactSlickSlider)(templateObject_1$f || (templateObject_1$f = __makeTemplateObject(["\n  &.slick-slider .slick-track {\n    display: flex;\n  }\n\n  &.slick-slider .slick-dots {\n    position: static;\n  }\n\n  &.slick-slider .slick-dots li button::before {\n    font-size: 30px;\n    opacity: 1;\n    color: ", ";\n  }\n\n  &.slick-slider .slick-dots li.slick-active button::before {\n    color: ", ";\n    font-size: 30px;\n  }\n"], ["\n  &.slick-slider .slick-track {\n    display: flex;\n  }\n\n  &.slick-slider .slick-dots {\n    position: static;\n  }\n\n  &.slick-slider .slick-dots li button::before {\n    font-size: 30px;\n    opacity: 1;\n    color: ", ";\n  }\n\n  &.slick-slider .slick-dots li.slick-active button::before {\n    color: ", ";\n    font-size: 30px;\n  }\n"])), function (_a) {
    var theme = _a.theme;
    return theme.colors.carousel.dots.color;
}, function (_a) {
    var theme = _a.theme;
    return theme.colors.carousel.dots.active.color;
});
var templateObject_1$f;

var defaultSliderSettings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 3,
    slidesToScroll: 1,
    variableWidth: true,
    responsive: [
        {
            breakpoint: 768,
            settings: {
                slidesToShow: 1,
            },
        },
        {
            breakpoint: 1240,
            settings: {
                slidesToShow: 2,
            },
        },
    ],
};
var Carousel = function (_a) {
    var children = _a.children, _b = _a.showArrows, showArrows = _b === void 0 ? true : _b, _c = _a.disableLeftArrow, disableLeftArrow = _c === void 0 ? false : _c, _d = _a.disableRightArrow, disableRightArrow = _d === void 0 ? false : _d, sliderSettings = _a.sliderSettings;
    var slider = useRef(null);
    var settings = sliderSettings
        ? __assign$1(__assign$1({}, defaultSliderSettings), sliderSettings) : defaultSliderSettings;
    return (jsxs(Fragment, { children: [showArrows ? (jsx(SliderArrows, { sliderRef: slider, disableLeftArrow: disableLeftArrow, disableRightArrow: disableRightArrow })) : null, jsx(Slider, __assign$1({ ref: slider }, settings, { children: children }))] }));
};

var BaseCarousel = function (_a) {
    var cards = _a.cards;
    var _b = useIsScreenSize(), isScreenLG = _b.isScreenLG, isScreenXL = _b.isScreenXL, isScreenMD = _b.isScreenMD;
    var _c = useState(0), nextImage = _c[0], setNextImage = _c[1];
    var isScreenAboveSM = useIsScreenSize().isScreenAboveSM;
    var customSliderSettings = {
        infinite: false,
        accessibility: true,
        beforeChange: function (_, next) {
            setNextImage(next);
        },
        touchMove: !isScreenAboveSM,
        arrows: false,
    };
    if (!(cards === null || cards === void 0 ? void 0 : cards.length))
        return null;
    return (jsx(Carousel, __assign$1({ sliderSettings: customSliderSettings, showArrows: isScreenLG || isScreenXL || isScreenMD, disableLeftArrow: nextImage === 0, disableRightArrow: nextImage === cards.length - 3 }, { children: cards })));
};

var SubtitleComponent = function (_a) {
    var subtitle = _a.subtitle, mode = _a.mode;
    return (jsxs(Fragment, { children: [jsx(Gutter, { size: 16 }), jsx(Body3$2, __assign$1({ color: mode === 'light'
                    ? theme.crediteaColors.primary.neutral[50]
                    : theme.crediteaColors.primary.neutral[600] }, { children: subtitle }))] }));
};
var LinkComponent$1 = function (_a) {
    var linkAsButton = _a.linkAsButton, linkData = _a.linkData, nextLink = _a.nextLink, _b = _a.mode, mode = _b === void 0 ? 'dark' : _b;
    return linkAsButton ? (jsx(LinkAsButton, { NextLink: nextLink, linkData: linkData, mode: mode })) : (jsx(LinkWithArrow, { NextLink: nextLink, linkData: linkData, mode: mode }));
};

var Proportions;
(function (Proportions) {
    Proportions["symmetric"] = "symmetric";
    Proportions["asymmetricMd"] = "asymmetricMd";
    Proportions["asymmetricMdVideo"] = "asymmetricMdVideo";
    Proportions["asymmetricLg"] = "asymmetricLg";
    Proportions["asymmetricLgVideo"] = "asymmetricLgVideo";
})(Proportions || (Proportions = {}));

var SectionWrapper$2 = styled.div(templateObject_1$e || (templateObject_1$e = __makeTemplateObject(["\n  background-color: ", ";\n"], ["\n  background-color: ", ";\n"])), function (_a) {
    var bgColor = _a.bgColor;
    return bgColor;
});
var FeatureWrapper = styled(Row$1)(templateObject_2$b || (templateObject_2$b = __makeTemplateObject(["\n  &&& {\n    background-color: ", ";\n    ", "\n\n    ", "\n  }\n"], ["\n  &&& {\n    background-color: ", ";\n    ", "\n\n    ", "\n  }\n"])), function (_a) {
    var bgColor = _a.bgColor;
    return bgColor;
}, function (_a) {
    var bgColor = _a.bgColor;
    return bgColor !== '#FFFFFF' &&
        "\n      padding-top: 60px;\n      padding-bottom: 60px;\n    ";
}, function (_a) {
    var proportions = _a.proportions, theme = _a.theme, reverse = _a.reverse;
    return proportions === Proportions.asymmetricMd &&
        "\n    flex-direction: ".concat(reverse ? 'column-reverse' : 'column', ";\n\n    ").concat(theme.breakpoints.lg, " {\n      flex-direction:  ").concat(reverse ? 'row-reverse' : 'row', ";\n    }\n  ");
});
var ImageColumn = styled(Col)(templateObject_3$a || (templateObject_3$a = __makeTemplateObject(["\n  &&& {\n    display: ", ";\n    width: 100%;\n    height: 100%;\n    ", " {\n      display: flex;\n    }\n  }\n"], ["\n  &&& {\n    display: ", ";\n    width: 100%;\n    height: 100%;\n    ", " {\n      display: flex;\n    }\n  }\n"])), function (_a) {
    var hideImageOnMobile = _a.hideImageOnMobile;
    return (hideImageOnMobile ? 'none' : 'flex');
}, function (_a) {
    var theme = _a.theme;
    return theme.breakpoints.md;
});
var ImageWrapper$5 = styled.div(templateObject_4$9 || (templateObject_4$9 = __makeTemplateObject(["\n  height: ", ";\n  width: 100%;\n  position: relative;\n  margin-left: auto;\n  margin-right: auto;\n  overflow: hidden;\n\n  & > img {\n    object-fit: contain;\n  }\n\n  ", "\n\n  ", " {\n    ", "\n    ", "\n  }\n\n  ", " {\n    margin-right: ", ";\n    margin-left: ", ";\n\n    ", "\n  }\n\n  ", " {\n    width: ", ";\n    height: ", ";\n\n    ", "\n    ", "\n  }\n"], ["\n  height: ", ";\n  width: 100%;\n  position: relative;\n  margin-left: auto;\n  margin-right: auto;\n  overflow: hidden;\n\n  & > img {\n    object-fit: contain;\n  }\n\n  ", "\n\n  ", " {\n    ", "\n    ", "\n  }\n\n  ", " {\n    margin-right: ", ";\n    margin-left: ", ";\n\n    ", "\n  }\n\n  ", " {\n    width: ", ";\n    height: ", ";\n\n    ", "\n    ", "\n  }\n"])), function (_a) {
    var proportions = _a.proportions;
    var largeProportions = proportions === Proportions.asymmetricLg ||
        proportions === Proportions.asymmetricMdVideo ||
        proportions === Proportions.asymmetricLgVideo;
    return !largeProportions ? '380px' : '100%';
}, function (_a) {
    var proportions = _a.proportions;
    return proportions === Proportions.asymmetricLg &&
        "\n      min-height: 327px;\n  ";
}, function (_a) {
    var theme = _a.theme;
    return theme.breakpoints.sm;
}, function (_a) {
    var proportions = _a.proportions;
    return proportions === Proportions.asymmetricLg &&
        "\n      margin-bottom: unset;\n  ";
}, function (_a) {
    var proportions = _a.proportions;
    return proportions === Proportions.asymmetricMd &&
        "\n      height: 260px;\n      width: 340px; \n  ";
}, function (_a) {
    var theme = _a.theme;
    return theme.breakpoints.md;
}, function (props) { return (props.reverse ? '0px' : 'auto'); }, function (props) { return (props.reverse ? 'auto' : '0px'); }, function (_a) {
    var proportions = _a.proportions;
    return proportions === Proportions.asymmetricMd &&
        "\n      margin-left: auto;\n      margin-right: auto;\n  ";
}, function (_a) {
    var theme = _a.theme;
    return theme.breakpoints.lg;
}, function (_a) {
    var proportions = _a.proportions;
    return (!proportions || proportions === 'symmetric' ? '448px' : '100%');
}, function (_a) {
    var proportions = _a.proportions;
    return !proportions || proportions === Proportions.symmetric ? '448px' : '100%';
}, function (_a) {
    var proportions = _a.proportions;
    return proportions === Proportions.asymmetricLg &&
        "\n      min-height: 352px;\n  ";
}, function (_a) {
    var proportions = _a.proportions;
    return proportions === Proportions.asymmetricMd &&
        "\n      height: 328px;\n      width: 448px;\n  ";
});
var TextWrapper = styled.div(templateObject_5$7 || (templateObject_5$7 = __makeTemplateObject(["\n  ", "\n  ", "\n  ", "\n"], ["\n  ", "\n  ", "\n  ", "\n"])), function (_a) {
    var proportions = _a.proportions, theme = _a.theme;
    return (proportions === Proportions.asymmetricLgVideo ||
        proportions === Proportions.asymmetricMdVideo) &&
        "\n      padding-top: ".concat(theme.space[24], ";\n      \n      ").concat(theme.breakpoints.sm, " {\n        padding-top: ").concat(theme.space[0], ";\n      }\n    ");
}, function (_a) {
    var proportions = _a.proportions, theme = _a.theme, reverse = _a.reverse;
    return proportions === Proportions.asymmetricLg &&
        "\n      padding-top: ".concat(theme.space[24], ";\n      \n      ").concat(theme.breakpoints.sm, " {\n        ").concat(reverse && '&', " {\n          padding-top: ").concat(theme.space[0], ";\n        }\n      }\n  ");
}, function (_a) {
    var proportions = _a.proportions, theme = _a.theme, reverse = _a.reverse;
    return proportions === Proportions.asymmetricMd &&
        "    \n      ".concat(theme.breakpoints.sm, " {\n        ").concat(reverse && '&', " {\n          padding-bottom: ").concat(theme.space[24], ";\n        }\n        ").concat(!reverse && '&', " {\n          padding-top: ").concat(theme.space[24], ";\n        }\n      }\n  ");
});
var LinkWrapper$2 = styled.div(templateObject_6$7 || (templateObject_6$7 = __makeTemplateObject(["\n  margin-top: 32px;\n  ", " {\n    margin-top: 40px;\n  }\n\n  & > button {\n    width: auto;\n  }\n"], ["\n  margin-top: 32px;\n  ", " {\n    margin-top: 40px;\n  }\n\n  & > button {\n    width: auto;\n  }\n"])), function (_a) {
    var theme = _a.theme;
    return theme.breakpoints.sm;
});
var LinksArrayWrapper = styled.div(templateObject_9$5 || (templateObject_9$5 = __makeTemplateObject(["\n  display: flex;\n  flex-direction: column;\n  gap: 10px 10px;\n\n  ", "\n\n  ", "\n  \n  & > div {\n    width: auto;\n  }\n"], ["\n  display: flex;\n  flex-direction: column;\n  gap: 10px 10px;\n\n  ", "\n\n  ", "\n  \n  & > div {\n    width: auto;\n  }\n"])), function (_a) {
    var theme = _a.theme;
    return css(templateObject_7$7 || (templateObject_7$7 = __makeTemplateObject(["\n    ", " {\n      display: grid;\n      grid-template-columns: repeat(2, 1fr);\n    }\n  "], ["\n    ", " {\n      display: grid;\n      grid-template-columns: repeat(2, 1fr);\n    }\n  "])), theme.breakpoints.sm);
}, function (_a) {
    var theme = _a.theme;
    return css(templateObject_8$5 || (templateObject_8$5 = __makeTemplateObject(["\n    ", " {\n      grid-template-columns: repeat(3, 1fr);\n    }\n  "], ["\n    ", " {\n      grid-template-columns: repeat(3, 1fr);\n    }\n  "])), theme.breakpoints.lg);
});
var TextContent$1 = styled.div(templateObject_10$4 || (templateObject_10$4 = __makeTemplateObject(["\n  > :is(h1, h2, h3, h4, h5, h6, p, ul) {\n    color: ", ";\n    *:not(a) {\n      color: inherit;\n    }\n  }\n"], ["\n  > :is(h1, h2, h3, h4, h5, h6, p, ul) {\n    color: ", ";\n    *:not(a) {\n      color: inherit;\n    }\n  }\n"])), function (_a) {
    var textColor = _a.textColor;
    return textColor;
});
var templateObject_1$e, templateObject_2$b, templateObject_3$a, templateObject_4$9, templateObject_5$7, templateObject_6$7, templateObject_7$7, templateObject_8$5, templateObject_9$5, templateObject_10$4;

var Feature = function (_a) {
    var title = _a.title, subtitle = _a.subtitle, reverse_position = _a.reverse_position, linkData = _a.linkData, nextLink = _a.nextLink, nextImage = _a.nextImage, textContent = _a.textContent, proportions = _a.proportions, linkAsButton = _a.linkAsButton, _b = _a.bgColor, bgColor = _b === void 0 ? theme.colors.feature.defaultBackground : _b, _c = _a.textColor, textColor = _c === void 0 ? theme.colors.feature.defaultColor : _c, hideImageOnMobile = _a.hideImageOnMobile, PortableTextWrapper = _a.PortableTextWrapper, videoUrl = _a.videoUrl, linksArray = _a.linksArray, buttonConfig = _a.buttonConfig;
    var shouldShowLink = (linkData === null || linkData === void 0 ? void 0 : linkData.linkTitle) && (linkData.externalUrl || linkData.internalLink);
    var mode = textColor === theme.crediteaColors.primary.neutral[50] ? 'light' : 'dark';
    var videoProportions = proportions === Proportions.asymmetricMdVideo || proportions === Proportions.asymmetricLgVideo;
    // if symmetric
    var imageColumnSM = 4;
    var imageColumnMD = 6;
    var imageColumnLG = 6;
    var contentColumnSM = 6;
    var contentColumnMD = 6;
    var contentColumnLG = 6;
    if (proportions === Proportions.asymmetricMd) {
        imageColumnSM = 8;
        imageColumnMD = 12;
        imageColumnLG = 5;
        contentColumnSM = 8;
        contentColumnMD = 12;
        contentColumnLG = 7;
    }
    if (proportions === Proportions.asymmetricMdVideo) {
        imageColumnSM = 4;
        imageColumnMD = 6;
        imageColumnLG = 7;
        contentColumnSM = hideImageOnMobile ? 8 : 4;
        contentColumnMD = 6;
        contentColumnLG = 5;
    }
    if (proportions === Proportions.asymmetricLg) {
        imageColumnSM = 3;
        imageColumnMD = 4;
        imageColumnLG = 4;
        contentColumnSM = 5;
        contentColumnMD = 8;
        contentColumnLG = 8;
    }
    if (proportions === Proportions.asymmetricLgVideo) {
        imageColumnSM = 5;
        imageColumnMD = 8;
        imageColumnLG = 8;
        contentColumnSM = hideImageOnMobile ? 8 : 3;
        contentColumnMD = 4;
        contentColumnLG = 4;
    }
    return (jsx(SectionWrapper$2, __assign$1({ bgColor: bgColor }, { children: jsxs(Container$1, { children: [jsx(Gutter, { size: "grid" }), jsxs(FeatureWrapper, __assign$1({ start: "xs", center: "sm", reverse: reverse_position !== null && reverse_position !== void 0 ? reverse_position : false, proportions: proportions, bgColor: bgColor, middle: "xs" }, { children: [jsx(ImageColumn, __assign$1({ xs: 4, sm: imageColumnSM, md: imageColumnMD, lg: imageColumnLG, paddingTop: 1, hideImageOnMobile: Boolean(hideImageOnMobile) }, { children: jsxs(ImageWrapper$5, __assign$1({ reverse: reverse_position, proportions: proportions }, { children: [nextImage, jsx(VideoPlayer, { url: videoUrl })] })) })), jsx(Col, __assign$1({ xs: 4, sm: contentColumnSM, md: contentColumnMD, lg: contentColumnLG, justify: Col.Justify.Center, paddingTop: 1 }, { children: jsxs(TextWrapper, __assign$1({ proportions: proportions, reverse: reverse_position }, { children: [proportions === Proportions.symmetric || videoProportions ? (jsx(Header3$2, __assign$1({ color: textColor }, { children: title }))) : (jsx(Header2, __assign$1({ color: textColor }, { children: title }))), subtitle && jsx(SubtitleComponent, { mode: mode, subtitle: subtitle }), textContent && !hasEmptyTextContent(textContent) && PortableTextWrapper && (jsxs(TextContent$1, __assign$1({ textColor: textColor }, { children: [jsx(Gutter, { size: 24 }), jsx(PortableTextWrapper, { portableText: textContent.textRaw, mode: mode })] }))), shouldShowLink && (jsx(LinkWrapper$2, { children: jsx(LinkComponent$1, { linkAsButton: linkAsButton, nextLink: nextLink, linkData: linkData, mode: mode }) })), linksArray && linksArray.length > 0 && (jsxs(Fragment, { children: [jsx(Gutter, { size: 24, sizeMobile: 16 }), jsx(LinksArrayWrapper, { children: linksArray === null || linksArray === void 0 ? void 0 : linksArray.map(function (link, key) { return (jsx(ButtonOrDownloadFile, { nextLink: nextLink, linkData: link, buttonConfig: buttonConfig }, key)); }) })] }))] })) }))] }))] }) })));
};

var Wrapper$1 = styled.section(templateObject_1$d || (templateObject_1$d = __makeTemplateObject(["\n  background-color: ", ";\n  padding-top: ", ";\n  padding-bottom: ", ";\n  ", " {\n    padding-bottom: ", ";\n  }\n"], ["\n  background-color: ", ";\n  padding-top: ", ";\n  padding-bottom: ", ";\n  ", " {\n    padding-bottom: ", ";\n  }\n"])), function (_a) {
    var backgroundColor = _a.backgroundColor;
    return backgroundColor;
}, function (_a) {
    var theme = _a.theme;
    return theme.space[5];
}, function (_a) {
    var theme = _a.theme;
    return theme.space[32];
}, function (_a) {
    var theme = _a.theme;
    return theme.breakpoints.sm;
}, function (_a) {
    var theme = _a.theme;
    return theme.space[40];
});
var templateObject_1$d;

var FeatureList = function (_a) {
    var variant = _a.variant, features = _a.features, _b = _a.backgroundColor, backgroundColor = _b === void 0 ? theme.colors.featureList.defaultBackground : _b, _c = _a.textColor, textColor = _c === void 0 ? theme.colors.featureList.defaultColor : _c;
    var isScreenXS = useIsScreenSize().isScreenXS;
    return (jsxs(Wrapper$1, __assign$1({ backgroundColor: backgroundColor }, { children: [jsx(Gutter, { size: "grid" }), jsx(Container$1, { children: jsx(Row$1, __assign$1({ center: "xs", between: "md" }, { children: features === null || features === void 0 ? void 0 : features.map(function (feature, key) { return (jsx(Col, __assign$1({ direction: "row", xs: 4, sm: true, md: variant === 'text' ? 4 : true, lg: variant === 'text' ? 4 : true, justify: variant === 'text' && isScreenXS ? Col.Justify.Start : Col.Justify.Center }, { children: variant === 'text' ? (jsxs(Fragment, { children: [jsx(Icon$4, { name: backgroundColor === crediteaColors.primary.brandBlue[600]
                                        ? 'Check'
                                        : 'CheckBlue' }), jsx(Gutter, { size: 8 }), jsx(Caption1$1, __assign$1({ color: textColor }, { children: feature }))] })) : (jsx("div", { children: feature })) }), key)); }) })) })] })));
};

var SectionWrapper$1 = styled.div(templateObject_1$c || (templateObject_1$c = __makeTemplateObject(["\n  background-color: ", ";\n  padding: 20px 0 60px 0;\n  ", " {\n    padding: 60px 0;\n  }\n"], ["\n  background-color: ", ";\n  padding: 20px 0 60px 0;\n  ", " {\n    padding: 60px 0;\n  }\n"])), function (_a) {
    var backgroundColor = _a.backgroundColor;
    return backgroundColor;
}, theme.breakpoints.lg);
var CardsContainer = styled.div(templateObject_3$9 || (templateObject_3$9 = __makeTemplateObject(["\n  display: flex;\n  flex-direction: column;\n  gap: 24px 24px;\n  ", "\n"], ["\n  display: flex;\n  flex-direction: column;\n  gap: 24px 24px;\n  ", "\n"])), function (_a) {
    var theme = _a.theme;
    return css(templateObject_2$a || (templateObject_2$a = __makeTemplateObject(["\n    ", " {\n      display: grid;\n      grid-template-columns: repeat(2, 1fr);\n    }\n    ", " {\n      gap: 32px;\n      grid-template-columns: repeat(auto-fit, minmax(190px, 1fr));\n    }\n  "], ["\n    ", " {\n      display: grid;\n      grid-template-columns: repeat(2, 1fr);\n    }\n    ", " {\n      gap: 32px;\n      grid-template-columns: repeat(auto-fit, minmax(190px, 1fr));\n    }\n  "])), theme.breakpoints.sm, theme.breakpoints.lg);
});
var templateObject_1$c, templateObject_2$a, templateObject_3$9;

var FlippingCards = function (_a) {
    var heading = _a.heading, _b = _a.textColor, textColor = _b === void 0 ? theme.colors.flippingCards.textColor : _b, _c = _a.backgroundColor, backgroundColor = _c === void 0 ? theme.colors.flippingCards.defaultBackground : _c, elements = _a.elements;
    var isScreenDesktop = useIsScreenSize().isScreenDesktop;
    return (jsx(SectionWrapper$1, __assign$1({ backgroundColor: backgroundColor }, { children: jsxs(Container$1, { children: [heading && (jsxs(Fragment, { children: [jsx(Header3$2, __assign$1({ align: "left", color: textColor }, { children: heading })), isScreenDesktop ? jsx(Gutter, { size: 48 }) : jsx(Gutter, { size: 24 })] })), jsx(CardsContainer, __assign$1({ "data-testid": "cards-container" }, { children: elements === null || elements === void 0 ? void 0 : elements.map(function (item, i) {
                        var _a;
                        return (jsx(FlippingCard, { backgroundColor: backgroundColor, icon: item.iconName, iconBackgroundColor: (_a = item.iconColor) === null || _a === void 0 ? void 0 : _a.value, title: item.title, textColor: textColor, reversedText: item.reversedText }, i));
                    }) }))] }) })));
};

var Footer$1 = styled.footer(templateObject_1$b || (templateObject_1$b = __makeTemplateObject(["\n  position: absolute;\n  bottom: 0;\n  width: 100%;\n  background-color: ", ";\n"], ["\n  position: absolute;\n  bottom: 0;\n  width: 100%;\n  background-color: ", ";\n"])), function (props) { return props.backgroundColor; });
var ContactsRow = styled(Row$1)(templateObject_2$9 || (templateObject_2$9 = __makeTemplateObject(["\n  &&& {\n    flex-direction: column;\n    ", "\n  }\n"], ["\n  &&& {\n    flex-direction: column;\n    ", "\n  }\n"])), makeMediaQuery('md', 'flex-direction: row;'));
var templateObject_1$b, templateObject_2$9;

var Footer = React.forwardRef(function (_a, ref) {
    var title = _a.title, email = _a.email, phone = _a.phone, _b = _a.backgroundColor, backgroundColor = _b === void 0 ? theme.colors.footer.defaultBackground : _b;
    return (jsx(Footer$1, __assign$1({ id: "footer-menu", ref: ref, backgroundColor: backgroundColor }, { children: jsxs(Container$1, { children: [jsx(Gutter, { size: 24 }), jsxs(Row$1, __assign$1({ middle: "xs", between: "xs" }, { children: [jsx(Col, __assign$1({ xs: 4, md: 4 }, { children: jsx(Body2, { children: title }) })), jsx(Col, __assign$1({ xs: 4, md: 8 }, { children: jsxs(ContactsRow, __assign$1({ end: "md" }, { children: [jsx(Col, { children: jsx("a", __assign$1({ href: "tel:".concat(phone) }, { children: jsx(Button$2, __assign$1({ variant: "text", withoutSurroundingSpacing: true, autoWidth: "xs", prependIcon: "Phone" }, { children: phone })) })) }), jsx(Col, { children: jsx("a", __assign$1({ href: "mailto:".concat(email) }, { children: jsx(Button$2, __assign$1({ variant: "text", withoutSurroundingSpacing: true, autoWidth: "xs", prependIcon: "Email" }, { children: email })) })) })] })) }))] })), jsx(Gutter, { size: 24 })] }) })));
});

var Header$1 = styled.header(templateObject_3$8 || (templateObject_3$8 = __makeTemplateObject(["\n  position: absolute;\n  top: ", ";\n  width: 100%;\n  z-index: ", ";\n  background-color: ", ";\n  transform: translateY(-100%);\n  transition: background-color 0.3s ease-in;\n\n  ", " {\n    top: ", ";\n  }\n\n  ", "\n\n  ", "\n"], ["\n  position: absolute;\n  top: ", ";\n  width: 100%;\n  z-index: ", ";\n  background-color: ", ";\n  transform: translateY(-100%);\n  transition: background-color 0.3s ease-in;\n\n  ", " {\n    top: ", ";\n  }\n\n  ", "\n\n  ", "\n"])), function (props) { return (props.hasPromoBar ? '150px' : '74px'); }, function (_a) {
    var theme = _a.theme;
    return theme.customZIndex.secondary;
}, function (props) { return (props.backgroundColor ? props.backgroundColor : 'transparent'); }, function (_a) {
    var theme = _a.theme;
    return theme.breakpoints.sm;
}, function (props) { return (props.hasPromoBar ? '124px' : '74px'); }, function (_a) {
    var offset = _a.offset, hasPromoBar = _a.hasPromoBar, stickyBackgroundColor = _a.stickyBackgroundColor;
    return offset > 100 && css(templateObject_1$a || (templateObject_1$a = __makeTemplateObject(["\n      position: fixed;\n      top: ", ";\n      background-color: ", ";\n\n      ", " {\n        top: ", ";\n      }\n    "], ["\n      position: fixed;\n      top: ", ";\n      background-color: ", ";\n\n      ", " {\n        top: ", ";\n      }\n    "])), hasPromoBar ? '76px' : '0', function (_a) {
        var theme = _a.theme;
        return stickyBackgroundColor || theme.colors.header.defaultStickyBackground;
    }, function (_a) {
        var theme = _a.theme;
        return theme.breakpoints.sm;
    }, hasPromoBar ? '50px' : '0');
}, function (_a) {
    var offset = _a.offset;
    return offset > 200 && css(templateObject_2$8 || (templateObject_2$8 = __makeTemplateObject(["\n      transform: translateY(0);\n      transition: transform 0.3s ease-in;\n    "], ["\n      transform: translateY(0);\n      transition: transform 0.3s ease-in;\n    "])));
});
var LogoWrapper = styled.div(templateObject_4$8 || (templateObject_4$8 = __makeTemplateObject(["\n  margin-right: ", ";\n"], ["\n  margin-right: ", ";\n"])), function (_a) {
    var theme = _a.theme;
    return theme.space[60];
});
var templateObject_1$a, templateObject_2$8, templateObject_3$8, templateObject_4$8;

var Header = function (_a) {
    var children = _a.children, _b = _a.hasPromoBar, hasPromoBar = _b === void 0 ? false : _b, logoLink = _a.logoLink, rest = __rest$1(_a, ["children", "hasPromoBar", "logoLink"]);
    var offsetY = useScroll().y;
    return (jsx(Header$1, __assign$1({ id: "header-menu", offset: offsetY || 1, hasPromoBar: hasPromoBar }, rest, { children: jsx(Container$1, { children: jsxs(Row$1, __assign$1({ middle: "xs" }, { children: [jsxs(Col, __assign$1({ justify: Col.Justify.Center, xs: 2, sm: 3, md: 4, xl: 3 }, { children: [jsx(Gutter, { size: 24 }), jsx(LogoWrapper, { children: logoLink ? (jsx("a", __assign$1({ href: logoLink }, { children: jsx(Icon$4, { name: "LogoCreditea" }) }))) : (jsx(Icon$4, { name: "LogoCreditea" })) }), jsx(Gutter, { size: 20 })] })), jsx(Col, __assign$1({ xs: 2, sm: 5, md: 8, xl: 9 }, { children: children }))] })) }) })));
};

var roundedCornerCss = function () { return css(templateObject_1$9 || (templateObject_1$9 = __makeTemplateObject(["\n  border-bottom-right-radius: 80px;\n  ", " {\n    border-bottom-right-radius: 100px;\n  }\n  ", " {\n    border-bottom-right-radius: 150px;\n  }\n"], ["\n  border-bottom-right-radius: 80px;\n  ", " {\n    border-bottom-right-radius: 100px;\n  }\n  ", " {\n    border-bottom-right-radius: 150px;\n  }\n"])), function (_a) {
    var theme = _a.theme;
    return theme.breakpoints.sm;
}, function (_a) {
    var theme = _a.theme;
    return theme.breakpoints.lg;
}); };
var navbarHeight = 74;
var promoBarMobileHeight = 80;
var promoBarDesktopHeight = 58;
var mobileSpacing = 32;
var tabletSpacing = 60;
var desktopSpacing = 0;
var navbarHeightWithPromoBar = function (hasContent) { return css(templateObject_2$7 || (templateObject_2$7 = __makeTemplateObject(["\n  padding-top: ", "px;\n  ", " {\n    padding-top: ", "px;\n  }\n  ", " {\n    padding-top: ", "px;\n  }\n"], ["\n  padding-top: ", "px;\n  ", " {\n    padding-top: ", "px;\n  }\n  ", " {\n    padding-top: ", "px;\n  }\n"])), navbarHeight + promoBarMobileHeight + (hasContent ? mobileSpacing : 0), function (_a) {
    var theme = _a.theme;
    return theme.breakpoints.sm;
}, navbarHeight + promoBarDesktopHeight + (hasContent ? tabletSpacing : 0), function (_a) {
    var theme = _a.theme;
    return theme.breakpoints.md;
}, navbarHeight + promoBarDesktopHeight + desktopSpacing); };
var navbarHeightWithSpacing = function (hasContent) { return css(templateObject_3$7 || (templateObject_3$7 = __makeTemplateObject(["\n  padding-top: ", "px;\n  ", " {\n    padding-top: ", "px;\n  }\n  ", " {\n    padding-top: ", "px;\n  }\n"], ["\n  padding-top: ", "px;\n  ", " {\n    padding-top: ", "px;\n  }\n  ", " {\n    padding-top: ", "px;\n  }\n"])), navbarHeight + (hasContent ? mobileSpacing : 0), function (_a) {
    var theme = _a.theme;
    return theme.breakpoints.sm;
}, navbarHeight + (hasContent ? tabletSpacing : 0), function (_a) {
    var theme = _a.theme;
    return theme.breakpoints.md;
}, navbarHeight + desktopSpacing); };
var HeroWrapper = styled.section(templateObject_4$7 || (templateObject_4$7 = __makeTemplateObject(["\n  background-color: ", ";\n  display: flex;\n  width: 100%;\n  ", "\n  ", ";\n  ", " {\n    ", "\n  }\n"], ["\n  background-color: ", ";\n  display: flex;\n  width: 100%;\n  ", "\n  ", ";\n  ", " {\n    ", "\n  }\n"])), function (_a) {
    var backgroundColor = _a.backgroundColor;
    return backgroundColor;
}, function (_a) {
    var rightBottomCornerRounded = _a.rightBottomCornerRounded;
    return rightBottomCornerRounded && roundedCornerCss();
}, function (_a) {
    var isPromoBar = _a.isPromoBar, _b = _a.hasContent, hasContent = _b === void 0 ? true : _b;
    return isPromoBar ? navbarHeightWithPromoBar(hasContent) : navbarHeightWithSpacing(hasContent);
}, function (_a) {
    var theme = _a.theme;
    return theme.breakpoints.sm;
}, function (_a) {
    var isPromoBar = _a.isPromoBar, promoBarOnlyMobile = _a.promoBarOnlyMobile, _b = _a.hasContent, hasContent = _b === void 0 ? true : _b;
    return isPromoBar && !promoBarOnlyMobile
        ? navbarHeightWithPromoBar(hasContent)
        : navbarHeightWithSpacing(hasContent);
});
var HeroTitle = styled.div(templateObject_5$6 || (templateObject_5$6 = __makeTemplateObject(["\n  text-align: ", ";\n  ", " {\n    text-align: left;\n  }\n"], ["\n  text-align: ", ";\n  ", " {\n    text-align: left;\n  }\n"])), function (props) { return (props.titleAlignLeft ? 'left' : 'center'); }, function (_a) {
    var theme = _a.theme;
    return theme.breakpoints.md;
});
var HeroDescription = styled.div(templateObject_6$6 || (templateObject_6$6 = __makeTemplateObject(["\n  text-align: ", ";\n  ", " {\n    text-align: left;\n  }\n\n  > :is(h1, h2, h3, h4, h5, h6, p, ul) {\n    color: ", ";\n    *:not(a) {\n      color: inherit;\n    }\n  }\n"], ["\n  text-align: ", ";\n  ", " {\n    text-align: left;\n  }\n\n  > :is(h1, h2, h3, h4, h5, h6, p, ul) {\n    color: ", ";\n    *:not(a) {\n      color: inherit;\n    }\n  }\n"])), function (props) { return (props.titleAlignLeft ? 'left' : 'center'); }, function (_a) {
    var theme = _a.theme;
    return theme.breakpoints.md;
}, function (_a) {
    var textColor = _a.textColor;
    return textColor;
});
var HeroButton = styled.div(templateObject_7$6 || (templateObject_7$6 = __makeTemplateObject(["\n  display: flex;\n  justify-content: center;\n  ", " {\n    justify-content: flex-start;\n  }\n"], ["\n  display: flex;\n  justify-content: center;\n  ", " {\n    justify-content: flex-start;\n  }\n"])), function (_a) {
    var theme = _a.theme;
    return theme.breakpoints.md;
});
var ImageWrapper$4 = styled.div(templateObject_8$4 || (templateObject_8$4 = __makeTemplateObject(["\n  height: 280px;\n  width: 100%;\n  position: relative;\n  margin-left: auto;\n  margin-right: auto;\n  display: ", ";\n\n  & > img {\n    object-fit: contain;\n  }\n\n  ", " {\n    height: 290px;\n  }\n\n  ", " {\n    display: flex;\n  }\n\n  ", " {\n    height: 360px;\n    max-width: 544px;\n  }\n"], ["\n  height: 280px;\n  width: 100%;\n  position: relative;\n  margin-left: auto;\n  margin-right: auto;\n  display: ", ";\n\n  & > img {\n    object-fit: contain;\n  }\n\n  ", " {\n    height: 290px;\n  }\n\n  ", " {\n    display: flex;\n  }\n\n  ", " {\n    height: 360px;\n    max-width: 544px;\n  }\n"])), function (_a) {
    var hideImageOnMobile = _a.hideImageOnMobile;
    return (hideImageOnMobile ? 'none' : 'flex');
}, function (_a) {
    var theme = _a.theme;
    return theme.breakpoints.sm;
}, function (_a) {
    var theme = _a.theme;
    return theme.breakpoints.md;
}, function (_a) {
    var theme = _a.theme;
    return theme.breakpoints.lg;
});
var ColLoanFormWrapper = styled.div(templateObject_9$4 || (templateObject_9$4 = __makeTemplateObject(["\n  margin-top: -16px;\n  ", " {\n    margin-top: -24px;\n  }\n  ", " {\n    margin-top: 0;\n  }\n"], ["\n  margin-top: -16px;\n  ", " {\n    margin-top: -24px;\n  }\n  ", " {\n    margin-top: 0;\n  }\n"])), function (_a) {
    var theme = _a.theme;
    return theme.breakpoints.sm;
}, function (_a) {
    var theme = _a.theme;
    return theme.breakpoints.md;
});
var RowLoanFormWrapper = styled.div(templateObject_10$3 || (templateObject_10$3 = __makeTemplateObject(["\n  margin-top: -16px;\n  ", " {\n    margin-top: -24px;\n  }\n  ", " {\n    margin-top: -32px;\n  }\n"], ["\n  margin-top: -16px;\n  ", " {\n    margin-top: -24px;\n  }\n  ", " {\n    margin-top: -32px;\n  }\n"])), function (_a) {
    var theme = _a.theme;
    return theme.breakpoints.sm;
}, function (_a) {
    var theme = _a.theme;
    return theme.breakpoints.md;
});
var TopPaddingForDesktop = styled.div(templateObject_11$2 || (templateObject_11$2 = __makeTemplateObject(["\n  ", " {\n    padding-top: ", ";\n  }\n"], ["\n  ", " {\n    padding-top: ", ";\n  }\n"])), function (_a) {
    var theme = _a.theme;
    return theme.breakpoints.md;
}, function (_a) {
    var topPadding = _a.topPadding;
    return "".concat(topPadding, "px");
});
var templateObject_1$9, templateObject_2$7, templateObject_3$7, templateObject_4$7, templateObject_5$6, templateObject_6$6, templateObject_7$6, templateObject_8$4, templateObject_9$4, templateObject_10$3, templateObject_11$2;

var HeroButtonWrapper = function (_a) {
    var children = _a.children;
    return (jsxs(Fragment, { children: [jsx(HeroButton, { children: children }), jsx(Gutter, { size: 24 })] }));
};
var HeroTextSection = function (_a) {
    var _b, _c, _d, _e;
    var title = _a.title, textContent = _a.textContent, titleColor = _a.titleColor, textColor = _a.textColor, _f = _a.titleAlignLeft, titleAlignLeft = _f === void 0 ? false : _f, _g = _a.heroWithButton, heroWithButton = _g === void 0 ? false : _g;
    var hasEmptyTextContent = !((_b = textContent === null || textContent === void 0 ? void 0 : textContent.props) === null || _b === void 0 ? void 0 : _b.portableText) ||
        (((_d = (_c = textContent === null || textContent === void 0 ? void 0 : textContent.props) === null || _c === void 0 ? void 0 : _c.portableText) === null || _d === void 0 ? void 0 : _d.length) === 1 &&
            ((_e = textContent === null || textContent === void 0 ? void 0 : textContent.props) === null || _e === void 0 ? void 0 : _e.portableText[0].children[0].text) === '');
    return (jsxs(Fragment, { children: [title && (jsxs(Fragment, { children: [!hasEmptyTextContent && heroWithButton && jsx(Gutter, { size: 24 }), jsx(HeroTitle, __assign$1({ titleAlignLeft: titleAlignLeft }, { children: jsx(Header1, { color: titleColor, dangerouslySetInnerHTML: { __html: title } }) })), hasEmptyTextContent && !heroWithButton && jsx(Gutter, { size: 24 })] })), !hasEmptyTextContent && (jsxs(Fragment, { children: [jsx(Gutter, { size: 24 }), jsx(HeroDescription, __assign$1({ textColor: textColor, titleAlignLeft: titleAlignLeft }, { children: textContent })), jsx(Gutter, { size: 32 })] }))] }));
};

var checkIfContentHas = function (content, contentType) { var _a; return (_a = Object.keys(content)) === null || _a === void 0 ? void 0 : _a.some(function (key) { return key === contentType; }); };
var HeroContentWrapper = function (_a) {
    var size = _a.size, children = _a.children;
    return (jsx(Col, __assign$1({ xs: 4, md: size }, { children: children })));
};
var CreateHeroTree = function (_a) {
    var content = _a.content, contentWithButton = _a.contentWithButton, isRowContent = _a.isRowContent, textColor = _a.textColor, titleColor = _a.titleColor, titleAlignLeft = _a.titleAlignLeft;
    var key = content[0], value = content[1];
    switch (key) {
        case 'textSection':
            return (jsx(HeroTextSection, __assign$1({}, value, { heroWithButton: contentWithButton, textColor: textColor, titleColor: titleColor, titleAlignLeft: titleAlignLeft })));
        case 'button':
            return jsx(HeroButtonWrapper, { children: value });
        case 'image':
            return jsx(ImageWrapper$4, __assign$1({ hideImageOnMobile: value.hideImageOnMobile }, { children: value.image }));
        case 'loanForm':
            if (!isRowContent)
                return jsx(ColLoanFormWrapper, { children: value });
            return jsx(RowLoanFormWrapper, { children: value });
        case 'HeroSearchInput':
            return value;
        case 'downloadAppButtons':
            return value;
        default:
            return null;
    }
};
var HeroContent = function (_a) {
    var content = _a.content, textColor = _a.textColor, titleColor = _a.titleColor, titleAlignLeft = _a.titleAlignLeft;
    var contentWithButton = checkIfContentHas(content, 'button');
    var isRowContent = content.size === 12;
    return (jsx(HeroContentWrapper, __assign$1({ size: (content === null || content === void 0 ? void 0 : content.size) || 6 }, { children: Object.entries(content).map(function (item) { return (jsx(CreateHeroTree, { content: item, textColor: textColor, contentWithButton: contentWithButton, isRowContent: isRowContent, titleColor: titleColor, titleAlignLeft: titleAlignLeft }, item[0])); }) })));
};

var anythingButTheImage = ['textSection', 'selectSection', 'button'];
var checkAnythingButImage = function (content) {
    var contentHasAnthingButImage = content === null || content === void 0 ? void 0 : content.map(function (data) {
        return Object.keys(data).every(function (key) { return anythingButTheImage.includes(key); });
    });
    return contentHasAnthingButImage
        ? contentHasAnthingButImage.some(function (booleanValue) { return booleanValue === true; })
        : true;
};
var Hero = function (_a) {
    var backgroundColor = _a.backgroundColor, textColor = _a.textColor, titleColor = _a.titleColor, _b = _a.rightBottomCornerRounded, rightBottomCornerRounded = _b === void 0 ? false : _b, _c = _a.align, align = _c === void 0 ? 'top' : _c, topPadding = _a.topPadding, bottomPadding = _a.bottomPadding, content = _a.content, isPromoBar = _a.isPromoBar, promoBarOnlyMobile = _a.promoBarOnlyMobile, titleAlignLeft = _a.titleAlignLeft;
    var hasAnythingButTheImage = checkAnythingButImage(content);
    return (jsx(HeroWrapper, __assign$1({ backgroundColor: backgroundColor, rightBottomCornerRounded: rightBottomCornerRounded, isPromoBar: isPromoBar, promoBarOnlyMobile: promoBarOnlyMobile, hasContent: content.length > 0 }, { children: jsxs(Container$1, { children: [jsx(TopPaddingForDesktop, { topPadding: topPadding }), jsx(Row$1, __assign$1({ bottom: align === 'bottom' ? 'xs' : undefined, top: align === 'top' ? 'xs' : undefined, middle: align === 'middle' ? 'xs' : undefined, end: hasAnythingButTheImage ? undefined : 'xs' }, { children: content === null || content === void 0 ? void 0 : content.map(function (contentItem, key) { return (jsx(HeroContent, { content: contentItem, textColor: textColor, titleColor: titleColor, titleAlignLeft: titleAlignLeft }, key)); }) })), bottomPadding && jsx(Gutter, { size: bottomPadding })] }) })));
};

var LastArticleForCz = function (_a) {
    var featuredPost = _a.featuredPost, language = _a.language, nextLink = _a.nextLink;
    return (jsx(Container$1, __assign$1({ "aria-label": "Latest articles" }, { children: jsx(Row$1, { children: jsxs(Col, __assign$1({ xs: 4, sm: 8, lg: 12, "aria-label": "Latest blog post" }, { children: [jsx(BlogCard, __assign$1({}, featuredPost, { type: "large", NextLink: nextLink, language: language })), jsx(Gutter, { size: 60 })] })) }) })));
};

var LatestArticlesContainer = styled.div(templateObject_1$8 || (templateObject_1$8 = __makeTemplateObject(["\n  width: 100%;\n  background-color: ", ";\n  ", "\n"], ["\n  width: 100%;\n  background-color: ", ";\n  ", "\n"])), function (_a) {
    var theme = _a.theme;
    return theme.colors.latestArticles.backgroundColor;
}, makeMediaQuery('md', 'padding: 0 24px'));
var templateObject_1$8;

var LatestArticlesContent = function (_a) {
    var headline = _a.headline, allArticles = _a.allArticles, language = _a.language;
    return (jsxs(Fragment, { children: [jsx(Gutter, { size: 60 }), jsx(Header4, { children: headline }), jsx(Gutter, { size: 48 }), jsx(Row$1, { children: allArticles === null || allArticles === void 0 ? void 0 : allArticles.map(function (article) { return (jsx(Col, __assign$1({ xs: 4, sm: 2, md: 3, lg: 12, "aria-label": "Blog post", paddingTop: 24, paddingLeft: 24 }, { children: jsx(BlogCard, __assign$1({}, article, { type: "small", language: language })) }), article.title)); }) }), jsx(Gutter, { size: 60 })] }));
};
var LatestArticles = function (_a) {
    var allArticles = _a.allArticles, featuredPost = _a.featuredPost, language = _a.language, _b = _a.headline, headline = _b === void 0 ? 'Latest articles' : _b, nextLink = _a.nextLink;
    var isScreenDesktop = useIsScreenSize().isScreenDesktop;
    if (!isScreenDesktop) {
        return (jsxs(Fragment, { children: [jsxs(Container$1, { children: [jsx(BlogCard, __assign$1({}, featuredPost, { type: "large", NextLink: nextLink, language: language })), jsx(Gutter, { size: 100 })] }), jsx(LatestArticlesContainer, { children: jsx(Container$1, { children: jsx(LatestArticlesContent, { allArticles: allArticles, headline: headline, language: language }) }) })] }));
    }
    return (jsx(Container$1, __assign$1({ "aria-label": "Latest articles" }, { children: jsxs(Row$1, { children: [jsx(Col, __assign$1({ xs: 4, sm: 8, lg: 7, "aria-label": "Latest blog post" }, { children: jsx(BlogCard, __assign$1({}, featuredPost, { type: "large", NextLink: nextLink, language: language })) })), jsx(Col, __assign$1({ xs: 4, sm: 8, lg: 5 }, { children: jsx(LatestArticlesContainer, { children: jsx(LatestArticlesContent, { allArticles: allArticles, headline: headline, language: language }) }) }))] }) })));
};

var Markets;
(function (Markets) {
    Markets["Estonia"] = "EE";
    Markets["Latvia"] = "LV";
    Markets["Lithuania"] = "LT";
    Markets["Mexico"] = "MX";
    Markets["CzechRepublic"] = "CZ";
})(Markets || (Markets = {}));

var FormWrapper$1 = styled.section(templateObject_1$7 || (templateObject_1$7 = __makeTemplateObject(["\n  ", "\n"], ["\n  ", "\n"])), function (_a) {
    var theme = _a.theme;
    return "\n    padding: 0 ".concat(theme.space[24], ";\n    ").concat(theme.breakpoints.md, " {\n      padding: ").concat(theme.space[10], " ").concat(theme.space[40], ";\n    }\n  ");
});
var SummaryWrapper = styled.div(templateObject_2$6 || (templateObject_2$6 = __makeTemplateObject(["\n  background-color: ", ";\n  padding: ", ";\n  border-radius: 10px;\n  text-align: ", ";\n"], ["\n  background-color: ", ";\n  padding: ", ";\n  border-radius: 10px;\n  text-align: ", ";\n"])), function (_a) {
    var theme = _a.theme;
    return theme.colors.loanForm.summaryWrapper.backgroundColor;
}, function (_a) {
    var theme = _a.theme;
    return theme.space[24];
}, function (_a) {
    var market = _a.market;
    return (market === Markets.Latvia ? 'center' : 'left');
});
var Description = styled(Body3$2).attrs({
    color: theme.colors.loanForm.description,
})(templateObject_3$6 || (templateObject_3$6 = __makeTemplateObject(["\n  margin-bottom: 0;\n  ", "\n"], ["\n  margin-bottom: 0;\n  ", "\n"])), function (_a) {
    var theme = _a.theme;
    return "\n    line-height: ".concat(theme.fonts.lineHeight.desktop.xxs, "; \n    ").concat(theme.breakpoints.sm, " {\n     margin-bottom: ").concat(theme.space[24], ";\n    }\n     ").concat(theme.breakpoints.lg, " {\n     margin-bottom: ").concat(theme.space[8], ";\n    }\n  ");
});
var Total = styled(Header3$2)(templateObject_4$6 || (templateObject_4$6 = __makeTemplateObject(["\n  ", "\n"], ["\n  ", "\n"])), function (_a) {
    var _b = _a.theme, breakpoints = _b.breakpoints, colors = _b.colors, _c = _b.fonts, size = _c.size, lineHeight = _c.lineHeight, space = _b.space, market = _a.market;
    return "\n    ".concat(market === Markets.Estonia || market === Markets.CzechRepublic
        ? "\n        color: ".concat(colors.loanForm.total, ";\n        font-size: ").concat(size.mobile.xxl, ";\n        line-height: ").concat(lineHeight.mobile.xl, ";\n        text-align: left;\n        margin-block: ").concat(space[12], ";\n        ")
        : "\n        font-size: ".concat(size.mobile.m, ";\n        line-height: ").concat(lineHeight.mobile.xs, ";\n        margin-top: ").concat(space[12], ";\n        "), "\n    ").concat(breakpoints.md, " {\n      ").concat(market === Markets.Estonia || market === Markets.CzechRepublic
        ? "\n          font-size: ".concat(size.tablet.xxl, ";\n          line-height: ").concat(lineHeight.tablet.l, ";\n          text-align: left;\n          margin-top: ").concat(space[16], ";\n          margin-bottom: ").concat(space[8], ";\n          ")
        : "\n          font-size: ".concat(size.tablet.l, ";\n          line-height: ").concat(lineHeight.tablet.m, ";\n          text-align: center;\n          "), "\n    }\n  ");
});
styled.ul(templateObject_5$5 || (templateObject_5$5 = __makeTemplateObject(["\n  padding: 0;\n  margin: 0;\n  list-style: none;\n"], ["\n  padding: 0;\n  margin: 0;\n  list-style: none;\n"])));
styled.li(templateObject_6$5 || (templateObject_6$5 = __makeTemplateObject(["\n  position: relative;\n  display: flex;\n  justify-content: center;\n  align-items: center;\n  gap: 15px;\n"], ["\n  position: relative;\n  display: flex;\n  justify-content: center;\n  align-items: center;\n  gap: 15px;\n"])));
styled(Body4)(templateObject_7$5 || (templateObject_7$5 = __makeTemplateObject(["\n  text-align: left;\n  flex: 1 1 auto;\n  max-width: 75%;\n"], ["\n  text-align: left;\n  flex: 1 1 auto;\n  max-width: 75%;\n"])));
var CtaTitle = styled(Header7)(templateObject_8$3 || (templateObject_8$3 = __makeTemplateObject(["\n  font-weight: ", ";\n  margin-bottom: ", ";\n"], ["\n  font-weight: ", ";\n  margin-bottom: ", ";\n"])), function (_a) {
    var market = _a.market;
    return market === Markets.Estonia || market === Markets.CzechRepublic ? 'bold' : 'normal';
}, function (_a) {
    var theme = _a.theme;
    return theme.space[4];
});
var CtaDescription = styled.p(templateObject_9$3 || (templateObject_9$3 = __makeTemplateObject(["\n  font-size: ", ";\n"], ["\n  font-size: ", ";\n"])), function (_a) {
    var theme = _a.theme;
    return theme.fonts.size.mobile.xxs;
});
var ModalButton = styled(Button$2)(templateObject_10$2 || (templateObject_10$2 = __makeTemplateObject(["\n  padding-left: 0;\n  padding-bottom: 24px;\n  margin-top: ", ";\n  ", " {\n    padding-bottom: 17px;\n    margin-top: ", ";\n  }\n  width: max-content;\n"], ["\n  padding-left: 0;\n  padding-bottom: 24px;\n  margin-top: ", ";\n  ", " {\n    padding-bottom: 17px;\n    margin-top: ", ";\n  }\n  width: max-content;\n"])), theme.space[8], theme.breakpoints.sm, theme.space[16]);
var GutterForEE = styled.div(templateObject_11$1 || (templateObject_11$1 = __makeTemplateObject(["\n  height: 0;\n  ", " {\n    height: ", ";\n  }\n"], ["\n  height: 0;\n  ", " {\n    height: ", ";\n  }\n"])), theme.breakpoints.md, theme.space[24]);
var GutterForCZ = styled.div(templateObject_12$1 || (templateObject_12$1 = __makeTemplateObject(["\n  height: ", ";\n  ", " {\n    height: ", ";\n  }\n  ", " {\n    height: ", ";\n  }\n"], ["\n  height: ", ";\n  ", " {\n    height: ", ";\n  }\n  ", " {\n    height: ", ";\n  }\n"])), theme.space[24], theme.breakpoints.sm, theme.space[16], theme.breakpoints.lg, theme.space[32]);
var GutterSlider = styled.div(templateObject_13$1 || (templateObject_13$1 = __makeTemplateObject(["\n  height: ", ";\n  ", " {\n    height: ", ";\n  }\n  ", " {\n    height: 0;\n  }\n"], ["\n  height: ", ";\n  ", " {\n    height: ", ";\n  }\n  ", " {\n    height: 0;\n  }\n"])), theme.space[16], theme.breakpoints.sm, theme.space[8], theme.breakpoints.lg);
var templateObject_1$7, templateObject_2$6, templateObject_3$6, templateObject_4$6, templateObject_5$5, templateObject_6$5, templateObject_7$5, templateObject_8$3, templateObject_9$3, templateObject_10$2, templateObject_11$1, templateObject_12$1, templateObject_13$1;

var LoanForm = function (_a) {
    var _b;
    var market = _a.market, sliders = _a.sliders, format = _a.format, termsDescription = _a.termsDescription, termsDisclaimer = _a.termsDisclaimer, callToAction = _a.callToAction, paymentValue = _a.paymentValue, amountValue = _a.amountValue, periodValue = _a.periodValue, totalValue = _a.totalValue, fixedInterest = _a.fixedInterest, valueList = _a.valueList, loanFormTitle = _a.loanFormTitle, loanFormDescription = _a.loanFormDescription, loanFormMode = _a.loanFormMode;
    var isHorizontal = [Markets.Estonia, Markets.CzechRepublic].includes(market);
    var fullTabletWidthMarkets = [Markets.Latvia, Markets.Lithuania].includes(market);
    var decimalNumber = [Markets.Latvia, Markets.Lithuania].includes(market) ? 2 : 0;
    var _c = useState(false), isModalOpen = _c[0], setIsModalOpen = _c[1];
    var TermsDescription = (jsx(Body4, __assign$1({ color: theme.colors.loanForm.termsDescription }, { children: termsDescription })));
    var TermsDisclaimer = termsDisclaimer && jsx(Body4, { children: termsDisclaimer });
    var SliderDisclaimer = loanFormMode &&
        loanFormMode.disclaimerText &&
        sliders[0].value >= loanFormMode.disclaimerTrigger && (jsxs(Fragment, { children: [jsx(Gutter, { size: 16 }), jsx(Body4, { children: loanFormMode.disclaimerText })] }));
    var CTAButton = callToAction.buttonText && (jsx(Button$2, __assign$1({ id: "calculator", variant: "primary", autoWidth: false, onClick: callToAction.buttonAction, mode: (_b = callToAction.colorMode) !== null && _b !== void 0 ? _b : 'dark', "data-testid": "loan_form_apply_button" }, { children: callToAction.buttonText })));
    var ModalButtonCZ = callToAction.buttonText && (jsx(Button$2, __assign$1({ variant: "primary", autoWidth: "lg", onClick: callToAction.buttonAction }, { children: callToAction.buttonText })));
    return (jsx(RoundedBackground, __assign$1({ color: theme.colors.loanForm.backgroundColor, roundedType: RoundedBackground.RoundTypes.EachCornerSeparateValue, eachCornerSeparateValues: {
            topLeft: '60px',
            topRight: '0',
            bottomLeft: '0',
            bottomRight: '60px',
        }, fluid: true }, { children: jsx(FormWrapper$1, __assign$1({ "data-testid": "loan_form_container" }, { children: jsxs(Row$1, __assign$1({ middle: market === LoanForm.Markets.CzechRepublic ? 'xs' : undefined }, { children: [jsxs(Col, __assign$1({ xs: 4, sm: fullTabletWidthMarkets ? 8 : 4, md: isHorizontal ? 8 : 12 }, { children: [market === Markets.CzechRepublic && (jsxs(Fragment, { children: [jsx(Header6, { children: loanFormTitle }), jsx(Gutter, { size: 8 }), jsx(Description, { children: loanFormDescription })] })), sliders.map(function (props, key) {
                                var dataTestId = "slider_pointer_container".concat(key + 1);
                                return jsx(Slider$1, __assign$1({}, props, { dataTestId: dataTestId }), key);
                            }), SliderDisclaimer, market === Markets.Estonia && jsx(GutterForEE, {}), fullTabletWidthMarkets && jsx(GutterSlider, {}), market === Markets.CzechRepublic && jsx(GutterForCZ, {}), isHorizontal && TermsDescription, market === Markets.CzechRepublic && (jsxs(Fragment, { children: [jsx(ModalButton, __assign$1({ onClick: function () { return setIsModalOpen(true); }, variant: "text", appendIcon: "ArrowRightOn", "data-testid": "loan_example_button" }, { children: "Reprezentativn\u00ED p\u0159\u00EDklad" })), jsxs(Modal, __assign$1({ buttons: __spreadArray$1([], (ModalButtonCZ ? [ModalButtonCZ] : []), true), handleClose: function () { return setIsModalOpen(false); }, isOpen: isModalOpen, modalCloseButton: true, title: "Reprezentativn\u00ED p\u0159\u00EDklad" }, { children: [jsxs(Body2, { children: ["Toto je reprezentativn\u00ED p\u0159\u00EDklad p\u016Fj\u010Dky na ", format(amountValue), " v d\u00E9lce ", periodValue, " m\u011Bs\u00EDc\u016F, s ro\u010Dn\u00EDm \u00FArokem 9,84% p.a., RPSN 10,45%."] }), jsx(Gutter, { size: 16 }), jsxs(Body2, { children: ["M\u011Bs\u00ED\u010Dn\u00ED spl\u00E1tky \u010Din\u00ED ", format(paymentValue), ", pevn\u00FD \u00FArok", ' ', format(fixedInterest), ", poplatky p\u0159i \u0159\u00E1dn\u00E9m spl\u00E1cen\u00ED 0 K\u010D. Celkem z\u00E1kazn\u00EDk zaplat\u00ED ", format(totalValue), ". Fin\u00E1ln\u00ED v\u00FD\u0161e spl\u00E1tky z\u00E1le\u017E\u00ED na individu\u00E1ln\u00EDm posouzen\u00ED \u00FAv\u011Brov\u00E9 \u017E\u00E1dosti."] })] }))] }))] })), jsxs(Col, __assign$1({ xs: 4, sm: fullTabletWidthMarkets ? 8 : 4, md: isHorizontal ? 4 : 12 }, { children: [jsx(SummaryWrapper, __assign$1({ market: market }, { children: market === Markets.Lithuania && valueList ? (jsx(StyledList, { list: valueList, flush: true })) : (jsxs(Fragment, { children: [jsx(CtaTitle, __assign$1({ market: market }, { children: callToAction.title })), callToAction.description && (jsxs(CtaDescription, { children: [periodValue, " ", callToAction.description] })), jsx(Total, __assign$1({ market: market, "data-testid": "monthly_amount" }, { children: format(paymentValue, decimalNumber) })), callToAction.refundsLabel && (jsxs(Caption3$1, { children: [callToAction.refundsLabel, ' ', totalValue && (jsx(Caption3$1, __assign$1({ as: "span", color: theme.colors.loanForm.totalRefundsValueColor, "data-testid": "total_amount" }, { children: format(totalValue) })))] })), isHorizontal && (jsxs(Fragment, { children: [jsx(Gutter, { size: 16 }), CTAButton] }))] })) })), !isHorizontal && (jsxs(Fragment, { children: [jsx(Gutter, { size: 24 }), CTAButton, jsx(Gutter, { size: 16 }), TermsDescription, TermsDisclaimer] }))] }))] })) })) })));
};
LoanForm.Markets = Markets;

var NewsletterContainer = styled.div(templateObject_1$6 || (templateObject_1$6 = __makeTemplateObject(["\n  padding: 0 20px;\n  ", " {\n    padding: ", ";\n  }\n  ", " {\n    padding: ", ";\n  }\n"], ["\n  padding: 0 20px;\n  ", " {\n    padding: ", ";\n  }\n  ", " {\n    padding: ", ";\n  }\n"])), function (_a) {
    var theme = _a.theme;
    return theme.breakpoints.sm;
}, function (_a) {
    var theme = _a.theme;
    return "".concat(theme.space[40], " ").concat(theme.space[100]);
}, function (_a) {
    var theme = _a.theme;
    return theme.breakpoints.md;
}, function (_a) {
    var theme = _a.theme;
    return "".concat(theme.space[50], " ").concat(theme.space[32]);
});
var HeaderContainer = styled.div(templateObject_2$5 || (templateObject_2$5 = __makeTemplateObject(["\n  position: relative;\n"], ["\n  position: relative;\n"])));
var Title$3 = styled(Header3$2)(templateObject_3$5 || (templateObject_3$5 = __makeTemplateObject(["\n  position: relative;\n  margin-top: 24px;\n  ", " {\n    font-size: ", ";\n    line-height: ", ";\n  }\n"], ["\n  position: relative;\n  margin-top: 24px;\n  ", " {\n    font-size: ", ";\n    line-height: ", ";\n  }\n"])), function (_a) {
    var theme = _a.theme;
    return theme.breakpoints.md;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.size.desktop.xxl;
}, function (_a) {
    var theme = _a.theme;
    return theme.fonts.lineHeight.mobile.xxl;
});
var ImageWrapper$3 = styled.div(templateObject_4$5 || (templateObject_4$5 = __makeTemplateObject(["\n  position: absolute;\n  height: 100%;\n  width: 100%;\n  bottom: -40%;\n\n  > * {\n    max-width: 120%;\n  }\n"], ["\n  position: absolute;\n  height: 100%;\n  width: 100%;\n  bottom: -40%;\n\n  > * {\n    max-width: 120%;\n  }\n"])));
var FormWrapper = styled.div(templateObject_5$4 || (templateObject_5$4 = __makeTemplateObject(["\n  display: flex;\n  flex-direction: column;\n  align-items: flex-start;\n  gap: 24px;\n  ", " {\n    flex-direction: row;\n  }\n"], ["\n  display: flex;\n  flex-direction: column;\n  align-items: flex-start;\n  gap: 24px;\n  ", " {\n    flex-direction: row;\n  }\n"])), function (_a) {
    var theme = _a.theme;
    return theme.breakpoints.sm;
});
var FormFieldWrapper = styled.div(templateObject_6$4 || (templateObject_6$4 = __makeTemplateObject(["\n  flex-grow: 1;\n  width: 100%;\n"], ["\n  flex-grow: 1;\n  width: 100%;\n"])));
var TermsDescription = styled(Body4)(templateObject_7$4 || (templateObject_7$4 = __makeTemplateObject(["\n  margin-top: 24px;\n\n  ", " {\n    margin-left: 0;\n  }\n"], ["\n  margin-top: 24px;\n\n  ", " {\n    margin-left: 0;\n  }\n"])), function (_a) {
    var theme = _a.theme;
    return theme.breakpoints.xs;
});
var templateObject_1$6, templateObject_2$5, templateObject_3$5, templateObject_4$5, templateObject_5$4, templateObject_6$4, templateObject_7$4;

var Newsletter = function (_a) {
    var title = _a.title, titleUnderlineImage = _a.titleUnderlineImage, description = _a.description, isInputRequired = _a.isInputRequired, inputPlaceholder = _a.inputPlaceholder, submitButtonText = _a.submitButtonText, errorMessage = _a.errorMessage, termsDescription = _a.termsDescription, onInputChange = _a.onInputChange, onSubmit = _a.onSubmit;
    var RoundTypes = RoundedBackground.RoundTypes;
    return (jsx(RoundedBackground, __assign$1({ color: theme.colors.newsletter.defaultBackground, roundedType: RoundTypes.DiagonalCorners }, { children: jsx(NewsletterContainer, { children: jsxs(Row$1, __assign$1({ center: "xs", middle: "xs" }, { children: [jsxs(Col, __assign$1({ xs: 4, sm: 8, lg: 6, align: Col.Align.Start }, { children: [jsxs(HeaderContainer, { children: [jsx(ImageWrapper$3, { children: titleUnderlineImage }), jsx(Title$3, { children: title })] }), jsx(Gutter, { size: 24 }), jsx(Body3$2, { children: description }), jsx(Gutter, { size: 24 })] })), jsx(Col, __assign$1({ xs: 4, sm: 8, lg: 6, align: Col.Align.Stretch }, { children: jsxs("form", __assign$1({ onSubmit: onSubmit, noValidate: true }, { children: [jsxs(FormWrapper, { children: [jsx(FormFieldWrapper, { children: jsx(InputItem$1, { name: "emailInput", placeholder: inputPlaceholder, errorMessage: errorMessage, required: isInputRequired, onChange: function (event) { return onInputChange(event); }, type: "email" }) }), jsx("div", { children: jsx(Button$2, __assign$1({ type: "submit", variant: "secondary" }, { children: submitButtonText })) })] }), jsx(TermsDescription, __assign$1({ color: theme.colors.newsletter.termsColor }, { children: termsDescription }))] })) }))] })) }) })));
};

var imageOffset = 100;
var imageOffsetTablet = 48;
var imageOffsetMobile = 65;
var verticalPadding = 60; // sum of current and rounded background components padding
var CardSpacing = styled('div')(templateObject_1$5 || (templateObject_1$5 = __makeTemplateObject(["\n  height: ", "px;\n\n  ", " {\n    height: ", "px;\n  }\n\n  ", " {\n    height: ", "px;\n  }\n"], ["\n  height: ", "px;\n\n  ", " {\n    height: ", "px;\n  }\n\n  ", " {\n    height: ", "px;\n  }\n"])), imageOffsetMobile, theme.breakpoints.sm, imageOffsetTablet, theme.breakpoints.md, imageOffset);
var FlexContainer = styled.section(templateObject_2$4 || (templateObject_2$4 = __makeTemplateObject(["\n  display: flex;\n  flex-direction: column;\n  justify-content: center;\n  padding: 30px 16px;\n\n  ", " {\n    flex-direction: row;\n    justify-content: flex-start;\n    padding: 30px 24px;\n  }\n\n  ", " {\n    padding: 30px 96px;\n  }\n"], ["\n  display: flex;\n  flex-direction: column;\n  justify-content: center;\n  padding: 30px 16px;\n\n  ", " {\n    flex-direction: row;\n    justify-content: flex-start;\n    padding: 30px 24px;\n  }\n\n  ", " {\n    padding: 30px 96px;\n  }\n"])), theme.breakpoints.sm, theme.breakpoints.md);
var RightColumn = styled.div(templateObject_3$4 || (templateObject_3$4 = __makeTemplateObject(["\n  display: flex;\n  flex-direction: column;\n  padding-left: 16px;\n  width: 100%;\n\n  ", " {\n    width: 50%;\n    padding-left: 64px;\n  }\n"], ["\n  display: flex;\n  flex-direction: column;\n  padding-left: 16px;\n  width: 100%;\n\n  ", " {\n    width: 50%;\n    padding-left: 64px;\n  }\n"])), theme.breakpoints.md);
var LeftColumn = styled.div(templateObject_4$4 || (templateObject_4$4 = __makeTemplateObject(["\n  position: relative;\n  width: 100%;\n\n  ", " {\n    width: 50%;\n  }\n"], ["\n  position: relative;\n  width: 100%;\n\n  ", " {\n    width: 50%;\n  }\n"])), theme.breakpoints.md);
var StandaloneTitle$1 = styled(Header3$2)(templateObject_5$3 || (templateObject_5$3 = __makeTemplateObject(["\n  ", " {\n    font-size: 42px;\n    line-height: 50px;\n  }\n"], ["\n  ", " {\n    font-size: 42px;\n    line-height: 50px;\n  }\n"])), theme.breakpoints.lg);
// Price info brief styles
var CreditCardContainer = styled.div(templateObject_6$3 || (templateObject_6$3 = __makeTemplateObject(["\n  pointer-events: none;\n  display: flex;\n  justify-content: center;\n"], ["\n  pointer-events: none;\n  display: flex;\n  justify-content: center;\n"])));
var ImageWrapper$2 = styled.div(templateObject_7$3 || (templateObject_7$3 = __makeTemplateObject(["\n  position: absolute;\n  top: -", "px;\n  height: 177px;\n  width: 270px;\n\n  & > img {\n    object-fit: contain;\n  }\n\n  ", " {\n    top: -", "px;\n    height: 190px;\n    width: 300px;\n    max-width: 100%;\n  }\n\n  ", " {\n    top: -", "px;\n    height: 260px;\n    width: 410px;\n    max-width: initial;\n  }\n"], ["\n  position: absolute;\n  top: -", "px;\n  height: 177px;\n  width: 270px;\n\n  & > img {\n    object-fit: contain;\n  }\n\n  ", " {\n    top: -", "px;\n    height: 190px;\n    width: 300px;\n    max-width: 100%;\n  }\n\n  ", " {\n    top: -", "px;\n    height: 260px;\n    width: 410px;\n    max-width: initial;\n  }\n"])), imageOffsetMobile + verticalPadding, theme.breakpoints.sm, imageOffsetTablet + verticalPadding, theme.breakpoints.md, imageOffset + verticalPadding);
var InfoContent = styled.div(templateObject_8$2 || (templateObject_8$2 = __makeTemplateObject(["\n  margin-top: ", "px;\n\n  ", " {\n    margin-top: ", "px;\n  }\n\n  ", " {\n    margin-top: ", "px;\n  }\n"], ["\n  margin-top: ", "px;\n\n  ", " {\n    margin-top: ", "px;\n  }\n\n  ", " {\n    margin-top: ", "px;\n  }\n"])), 160 - verticalPadding, theme.breakpoints.sm, 195 - verticalPadding, theme.breakpoints.md, 220 - verticalPadding);
var Title$2 = styled(Header6)(templateObject_9$2 || (templateObject_9$2 = __makeTemplateObject(["\n  text-align: center;\n\n  ", " {\n    font-size: 26px;\n    line-height: 36px;\n    margin-bottom: 24px;\n  }\n"], ["\n  text-align: center;\n\n  ", " {\n    font-size: 26px;\n    line-height: 36px;\n    margin-bottom: 24px;\n  }\n"])), theme.breakpoints.md);
var PriceContainer = styled.section(templateObject_10$1 || (templateObject_10$1 = __makeTemplateObject(["\n  display: flex;\n  justify-content: center;\n  align-items: baseline;\n  margin-top: 16px;\n  margin-bottom: 16px;\n\n  ", " {\n    margin-bottom: 24px;\n  }\n"], ["\n  display: flex;\n  justify-content: center;\n  align-items: baseline;\n  margin-top: 16px;\n  margin-bottom: 16px;\n\n  ", " {\n    margin-bottom: 24px;\n  }\n"])), theme.breakpoints.md);
var PriceTitle = styled(Header3$2)(templateObject_11 || (templateObject_11 = __makeTemplateObject(["\n  color: ", ";\n\n  ", " {\n    font-size: 42px;\n    line-height: 50px;\n  }\n"], ["\n  color: ", ";\n\n  ", " {\n    font-size: 42px;\n    line-height: 50px;\n  }\n"])), function (_a) {
    var theme = _a.theme;
    return theme.colors.priceInfo.priceTitle;
}, theme.breakpoints.md);
var PricePeriod = styled(Header8)(templateObject_12 || (templateObject_12 = __makeTemplateObject(["\n  color: ", ";\n  margin-left: 8px;\n\n  ", " {\n    font-size: 20px;\n    line-height: 26px;\n  }\n"], ["\n  color: ", ";\n  margin-left: 8px;\n\n  ", " {\n    font-size: 20px;\n    line-height: 26px;\n  }\n"])), function (_a) {
    var theme = _a.theme;
    return theme.colors.priceInfo.pricePeriod;
}, theme.breakpoints.md);
var FreeInfo = styled(Header8)(templateObject_13 || (templateObject_13 = __makeTemplateObject(["\n  text-align: center;\n  font-weight: bold;\n  width: 100%;\n  margin-bottom: 40px;\n\n  ", " {\n    font-size: 20px;\n    line-height: 26px;\n  }\n"], ["\n  text-align: center;\n  font-weight: bold;\n  width: 100%;\n  margin-bottom: 40px;\n\n  ", " {\n    font-size: 20px;\n    line-height: 26px;\n  }\n"])), theme.breakpoints.md);
// list styles
var ListContainer = styled.ul(templateObject_14 || (templateObject_14 = __makeTemplateObject(["\n  min-width: 50%;\n  margin: 0;\n  padding: 0;\n  list-style-type: none;\n  margin-bottom: 24px;\n"], ["\n  min-width: 50%;\n  margin: 0;\n  padding: 0;\n  list-style-type: none;\n  margin-bottom: 24px;\n"])));
var ListCategoryTitle = styled(Caption3$1)(templateObject_15 || (templateObject_15 = __makeTemplateObject(["\n  color: ", ";\n  padding-bottom: 10px;\n  border-bottom: 1px solid ", ";\n"], ["\n  color: ", ";\n  padding-bottom: 10px;\n  border-bottom: 1px solid ", ";\n"])), function (_a) {
    var theme = _a.theme;
    return theme.colors.priceInfo.listCategoryTitle.color;
}, function (_a) {
    var theme = _a.theme;
    return theme.colors.priceInfo.listCategoryTitle.borderBottom;
});
// link styles
var LinkWrapper$1 = styled.div(templateObject_16 || (templateObject_16 = __makeTemplateObject(["\n  padding-left: 10px;\n"], ["\n  padding-left: 10px;\n"])));
// story wrapper
styled.div(templateObject_17 || (templateObject_17 = __makeTemplateObject(["\n  max-width: 1120px;\n  margin-inline: auto;\n  padding-inline: 24px;\n"], ["\n  max-width: 1120px;\n  margin-inline: auto;\n  padding-inline: 24px;\n"])));
var templateObject_1$5, templateObject_2$4, templateObject_3$4, templateObject_4$4, templateObject_5$3, templateObject_6$3, templateObject_7$3, templateObject_8$2, templateObject_9$2, templateObject_10$1, templateObject_11, templateObject_12, templateObject_13, templateObject_14, templateObject_15, templateObject_16, templateObject_17;

var PriceInfoBrief = function (_a) {
    var title = _a.title, image = _a.image, price = _a.price, period = _a.period, promotionLabel = _a.promotionLabel;
    return (jsxs(CreditCardContainer, { children: [jsx(ImageWrapper$2, { children: image }), jsxs(InfoContent, { children: [jsx(Title$2, { children: title }), jsxs(PriceContainer, { children: [jsx(PriceTitle, { children: price }), jsxs(PricePeriod, { children: ["/", period] })] }), promotionLabel && jsx(FreeInfo, { children: promotionLabel })] })] }));
};
var PriceInfoLists = function (_a) {
    var categoryTitle = _a.categoryTitle, list = _a.list;
    return (jsxs(ListContainer, { children: [jsx("li", { children: categoryTitle && jsx(ListCategoryTitle, { children: categoryTitle }) }), list === null || list === void 0 ? void 0 : list.map(function (listItem) { return (createElement(DescriptiveListItem, __assign$1({}, listItem, { icon: listItem.icon, key: listItem._key }))); })] }));
};

var PriceInfo = function (_a) {
    var title = _a.title, type = _a.type, categories = _a.categories, image = _a.image, price = _a.price, period = _a.period, promotionLabel = _a.promotionLabel, linkData = _a.linkData, nextLink = _a.nextLink;
    var shouldShowLink = (linkData === null || linkData === void 0 ? void 0 : linkData.linkTitle) && (linkData.externalUrl || linkData.internalLink);
    return (jsxs(Fragment, { children: [image && jsx(CardSpacing, {}), jsx(RoundedBackground, __assign$1({ color: theme.colors.priceInfo.backgroundColor, roundedType: RoundedBackground.RoundTypes.AllCorners }, { children: jsxs(FlexContainer, { children: [jsx(LeftColumn, { children: type === 'LIST_WITH_SUMMARY' ? (jsx(PriceInfoBrief, { image: image, title: title, price: price, period: period, promotionLabel: promotionLabel })) : (jsxs(Fragment, { children: [jsx(StandaloneTitle$1, { children: title }), jsx(Gutter, { size: 40 })] })) }), jsxs(RightColumn, { children: [categories === null || categories === void 0 ? void 0 : categories.map(function (_a) {
                                    var categoryTitle = _a.categoryTitle, list = _a.list, _key = _a._key;
                                    return (jsx(PriceInfoLists, { list: list, categoryTitle: categoryTitle }, _key));
                                }), shouldShowLink && (jsx(LinkWrapper$1, { children: jsx(LinkWithArrow, { NextLink: nextLink, linkData: linkData }) }))] })] }) }))] }));
};

var SectionWrapper = styled.div(templateObject_1$4 || (templateObject_1$4 = __makeTemplateObject(["\n  background-color: ", ";\n"], ["\n  background-color: ", ";\n"])), function (_a) {
    var backgroundColor = _a.backgroundColor;
    return backgroundColor;
});
var PromoBannerWrapper = styled(Row$1)(templateObject_2$3 || (templateObject_2$3 = __makeTemplateObject(["\n  &&& {\n    padding: 60px 0;\n    display: flex;\n    flex-direction: column;\n\n    ", " {\n      padding: 48px 0;\n    }\n    ", " {\n      flex-direction: row;\n    }\n    ", " {\n      padding: 80px 0;\n    }\n  }\n"], ["\n  &&& {\n    padding: 60px 0;\n    display: flex;\n    flex-direction: column;\n\n    ", " {\n      padding: 48px 0;\n    }\n    ", " {\n      flex-direction: row;\n    }\n    ", " {\n      padding: 80px 0;\n    }\n  }\n"])), function (_a) {
    var theme = _a.theme;
    return theme.breakpoints.sm;
}, function (_a) {
    var theme = _a.theme;
    return theme.breakpoints.md;
}, function (_a) {
    var theme = _a.theme;
    return theme.breakpoints.lg;
});
var ImageWrapper$1 = styled(Col)(templateObject_3$3 || (templateObject_3$3 = __makeTemplateObject(["\n  &&& {\n    position: relative;\n    min-height: 250px;\n    order: 1;\n\n    & > img {\n      object-fit: contain;\n    }\n\n    ", " {\n      min-height: 260px;\n    }\n    ", " {\n      height: 328px;\n      order: revert;\n      margin-bottom: 0;\n    }\n  }\n"], ["\n  &&& {\n    position: relative;\n    min-height: 250px;\n    order: 1;\n\n    & > img {\n      object-fit: contain;\n    }\n\n    ", " {\n      min-height: 260px;\n    }\n    ", " {\n      height: 328px;\n      order: revert;\n      margin-bottom: 0;\n    }\n  }\n"])), function (_a) {
    var theme = _a.theme;
    return theme.breakpoints.sm;
}, function (_a) {
    var theme = _a.theme;
    return theme.breakpoints.md;
});
var ContentWrapper$1 = styled(Col)(templateObject_4$3 || (templateObject_4$3 = __makeTemplateObject(["\n  &&& {\n    order: 2;\n\n    ", " {\n      order: revert;\n    }\n\n    & > button[type='button'] {\n      width: fit-content;\n    }\n  }\n"], ["\n  &&& {\n    order: 2;\n\n    ", " {\n      order: revert;\n    }\n\n    & > button[type='button'] {\n      width: fit-content;\n    }\n  }\n"])), function (_a) {
    var theme = _a.theme;
    return theme.breakpoints.md;
});
var Title$1 = styled(Header2)(templateObject_5$2 || (templateObject_5$2 = __makeTemplateObject(["\n  position: relative;\n  z-index: 10;\n  margin: 24px 0;\n  color: ", ";\n\n  ", " {\n    margin-bottom: 32px;\n  }\n\n  ", " {\n    margin-top: 0;\n    gap: 0;\n  }\n"], ["\n  position: relative;\n  z-index: 10;\n  margin: 24px 0;\n  color: ", ";\n\n  ", " {\n    margin-bottom: 32px;\n  }\n\n  ", " {\n    margin-top: 0;\n    gap: 0;\n  }\n"])), function (_a) {
    var textColor = _a.textColor;
    return textColor;
}, function (_a) {
    var theme = _a.theme;
    return theme.breakpoints.sm;
}, function (_a) {
    var theme = _a.theme;
    return theme.breakpoints.md;
});
var InputsWrapper = styled.div(templateObject_6$2 || (templateObject_6$2 = __makeTemplateObject(["\n  display: none;\n\n  ", " {\n    display: flex;\n    margin-bottom: 24px;\n    gap: 24px;\n    max-width: 520px;\n\n    & > div:last-of-type {\n      flex: 1;\n      z-index: 10;\n      max-width: 450px;\n    }\n  }\n  ", " {\n    gap: 16px;\n  }\n"], ["\n  display: none;\n\n  ", " {\n    display: flex;\n    margin-bottom: 24px;\n    gap: 24px;\n    max-width: 520px;\n\n    & > div:last-of-type {\n      flex: 1;\n      z-index: 10;\n      max-width: 450px;\n    }\n  }\n  ", " {\n    gap: 16px;\n  }\n"])), function (_a) {
    var theme = _a.theme;
    return theme.breakpoints.sm;
}, function (_a) {
    var theme = _a.theme;
    return theme.breakpoints.lg;
});
var SelectContainer = styled.div(templateObject_7$2 || (templateObject_7$2 = __makeTemplateObject(["\n  width: 160px;\n"], ["\n  width: 160px;\n"])));
styled(Container$1)(templateObject_8$1 || (templateObject_8$1 = __makeTemplateObject(["\n  background-color: ", ";\n"], ["\n  background-color: ", ";\n"])), function (_a) {
    var theme = _a.theme;
    return theme.colors.promoBanner.backgroundColor;
});
styled.div(templateObject_9$1 || (templateObject_9$1 = __makeTemplateObject(["\n  width: 100%;\n  background-color: ", ";\n"], ["\n  width: 100%;\n  background-color: ", ";\n"])), function (_a) {
    var theme = _a.theme;
    return theme.colors.promoBanner.backgroundColor;
});
var templateObject_1$4, templateObject_2$3, templateObject_3$3, templateObject_4$3, templateObject_5$2, templateObject_6$2, templateObject_7$2, templateObject_8$1, templateObject_9$1;

var PromoBanner = function (_a) {
    var _b, _c;
    var countrySelectPlaceholder = _a.countrySelectPlaceholder, phoneInputPlaceholder = _a.phoneInputPlaceholder, title = _a.title, _d = _a.backgroundColor, backgroundColor = _d === void 0 ? theme.colors.promoBanner.backgroundColor : _d, _e = _a.textColor, textColor = _e === void 0 ? theme.colors.promoBanner.defaultColor : _e, buttonText = _a.buttonText, nextImage = _a.nextImage, selectOptions = _a.selectOptions, onSelectChange = _a.onSelectChange, onTextInputChange = _a.onTextInputChange, onButtonClick = _a.onButtonClick, buttonConfig = _a.buttonConfig;
    return (jsx(SectionWrapper, __assign$1({ backgroundColor: backgroundColor }, { children: jsx(Container$1, { children: jsxs(PromoBannerWrapper, { children: [jsxs(ContentWrapper$1, __assign$1({ xs: 4, sm: 8, md: 7, lg: 8, paddingTop: 1 }, { children: [jsx(Title$1, __assign$1({ textColor: textColor }, { children: title })), jsxs(InputsWrapper, { children: [jsx(SelectContainer, { children: jsx(SelectItem$1, { inputId: "sampleID", title: countrySelectPlaceholder, name: "country", onChange: function () { }, onSelectItem: onSelectChange, options: selectOptions }) }), jsx(Input$1, { name: "phoneInput", placeholder: phoneInputPlaceholder, onChange: onTextInputChange })] }), jsx(Button$2, __assign$1({ variant: (_b = buttonConfig === null || buttonConfig === void 0 ? void 0 : buttonConfig.variant) !== null && _b !== void 0 ? _b : 'primary', mode: (_c = buttonConfig === null || buttonConfig === void 0 ? void 0 : buttonConfig.colorMode) !== null && _c !== void 0 ? _c : 'dark', onClick: onButtonClick }, { children: buttonText }))] })), jsx(ImageWrapper$1, __assign$1({ xs: 4, sm: 8, md: 5, lg: 4, paddingTop: 1 }, { children: nextImage }))] }) }) })));
};

var RelatedArticlesWrapper = styled.footer(templateObject_1$3 || (templateObject_1$3 = __makeTemplateObject(["\n  padding: 100px 0;\n  background: ", "};\n"], ["\n  padding: 100px 0;\n  background: ", "};\n"])), function (_a) {
    var theme = _a.theme, variant = _a.variant;
    return theme.colors.relatedArticles.backgroundColorVariant[variant];
});
var templateObject_1$3;

var RelatedArticles = function (_a) {
    var title = _a.title, articles = _a.articles, language = _a.language, variant = _a.variant, nextLink = _a.nextLink;
    var isScreenMD = useIsScreenSize().isScreenMD;
    var allArticles = articles;
    if (isScreenMD) {
        allArticles = articles.slice(0, 2);
    }
    return (jsx(RelatedArticlesWrapper, __assign$1({ variant: variant }, { children: jsxs(Container$1, { children: [jsx(Row$1, { children: jsxs(Col, __assign$1({ xs: 4 }, { children: [jsx(Header4, { children: title }), jsx(Gutter, { size: 60 })] })) }), jsx(Row$1, { children: allArticles.map(function (blogArticle, index) {
                        return (jsx(Col, __assign$1({ xs: 4, sm: 4, md: 6, lg: 4 }, { children: jsx(BlogCard, { image: blogArticle.image, description: blogArticle.description, title: blogArticle.title, category: blogArticle === null || blogArticle === void 0 ? void 0 : blogArticle.category, categorySlug: blogArticle === null || blogArticle === void 0 ? void 0 : blogArticle.categorySlug, dateAndReadTime: blogArticle.dateAndReadTime, type: "medium", onClickLinkAction: blogArticle.onClickLinkAction, language: language, _id: blogArticle._id, NextLink: nextLink }) }), index));
                    }) })] }) })));
};

var Wrapper = styled(Col)(templateObject_1$2 || (templateObject_1$2 = __makeTemplateObject(["\n  margin: 0 auto;\n"], ["\n  margin: 0 auto;\n"])));
var RowWrapper = styled(Row$1)(templateObject_2$2 || (templateObject_2$2 = __makeTemplateObject(["\n  &&& {\n    justify-content: space-between;\n    align-items: center;\n    margin: 10px 0;\n    gap: 32px;\n\n    ", " {\n      margin: 18px 0;\n      gap: 24px;\n    }\n    ", " {\n      gap: 32px;\n    }\n  }\n"], ["\n  &&& {\n    justify-content: space-between;\n    align-items: center;\n    margin: 10px 0;\n    gap: 32px;\n\n    ", " {\n      margin: 18px 0;\n      gap: 24px;\n    }\n    ", " {\n      gap: 32px;\n    }\n  }\n"])), function (_a) {
    var theme = _a.theme;
    return theme.breakpoints.sm;
}, function (_a) {
    var theme = _a.theme;
    return theme.breakpoints.md;
});
var Title = styled(Header3$2)(templateObject_3$2 || (templateObject_3$2 = __makeTemplateObject(["\n  margin-bottom: 8px;\n  ", " {\n    margin-bottom: 16px;\n    text-align: ", ";\n  }\n  ", " {\n    text-align: left;\n  }\n  color: ", ";\n"], ["\n  margin-bottom: 8px;\n  ", " {\n    margin-bottom: 16px;\n    text-align: ", ";\n  }\n  ", " {\n    text-align: left;\n  }\n  color: ", ";\n"])), function (_a) {
    var theme = _a.theme;
    return theme.breakpoints.sm;
}, function (_a) {
    var fullWidthOnMobile = _a.fullWidthOnMobile;
    return (fullWidthOnMobile ? 'center' : 'left');
}, function (_a) {
    var theme = _a.theme;
    return theme.breakpoints.md;
}, function (_a) {
    var textColor = _a.textColor;
    return textColor;
});
var ContentWrapper = styled(Col)(templateObject_4$2 || (templateObject_4$2 = __makeTemplateObject(["\n  &&& {\n    padding-right: ", ";\n    padding-left: ", ";\n    ", " {\n      padding-left: 32px;\n      padding-right: 32px;\n      align-items: ", ";\n    }\n    ", " {\n      padding-right: 0;\n      flex-grow: 2;\n      max-width: 100%;\n      align-items: flex-start;\n    }\n    ", " {\n      padding-left: 48px;\n    }\n  }\n"], ["\n  &&& {\n    padding-right: ", ";\n    padding-left: ", ";\n    ", " {\n      padding-left: 32px;\n      padding-right: 32px;\n      align-items: ", ";\n    }\n    ", " {\n      padding-right: 0;\n      flex-grow: 2;\n      max-width: 100%;\n      align-items: flex-start;\n    }\n    ", " {\n      padding-left: 48px;\n    }\n  }\n"])), function (_a) {
    var fullWidthOnMobile = _a.fullWidthOnMobile;
    return (fullWidthOnMobile ? '24px' : '16px');
}, function (_a) {
    var fullWidthOnMobile = _a.fullWidthOnMobile;
    return (fullWidthOnMobile ? '24px' : '16px');
}, function (_a) {
    var theme = _a.theme;
    return theme.breakpoints.sm;
}, function (_a) {
    var fullWidthOnMobile = _a.fullWidthOnMobile;
    return (fullWidthOnMobile ? 'center' : ' flex-start');
}, function (_a) {
    var theme = _a.theme;
    return theme.breakpoints.md;
}, function (_a) {
    var theme = _a.theme;
    return theme.breakpoints.lg;
});
var ImageWrapper = styled(Col)(templateObject_5$1 || (templateObject_5$1 = __makeTemplateObject(["\n  &&& {\n    position: relative;\n    height: 250px;\n    flex-basis: 100%;\n    max-width: 100%;\n\n    & > img {\n      object-fit: contain;\n    }\n\n    ", " {\n      height: 260px;\n    }\n    ", " {\n      max-width: 340px;\n    }\n\n    ", " {\n      max-width: 448px;\n      height: 328px;\n    }\n  }\n"], ["\n  &&& {\n    position: relative;\n    height: 250px;\n    flex-basis: 100%;\n    max-width: 100%;\n\n    & > img {\n      object-fit: contain;\n    }\n\n    ", " {\n      height: 260px;\n    }\n    ", " {\n      max-width: 340px;\n    }\n\n    ", " {\n      max-width: 448px;\n      height: 328px;\n    }\n  }\n"])), function (_a) {
    var theme = _a.theme;
    return theme.breakpoints.sm;
}, function (_a) {
    var theme = _a.theme;
    return theme.breakpoints.md;
}, function (_a) {
    var theme = _a.theme;
    return theme.breakpoints.lg;
});
var ButtonContainer = styled.div(templateObject_6$1 || (templateObject_6$1 = __makeTemplateObject(["\n  margin-top: 32px;\n"], ["\n  margin-top: 32px;\n"])));
var LinkWrapper = styled.div(templateObject_7$1 || (templateObject_7$1 = __makeTemplateObject(["\n  padding: 16px 0;\n  margin-top: 16px;\n  ", " {\n    margin-top: 24px;\n  }\n  ", " {\n    margin-top: 32px;\n  }\n"], ["\n  padding: 16px 0;\n  margin-top: 16px;\n  ", " {\n    margin-top: 24px;\n  }\n  ", " {\n    margin-top: 32px;\n  }\n"])), function (_a) {
    var theme = _a.theme;
    return theme.breakpoints.sm;
}, function (_a) {
    var theme = _a.theme;
    return theme.breakpoints.lg;
});
var TextContent = styled.div(templateObject_8 || (templateObject_8 = __makeTemplateObject(["\n  > :is(h1, h2, h3, h4, h5, h6, p, ul) {\n    color: ", ";\n    *:not(a) {\n      color: inherit;\n    }\n  }\n"], ["\n  > :is(h1, h2, h3, h4, h5, h6, p, ul) {\n    color: ", ";\n    *:not(a) {\n      color: inherit;\n    }\n  }\n"])), function (_a) {
    var textColor = _a.textColor;
    return textColor;
});
var OnlyTextWrapper = styled.div(templateObject_9 || (templateObject_9 = __makeTemplateObject(["\n  padding: 18px 24px;\n  ", " {\n    padding: 18px 32px;\n  }\n  ", " {\n    padding: 18px 48px;\n  }\n"], ["\n  padding: 18px 24px;\n  ", " {\n    padding: 18px 32px;\n  }\n  ", " {\n    padding: 18px 48px;\n  }\n"])), function (_a) {
    var theme = _a.theme;
    return theme.breakpoints.sm;
}, function (_a) {
    var theme = _a.theme;
    return theme.breakpoints.lg;
});
var SocialsWrapper = styled.div(templateObject_10 || (templateObject_10 = __makeTemplateObject(["\n  padding-top: 28px;\n  ", " {\n    padding-top: 16px;\n  }\n  ", " {\n    padding-top: 28px;\n  }\n"], ["\n  padding-top: 28px;\n  ", " {\n    padding-top: 16px;\n  }\n  ", " {\n    padding-top: 28px;\n  }\n"])), function (_a) {
    var theme = _a.theme;
    return theme.breakpoints.sm;
}, function (_a) {
    var theme = _a.theme;
    return theme.breakpoints.md;
});
var templateObject_1$2, templateObject_2$2, templateObject_3$2, templateObject_4$2, templateObject_5$1, templateObject_6$1, templateObject_7$1, templateObject_8, templateObject_9, templateObject_10;

var LinkComponent = function (_a) {
    var _b, _c;
    var nextLink = _a.nextLink, linkData = _a.linkData, linkAsButton = _a.linkAsButton, buttonConfig = _a.buttonConfig, _d = _a.mode, mode = _d === void 0 ? 'dark' : _d;
    return linkAsButton ? (jsx(ButtonContainer, { children: jsx(LinkAsButton, { NextLink: nextLink, linkData: linkData, mode: (_b = buttonConfig === null || buttonConfig === void 0 ? void 0 : buttonConfig.colorMode) !== null && _b !== void 0 ? _b : 'dark', variant: (_c = buttonConfig === null || buttonConfig === void 0 ? void 0 : buttonConfig.variant) !== null && _c !== void 0 ? _c : 'primary' }) })) : (jsx(LinkWrapper, { children: jsx(LinkWithArrow, { NextLink: nextLink, linkData: linkData, mode: mode }) }));
};
var TitleAndDescription = function (_a) {
    var textColor = _a.textColor, title = _a.title, description = _a.description, PortableTextWrapper = _a.PortableTextWrapper, fullWidthOnMobile = _a.fullWidthOnMobile, mode = _a.mode;
    return (jsxs(Fragment, { children: [title && (jsx(Title, __assign$1({ textColor: textColor, fullWidthOnMobile: fullWidthOnMobile }, { children: title }))), description && !hasEmptyTextContent(description) && PortableTextWrapper && (jsx(TextContent, __assign$1({ textColor: textColor }, { children: description.textRaw && (jsx(PortableTextWrapper, { portableText: description.textRaw, mode: mode })) })))] }));
};
var RoundedFeatureWithImage = function (_a) {
    var textColor = _a.textColor, title = _a.title, description = _a.description, nextImage = _a.nextImage, nextLink = _a.nextLink, linkData = _a.linkData, PortableTextWrapper = _a.PortableTextWrapper, linkAsButton = _a.linkAsButton, buttonConfig = _a.buttonConfig, socialList = _a.socialList, fullWidthOnMobile = _a.fullWidthOnMobile, mode = _a.mode;
    var isScreenSM = useIsScreenSize().isScreenSM;
    var shouldShowLink = (linkData === null || linkData === void 0 ? void 0 : linkData.linkTitle) && (linkData.externalUrl || linkData.internalLink);
    return (jsxs(RowWrapper, __assign$1({ reverse: !isScreenSM }, { children: [jsx(ImageWrapper, __assign$1({ xs: 4, sm: 4, md: 5, justify: Col.Justify.Center, paddingTop: 1, paddingLeft: 1 }, { children: nextImage })), jsxs(ContentWrapper, __assign$1({ xs: 4, sm: 8, md: 6, justify: Col.Justify.Center, paddingTop: 1, fullWidthOnMobile: fullWidthOnMobile }, { children: [jsx(TitleAndDescription, { textColor: textColor, title: title, description: description, PortableTextWrapper: PortableTextWrapper, fullWidthOnMobile: fullWidthOnMobile, mode: mode }), shouldShowLink && (jsx(LinkComponent, { nextLink: nextLink, linkData: linkData, linkAsButton: linkAsButton, buttonConfig: buttonConfig, mode: mode })), socialList && (jsx(SocialsWrapper, { children: jsx(Socials, { socialList: socialList, withMargin: false }) }))] }))] })));
};

var RoundedFeature = function (_a) {
    var _b = _a.backgroundColor, backgroundColor = _b === void 0 ? theme.colors.roundedFeature.defaultBackground : _b, _c = _a.textColor, textColor = _c === void 0 ? theme.colors.roundedFeature.defaultColor : _c, cornersType = _a.cornersType, title = _a.title, description = _a.description, nextImage = _a.nextImage, nextLink = _a.nextLink, linkData = _a.linkData, PortableTextWrapper = _a.PortableTextWrapper, linkAsButton = _a.linkAsButton, buttonConfig = _a.buttonConfig, _d = _a.narrowVersion, narrowVersion = _d === void 0 ? false : _d, fullWidthOnMobile = _a.fullWidthOnMobile, socialList = _a.socialList;
    var mode = textColor === theme.crediteaColors.primary.neutral[50] ? 'light' : 'dark';
    return (jsx(Wrapper, __assign$1({ xs: 4, sm: 8, md: narrowVersion ? 8 : 12, paddingLeft: 0, paddingTop: 0 }, { children: jsx(RoundedBackground, __assign$1({ color: backgroundColor || theme.colors.roundedFeature.defaultBackground, roundedType: cornersType || RoundedBackground.RoundTypes.AllCorners, fullWidthOnMobile: fullWidthOnMobile }, { children: nextImage ? (jsx(RoundedFeatureWithImage, { textColor: textColor, title: title, description: description, nextImage: nextImage, nextLink: nextLink, linkData: linkData, PortableTextWrapper: PortableTextWrapper, linkAsButton: linkAsButton, buttonConfig: buttonConfig, socialList: socialList, fullWidthOnMobile: fullWidthOnMobile, mode: mode })) : (jsx(OnlyTextWrapper, { children: jsx(TitleAndDescription, { textColor: textColor, title: title, description: description, PortableTextWrapper: PortableTextWrapper, mode: mode }) })) })) })));
};

var limitCharacters = css(templateObject_1$1 || (templateObject_1$1 = __makeTemplateObject(["\n  display: -webkit-box;\n  line-clamp: 1;\n  -webkit-line-clamp: 1;\n  -webkit-box-orient: vertical;\n  text-overflow: ellipsis;\n  overflow: hidden;\n"], ["\n  display: -webkit-box;\n  line-clamp: 1;\n  -webkit-line-clamp: 1;\n  -webkit-box-orient: vertical;\n  text-overflow: ellipsis;\n  overflow: hidden;\n"])));
var bottomItemPosition = css(templateObject_2$1 || (templateObject_2$1 = __makeTemplateObject(["\n  left: ", ";\n  right: ", ";\n  bottom: ", ";\n  width: calc(100% - ", ");\n"], ["\n  left: ", ";\n  right: ", ";\n  bottom: ", ";\n  width: calc(100% - ", ");\n"])), function (_a) {
    var theme = _a.theme;
    return "".concat(theme.space[12]);
}, function (_a) {
    var theme = _a.theme;
    return "".concat(theme.space[12]);
}, function (_a) {
    var theme = _a.theme;
    return "".concat(theme.space[8]);
}, function (_a) {
    var theme = _a.theme;
    return "".concat(theme.space[24]);
});
var CardContainer = styled.div(templateObject_3$1 || (templateObject_3$1 = __makeTemplateObject(["\n  position: relative;\n  height: 180px;\n  width: calc(100vw - 46px);\n\n  @media (min-width: 425px) {\n    width: calc(50vw - 32px);\n  }\n  @media (min-width: 600px) {\n    width: calc(50vw - 44px);\n  }\n  @media (min-width: 660px) {\n    width: calc(33vw - 36px);\n  }\n  ", " {\n    width: 190px;\n  }\n  ", " {\n    width: 195px;\n  }\n\n  &:hover,\n  &:focus-within {\n    button {\n      opacity: 1;\n    }\n  }\n"], ["\n  position: relative;\n  height: 180px;\n  width: calc(100vw - 46px);\n\n  @media (min-width: 425px) {\n    width: calc(50vw - 32px);\n  }\n  @media (min-width: 600px) {\n    width: calc(50vw - 44px);\n  }\n  @media (min-width: 660px) {\n    width: calc(33vw - 36px);\n  }\n  ", " {\n    width: 190px;\n  }\n  ", " {\n    width: 195px;\n  }\n\n  &:hover,\n  &:focus-within {\n    button {\n      opacity: 1;\n    }\n  }\n"])), function (_a) {
    var theme = _a.theme;
    return theme.breakpoints.md;
}, function (_a) {
    var theme = _a.theme;
    return theme.breakpoints.lg;
});
var BackgroundImage = styled.div(templateObject_4$1 || (templateObject_4$1 = __makeTemplateObject(["\n  position: absolute;\n  width: 100%;\n  height: 100%;\n  border-radius: ", ";\n  overflow: hidden;\n\n  &::after {\n    content: '';\n    position: absolute;\n    background-color: rgba(0, 0, 0, 0.5);\n    inset: 0;\n    z-index: 1;\n  }\n\n  > img {\n    position: absolute;\n    width: 100%;\n    object-fit: cover;\n  }\n"], ["\n  position: absolute;\n  width: 100%;\n  height: 100%;\n  border-radius: ", ";\n  overflow: hidden;\n\n  &::after {\n    content: '';\n    position: absolute;\n    background-color: rgba(0, 0, 0, 0.5);\n    inset: 0;\n    z-index: 1;\n  }\n\n  > img {\n    position: absolute;\n    width: 100%;\n    object-fit: cover;\n  }\n"])), function (_a) {
    var theme = _a.theme;
    return theme.space[16];
});
var LogoImageWrapper = styled.div(templateObject_5 || (templateObject_5 = __makeTemplateObject(["\n  position: absolute;\n  display: grid;\n  place-items: center;\n  width: 100%;\n  height: 100%;\n  overflow: hidden;\n  z-index: 2;\n\n  > img {\n    object-fit: contain;\n    height: 90px;\n    width: 80%;\n    padding-bottom: ", ";\n    filter: invert(1) grayscale(1) brightness(2);\n  }\n"], ["\n  position: absolute;\n  display: grid;\n  place-items: center;\n  width: 100%;\n  height: 100%;\n  overflow: hidden;\n  z-index: 2;\n\n  > img {\n    object-fit: contain;\n    height: 90px;\n    width: 80%;\n    padding-bottom: ", ";\n    filter: invert(1) grayscale(1) brightness(2);\n  }\n"])), function (_a) {
    var theme = _a.theme;
    return "".concat(theme.space[12]);
});
var ShopName = styled(Body5)(templateObject_6 || (templateObject_6 = __makeTemplateObject(["\n  position: absolute;\n  color: ", ";\n  font-size: ", ";\n  font-weight: ", ";\n  z-index: 2;\n  ", "\n  ", "\n"], ["\n  position: absolute;\n  color: ", ";\n  font-size: ", ";\n  font-weight: ", ";\n  z-index: 2;\n  ", "\n  ", "\n"])), function (_a) {
    var theme = _a.theme;
    return "".concat(theme.colors.shopCard.white);
}, function (_a) {
    var theme = _a.theme;
    return "".concat(theme.fonts.size.desktop.xxs);
}, function (_a) {
    var theme = _a.theme;
    return "".concat(theme.fonts.weight.medium500);
}, bottomItemPosition, limitCharacters);
var CallToActionButton = styled(Button$2)(templateObject_7 || (templateObject_7 = __makeTemplateObject(["\n  position: absolute;\n  height: 36px;\n  padding: ", ";\n  z-index: 2;\n  opacity: 0;\n  ", "\n\n  transition: opacity 0.15s ease-in-out;\n"], ["\n  position: absolute;\n  height: 36px;\n  padding: ", ";\n  z-index: 2;\n  opacity: 0;\n  ", "\n\n  transition: opacity 0.15s ease-in-out;\n"])), function (_a) {
    var theme = _a.theme;
    return "".concat(theme.space[4], " ").concat(theme.space[8]);
}, bottomItemPosition);
var templateObject_1$1, templateObject_2$1, templateObject_3$1, templateObject_4$1, templateObject_5, templateObject_6, templateObject_7;

var ShopCard = function (_a) {
    var backgroundImage = _a.backgroundImage, logoImage = _a.logoImage, shopName = _a.shopName, linkData = _a.linkData, nextLink = _a.nextLink;
    return (jsx(LinkWithChildren, __assign$1({ NextLink: nextLink, linkData: linkData }, { children: jsxs(CardContainer, { children: [jsx(BackgroundImage, { children: backgroundImage }), logoImage && jsx(LogoImageWrapper, { children: logoImage }), shopName && jsx(ShopName, { children: shopName }), jsx(CallToActionButton, __assign$1({ variant: "primary" }, { children: linkData === null || linkData === void 0 ? void 0 : linkData.linkTitle }))] }) })));
};

var ShopListWithCategories = function (_a) {
    var _b;
    var title = _a.title, categories = _a.categories, _c = _a.initialSelectedValue, initialSelectedValue = _c === void 0 ? '' : _c;
    var _d = useState(initialSelectedValue), selectedValue = _d[0], setSelectedValue = _d[1];
    var currentCategory = categories.find(function (category) { return category.value === selectedValue; });
    var handleSelect = function (e) {
        var _a;
        var newSelectedCategory = categories.find(function (category) { return category.value === (e === null || e === void 0 ? void 0 : e.currentTarget.value); });
        setSelectedValue((_a = newSelectedCategory === null || newSelectedCategory === void 0 ? void 0 : newSelectedCategory.value) !== null && _a !== void 0 ? _a : null);
    };
    return (jsxs(Fragment, { children: [title && (jsx(Row$1, { children: jsx(Col, __assign$1({ xs: 4 }, { children: jsx(Header3$2, { children: title }) })) })), jsx(CategoryFilter, { currentCategoryId: selectedValue !== null && selectedValue !== void 0 ? selectedValue : '', handleSelect: handleSelect, options: categories, buttonVariant: Variant.ImageWithText }), jsx(Row$1, { children: (_b = currentCategory === null || currentCategory === void 0 ? void 0 : currentCategory.shopList) === null || _b === void 0 ? void 0 : _b.map(function (shop) {
                    return (jsx(Col, { children: jsx(ShopCard, __assign$1({}, shop)) }));
                }) })] }));
};

var StandaloneTitle = styled(Header2)(templateObject_1 || (templateObject_1 = __makeTemplateObject(["\n  text-align: center;\n  margin-bottom: 32px;\n"], ["\n  text-align: center;\n  margin-bottom: 32px;\n"])));
var TitleSection = styled.div(templateObject_2 || (templateObject_2 = __makeTemplateObject(["\n  text-align: center;\n"], ["\n  text-align: center;\n"])));
var ElementsWrapper = styled.div(templateObject_3 || (templateObject_3 = __makeTemplateObject(["\n  display: flex;\n  flex-direction: column;\n  padding: 30px 16px; // offset for rounded background that gives 30px top&bottom\n  gap: 32px;\n\n  article {\n    flex: 1;\n  }\n\n  ", " {\n    padding: 30px 32px; // offset for rounded background that gives 30px top&bottom\n  }\n\n  ", " {\n    flex-direction: row;\n  }\n"], ["\n  display: flex;\n  flex-direction: column;\n  padding: 30px 16px; // offset for rounded background that gives 30px top&bottom\n  gap: 32px;\n\n  article {\n    flex: 1;\n  }\n\n  ", " {\n    padding: 30px 32px; // offset for rounded background that gives 30px top&bottom\n  }\n\n  ", " {\n    flex-direction: row;\n  }\n"])), function (_a) {
    var theme = _a.theme;
    return theme.breakpoints.sm;
}, function (_a) {
    var theme = _a.theme;
    return theme.breakpoints.md;
});
var MainContentWrapper = styled.div(templateObject_4 || (templateObject_4 = __makeTemplateObject(["\n  ", " {\n    margin: 0 91px;\n  }\n  ", " {\n    margin: 0;\n  }\n"], ["\n  ", " {\n    margin: 0 91px;\n  }\n  ", " {\n    margin: 0;\n  }\n"])), function (_a) {
    var theme = _a.theme;
    return theme.breakpoints.sm;
}, function (_a) {
    var theme = _a.theme;
    return theme.breakpoints.lg;
});
var templateObject_1, templateObject_2, templateObject_3, templateObject_4;

var StepsSection = function (_a) {
    var title = _a.title, sectionDescription = _a.sectionDescription, _b = _a.backgroundColor, backgroundColor = _b === void 0 ? theme.colors.stepsSection.defaultBackgroundTemporary : _b, _c = _a.textColor, textColor = _c === void 0 ? theme.colors.stepsSection.defaultColor : _c, elements = _a.elements, PortableTextWrapper = _a.PortableTextWrapper;
    var mode = textColor === theme.crediteaColors.primary.neutral[50] ? 'light' : 'dark';
    return (jsxs(Fragment, { children: [sectionDescription && !hasEmptyTextContent(sectionDescription) && PortableTextWrapper ? (jsxs(TitleSection, { children: [jsx(Header2, { children: title }), jsx(Gutter, { size: 16 }), sectionDescription.textRaw && (jsx(PortableTextWrapper, { portableText: sectionDescription.textRaw, mode: mode })), jsx(Gutter, { size: 24 })] })) : (jsx(StandaloneTitle, { children: title })), jsx(MainContentWrapper, { children: jsx(RoundedBackground, __assign$1({ color: backgroundColor, roundedType: RoundedBackground.RoundTypes.DiagonalCorners }, { children: jsx(ElementsWrapper, __assign$1({ "data-testid": "stepsSectionElementsWrapper" }, { children: elements === null || elements === void 0 ? void 0 : elements.map(function (item) {
                            var _a, _b, _c, _d;
                            return (jsx(FeatureCard, { icon: item.iconName, iconBackgroundColor: (_b = (_a = item.iconColor) === null || _a === void 0 ? void 0 : _a.value) !== null && _b !== void 0 ? _b : '', title: item.title, textColor: textColor, textContent: ((_c = item.textContent) === null || _c === void 0 ? void 0 : _c.textRaw) &&
                                    !hasEmptyTextContent(item.textContent) &&
                                    PortableTextWrapper && (jsx(PortableTextWrapper, { portableText: (_d = item.textContent) === null || _d === void 0 ? void 0 : _d.textRaw, mode: mode })), alignment: "center", fontSizeVariant: "H6", mode: mode }, item.title));
                        }) })) })) })] }));
};

var currencyFormatEE = function (value, digits) {
    if (digits === void 0) { digits = 0; }
    return new Intl.NumberFormat('et-EE', {
        style: 'currency',
        currency: 'EUR',
        currencyDisplay: 'narrowSymbol',
        maximumFractionDigits: digits,
    }).format(value);
};
var currencyFormatLV = function (value, digits) {
    if (digits === void 0) { digits = 0; }
    return new Intl.NumberFormat('lv-LV', {
        style: 'currency',
        currency: 'EUR',
        currencyDisplay: 'narrowSymbol',
        maximumFractionDigits: digits,
    }).format(value);
};
var currencyFormatLT = function (value, digits) {
    if (digits === void 0) { digits = 0; }
    return new Intl.NumberFormat('lt-LT', {
        style: 'currency',
        currency: 'EUR',
        currencyDisplay: 'narrowSymbol',
        maximumFractionDigits: digits,
    }).format(value);
};
var currencyFormatCZ = function (value, digits) {
    if (digits === void 0) { digits = 0; }
    return new Intl.NumberFormat('cs-CZ', {
        style: 'currency',
        currency: 'CZK',
        currencyDisplay: 'narrowSymbol',
        maximumFractionDigits: digits,
    }).format(value);
};
var currencyFormatMX = function (value, digits) {
    if (digits === void 0) { digits = 0; }
    return new Intl.NumberFormat('es-MX', {
        style: 'currency',
        currency: 'MXN',
        currencyDisplay: 'narrowSymbol',
        minimumFractionDigits: digits,
    }).format(value);
};

export { Accordion, AccordionsGroup, AlternativeFeatures, BaseCarousel, BlogArticles, BlogCard, BlogHero, BulletList, BulletListItem, Button$2 as Button, ButtonOrDownloadFile, ButtonsLayout, CTABanner, CTATextSection, CampaignForm, Card, CardsSection, CardsWithImage, Carousel, CarouselCard, CarouselPromotionCard, CategoryFilter, Checkbox$1 as Checkbox, CheckboxItem$1 as CheckboxItem, CircleDiagram, Col, Container$1 as Container, ContentList, DatePicker, DateRangePicker, DescriptiveListItem, Divider, DownloadAppButtons, ErrorMessage, Feature, FeatureCard, FeatureList, FlippingCard, FlippingCards, Footer, FullWidthLink, index$1 as Grid, Gutter, Header, Hero, Icon$4 as Icon, ImageCard, InlineNotification, Input$1 as Input, InputItem$1 as InputItem, LanguagePicker, LastArticleForCz, LatestArticles, LinkAsButton, LinkBody, LinkSection, LinkWithChildren, LinksWithTitle, Loader, LoanForm, Markets, DropDown as MenuDropdown, MenuBodyItem as MenuItemBody, MobileDrawer, Modal, Newsletter, PageWrapper, Pagination, PhoneButton, PriceInfo, PromoBanner, PromoBar, ProvidentTextLogo, RangeInput, RelatedArticles, RoundedBackground, RoundedFeature, Row$1 as Row, SearchInput, SelectButton, SelectItem$1 as SelectItem, ShopListWithCategories, ShortFaq, Slider$1 as Slider, Snackbar, Socials, Stepper, StepsSection, StyledList, Table, Tabs, TextAreaItem, ThemeProvider, Tooltip, TwoColumnText, Typography_style as Typography, VideoPlayer, index$2 as constans, crediteaGlobalStyle, currencyFormatCZ, currencyFormatEE, currencyFormatLT, currencyFormatLV, currencyFormatMX, formValidationPattern, makeMediaQuery, slugify, theme, useDelayUnmount, useEventListener, useIsScreenSize, useScroll, useSnackbar };
//# sourceMappingURL=index.esm.js.map
